/*
 *AiRA Freedom Financial
Copyright (C) 2026 

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published
by the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <https://www.gnu.org/licenses/>.

---------------------------------------------------------------------
ADDITIONAL TERMS (Dual Licensing):
If the terms of the AGPL v3 are incompatible with your use of the software, 
alternative commercial licensing terms are available. 
Please contact us in the feedback section of the app for proprietary licensing options 
including distribution rights and royalty arrangements.
---------------------------------------------------------------------

/*Disclaimer and Terms of Use
Last Updated: April 11, 2026

1. Not Financial Advice
The Aira Freedom Financial application (the "App") is provided as a financial modeling and educational tool for informational purposes only. 
It does not constitute professional financial, investment, tax, or legal advice.  The developers of this app  are not acting as your financial advisor, fiduciary, or broker through the provision of this App.

All simulations, including Monte Carlo analyses and withdrawal strategies, are based on historical data and mathematical projections. 
Past performance is not indicative of future results. Financial markets are inherently volatile, and there is no guarantee that the assumptions used in the App will materialize.

2. "Use at Your Own Risk" & Accuracy
While the logic and methodologies used in this tool are utilized by the developer 
for personal planning, they are provided "as is" and "as available." 
We make no warranties, express or implied, regarding the accuracy, completeness, or reliability of the calculations. 
Financial planning involves complex variables that may not be fully captured by this software. 
You are solely responsible for verifying any output from the App with a qualified professional before making any financial decisions.

3. Limitation of Liability and Indemnification
By using this App, you agree to assume full responsibility for any financial decisions or "critical errors" made based on its output.

To the maximum extent permitted by law, you agree to indemnify, defend, and 
hold harmless the developers of this app, and any affiliates 
from and against any and all claims, losses, damages, liabilities, and expenses (including legal fees) arising from:

Your use or misuse of the App.

Any errors, omissions, or inaccuracies in the data or results generated.

Any financial loss, loss of profit, or "sequence of returns" failures resulting from reliance on the App.

4. User Responsibility
You acknowledge that financial planning is highly individualized. 
The "spending smiles," guardrails, or projections provided by Aira may not be suitable for your specific financial situation, risk tolerance, or time horizon.  Use at  your own risk and always
consult your fiduciary, CPA or tax accountant. 

 * ============================================================ */
import React, { useState, useEffect, useRef, useCallback, useMemo, memo, useContext, createContext } from "react";
import ReactDOM from "react-dom";
import { ABOUT_ME, ABOUT_THANKS, ABOUT_PRODUCT, ABOUT_FEATURES } from "./about.js";
import {
  taxableSocialSecurity,
  computeHouseholdSS,
  LTCG_BRACKETS_2026_MFJ, LTCG_BRACKETS_2026_SINGLE,
  NIIT_THRESHOLD_MFJ, NIIT_THRESHOLD_SINGLE, NIIT_RATE,
  getSeniorBonusDeduction, OBBBA_SENIOR_LAST_YEAR,
} from "./engine/buildRothExplorer.js";
import { buildWithdrawalWaterfall, accumulateToRetirement, resolveDrawOrder, effectiveRetireAge, gkReferenceWR, migrateOrderingMode } from "./engine/buildWithdrawalWaterfall.js";
import { expectedReturn, SP500, BONDS, INFL } from "./engine/expectedReturn.js";
import { resolveGlidepathSwitchAge, glidepathEquityWeight, glidepathEqPct } from "./engine/glidepath.js";
import { jobContributionsForYear, householdAnnualContribution, totalRetirementIncome } from "./engine/contributions.js";
import { explainScore } from "./engine/explainScore.js";
import { buildConversionPlan, buildConversionLadder, buildWaterfallComparison } from "./engine/rothConversionPlan.js";
import { mortgageSchedule, mortgageAnnualPayments, computeOtherIncome, computeCashFlowEvents, spendingSmileFactor, healthcareShockDraw, expectedHealthcareShock } from "./engine/expenses.js";
import { scheduleSpendForYear, parseExpenseCsv, resolveSpendGuardrails, SINGLE_YEAR_TEMPLATE, MULTI_YEAR_TEMPLATE } from "./engine/expenseImport.js";
import { evaluateRules as evaluateRulesEngine } from "./engine/rulesEngine.js";
import { earlyWithdrawalPenalty, detectEmployerPlan, ruleOf55SeparationQualifies, EARLY_PENALTY_AGE } from "./engine/earlyWithdrawal.js";
import { isYearEndWindow, daysLeftInTaxYear, yearEndTaxRoom } from "./engine/yearEnd.js";
import CountdownCard from "./CountdownCard";
import DateField from "./DateField";
import SpouseDobField from "./SpouseDobField";
import { BucketLegend, BucketResetButton } from "./BucketLegend";
import BudgetEditor, { budgetLinesToCsv, lineError } from "./planInputs/BudgetEditor";
import { NavContext, GoStep } from "./NavContext";
import CheckpointsPanel from "./forecast/CheckpointsPanel";
import { INCOME_LABELS } from "./engine/incomeLabels";
import { bucketColor } from "./engine/bucketColors";
import { retirementTarget, formatRemaining } from "./engine/retirementTarget";
import { ageFromDob, parseCalendarDate, personAgeNow, spouseAgeOffset, spouseAgeAt, personsAtLeastAge, filesJointlyAt, filingStatusAt, spouseDeathOnPrimaryClock, planEndAgeOnPrimaryClock, survivorAgeOnPrimaryClock, survivorIsPrimary, firstToDie, contribStopOnPrimaryClock } from "./engine/ages.js";
import { survivorFra, survivorReductionFactor, survivorBasis, resolveSurvivorClaimAge } from "./engine/survivorBenefit.js";
import { STRATEGY_LABELS, resolveStrategy, migrateWithdrawalStrategy, migrationNotice } from "./engine/withdrawalStrategies.js";
import { _defaultBucket, accountBucketPieces, expandAccountBuckets, clampBucket, bucketFractionsByCategory, bucketBalancesForRow, computeBucket1Runway } from "./engine/buckets.js";
import { dollarBasisLabel, retirementBasisYear, deflate, mcMedianAtAge, selectPortfolioAtAge, selectTerminalMeanAtAge } from "./engine/mcSelectors.js";
import { taxableYieldSplit } from "./engine/taxableYield.js";
// One declaration of every figure's arithmetic, rendered here and enforced by
// provenance.test.js. Never inline a formula string — it would drift from the test.
import { formulaFor } from "./provenance.js";
import { solveRetirementDate, GEMINI_MODELS, DEFAULT_GEMINI_MODEL, AiUsageBadge, BILLING_ENABLED /*, AiraAITab — hidden pending test */ } from "./ai/ai-analysis.js";
import { CreditBalanceBadge, CreditPackModal, RecoveryLinkModal, RestoreAccessModal, useStripeReturn, useRestoreReturn, useCreditBalance, useReportUnlocked, useReportCapability , getStoredJWT, MIN_CREDITS_TO_RUN, LOW_BALANCE_WARN_AT, getStoredRecoveryLink } from "./billing/credits.js";
import { AdminPanel, useOwnerVerified } from "./billing/admin-panel.js";
import PrintReport from "./report/PrintReport.jsx";
import { AGE_LIMITS } from './data/ageLimits.js';
import { ComposedChart,Area,BarChart,Bar,LineChart,Line,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,ReferenceLine,ReferenceDot,Legend,RadarChart,PolarGrid,PolarAngleAxis,PolarRadiusAxis,Radar,PieChart,Pie,Cell,} from "recharts";
import { jointRmdDivisor } from './data/jointRmdTable.js';
import { STATE_BRACKETS } from './data/stateBrackets.js';
import { IRMAA_2026 } from './data/irmaa2026.js';

// Dropped DM Sans / DM Mono. Down to one text family (Inter) and one mono
// family (JetBrains Mono), both pulled in through the same @import in the CSS
// constant below. We used to load two text fonts and two mono fonts for the
// same job (body text, numeric readout), which just meant two places to keep
// in sync for no reason. The landing hero classes `.lp-age` and `.lp-val` were
// the only things still using DM Mono; they're on JetBrains Mono now too.

/** This application is  Aira - Freedom Financial Forecaster
 * Here is some reference information: 
 * IRS Publication 590‑B (PDF) – see Appendix B (pages 46‑60)  https://www.irs.gov/pub/irs-pdf/p590b.pdf
 * Capital Group's joint life table – excerpt for ages 55‑80 https://www.capitalgroup.com/individual/service-and-support/rmd/how-to-calculate/irs-joint-life-table.html

🔢 Please see the disclaimer below. This is an app to help you with retirement planning but not financial advice.
 * 
 */


/**
 * Age from a date of birth, by calendar birthday. `asOf` defaults to today, so
 * `ageFromDob(dob)` is "age now" and `ageFromDob(dob, someDate)` is "age on that
 * date". Returns null for a missing/unparseable input so callers choose their
 * own fallback.
 *
 * Please don't re-inline this. At one point we had four separate copies of
 * this logic. Two divided elapsed milliseconds by 365.25 days, which is off by
 * a full year from the calendar answer if you're near your birthday, so the
 * engine and the Profile panel could report different ages for the same
 * person. The other two hand-rolled the birthday adjustment for checkpoint
 * dates, and one of those compared "month-day" as plain strings ("9-5" <
 * "10-1" is true as strings but wrong as dates).
 *
 * On top of that, some views read the stored `currentAge` field instead of
 * deriving it from `dob`, so editing a birthday updated the simulation but
 * left the portfolio fan chart and MC band table showing the old age —
 * including the band table's calendar-year column, quietly shifted off by
 * the difference.
 *
 * `dob` is the source of truth, always derive from it. Stored `currentAge` is
 * only a fallback for imported profiles that don't have a dob.
 */
/* Lives in src/engine/ages.js now — the engines need it too and can't import
 * from App.jsx without creating a cycle. Re-exported at the bottom of this
 * file so every existing caller and ageDerivation.test.js still work. Please
 * don't add a local copy back — we had four different ways of computing age
 * here before and two of them were wrong. */

/**
 * Parse a date that represents a calendar day (a birthday, a checkpoint date),
 * not a specific instant in time.
 *
 * `new Date("1970-07-27")` parses as UTC midnight, but getFullYear/getMonth/
 * getDate all read it back in local time. West of UTC that lands on the
 * previous day: for a US user that birthday becomes July 26th, and a dob of
 * "1970-01-01" becomes 1969-12-31 — shifting the derived age by a year for
 * the rest of that year. So date-only strings get split and rebuilt with the
 * local-time constructor instead. Anything that already carries a time (or is
 * already a Date object) passes through untouched.
 *
 * Lives in src/engine/ages.js now, same reason as ageFromDob above.
 */

/**
 * Age input bounds, one definition, used by every age control.
 *
 * These used to be hardcoded in three separate places and didn't agree: the
 * sidebar retire-age slider allowed 50-68 while the wizard's input for the
 * same value allowed 50-100, so typing 70 in the Profile and then touching
 * the slider would silently snap it back to 68 — meaning you couldn't model
 * delaying retirement to 70, which is the most common Social Security
 * optimization there is. The landing hero also capped current age at 62 on
 * its own, locking out anyone already retired.
 *
 * These ranges cover real users at both ends: FIRE at 45, delayed SS at 70+,
 * and people who are already retired and just want to know how it's going.
 */


/**
 * Where feedback goes.
 *
 * This replaced an EmailJS integration that broke twice in one week for the
 * same reason: its three REACT_APP_EMAILJS_* keys get inlined by CRA at build
 * time, and `.env*` is gitignored, so those values only ever existed on one
 * machine. A fresh clone and production both shipped `undefined` and the
 * button just failed — once telling the user to go open a browser console.
 *
 * A mailto has no keys, no build-time config, no npm dependency, and no public
 * key sitting in the bundle for someone to spam through. It can't break on a
 * new machine because there's nothing to configure. The real tradeoff is that
 * mailto needs the visitor to have a mail client set up, so the address is
 * also shown as selectable text in the dialog for anyone on webmail.
 */
const FEEDBACK_EMAIL = "tiredtoretire@gmail.com";

const APP_VERSION = "1.3.0";
export const BUILD_TAG = `[main] v1.3.0 - Home screen: the inputs come first
- CHANGED: Plan basics is at the top of the left side, right under Configure. The five main inputs show next to your answer on a first run.
- CHANGED: The Net Worth chart is shorter (320px, was 540px), so the cards under the tabs stay on screen.
- FIXED: The info icon on the Portfolio projection chart did nothing when clicked. It opens a short explanation now.
- PRIOR (v1.2.150): design pass: colors and sizes come from theme tokens, status dots, Stress Lab fixes
- CHANGED: type sizes use --fs-* tokens; about 750 hard-coded colors now use theme tokens (--chart-N, --tint-*, --text-*), so a theme change is one edit. Eight low-contrast text colors now pass 4.5:1.
- CHANGED: emoji removed from headings, buttons and sub-tabs; status marks are a small colored dot or plain words; labels are sentence case; main and nav landmarks added.
- STRESS LAB: Run is blocked and explained for out-of-range years; a severity change says it cleared the precise result; a pasted list longer than 10 years says it was cut; deltas show in pp.
- ENGINE: a $0-spend plan no longer draws the Guyton-Klinger floor every year.
- PRIOR (v1.2.149): runMC audit fixes: income nets against total outlay; dead code removed
- FIXED: retirement 'need' now nets SS + rental + other income against base spend AND fixed costs together (max(0, spend+fixedCosts-income)). The old form offset income against base spend only, so a household whose income exceeded base spend still sold assets to cover housing/carveouts/events/healthcare — biasing the success rate DOWN. need / incomeSurplus / remaining-draw now net the same quantity.
- REMOVED dead code in runMC: unused gg/sg/ng growth draws and the unused initWR calibration block (GK uses gkReferenceWR, not initWR). Dropping gg/sg/ng removes 6 rand() draws/path, so headline numbers re-baseline by RNG shift (not a logic change).
- NEXT (audit backlog, not yet done): (1) per-path RNG seed so changing spend by \$1 doesn't re-roll later paths / stabilize property tests [mulberry32(seed + i*0x9E3779B1)]; (2) || vs ?? zero-input bug (p.inf=0, abGrowth=0, mortRate=0 fall back to defaults); (3) cash return label-vs-math (cashRealReturn applied nominally; cash earns equity return in accumulation); (4) cost inflation uses deterministic p.inf while spend tracks path inflY; (5) consider block/stationary bootstrap vs current single-year i.i.d. sampling; (6) verify ninety_five_rule y===1 reset, mwRate single-life for couples, RMD_DIV ||15 fallback.
- PRIOR (v1.2.148): Clearer first run and Forecast: facts live in Plan inputs, what-if sliders in the sidebar; plain headings; keyboard access
- Standalone Configure / Adjust values launcher; Retirement Age, Planning Horizon, US Spending and SS Start Age are edited by the sidebar sliders only
- After the quick estimate, a "We assumed" card lists what was filled in, and the app opens on the retire age the estimate showed
- Forecast tab reordered: chart first, then why and what to do, the age table, assumptions, and checkpoints; the "not covered" warning is one line with details on click
- Checkpoints panel rebuilt (labelled actions, confirm before set baseline); one dollar-basis label naming the retirement year
- Top bar grouped and de-cluttered; Options card uses one toggle and one info icon; switches and pop-ups work from the keyboard; Help opens one topic at a time
- PRIOR (v1.2.147): Plan inputs consolidation: sidebar Configure button, Start over, and stale Profile pointers now link to the right Plan inputs step
- Sidebar Configure button and Start over (with confirm); countdown shows honest years/months and hides until a real date of birth exists
- Plan inputs gains the Real Estate & Debt step and an inline budget editor in Spending & Expenses; the Analysis view is read-only
- Edit accounts / Edit split open the Current Savings step; Mortgage/Housing row says why it is empty
- PRIOR (v1.2.146): Life-phase Sector badge is now a clickable InfoModal (all five sectors, current phase marked "You are here"), and the sector age windows are contiguous
- SectorBadge opens the shared InfoModal explaining every sector (age window + tax-and-access regime); the active phase carries a "You are here" pill instead of a wrapping em-dash suffix
- Fixed dead age ranges: sectors now partition the whole age line with no gaps/overlaps (Escape <59½ · Gap 59½-65 · Maneuver 65-73 · Torpedo 73-80 · Legacy 80+). Previously ages 63-65 and 73+ matched no sector and mislabeled as "Sector 1: The Escape"
- PRIOR (v1.2.145): Guardrails view rebuilt as tabs (Spending Path · Adjustment Events · Across All Scenarios)
- <GuardrailsView>: cross-scenario headline strip stays persistent; sub-tabs split the crowded stack. Spending Path adds the Portfolio line on a 2nd (right) axis for more info; Adjustment Events lists every cut/raise (age, from→to, why); Across All Scenarios expands mc.gkStats into stat cards. Median-path emptiness is explained, not hidden (bad sequences live in the cross-scenario tab)
- Prominent cut/raise triangles (~17x13px)
- PRIOR (v1.2.144): Uniform info-modal styling + semantic accent palette (INFO_ACCENT) + modal body-text kit (ModalLede/ModalP/Em/ModalNote)
- ONE header template for EVERY info modal, in the shared InfoModal shell: an accent icon badge + the title carrying a 2.5px accent underline over a faint accent hairline. Fix all of them at the source, not per modal
- Semantic accent palette INFO_ACCENT (colors for ideas): method=teal, money=blue, tax=purple, positive=green, risk=amber. Applied to Success-rate method modal (teal), Median Final Balance (blue/money), the Tax Modeling + Non-Resident State Tax modals (purple)
- Fixed the shell's "Got it" button hex-alpha concat bug (accent+"22" produced invalid CSS for var() tokens - the v1.2.112 class of bug) → withAlpha()
- Guardrail cut/raise triangles made prominent (~17x13px, offset clear of the spend line)
- PRIOR (v1.2.143): Guardrails chart surfaced on the Monte Carlo tab + info-modal cleanup
- Guardrails "Spending Path with Guardrail Adjustments" chart (+ cross-scenario strip) is now discoverable on the Monte Carlo tab, not buried 4 levels deep in Scenarios → Withdrawal Plan. Extracted DeepSeek's chart verbatim into one shared <GuardrailsSpendingPath> rendered by BOTH tabs (rule 8 - one source, no drift); gated via isGuardrailStrategy()
- Fixed a dead MEDIAN FINAL BALANCE ⓘ (was a title=-only span, dead on touch) → real click InfoModal
- Moved the MC-tab "Not financial advice" banner from mid-page (between inputs and the fan chart) to the true bottom, after the chart + band table
- Rich "How this simulation works" modal on the Success Rate ⓘ
- The Success Rate card's "ⓘ How this works" link opens a Chance-of-Success method modal: what it is, the 4-phase method, why probability beats a single projection, a "what the simulation draws from" pool-stats box (real 1928-2025 Damodaran arrays), and the data source
- Describes AiRA's ACTUAL engine: a bootstrap Monte Carlo that draws random years WITH REPLACEMENT, order reshuffled - NOT a sequential historical replay (the past won't repeat in order). Removed the competitor-style filter-by-year table that implied a replay method we don't use
- design-authority verdict (2026-09-24): deleted the duplicate inline "View method & data" card (opened the same modal as the ⓘ - single point of control); kept "Simulation inputs & assumptions" inline as the per-run audit trail (Rule 5)
- PRIOR (v1.2.141): Guyton-Klinger guardrails Spending Path with Adjustment Events
- New "Spending Path with Guardrail Adjustments" chart on the Withdrawal Plan tab: floor/ceiling bands, prior-vs-actual spend, and a per-year cut/raise marker for every year the guardrail rule fired (deflated to retirement-year dollars). Every series is an engine output on the schedule row (spEntering / spAfterGK / gkFloor / gkCeiling / gkEvent) - the UI applies no guardrail arithmetic of its own (Rule 8)
- runMC now returns mc.gkStats (cutRate / raiseRate / avgCutsPerPath / spend range) counted per path, for the cross-scenario summary
- Added an agent attribution + tamper-evidence registry (agent-marks.json + src/agentMarks.js + scripts/agent-marks.mjs), enforced by src/agentMarks.test.js, so an edit to another agent's region fails the build instead of sliding in
- Added a render smoke test for the guardrails chart (guardrailsChartRender.test.js) - the RothLadder-class gap that engine tests can't catch`;
export const BUILD_TIME = "2026-10-04T12:00:00Z";
if (typeof window !== "undefined" && !window.__AIRA_BUILD_LOGGED__) {
  window.__AIRA_BUILD_LOGGED__ = true;
  // eslint-disable-next-line no-console
  console.log(`[AiRA] build ${BUILD_TAG} · ${BUILD_TIME} · v${APP_VERSION}`);
}

// Simulation + guardrail constants, all in one place. UI text interpolates
// these instead of retyping the numbers, so changing a value here changes the
// engine and every label that mentions it at the same time.
export const MC_PATHS = 3000;            // Monte Carlo stochastic paths
export const STRESS_PATHS = 2000;        // 2000–2012 sequence-risk stress paths
export const MC_PATHS_LABEL = MC_PATHS.toLocaleString();      // "3,000"
export const STRESS_PATHS_LABEL = STRESS_PATHS.toLocaleString(); // "2,000"
// Bounds for the user-adjustable path count (MC tab → ⚙ Advanced Settings).
// The floor keeps a percentile band statistically meaningful; the ceiling is
// where a main-thread run stops feeling instant.
export const MC_PATH_MIN = 500;
export const MC_PATH_MAX = 20000;
// The window of the Damodaran arrays in engine/expectedReturn.js: 1928–2025,
// 98 paired years. The Monte Carlo samples whole calendar years out of this
// range, so the range bounds are a property of the DATA, not a preference —
// they live here as one pair of constants rather than being retyped into the
// slider limits, the validation, and the modal copy.
export const SAMPLE_START_YEAR = 1928;
export const SAMPLE_END_YEAR = 2025;
export const SAMPLE_YEARS = SAMPLE_END_YEAR - SAMPLE_START_YEAR + 1;
// Monte Carlo score bands. rateColor() and riskLabel() used to each hardcode
// these four numbers separately, and the explainer sentence had a fifth,
// unrelated one baked in — that's how five copies of "what counts as a good
// score" end up disagreeing. Same values as before, just one owner now.
export const MC_BAND_LOW_RISK  = 0.90;
export const MC_BAND_MODERATE  = 0.80;
export const MC_BAND_ELEVATED  = 0.70;
export const MC_BAND_HIGH      = 0.60;
// Band COLOUR and band LABEL, hoisted to module scope so every surface that
// colours a success figure reads the same thresholds. They used to be a local
// `rateColor`/`riskLabel` inside MCTab while MCBandTable carried its OWN inline
// `0.9 / 0.75` literals in `fundedColor` — two definitions of "what counts as a
// good score", which is the drift the MC_BAND_* constants were introduced to
// stop (see the comment above them). One definition, two consumers.
const rateColor = (r) =>
  r >= MC_BAND_LOW_RISK ? "var(--positive)" : r >= MC_BAND_MODERATE ? "var(--positive)" : r >= MC_BAND_ELEVATED ? "var(--accent-gold)" : r >= MC_BAND_HIGH ? "var(--chart-7)" : "var(--negative)";
// Short form of the band, for a per-row badge where `riskLabel`'s sentence
// would not fit. Keys off the same constants, so a badge and the headline it
// summarises can never disagree about which band a rate is in.
const bandLabel = (r) =>
  r >= MC_BAND_LOW_RISK ? "Excellent" : r >= MC_BAND_MODERATE ? "Good" : r >= MC_BAND_ELEVATED ? "Concerning" : "Critical";
// Not a band edge, on purpose — this is the "generally considered a solid
// plan" rule of thumb from the planning literature, separate from our own
// severity cutoffs above.
export const MC_SOLID_PLAN_RATE = 0.85;

// Semantic accent palette for info modals — ONE color per KIND of idea, so a
// modal's tint tells you its domain at a glance. Pass as InfoModal `accent`.
// The header template (icon badge + underline + hairline) is uniform in the
// InfoModal shell; only the hue changes by meaning. Tokens (not raw hex) so
// dark/light theming carries through; the shell routes every alpha through
// withAlpha(), so a var() token never gets string-concatenated into bad CSS.
export const INFO_ACCENT = {
  method:   "var(--accent-teal)",    // how it works / neutral explanation
  money:    "var(--chart-2)",               // balances, portfolio values, percentiles
  tax:      "var(--accent-purple)",  // tax, IRMAA, brackets, Roth conversions
  positive: "var(--positive)",       // success, probability, "good" outcomes
  risk:     "var(--accent-gold)",    // warnings, landmines, caution
};

/**
 * PillTab — the app's one row-of-choices idiom.
 *
 * WHY: three groups of pills had each grown their own active state, and a fourth
 * lived in `.mbtn.on`. Four ways to say "this one is selected" means a user has
 * to learn the gesture again on every screen, and it means every new control
 * starts by copying whichever neighbour the author last looked at.
 *
 * THE TREATMENT (Vincent, 2026-09-25): bare text on a shared hairline, active
 * item marked by a 2px accent underline — the same language as the top-level
 * tabs and the Monte Carlo Simulation/Guardrails row. An earlier pass gave these
 * a tinted surface + coloured border box; on screen that read as a wall of
 * buttons competing with the tabs above them, and the boxes made every row look
 * like a form control rather than navigation. The accent colour carries the
 * state; no fill and no border box.
 *
 * WHAT IS STILL NOT UNIFIED, ON PURPOSE: the accent colour. Some rows encode
 * meaning in their hue and flattening it would delete information —
 *   · Bracket Fill: green = safe bracket, amber = caution, red = 32%+ / IRMAA risk
 *   · Tax Room:     amber marks figures anchored to REAL entered income, versus
 *                   the optimizer's projections elsewhere in the row
 * So `accentFor` is a prop and each caller passes what the choice MEANS. It now
 * paints the active item's text AND its underline, which is the only colour the
 * row needs.
 *
 * `size` exists because the Analysis row is primary navigation (7 items, wide
 * spacing) while the compact rows sit inside a settings card; forcing one size
 * on both is what made the Analysis row look like a form last time.
 */
function PillTab({
  options, value, onChange, accentFor,
  defaultAccent = INFO_ACCENT.method,
  ariaLabel, style, size = "md",
}) {
  const SIZES = {
    md: { fontSize: "var(--fs-label)", padding: "7px 13px" },
    sm: { fontSize: "var(--fs-small)", padding: "5px 10px" },
  };
  const s = SIZES[size] || SIZES.md;
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        // No gap: the items sit flush on the hairline like real tabs, separated
        // by their own padding. `marginBottom:-1` on each item drops it onto the
        // rule so the active underline replaces the grey line instead of
        // floating above it.
        gap: 2,
        flexWrap: "wrap",
        alignItems: "stretch",
        // The full-width hairline the underline sits on — this is the line that
        // makes a row of labels read as a tab bar.
        borderBottom: "1px solid var(--divider)",
        ...style,
      }}
    >
      {options.map(([key, label]) => {
        const on = key === value;
        const accent = (accentFor && accentFor(key)) || defaultAccent;
        return (
          <button
            key={key}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(key)}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: `2px solid ${on ? accent : "transparent"}`,
              marginBottom: -1,
              padding: s.padding,
              fontSize: s.fontSize,
              fontFamily: "var(--font-sans)",
              fontWeight: on ? 800 : 600,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              color: on ? accent : "var(--text-muted)",
              cursor: "pointer",
              transition: "color 0.15s, border-color 0.15s",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Guyton-Klinger guardrails, as % of core spend
export const GK_FLOOR_DEFAULT_PCT = 65;export const GK_CEILING_DEFAULT_PCT = 135;
// Dollar fallbacks used only when a profile predates the % fields
export const GK_FLOOR_FALLBACK = 48_000;
export const GK_CEILING_FALLBACK = 115_000;
// "Today" as a calendar year, for age→year conversion and inflation-factor
// indexing (e.g. Math.pow(1+rate, yr - CURRENT_YEAR)). Same pattern as
// buildWithdrawalWaterfall.js's BASE_YEAR and buildRothExplorer.js's
// ROTH_BASE_YEAR — computed dynamically so it never goes stale like a
// hardcoded year would. Don't use this for the FED_BRACKETS_2026_* /
// IRMAA_2026 table names or their dollar figures though — those are the real
// IRS 2026 bracket numbers and need to stay pinned to 2026.
const CURRENT_YEAR = new Date().getFullYear();

// Theme system.
// Runtime accessor for chart series colors. Recharts wants literal color
// strings on props (`stroke="var(--accent)"`), so a chart that hardcodes those
// literals ignores the CSS token system entirely. `CHART_PALETTE.accent` reads
// the current value of `--accent` off the document root at call time, so once
// a chart switches `stroke="var(--accent)"` to `stroke={CHART_PALETTE.accent}`
// it follows the theme. Calling `getComputedStyle` on every render is cheap
// (native, cached by the browser). Guarded for SSR / test.
export const CHART_PALETTE = {
  get accent()       { return readCssVar('--accent'); },
  get teal()         { return readCssVar('--accent-teal'); },
  get purple()       { return readCssVar('--accent-purple'); },
  get ai()           { return readCssVar('--accent-ai'); },
  get gold()         { return readCssVar('--accent-gold'); },
  get positive()     { return readCssVar('--positive'); },
  get negative()     { return readCssVar('--negative'); },
  get textPrimary()  { return readCssVar('--text-primary'); },
  get textSecondary(){ return readCssVar('--text-secondary'); },
  get textMuted()    { return readCssVar('--text-muted'); },
  get gridLine()     { return readCssVar('--divider'); },
};
function readCssVar(name) {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}


// SP500 / BONDS live in engine/expectedReturn.js now, one source of truth for
// the historical bootstrap. Both arrays are Damodaran's year-aligned
// 1928-2025 series (98 pairs), so portReturn() can draw one shared random
// index and keep the real stock/bond correlation intact, including
// flight-to-quality in crashes (2008 SP -36.55% pairs with Bond +20.10%). The
// old arrays had a length mismatch (99 vs 51) and were duplicated across
// files — see engine/expectedReturn.js header for details.
// SP500 and BONDS are imported at the top of this file alongside expectedReturn().

// INFL is imported from engine/expectedReturn.js too now — extended to
// 1928-2025 (98 entries, aligned to SP500/BONDS), and no longer clamped. The
// old `Math.max(0.5, Math.min(7.0, r))` clamp was cutting off real deflation
// years (1932: -9.9%) along with real inflation spikes (1974: 11%, 1980:
// 13.5%, 2022: 8%). See the file header there for the source. Dropped the
// clamp for the same accuracy reasons as the stock/bond arrays above.

// GK paper: cap CPI pass-through in Guyton-Klinger withdrawal adjustments at 6%.
// Distinct from the INFL data clamp above (which bounds the historical CPI bootstrap).
const GK_INFLATION_CAP = 0.06;
// The other three GK paper numbers, named so the engine and the Advanced
// Settings disclosure read from ONE definition. They used to be inline
// literals in guytonKlingerWithdrawal, which meant the "20% band → ±10%"
// sentence in the UI was a second, independently-typed copy of the rule — the
// classic drift setup (the explainer and the engine disagreeing after one
// edit). Percentages, so `* 100` at the display site.
const GK_BAND_PCT = 0.20;    // WR vs initial WR that triggers an adjustment
const GK_ADJUST_PCT = 0.10;  // size of the raise/cut when the band is crossed
const GK_LONGEVITY_YEARS = 15; // no capital-preservation cut with ≤ this many years left

const SEQ_2000_2012 = [
  -0.091, -0.119, -0.221, 0.287, 0.109, 0.048, 0.158, 0.055, -0.37, 0.265,
  0.151, 0.021, 0.16,
];

// expectedReturn() (the expected-value helper used by computeInitialWR, the
// deterministic schedule, and the Fan Chart) lives in ./engine/expectedReturn.js
// now, so buildWithdrawalWaterfall.js, buildRothExplorer.js, and
// rothConversionPlan.js can all share the same formula instead of each
// hardcoding a flat 7%. SP500/BONDS stay here unchanged because they feed the
// stochastic bootstrap draws in portReturn/bootstrapDraw below — a different
// consumer than expectedReturn().

/**
 * Initial withdrawal rate diagnostic.
 * Projects the portfolio to retirement using real (inflation-adjusted) return,
 * so the result stays in today's dollars.
 * initDrawEst is the net portfolio need at retirement — gross spend minus SS/
 * rental/other income, plus housing/carveouts. It's the same quantity the GK
 * engines (runMC/simulateDeterministicWithStrategy/buildWithdrawalWaterfall)
 * calibrate their own initWR against, so this sidebar number matches what the
 * engines actually use. Housing/carveout/otherIncome terms are evaluated at
 * the retirement calendar year but left un-inflated (today's-dollars mortgage
 * map value / raw carveout annual amounts), to stay consistent with the rest
 * of this helper's today's-dollars framing.
 * initDrawEst also carries one-off expenses planned for the retirement year (a
 * renovation): that is what year one really draws. steadyWRpct leaves them out;
 * it is the plan's ongoing rate, the one guardrails and headlines should judge.
 * Returns { initWRpct, steadyWRpct, projectedPort, initDrawEst, steadyDraw, oneOffAtRetire,
 * baseSpend, ssAtRetire, rentalAtRetire, otherIncAtRetire, housingAtRetire, carveoutAtRetire,
 * annualAdds, accumRate, nominalRate, inflRate, yrsToRetire } so callers can show the math.
 * Accepts either a profile shape (values from RetirementPanel) or the assembled params shape.
 */
export function computeInitialWR(p) {
  const currentAge = p.currentAge || 35;
  const retireAge = p.retireAge || 65;
  const yrsToRetire = Math.max(0, retireAge - currentAge);
  const eqPct = p.preRetireEq ?? 91;
  const nominalRate = expectedReturn(eqPct) / 100;
  const inflRate = (p.inf ?? 2.5) / 100;
  const accumRate = nominalRate - inflRate;
  const hsaAnnual = p.hsaContrib != null ? p.hsaContrib : (p.hsaMonthly || 0) * 12;
  const annualAdds = (p.contrib || 0) + (p.employerContrib || 0) + hsaAnnual
    + (p.taxableContrib || 0) + (p.rothContrib || 0);
  const growth = (yrs) => Math.pow(1 + accumRate, yrs);
  const projectedPort = (p.port || 0) * growth(yrsToRetire) +
    (yrsToRetire > 0 && accumRate > 0
      ? annualAdds * (growth(yrsToRetire) - 1) / accumRate
      : annualAdds * yrsToRetire);
  // p.sp must be the total combined household spending (US + out-of-country).
  // The params useMemo already sums these; raw-profile callers must sum before calling.
  const baseSpend = p.sp || 0;
  const ssAtRetire = computeHouseholdSS(p, retireAge);
  const rentalAtRetire = (p.ab > 0 ? p.ab : 0) + (p.propIncome || 0);
  const retireCalYear = CURRENT_YEAR + (retireAge - currentAge);
  const { total: otherIncAtRetire } = computeOtherIncome(p.otherIncomes, retireCalYear);
  const housingType = p.housingType || "own";
  let housingAtRetire = 0;
  if (housingType === "own" && p.mortBalance > 0) {
    const ms = mortgageSchedule(p.mortBalance, p.mortRate ?? 6.5, p.mortStart || "2020-01", p.mortTerm || 30, p.mortExtra || 0);
    housingAtRetire = mortgageAnnualPayments(ms).get(retireCalYear) || 0;
  } else if (housingType === "rent") {
    housingAtRetire = p.annualRent || 0;
  }
  const carveoutAtRetire = (p.carveouts || []).reduce((sum, c) => {
    return sum + (retireCalYear <= (c.endYear || 9999) ? (c.annual || 0) : 0);
  }, 0);
  // Nominal for the retirement year, then deflated to today's dollars like projectedPort.
  // Doing it this way also handles events entered in future dollars (inflate: false).
  const oneOffAtRetire = computeCashFlowEvents(p.cashFlowEvents, retireCalYear, inflRate * 100, CURRENT_YEAR).total
    / Math.pow(1 + inflRate, Math.max(0, retireCalYear - CURRENT_YEAR));
  const steadyDraw = Math.max(0, baseSpend - ssAtRetire - rentalAtRetire - otherIncAtRetire) + housingAtRetire + carveoutAtRetire;
  const initDrawEst = steadyDraw + oneOffAtRetire;
  const pctOf = (draw) => projectedPort > 0 ? (draw / projectedPort) * 100 : 0;
  const initWRpct = pctOf(initDrawEst);
  const steadyWRpct = pctOf(steadyDraw);
  return { initWRpct, steadyWRpct, projectedPort, initDrawEst, steadyDraw, oneOffAtRetire, baseSpend, ssAtRetire, rentalAtRetire,
    otherIncAtRetire, housingAtRetire, carveoutAtRetire, annualAdds, accumRate, nominalRate, inflRate, yrsToRetire };
}


function getStateBrackets(state, isMFJ) {
  const entry = STATE_BRACKETS[state];
  if (!entry) return null; // no state income tax
  return isMFJ ? entry.mfj : entry.single;
}


// One label map for the whole app, including the print report — see
// engine/withdrawalStrategies.js. It covers retired strategy ids too, so a
// migration notice or an old checkpoint can still name what the user used to
// have. Only LIVE_STRATEGIES shows up in the picker.
export const getStrategyLabel = (strategy) => STRATEGY_LABELS[strategy] || strategy;

export const getStrategyDescription = (strategy) => {
  const descriptions = {
    smart: "Smart Waterfall — tax-optimal bucket sequencing with a horizon-aware spending rule: Guyton-Klinger guardrails when more than 15 years remain (adaptive), Bengen 4% inflation-only when 15 or fewer years remain (steady). The split matches GK's own longevity-clause threshold so we hand off exactly where GK's safety brake would otherwise be disabled. Bucket sourcing: cash → taxable → pre-tax (bracket-capped) → Roth last.",
    gk: "Guyton‑Klinger guardrails — your spending adapts each year based on portfolio performance, so the simulation reflects how a real retiree would behave, not a robot spending a fixed amount no matter what.",
    bengen: "Bengen 4% Rule (1994) — set your initial spend, then inflation-adjust it every year and ignore the portfolio. Does NOT react to market moves. The portfolio CAN run out, making this an honest model of late-stage risk for fixed-budget retirees.",
    fixed: "Fixed Percentage Withdrawal — you withdraw a constant percentage of your portfolio each year, adjusting automatically with market movements.",
    ninety_five_rule: "95% Rule — spending can only decrease to 95% of last year's amount, otherwise tracks inflation.",
    vpw: "Variable Percentage Withdrawal (VPW) — spending is recalculated each year based on remaining portfolio and life expectancy, so the plan spends down to roughly zero by your plan-to age.",
  };
  return descriptions[resolveStrategy(strategy)] || descriptions.gk;
};

// Profiles.
// Personal data lives in AiRA_Profile.json, never hardcoded here.
// Use Export to save your data, Import to load it back.

// _defaultBucket / accountBucketPieces / expandAccountBuckets moved to
// ./engine/buckets.js (imported above) so buildWithdrawalWaterfall.js and
// bucketStrategy.js can share the same account→bucket partition instead of
// each growing their own copy.
export { _defaultBucket, accountBucketPieces, expandAccountBuckets, clampBucket };

export const BLANK_PROFILE = {
  label: "My Plan",
  name: "",
  dob: "",
  dobIsEstimate: false,        // true when dob was invented from the landing's age slider
  sex: "blended",              // "male" | "female" | "blended"
  stateOfResidence: "",
  currentAge: 50,
  retireAge: 60,
  endAge: 85,
  port: 1_000_000,
  contrib: 20,
  employerContrib: 0,           // annual employer contribution (fixed dollar amount, e.g. 401k match + profit sharing)
  // Savings that aren't tax-advantaged. Kept separate from `contrib` because
  // the destination bucket changes the tax outcome decades later: taxable
  // dollars get drawn at LTCG rates against a cost basis (waterfall Step 3),
  // while pre-tax dollars are ordinary income and also grow the RMD base at
  // rmdStartAge. Folding brokerage savings into the 401(k) field would quietly
  // turn one into the other.
  taxableContrib: 0,            // annual after-tax brokerage/savings contribution ($/yr)
  rothContrib: 0,               // annual Roth IRA contribution ($/yr, direct or backdoor)
  inf: 2.5,
  sp: 10_000,                   // US-domestic annual spending (subject to state tax when applicable)
  spOutOfCountry: 0,            // additive out-of-country annual spending (never state-taxed)
  spSpendOutofState: 0,         // legacy field — kept for profile-load migration; superseded by spOutOfCountry
  portfolioGoal: 1_000_000,
  ssAge: 67,
  ssb: 24_000,
  ssPia: 0,                    // own FRA/PIA annual amount — only needed if claiming before/after FRA
  // Spousal Social Security: additive, off by default so every existing
  // single-person profile still computes exactly the same as before this
  // feature existed.
  spouse: {
    enabled: false,
    // Spouse's date of birth. This is what makes per-person modeling
    // possible: every engine walks one `age`, the primary's, so without this
    // the spouse's Social Security claim age got compared against the
    // primary's age and a younger spouse ended up collecting years early.
    // Blank means the spouse is assumed to be the same age as the primary,
    // which is exactly the old behavior, so no saved profile changes until
    // this gets filled in.
    dob: "",
    ssb: 0,                    // spouse's own annual benefit at their claim age
    ssAge: 67,                 // spouse's own claim age, independent of the primary's
    ssPia: 0,                  // spouse's own FRA/PIA annual amount, for the spousal top-up
    // The spouse's own age at a modeled first death (the widow's-penalty
    // scenario). null means it's not modeled, so filing status stays constant
    // exactly like before. This is one user-entered age rather than a
    // mortality draw on purpose — that keeps it a deterministic event in a
    // known year instead of a variable threaded through 3,000 Monte Carlo paths.
    deathAge: null,
    // Whose age `deathAge` is. "spouse" (default) means the spouse dies and
    // the primary survives, the only case the model used to handle. "primary"
    // means the higher earner dies first, which is often more realistic since
    // they're frequently the older partner. This isn't just a label — the
    // survivor's identity decides the plan horizon, Medicare start, the
    // age-65 add-on, the RMD clock, and the survivor's own FRA. Default
    // preserves every existing plan.
    firstToDie: "spouse",
    // Survivor benefit.
    // Deemed filing doesn't apply to survivor benefits, so the survivor's own
    // retirement benefit and the survivor benefit are independent — either
    // can be claimed first and switched later. That flexibility doesn't exist
    // anywhere else in Social Security, and it only works if we track a
    // separate claim age. null means claim as soon as eligible (60, or the
    // death year if later).
    survivorClaimAge: null,
    // Optional: the survivor benefit as SSA quotes it at that claim age,
    // already reduced. Supplying it skips our reduction schedule entirely —
    // if you have the real number, use it. 0/blank means derive it from the
    // deceased's check or PIA instead.
    survivorBenefitAtClaim: 0,
    // Per-person contributions.
    // The household used to have one set of contribution fields running for
    // `retireAge - currentAge` — one retirement date for two people.
    // Splitting the amounts doesn't actually change anything (all three
    // engines just sum into buckets, so 24,500 + 18,000 in one field is
    // identical to two fields); splitting the stop date is the whole point.
    // One partner retiring at 62 while the other works to 67 used to lose —
    // or invent — five years of one salary's savings, compounded to retirement.
    //
    // Only job-bound streams live here. Brokerage savings stay
    // household-level (no employment link, no statutory cap), and the HSA
    // stays household-level too because its stop rule is Medicare
    // enrollment, not retirement.
    //
    // null/0 defaults reproduce the pre-feature result exactly for every
    // saved profile, same regression-safe idiom as spouse.dob above.
    retireAge: null,        // their retirement age, on their own clock. null = same as primary
    contrib: 0,             // their pre-tax 401(k)/403(b)/457(b) deferral
    employerContrib: 0,     // their employer match / profit sharing
    rothContrib: 0,         // their Roth IRA
  },
  ab: 0,
  useAb: true,
  smile: true,
  tax: true,
  real: true,
  twoHousehold: false,
  employerStartDate: "",
  gkFloor: 48_000,
  gkFloorSpendOutofState: 48_000,
  gkFloorPct: GK_FLOOR_DEFAULT_PCT,    // floor as % of core spend
  gkTarget: 72_000,
  gkCeiling: 100_000,
  gkCeilingPct: GK_CEILING_DEFAULT_PCT, // ceiling as % of core spend
  // Mortgage
  mortBalance: 0,
  mortRate: 5.0,
  mortStart: "2020-01",
  mortTerm: 30,
  mortExtra: 0,
  mortPI: 0,
  // Real estate (not in liquid portfolio)
  properties: [
    { id:"p1", label:"Primary Residence", value:0, mortgage:0, income:0 },
    { id:"p2", label:"Property 2",        value:0, mortgage:0, income:0 },
  ],
  // NEW:
  filingStatus: "mfj",          // "mfj" | "single" — drives federal brackets & std deduction
  reGrowthRate: 3.0,            // annual home/RE appreciation rate (%)
  useJointRmdTable: false,      // default: use Uniform Lifetime table
  cashRealReturn: 3.0,          // default return for cash/HYSA (percent)
  // ── Monte Carlo simulation mechanics (edited in the MC tab's ⚙ Advanced
  // Settings, not in Profile). Both defaults reproduce the previous
  // behaviour EXACTLY: MC_PATHS paths over the whole 1928–2025 history. That
  // matters — an absent field must not change any existing result, so
  // resolveSampleRange() returns null (= sample the full arrays) unless the
  // user actually narrows the window.
  mcPaths: MC_PATHS,            // stochastic paths per run
  mcRangeStart: SAMPLE_START_YEAR,
  mcRangeEnd: SAMPLE_END_YEAR,
  taxableBasisPct: 70,          // % of TODAY's taxable-brokerage balance that is cost basis (rest = unrealized LTCG)
  taxableYieldPct: 0,           // annual yield (%) taxed every year regardless of withdrawal — default off, 0 reproduces pre-feature behavior exactly
  taxableYieldOrdinaryPct: 50,  // % of that yield taxed as ordinary income (interest); rest at qualified-dividend/LTCG rates
  // Expense model
  housingType: "own",           // "own" | "rent" | "none"
  annualRent: 0,                // annual rent if housingType === "rent" (today's dollars)
  carveouts: [],                // [{id, label, annual, endYear}] Other Expenses (HOA, Insurance, etc.) in today's dollars; endYear = null for indefinite
  // Planned one-off / periodic costs — see computeCashFlowEvents in engine/expenses.js.
  // [{id, label, year, amount, recurEveryYears, recurUntilYear, inflate, deferrable}]
  cashFlowEvents: [],
  spSchedule: null,             // [{year, amount}] explicit per-year core spend from a detailed CSV import; null = use sp + strategy
  budgetLines: [],              // rows typed into the inline budget editor (Plan inputs > Spending & Expenses)
  spImportMeta: null,           // { mode, fileName, importedAt, total|years, essentialTotal } — display only, for the import summary card
  rothConversionTarget: "off",  // "off" | "12" | "22" | "24" | "irmaa"
  fafsaGuard: false,            // cap Roth conversions during college aid years — set true + fafsaEndYear to activate
  fafsaEndYear: null,           // last year to cap at 12% (FAFSA lookback window); e.g. 2034
  cssEndYear: null,             // last year to cap at 22% (CSS Profile window)
  conversionOverrides: [],      // [{id, year, amount}] manual per-year conversion amounts — populated in user's exported JSON
  // Account breakdown (feeds port total)
  accounts: [
    { id: "1", category: "pretax", name: "401(k)",      balance: 0, bucket: 2 },
    { id: "2", category: "roth",   name: "Roth IRA",    balance: 0, bucket: 3 },
    { id: "3", category: "taxable",name: "Brokerage",   balance: 0, bucket: 2 },
    { id: "4", category: "hsa",    name: "HSA",         balance: 0, bucket: 3 },
    { id: "5", category: "cash",   name: "Cash/Savings",balance: 0, bucket: 1 },
  ],
  // MC assumptions
  abReliability: 80,
  otherIncomes: [],   // [{ id, name, annual, startYear, endYear, growthMode:"pct"|"fixed", growthRate, growthAmount, growthCapYears, taxable }]
  abGrowth: 3.0,
  ssCola: 2.4,
  preRetireEq: 91,
  postRetireEq: 70,
  // null → the glidepath shifts at retireAge (previous behaviour). Set it to
  // separate "when I de-risk" from "when I stop working" — e.g. 90/10 until 67
  // while retiring at 62. See engine/glidepath.js.
  glidepathSwitchAge: null,
  fixedWithdrawalRate: 4.0,
  hcShockAge: 72,
  hcProb: 3.5,
  hcMin: 70_000,
  hcMax: 130_000,
  checkpoints: [],          // each: { id, date, value, note }
  earlyRetireTarget: 2_000_000,
  withdrawalStrategy: "gk",
  withdrawalBracketTarget: "22",  // "10"|"12"|"22"|"24"|"irmaa"|"off" — pretax ceiling in smart mode
  irmaaGuard: false,              // cap pretax draws below IRMAA tier-1 ceiling (ages 63+)
  ssTorpedoGuard: false,          // show SS torpedo landmine warnings in Withdrawal Plan tab
  rothEmergencyReserve: 0,        // never draw Roth below this $ floor
  // IRC 72(t) exceptions to the 10% early-distribution tax. Both default off:
  // the penalty is the law, and an exception is something the user has to
  // affirm. Only asked when retireAge < 59.5 (see the Early Retirement card).
  ruleOf55: false,                // separated from employer at 55+, plan not rolled over
  sepp72t: false,                 // a 72(t) SEPP is running
  sepp72tStartAge: null,          // series start age; must run to max(start+5, 59.5)
  orderingMode: "tax_reactive",   // "tax_reactive"|"custom"|"pretax_first" — which bucket drains first (orthogonal to strategy + guardrails)
  withdrawalOrder: ["cash", "taxable", "pretax", "roth"], // used only when orderingMode === "custom"
  // Bucket 1's target size, in years of spending — caps the yield sweep
  // above: once Bucket 1 reaches this size, Bucket 2 stops losing return to
  // it. Same concept BucketsTab already shows as its own (currently
  // localStorage-only) b1Years config; this profile-level default is what
  // the return engines read until that gets unified.
  b1Years: 2,
  // b2Years: not yet read by any engine (only b1Years caps the yield
  // sweep) — promoted alongside b1Years so BucketsTab's two "years of
  // spending" steppers share one source of truth instead of one living in
  // the profile and the other in localStorage for no principled reason.
  b2Years: 5,
  geminiApiKey: "",
  geminiModel: "",  // empty = use ai-analysis.js DEFAULT_GEMINI_MODEL
};

/* Real-world probability analogies, grouped into success-rate bands (min =
 * band floor in %). Several entries per band feed the revolving display so
 * users get varied context for what their number actually means. Every `stat`
 * is the analogy's own real-world probability, stated approximately but
 * honestly — users should be able to sanity-check the comparison, not just
 * take the vibe. (Fixed two that were wrong: "calling heads three times in a
 * row" is 12.5%, not the 87.5% chance of NOT flipping three heads in a row —
 * and the 4-year college graduation rate is about 46%, nowhere near 80%.) */
const ANALOGUES = [
  {
    min: 95,
    text: "As reliable as a commercial flight landing safely",
    emoji: "✈️",
    color: "var(--positive)",
  },
  {
    min: 95,
    text: "A tour pro sinking a three-foot putt",
    stat: "≈99%",
    emoji: "⛳",
    color: "var(--positive)",
  },
  {
    min: 95,
    text: "A next-day weather forecast being right",
    stat: "≈95%",
    emoji: "☀️",
    color: "var(--positive)",
  },
  {
    min: 90,
    text: "Odds a 50-year-old reaches age 65 — F-You Money territory",
    stat: "≈9 in 10",
    emoji: "💪",
    color: "var(--positive)",
  },
  {
    min: 90,
    text: "An NFL kicker making an extra point",
    stat: "≈94%",
    emoji: "🏈",
    color: "var(--positive)",
  },
  {
    min: 90,
    text: "A full year of driving without a collision claim",
    stat: "≈94%",
    emoji: "🚗",
    color: "var(--positive)",
  },
  {
    min: 85,
    text: "Not flipping three heads in a row",
    stat: "87.5%",
    emoji: "🪙",
    color: "#6ee7b7",
  },
  {
    min: 85,
    text: "A U.S. high-school freshman graduating on time",
    stat: "≈87%",
    emoji: "🎓",
    color: "#6ee7b7",
  },
  {
    min: 85,
    text: "No ace showing up in two dealt cards",
    stat: "≈85%",
    emoji: "🃏",
    color: "#6ee7b7",
  },
  {
    min: 80,
    text: "Avoiding a 1 on a single die roll",
    stat: "≈83%",
    emoji: "🎲",
    color: "var(--accent-gold)",
  },
  {
    min: 80,
    text: "Staying dry when the forecast says 20% chance of rain",
    stat: "80%",
    emoji: "☔",
    color: "var(--accent-gold)",
  },
  {
    min: 75,
    text: "About the odds an NBA player makes a free throw",
    stat: "≈78%",
    emoji: "🏀",
    color: "var(--accent-gold)",
  },
  {
    min: 75,
    text: "A U.S. flight arriving on time",
    stat: "≈78%",
    emoji: "🛫",
    color: "var(--accent-gold)",
  },
  {
    min: 75,
    text: "Not drawing a spade from a full deck",
    stat: "75%",
    emoji: "♠️",
    color: "var(--accent-gold)",
  },
  {
    min: 70,
    text: "Odds a new business survives its first two years",
    stat: "≈70%",
    emoji: "🏢",
    color: "var(--chart-7)",
  },
  {
    min: 70,
    text: "Not flipping two heads in a row",
    stat: "75%",
    emoji: "🪙",
    color: "var(--chart-7)",
  },
  {
    min: 60,
    text: "Graduating college within six years of starting",
    stat: "≈64%",
    emoji: "🎓",
    color: "var(--chart-3)",
  },
  {
    min: 60,
    text: "Rolling 3 or higher on a single die",
    stat: "≈67%",
    emoji: "🎲",
    color: "var(--chart-3)",
  },
  {
    min: 50,
    text: "A literal coin flip — retirement shouldn't be one",
    stat: "50%",
    emoji: "🪙",
    color: "var(--chart-1)",
  },
  {
    min: 50,
    text: "Guessing a card's color correctly",
    stat: "50%",
    emoji: "🎴",
    color: "var(--chart-1)",
  },
  {
    min: 0,
    text: "Worse than a coin flip — plan needs structural changes, not luck",
    emoji: "😰",
    color: "var(--negative)",
  },
  {
    min: 0,
    text: "Lower spending, later retirement, or different allocation beat hoping for good markets",
    emoji: "⚠️",
    color: "var(--negative)",
  },
];


// Math core
function mulberry32(seed) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function normalDraw(mean, vol, rand) {
  const u = Math.max(rand(), 1e-10);
  return (
    mean + vol * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rand())
  );
}
function clip(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}
function bootstrapDraw(arr, rand) {
  return arr[Math.floor(rand() * arr.length)];
}
/**
 * Resolve the user's historical-sampling window into array offsets.
 *
 * Returns `null` when the window is the full history, which is the signal for
 * every sampler to take its ORIGINAL `Math.floor(rand() * SP500.length)` path.
 * That is deliberate: the full range must reproduce the pre-feature behaviour
 * exactly — same rand() call count, same index mapping — so existing seeds,
 * saved results and the whole test suite are unaffected by this feature
 * existing. Only a genuinely narrowed window changes the indexing.
 *
 * Bounds come from SAMPLE_START_YEAR/SAMPLE_END_YEAR (the length of the
 * Damodaran arrays), never from a literal, and a reversed or out-of-range
 * input falls back to the full history rather than throwing.
 */
function resolveSampleRange(p) {
  const lo = Number(p?.mcRangeStart);
  const hi = Number(p?.mcRangeEnd);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  const start = Math.max(0, Math.round(lo) - SAMPLE_START_YEAR);
  const end   = Math.min(SP500.length - 1, Math.round(hi) - SAMPLE_START_YEAR);
  if (end <= start) return null;                 // degenerate → full history
  if (start === 0 && end === SP500.length - 1) return null;  // full → fast path
  return { start, count: end - start + 1, lo: SAMPLE_START_YEAR + start, hi: SAMPLE_START_YEAR + end };
}
// One shared index pick, so portReturn and drawYearBundle can't drift apart on
// what "sample a year" means (rule 8's spirit: one owner for one decision).
function sampleYearIndex(rand, range) {
  return range ? range.start + Math.floor(rand() * range.count)
               : Math.floor(rand() * SP500.length);
}
function portReturn(age, rand, preRetireEq, postRetireEq, switchAge, range = null) {
  // The glidepath switches at `glidepathSwitchAge`, defaulting to the user's
  // retirement age. Callers pass the already-resolved age from
  // resolveGlidepathSwitchAge — see engine/glidepath.js for why this is
  // single-sourced (four call sites had drifted onto a hardcoded 62 before).
  const eqW = glidepathEquityWeight(age, preRetireEq, postRetireEq, switchAge);
  // Paired bootstrap sampling: one shared random index draws the same
  // calendar year's stock and bond return together, so the historical
  // stock/bond correlation is preserved — including the flight-to-quality
  // flip in crashes (2008: SP500[i]=-36.55%, BONDS[i]=+20.10%) and the rare
  // double-down (2022: SP500[i]=-18.04%, BONDS[i]=-17.83%). Drawing them
  // independently, like the old code did, effectively forces
  // cov(stocks, bonds) = 0 and invents year combinations that never
  // happened. Needs SP500 and BONDS to be equal-length and year-aligned;
  // engine/expectedReturn.js guarantees both (Damodaran 1928-2025, 98 pairs).
  const i = sampleYearIndex(rand, range);
  return eqW * SP500[i] + (1 - eqW) * BONDS[i];
}
// Paired return + inflation for one retirement year. Same shared index
// selects SP500[i], BONDS[i], and INFL[i], so a year like 1974 draws stocks
// (-25.9%), bonds (+1.99%) and inflation (+11.0%) together — the canonical
// stagflation-year triple. The old code drew INFL independently via a second
// rand() call, so a bootstrap could pair 2008's crash with 2015's 0.1% CPI,
// which understates how bad stagflation actually is for retirement spending
// power.
//
// The second `rand()` call below is thrown away on purpose — it just keeps
// the rand() call count per retirement year matching the old (independent-INFL)
// version, so the same seed still produces the same draw sequence and the
// test suite doesn't need rebalancing over an RNG-order shift. If we ever
// drop that RNG-ordering discipline (see the comment above `netNeed offset`
// in runMC), this can go.
function drawYearBundle(age, rand, preRetireEq, postRetireEq, switchAge, range = null) {
  const eqW = glidepathEquityWeight(age, preRetireEq, postRetireEq, switchAge);
  const i = sampleYearIndex(rand, range);
  rand(); // rand()-count shim — see comment above
  return {
    ret: eqW * SP500[i] + (1 - eqW) * BONDS[i],
    inflY: INFL[i],
    // The sampled historical-year index, additive (existing {ret, inflY}
    // destructuring callers are unaffected). Exposed so the 3-Bucket strategy
    // (bucketStrategy.js) can compute Bucket 1/2/3 returns from the SAME
    // sampled year as `ret` — reusing `i` instead of drawing a second random
    // year preserves the paired stock/bond correlation this function exists
    // to guarantee (see the paired-bootstrap comment above portReturn).
    i,
  };
}


function guytonKlingerWithdrawal(
    portfolioValue,
    initialWR,
    lastWithdrawal,
    lastReturn,
    inflationRate,
    floor,
    ceiling,
    yearsRemaining = Infinity,
    incomeOffset = 0,
    fixedCosts = 0,
    // Optional sink for WHAT the guardrail did this year. The function has
    // always applied the cut/raise and then thrown that fact away by returning
    // only a dollar figure, so nothing downstream — not the schedule, not the
    // chart, not the "how often do the guardrails bind?" summary — could say
    // whether a year was cut, raised, or left alone without re-deriving it from
    // the paper's rules (a second copy that would drift). Callers that pass
    // nothing keep the old numeric return exactly.
    out = null
  ) {
    if (out) { out.event = null; out.reason = null; out.currentWR = null; }
    // NaN guards — fall back to safe values if any parameter is invalid
    if (isNaN(portfolioValue) || portfolioValue <= 0) return floor || 0;
    if (isNaN(lastWithdrawal)) lastWithdrawal = floor || 0;
    if (isNaN(lastReturn)) lastReturn = 0;
    if (isNaN(inflationRate)) inflationRate = 0.02;
    if (isNaN(initialWR)) initialWR = 0.04;
    if (isNaN(incomeOffset)) incomeOffset = 0;
    if (isNaN(fixedCosts)) fixedCosts = 0;

    // GK Rule: Withdrawal/Inflation — adjust by CPI only when prior-year return ≥ 0,
    // and cap the inflation pass-through per the original paper.
    const cappedInfl = Math.min(GK_INFLATION_CAP, inflationRate);
    let w =
      lastReturn >= 0 ? lastWithdrawal * (1 + cappedInfl) : lastWithdrawal;

    // Guard: if the baseline draw is already fully covered by income at
    // retirement (initialWR <= 0), skip the band adjustments entirely —
    // otherwise currentWR <= 0.8*0 is always true and fires a meaningless
    // +10% raise every year regardless of portfolio health.
    if (initialWR > 0) {
      // This ratio has to track the same quantity initialWR was calibrated
      // against — net portfolio need (gross withdrawal minus SS/annuity/
      // otherIncome, plus housing/carveouts), not the gross withdrawal `w`.
      // Otherwise a retiree whose SS starts at retirement gets a currentWR
      // far above initialWR every year, which triggers a bogus
      // capital-preservation cut no matter how healthy the portfolio is.
      const netNeed = Math.max(0, w - incomeOffset) + fixedCosts;
      const currentWR = portfolioValue !== 0 ? netNeed / portfolioValue : 0;

      // GK Prosperity Rule: WR drops 20% below initial → +10%
      if (currentWR <= initialWR * (1 - GK_BAND_PCT)) {
        w *= 1 + GK_ADJUST_PCT;
        if (out) { out.event = "raise"; out.reason = "prosperity"; }
      }
      // GK Capital Preservation Rule: WR rises 20% above initial → -10%.
      // GK Longevity Rule: skip the cut when ≤15 years remaining.
      else if (currentWR >= initialWR * (1 + GK_BAND_PCT) && yearsRemaining > GK_LONGEVITY_YEARS) {
        w *= 1 - GK_ADJUST_PCT;
        if (out) { out.event = "cut"; out.reason = "preservation"; }
      } else if (out && currentWR >= initialWR * (1 + GK_BAND_PCT)) {
        // The band was crossed but the longevity rule blocked the cut — worth
        // distinguishing from "nothing happened", because it is the guardrail
        // deliberately standing down near the end of the plan.
        out.reason = "longevity-hold";
      }
      if (out) out.currentWR = currentWR;
    }

    // Custom safety belt (not from the GK paper): clamp to floor/ceiling.
    //
    // A bind here reports its OWN event type, `constrained`, rather than folding
    // into "cut"/"raise". Different cause, different remedy: a band rule fires
    // because the portfolio drifted from the reference withdrawal rate, whereas
    // this fires because the user's own gkFloorPct/gkCeilingPct settings left the
    // rule nowhere to go. Merging them would let the headline number claim
    // market-driven adjustments that were really produced by a slider — and it
    // fixes an inverted signal, since tightening the band makes binding MORE
    // frequent while leaving cutRate/raiseRate unchanged or lower.
    //
    // Detection compares `w` BEFORE the clamp. Testing the returned value against
    // the band can never show a bind, because it has already been pulled inside
    // it. The 1e-9 tolerance absorbs float noise on a value that landed exactly
    // on a limit through multiplication.
    const clampedW = Math.max(floor || 0, Math.min(ceiling || Infinity, w));
    if (out && Number.isFinite(w)) {
      if (Number.isFinite(ceiling) && w > ceiling + 1e-9) {
        out.event = "constrained";
        out.reason = "ceiling-bind";
      } else if (floor != null && w < floor - 1e-9) {
        out.event = "constrained";
        out.reason = "floor-bind";
      }
    }
    return clampedW;
}

/**
 * Realized capital gain from a taxable-brokerage draw, using average-cost
 * basis tracking (not per-lot). g = draw × (1 − basis/balance) — the fraction
 * of the account that is unrealized gain. Guards balance<=0 and basis>=balance
 * (fully-basis accounts realize $0 gain). Shared by runMC and the deterministic
 * conversion-delta helper; buildWithdrawalWaterfall.js keeps its own copy since
 * it does not import from App.jsx.
 */
function realizedGainFromDraw(draw, balance, basis) {
  if (!draw || draw <= 0 || !balance || balance <= 0) return 0;
  const frac = Math.max(0, 1 - (basis || 0) / balance);
  return draw * frac;
}

function calcYearTax(
  age,
  yr,
  withdrawalAmount,
  ssIncome,
  RentalIncome,
  rmdIncome,
  conversionAmount,
  isTwoHousehold,
  inflationRate,
  filingStatus = "mfj",
  stateOfResidence = "NJ",
  ltcgAmount = 0,
  // IRMAA 2-year lookback: MAGI from two years ago (SSA charges year T's
  // premium off the tax return filed two years prior). When the caller has
  // that history it gets passed here; the current year `yr` still selects the
  // bracket table, only the MAGI used to walk the table changes. `null`
  // (default) keeps the old same-year-MAGI behavior for any caller that
  // hasn't wired the history through yet.
  magiLookback = null,
  // Spouse's age in this same tax year, or null when unknown. Only the
  // per-person amounts read it: the age-65 standard-deduction add-on and the
  // OBBBA senior bonus. null reproduces the old behavior (one age standing in
  // for both filers), so callers that haven't passed a spouse age are unaffected.
  spouseAge = null
) {
  // Replace any NaN arguments with 0
  withdrawalAmount = isNaN(withdrawalAmount) ? 0 : withdrawalAmount;
  ssIncome = isNaN(ssIncome) ? 0 : ssIncome;
  RentalIncome = isNaN(RentalIncome) ? 0 : RentalIncome;
  rmdIncome = isNaN(rmdIncome) ? 0 : rmdIncome;
  conversionAmount = isNaN(conversionAmount) ? 0 : conversionAmount;
  inflationRate = isNaN(inflationRate) ? 0.025 : inflationRate;
  ltcgAmount = isNaN(ltcgAmount) ? 0 : Math.max(0, ltcgAmount || 0);

  const isMFJ = filingStatus !== "single";
  const otherIncome =
    (withdrawalAmount || 0) +
    (RentalIncome || 0) +
    (rmdIncome || 0) +
    (conversionAmount || 0);
  // IRC §86 provisional-income tiers: 0% / 50% / 85% of SS taxable by income
  // level. Realized capital gains count toward provisional income (they're
  // part of MAGI), so they get added to the "other income" side of the
  // SS-taxability test even though they aren't part of ordinary
  // `otherIncome`/`totalIncome` below.
  const taxableSS = taxableSocialSecurity(ssIncome, otherIncome + ltcgAmount, isMFJ);
  const totalIncome = taxableSS + otherIncome; // ordinary income total (excludes LTCG)
  const inflationFactor = Math.pow(1 + inflationRate, Math.max(0, yr - CURRENT_YEAR));

  // IRMAA MAGI = AGI + tax-exempt interest; untaxed SS doesn't get added back.
  // AGI includes the full realized gain (pre-deduction), unlike taxableIncome
  // below. It's computed here, ahead of every deduction, for two reasons: it
  // genuinely doesn't depend on deductions, and the OBBBA senior bonus
  // deduction's phase-out is keyed to MAGI, so MAGI needs to exist first.
  // Keeping it above the deduction lines also makes it structurally
  // impossible for a deduction to ever net out of MAGI — IRMAA doesn't take one.
  const magi = totalIncome + ltcgAmount;

  // Standard deduction (including the age-65+ add-on), inflation-adjusted
  // forward. Single source: getStandardDeduction, see TAX_REFERENCE.md.
  const stdDeduction = getStandardDeduction(age, filingStatus, inflationFactor, spouseAge);
  // OBBBA senior bonus deduction (2025-2028 only, $0 from 2029) — a separate,
  // additive deduction, not inflation-indexed on purpose, stacked on top of
  // the standard deduction and its age-65 add-on. It only reduces taxable
  // income; `magi` above is already fixed and never gets reduced by it.
  const seniorBonus = getSeniorBonusDeduction(age, filingStatus, magi, yr, spouseAge);
  const totalDeduction = stdDeduction + seniorBonus;
  const taxableIncome = Math.max(0, totalIncome - totalDeduction);
  // LTCG stacks on top of ordinary income (the IRS stacking rule): gains
  // occupy the taxable-income band from `taxableIncome` up to
  // `taxableIncome + gainTaxable`. If ordinary income didn't fully use up the
  // deductions, gains soak up whatever's left of them first.
  const gainTaxable = Math.max(0, totalIncome + ltcgAmount - totalDeduction) - taxableIncome;

  // Select federal brackets by filing status
  const rawBrackets = isMFJ ? FED_BRACKETS_2026_MFJ : FED_BRACKETS_2026_SINGLE;
  const fedBrackets = idxB(rawBrackets, inflationFactor);
  const fedTaxOrdinary = progTax(taxableIncome, fedBrackets);

  // LTCG bracket walk over the stacked interval [taxableIncome, taxableIncome+gainTaxable).
  const ltcgBrackets = idxB(isMFJ ? LTCG_BRACKETS_2026_MFJ : LTCG_BRACKETS_2026_SINGLE, inflationFactor);
  const ltcgTax = Math.round(
    progTax(taxableIncome + gainTaxable, ltcgBrackets) - progTax(taxableIncome, ltcgBrackets)
  );

  // NIIT (IRC §1411): 3.8% of the lesser of net investment income (LTCG here)
  // or the excess of MAGI over the statutory (not inflation-indexed) threshold.
  const niitThreshold = isMFJ ? NIIT_THRESHOLD_MFJ : NIIT_THRESHOLD_SINGLE;
  const niit = ltcgAmount > 0
    ? Math.round(NIIT_RATE * Math.min(ltcgAmount, Math.max(0, magi - niitThreshold)))
    : 0;

  // LTCG tax + NIIT fold into the federal total so the funding-identity math
  // downstream (totalTax = fedTax + stateTax + irmaa) keeps working unchanged;
  // the components also come back separately (ltcgTax, niit) for the UI to show.
  const fedTax = fedTaxOrdinary + ltcgTax + niit;
  let stateTax = 0;

  if (!isTwoHousehold) {
    const stateBr = getStateBrackets(stateOfResidence, isMFJ);
    // NJ fully exempts Social Security from state tax (CLAUDE.md rule 4) —
    // taxableSS is already folded into totalIncome/taxableIncome above, so
    // back it out of the state base specifically. Only NJ's exemption is
    // modeled explicitly (the app's documented domicile); other states keep
    // their existing (SS-inclusive) behavior.
    const stateTaxSS = stateOfResidence === "NJ" ? taxableSS : 0;
    // Most states tax capital gains as ordinary income (no LTCG preferential
    // rate), so add the realized gain to the state taxable base.
    if (stateBr) stateTax = Math.round(progTax(Math.max(0, taxableIncome - stateTaxSS) + ltcgAmount, idxB(stateBr, inflationFactor)));
  }
      // IRMAA charge uses the 2-year-old MAGI when the caller supplied one,
      // otherwise falls back to this year's own MAGI (the pre-lookback behavior).
      const irmaaMagi = (typeof magiLookback === "number" && !isNaN(magiLookback)) ? magiLookback : magi;
      // Medicare starts at each person's own 65. `medicareHeads` is 0 before
      // either qualifies, 1 during an age gap, 2 once both are on Medicare —
      // so an age-gapped couple isn't charged two surcharges starting from
      // the older one's 65th birthday.
      const medicareHeads = personsAtLeastAge(age, spouseAge, isMFJ, 65);
      const irmaa = medicareHeads > 0
        ? irmaaCost(irmaaMagi, yr, inflationRate, isMFJ, medicareHeads)
        : 0;
      const totalTax = fedTax + stateTax + irmaa;
      const effectiveRate = (totalIncome + ltcgAmount) > 0 ? totalTax / (totalIncome + ltcgAmount) : 0;
      let marginalBracket = 0;

  for (const b of fedBrackets) {
    if (taxableIncome > b.lo) marginalBracket = b.rate;
    else break;
  }
  return {
    fedTax, stateTax, irmaa, totalTax, effectiveRate, marginalBracket, taxableIncome,
    ltcgTax, niit, realizedGain: Math.round(ltcgAmount),
    // Deduction components, returned separately so the UI can explain the
    // 2028→2029 jump when the OBBBA senior bonus sunsets.
    stdDeduction, seniorBonus,
    // This year's own MAGI (never the lookback substitution) — callers store
    // this in a per-age history so it becomes the magiLookback input two
    // years from now.
    magi,
  };
}

/**
 * Standard deduction (MFJ/Single), with the age-65+ add-on, inflated forward.
 * Canonical source: TAX_REFERENCE.md → "Standard Deduction (MFJ 2026)".
 * One source of truth — calcYearTax and the sourcing waterfall both call
 * this instead of re-declaring the numbers themselves.
 */
function getStandardDeduction(age, filingStatus, inflFactor, spouseAge = null) {
  const mfj = filingStatus !== "single";
  let sd = mfj ? 32_200 : 16_100;          // base, 2026
  // The age-65 add-on is per filer ($1,650 each). It used to get granted to
  // both spouses as soon as the primary turned 65, which overstated the
  // deduction by $1,650/yr for the whole age gap. `spouseAge = null` keeps the
  // old behavior for profiles with no spouse birthdate. See engine/ages.js.
  const seniors = personsAtLeastAge(age, spouseAge, mfj, 65);
  sd += seniors * 1_650;
  return Math.round(sd * inflFactor);
}

/**
 * IRMAA MAGI ceiling for a given tier, inflated forward.
 * Canonical source: TAX_REFERENCE.md → "IRMAA Thresholds (MFJ 2026)".
 * tier 1 = base tier below which there is no Medicare surcharge.
 */
function getIrmaaCeiling(tier, filingStatus, inflFactor) {
  const mfj = filingStatus !== "single";
  const base = mfj ? 218_000 : 109_000;    // tier-1 MAGI ceiling, 2026
  return Math.round(base * inflFactor);
}

/**
 * Returns the TAXABLE INCOME ceiling (after std deduction) for a given bracket target.
 * Values are 2026 estimates, inflated by inflFactor for future years.
 * The "irmaa" target delegates to getIrmaaCeiling so the threshold has one home.
 */
function getBracketCeiling(target, filingStatus, inflFactor) {
  if (target === "irmaa") return getIrmaaCeiling(1, filingStatus, inflFactor);
  const mfj = filingStatus !== "single";
  const ceilings = mfj
    ? { "10": 24_800, "12": 100_800, "22": 211_400, "24": 403_550, "32": 512_450, "35": 768_700, "37": Infinity }
    : { "10": 12_400, "12":  50_400, "22": 105_700, "24": 201_800, "32": 256_225, "35": 640_600, "37": Infinity };
  const base = ceilings[target] ?? ceilings["22"];
  return base === Infinity ? Infinity : Math.round(base * inflFactor);
}

// Optional flat pre-retirement return from Assumptions (percent, nominal). Blank/NaN
// means "not set": callers fall back to the historical draw / expected return.
function flatPreRetReturn(p) {
  const v = p.preRetireReturn;
  return v === "" || v == null || !Number.isFinite(Number(v)) ? null : Number(v) / 100;
}

// One prescribed sequence year. `entry` is a bare equity return, or
// {stock, bond?, infl?} (a real historical year with all three paired).
// Bond/inflation are always drawn so the rand() stream is identical either way;
// a paired value then overrides the draw.
function seqYear(entry, eqW, rand) {
  const stock = typeof entry === "number" ? entry : entry.stock;
  const bondDraw = bootstrapDraw(BONDS, rand);
  const inflDraw = bootstrapDraw(INFL, rand);
  const bond = entry?.bond ?? bondDraw;
  return { stock, bond, infl: entry?.infl ?? inflDraw, r: eqW * stock + (1 - eqW) * bond };
}

// Real calendar years `startYear...` as paired {stock, bond, infl, year} entries
// (1928-2025), for seqOverride. Stops at the end of the dataset.
export function historicalSequence(startYear, years = 13) {
  const i0 = startYear - SAMPLE_START_YEAR;
  return Array.from({ length: years }, (_, k) => i0 + k)
    .filter((i) => i >= 0 && i < SP500.length)
    .map((i) => ({ year: SAMPLE_START_YEAR + i, stock: SP500[i], bond: BONDS[i], infl: INFL[i] }));
}

// seqOverride: optional array of equity returns (decimals) prescribed for the
// first N retirement years — used by the Stress Test to force the 2000-2012
// sequence at retirement. When supplied, year y's equity component is
// seqOverride[y] (blended with a bootstrapped bond draw at the same
// age-based equity weight portReturn uses); past the array length, returns
// fall back to the normal bootstrap. Everything else — tax (calcYearTax,
// including the non-resident/state toggle), RMDs, bucket sourcing, strategy —
// is identical to a normal run, so the stress pivot can never diverge from the MC.
function runMC(p, endAge, N = MC_PATHS, seed = 42, useGK = true, seqOverride = null, seqToday = false, keepFailures = false) {
  const rand = mulberry32(seed);
  // "Tax drag" master toggle. Off (p.tax === false) zeroes all tax — federal,
  // state, IRMAA, and Roth-conversion cost — for a pure pre-tax view of
  // portfolio dynamics. calcYearTax still runs (its taxableIncome /
  // marginalBracket feed sourcing and conversion sizing), just no tax dollars
  // get withdrawn. Since the Stress Test delegates to runMC, this toggle
  // governs MC and Stress the same way. Only an explicit boolean false turns
  // it off — a numeric/true value (default) keeps tax on.
  const taxEnabled = p.tax !== false;
  // Account draw order (which bucket drains first) — constant for the whole
  // run. Default "tax_reactive" resolves to cash → taxable → pretax → roth,
  // the old hardcoded sequence. Shared resolver keeps this matching
  // buildWithdrawalWaterfall's smart scenario.
  const drawOrderMC = resolveDrawOrder(p.orderingMode, p.withdrawalOrder);
  // 3-Bucket asset-location strategy (additive layer — see
  // engine/bucketStrategy.js header comment). Computed once from today's
  // accounts, held constant for the whole path per the documented
  // simplification in buildWithdrawalWaterfall.js's identical precompute.
  // Already-retired users enter the age they actually retired at, which is in
  // the past. Balances are always today's, so starting the drawdown there
  // would replay years that already happened. See effectiveRetireAge.
  const retAgeMC = effectiveRetireAge(p.retireAge, p.currentAge);
  // Equity glidepath switch age — resolved once here so the accumulation
  // loop, the normal drawdown draw, and the stress-sequence branch below
  // can't disagree (the stress branch used to hardcode 62). Uses the
  // effective retirement age as the fallback, matching every other age in
  // this engine.
  // Share of pre-tax sitting in a former-employer plan — the only slice Rule
  // of 55 can reach. Constant for the run (detected off starting balances).
  const ruleOf55ShareMC = detectEmployerPlan(p.accounts).share;
  // Calendar-year Rule-of-55 test, computed once per run. Separation in or
  // after the year the employee turns 55 qualifies — `retireAge >= 55` was
  // stricter than the statute actually requires. See ruleOf55SeparationQualifies.
  const ruleOf55OkMC = ruleOf55SeparationQualifies({
    dob: p.dob, birthYear: p.birthYear, currentAge: p.currentAge, retireAge: retAgeMC,
  });
  const glideSwitchAgeMC = resolveGlidepathSwitchAge({ ...p, retireAge: retAgeMC });
  // Historical sampling window for this run (null = full 1928–2025 history).
  // Resolved once so the accumulation loop, the retirement draw, and any
  // future sampler all use the same window — the same single-resolution
  // discipline the glidepath switch age above needs.
  const sampleRangeMC = resolveSampleRange(p);
  const accYrs = Math.max(0, retAgeMC - p.currentAge);
  // The horizon follows whoever is alive. When the primary dies first and a
  // younger spouse survives, the money has to last until the survivor reaches
  // endAge, so the projection runs past the age the primary would have
  // reached. Same as endAge whenever no first death is modeled or the primary
  // is the survivor, so no existing plan changes length.
  const planEndMC = planEndAgeOnPrimaryClock(p, endAge);
  const retYrs = planEndMC - retAgeMC;
  const results = [];
  // Guardrail-binding statistics, accumulated across every path so the
  // Guardrails view can say how often the GK rules actually fire — the "cuts in
  // X% of scenarios, raises in Y%" summary. Counted where the adjustment is
  // APPLIED (guytonKlingerWithdrawal's `out` sink), never re-derived from the
  // spend path afterwards; a re-derivation would have to re-implement the
  // bands, the longevity rule and the income offset, and would drift.
  //
  // `constrained` / `pathsWithConstrained` track the THIRD event type: years the
  // floor/ceiling band bound, which is a settings effect rather than a market
  // effect and therefore never folded into cuts/raises.
  const gkStats = { paths: 0, pathsWithCut: 0, pathsWithRaise: 0, cuts: 0, raises: 0, longevityHolds: 0, constrained: 0, pathsWithConstrained: 0, ceilingBinds: 0, floorBinds: 0, spendMinReal: Infinity, spendMaxReal: 0, cutCounts: [] };
  const gkFloor = p.gkFloor || GK_FLOOR_FALLBACK;
  const gkCeiling = p.gkCeiling || GK_CEILING_FALLBACK;
  // resolveStrategy, not `|| "gk"`. A retired id (an old saved profile) or a
  // typo would otherwise match no branch in the chain below and leave `sp`
  // unassigned for the whole run — spending never inflation-adjusts and the
  // smile quietly deflates it about 1%/yr with no error. See
  // engine/withdrawalStrategies.js.
  const withdrawalStrategy = resolveStrategy(p.withdrawalStrategy);

  // User settings for cash return and RMD table
  const cashRealReturn = (p.cashRealReturn ?? 3.0) / 100;
  // Joint & Last Survivor is only legal while the much-younger spouse is
  // alive and the sole beneficiary, so this has to be a per-year test once a
  // first death is modeled — a hoisted constant would keep using the longer
  // divisors and understate every post-death RMD. filesJointlyAt carries both
  // conditions (filing status and the death year).
  const useJointTableAt = (age) => (p.useJointRmdTable ?? false) && filesJointlyAt(p, age);
  const UNIFORM_TABLE = RMD_DIV;

  // SECURE 2.0 RMD start age (72/73/75 by birth year), user override wins
  const rmdStartAge = (typeof p.rmdStartAge === "number" && p.rmdStartAge > 0)
    ? p.rmdStartAge
    : getRmdStartAge({ dob: p.dob, birthYear: p.birthYear, currentAge: p.currentAge });

  // Expected inflation for tax-bracket/IRMAA indexing. Brackets have to
  // compound at the assumed long-run rate, not a single bootstrapped year's
  // draw (see inflY below).
  const taxInfl = (p.inf ?? 2.5) / 100;

  // Percent of today's taxable-brokerage balance that is cost basis (the
  // user reads this off their brokerage statement). The rest is unrealized
  // gain, realized proportionally (average-cost basis, not per-lot) as the
  // account gets drawn down.
  const taxableBasisPct = Math.max(0, Math.min(100, p.taxableBasisPct ?? 70));

  // Pre-compute the actual annual mortgage cash cost per calendar year
  // (including extra payments and the partial payoff year), constant across
  // all paths since the mortgage is path-independent.
  let mortByYear = new Map();
  if (p.mortBalance > 0) {
    const ms = mortgageSchedule(
      p.mortBalance,
      p.mortRate ?? 6.5,
      p.mortStart || "2020-01",
      p.mortTerm || 30,
      p.mortExtra || 0
    );
    mortByYear = mortgageAnnualPayments(ms);
  }

  // One-off inflows (inheritance, home sale, pension lump sum) landing during
  // the accumulation years, precomputed once since they're path-independent
  // like the mortgage. Only the retirement loop used to process
  // cashFlowEvents, so a lump sum arriving before retirement got silently
  // dropped from every path — a user testing a $1M future inheritance would
  // see the numbers not move at all. Accumulation year y is calendar year
  // CURRENT_YEAR + y; the retirement loop starts at CURRENT_YEAR + accYrs, so
  // an event fires in exactly one phase.
  const preRetFlat = flatPreRetReturn(p);
  const seqOffset = seqToday ? accYrs : 0; // retirement years continue after the accumulation years
  const failedRuns = [];
  const accInflowByYear = [];
  for (let y = 0; y < accYrs; y++) {
    accInflowByYear.push(computeCashFlowEvents(p.cashFlowEvents, CURRENT_YEAR + y, p.inf ?? 2.5, CURRENT_YEAR));
  }

  for (let i = 0; i < N; i++) {
    // Initialize buckets from p.accounts for this path
    let pretax = 0, roth = 0, taxable = 0, cash = 0;
    for (const acct of (p.accounts || [])) {
      const bal = acct.balance || 0;
      if (acct.category === "pretax") pretax += bal;
      else if (acct.category === "roth") roth += bal;
      else if (acct.category === "taxable") taxable += bal;
      // "hsa" and any unrecognized category fall through to cash. This used
      // to be `else if (category === "cash")`, which silently dropped every
      // hsa-category balance from the simulation — BLANK_PROFILE ships an HSA
      // account by default, so any user with an HSA had it vanish from their
      // portfolio. accumulateToRetirement() already bucketed hsa into cash
      // via a catch-all else, so the two engines disagreed before. Now they match.
      else cash += bal;
    }
    // Basis is a % of today's taxable balance, fixed in dollars from here on
    // — accumulation-phase growth (below) increases the balance but not the
    // basis (growth is unrealized gain), so the basis fraction shrinks by
    // retirement even though no draw has happened yet.
    let taxableBasis = taxable * (taxableBasisPct / 100);
    let totalPort = pretax + roth + taxable + cash;

    // Accumulation phase
    for (let y = 0; y < accYrs; y++) {
      // Optional flat pre-retirement return (nominal %). Blank keeps the historical draw.
      // seqToday: the prescribed sequence starts now, so it covers these years first.
      let ret;
      if (seqToday && seqOverride && y < seqOverride.length) {
        ret = seqYear(seqOverride[y], glidepathEquityWeight(p.currentAge + y, p.preRetireEq, p.postRetireEq, glideSwitchAgeMC), rand).r;
      } else {
        // Draw even when a flat rate is set, so the retirement years keep the same market draws.
        ret = portReturn(p.currentAge + y, rand, p.preRetireEq, p.postRetireEq, glideSwitchAgeMC, sampleRangeMC);
        if (preRetFlat != null) ret = preRetFlat;
      }
      pretax   = Math.max(0, pretax   * (1 + ret));
      roth     = Math.max(0, roth     * (1 + ret));
      taxable  = Math.max(0, taxable  * (1 + ret));
      cash     = Math.max(0, cash     * (1 + ret));
      // Contributions land in the bucket they actually belong to. They used
      // to all get added to `pretax`, which mispriced every later withdrawal:
      // brokerage savings became ordinary income instead of LTCG-with-basis,
      // and it inflated the RMD base at rmdStartAge.
      // Job-bound streams come from one shared helper so this loop,
      // simulateDeterministicWithStrategy, and accumulateToRetirement can't
      // disagree about whose contributions have stopped. A spouse who
      // retires before the primary stops contributing on their own date, not
      // the primary's.
      const jc = jobContributionsForYear(p, p.currentAge + y);
      pretax  += jc.pretax;
      cash    += (p.hsaContrib || 0);          // HSA balances live in the cash bucket
      roth    += jc.roth;
      taxable += (p.taxableContrib || 0);
      // Brokerage contributions are already-taxed dollars, so they add basis
      // one-for-one. Only market growth is unrealized gain.
      taxableBasis += (p.taxableContrib || 0);
      // Pre-retirement one-off inflows get deposited into their bucket, like
      // contributions (no return in the arrival year). Outflow events stay
      // retirement-only — pre-retirement spending is assumed to be paid from
      // wages, which this engine doesn't model. See accInflowByYear above.
      const evAcc = accInflowByYear[y];
      if (evAcc && evAcc.inflow > 0) {
        pretax += evAcc.byBucket.pretax || 0;
        roth   += evAcc.byBucket.roth   || 0;
        cash   += evAcc.byBucket.cash   || 0;
        const taxableIn = evAcc.byBucket.taxable || 0;
        taxable += taxableIn;
        taxableBasis += taxableIn;   // already-taxed money arrives as basis
      }
      totalPort = pretax + roth + taxable + cash;
    }

    const portAtRetire = Math.round(totalPort);

    const path = [portAtRetire];
    let survived = true, exhaustAge = null;
    // How many years this path had to exceed the bracket target to stay
    // funded. Aggregated below so the UI can eventually tell the user a tax
    // preference got overridden, instead of them wondering why their
    // marginal rate exceeds the target they chose.
    let bracketOverrideYears = 0;
    let rothReserveBrokenYears = 0;
    // Per-path guardrail tallies (see gkStats above).
    let gkYearSink = null;
    let pathCuts = 0;
    let pathRaise = false;
    // Years this path was constrained by the floor/ceiling band itself rather
    // than by a GK band rule. Counted separately — see the `constrained` event
    // note in guytonKlingerWithdrawal for why it must not join cuts/raises.
    let pathConstrained = 0;
    let sp = p.sp;
    let lastReturn = 0;

    // IRMAA 2-year lookback history for this path — rolled forward at the
    // end of each retirement year below. Pre-retirement wage income isn't
    // modeled (this engine only knows portfolio/SS/rental income), so the
    // first two retirement years (y === 0, 1) have no usable 2-years-ago
    // figure; those years fall back to same-year MAGI (the pre-lookback
    // approximation) via the `y >= 2` gate below.
    let magiOneYearAgo = null, magiTwoYearsAgo = null;

    // Opt-in per-year detail for the "why plans failed" view (null = off, zero cost).
    const rows = keepFailures ? [] : null;
    for (let y = 0; y < retYrs; y++) {
      const startBal = totalPort;
      const age = retAgeMC + y;
      const calYear = CURRENT_YEAR + (age - p.currentAge);
      // Stress sequence override: prescribe the equity leg for the first
      // seqOverride.length retirement years; bond leg stays bootstrapped at
      // the same age-based equity weight portReturn uses. No override means
      // a normal draw.
      //
      // The seqOverride branch draws bonds and inflation independently (two
      // rand() calls) because the SEQ_2000_2012 array has no year labels to
      // pair against. The normal branch uses drawYearBundle(), which returns
      // a paired (stock, bond, inflation) triple from one historical year —
      // see its definition for the rand()-count shim that keeps this branch
      // matching the seqOverride branch's rand() consumption.
      let r, inflY;
      // The seqOverride branch below builds `r` from these two directly.
      let stockReturnMC, bondReturnMC;
      if (seqOverride && y + seqOffset < seqOverride.length) {
        // Same weight portReturn would have used at this age. This line used
        // to hardcode 62 while portReturn switched at the retirement age, so
        // every Stress Test scenario modeled a different investor than the
        // headline run: a 67 retiree got de-risked five years early, a 60
        // retiree stayed aggressive two years too long.
        const eqW = glidepathEquityWeight(age, p.preRetireEq, p.postRetireEq, glideSwitchAgeMC);
        ({ stock: stockReturnMC, bond: bondReturnMC, infl: inflY, r } = seqYear(seqOverride[y + seqOffset], eqW, rand));
      } else {
        const bundle = drawYearBundle(age, rand, p.preRetireEq, p.postRetireEq, glideSwitchAgeMC, sampleRangeMC);
        r = bundle.ret;
        inflY = bundle.inflY;
        stockReturnMC = SP500[bundle.i];
      }

      const cumInfl = Math.pow(1 + (p.inf ?? 2.5) / 100, y);
      const adjFloor = gkFloor * cumInfl;
      const adjCeiling = gkCeiling * cumInfl;

      // Deterministic income/fixed-cost pieces needed both by this year's GK
      // netNeed offset (below) and by `need` after the strategy block —
      // moved above the strategy switch without touching the rand() call
      // order: none of these consume rand(), so r/inflY/abReliable's draw
      // sequence is unaffected. abReliable itself stays in its original spot
      // (right after `lastReturn = r`) — only these deterministic values
      // moved earlier.
      const ss = computeHouseholdSS(p, age);
      const growthFactor = Math.pow(1 + (p.abGrowth ?? 3) / 100, Math.min(y, 20));
      const totalRental = Math.round(((p.propIncome || 0) + (p.ab > 0 ? p.ab : 0)) * growthFactor);
      // Deterministic expected rental (abEndYear cutoff applied, but not the
      // abReliability coin-flip below) — used only to offset GK's netNeed.
      // The actual reliability draw still gates `effectiveAb`, the real
      // income used in the real `need` afterward, exactly like before.
      const rentalForGK = (p.abEndYear && calYear > p.abEndYear) ? 0 : totalRental;

      // Housing cost (own = mortgage cash cost while active, rent = inflation-adjusted rent, none = 0)
      const housingType = p.housingType || "own";
      let housingCost = 0;
      if (housingType === "own") {
        housingCost = mortByYear.get(calYear) || 0;
      } else if (housingType === "rent") {
        housingCost = Math.round((p.annualRent || 0) * cumInfl);
      }

      // Active fixed carveouts
      const carveoutCost = (p.carveouts || []).reduce((sum, c) => {
        return sum + (calYear <= (c.endYear || 9999) ? Math.round((c.annual || 0) * cumInfl) : 0);
      }, 0);

      const { total: otherIncTotal, totalTaxable: otherIncTaxable } = computeOtherIncome(p.otherIncomes, calYear);

      // GK's netNeed income offset — same net portfolio need quantity initWR
      // was calibrated against: SS + expected rental + other income offset
      // gross spend; housing + carveouts add to it.
      // Planned one-off / periodic expenses (roof, car, wedding). Additive,
      // so the distribution strategy still governs the recurring base —
      // unlike a multi-year CSV, which replaces the spend rule and disables
      // guardrails.
      const ev = computeCashFlowEvents(p.cashFlowEvents, calYear, p.inf ?? 2.5, CURRENT_YEAR);
      const eventCost = ev.total;

      // Inflows (lump-sum pension, cash-balance rollover, inheritance, home
      // sale) get deposited into their bucket so they compound. They must not
      // be netted against spending — `need` is Math.max(0, sp - income),
      // which would discard everything past one year's need.
      if (ev.inflow > 0) {
        pretax  += ev.byBucket.pretax  || 0;
        roth    += ev.byBucket.roth    || 0;
        cash    += ev.byBucket.cash    || 0;
        const taxableIn = ev.byBucket.taxable || 0;
        taxable += taxableIn;
        taxableBasis += taxableIn;   // already-taxed money arrives as basis
        totalPort = pretax + roth + taxable + cash;
      }

      // Annual yield tax drag (see engine/taxableYield.js) — computed once
      // against the entering balance (untouched by anything below until the
      // growth line). Real interest/dividends are taxed the year they're
      // paid whether or not they're reinvested. ordinaryYieldMC stacks into
      // this year's tax calc the same way effectiveAb/otherIncTaxable does;
      // qualifiedYieldMC stacks onto realized capital gains at each
      // calcYearTax call below. Credited to taxableBasis after the draws/tax
      // resolve (with rmdExcess/surplusToTaxableMC), not by touching the
      // growth line — it's already-taxed money that must never be taxed
      // again on a later withdrawal.
      const { yieldAmount: taxableYieldAmtMC, ordinaryYield: ordinaryYieldMC, qualifiedYield: qualifiedYieldMC } =
        taxableYieldSplit(taxable, p.taxableYieldPct, p.taxableYieldOrdinaryPct);

      // Healthcare shock — stochastic per path, inflated to this year.
      // Treated as a committed cost: a medical bill isn't discretionary
      // spending the guardrails can trim.
      const hcShock = healthcareShockDraw(age, rand, p, cumInfl);

      const gkIncomeOffset = ss + rentalForGK + otherIncTotal;
      // Only committed events are shielded from the guardrails; a deferrable
      // one (a big travel year) is discretionary and can be trimmed like base spend.
      const gkFixedCosts = housingCost + carveoutCost + ev.committed + hcShock;

      // ── Withdrawal strategy ──
      if (p.spSchedule && p.spSchedule.length) {
        // A detailed year-by-year budget is the spending plan — it overrides
        // the distribution strategy's spend rule. Values are nominal for each
        // listed year; beyond the last year the last value carries forward, inflated.
        sp = scheduleSpendForYear(p.spSchedule, calYear, p.inf ?? 2.5);
      } else if (y === 0) {
        // First year: use target spend (p.sp)
      } else {
        if (withdrawalStrategy === "gk") {
          // Years remaining uses the horizon being simulated (`endAge`), not
          // p.endAge, so the GK longevity rule stays consistent with this
          // run's survival test. Reference re-based on this year's income so
          // a scheduled pension raise can't masquerade as portfolio
          // outperformance. See gkReferenceWR.
          const refWR = gkReferenceWR({ plannedSpend: p.sp, cumInfl, incomeOffset: gkIncomeOffset, fixedCosts: gkFixedCosts, portAtRetire });
          // Record the guardrail's action for this path-year so the run can
          // report how often the guardrails actually bind (the summary above
          // the Guardrails chart). Accumulated per path, never re-derived.
          gkYearSink = {};
          sp = guytonKlingerWithdrawal(totalPort, refWR, sp, lastReturn, inflY, adjFloor, adjCeiling, endAge - age, gkIncomeOffset, gkFixedCosts, gkYearSink);
          if (gkYearSink.event === "cut") { pathCuts++; gkStats.cuts++; }
          else if (gkYearSink.event === "raise") { pathRaise = true; gkStats.raises++; }
          else if (gkYearSink.event === "constrained") {
            // Third type: the band bound, not a GK rule. Deliberately NOT counted
            // as a cut or raise — see guytonKlingerWithdrawal's clamp note. A
            // ceiling bind raises spend and a floor bind cuts it, so direction is
            // recorded from which limit bound rather than inferred from `event`.
            pathConstrained++;
            gkStats.constrained++;
            if (gkYearSink.reason === "ceiling-bind") gkStats.ceilingBinds++;
            else if (gkYearSink.reason === "floor-bind") gkStats.floorBinds++;
          }
          else if (gkYearSink.reason === "longevity-hold") gkStats.longevityHolds++;
        }
        else if (withdrawalStrategy === "fixed") {
          // Pure fixed %: draw = rate × port. No GK clamp — that defeats the purpose.
          const fixedRate = p.fixedWithdrawalRate ?? 0.04;
          sp = totalPort * fixedRate;
        }
        else if (withdrawalStrategy === "vpw") {
          // VPW = portfolio amortized over remaining years at an assumed
          // return. Canonical PMT payout rate: r / (1 - (1+r)^(-n)), n =
          // years of payments left. r is a fixed assumption (that's VPW's
          // defining feature), profile-overridable.
          const rVPW = p.vpwRealReturn ?? 0.0376;
          // Deplete by the plan-to age, not a separate hardcoded 100.
          // vpwEndAge used to be its own field defaulting to 100, so setting
          // "Plan to age 105" still amortized to 100 — the spend-to-zero
          // answer was five years off with nothing in the UI saying so. An
          // explicit vpwEndAge still wins if set.
          const n = Math.max(1, (p.vpwEndAge ?? endAge ?? 100) - age);
          const rateVPW = rVPW === 0 ? 1 / n : rVPW / (1 - Math.pow(1 + rVPW, -n));
          const newSp = totalPort * Math.min(0.10, rateVPW);
          sp = Math.max(adjFloor, Math.min(adjCeiling, newSp));
        }
        else if (withdrawalStrategy === "ninety_five_rule") {
          if (y === 1) {
            sp = p.sp;
          } else {
            const inflated = sp * (1 + inflY);
            const floor95 = sp * 0.95;
            sp = Math.max(floor95, inflated);
          }
          sp = Math.max(adjFloor, Math.min(adjCeiling, sp));
        }
        else if (withdrawalStrategy === "bengen") {
          // Bengen 4% rule (1994): inflation-adjusted constant spending.
          // Does NOT react to portfolio moves — that's the defining feature.
          // Can fail (portfolio depletes). Honest about late-stage risk.
          sp = sp * (1 + inflY);
        }
        else if (withdrawalStrategy === "smart") {
          // Smart Waterfall hybrid:
          //   yearsRemaining > 15  → GK guardrails (adaptive, paper-faithful)
          //   yearsRemaining ≤ 15  → Bengen (inflation-only, no portfolio reaction)
          // The split point matches GK's own longevity-clause threshold so we
          // exit GK exactly where its safety brake would have been disabled.
          // Years remaining uses the simulated horizon (`endAge`), not p.endAge — see gk note above.
          // Bucket sourcing is handled below regardless.
          const yrsRemaining = endAge - age;
          if (yrsRemaining > 15) {
            const refWR2 = gkReferenceWR({ plannedSpend: p.sp, cumInfl, incomeOffset: gkIncomeOffset, fixedCosts: gkFixedCosts, portAtRetire });
            sp = guytonKlingerWithdrawal(totalPort, refWR2, sp, lastReturn, inflY, adjFloor, adjCeiling, yrsRemaining, gkIncomeOffset, gkFixedCosts);
          } else {
            sp = sp * (1 + inflY);
          }
        }
        else {
          // Shouldn't be reachable while `withdrawalStrategy` comes from
          // resolveStrategy, but keeping it here anyway: an unmatched id used
          // to leave `sp` untouched, which reads as a 1%/yr spending cut
          // instead of an error. Inflation-adjusting is the least-surprising
          // fallback.
          sp = sp * (1 + inflY);
        }
      }
      lastReturn = r;

      // Post-guardrail annual spending, tracked across every path and year —
      // the "spending actually seen" range.
      //
      // REAL dollars, deflated to the RETIREMENT YEAR (cumInfl is 1 at y === 0,
      // so retirement year one is the yardstick — the same basis
      // engine/mcSelectors.js::deflate() uses, and the same one the spend the
      // user typed is consumed in). Nominal would have been the easy version and
      // the wrong one: a nominal range spanning 30 years of inflation mostly
      // measures the inflation, so the low end is always year one and the high
      // end always the last year — a range that says nothing about the
      // guardrails. The field NAMES carry the basis (`...Real`) because
      // "spendMin" with an unstated basis is exactly how a dollar figure ends up
      // labelled "today's dollars" when it is retirement-year dollars
      // (REQUIREMENTS §41 A3).
      //
      // Positive values only: a depleted path's trailing $0 years would drag the
      // minimum to zero and make the range meaningless.
      if (sp > 0) {
        const spReal = sp / cumInfl;
        if (spReal < gkStats.spendMinReal) gkStats.spendMinReal = spReal;
        if (spReal > gkStats.spendMaxReal) gkStats.spendMaxReal = spReal;
      }

      // Income from SS and rental/AB. COLA compounds from the claiming age
      // (p.ssAge), not the retirement-year loop counter `y` — someone who
      // retires before claiming SS would otherwise get bogus pre-claim COLA
      // compounding baked into their very first check (matches
      // buildWithdrawalWaterfall.js's `age - ssAge` pattern). ss/totalRental
      // are already computed above (before the strategy block); only the
      // reliability coin-flip and the real effectiveAb need computing here.
      const abReliable = rand() < (p.abReliability || 80) / 100;
      const effectiveAb = (p.abEndYear && calYear > p.abEndYear) ? 0 :
        (abReliable ? totalRental : 0);

      // Blanchett spending smile — a real lifestyle curve applied to this
      // year's spend. Not fed back into `sp` on purpose, since that's the
      // withdrawal strategy's running state: multiplying it would compound
      // the smile year over year and corrupt GK's own inflation logic. This
      // is an overlay on what the strategy decided, not a change to the strategy.
      const spSmiled = sp * spendingSmileFactor(age, retAgeMC, p.smile !== false);
      // Income nets against the WHOLE outlay (base spend + every fixed cost),
      // not base spend alone. The old form — max(0, spend - income) + fixedCosts
      // — floored the income offset at base spend and then added fixed costs
      // raw, so a household whose SS + rental + other income EXCEEDED base spend
      // still sold assets to cover housing/carveouts/events/healthcare. That
      // biased the success rate DOWN whenever income > base spend. Folding the
      // fixed costs inside the max lets surplus income offset them, and leaves
      // `incomeSurplus` (below) + the remaining draw (further below) netting the
      // same quantity, so all three agree.
      const need = Math.max(0, (spSmiled + housingCost + carveoutCost + eventCost + hcShock) - ss - effectiveAb - otherIncTotal);
      // Income above spending used to get discarded by the max(0, ...) above
      // while the tax on it still got charged to the portfolio, so received
      // money vanished and assets were sold to pay its bill. Now surplus
      // funds the tax first and the remainder gets deposited (see the long
      // note in buildWithdrawalWaterfall).
      const incomeSurplus = Math.max(0,
        (ss + effectiveAb + otherIncTotal) - (spSmiled + housingCost + carveoutCost + eventCost + hcShock));

      // RMD calculation
      let rmd = 0;
      if (age >= rmdStartAge && pretax > 0) {
        // Table II needs both ages and only applies when the spouse is more than 10 years
        // younger; jointRmdDivisor returns null otherwise, which falls back to Uniform.
        const jointDiv = useJointTableAt(age) ? jointRmdDivisor(age, spouseAgeAt(p, age)) : null;
        const divisor = jointDiv ?? (UNIFORM_TABLE[age] || 15.0);
        rmd = Math.round(pretax / divisor);
      }
      // Tax + withdrawal sizing (source-aware, fixed-point).
      // Ordinary income = RMD + discretionary pretax draw only; cash/taxable/
      // Roth draws aren't ordinary income (LTCG on taxable draws is a
      // documented gap). Taxes depend on the pretax draw, which depends on
      // the total draw size (need + taxes), which depends on taxes — iterate
      // to convergence. RMD proceeds fund spending first; any excess gets
      // reinvested in taxable.
      const yr = CURRENT_YEAR + (age - p.currentAge);
      // Filing status has to vary over time here. This used to be
      // `p.filingStatus || "mfj"`, a constant, so the survivor's tax bill
      // couldn't be modeled: on a first death the brackets narrow, the
      // standard deduction roughly halves, the IRMAA tiers halve, and the
      // senior bonus halves. One shared rule with buildWithdrawalWaterfall
      // via engine/ages.js, otherwise the two engines describe different households.
      const filingStatus = filingStatusAt(p, age);

      // Sourcing guardrails (bracket cap, IRMAA guard, Roth reserve) are
      // independent of the distribution strategy — they apply to any
      // strategy once the user has chosen a bracket target. "off" opts out
      // to naive (pretax-first, uncapped). The rooms don't depend on the
      // draw size, so compute them once.
      let bracketRoomMC = Infinity;
      // Either constraint binds independently. The IRMAA guard used to sit
      // inside this bracket-target check, so it did nothing whenever the
      // target was "off" — a dead setting (verified byte-identical with the
      // guard on and off). Decoupled here and in buildWithdrawalWaterfall
      // together, since letting the two engines disagree is exactly the kind
      // of drift this codebase keeps running into.
      const bracketSetMC = !!(p.withdrawalBracketTarget && p.withdrawalBracketTarget !== "off");
      const irmaaOnMC    = !!p.irmaaGuard && age >= 63;
      if (bracketSetMC || irmaaOnMC) {
        const inflFactorMC = Math.pow(1 + taxInfl, Math.max(0, yr - CURRENT_YEAR));
        const sdMC = getStandardDeduction(age, filingStatus, inflFactorMC, spouseAgeAt(p, age));
        // 85% SS inclusion is a worst-case estimate on purpose, so the cap never overshoots.
        const ordinaryFloorMC = Math.round(ss * 0.85) + rmd + (effectiveAb + otherIncTaxable) + ordinaryYieldMC;
        // Infinity when only the IRMAA guard is on — the min() below then
        // makes the IRMAA tier the sole binding ceiling.
        const ceilingMC = bracketSetMC
          ? getBracketCeiling(p.withdrawalBracketTarget, filingStatus, inflFactorMC)
          : Infinity;
        // The OBBBA senior bonus shelters ordinary income the same way the
        // standard deduction does, so the bracket room needs to include it or
        // sourcing will under-fill the bracket that calcYearTax actually
        // grants. Its phase-out is MAGI-keyed and MAGI rises with the very
        // draw being sized, so estimate the bonus at the high end of this
        // year's plausible MAGI (floor + the room before the bonus) — that
        // gives the worst-case phase-out and the smallest bonus. Same
        // conservative spirit as the 85% SS inclusion above: the cap can
        // under-fill the bracket but must never overshoot it. The bonus
        // estimate needs a finite ceiling; with no bracket target the IRMAA
        // tier is the only thing that can bind, so use it as the proxy.
        const ceilingForBonusMC = Number.isFinite(ceilingMC)
          ? ceilingMC
          : Math.max(0, getIrmaaCeiling(1, filingStatus, inflFactorMC) - sdMC);
        // NOT Math.max(0, ordinaryFloorMC - sdMC)/Math.max(0, ... ) — a
        // deduction unused by other-ordinary-income (Golden Window years:
        // early retirement, no SS/RMD/rental yet, ordinaryFloorMC can be $0)
        // still has to carry into this draw's room, or the bracket-fill
        // target under-fills by up to the whole standard deduction
        // (~$32-35K MFJ). Only the OUTER clamp (bracketRoomMC = Math.max(0,
        // ceilingMC - taxableSoFarMC), below) needs a floor at 0. Matches
        // the corresponding fix in buildWithdrawalWaterfall.js — same bug,
        // same root cause, found by a from-scratch engine audit.
        const roomBeforeBonusMC = Math.max(0, ceilingForBonusMC - (ordinaryFloorMC - sdMC));
        const seniorBonusMC = getSeniorBonusDeduction(
          age, filingStatus, ordinaryFloorMC + roomBeforeBonusMC, yr, spouseAgeAt(p, age)
        );
        const taxableSoFarMC = ordinaryFloorMC - (sdMC + seniorBonusMC);
        // Bracket room lives in taxable-income space (ceiling is post-deduction).
        bracketRoomMC = Number.isFinite(ceilingMC)
          ? Math.max(0, ceilingMC - taxableSoFarMC)
          : Infinity;
        // IRMAA room lives in MAGI space — don't subtract the standard
        // deduction. A pretax draw raises taxable income and MAGI by the
        // same dollar, so both rooms cap the same incremental draw; take
        // whichever is tighter. (LTCG from the taxable draw isn't folded
        // into the MAGI base here yet — a known modeling gap.)
        if (irmaaOnMC) {
          const irmaaRoom = Math.max(0, getIrmaaCeiling(1, filingStatus, inflFactorMC) - ordinaryFloorMC);
          bracketRoomMC = Math.min(bracketRoomMC, irmaaRoom);
        }
      }

      const rothFloorMC = p.rothEmergencyReserve || 0;
      // IRMAA 2-year lookback for this year's charge: the MAGI from age-2,
      // rolled forward at the bottom of the previous two iterations. The
      // first two retirement years (y < 2) have no pre-retirement wage
      // history to look back on (this engine doesn't model wages), so they
      // fall back to null and calcYearTax uses same-year MAGI, matching the
      // pre-lookback approximation for those two years only.
      const magiLookbackMC = (y >= 2 && typeof magiTwoYearsAgo === "number") ? magiTwoYearsAgo : null;
      let taxResult = null;
      let totalTax = 0;
      let fromCash = 0, fromTaxable = 0, fromPretax = 0, fromRoth = 0;
      let shortfall = 0;
      // Nominal dollars. `spend` is the year's total outlay before income; `income` is SS + other.
      const rowOf = (end, short = 0) => ({
        age, year: calYear, stock: stockReturnMC, ret: r, infl: inflY,
        spend: Math.round(spSmiled + housingCost + carveoutCost + eventCost + hcShock),
        income: Math.round(ss + effectiveAb + otherIncTotal), tax: Math.round(totalTax),
        start: Math.round(startBal), end: Math.round(end), short: Math.round(short),
      });
      let earlyPenMC = { penalty: 0, exemptAmount: 0, reason: "" };
      // 12 passes, not 4 — the tax/draw fixed point converges geometrically
      // at roughly the marginal rate (about 0.3x/pass); 4 passes came out
      // $100-350 short of the true tax bill every year. The <$1 break makes
      // extra passes free once it's converged. Matches
      // buildWithdrawalWaterfall's pass cap exactly.
      for (let pass = 0; pass < 12; pass++) {
        // Withdraw from buckets in the user's chosen order (default
        // tax_reactive = cash → taxable → pretax capped → roth). The
        // bracket/IRMAA cap stays on the pretax step and the reserve floor on
        // the roth step wherever each lands. Surplus income funds the tax
        // bill before any asset gets sold.
        let remaining = Math.max(0, need + totalTax - rmd - incomeSurplus);
        fromCash = fromTaxable = fromPretax = fromRoth = 0;
        const drawMC = {
          cash:    () => { fromCash    = Math.min(remaining, cash);                                             remaining -= fromCash;    },
          taxable: () => { fromTaxable = Math.min(remaining, taxable);                                          remaining -= fromTaxable; },
          pretax:  () => { fromPretax  = Math.min(Math.min(remaining, bracketRoomMC), Math.max(0, pretax - rmd)); remaining -= fromPretax;  },
          roth:    () => { fromRoth    = Math.min(remaining, Math.max(0, roth - rothFloorMC));                  remaining -= fromRoth;    },
        };
        for (const bucket of drawOrderMC) drawMC[bucket]();

        // Essential-spending override — the bracket cap is soft. Mirrors
        // buildWithdrawalWaterfall's override exactly, see the long
        // rationale there. Short version: `bracketRoomMC` decides which
        // dollars to draw, never whether the household eats. A household
        // whose rental/pension income fills the target bracket by itself had
        // zero room, so a mostly-pre-tax portfolio couldn't be drawn at all,
        // `shortfall` stayed positive, and the path below got marked
        // `survived = false` while holding millions. That's how a ~100% plan
        // ended up reporting 3.3%.
        //
        // Has to live in both engines, or they disagree about survival —
        // that's the cross-engine drift this codebase keeps running into.
        if (remaining > 0.01) {
          const pretaxAvail = Math.max(0, pretax - rmd) - fromPretax;
          if (pretaxAvail > 0) {
            const extra = Math.min(remaining, pretaxAvail);
            fromPretax += extra;
            remaining  -= extra;
            bracketOverrideYears++;
          }
        }
        // Roth emergency reserve, same principle — see the long note in
        // buildWithdrawalWaterfall. A reserve you can't reach in an emergency
        // is just a vault, and holding it past the point of failure protects
        // the money from its own purpose. Measured before this fix: a $1.9M
        // reserve on a $2.05M portfolio took success from 100% to 47% while
        // the reserve sat untouched. Strictly last, after uncapped pre-tax.
        if (remaining > 0.01 && rothFloorMC > 0) {
          const reserveLeft = Math.max(0, roth - fromRoth);
          if (reserveLeft > 0) {
            const extra = Math.min(remaining, reserveLeft);
            fromRoth  += extra;
            remaining -= extra;
            rothReserveBrokenYears++;
          }
        }
        shortfall = remaining;

        // Realized LTCG on this pass's taxable draw — read-only off the
        // current (pre-draw) taxable balance/basis; the real `taxableBasis`
        // gets mutated exactly once below, after the fixed point converges,
        // using the final fromTaxable (not accumulated pass-by-pass).
        const gPass = realizedGainFromDraw(fromTaxable, taxable, taxableBasis);
        taxResult = calcYearTax(
          age, yr, fromPretax, ss, effectiveAb + otherIncTaxable + ordinaryYieldMC, rmd, 0,
          p.twoHousehold || false, taxInfl, filingStatus, p.stateOfResidence || "NJ", gPass + qualifiedYieldMC, magiLookbackMC,
          spouseAgeAt(p, age)
        );
        // IRC §72(t) additional tax on pre-59½ distributions. It's inside the
        // fixed point for the same reason as the rest of the bill: a bigger
        // pretax draw owes a bigger penalty, which widens the need, which
        // grows the draw. Without this the MC's success rate rated an
        // early-retirement plan as cheaper than it really is, while the
        // waterfall (which does charge it) said otherwise — the two engines
        // need to price the same dollars.
        earlyPenMC = earlyWithdrawalPenalty({
          separationQualifies: ruleOf55OkMC,
          age, pretaxDistribution: fromPretax + rmd,
          ruleOf55: p.ruleOf55, ruleOf55Share: ruleOf55ShareMC, retireAge: retAgeMC,
          sepp72t: p.sepp72t, sepp72tStartAge: p.sepp72tStartAge ?? retAgeMC,
        });
        const newTax = taxEnabled ? taxResult.totalTax + earlyPenMC.penalty : 0;
        if (Math.abs(newTax - totalTax) < 1) { totalTax = newTax; break; }
        totalTax = newTax;
      }

      if (shortfall > 0.01) {
        survived = false;
        exhaustAge = age;
        if (rows) rows.push(rowOf(0, shortfall));
        break;
      }

      // Realized gain for the year (final, converged fromTaxable) — the one
      // authoritative value used both to mutate taxableBasis below and to
      // feed the Roth-conversion delta-tax helper further down, so a pure
      // conversion cost doesn't get polluted by a second, different gain estimate.
      const realizedGainMC = realizedGainFromDraw(fromTaxable, taxable, taxableBasis);

      // Update bucket balances. Excess RMD (forced out beyond what spending
      // and taxes consumed) gets reinvested in the taxable bucket, not vaporized.
      const rmdExcess = Math.max(0, rmd - (need + totalTax));
      // Basis consumed by the draw = draw - realized gain (the non-gain,
      // return-of-basis portion); reinvested rmdExcess is fresh money, so
      // it's fresh basis dollar-for-dollar.
      const consumedBasisMC = fromTaxable - realizedGainMC;
      // Surplus left after this year's tax gets deposited as basis (already
      // taxed money); only later growth is gain. Mirrors the waterfall exactly.
      const surplusToTaxableMC = Math.max(0, incomeSurplus - Math.max(0, totalTax - rmd));
      // The yield's tax was already funded above (it widened totalTax, which
      // widened this year's draws) — crediting it to basis here (not to the
      // balance, which grows unchanged below) is what stops it from being
      // taxed a second time as unrealized gain on a later withdrawal.
      taxableBasis = Math.max(0, taxableBasis - consumedBasisMC) + rmdExcess + surplusToTaxableMC + taxableYieldAmtMC;
      cash    = Math.max(0, cash    - fromCash);
      taxable = Math.max(0, taxable - fromTaxable) + rmdExcess + surplusToTaxableMC;
      pretax  = Math.max(0, pretax  - fromPretax - rmd);
      roth    = Math.max(0, roth    - fromRoth);

      // NaN-proof buckets before growth (just in case)
      cash    = isNaN(cash)    ? 0 : cash;
      taxable = isNaN(taxable) ? 0 : taxable;
      pretax  = isNaN(pretax)  ? 0 : pretax;
      roth    = isNaN(roth)    ? 0 : roth;

      // IRMAA lookback history: this year's MAGI, used two years from now.
      // Defaults to the no-conversion taxResult.magi, overwritten below with
      // the post-conversion MAGI when a conversion actually executes (a
      // conversion raises MAGI, so the age+2 IRMAA charge needs to see it —
      // the conversion can't affect its own year's charge under the lookback).
      let finalMagiMC = taxResult.magi;

      // Bracket-fill Roth conversion (after spending withdrawals, before
      // growth). Bracket ceilings index at the assumed long-run inflation
      // rate, not inflY — compounding a single bootstrapped year's draw over
      // the whole horizon would swing the ceiling wildly with RNG noise.
      if (p.rothConversionTarget && p.rothConversionTarget !== "off" && pretax > 1000) {
        const inflFactor = Math.pow(1 + taxInfl, Math.max(0, yr - CURRENT_YEAR));
        const bracketCeiling = getBracketCeiling(p.rothConversionTarget, filingStatus, inflFactor);
        const room = Math.max(0, bracketCeiling - (taxResult.taxableIncome || 0));
        if (room > 500) {
          let convAmt = Math.min(room, pretax);
          let lastWithConv = null;
          // True conversion cost is the delta in total tax versus the
          // no-conversion tax (correct progressive bracket stacking), not a
          // flat marginal-rate estimate — a single bracket's rate
          // understates cost whenever `room` spans intervening brackets.
          // Mirrors buildWithdrawalWaterfall.js's Step 6.5/7 exactly:
          // recompute full tax with the conversion stacked as ordinary
          // income via calcYearTax's own `conversionAmount` parameter, then
          // take the delta. Note: `totalTax` (and `withConv.totalTax`)
          // exclude IRMAA (calcYearTax.totalTax = fedTax+stateTax), so this
          // delta was already a pure fed+state conversion cost before the
          // lookback landed, and it needs no change now that IRMAA is a
          // fixed, lookback-driven constant for the year — a same-year
          // conversion can't move it.
          const convTaxFor = (amt) => {
            if (!taxEnabled || amt <= 0) return 0;
            // Same realizedGainMC as the spending-draw tax call above — the
            // delta has to isolate the conversion's own cost, not a
            // different LTCG estimate. Same magiLookbackMC too, so the
            // (fixed) IRMAA component agrees with taxResult's — it's this
            // year's charge, unaffected by convAmt.
            const withConv = calcYearTax(
              age, yr, fromPretax, ss, effectiveAb + otherIncTaxable + ordinaryYieldMC, rmd, amt,
              p.twoHousehold || false, taxInfl, filingStatus, p.stateOfResidence || "NJ", realizedGainMC + qualifiedYieldMC, magiLookbackMC,
              spouseAgeAt(p, age)
            );
            lastWithConv = withConv;
            return Math.max(0, withConv.totalTax - totalTax);
          };
          let convTax = convTaxFor(convAmt);

          // Shrink (rather than all-or-nothing skip) when pretax can't
          // self-fund the conversion plus its incremental tax — converge on
          // the largest amount the remaining pretax balance can afford,
          // mirroring the waterfall's own loop. Who pays the conversion tax
          // has to match buildWithdrawalWaterfall exactly, or the Monte
          // Carlo success rate describes a different plan than the
          // Withdrawal/Conversion tabs. This block used to do `pretax -=
          // convAmt + convTax` unconditionally, ignoring the setting entirely.
          const withholdConvMC = p.taxFunding === "from_conv" || p.taxFunding === "from_conversion";
          const outsideForConvMC = withholdConvMC ? 0 : Math.max(0, taxable) + Math.max(0, cash);

          for (let i = 0; i < 5 && convAmt > 0; i++) {
            const taxOnPretaxMC = withholdConvMC ? 0 : Math.max(0, convTax - outsideForConvMC);
            const needMC = withholdConvMC ? convAmt : convAmt + taxOnPretaxMC;
            const shortfall = needMC - pretax;
            if (shortfall <= 0) break;
            convAmt = Math.max(0, convAmt - shortfall);
            convTax = convTaxFor(convAmt);
          }

          if (convAmt > 500) {
            if (withholdConvMC) {
              // Withheld out of the transfer: gross leaves pretax, net reaches Roth.
              pretax -= convAmt;
              roth   += Math.max(0, convAmt - convTax);
            } else {
              // Real buckets, same order the waterfall uses: taxable → cash → pretax.
              let owed = convTax;
              const fromTaxConv  = Math.min(owed, Math.max(0, taxable)); owed -= fromTaxConv;
              const fromCashConv = Math.min(owed, Math.max(0, cash));    owed -= fromCashConv;
              taxable = Math.max(0, taxable - fromTaxConv);
              cash    = Math.max(0, cash    - fromCashConv);
              pretax -= (convAmt + Math.max(0, owed));
              roth   += convAmt;
            }
            if (lastWithConv) finalMagiMC = lastWithConv.magi;
          }
        }
      }

      // Roll the 2-year IRMAA lookback history forward: this year's final MAGI
      // (post-conversion when one executed) becomes "two years ago" once we
      // reach year y+2.
      magiTwoYearsAgo = magiOneYearAgo;
      magiOneYearAgo = finalMagiMC;

      // Apply growth. Cash earns its own safe rate; everything else earns the
      // portfolio-wide glidepath return for this sampled year.
      cash    = Math.max(0, cash    * (1 + cashRealReturn));
      pretax  = Math.max(0, pretax  * (1 + r));
      roth    = Math.max(0, roth    * (1 + r));
      taxable = Math.max(0, taxable * (1 + r));

      totalPort = pretax + roth + taxable + cash;
      path.push(Math.round(totalPort));
      if (rows) rows.push(rowOf(totalPort));

      if (totalPort <= 0 && survived) {
        survived = false;
        exhaustAge = age;
      }
    }
    // The shortfall branch above `break`s out of this loop before its
    // `path.push`, so an exhausted path's array is shorter than a survivor's
    // — its last entry is a stale mid-run balance, not $0 at the terminal
    // age. `pcts` (below) already treats a missing entry as `?? 0`; padding
    // here makes `term`'s per-path-last-value quantile (mc.term.p50, the
    // Forecast tab headline) count the same $0 for that path, matching
    // `pcts`'s terminal row (the Net Worth tab chart) instead of diverging from it.
    while (path.length <= retYrs) path.push(0);
    // Fold this path's guardrail behaviour into the run-level totals.
    gkStats.paths++;
    gkStats.cuts += pathCuts;
    // Keep every path's own count. The MEAN alone was misleading on screen: the
    // headline read "avg 8.9 cuts per scenario" while the median path never cut
    // at all, because a minority of sequences cut repeatedly and dragged the
    // average up. A mean is not a typical experience when the distribution is
    // this skewed, so the distribution is kept and the median reported beside it
    // (the same reasoning as Mean vs Median Final Balance on the Overview).
    gkStats.cutCounts.push(pathCuts);
    if (pathCuts > 0) gkStats.pathsWithCut++;
    if (pathRaise) gkStats.pathsWithRaise++;
    // `constrained` is per-path incidence of the band binding at least once.
    // Reported as its own rate rather than merged, so tightening gkFloorPct /
    // gkCeilingPct moves a visible number in the direction it should.
    if (pathConstrained > 0) gkStats.pathsWithConstrained++;
    results.push({ path, survived, exhaustAge, portAtRetire, bracketOverrideYears, rothReserveBrokenYears });
    if (rows && !survived) failedRuns.push({ rows, exhaustAge, portAtRetire, path: i });
  }

  // Aggregate results
  const pcts = [];
  for (let t = 0; t <= retYrs; t++) {
    // Use ?? 0 so exhausted paths count as $0, not undefined
    const vals = results.map(r => r.path[t] ?? 0).sort((a, b) => a - b);
    const q = pct => vals[Math.floor(pct * (vals.length - 1))];
    // Clamped, matching the drawdown loop that produced `path` — otherwise
    // the fan chart's age axis (and every calendar year derived from it)
    // would start before today for an already-retired user.
    const ageT = retAgeMC + t;
    // Fraction of paths not yet exhausted at this age — feeds the per-age
    // band table under the fan chart. A path counts as funded at ageT if it
    // never exhausted, or exhausted at a later age.
    const alive = results.filter(r => r.exhaustAge == null || r.exhaustAge > ageT).length / N;
    pcts.push({
      age: ageT,
      p10: q(0.1), p25: q(0.25), p50: q(0.5), p75: q(0.75), p90: q(0.9),
      alive,
    });
  }
  const nS = results.filter(r => r.survived).length;
  // Share of paths that had to break the bracket target at least once to
  // stay funded. A high value means the chosen target is unreachable for
  // this household, usually because non-portfolio income already fills it.
  const bracketOverrideRate = results.filter(r => r.bracketOverrideYears > 0).length / N;
  // Median age at which money ran out, across failing paths only. This is
  // the single most useful number for explaining a low score — "it fails"
  // isn't actionable, "it typically fails at 78" is. null when nothing failed.
  const exhaustAges = results.filter(r => !r.survived && Number.isFinite(r.exhaustAge))
    .map(r => r.exhaustAge).sort((a, b) => a - b);
  const medianExhaustAge = exhaustAges.length
    ? exhaustAges[Math.floor(exhaustAges.length / 2)]
    : null;
  const rothReserveBrokenRate = results.filter(r => r.rothReserveBrokenYears > 0).length / N;
  const rV = results.map(r => r.portAtRetire).sort((a, b) => a - b);
  const medR = rV[Math.floor(rV.length / 2)];
  const tV = results.map(r => r.path[r.path.length - 1]).sort((a, b) => a - b);
  const qt = p => tV[Math.floor(p * (tV.length - 1))];
  // Mean of the terminal distribution. Deliberately NOT the average of p10..p90:
  // that would be an average of five quantiles, which is not the distribution's
  // mean and would understate it (the mean sits above the median here because a
  // handful of very good sequences drag it up — the right tail the percentile
  // ladder deliberately truncates). Rounded once, here, so every surface shows
  // the same figure.
  const termMean = tV.length ? Math.round(tV.reduce((s, v) => s + v, 0) / tV.length) : 0;
  // Mortality-weighted success: a failed path only fails you if you're alive
  // to experience it. Weight each failure by P(alive at its exhaust age),
  // from the same SSA table the fan chart's mortality overlay uses. The raw
  // `rate` is the conservative "live to the horizon" number; `mwRate` is the
  // actuarial "chance the money outlives you" number (Blanchett/Kitces-style).
  // mwRate is always >= rate, since each failure's weight is <= 1.
  const failSurvivalSum = results.reduce(
    (s, r) => s + (r.survived ? 0 : survivalToAge(p.currentAge, r.exhaustAge ?? endAge, p.sex)),
    0
  );
  const mwRate = 1 - failSurvivalSum / N;
  return {
    rate: nS / N,
    bracketOverrideRate,
    rothReserveBrokenRate,
    medianExhaustAge,
    mwRate,
    // How often the spending guardrails actually bound, counted where they were
    // applied. null-shaped (never Infinity) when no path registered a spend.
    // The spend range is in RETIREMENT-YEAR dollars (see the accumulation note
    // in the year loop) — label it with that basis, never "today's dollars".
    gkStats: gkStats.paths > 0 ? {
      ...gkStats,
      spendMinReal: Number.isFinite(gkStats.spendMinReal) ? Math.round(gkStats.spendMinReal) : 0,
      spendMaxReal: Math.round(gkStats.spendMaxReal),
      cutRate: gkStats.pathsWithCut / gkStats.paths,
      raiseRate: gkStats.pathsWithRaise / gkStats.paths,
      avgCutsPerPath: gkStats.cuts / gkStats.paths,
      // The typical experience, and the average among the paths that actually
      // cut. Reporting only the overall mean invited "the typical plan cuts ~9
      // times", which the median-path chart directly contradicted.
      medianCutsPerPath: gkStats.cutCounts.length
        ? [...gkStats.cutCounts].sort((a, b) => a - b)[Math.floor(gkStats.cutCounts.length / 2)]
        : 0,
      avgCutsAmongCutters: gkStats.pathsWithCut > 0 ? gkStats.cuts / gkStats.pathsWithCut : 0,
      // Third event type — the floor/ceiling band binding, counted apart from the
      // GK rules. `constrainedRate` is per-path incidence; the split lets the view
      // say which limit bound, since a ceiling bind and a floor-bind imply
      // opposite setting changes.
      constrainedRate: gkStats.pathsWithConstrained / gkStats.paths,
      avgConstrainedPerPath: gkStats.constrained / gkStats.paths,
    } : null,
    pcts,
    medR,
    // Only with keepFailures. Three representative failed paths with per-year rows.
    failures: failedRuns.length ? (() => {
      const s = [...failedRuns].sort((a, b) => a.exhaustAge - b.exhaustAge);
      return { count: s.length, worst: s[0], typical: s[s.length >> 1], closest: s[s.length - 1] };
    })() : null,
    term: { p10: qt(0.1), p25: qt(0.25), p50: qt(0.5), p75: qt(0.75), p90: qt(0.9), mean: termMean },
    N,
  };
}

// Stress Test is the same engine as runMC, with the 2000-2012 equity
// sequence forced at retirement (sequence-of-returns risk). Delegating to
// runMC instead of carrying a parallel implementation guarantees one tax
// model, one sourcing waterfall, and one RMD/strategy code path across the
// Monte Carlo, the deterministic schedule, and the stress pivot. The
// non-resident/state-tax toggle, IRMAA, SS torpedo, and bracket caps all
// propagate to the stress number automatically, so no field can read
// differently here than anywhere else. (This used to run a flat taxDragRate
// heuristic that ignored p.twoHousehold, so the toggle had no effect on the
// stress success rate.)
function runStress(p, endAge, N = STRESS_PATHS, seed = 99) {
  return runMC(p, endAge, N, seed, true, SEQ_2000_2012);
}

// Deterministic withdrawal schedule (median returns)

function simulateDeterministicWithStrategy(p, inf, strategyArg) {
  // The strategy arrives as an argument here (the Withdrawal tab passes its
  // preview selection, not p.withdrawalStrategy), so it needs its own guard
  // — migrating the saved profile alone doesn't protect this entry point.
  const withdrawalStrategy = resolveStrategy(strategyArg ?? p.withdrawalStrategy);
  // Smart Waterfall: source the schedule directly from
  // buildWithdrawalWaterfall's "smart" scenario, the one source of truth for
  // bucket draws, Roth conversions, mortgage/carveout costs, and
  // source-aware tax. This is the real-life year-by-year plan, not a
  // re-derived approximation.
  if (withdrawalStrategy === "smart") {
    const wf = buildWithdrawalWaterfall(p);
    const { total: portAtRetire } = accumulateToRetirement(p);
    const schedule = (wf?.smart?.rows ?? []).map((r) => ({
      age: r.age, yr: r.yr,
      spending: r.spending,
      ss: r.ss, Rental: r.annuityRental, OtherIncome: r.otherIncome,
      portfolioDraw: r.needFromPort,
      housingCost: r.housingCost,
      carveoutCost: r.carveoutCost,
      healthcareRisk: r.healthcareRisk,
      conversionAmount: r.conversionAmount,
      // Carried so this table can disclose the same reconciliation the
      // Withdrawal Plan table does. It shows the identical conversion
      // figure, so it invites the identical (wrong) comparison against a
      // nominal published bracket top.
      bracketTopYr: r.bracketTopYr, stdDedYr: r.stdDedYr,
      taxableIncome: r.taxableIncome, totInc: r.totInc,
      marginalBracket: r.marginalBracket, convCapReason: r.convCapReason,
      fedTax: r.fedTax,
      stateTax: r.stateTax,
      irmaa: r.irmaa,
      totalTax: r.totalTax,
      totalWithdrawal: r.totalWithdrawal,
      portfolioEnd: r.totalPort,
    }));
    const initWR = portAtRetire > 0 ? Math.max(0, p.sp - (schedule[0]?.ss || 0) - (schedule[0]?.Rental || 0) - (schedule[0]?.OtherIncome || 0)) / portAtRetire : 0.04;
    return { schedule, portAtRetire: Math.round(portAtRetire), initWR };
  }

  // Clamped for already-retired users — see effectiveRetireAge.
  const retAgeDet = effectiveRetireAge(p.retireAge, p.currentAge);
  // Same resolved switch age runMC uses, so the deterministic table and the
  // Monte Carlo it sits beside model the same investor (this engine used to
  // hardcode 62 in its retirement loop).
  const glideSwitchAgeDet = resolveGlidepathSwitchAge({ ...p, retireAge: retAgeDet });
  const accYrs = Math.max(0, retAgeDet - p.currentAge);
  const preRetFlat = flatPreRetReturn(p);
  // Same horizon rule as runMC.
  const planEndDet = planEndAgeOnPrimaryClock(p, p.endAge);
  const retYrs = planEndDet - retAgeDet;
  // `p.port` is the aggregate starting balance the UI keeps in sync with the
  // sum of `accounts` (see the account editor and the import path, which both
  // recompute one from the other). Callers that build a profile from accounts
  // alone — tests, and any programmatic entry point — leave it undefined, and
  // reading it raw poisoned the whole schedule with NaN from year 0: the
  // portfolio went NaN on the first draw, so every subsequent GK call tripped
  // the `portfolioValue <= 0 || isNaN` guard and silently returned the floor
  // with an empty guardrail sink (no event/reason/wr recorded). Fall back to
  // the accounts sum so an accounts-only profile behaves identically to the
  // in-sync UI profile; when `p.port` is a finite number, nothing changes.
  let port = Number.isFinite(p.port)
    ? p.port
    : (p.accounts || []).reduce((s, a) => s + (a.balance || 0), 0);

  // Accumulation using median returns
  for (let y = 0; y < accYrs; y++) {
    const ret = preRetFlat ?? expectedReturn(glidepathEqPct(p.currentAge + y, p.preRetireEq, p.postRetireEq, glideSwitchAgeDet)) / 100;
    // Aggregate portfolio here (no per-bucket split in this engine), but the
    // total needs to include every contribution stream runMC applies or the
    // two engines report different portfolio-at-retirement figures.
    // Job-bound streams via the shared per-person-contributions helper —
    // same per-person stop rule runMC applies, or the two engines disagree
    // the moment a spouse retires first.
    const jcDet = jobContributionsForYear(p, p.currentAge + y);
    port = port * (1 + ret)
      + jcDet.pretax + jcDet.roth + (p.hsaContrib || 0)
      + (p.taxableContrib || 0);
    // Pre-retirement one-off inflows (inheritance, lump-sum pension) get
    // deposited like contributions — same fix as runMC's accumulation loop;
    // this engine tracks one aggregate portfolio, so the inflow just adds.
    const evAccDet = computeCashFlowEvents(p.cashFlowEvents, CURRENT_YEAR + y, p.inf ?? 2.5, CURRENT_YEAR);
    if (evAccDet.inflow > 0) port += evAccDet.inflow;
  }

  const portAtRetire = port;
  // Precompute the actual annual mortgage cash cost per calendar year
  // (including extra payments and the partial payoff year), same model as
  // buildWithdrawalWaterfall/runMC.
  let mortByYear = new Map();
  if (p.mortBalance > 0) {
    const ms = mortgageSchedule(p.mortBalance, p.mortRate ?? 6.5, p.mortStart || "2020-01", p.mortTerm || 30, p.mortExtra || 0);
    mortByYear = mortgageAnnualPayments(ms);
  }
  const gkFloor = p.gkFloor || GK_FLOOR_FALLBACK;
  const gkCeiling = p.gkCeiling || GK_CEILING_FALLBACK;
  // Baseline initWR = net portfolio need at retirement / portfolio, no tax,
  // same quantity the yearly loop's GK netNeed offset computes (mirrors
  // runMC/buildWithdrawalWaterfall's calibration). ab0 includes propIncome
  // to match this engine's own `ab` term in the yearly loop below.
  const ss0 = computeHouseholdSS(p, retAgeDet);
  const ab0 = (p.ab > 0 ? p.ab : 0) + (p.propIncome || 0);
  const calYear0 = CURRENT_YEAR + (retAgeDet - p.currentAge);
  const { total: otherInc0 } = computeOtherIncome(p.otherIncomes, calYear0);
  const housingType0 = p.housingType || "own";
  let housing0 = 0;
  if (housingType0 === "own") {
    housing0 = mortByYear.get(calYear0) || 0;
  } else if (housingType0 === "rent") {
    housing0 = Math.round(p.annualRent || 0);
  }
  const carveout0 = (p.carveouts || []).reduce((sum, c) => {
    return sum + (calYear0 <= (c.endYear || 9999) ? Math.round(c.annual || 0) : 0);
  }, 0);
  const initNeed0 = Math.max(0, p.sp - ss0 - ab0 - otherInc0) + housing0 + carveout0;
  const initWR = portAtRetire > 0 ? initNeed0 / portAtRetire : 0.04;
  // Source-aware tax: reuse the Smart Waterfall engine so the tax column
  // here matches what the Waterfall tab shows for the same age. The
  // waterfall knows each account type (cash/taxable/pretax/roth) and only
  // treats pretax draws as ordinary income — without this override,
  // calcYearTax would treat every portfolio draw as ordinary income, badly
  // overstating fed tax for years that draw from taxable brokerage or Roth.
  const smartTaxByAge = new Map();
  try {
    const wf = buildWithdrawalWaterfall(p);
    for (const row of wf?.smart?.rows ?? []) {
      smartTaxByAge.set(row.age, {
        fedTax: row.fedTax || 0,
        stateTax: row.stateTax || 0,
        irmaa: row.irmaa || 0,
        totalTax: row.totalTax || 0,
        // Real cash costs the waterfall itself draws real money to cover
        // (irmaaFull = fedTax+stateTax+irmaa; earlyPenalty is the separate
        // IRC 72(t) 10% additional tax) — omitted here before, so this
        // table's totalDraw only ever counted fed+state, understating the
        // draw (and overstating Portfolio End) in any year either applies.
        irmaaFull: row.irmaaFull || 0,
        earlyPenalty: row.earlyPenalty || 0,
      });
    }
  } catch { /* fall back to calcYearTax below */ }

  // Same "Tax drag" master toggle as runMC — off zeroes all tax so the
  // deterministic table matches the MC/Stress pivots under a pre-tax view.
  const taxEnabled = p.tax !== false;

  // The year loop runs twice.
  // The tax columns are borrowed from buildWithdrawalWaterfall, and that's
  // correct — the waterfall is account-aware, so only pre-tax draws are
  // ordinary income, whereas calcYearTax on an aggregate draw treats every
  // dollar as ordinary and badly overstates tax. The bug was that the
  // waterfall taxed its own spend path (the GK/Bengen hybrid) no matter
  // which strategy was selected, so the draw on screen and the tax beside it
  // described different plans. Measured before this fix: five strategies
  // drawing between $2.28M and $3.45M over a lifetime all displayed the same
  // $210,686 of tax.
  //
  // So: pass 1 discovers this strategy's spend path, the waterfall gets
  // re-run against that path, and pass 2 uses the resulting per-age tax.
  //
  // Everything the loop mutates (`port`, `sp`, `lastReturn`, `schedule`) is
  // declared inside runPass, so a second call starts from the same state as
  // the first. `portAtRetire` is the accumulation result and is read-only here.
  const runPass = (taxByAge) => {
  let port = portAtRetire;
  let sp = p.sp;
  let lastReturn = 0;
  const schedule = [];
  // Pre-smile spend per calendar year — the input to pass 2's waterfall.
  // Pre-smile matters here: buildWithdrawalWaterfall applies
  // spendingSmileFactor after its spSchedule override, so feeding back the
  // post-smile `spending` field would apply the smile twice. Measured on a
  // $1.5M/$80k profile: $20,913 too little at age 80.
  const spByYear = [];

  for (let y = 0; y < retYrs; y++) {
    const age = retAgeDet + y;
    const yr = CURRENT_YEAR + (age - p.currentAge);
    const ret = expectedReturn(glidepathEqPct(age, p.preRetireEq, p.postRetireEq, glideSwitchAgeDet)) / 100;
    const inflY = inf / 100;
    const cumInfl = Math.pow(1 + inflY, y);
    const adjFloor = gkFloor * cumInfl;
    const adjCeiling = gkCeiling * cumInfl;

    // Deterministic income/fixed-cost pieces — moved above the strategy switch
    // so GK's netNeed offset can use this year's own figures (no rand() in
    // this engine, so there's no draw-order concern to preserve here).
    // COLA compounds from the claiming age (p.ssAge), not the retirement-year
    // loop counter `y` — see runMC's identical fix above.
    const ss = computeHouseholdSS(p, age);
    const growthFactor = Math.pow(1 + (p.abGrowth ?? 3)/100, Math.min(y, 20));
    const rawAb = Math.round(((p.ab > 0 ? p.ab : 0) + (p.propIncome || 0)) * growthFactor);
    const ab = (p.abEndYear && yr > p.abEndYear) ? 0 : rawAb;
    const { total: otherIncTotal } = computeOtherIncome(p.otherIncomes, yr);

    // Housing cost: mortgage cash cost while active, or inflation-adjusted
    // rent — same model as buildWithdrawalWaterfall's Step 1.
    let housingCost = 0;
    if ((p.housingType || "own") === "own") {
      housingCost = mortByYear.get(yr) || 0;
    } else if (p.housingType === "rent") {
      housingCost = Math.round((p.annualRent || 0) * cumInfl);
    }
    const carveoutCost = (p.carveouts || []).reduce((sum, c) => {
      return sum + (yr <= (c.endYear || 9999) ? Math.round((c.annual || 0) * cumInfl) : 0);
    }, 0);

    // GK's netNeed income offset — same net portfolio need quantity initWR
    // was calibrated against.
    const evDet = computeCashFlowEvents(p.cashFlowEvents, yr, p.inf ?? 2.5, CURRENT_YEAR);
    const eventCostDet = evDet.total;
    // This engine tracks one aggregate portfolio, so an inflow just adds to
    // it; the per-bucket routing that matters for tax lives in runMC and
    // buildWithdrawalWaterfall.
    if (evDet.inflow > 0) port += evDet.inflow;

    // Advisory only, not charged to this year's draw on purpose. See the
    // matching note in buildWithdrawalWaterfall: this is E[X] on a median
    // path, and this table is what a real person enacts. At the default
    // 3.5%/yr the median shock is $0, so charging $3,500 produced a
    // withdrawal figure that's wrong in every actual year. runMC still draws
    // it stochastically, so the success rate beside this table still prices
    // the risk correctly.
    const hcRiskDet = expectedHealthcareShock(age, p, cumInfl);

    const gkIncomeOffset = ss + ab + otherIncTotal;
    const gkFixedCosts = housingCost + carveoutCost + evDet.committed;

    // What the guardrail does this year, recorded rather than re-derived. Set
    // by guytonKlingerWithdrawal via its `out` sink, consumed by the schedule
    // row below and charted in the Guardrails view.
    let gkEvent = null;
    // The spend this year STARTS from, before the strategy touches it. GK's
    // adjustments are relative to last year's figure, so the chart needs both
    // ends of that move to show what a cut or raise actually did.
    const spEntering = sp;

    // Apply withdrawal strategy (deterministic version)
    if (p.spSchedule && p.spSchedule.length) {
      // Detailed budget overrides the distribution strategy (see runMC note).
      sp = scheduleSpendForYear(p.spSchedule, yr, p.inf ?? 2.5);
    } else if (y === 0) {
      // first year: use target spend
    } else {
      if (withdrawalStrategy === "gk") {
        const refWRd = gkReferenceWR({ plannedSpend: p.sp, cumInfl, incomeOffset: gkIncomeOffset, fixedCosts: gkFixedCosts, portAtRetire });
        // `gkEvent` captures what the guardrail did, so the schedule can chart
        // cuts and raises without re-deriving the rules (see the `out` param).
        gkEvent = {};
        sp = guytonKlingerWithdrawal(port, refWRd, sp, lastReturn, inflY, adjFloor, adjCeiling, p.endAge - age, gkIncomeOffset, gkFixedCosts, gkEvent);
      }
      else if (withdrawalStrategy === "fixed") {
        // Pure fixed %: draw = rate × port. No GK clamp.
        const fixedRate = p.fixedWithdrawalRate ?? 0.04;
        sp = port * fixedRate;
      }
      else if (withdrawalStrategy === "vpw") {
        // VPW PMT payout rate: r / (1 - (1+r)^(-n)). Has to match runMC exactly.
        const rVPW = p.vpwRealReturn ?? 0.0376;
        // Follows the plan-to age — see the matching note in runMC.
        const n = Math.max(1, (p.vpwEndAge ?? p.endAge ?? 100) - age);
        const rate = rVPW === 0 ? 1 / n : rVPW / (1 - Math.pow(1 + rVPW, -n));
        const newSp = port * Math.min(0.10, rate);
        sp = Math.max(adjFloor, Math.min(adjCeiling, newSp));
      }
      else if (withdrawalStrategy === "ninety_five_rule") {
        if (y === 1) sp = p.sp;
        else {
          const inflated = sp * (1 + inflY);
          const floor95 = sp * 0.95;
          sp = Math.max(floor95, inflated);
        }
        sp = Math.max(adjFloor, Math.min(adjCeiling, sp));
      }
      else if (withdrawalStrategy === "bengen") {
        // Bengen 4% rule: inflation-adjusted constant. Does not react to portfolio.
        sp = sp * (1 + inflY);
      }
      else if (withdrawalStrategy === "smart") {
        // Smart Waterfall hybrid: GK when yearsRemaining > 15, Bengen when
        // <= 15. The split matches GK's own longevity-clause threshold so we
        // exit GK exactly where its safety brake would otherwise be disabled.
        const yrsRemaining = p.endAge - age;
        if (yrsRemaining > 15) {
          const refWRd2 = gkReferenceWR({ plannedSpend: p.sp, cumInfl, incomeOffset: gkIncomeOffset, fixedCosts: gkFixedCosts, portAtRetire });
          sp = guytonKlingerWithdrawal(port, refWRd2, sp, lastReturn, inflY, adjFloor, adjCeiling, yrsRemaining, gkIncomeOffset, gkFixedCosts);
        } else {
          sp = sp * (1 + inflY);
        }
      }
      else {
        // Unreachable via resolveStrategy; see the matching note in runMC.
        sp = sp * (1 + inflY);
      }
    }
    lastReturn = ret;

    // Blanchett spending smile — a real lifestyle curve applied to this
    // year's spend. Not fed back into `sp` on purpose, since that's the
    // withdrawal strategy's running state: multiplying it would compound the
    // smile year over year and corrupt GK's own inflation logic. This is an
    // overlay on what the strategy decided, not a change to the strategy.
    const spSmiledDet = sp * spendingSmileFactor(age, retAgeDet, p.smile !== false);
    // hcRiskDet isn't in this sum — see its declaration above. Every term
    // here is a cash obligation the user actually pays this year.
    const need = Math.max(0, spSmiledDet - ss - ab - otherIncTotal) + housingCost + carveoutCost + eventCostDet;
    // This engine tracks one aggregate portfolio, so surplus income just
    // stays invested rather than evaporating. Same rule as the other two
    // engines, or they'd disagree about the balance — the drift class this
    // codebase keeps running into.
    const incomeSurplusDet = Math.max(0,
      (ss + ab + otherIncTotal) - (spSmiledDet + housingCost + carveoutCost + eventCostDet));

    // Prefer the Smart Waterfall's source-aware tax (matches the Waterfall
    // tab) — this path automatically carries LTCG/cost-basis and the IRMAA
    // 2-year lookback since buildWithdrawalWaterfall models both now. Fall
    // back to the legacy "treat everything as ordinary income" calc only
    // when no waterfall row exists for this age (e.g. accounts not
    // configured); that fallback has no taxable-bucket split (ltcgAmount
    // defaults to 0) and no lookback history to thread through (magiLookback
    // defaults to null, so same-year MAGI, the pre-lookback approximation) —
    // both unchanged and out of scope here.
    const wfTax = taxByAge.get(age);
    const taxResult = wfTax ?? calcYearTax(age, yr, need, ss, ab, 0, 0, p.twoHousehold || false, inflY, filingStatusAt(p, age), p.stateOfResidence || "NJ", 0, null, spouseAgeAt(p, age));
    // Kept for display (matches the waterfall's own fed+state-only totalTax,
    // shown alongside the separate irmaa column) — NOT what funds the draw.
    const totalTax = taxEnabled ? taxResult.totalTax : 0;
    // What actually has to be drawn: fed+state+IRMAA+early-withdrawal-penalty,
    // all real cash costs. When wfTax came from the waterfall, totalTax
    // deliberately excludes irmaa (irmaaFull carries fed+state+irmaa
    // instead) and never carried earlyPenalty at all — both omitted here
    // silently understated every year's draw (and overstated Portfolio End)
    // whenever either applied. The calcYearTax fallback's totalTax already
    // includes irmaa in its own definition; it never computes an early
    // penalty at all (a separate, pre-existing, out-of-scope gap for that
    // fallback path only — it has no per-category state to compute one from).
    const fundedTax = taxEnabled
      ? (wfTax ? (wfTax.irmaaFull + wfTax.earlyPenalty) : taxResult.totalTax)
      : 0;
    const totalDraw = need + fundedTax;
    // Draw-then-grow, matching buildWithdrawalWaterfall's own per-bucket
    // pattern (draw and surplus applied first, growth multiplies what's
    // left) — growing the FULL balance and only then subtracting the draw
    // credited a full year's growth to money that was actually withdrawn
    // and spent, silently overstating Portfolio End every year, whether or
    // not any tax applied.
    port = Math.max(0, port - totalDraw + incomeSurplusDet) * (1 + ret);

    // Pre-smile, and one entry for every plan year. Full coverage matters
    // here too: scheduleSpendForYear carries the last entry forward
    // inflated, so a sparse schedule would inflate an already-nominal figure
    // a second time. With an entry per year, `elapsed` is always 0 and each
    // value comes back verbatim (verified in deterministicTaxPath.test.js).
    spByYear.push({ year: yr, amount: Math.round(sp) });

    schedule.push({
      age, yr,
      spending: Math.round(spSmiledDet),
      // Guardrail provenance for this year, straight from the engine that
      // applied it (see guytonKlingerWithdrawal's `out` param). `spEntering`
      // and `spAfterGK` bracket the adjustment; floor/ceiling are the year's
      // inflation-adjusted bands, so the chart's dashed lines are the limits
      // the engine actually clamped to rather than a UI reconstruction.
      spEntering: Math.round(spEntering),
      spAfterGK: Math.round(sp),
      gkEvent: gkEvent ? gkEvent.event : null,
      gkReason: gkEvent ? gkEvent.reason : null,
      gkWR: gkEvent && gkEvent.currentWR != null ? gkEvent.currentWR : null,
      gkFloor: Math.round(adjFloor),
      gkCeiling: Math.round(adjCeiling),
      ss, Rental: ab, OtherIncome: Math.round(otherIncTotal),
      portfolioDraw: Math.round(need),
      housingCost: Math.round(housingCost),
      carveoutCost: Math.round(carveoutCost),
      // Advisory, NOT included in portfolioDraw/totalWithdrawal above.
      healthcareRisk: Math.round(hcRiskDet),
      conversionAmount: 0,
      fedTax: taxEnabled ? taxResult.fedTax : 0,
      stateTax: taxEnabled ? taxResult.stateTax : 0,
      irmaa: taxEnabled ? taxResult.irmaa : 0,
      totalTax,
      totalWithdrawal: Math.round(totalDraw),
      portfolioEnd: Math.max(0, Math.round(port)),
    });
    if (port <= 0) break;
  }
  return { schedule, spByYear };
  };

  // Pass 1 — the strategy's own spend path, still carrying the waterfall's
  // default-path tax. Its draws are already correct; only its tax is
  // borrowed from the wrong plan.
  const pass1 = runPass(smartTaxByAge);

  // When the user has supplied a detailed budget, that budget already
  // overrides the distribution strategy in both engines — the waterfall is
  // already taxing this exact spend path, so a second pass would just
  // recompute the same numbers and risk clobbering the user's own schedule.
  // Nothing to reconcile.
  if (p.spSchedule && p.spSchedule.length) {
    return { schedule: pass1.schedule, portAtRetire: Math.round(portAtRetire), initWR };
  }

  // Re-run the waterfall against this strategy's spending, then replay the
  // year loop with the tax that produces.
  //
  // Single iteration, on purpose. Pass 2's tax differs slightly from pass
  // 1's, which changes the portfolio, which changes next year's spend for
  // the portfolio-linked strategies (fixed, vpw). That residual is
  // second-order and much smaller than the defect being fixed, but it's
  // still a residual: the tax column is now computed against this
  // strategy's spending, not proven to be its exact fixed point.
  let pass2 = null;
  try {
    const wf2 = buildWithdrawalWaterfall({ ...p, spSchedule: pass1.spByYear });
    const taxByAge2 = new Map();
    for (const row of wf2?.smart?.rows ?? []) {
      taxByAge2.set(row.age, {
        fedTax: row.fedTax || 0,
        stateTax: row.stateTax || 0,
        irmaa: row.irmaa || 0,
        totalTax: row.totalTax || 0,
        irmaaFull: row.irmaaFull || 0,
        earlyPenalty: row.earlyPenalty || 0,
      });
    }
    // Only accept the second pass if it actually produced tax rows. An empty
    // map would silently drop every row back to the calcYearTax fallback,
    // which treats all draws as ordinary income — worse than the bug being fixed.
    if (taxByAge2.size > 0) pass2 = runPass(taxByAge2);
  } catch { /* keep pass 1 */ }

  const finalSchedule = (pass2?.schedule?.length ? pass2 : pass1).schedule;
  // pass1.spByYear is this strategy's own pre-smile spend path, in
  // buildWithdrawalWaterfall's spSchedule shape ([{year, amount}]) — exposed
  // so callers other than this function's own pass 2 (e.g.
  // waterfallForActiveStrategy below) can re-run the waterfall against the
  // ACTUAL selected strategy's spending instead of its own internal
  // GK/Bengen-at-15yr default. Not populated for withdrawalStrategy ===
  // "smart" (returned earlier above) or when p.spSchedule already overrides
  // every strategy identically — callers should treat a missing/empty
  // spByYear as "no override needed."
  return { schedule: finalSchedule, portAtRetire: Math.round(portAtRetire), initWR, spByYear: pass1.spByYear };
}

/**
 * The one place that decides what spend schedule buildWithdrawalWaterfall
 * should run against for a given profile — used by every UI surface that
 * needs the waterfall's sourcing/tax detail for the user's ACTUAL saved
 * strategy (the B1 End table, the Buckets tab's runway card, the
 * Income & Expenses chart, the year-end tax-room prompt), instead of each
 * one calling buildWithdrawalWaterfall(p) directly.
 *
 * buildWithdrawalWaterfall(p) with no spSchedule override always paces
 * spending with its OWN internal GK-guardrail/Bengen-at-15yr hybrid — the
 * "Smart Waterfall" distribution option — regardless of p.withdrawalStrategy.
 * That's correct when the strategy actually IS "smart" (or the user supplied
 * an explicit spSchedule import, which already overrides every strategy
 * identically), but for gk/bengen/fixed/vpw/ninety_five_rule it silently
 * substitutes a different plan's spending for the one on screen — the same
 * defect simulateDeterministicWithStrategy's own comment documents fixing
 * for ITS tax column ("five strategies... all displayed the same $210,686 of
 * tax"). This applies that same fix — discover the strategy's own spend path
 * first, then re-run the waterfall against it — to every other consumer.
 */
function waterfallForActiveStrategy(p) {
  const strategy = resolveStrategy(p?.withdrawalStrategy);
  if (strategy === "smart" || (p?.spSchedule && p.spSchedule.length)) {
    return buildWithdrawalWaterfall(p);
  }
  const det = simulateDeterministicWithStrategy(p, p?.inf ?? 2.5, strategy);
  if (!det?.spByYear?.length) return buildWithdrawalWaterfall(p); // defensive: no path found, fall back rather than throw
  return buildWithdrawalWaterfall({ ...p, spSchedule: det.spByYear });
}

// Roth conversion explorer
// 2026 MFJ federal brackets (inflation-adjusted from 2025)
const FED_BRACKETS_2026_MFJ = [
  { lo: 0,       hi: 24800,  rate: 0.10 },
  { lo: 24800,   hi: 100800, rate: 0.12 },
  { lo: 100800,  hi: 211400, rate: 0.22 },
  { lo: 211400,  hi: 403550, rate: 0.24 },
  { lo: 403550,  hi: 512450, rate: 0.32 },
  { lo: 512450,  hi: 768700, rate: 0.35 },
  { lo: 768700,  hi: Infinity, rate: 0.37 },
];
// 2026 Single filer federal brackets (~half the MFJ thresholds)
const FED_BRACKETS_2026_SINGLE = [
  { lo: 0,      hi: 12400,  rate: 0.10 },
  { lo: 12400,  hi: 50400,  rate: 0.12 },
  { lo: 50400,  hi: 105700, rate: 0.22 },
  { lo: 105700, hi: 201800, rate: 0.24 },
  { lo: 201800, hi: 256225, rate: 0.32 },
  { lo: 256225, hi: 640600, rate: 0.35 },
  { lo: 640600, hi: Infinity, rate: 0.37 },
];

// IRS Pub 590-B Table III (Uniform Lifetime) divisors, 2022+ table.
// Default table for owners whose sole-beneficiary spouse is NOT >10 years younger.
// (The >10-years-younger Joint & Last Survivor case uses jointRmdDivisor from src/data/jointRmdTable.js.)
const RMD_DIV = {
  72: 27.4,
  73: 26.5,
  74: 25.5,
  75: 24.6,
  76: 23.7,
  77: 22.9,
  78: 22.0,
  79: 21.1,
  80: 20.2,
  81: 19.4,
  82: 18.5,
  83: 17.7,
  84: 16.8,
  85: 16.0,
  86: 15.2,
  87: 14.4,
  88: 13.7,
  89: 12.9,
  90: 12.2,
  91: 11.5,
  92: 10.8,
  93: 10.1,
  94: 9.5,
  95: 8.9,
  96: 8.4,
  97: 7.8,
  98: 7.3,
  99: 6.8,
  100: 6.4,
  101: 6.0,
  102: 5.6,
  103: 5.2,
  104: 4.9,
  105: 4.6,
};

function progTax(ti, br) {
  let t = 0;
  for (const b of br) {
    if (ti <= b.lo) break;
    t += Math.max(0, Math.min(ti, b.hi) - b.lo) * b.rate;
  }
  return t;
}
function idxB(br, f) {
  return br.map((b) => ({
    lo: Math.round(b.lo * f),
    hi: b.hi === Infinity ? Infinity : Math.round(b.hi * f),
    rate: b.rate,
  }));
}
/**
 * @param {number|null} beneficiaries  How many people in the household are
 *   actually on Medicare this year (65+). IRMAA thresholds are per tax
 *   return — they stay keyed to filing status — but the surcharge is per
 *   beneficiary, so a couple where only one has reached 65 pays one
 *   surcharge against the MFJ threshold. This used to always charge two for
 *   MFJ, overstating Medicare cost for the whole age gap. `null` keeps the
 *   old assumption (2 for MFJ, 1 for single) for callers with no spouse age.
 */
function irmaaCost(magi, yr, infR = 0.025, isMFJ = true, beneficiaries = null) {
  const f = Math.pow(1 + (isNaN(infR) ? 0.025 : infR), yr - CURRENT_YEAR);
  // IRMAA_2026[i].f is the two-person MFJ surcharge, so half of it is one person's.
  const n = beneficiaries != null ? Math.max(0, beneficiaries) : (isMFJ ? 2 : 1);
  if (n === 0) return 0;
  for (let i = IRMAA_2026.length - 1; i >= 0; i--) {
    // Single tiers are half the MFJ thresholds, except the top tier ($500,000 vs $750,000).
    const thresh = isMFJ ? IRMAA_2026[i].m
      : (i === IRMAA_2026.length - 1 ? 500_000 : IRMAA_2026[i].m / 2);
    const cost = (IRMAA_2026[i].f / 2) * n;
    // CMS: tiers 1-4 start ABOVE the ceiling (MAGI <= $218,000 pays nothing); only the top tier is "greater than or equal".
    if (i === IRMAA_2026.length - 1 ? magi >= thresh * f : magi > thresh * f) return Math.round(cost * f);
  }
  return 0;
}

const ROTH_BASE_YEAR = new Date().getFullYear();

/**
 * SECURE Act 2.0 RMD start age.
 * Born before 1951 → 72 (pre-SECURE 2.0 transition)
 * Born 1951–1959  → 73
 * Born 1960+      → 75
 * Accepts either a birth year (number) or an ISO dob string ("YYYY-MM-DD").
 */
function getRmdStartAge({ dob, birthYear, currentAge } = {}) {
  let by = null;
  if (typeof birthYear === "number" && birthYear > 0) by = birthYear;
  else if (typeof dob === "string" && dob.length >= 4) {
    const y = parseInt(dob.slice(0, 4), 10);
    if (!isNaN(y)) by = y;
  } else if (typeof currentAge === "number" && currentAge > 0) {
    by = ROTH_BASE_YEAR - currentAge;
  }
  if (by === null) return 73; // safe default
  if (by >= 1960) return 75;
  if (by >= 1951) return 73;
  return 72;
}

// Disclaimers
/**
 * Section-level "this is a projection, not advice" notice.
 *
 * The footer disclaimer covers the app, but a user who lands on a results
 * tab, reads a success rate or a year-by-year withdrawal table, and acts on
 * it might never scroll to the footer at all. The two surfaces that most
 * resemble advice — a probability of success, and a schedule saying how much
 * to take from which account — carry it inline, next to the number instead
 * of a page away.
 *
 * One component, not a retyped string, so the wording can't drift between
 * sections.
 *
 * ── Why it is collapsed, and why that is still enough ─────────────────────
 * The full paragraph was four lines of amber on every results tab — a lot of
 * prime screen for text nobody rereads. It now ships as a one-line short-form
 * notice with the long form one click away.
 *
 * The legal line this walks: what a disclaimer has to do is WARN, and the two
 * load-bearing hooks of this one are "not financial advice" and "consult a
 * professional before acting". Both stay in the ALWAYS-VISIBLE line — they are
 * never behind the rollout. Only the elaboration (that a success rate is a
 * share of simulated scenarios rather than a probability, that past results
 * don't predict future ones) is collapsed, and that elaboration is also
 * permanent in the footer and the About panel regardless of this control.
 * So the collapsed state is a complete short-form disclaimer on its own, not
 * a stub that needs expanding to mean anything.
 *
 * Also note: a roll-out disclosure is a real control, not a tooltip. The old
 * `title=`-only warnings were invisible on touch — the failure mode this
 * codebase keeps hitting — so the toggle is a button with aria-expanded, and
 * the text expands inline in the page rather than into a dialog.
 */
function SectionDisclaimer({ children, summary }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      role="note"
      style={{
        fontSize: "var(--fs-small)",
        lineHeight: 1.55,
        color: "var(--text-muted)",
        background: "rgba(251,191,36,0.04)",
        border: "1px solid rgba(251,191,36,0.16)",
        borderLeft: "2px solid rgba(251,191,36,0.55)",
        borderRadius: 7,
        padding: "6px 10px",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ minWidth: 0 }}>
          <strong style={{ color: "#fbbf24" }}>Not financial advice.</strong>{" "}
          {summary || "Hypothetical model projections, not predictions. Consult a licensed professional before acting."}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            color: "#fbbf24", fontSize: "var(--fs-small)", fontWeight: 700, fontFamily: "var(--font-sans)",
            whiteSpace: "nowrap", textDecoration: "underline", textUnderlineOffset: 2,
          }}
        >
          {open ? "Hide full disclosure ▴" : "Full disclosure ▾"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 7, paddingTop: 7, borderTop: "1px solid rgba(251,191,36,0.16)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Icons
/**
 * Info icon, drawn as SVG instead of the Unicode "ⓘ" (U+24D8) it replaces.
 *
 * The glyph looked wrong for reasons no amount of CSS could fix: its shape
 * comes from whatever font happens to resolve it, so the circle's weight,
 * diameter, and baseline offset all changed between platforms and between
 * the app's two typefaces. At the 10-11px size it was used at, the ring
 * rendered hairline-thin and sat a pixel or two above the text it
 * annotated. A vector draws identically everywhere, stays crisp at any
 * size, and lines up on the text baseline.
 *
 * Inherits color through `currentColor`, so callers keep styling it with
 * plain `color` — including hover transitions — exactly as they did the glyph.
 */
function InfoIcon({ size = 14, title, style }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16"
      fill="none" aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      focusable="false"
      style={{ flexShrink: 0, verticalAlign: "-0.15em", ...style }}
    >
      {title && <title>{title}</title>}
      <circle cx="8" cy="8" r="6.9" stroke="currentColor" strokeWidth="1.3" opacity="0.75" />
      <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
      <path d="M8 7.15v4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The standard hoverable info affordance: an InfoIcon in a muted circular
 * well that brightens on hover, with the explanation as a native tooltip.
 * Replaces the hand-rolled `infoDot` style object that used to get retyped
 * at each call site.
 */
// Root-cause fix (2026-09-23): this used to be a `title=` tooltip only, which
// shows nothing on many setups and is dead on touch — the exact trap that hid
// the Success Rate card's explanation. Now it opens the same click InfoModal
// the rest of the app uses, so the info actually displays. Same props, so
// every caller is fixed at once. `heading` optionally titles the dialog.
function InfoDot({ title, heading = "What this means", size = 14, color = "var(--text-secondary)", hoverColor = "var(--text-primary)" }) {
  const [hover, setHover] = useState(false);
  return (
    <InfoModal
      title={heading}
      trigger={
        <span
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: size + 6, height: size + 6, borderRadius: "50%",
            background: hover ? "var(--tint-white-15)" : "var(--card-border)",
            color: hover ? hoverColor : color,
            cursor: "pointer", transition: "background 0.15s, color 0.15s",
          }}
        >
          <InfoIcon size={size} />
        </span>
      }
    >
      <div style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.7 }}>{title}</div>
    </InfoModal>
  );
}

// ── "How this simulation works" — the rich Chance-of-Success method modal.
// Single entry point: the Success Rate card's ⓘ (design-authority verdict,
// 2026-09-24 — the old inline "View method & data" card was a duplicate
// trigger to this same dialog and was deleted). Describes AiRA's ACTUAL engine:
// a bootstrap Monte Carlo that draws random years (with replacement, order
// reshuffled) from the real 1928–2025 Damodaran arrays runMC samples
// (SP500/BONDS/INFL) — NOT a sequential historical replay. The pool-stats box
// characterizes that distribution honestly instead of aping a year-scrubber.
// One plain-language description per withdrawal strategy, keyed by resolved
// strategy id. Module scope so every ⓘ that opens SimMethodModal (the hero
// "Success to Age" ⓘ and the Monte Carlo tab's Success Rate card) reads the
// same text — it used to be local to MCTab, which is why the hero ⓘ could not
// reach the rich modal at all.
const STRATEGY_HOW_IT_WORKS = {
  gk: "Guyton‑Klinger guardrails — Every year, if the current withdrawal rate exceeds 120% of the initial rate, spending cuts 10% (never below floor). If it falls below 80%, spending increases 10% (never above ceiling).",
  fixed: "Fixed Percentage — You withdraw a constant percentage of the current portfolio each year, automatically adjusting with market value.",
  vpw: "Variable Percentage Withdrawal (VPW) — Spending is recalculated annually as the portfolio amortized over your remaining years, so the plan is designed to spend down to roughly zero by your plan-to age.",
  ninety_five_rule: "95% Rule — Spending can drop to 95% of last year's amount during downturns, otherwise tracks inflation.",
  bengen: "Bengen 4% Rule — Spend your planned annual amount in year one, then increase that dollar amount with inflation every year after (the \"4%\" is the rule's name, not a rate applied to your portfolio). Spending never reacts to portfolio performance, for better or worse — an honest model of late-stage risk for fixed-budget retirees.",
  smart: "Smart Waterfall (hybrid) — Guyton‑Klinger guardrails while more than 15 years remain in the plan, then switches to the Bengen 4% Rule for the final 15 years — the split matches GK's own longevity-safety-brake threshold, so the switch happens exactly where GK's brake would otherwise be disabled."
};

function SimMethodModal({ params, withdrawalStrategy, strategyHowItWorks = STRATEGY_HOW_IT_WORKS, trigger }) {
  const strat = resolveStrategy(withdrawalStrategy);
  // Honest characterization of the bootstrap POOL — the distribution runMC
  // samples from — NOT a year-by-year scenario table (that would ape a
  // sequential-backtest app; AiRA draws years at random, never in order).
  const pool = useMemo(() => {
    const eq = SP500.map((v) => v * 100);
    const infl = INFL.map((v) => (v ?? 0) * 100);
    const mean = (a) => a.reduce((s, v) => s + v, 0) / a.length;
    return {
      years: SP500.length,
      eqAvg: mean(eq),
      eqBest: Math.max(...eq),
      eqWorst: Math.min(...eq),
      downPct: Math.round((eq.filter((v) => v < 0).length / eq.length) * 100),
      inflAvg: mean(infl),
    };
  }, []);
  return (
    <InfoModal
      title="Chance of Success — How This Simulation Works"
      accent={INFO_ACCENT.method}
      maxWidth={640}
      bodyMaxHeight="72vh"
      trigger={trigger}
    >
      <div>
        <div>
          <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 10 }}>What is Chance of Success?</div>
          <div style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.7 }}>
            It runs your plan through <strong style={{ color: "var(--text-primary)" }}>{MC_PATHS_LABEL} Monte Carlo simulations</strong>. For <em>every year</em> of your retirement, each path draws a <strong style={{ color: "var(--text-primary)" }}>random</strong> year from <strong style={{ color: "var(--text-primary)" }}>{SAMPLE_YEARS} years of real market history (1928–2025)</strong> and applies that year’s actual S&P 500 return, 10-year Treasury return, and inflation <em>together</em> — so the real correlation between them survives (e.g. 2008’s −36.6% stocks paired with its +20.1% bond rally).
          </div>
          <div style={{ marginTop: 8, fontSize: "var(--fs-label)", color: "var(--text-muted)", lineHeight: 1.65 }}>
            Years are drawn <strong style={{ color: "var(--text-secondary)" }}>with replacement</strong> and the <strong style={{ color: "var(--text-secondary)" }}>order is reshuffled on every path</strong> — so 2008 might strike twice in one run and never in another. This is a bootstrap of history into thousands of <em>plausible</em> futures, <strong style={{ color: "var(--text-secondary)" }}>not a replay of one past sequence in calendar order</strong>.
          </div>
          <div style={{ marginTop: 10, fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.7 }}>
            A path “succeeds” if the portfolio still has money at age {params.endAge}. The success rate is the share of paths that survive. <strong style={{ color: "var(--accent-teal)" }}>Above {Math.round(MC_SOLID_PLAN_RATE * 100)}% is generally considered a solid plan.</strong>
          </div>
          <div style={{ marginTop: 10, fontSize: "var(--fs-label)", color: "var(--text-muted)", lineHeight: 1.6 }}>
            AiRA also applies <strong style={{ color: "var(--accent-gold)" }}>{getStrategyDescription(withdrawalStrategy)}</strong>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14, fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.6 }}>
            <div><div style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 3 }}>1. Accumulation (ages {params.currentAge}–{params.retireAge})</div>Each path draws a random paired S&P 500 + bond year, blended by glide path. Contributions added annually.</div>
            <div><div style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 3 }}>2. Retirement spending</div>Fresh random returns each year. {params.smile !== false ? "Blanchett smile curve." : "Flat real spending."} SS{params.ab > 0 ? " and rental" : ""} income offsets draws.</div>
            <div><div style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 3 }}>3. {getStrategyLabel(strat)} {strat === "gk" ? "guardrails" : "strategy"}</div>{strategyHowItWorks[strat] || strategyHowItWorks.gk}</div>
            <div><div style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 3 }}>4. Survival check</div>Balance above $0 through age {params.endAge} = success. The fan chart shows the 10th–90th percentile spread.</div>
          </div>
          <div style={{ marginTop: 14, background: "var(--tint-teal-600-08)", border: "1px solid var(--tint-teal-600-25)", borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--accent-teal)", marginBottom: 6 }}>Why this beats a single projection</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.7 }}>
              <li>Built on real historical market performance, not a guessed average</li>
              <li>Reports a probability and a range, not one false-precision number</li>
              <li>Captures sequence-of-returns risk — the order of good and bad years</li>
              <li>Surfaces both best-case and worst-case outcomes</li>
            </ul>
          </div>
          <div style={{ marginTop: 12, fontSize: "var(--fs-small)", color: "var(--text-faint)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--text-muted)" }}>Data source:</strong> Aswath Damodaran’s historical returns dataset (S&P 500, 10-year U.S. Treasury, CPI-U), 1928–2025, paired by calendar year. No winsorization — real tail years (1931, 2008, 2022) are kept.
          </div>
        </div>
        <div style={{ marginTop: 16, background: "var(--row-highlight)", border: "1px solid var(--card-border)", borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 3 }}>What the simulation draws from</div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.55 }}>
            The pool of <strong style={{ color: "var(--text-secondary)" }}>{pool.years} real market years (1928–2025)</strong>. Each simulated year picks one at random — <strong style={{ color: "var(--text-secondary)" }}>the past won’t repeat in order</strong>, so we reshuffle history, we don’t replay it.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))", gap: 10 }}>
            {[
              ["Avg stock year", `${pool.eqAvg >= 0 ? "+" : ""}${pool.eqAvg.toFixed(1)}%`, "var(--accent-teal)"],
              ["Best / worst", `+${pool.eqBest.toFixed(0)}% / ${pool.eqWorst.toFixed(0)}%`, "var(--text-secondary)"],
              ["Down years", `${pool.downPct}% of ${pool.years}`, "var(--chart-1)"],
              ["Avg inflation", `${pool.inflAvg.toFixed(1)}%`, "var(--accent-gold)"],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: 6, padding: "8px 10px" }}>
                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: c, fontFamily: "var(--font-mono)" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </InfoModal>
  );
}

// Formatters
/* The one money formatter. Whole dollars, grouped, no abbreviation, ever.
 * There used to be `fmtK` and `fmtM` alongside this, byte-identical to it
 * and to each other. Three names implying three formats invited exactly one
 * bug: something that actually did abbreviate (`landingMoney`) read as
 * normal because "we have a K formatter". Retirement numbers get compared,
 * not skimmed — $1,049,999 shown as "$1.0M" hides $50K. One name, one format. */
const fmtDollar = (v) => `$${Math.round(v).toLocaleString()}`;
/* Upper bound for any typed dollar field. ANumInput clamps to `max` on blur,
 * so a tight max silently rewrites what the user entered — the old $10M cap
 * on the portfolio target turned a $100M plan into a $10M one with no
 * warning. Input validation shouldn't encode an assumption about how rich
 * the user is — that's a presentation concern, not a data one. Not applied
 * to fields with a statutory ceiling (401k/Roth/HSA contributions, SS
 * benefit) or to rates, percentages and ages, where the bound is real
 * domain validation. */
const MAX_MONEY_INPUT = 1_000_000_000;
/* Gutter for a Y axis labeled in whole dollars. The per-chart widths used to
 * be 46-58px, which clipped seven-figure ticks by 4-6px ("$2,184,596"
 * measures 57px at fontSize 9) — a silently truncated axis number is worse
 * than a wide gutter. Sized for nine figures, since portfolios up to $250M
 * are enterable now. 92 since tick text went from 9px to 11px (--fs-small):
 * "$44,501,412" in mono at 11px needs ~84px with its tick margin. */
const MONEY_AXIS_WIDTH = 92;
const fmtPct = (v) => `${(v * 100).toFixed(1)}%`;

// Status dot: colour comes from a token and the words beside it carry the meaning.
const Dot = ({ tone = "info" }) => <span className={`dot dot-${tone}`} aria-hidden="true" />;

// Appending hex alpha to a color (`${c}44`) only works when `c` is a hex
// literal. The design-token migration turned many color sources into CSS
// tokens, and "var(--positive)44" isn't a color — the browser drops the
// whole declaration, so the tint and border silently vanish. A few of these
// sources are ternaries that return a token on one branch and a raw hex on
// the other (rateColor, wrColor), so the same card renders bordered at one
// value and borderless at another — that's how this survived, it looks like
// a theme quirk rather than a bug. color-mix handles tokens; hex inputs
// keep their exact prior string.
export const withAlpha = (color, hexAlpha) => {
  if (typeof color !== "string" || !color.includes("var(")) return `${color}${hexAlpha}`;
  const pct = Math.round((parseInt(hexAlpha, 16) / 255) * 1000) / 10;
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
};

// SINGLE SOURCE OF TRUTH for account-category label + color. Used by the
// Savings panel editor (SavingsPanel) AND the sidebar AllocationDonut, so the
// donut's slice colors can never drift from the account chips they summarize.
export const ACCOUNT_CATEGORIES = [
  { key: "pretax",  label: "Pre-Tax",        color: "#0ea5e9", defaultName: "401(k)" },
  { key: "roth",    label: "Roth",           color: "var(--accent-purple)", defaultName: "Roth IRA" },
  { key: "taxable", label: "Taxable",        color: "var(--accent-gold)", defaultName: "Brokerage" },
  { key: "hsa",     label: "HSA",            color: "var(--positive)", defaultName: "HSA" },
  { key: "cash",    label: "Cash / Savings", color: "var(--text-secondary)", defaultName: "Savings" },
];

// Tax-treatment donut of the CURRENT portfolio, by account category. Reads the
// same accounts + ACCOUNT_CATEGORIES the profile editor writes — no re-derived
// totals. Zero-balance categories are dropped so the ring shows only what the
// user actually holds. Drawn as a CSS conic-gradient ring rather than a
// Recharts Pie: a single-category (100%) portfolio is a full-circle slice,
// which Recharts 3.8 renders as an EMPTY path — conic-gradient has no such
// degenerate case, needs no chart lib mounted in an 84px box, and the
// radial mask punches the hole so it reads as a ring over any background.
function AllocationDonut({ accounts, onEdit }) {
  const data = ACCOUNT_CATEGORIES
    .map((c) => ({ ...c, value: (accounts || []).filter((a) => a.category === c.key).reduce((s, a) => s + (a.balance || 0), 0) }))
    .filter((d) => d.value > 0);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return null;
  let acc = 0;
  const stops = data.map((d) => {
    const start = (acc / total) * 360;
    acc += d.value;
    const end = (acc / total) * 360;
    return `${d.color} ${start}deg ${end}deg`;
  }).join(", ");
  const ringMask = "radial-gradient(circle, transparent 33px, #000 34px)";
  const Ring = onEdit ? "button" : "div";
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--tint-white-06)" }}>
      <h2 style={{ margin: "0 0 6px", fontWeight: 400, fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
        Tax Treatment
      </h2>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* The ring is the thing a new user reaches for to change the split,
            so with onEdit it IS the control. The mask lives on the inner span
            so it can't clip the button's focus outline. */}
        <Ring
          {...(onEdit ? { type: "button", className: "donut-btn", onClick: onEdit, "aria-label": "Edit account split" } : { style: { flexShrink: 0 } })}
          title={data.map((d) => `${d.label}: ${fmtDollar(d.value)} (${((d.value / total) * 100).toFixed(0)}%)`).join("\n")}
        >
          <span
            style={{
              display: "block", width: 84, height: 84, borderRadius: "50%",
              background: `conic-gradient(${stops})`,
              WebkitMask: ringMask, mask: ringMask,
            }}
          />
        </Ring>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
          {data.map((d) => (
            <div key={d.key} title={`${d.label}: ${fmtDollar(d.value)}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "var(--fs-small)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--text-secondary)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                {d.label}
              </span>
              <span style={{ color: "var(--text-muted)", fontFamily: "'JetBrains Mono',monospace" }}>
                {((d.value / total) * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Numeric entry
/* The one parser every typed number field uses. People paste what they see
 * — "$1,250,000" — and type shorthand — "1.25M", "750k". Raw `Number()`
 * returns NaN for all of those, and a field that drops NaN on the floor
 * silently discards the entry and snaps back to the old value on blur. That
 * reads as the app eating your data, with no error to explain it.
 *
 * Returns null when the text genuinely isn't a number, so the caller can
 * leave the existing value alone. Finiteness gets checked explicitly
 * because `Number("1e999")` is Infinity and passes `!isNaN` — Infinity in a
 * balance renders as "∞" and poisons every projection downstream of it. */
function parseNumericEntry(text) {
  const cleaned = String(text).trim().replace(/[$,%\s]/g, "");
  if (!cleaned) return null;
  const suffix = /[mk]$/i.test(cleaned);
  const mult = suffix ? (/m$/i.test(cleaned) ? 1e6 : 1e3) : 1;
  const n = Number(suffix ? cleaned.slice(0, -1) : cleaned);
  return Number.isFinite(n) ? n * mult : null;
}
/* Whole dollars at or above `min`. Returns null when the text isn't a number,
 * so the caller leaves the existing value untouched. */
function parseMoneyInput(text, min) {
  const n = parseNumericEntry(text);
  return n === null ? null : Math.max(min, Math.round(n));
}
/* All analogies in the band the rate falls into (band = highest `min` ≤ rate).
 * Exported for tests. The array is ordered highest-band-first, so the first
 * match's `min` identifies the band. */
export function getAnalogues(rate) {
  const pct = rate * 100;
  const first = ANALOGUES.find((a) => pct >= a.min) || ANALOGUES[ANALOGUES.length - 1];
  return ANALOGUES.filter((a) => a.min === first.min);
}

function getAnalogue(rate) {
  return getAnalogues(rate)[0];
}

/* Revolving analogy card — cycles through every analogy in the current band so
 * users see several independent real-world reference points for the same
 * number, each with its own (approximate, defensible) probability. Click
 * advances immediately; auto-advances every 7s. */
function RotatingAnalogue({ rate, endAge }) {
  const pool = getAnalogues(rate);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [rate]);
  useEffect(() => {
    if (pool.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % pool.length), 7000);
    return () => clearInterval(t);
  }, [pool.length, rate]);
  const a = pool[idx % pool.length];
  return (
    <div
      className="analogue"
      onClick={() => pool.length > 1 && setIdx((i) => (i + 1) % pool.length)}
      style={{ cursor: pool.length > 1 ? "pointer" : "default" }}
      title={pool.length > 1 ? "Click for another comparison" : undefined}
    >
      <span key={idx} className="analogue-fade" style={{ display: "inline" }}>
        {a.emoji} “{a.text}{a.stat ? ` (${a.stat})` : ""}.” — your plan: {fmtPct(rate)} to age {endAge}.
      </span>
      {pool.length > 1 && (
        <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>
          {pool.map((_, i) => (
            <span key={i} style={{ color: i === idx % pool.length ? "var(--text-secondary)" : "#334155", fontSize: "var(--fs-small)", marginRight: 3 }}>●</span>
          ))}
        </span>
      )}
    </div>
  );
}

// dollarBasisLabel/deflate/mcMedianAtAge/selectPortfolioAtAge moved to
// engine/mcSelectors.js (imported at the top of this file) so the rules
// engine, score explainer, and printable report can share them without a
// circular import back into this file. The only thing ever wrong here was
// the label "today's dollars", which promised a basis the math does not
// use — see mcSelectors.js's own header for the full history.

/* Per-age band table under the Monte Carlo fan chart — the same percentile
 * data the chart plots (deflated the same way when Real $ is on), one row
 * per age, plus the share of simulated paths still funded at that age.
 * Milestone rows (SS claiming, RMD start) are flagged so the table reads
 * like the chart's reference lines. */
function MCBandTable({ pcts, inf, useReal, ssAge, rmdAge, currentAge, endAge, hoveredAge, onHoverAge, totalPaths, retireAge, landmarkOnly = false, embedded = false }) {
  const [show, setShow] = useState(embedded);
  // "What do these numbers mean?" — an inline, mobile-readable explainer
  // (not a browser tooltip) condensing the About page's "still-funded-percent"
  // card. Independent of `show` so it's reachable even with the table collapsed.
  const [showExplainer, setShowExplainer] = useState(false);
  const data = useMemo(() => deflate(pcts, inf, useReal), [pcts, inf, useReal]);
  if (!data || data.length === 0) return null;
  // Landmark mode: fewer rows of the SAME table, for a summary surface. The
  // Overview tab wants a handful of ages, not forty — but a condensed COPY of
  // this table would be a second reader of `mc.pcts`, which is the anti-goal
  // (REQUIREMENTS §41 §5). So the rows are filtered here and every cell below
  // still comes from one implementation.
  // Ages kept: the first, your retirement, SS claim, Medicare, first RMD, the
  // decade markers inside the horizon, and the plan age — enough to see the
  // shape without a second table.
  const rows = useMemo(() => {
    if (!landmarkOnly) return data;
    const keep = new Set([data[0]?.age, data[data.length - 1]?.age, retireAge, ssAge, rmdAge, 60, 65, 70, 75, 80, 85].filter((a) => a != null));
    const picked = data.filter((d) => keep.has(d.age));
    // Never render an empty table if the horizon missed every landmark.
    return picked.length ? picked : [data[0], data[data.length - 1]].filter(Boolean);
  }, [data, landmarkOnly, retireAge, ssAge, rmdAge]);
  // Colour comes from the shared band helper, not a local copy. This used to be
  // `a >= 0.9 ? green : a >= 0.75 ? gold : red` — inline literals that had
  // already drifted from the app's own bands (0.75 is not a band edge; 0.80 is).
  // Two definitions of "a good score" is how five copies happened once before.
  const fundedColor = (a) => rateColor(a ?? 1);
  return (
    <div className={embedded ? undefined : "chart-card"}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: show || showExplainer ? 8 : 0, flexWrap: "wrap", gap: 6 }}>
        <div className="ct" style={{ marginBottom: 0 }}>
          Age-by-Age Projection Bands · {dollarBasisLabel(useReal, retirementBasisYear(currentAge, retireAge, CURRENT_YEAR))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => setShowExplainer(!showExplainer)}
            className="mbtn"
            style={{ fontSize: "var(--fs-label)", padding: "3px 8px" }}
          >
            ℹ️ What do these numbers mean?
          </button>
          {!embedded && (
            <button onClick={() => setShow(!show)} className="mbtn" style={{ fontSize: "var(--fs-label)", padding: "3px 8px" }}>
              {show ? "Hide Table" : "Show Table"}
            </button>
          )}
        </div>
      </div>
      {showExplainer && (
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.6, background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          <strong style={{ color: "var(--text-secondary)" }}>Still Funded</strong> is the share of simulated retirement
          histories where your accounts still had money at that age — not a literal forecast, and not a
          chance of zero income. Social Security, rental, and pension income keep paying even in "failed"
          paths; a failure just means living on those guaranteed streams alone from that age onward.
          Read Still Funded together with the <strong style={{ color: "var(--text-secondary)" }}>10th percentile</strong> column
          for fragility: a high Still Funded % paired with a thin 10th percentile is one bad market
          sequence away from joining the failures, while a high 10th percentile means real margin.
          <br /><br />
          Full explanation: Help → Reading the Charts → "What does Still Funded % actually mean?"
        </div>
      )}
      {show && (
        <>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", margin: "6px 0 8px", lineHeight: 1.5 }}>
            {landmarkOnly
              ? "Landmark ages only — your retirement, Social Security claim, Medicare, first RMD and the decade markers inside your horizon. "
              : "Each row is one age from the fan chart above: "}
            The percentile spread of{" "}
            {/* The RUN's path count, not the MC_PATHS constant. This sentence
                said "3,000" no matter what was run, so after the path count
                became user-adjustable (⚙ Advanced Settings) it described a
                simulation the reader was not looking at. A caption that names a
                number has to read the same value the table's own count column
                does. */}
            <strong style={{ color: "var(--text-secondary)" }}>
              {totalPaths ? totalPaths.toLocaleString() : MC_PATHS_LABEL}
            </strong>{" "}
            simulated portfolios and the share of paths still funded. 10th %ile = pessimistic (90% of outcomes were better);
            90th %ile = optimistic.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="nw-table" style={{ fontSize: "var(--fs-label)" }}>
              <thead>
                <tr>
                  <th>Age</th><th>Year</th>
                  <ThInfo tip={"Share of simulated paths whose portfolio has not run out by this age"}>Still Funded</ThInfo>
                  <ThInfo tip={`How many of the ${totalPaths ? totalPaths.toLocaleString() : "simulated"} paths are still funded at this age — the same share as the column to its left, shown as a count`}>Success Count</ThInfo>
                  <ThInfo tip={"Pre-Retirement = ages before you stop working; Retirement = the drawdown years. Your portfolio is still being added to in the first phase, so a low figure there means something different."}>Phase</ThInfo>
                  <ThInfo tip={"Pessimistic — 90% of simulated outcomes were better than this"}>10th %ile</ThInfo>
                  <th>25th %ile</th>
                  <ThInfo tip={"The median outcome — half of paths above, half below"}>Median</ThInfo>
                  <th>75th %ile</th>
                  <ThInfo tip={"Optimistic — only 10% of simulated outcomes were better than this"}>90th %ile</ThInfo>
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => {
                  const yr = CURRENT_YEAR + (d.age - (currentAge ?? d.age));
                  const isSS = d.age === ssAge, isRMD = d.age === rmdAge;
                  const isHovered = hoveredAge === d.age;
                  return (
                    <tr
                      key={d.age}
                      onMouseEnter={onHoverAge ? () => onHoverAge(d.age) : undefined}
                      onMouseLeave={onHoverAge ? () => onHoverAge(null) : undefined}
                      style={{
                        background: isHovered
                          ? "rgba(56,189,248,0.14)"
                          : isSS || isRMD
                          ? "var(--tint-sky-06)"
                          : undefined,
                        boxShadow: isHovered ? "inset 3px 0 0 #38bdf8" : undefined,
                        cursor: onHoverAge ? "pointer" : undefined,
                        transition: "background 0.1s",
                      }}
                    >
                      <td style={{ textAlign: "left", whiteSpace: "nowrap" }}>
                        {d.age}{isSS && " 🏛️"}{isRMD && " 📋"}
                      </td>
                      <td>{currentAge != null ? yr : "—"}</td>
                      {/* Band badge sits INSIDE the Still Funded cell: it is the
                          same number, labelled with the app's band vocabulary —
                          not a fourth success metric, and not a column of its
                          own (that would just be the same figure twice). */}
                      <td style={{ fontWeight: 600 }}>
                        <span style={{ color: fundedColor(d.alive ?? 1) }}>
                          {d.alive != null ? `${(d.alive * 100).toFixed(1)}%` : "—"}
                        </span>
                        {d.alive != null && (
                          <span style={{ marginLeft: 6, fontSize: "var(--fs-small)", fontWeight: 700, color: fundedColor(d.alive), border: `1px solid ${withAlpha(fundedColor(d.alive), "44")}`, borderRadius: 999, padding: "1px 7px", whiteSpace: "nowrap" }}>
                            {bandLabel(d.alive)}
                          </span>
                        )}
                      </td>
                      <td style={{ color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                        {d.alive != null && totalPaths ? `${Math.round(d.alive * totalPaths).toLocaleString()}/${totalPaths.toLocaleString()}` : "—"}
                      </td>
                      <td style={{ textAlign: "left", color: "var(--text-muted)", whiteSpace: "nowrap", fontSize: "var(--fs-small)" }}>
                        {retireAge != null && d.age != null ? (d.age < retireAge ? "Pre-Retirement" : "Retirement") : "—"}
                      </td>
                      <td style={{ color: "var(--chart-1)" }}>{fmtDollar(d.p10)}</td>
                      <td style={{ color: "var(--accent-gold)" }}>{fmtDollar(d.p25)}</td>
                      <td style={{ color: "var(--accent-teal)", fontWeight: 700 }}>{fmtDollar(d.p50)}</td>
                      <td style={{ color: "var(--text-secondary)" }}>{fmtDollar(d.p75)}</td>
                      <td style={{ color: "var(--positive)" }}>{fmtDollar(d.p90)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Band key. Built from the MC_BAND_* constants rather than retyped
              percentages, so the legend can never disagree with the colours
              above it — the failure mode the constants exist to prevent. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", alignItems: "center", marginTop: 8, fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
            <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>Still Funded:</span>
            {[
              { label: "Excellent", at: MC_BAND_LOW_RISK },
              { label: "Good", at: MC_BAND_MODERATE },
              { label: "Concerning", at: MC_BAND_ELEVATED },
            ].map(({ label, at }) => (
              <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: rateColor(at) }} />
                {label} ({Math.round(at * 100)}%+)
              </span>
            ))}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: rateColor(0) }} />
              Critical (below {Math.round(MC_BAND_ELEVATED * 100)}%)
            </span>
            <span style={{ color: "var(--text-faint)" }}>· 🏛️ Social Security starts · 📋 RMDs begin</span>
          </div>
        </>
      )}
    </div>
  );
}

// The five life-phase "sectors" the badge cycles through as retirement age
// advances. Each carries the age window it covers and the one-line reason it
// matters, so the badge's InfoModal can explain the current phase (and the
// whole arc) instead of leaving "Sector 1: The Escape" as an unexplained label.
const SECTORS = [
  {
    n: "Sector 1: The Escape",
    color: "var(--negative)",
    range: "Before age 59½",
    active: (age) => age < 59.5,
    desc: (
      <>
        <ModalLede>The pre-59½ penalty zone — the years you have to bridge before tax-deferred money is reachable without a penalty.</ModalLede>
        <ModalP>
          Withdrawals from <Em>Traditional 401(k) / IRA</Em> accounts before 59½ trigger a
          {" "}<Em color="var(--negative)">10% early-withdrawal penalty</Em> on top of ordinary income tax.
          The play is to <Em>escape</Em> to 59½ by living on your taxable brokerage, cash
          reserves, and Roth contributions — or a structured <Em>72(t) / SEPP</Em> — without
          touching the penalized accounts.
        </ModalP>
        <ModalNote accent="var(--negative)">
          This is the highest sequence-of-returns risk window: an early market drop lands while
          the portfolio is largest and no penalty-free lever is available yet.
        </ModalNote>
      </>
    ),
  },
  {
    n: "Sector 2: The Gap",
    color: "#0ea5e9",
    range: "Age 59½ – 65",
    active: (age) => age >= 59.5 && age < 65,
    desc: (
      <>
        <ModalLede>Penalty-free withdrawals unlock, but Social Security, Medicare, and RMDs haven't started yet.</ModalLede>
        <ModalP>
          These are the <Em>gap years</Em>: the 10% penalty is gone, income is often at its
          lowest, and you're still before the 2-year <Em>IRMAA lookback</Em> that sets Medicare
          premiums at 65. Typically the cleanest, lowest-bracket window for
          {" "}<Em color="var(--accent-purple)">Roth conversions</Em>.
        </ModalP>
      </>
    ),
  },
  {
    n: "Sector 3: The Maneuver",
    color: "var(--accent-gold)",
    range: "Age 65 – 73",
    active: (age) => age >= 65 && age < 73,
    desc: (
      <>
        <ModalLede>Medicare and Social Security are live; required distributions still aren't. Your last window to steer income.</ModalLede>
        <ModalP>
          Medicare starts at 65, so <Em color="var(--accent-purple)">IRMAA tiers</Em> now drive
          your premiums; Social Security may have begun. But <Em>RMDs haven't started</Em>, so this
          is the window to finish conversions and maneuver taxable income before distributions
          become mandatory.
        </ModalP>
      </>
    ),
  },
  {
    n: "Sector 4: The Torpedo",
    color: "var(--chart-7)",
    range: "Age 73 – 80",
    active: (age) => age >= 73 && age < 80,
    desc: (
      <>
        <ModalLede>The tax torpedo: Required Minimum Distributions begin whether you need the money or not.</ModalLede>
        <ModalP>
          <Em color="var(--chart-7)">RMDs</Em> start at age 73 (SECURE 2.0), forcing taxable withdrawals
          from tax-deferred accounts on the IRS schedule. Those forced distributions can push you
          into a higher bracket and a higher <Em color="var(--accent-purple)">IRMAA tier</Em> —
          the "torpedo" that a large un-converted balance sets off.
        </ModalP>
      </>
    ),
  },
  {
    n: "Sector 5: Legacy",
    color: "var(--accent-purple)",
    range: "Age 80+",
    active: (age) => age >= 80,
    desc: (
      <>
        <ModalLede>The wealth-transfer phase — the focus shifts from your spend to what passes to heirs.</ModalLede>
        <ModalP>
          RMDs are ongoing; planning turns to estate efficiency and beneficiary tax treatment —
          the <Em>10-year inherited-IRA rule</Em> that governs how fast heirs must draw down what
          they receive.
        </ModalP>
      </>
    ),
  },
];

function SectorBadge({ age }) {
  const cur = SECTORS.find((s) => s.active(age)) || SECTORS[0];
  return (
    <InfoModal
      title="Life-phase sectors"
      accent={cur.color}
      maxWidth={460}
      bodyMaxHeight="60vh"
      trigger={
        <span
          title="Tap or click to learn about this retirement phase"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: `${withAlpha(cur.color, "18")}`,
            border: `1px solid ${withAlpha(cur.color, "44")}`,
            borderRadius: 12,
            padding: "2px 10px",
            fontSize: "var(--fs-small)",
            color: cur.color,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {cur.n} <InfoIcon size={11} />
        </span>
      }
    >
      <ModalP>
        AiRA splits the retirement horizon into <Em>five sectors</Em> — each a distinct tax-and-access
        regime. Your current phase is highlighted below.
      </ModalP>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
        {SECTORS.map((s) => {
          const isCur = s.n === cur.n;
          return (
            <div
              key={s.n}
              style={{
                border: `1px solid ${withAlpha(s.color, isCur ? "66" : "22")}`,
                borderLeft: `3px solid ${s.color}`,
                background: withAlpha(s.color, isCur ? "14" : "08"),
                borderRadius: 8,
                padding: "10px 12px",
                opacity: isCur ? 1 : 0.85,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
                {/* Left group wraps as a unit; the "You are here" marker is a
                    real pill (not an em-dash text suffix that dropped to its own
                    line and read as a floating dash). */}
                <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, minWidth: 0, flex: "1 1 auto" }}>
                  <span style={{ fontSize: "var(--fs-label)", fontWeight: 800, color: s.color }}>{s.n}</span>
                  {isCur && (
                    <span style={{ fontSize: "var(--fs-small)", fontWeight: 800, letterSpacing: 0.4,
                      color: s.color, background: withAlpha(s.color, "22"), border: `1px solid ${withAlpha(s.color, "55")}`,
                      borderRadius: 999, padding: "1px 7px", lineHeight: 1.55, whiteSpace: "nowrap" }}>You are here</span>
                  )}
                </span>
                <span style={{ fontSize: "var(--fs-small)", fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap", flexShrink: 0 }}>{s.range}</span>
              </div>
              {s.desc}
            </div>
          );
        })}
      </div>
    </InfoModal>
  );
}

// CSS
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap');
  /* Design tokens — single source for the palette. Reference as var(--x) in
     both CSS rules and inline styles (style={{ color: "var(--text-muted)" }}).
     Added semantic-status tokens, a spacing scale, a --accent-ai alias
     (purple, promoted from an ambient chart color into a named AI-branding
     role), and a --divider token. Dark theme only; the light override block was removed (see git history).
  */
  :root {
    /* Deep neutral ink (less blue-cast), one indigo trust anchor,
       teal/gold/violet held to strict semantic roles. */
    /* ── Type system ──────────────────────────────────────────────────────
       'Inter' and 'JetBrains Mono' were declared all over this stylesheet but
       NEVER loaded: there is no @font-face here and no webfont <link> in
       public/index.html, so every one of those declarations silently fell
       through to the browser default (Arial/Segoe on Windows, Times-adjacent
       metrics in some embeds) while the app looked like it had a chosen face.
       Rather than add a third-party font request — this app's whole pitch is
       that your data stays on your machine, and a Google Fonts call hands
       every visitor's IP to a third party — the stacks below name the best
       SYSTEM faces in preference order. Inter is still first, so anyone who
       has it installed gets it.
       Tabular numerals are on globally: retirement figures are compared
       column-to-column, and proportional digits make an aligned column of
       dollars look ragged. */
    --font-sans: Arial, 'Helvetica Neue', Helvetica, 'Liberation Sans',
                 'Segoe UI', Roboto, sans-serif;
    --font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, 'SF Mono',
                 'Cascadia Mono', 'Segoe UI Mono', Consolas, 'Liberation Mono',
                 Menlo, monospace;
    --bg-base: #0e1218;
    --bg-hdr: rgba(14,18,24,0.96);
    --card-bg: #151b24;
    --card-bg-raised: #1b222d;
    --card-border: #2a3646;
    --divider: #243040;
    --card-shadow: none;
    --text-primary: #e8edf5;
    --text-secondary: #c0c9d6;
    --text-muted: #9aa6ba;  /* 5.3:1 on card (was 3.5) */
    --text-faint: #8793a7;  /* 4.8:1 on card (was 1.95) */
    --accent: #7ab8f5;
    --accent-teal: #7ab8f5;
    --accent-purple: #b4bac4;
    --accent-ai: #b4bac4;
    --accent-gold: #f4d35e;
    --positive: #4fd88f;
    --negative: #ff7b72;
    /* Semantic status backgrounds — accent hue at 8-10% alpha, recomputed off
       the palette so a theme flip re-tints them correctly. */
    --bg-info:     rgba(122,184,245,0.12);
    --bg-success:  rgba(79,216,143,0.12);
    --bg-warning:  rgba(244,211,94,0.13);
    --bg-danger:   rgba(255,111,122,0.13);
    /* Neutral and status tints, one token per base colour and alpha. */
    --tint-amber-07: rgba(251,191,36,0.07);
    --tint-amber-08: rgba(251,191,36,0.08);
    --tint-amber-35: rgba(251,191,36,0.35);
    --tint-black-40: rgba(0,0,0,0.4);
    --tint-black-50: rgba(0,0,0,0.5);
    --tint-black-60: rgba(0,0,0,0.6);
    --tint-black-70: rgba(0,0,0,0.7);
    --tint-black-80: rgba(0,0,0,0.8);
    --tint-green-05: rgba(52,211,153,0.05);
    --tint-green-07: rgba(52,211,153,0.07);
    --tint-red-05: rgba(255,111,122,0.05);
    --tint-red-07: rgba(255,111,122,0.07);
    --tint-red-08: rgba(255,111,122,0.08);
    --tint-red-15: rgba(255,111,122,0.15);
    --tint-red-20: rgba(255,111,122,0.2);
    --tint-red-25: rgba(255,111,122,0.25);
    --tint-red-30: rgba(255,111,122,0.3);
    --tint-sky-06: rgba(90,209,240,0.06);
    --tint-sky-07: rgba(90,209,240,0.07);
    --tint-sky-20: rgba(90,209,240,0.2);
    --tint-sky-25: rgba(90,209,240,0.25);
    --tint-sky-30: rgba(90,209,240,0.3);
    --tint-sky-45: rgba(90,209,240,0.45);
    /* Data palette: one name per chart and category colour. */
    --chart-1: #ff6f7a;
    --chart-2: #6c9bff;
    --chart-3: #fb923c;
    --chart-4: #818cf8;
    --chart-5: #22d3ee;
    --chart-6: #f5a623;
    --chart-7: #f97316;
    --chart-8: #6366f1;
    --chart-9: #8b5cf6;
    --chart-10: #3b82f6;
    --chart-11: #a9d1ac;
    --tint-indigo-05: rgba(99,102,241,0.05);
    --tint-indigo-15: rgba(99,102,241,0.15);
    --tint-indigo-20: rgba(99,102,241,0.2);
    --tint-indigo-30: rgba(99,102,241,0.3);
    --tint-indigo-40: rgba(99,102,241,0.4);
    --tint-orange-10: rgba(251,146,60,0.1);
    --tint-orange-25: rgba(251,146,60,0.25);
    --tint-orange-35: rgba(251,146,60,0.35);
    --tint-sky-500-10: rgba(14,165,233,0.1);
    --tint-sky-500-18: rgba(14,165,233,0.18);
    --tint-sky-500-25: rgba(14,165,233,0.25);
    --tint-sky-500-45: rgba(14,165,233,0.45);
    --tint-slate-400-15: rgba(148,163,184,0.15);
    --tint-teal-600-06: rgba(13,148,136,0.06);
    --tint-teal-600-08: rgba(13,148,136,0.08);
    --tint-teal-600-25: rgba(13,148,136,0.25);
    --tint-violet-400-30: rgba(167,139,250,0.3);
    --tint-violet-600-08: rgba(124,58,237,0.08);
    --tint-white-02: rgba(255,255,255,0.02);
    --tint-white-03: rgba(255,255,255,0.03);
    --tint-white-04: rgba(255,255,255,0.04);
    --tint-white-05: rgba(255,255,255,0.05);
    --tint-white-06: rgba(255,255,255,0.06);
    --tint-white-07: rgba(255,255,255,0.07);
    --tint-white-08: rgba(255,255,255,0.08);
    --tint-white-09: rgba(255,255,255,0.09);
    --tint-white-10: rgba(255,255,255,0.1);
    --tint-white-12: rgba(255,255,255,0.12);
    --tint-white-14: rgba(255,255,255,0.14);
    --tint-white-15: rgba(255,255,255,0.15);
    --row-highlight: rgba(122,184,245,0.06);
    /* Spacing scale — dense numeric-table app; loosening broadly costs
       rows-per-screen. xs and 2xl added for badges/pills and hero breaks
       that were hardcoded ad hoc. */
    --space-xs: 4px;
    --space-sm: 8px;
    --space-md: 14px;
    --space-lg: 20px;
    --space-xl: 32px;
    --space-2xl: 48px;
    /* Typography scale — sizes used to be scattered literals across the app;
       one place now so a display/small tweak is one edit. */
    --fs-display: 32px;
    --fs-h1: 22px;
    --fs-h2: 16px;
    --fs-body: 13px;
    --fs-small: 11px;
    --fs-label: 12px;
    --fs-mono: 13px;
    --radius-card: 8px;
    --radius-btn: 8px;
    --radius-pill: 999px;
  }
  * { box-sizing:border-box; }
  /* Reusable surfaces / labels — prefer these over retyping the card and
     uppercase-label inline style objects. */
  .card { background:var(--card-bg); border:1px solid var(--card-border); border-radius:var(--radius-card); padding:13px; }
  .section-label { font-size:var(--fs-small); font-weight:700; color:var(--text-muted); }
  body { margin:0; font-family:var(--font-sans); background:var(--bg-base); color:var(--text-primary); font-size:13px; line-height:1.5;
    font-variant-numeric: tabular-nums; -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; text-rendering:optimizeLegibility; }
  .app { min-height:100vh; background:var(--bg-base); }
  .hdr { background:var(--bg-hdr); border-bottom:1px solid var(--divider); padding:10px 20px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100; backdrop-filter:blur(16px); }
  h1.logo { margin:0; }
  .sr-only { position:absolute; width:1px; height:1px; margin:-1px; padding:0; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  .logo { font-size:25px; font-weight:800; letter-spacing:-0.03em; color:#eaf1ff; }
  .logo-sub { color:var(--chart-2); font-weight:400; font-size:16px; margin-left:6px; }
  .mbtn { padding:5px 13px; border-radius:7px; border:1px solid var(--card-border); cursor:pointer; font-size:11px; font-family:var(--font-sans); font-weight:500; transition:all 0.2s; background:transparent; color:var(--text-secondary); }
  .mbtn:hover { color:var(--text-primary); border-color:var(--divider); }
  .mbtn.on { background:var(--accent); border-color:transparent; color:var(--bg-base); }
  .mbtn:disabled { opacity:0.4; cursor:not-allowed; }
  /* Thin divider separating the utility buttons from the support CTA. */
  .hdiv { width:1px; height:20px; background:var(--divider); margin:0 3px; flex-shrink:0; }
  /* Buy-me-a-coffee — the one prominent, filled support CTA in the header. */
  .coffee-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 15px; border-radius:8px; border:none; background:#ffdd00; color:#1a1a1a; font-size:12px; font-weight:800; font-family:var(--font-sans); letter-spacing:-0.01em; text-decoration:none; cursor:pointer; white-space:nowrap; transition:filter 0.15s; }
  .coffee-btn:hover { filter:brightness(1.06); }
  /* ── Visitor landing (first-screen hero) ── */
  .landing { min-height:100vh; background:var(--bg-base); display:flex; flex-direction:column; align-items:center; padding:clamp(26px,6vw,60px) 20px 60px; overflow-y:auto; }
  .lp-wrap { width:100%; max-width:760px; }
  .lp-brand { display:flex; align-items:center; justify-content:space-between; margin-bottom:clamp(30px,6vw,52px); }
  .lp-eyebrow { font-size:13px; font-weight:600; color:var(--accent); margin-bottom:16px; }
  .lp-answer { font-size:clamp(29px,6vw,50px); font-weight:500; line-height:1.05; letter-spacing:-0.03em; color:var(--text-primary); margin:0; }
  .lp-age { color:var(--accent-teal); font-family:'JetBrains Mono',monospace; }
  .lp-answer.short .lp-age { color:var(--accent-gold); }
  .lp-sub { margin-top:16px; font-size:16px; color:var(--text-secondary); max-width:54ch; line-height:1.55; }
  .lp-panel { margin-top:28px; background:var(--card-bg); border:1px solid var(--card-border); border-radius:16px; padding:22px; }
  .lp-row { display:grid; grid-template-columns:1fr auto; align-items:baseline; }
  .lp-label { font-size:14px; color:var(--text-secondary); font-weight:600; }
  .lp-val { font-size:16px; font-weight:500; color:var(--text-primary); font-family:'JetBrains Mono',monospace; }
  /* Typed hero readout. A fixed 14ch width + right alignment keeps the grid's
     right edge steady as the value grows — $250,000,000 must not shove the label. */
  .lp-val-input { width:14ch; text-align:right; background:transparent; border:1px solid transparent; border-radius:6px; padding:2px 6px; cursor:text; }
  .lp-val-input:hover { border-color:var(--card-border); background:var(--row-highlight); }
  .lp-val-input:focus { outline:none; border-color:var(--accent); background:var(--bg-info); color:var(--text-primary); }
  /* Scoped under .landing so it beats the global input[type=range] display:none. */
  .landing input[type=range].lp-range { -webkit-appearance:none; appearance:none; display:block; width:100%; height:6px; border-radius:100px; background:var(--divider); outline:none; margin:7px 0 18px; cursor:pointer; }
  .landing input[type=range].lp-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:22px; height:22px; border-radius:50%; background:var(--accent); border:3px solid var(--bg-base); cursor:pointer; }
  .landing input[type=range].lp-range::-moz-range-thumb { width:20px; height:20px; border-radius:50%; background:var(--accent); border:3px solid var(--bg-base); cursor:pointer; }
  .lp-cta { display:inline-flex; align-items:center; gap:9px; margin-top:26px; padding:15px 30px; border:none; border-radius:12px; background:var(--accent); color:var(--bg-base); font-size:17px; font-weight:600; font-family:var(--font-sans); cursor:pointer; transition:filter 0.15s; }
  .lp-cta:hover { filter:brightness(1.1); }
  .lp-skip { display:block; margin-top:15px; background:none; border:none; color:var(--text-muted); font-size:13px; cursor:pointer; font-family:var(--font-sans); text-decoration:underline; padding:0; }
  .lp-skip:hover { color:var(--text-secondary); }
  /* minmax(0,1fr), not bare 1fr: a grid track's auto minimum is its content's
     max-content width, so a wide child (the 15+ column Withdrawal Plan
     table) silently grows the whole right-pane track past the viewport
     instead of scrolling inside it — the browser has to be widened to see
     it. minmax(0,1fr) removes that content-based floor so .main is held to
     the track's actual size and its own overflow rules (below) take over. */
  .layout { display:grid; grid-template-columns:340px minmax(0, 1fr); height:calc(100vh - 56px); overflow:hidden; }
  .sidebar { border-right:1px solid var(--tint-white-08); padding:14px 12px; overflow-y:auto; background:rgba(10,15,30,0.78); display:flex; flex-direction:column; gap:12px; min-height:0; min-width:0; }
  .sb-card { background:var(--card-bg); border:1px solid var(--card-border); border-radius:var(--radius-card); padding:13px 12px; }
  .sb-title { font-size:var(--fs-label); font-weight:600; color:var(--text-muted); margin:0 0 10px; }
  .sl-row { display:flex; flex-direction:column; gap:6px; margin-bottom:13px; }
  .sl-nudge-btn { width:24px; height:24px; border-radius:5px; border:1px solid var(--tint-white-12); background:var(--tint-white-06); color:var(--text-secondary); cursor:pointer; font-size:14px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:all 0.15s; flex-shrink:0; line-height:1; user-select:none; }
  .sl-nudge-btn:hover:not(:disabled) { background:var(--tint-white-15); color:#ffffff; border-color:rgba(255,255,255,0.28); transform:scale(1.06); }
  .sl-nudge-btn:active:not(:disabled) { transform:scale(0.94); }
  .sl-nudge-btn:disabled { opacity:0.22; cursor:not-allowed; }
  /* The value cell is now an editable input (typed entry can exceed the slider max). */
  .sl-val-input { width:auto; min-width:70px; max-width:120px; text-align:center; background:rgba(15,23,42,0.65); border:1px solid var(--tint-white-12); border-radius:5px; padding:2px 6px; cursor:text; }
  .sl-val-input:hover { border-color:rgba(255,255,255,0.22); background:var(--tint-white-08); }
  .sl-val-input:focus { outline:none; border-color:var(--positive); background:rgba(90,209,240,0.12); color:var(--text-primary); box-shadow:0 0 0 2px var(--tint-sky-25); }
  .sl-label { font-size:12px; color:var(--text-secondary); font-weight:600; }
  .sl-val { font-size:12px; font-weight:700; text-align:right; color:var(--text-primary); font-family:'JetBrains Mono',monospace; }
  .tog-row { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:10px; gap:8px; flex-wrap:nowrap; }
  .sb-card > .tog-row:last-child { margin-bottom:0; }
  .tog-label { font-size:12px; line-height:18px; color:var(--text-secondary); font-weight:500; flex:1; min-width:0; }
  .tog { width:34px; height:18px; border:0; padding:0; border-radius:9px; cursor:pointer; position:relative; transition:background 0.2s; flex-shrink:0; }
  .tok { position:absolute; top:2px; width:14px; height:14px; border-radius:50%; background:white; transition:left 0.2s; box-shadow:0 1px 3px var(--tint-black-40); }
  .run-btn { width:100%; padding:10px; background:var(--accent); border:none; border-radius:6px; color:var(--bg-base); font-size:13px; font-weight:700; cursor:pointer; font-family:var(--font-sans); transition:all 0.2s; letter-spacing:-0.01em; flex-shrink:0; }
  .run-btn:hover { opacity:0.9; }
  .run-btn:disabled { opacity:0.4; cursor:not-allowed; box-shadow:none; }
  .main { padding:16px; overflow-y:auto; display:flex; flex-direction:column; gap:12px; min-height:0; min-width:0; }
  /* min-width:0 alongside flex-shrink:0: shrink is about the column axis
     (height) here, not width — without min-width:0 a card holding a wide
     table still stretches to that table's content width on the cross axis,
     which is the other half of the same overflow this section fixes. */
  .main > * { flex-shrink:0; min-width:0; }
  /* One level isn't enough. Tab bodies nest their own
     display:flex/flexDirection:column card stacks several layers deep
     (WithdrawalPlanCombined > a plain section div > WaterfallPlanView, each
     wrapping the next) before reaching the wide table's own overflow-x:auto
     div. Each of those intermediate flex containers has the exact same
     content-based auto-minimum-width problem .main > * fixes one level up —
     min-width:0 only stops the wide child from being a floor on ITS
     immediate flex parent, it doesn't cascade to grandparents. Rather than
     hunting down and annotating every nested flex wrapper in every tab
     (this pattern repeats across the app), apply the fix universally to
     every descendant of .main: min-width:0 is already CSS's default value,
     so this changes nothing for ordinary block boxes — it only removes the
     content-based floor for the specific flex/grid items that have one. */
  .main * { min-width:0; }
  .flag-w { border-left:3px solid var(--accent-gold); background:var(--bg-warning); padding:7px 12px; font-size:12px; color:#fde68a; border-radius:0 8px 8px 0; margin-bottom:4px; font-weight:500; }
  .flag-i { border-left:3px solid #38bdf8; background:rgba(56,189,248,0.08); color:#bae6fd; border-radius:0 8px 8px 0; padding:7px 12px; font-size:12px; margin-bottom:4px; font-weight:500; }
  /* ── Help / About body — the info-modal kit's rhythm, authored in HTML ──
     The ❓ Help modal and its cards render authored HTML from about.js via
     dangerouslySetInnerHTML, so they cannot use the JSX kit directly
     (ModalLede / ModalP / Em / ModalNote). These classes ARE that kit, in
     CSS, at the same values: change them together, or the help copy grows a
     second body style — the drift this block exists to prevent. */
  .help-body { font-size:13px; color:var(--text-secondary); line-height:1.75; }
  .help-body p { margin:0 0 10px; }
  .help-body > *:last-child { margin-bottom:0; }
  .help-lede { font-size:14.5px; font-weight:700; color:var(--text-primary); line-height:1.5; margin:0 0 10px; }
  .help-sub { font-size:13px; font-weight:700; color:var(--text-primary); line-height:1.5; margin:16px 0 7px; }
  .help-body strong { color:var(--text-primary); font-weight:700; }
  .help-body em { font-style:italic; }
  .help-body ul { margin:0 0 10px; padding-left:20px; }
  .help-body li { margin-bottom:4px; }
  .help-body code { font-family:var(--font-mono); font-size:12px; background:var(--tint-white-06); border:1px solid var(--tint-white-08); border-radius:4px; padding:1px 5px; }
  /* The ModalNote look. Accent comes from --note-accent so a risk-toned note
     is a modifier, not a second rule that can fall out of step. */
  .help-note { --note-accent: var(--accent-gold); display:flex; gap:9px;
    background:rgba(245,166,35,0.07); border:1px solid rgba(245,166,35,0.2);
    border-left:3px solid var(--note-accent); border-radius:8px; padding:10px 12px;
    margin:0 0 10px; font-size:12.5px; color:var(--text-secondary); line-height:1.6; }
  .help-note--risk { --note-accent: var(--negative); background:var(--tint-red-07);
    border-top-color:var(--tint-red-20); border-right-color:var(--tint-red-20);
    border-bottom-color:var(--tint-red-20); border-left:3px solid var(--note-accent); }
  .help-note--method { --note-accent: var(--accent-teal); background:var(--tint-sky-07);
    border-top-color:var(--tint-sky-20); border-right-color:var(--tint-sky-20);
    border-bottom-color:var(--tint-sky-20); border-left:3px solid var(--note-accent); }
  .help-note-icon { color:var(--note-accent); font-weight:800; flex-shrink:0; line-height:1.6; }
  /* auto-fit (not a fixed 4-column count) so a row with fewer cards than
     the column count STRETCHES to fill the width instead of leaving empty
     gaps — a fixed repeat(4,1fr) left a lone 5th card stranded on its own
     row with 3 blank slots next to it, and any future card count hits the
     same problem again at a different number. minmax(160px,1fr) keeps cards
     from getting too narrow to read before wrapping. */
  .metrics { display:grid; grid-template-columns:repeat(auto-fit, minmax(160px, 1fr)); gap:9px; }
  .met { background:var(--tint-white-04); border:1px solid var(--tint-white-09); border-radius:10px; padding:13px 15px; }
  .ml { font-size:var(--fs-small); color:var(--text-muted); margin-bottom:7px; font-weight:600; }
  .mv { font-size:22px; font-weight:800; font-family:'JetBrains Mono',monospace; line-height:1; }
  .ms { font-size:12px; color:var(--text-muted); margin-top:5px; }
  .analogue { background:var(--tint-white-03); border:1px solid var(--tint-white-08); border-radius:10px; padding:12px 16px; font-size:13px; color:var(--text-secondary); font-style:italic; }
  .analogue-fade { animation: analogueFade 0.6s ease; }
  @keyframes analogueFade { from { opacity: 0; } to { opacity: 1; } }
  .recomputing { animation: recomputePulse 1.15s ease-in-out infinite; }
  @keyframes recomputePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
  .tabs { display:flex; gap:2px; border-bottom:1px solid var(--tint-white-10); flex-wrap:wrap; }
  .tab { flex:1; min-width:72px; padding:10px 6px; border:none; border-bottom:2px solid transparent; margin-bottom:-1px; background:transparent; cursor:pointer; font-size:13px; font-family:var(--font-sans); color:var(--text-muted); transition:color 0.15s, border-color 0.15s, background 0.15s; font-weight:500; white-space:nowrap; letter-spacing:-0.01em; border-radius:7px 7px 0 0; position:relative; overflow:hidden; }
  .tab:hover { color:var(--text-muted); background:var(--tint-white-03); }
  /* The active tab's teal underline used to stop at that button's own width, so
     the "you are here" marker read as a short dash floating under one label
     while the grey rule carried on past it either side. This strip spans the
     whole bar and overflow:hidden on the button clips it to the selected tab,
     so the highlighter runs edge to edge like the reference design — and it
     stays right however the buttons wrap, with no hardcoded widths or nth-child
     maths. Sits on the border line itself (bottom:0, over the 2px
     border-bottom) rather than below it. */
  .tab::after { content:""; position:absolute; left:0; right:0; bottom:0; height:2px; background:var(--accent-teal); transform:scaleX(0); transform-origin:left; transition:transform 0.15s ease; pointer-events:none; }
  /* Folder-tab treatment: the active tab gets a tinted "page" surface that
     reads as connected to the panel below, plus the teal/green highlighter
     underline that was already carrying the state. The tint is one step of
     the same accent so the tab and its panel look like one surface. */
  .tab.on { color:var(--accent-teal); border-bottom-color:var(--accent-teal); font-weight:600; background:color-mix(in srgb, var(--accent-teal) 12%, transparent); }
  .tab.on::after { transform:scaleX(1); }
  .chart-card { background:var(--tint-white-03); border:1px solid var(--tint-white-08); border-radius:11px; padding:15px 17px; }
  .ct { font-size:18px; color:var(--text-muted); margin-bottom:12px; font-weight:500; }
  .ct-sub { font-size:11.5px; color:var(--text-muted); line-height:1.5; margin:-8px 0 10px; }
  .leg { display:flex; gap:14px; flex-wrap:wrap; margin-top:10px; }
  .li { display:flex; align-items:center; gap:5px; font-size:12px; color:var(--text-muted); }
  .ll { width:18px; height:2px; border-radius:1px; }
  .ppl-grid { display:flex; flex-wrap:wrap; gap:4px; margin:8px 0; }
  .dot { display:inline-block; width:6px; height:6px; border-radius:50%; margin-right:6px; vertical-align:middle; }
  .dot-ok { background:var(--positive); } .dot-warn { background:var(--accent-gold); } .dot-bad { background:var(--negative); } .dot-info { background:var(--accent); }
  .ppl-dot { width:18px; height:18px; border-radius:50%; }
  .roth-tbl { width:100%; border-collapse:collapse; font-size:12px; }
  /* ── Withdrawal table: grouped column views ───────────────────────────────
     The year-by-year schedule carries 23 columns, which is more than anyone can
     read across. Rather than maintain four separate hand-written tables (four
     places to update on every engine change), the columns carry a group tag and
     a view hides whole groups. Hiding is per-GROUP only, so a visible group's
     colSpan in the header row is always correct and never needs recomputing.
     This is the same "one table, different lenses" idea as the reference app's
     Main / Growth / Withdrawals / Tax tabs. */
  .wf-views { display:flex; gap:6px; flex-wrap:wrap; align-items:center; margin-bottom:10px; }
  .wf-views button { padding:4px 12px; font-size:11px; font-weight:600; border-radius:999px; cursor:pointer;
    border:1px solid var(--card-border); background:transparent; color:var(--text-secondary);
    font-family:var(--font-sans); transition:all 0.15s; }
  .wf-views button:hover { color:var(--text-primary); }
  .wf-views button.on { background:color-mix(in srgb, var(--accent-teal) 16%, transparent);
    border-color:color-mix(in srgb, var(--accent-teal) 45%, transparent); color:var(--accent-teal); }
  .wf-grp { font-size:9px; font-weight:800; text-transform:uppercase; letter-spacing:0.14em;
    color:var(--text-muted); text-align:center; padding:6px 6px 4px; white-space:nowrap;
    border-bottom:1px solid var(--divider); }
  .wf-grp-s { color:var(--accent-gold); }
  .wf-grp-i { color:var(--positive); }
  .wf-grp-d { color:var(--accent-teal); }
  .wf-grp-t { color:var(--chart-3); }
  .wf-grp-b { color:var(--accent-purple); }
  .wf-tbl[data-view="flows"] [data-cg="tax"],
  .wf-tbl[data-view="flows"] [data-cg="risk"],
  .wf-tbl[data-view="flows"] [data-cg="bal"] { display:none; }
  .wf-tbl[data-view="tax"] [data-cg="spend"],
  .wf-tbl[data-view="tax"] [data-cg="income"],
  .wf-tbl[data-view="tax"] [data-cg="draw"],
  .wf-tbl[data-view="tax"] [data-cg="conv"],
  .wf-tbl[data-view="tax"] [data-cg="bal"] { display:none; }
  .wf-tbl[data-view="bal"] [data-cg="spend"],
  .wf-tbl[data-view="bal"] [data-cg="income"],
  .wf-tbl[data-view="bal"] [data-cg="draw"],
  .wf-tbl[data-view="bal"] [data-cg="conv"],
  .wf-tbl[data-view="bal"] [data-cg="tax"],
  .wf-tbl[data-view="bal"] [data-cg="risk"] { display:none; }  .roth-tbl th { font-size:10px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; padding:7px 8px; text-align:right; border-bottom:1px solid var(--tint-white-09); }
  .roth-tbl th:first-child { text-align:left; }
  .roth-tbl td { padding:9px 8px; border-bottom:1px solid var(--tint-white-05); text-align:right; font-family:'JetBrains Mono',monospace; font-size:12px; color:var(--text-primary); }
  .roth-tbl td:first-child { text-align:left; font-family:var(--font-sans); color:var(--text-primary); }
  .gold { background:var(--tint-amber-07); }
  .gk-bar { background:rgba(14,165,233,0.07); border:1px solid rgba(14,165,233,0.2); border-radius:9px; padding:11px 15px; font-size:12px; color:#bae6fd; }
  .cfg-btn { display:block; width:100%; margin:0 0 12px; padding:9px 12px; border:0; border-radius:8px; background:var(--accent-teal); color:#04201c; font-size:13px; font-weight:800; letter-spacing:0.02em; cursor:pointer; text-align:center; }
  .cfg-btn:hover { filter:brightness(1.08); }
  .cfg-btn:focus-visible, .sb-edit:focus-visible, .donut-btn:focus-visible, .tog:focus-visible, .info-trig:focus-visible, .sb-title[role=button]:focus-visible { outline:2px solid var(--accent-teal); outline-offset:2px; }
  .cfg-launch { margin:0; min-height:44px; flex-shrink:0; }
  .cfg-launch:active { transform:translateY(1px); }
  .cfg-launch[aria-current] { background:transparent; color:var(--accent-teal); box-shadow:inset 0 0 0 1.5px var(--accent-teal); }
  .assume-row { display:flex; justify-content:space-between; align-items:baseline; gap:10px; width:100%; padding:7px 0; border:0; border-top:1px solid var(--tint-white-06); background:none; color:var(--text-primary); font:inherit; font-size:12px; text-align:left; cursor:pointer; }
  .assume-row span:last-child { color:var(--accent-teal); font-size:11px; font-weight:600; text-decoration:underline; flex-shrink:0; }
  .assume-row:focus-visible { outline:2px solid var(--accent-teal); outline-offset:2px; }
  .gap-row { display:flex; align-items:baseline; gap:9px; width:100%; padding:9px 13px; border:1px solid var(--tint-amber-35); border-radius:9px; background:var(--tint-amber-07); color:var(--text-secondary); font:inherit; font-size:12.5px; line-height:1.5; text-align:left; cursor:pointer; }
  .gap-row > span:first-child, .gap-row strong { color:var(--accent-gold); }
  .gap-row:hover { background:var(--tint-amber-08); }
  .gap-row:focus-visible { outline:2px solid var(--accent-gold); outline-offset:2px; }
  .gap-more { color:var(--accent-gold); font-weight:700; white-space:nowrap; text-decoration:underline; text-underline-offset:3px; }
  .donut-btn { padding:0; border:0; background:none; border-radius:50%; cursor:pointer; flex-shrink:0; }
  .donut-btn:hover { filter:brightness(1.2); }
  .sb-edit { background:none; border:0; padding:0; color:var(--accent-teal); font-size:10px; font-weight:600; text-decoration:underline; cursor:pointer; }
  .progress-bar { height:5px; background:var(--tint-white-08); border-radius:3px; overflow:hidden; margin-top:6px; }
  .progress-fill { height:100%; background:var(--accent); border-radius:3px; transition:width 1s; }
  .nw-table { width:100%; border-collapse:collapse; font-size:12px; }
  .nw-table th { font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.08em; padding:7px 8px; border-bottom:1px solid var(--tint-white-09); text-align:right; font-weight:700; }
  .nw-table th:first-child { text-align:left; }
  .nw-table td { padding:8px 8px; border-bottom:1px solid var(--tint-white-04); text-align:right; font-family:'JetBrains Mono',monospace; color:var(--text-primary); }
  .nw-table td:first-child { text-align:left; font-family:var(--font-sans); color:var(--text-primary); }
  .wizard-mobile-steps { display:none; }
  .ap-col { background:var(--tint-white-03); border:1px solid var(--tint-white-08); border-radius:10px; padding:14px; }
  .ap-hdr { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; margin-bottom:11px; }
  .ap-item { font-size:12px; padding:6px 0; border-bottom:1px solid var(--tint-white-05); color:var(--text-secondary); }
  .ms-dot { width:11px; height:11px; border-radius:50%; flex-shrink:0; margin-top:2px; }
  .ms-line { width:2px; background:var(--tint-white-08); margin:0 4px; }
  .tip-box { background:rgba(10,15,30,0.98); border:1px solid var(--tint-white-12); border-radius:8px; padding:9px 12px; font-size:14px; color:var(--text-primary); }
  /* Scrollbars.
     These used to be 3px wide at 12% opacity — invisible on every platform,
     and worse on macOS, where the OS uses overlay scrollbars that stay
     hidden until you actively scroll. Wide tables (the 15-column waterfall,
     the MC band table, the checkpoint table) looked truncated instead of
     scrollable, and Mac users never discovered the columns off to the right.

     Styling ::-webkit-scrollbar at all opts Chrome/Safari out of overlay
     mode, so the bar stays on screen; the size and contrast below make it
     actually visible once it is. Firefox uses the standard properties. */
  ::-webkit-scrollbar { width:10px; height:10px; }
  ::-webkit-scrollbar-track {
    background:var(--tint-white-04);
    border-radius:5px;
  }
  ::-webkit-scrollbar-thumb {
    background:rgba(148,163,184,0.45);
    border-radius:5px;
    border:2px solid transparent;
    background-clip:padding-box;
  }
  ::-webkit-scrollbar-thumb:hover {
    background:rgba(148,163,184,0.75);
    background-clip:padding-box;
  }
  ::-webkit-scrollbar-corner { background:transparent; }
  * { scrollbar-width:thin; scrollbar-color:rgba(148,163,184,0.45) rgba(255,255,255,0.04); }

  /* Horizontally scrollable regions get a right-edge fade so there's a
     visual cue that content continues past the viewport, independent of the
     scrollbar itself. */
  .scroll-x {
    overflow-x:auto;
    background:
      linear-gradient(to right, var(--bg-base) 30%, rgba(10,15,30,0)) left center,
      linear-gradient(to left,  var(--bg-base) 30%, rgba(10,15,30,0)) right center;
    background-repeat:no-repeat;
    background-size:36px 100%, 36px 100%;
    background-attachment:local, local;
  }

  /* Mobile / Responsive */
  @media (max-width: 768px) {
    .hdr { padding:8px 12px; gap:6px; flex-wrap:wrap; }
    .logo-sub { display:none; }
    .mbtn { padding:4px 9px; font-size:10px; }
    .layout { grid-template-columns:1fr; height:auto; overflow:visible; }
    .sidebar { border-right:none; border-bottom:1px solid var(--tint-white-06); max-height:220px; overflow-y:auto; min-height:unset; padding:10px; flex-direction:row; flex-wrap:wrap; gap:8px; }
    .sb-card { padding:10px; }
    .main { padding:10px; overflow-y:visible; min-height:unset; }
    .main > * { flex-shrink:0; }
    .metrics { grid-template-columns:1fr 1fr; }
    .sl-row { grid-template-columns:1fr 88px; }
    .tabs { gap:2px; }
    .tab { min-width:56px; padding:6px 4px; font-size:10px; }
    .wizard-grid { grid-template-columns:1fr !important; }
    .wizard-sidebar { display:none !important; }
    .wizard-panel { border-radius:0 !important; }
    .wizard-mobile-steps { display:block !important; }
    .metrics .met { padding:10px 12px; }
    .metrics .mv { font-size:17px; }
    .roth-tbl { font-size:11px; }
    .roth-tbl th, .roth-tbl td { padding:6px 5px; }
    .nw-table { font-size:11px; }
    .nw-table th, .nw-table td { padding:6px 5px; }
  }
  @media (max-width: 480px) {
    .metrics { grid-template-columns:1fr; }
    .tabs { gap:1px; }
    .tab { min-width:44px; font-size:9px; padding:5px 3px; }
    .hdr { justify-content:center; }
  }
`;

// Monte Carlo fan-chart palette.
// One source of truth for the percentile band colors so the chart lines/
// areas and the tooltip stay in lock-step. Distinct hue ramp (upside →
// downside): 90th indigo, 75th cyan, Median teal, 25th amber, 10th red.
// The Tip has to resolve by series name — Recharts hands Area series their
// fill (a gradient url) as `p.color`, which would otherwise render as a broken swatch.
const FAN_COLORS = {
  "90th":   "var(--chart-4)", // indigo
  "75th":   "var(--chart-5)", // cyan
  "Median": "var(--accent-teal)", // teal (bold anchor line)
  "25th":   "var(--accent-gold)", // amber
  "10th":   "var(--chart-1)", // red
};

/**
 * Heading for the shared chart tooltip.
 *
 * Pure and exported so it can be tested without a DOM — the bug it fixes is
 * a string-formatting decision, not a rendering one.
 *
 * `Tip` is shared by ten charts and they don't agree on their x-axis key —
 * most use dataKey="age", two use dataKey="yr". The heading used to be a
 * hardcoded `Age {label}`, so on the year-keyed charts it printed "Age
 * 2044," labeling a calendar year as an age (a user reported this with a screenshot).
 *
 * Reads the data row rather than the axis label: Recharts hands the whole
 * row back on payload[].payload, and these rows carry both `age` and `yr`.
 * Falls back to the label disambiguated by magnitude — nobody is 1900 years
 * old, and no plan year is below 130.
 */
/**
 * Headline for the first-death disclosure box.
 *
 * The true branch used to read "Your spouse survives" when
 * `primarySurvives` is true — i.e. when the spouse dies and you survive.
 * Exactly backwards, and a user reported it after selecting "My spouse"
 * under "Who passes first" and being told his spouse survives. The
 * arithmetic underneath was right all along (the survivor benefit shown is
 * the deceased spouse's, passing to the primary) — only the sentence was inverted.
 *
 * Pure and exported so the wording is covered by a test. A label that
 * contradicts the model is the most repeated kind of bug in this codebase,
 * and it's invisible to every engine test.
 */
export function firstDeathHeadline(primarySurvives, deathAtYourAge, survAgeAtDeath) {
  return primarySurvives
    ? `Modelled from your age ${deathAtYourAge} — your spouse dies first and you survive.`
    : `Modelled from your age ${deathAtYourAge} — you die first and your spouse, then ${survAgeAtDeath}, survives.`;
}

export function tipHeading(payload, label) {
  const row = (Array.isArray(payload) && payload[0]?.payload) || {};
  const lblNum = Number(label);
  const age = Number.isFinite(row.age) ? row.age
    : (Number.isFinite(lblNum) && lblNum > 0 && lblNum < 130 ? lblNum : null);
  const yr = Number.isFinite(row.yr) ? row.yr
    : (Number.isFinite(lblNum) && lblNum >= 1900 ? lblNum : null);
  if (age != null && yr != null) return `Age ${age} · ${yr}`;
  if (age != null) return `Age ${age}`;
  if (yr != null) return `${yr}`;
  return String(label ?? "");
}

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  // Filter to keep only the first occurrence of each name
  const seen = new Set();
  const uniquePayload = payload.filter(p => {
    if (seen.has(p.name)) return false;
    seen.add(p.name);
    return true;
  });

  return (
    <div className="tip-box">
      <div style={{ color: "var(--text-muted)", marginBottom: 3 }}>{tipHeading(uniquePayload, label)}</div>
      {uniquePayload
        .filter((p) => p.value > 0)
        .map((p, i) => {
          const c = FAN_COLORS[p.name] || p.color;
          return (
            <div key={i} style={{ color: c, marginBottom: 1, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: "inline-block", flexShrink: 0 }} />
              <span style={{ color: "var(--text-secondary)" }}>{p.name}: </span>
              {fmtDollar(p.value)}
            </div>
          );
        })}
    </div>
  );
};
const RateTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="tip-box">
      <div style={{ color: "var(--text-muted)", marginBottom: 3 }}>Age {label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 1 }}>
          <span style={{ color: "var(--text-secondary)" }}>{p.name}: </span>
          {(p.value * 100).toFixed(1)}%
        </div>
      ))}
    </div>
  );
};
const TaxYearTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const get = key => payload.find(p => p.dataKey === key)?.value || 0;
  const oF = get("opt_fed"), oS = get("opt_st"), oI = get("opt_irmaa");
  const cF = get("cur_fed"), cS = get("cur_st"), cI = get("cur_irmaa");
  const oTotal = oF + oS + oI, cTotal = cF + cS + cI;
  const delta = oTotal - cTotal;
  const hasState = oS > 0 || cS > 0;
  const hasIrmaa = oI > 0 || cI > 0;
  const N = v => `$${Math.round(v).toLocaleString()}`;
  const tipRow = (swatch, lbl, optV, curV, note) => (
    <tr key={lbl} style={{ borderBottom: "1px solid var(--tint-white-04)" }}>
      <td style={{ padding: "4px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: swatch, display: "inline-block", flexShrink: 0 }} />
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>{lbl}</span>
        </div>
      </td>
      <td style={{ padding: "4px 0 4px 12px", textAlign: "right", fontSize: "var(--fs-small)", color: note ? "#374151" : "var(--text-primary)" }}>
        {note ? <em style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{note}</em> : N(optV)}
      </td>
      <td style={{ padding: "4px 0 4px 8px", textAlign: "right", fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
        {note ? "" : N(curV)}
      </td>
    </tr>
  );
  return (
    <div style={{ background: "#0f172a", border: "1px solid var(--tint-white-12)", borderRadius: 8, padding: "10px 14px", minWidth: 280, boxShadow: "0 4px 20px var(--tint-black-60)" }}>
      <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--tint-white-08)" }}>
        Age {label} — Tax Breakdown
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", textAlign: "left", paddingBottom: 4, fontWeight: 500 }}>Type</th>
            <th style={{ fontSize: "var(--fs-small)", color: "var(--chart-4)", textAlign: "right", paddingBottom: 4, fontWeight: 500, paddingLeft: 12 }}>With Conv</th>
            <th style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", textAlign: "right", paddingBottom: 4, fontWeight: 500, paddingLeft: 8 }}>Without</th>
          </tr>
        </thead>
        <tbody>
          {tipRow("var(--chart-8)", "Federal Income Tax", oF, cF)}
          {hasState && tipRow("var(--chart-3)", "State Income Tax", oS, cS)}
          {hasIrmaa && tipRow("var(--chart-1)", "IRMAA Surcharge", oI, cI)}
          {tipRow("#334155", "FICA (payroll tax)", 0, 0, "not applicable — retired")}
          {tipRow("#334155", "Capital Gains Tax", 0, 0, "not modeled here")}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid var(--tint-white-10)" }}>
            <td style={{ paddingTop: 5, fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-primary)" }}>Total</td>
            <td style={{ paddingTop: 5, textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, paddingLeft: 12, color: delta > 0 ? "var(--chart-3)" : delta < 0 ? "var(--positive)" : "var(--text-secondary)" }}>{N(oTotal)}</td>
            <td style={{ paddingTop: 5, textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, paddingLeft: 8, color: "var(--text-secondary)" }}>{N(cTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
const IncYearTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const get = key => payload.find(p => p.dataKey === key)?.value || 0;
  const oSS = get("opt_ss"), oAb = get("opt_ab"), oRmd = get("opt_rmd"), oConv = get("opt_conv"), oPxs = get("opt_pxs");
  const cSS = get("cur_ss"), cAb = get("cur_ab"), cRmd = get("cur_rmd"), cPxs = get("cur_pxs");
  const oTotal = oSS + oAb + oRmd + oConv + oPxs, cTotal = cSS + cAb + cRmd + cPxs;
  const N = v => `$${Math.round(v).toLocaleString()}`;
  const tipRow = (swatch, lbl, optV, curV) => (optV > 0 || curV > 0) ? (
    <tr key={lbl} style={{ borderBottom: "1px solid var(--tint-white-04)" }}>
      <td style={{ padding: "4px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: swatch, display: "inline-block", flexShrink: 0 }} />
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>{lbl}</span>
        </div>
      </td>
      <td style={{ padding: "4px 0 4px 12px", textAlign: "right", fontSize: "var(--fs-small)", color: "var(--text-primary)" }}>{N(optV)}</td>
      <td style={{ padding: "4px 0 4px 8px", textAlign: "right", fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{N(curV)}</td>
    </tr>
  ) : null;
  return (
    <div style={{ background: "#0f172a", border: "1px solid var(--tint-white-12)", borderRadius: 8, padding: "10px 14px", minWidth: 280, boxShadow: "0 4px 20px var(--tint-black-60)" }}>
      <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--tint-white-08)" }}>
        Age {label} — Taxable Income Sources
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", textAlign: "left", paddingBottom: 4, fontWeight: 500 }}>Source</th>
            <th style={{ fontSize: "var(--fs-small)", color: "var(--accent-teal)", textAlign: "right", paddingBottom: 4, fontWeight: 500, paddingLeft: 12 }}>With Conv</th>
            <th style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", textAlign: "right", paddingBottom: 4, fontWeight: 500, paddingLeft: 8 }}>Without</th>
          </tr>
        </thead>
        <tbody>
          {tipRow("var(--accent-teal)", "Social Security (85%)", oSS, cSS)}
          {tipRow("var(--accent-gold)", "Annuity / Benefit", oAb, cAb)}
          {tipRow("var(--accent-purple)", "Required Min. Dist.", oRmd, cRmd)}
          {tipRow("var(--chart-2)", "Pretax Withdrawal", oPxs, cPxs)}
          {tipRow("var(--positive)", "Roth Conversion", oConv, 0)}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "2px solid var(--tint-white-10)" }}>
            <td style={{ paddingTop: 5, fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-primary)" }}>Total Taxable</td>
            <td style={{ paddingTop: 5, textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--accent-teal)", paddingLeft: 12 }}>{N(oTotal)}</td>
            <td style={{ paddingTop: 5, textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-secondary)", paddingLeft: 8 }}>{N(cTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
/**
 * `hint` used to render as a native `title=` tooltip, which meant the
 * explanation was hover-only — undiscoverable to anyone who doesn't think
 * to hover, and just missing entirely on touch devices. It now opens an
 * InfoModal on click via the existing InfoIcon affordance, which fixes
 * discoverability and touch in one move. `title` stays on the icon only as
 * harmless extra flavor for mouse users, never as the only way to see it.
 *
 * `infoTitle` names the modal; it defaults to the toggle's own label so
 * callers that only pass `hint` get a correct heading for free.
 */
function Toggle({ val, onChange, label, accent = "var(--positive)", hint, infoTitle, infoAccent }) {
  return (
    <div className="tog-row">
      <span className="tog-label">
        {label}
        {hint && (
          <InfoModal
            title={infoTitle || (typeof label === "string" ? label : "About this option")}
            accent={infoAccent || accent}
            trigger={
              <span style={{ marginLeft: 6, color: "var(--text-secondary)", cursor: "pointer", display: "inline-flex", verticalAlign: "-2px" }}
                    role="img" aria-label={`About ${typeof label === "string" ? label : "this option"}`}
                    title="Tap or click for more info">
                <InfoIcon size={13} />
              </span>
            }
          >
            {/* Hints are plain strings with \n\n paragraph breaks, or ready-made JSX. */}
            {typeof hint === "string" ? hint.split("\n\n").map((para, i) => (
              <p key={i} style={{ margin: "0 0 10px" }}>{para}</p>
            )) : hint}
          </InfoModal>
        )}
      </span>
      <button
        type="button" role="switch" aria-checked={!!val}
        aria-label={typeof label === "string" ? label : undefined}
        className="tog"
        onClick={() => onChange(!val)}
        style={{ background: val ? accent : "var(--tint-white-10)" }}
      >
        <span className="tok" style={{ left: val ? 18 : 2 }} />
      </button>
    </div>
  );
}

// About page content — edit everything below this line freely.
// No component changes needed, just update the objects and arrays.

// `open` + `onToggle` make it controlled, so a list can keep one topic open at
// a time (Help). Without them it manages itself (search results stay all-open).
function CollapsibleAboutCard({ entry, defaultOpen = false, open: controlled, onToggle }) {
  const [selfOpen, setSelfOpen] = React.useState(defaultOpen);
  const open = controlled ?? selfOpen;
  const ref = React.useRef(null);
  // When the topic above collapses, this one jumps up the page: keep it in view.
  React.useEffect(() => { if (controlled) ref.current?.scrollIntoView?.({ block: "nearest" }); }, [controlled]);
  return (
    <div ref={ref} style={{ background:"var(--card-bg)",
      border:"1px solid var(--tint-white-07)", borderRadius:10, overflow:"hidden" }}>
      <button
        type="button"
        onClick={onToggle || (() => setSelfOpen(o => !o))}
        style={{ width:"100%", display:"flex", alignItems:"center",
          justifyContent:"space-between", gap:8, padding:"13px 15px",
          background:"transparent", border:"none", cursor:"pointer",
          fontSize: "var(--fs-body)", fontWeight:700, color:"var(--text-primary)", textAlign:"left",
          fontFamily:"inherit" }}
        aria-expanded={open}
      >
        <span>{entry.icon} {entry.title}</span>
        <span style={{ color:"var(--text-muted)", fontSize: "var(--fs-small)", lineHeight:1,
          display:"inline-block", transition:"transform 0.15s",
          transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
      </button>
      {open && (
        <div className="help-body" style={{ padding:"0 15px 13px" }}
          dangerouslySetInnerHTML={{ __html: entry.body }} />
      )}
    </div>
  );
}

function AboutButton() {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState(0);
  const TABS = ["About Me", "The App"];

  const tabBtn = (i) => ({
    flex:1, padding:"7px 4px", fontSize: "var(--fs-small)", fontWeight: tab===i ? 700 : 500,
    background: tab===i ? "rgba(96,165,250,0.18)" : "transparent",
    color: tab===i ? "var(--chart-2)" : "var(--text-muted)",
    border:"none", borderBottom: tab===i ? "2px solid var(--chart-2)" : "2px solid transparent",
    cursor:"pointer", transition:"all 0.15s",
  });

  const overlay = open ? ReactDOM.createPortal(
    <div
      onClick={() => setOpen(false)}
      style={{ position:"fixed", inset:0, background:"var(--tint-black-70)", zIndex:99999,
        display:"flex", alignItems:"flex-start", justifyContent:"center",
        padding:"40px 16px 60px", overflowY:"auto" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:"#0f1729", border:"1px solid rgba(96,165,250,0.2)",
          borderRadius:14, padding:"24px 28px 28px", maxWidth:600, width:"100%",
          boxShadow:"0 24px 60px var(--tint-black-80)" }}
      >
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
          <div>
            <div style={{ fontSize: "var(--fs-h2)", fontWeight:800, color:"var(--text-primary)", letterSpacing:"-0.3px" }}>
              {ABOUT_PRODUCT.name}
            </div>
            <div style={{ fontSize: "var(--fs-small)", color:"var(--text-faint)", marginTop:3 }}>v{APP_VERSION}</div>
          </div>
          <button onClick={() => setOpen(false)}
            style={{ background:"transparent", border:"none", color:"var(--text-muted)",
              cursor:"pointer", fontSize: "var(--fs-h1)", lineHeight:1, padding:"0 2px" }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{ display:"flex", borderBottom:"1px solid var(--tint-white-08)", marginBottom:22 }}>
          {TABS.map((t, i) => (
            <button key={i} style={tabBtn(i)} onClick={() => setTab(i)}>{t}</button>
          ))}
        </div>

        {/* Tab 0 — About Me */}
        {tab === 0 && (
          <div>
            <div style={{ fontSize: "var(--fs-h2)", fontWeight:700, color:"var(--text-primary)" }}>{ABOUT_ME.name}</div>
            <div style={{ fontSize: "var(--fs-small)", color:"var(--chart-2)", marginTop:3, marginBottom:14, letterSpacing:"0.5px" }}>
              {ABOUT_ME.tagline}
            </div>
            <p style={{ fontSize: "var(--fs-body)", color:"var(--text-secondary)", lineHeight:1.75, margin:"0 0 20px" }}>
              {ABOUT_ME.bio}
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
              {ABOUT_ME.links.filter(l => l.url).map(l => (
                <a key={l.label} href={l.url} target={l.url.startsWith("mailto:") ? "_self" : "_blank"} rel="noreferrer"
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
                    background:"var(--row-highlight)", border:"1px solid var(--tint-white-08)",
                    borderRadius:9, color:"var(--text-primary)", fontSize: "var(--fs-body)", fontWeight:600,
                    textDecoration:"none", transition:"background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background="rgba(96,165,250,0.12)"}
                  onMouseLeave={e => e.currentTarget.style.background="var(--row-highlight)"}
                >
                  <span style={{ fontSize: "var(--fs-h2)" }}>{l.icon}</span>
                  <span>{l.label}</span>
                  <span style={{ marginLeft:"auto", color:"var(--text-faint)", fontSize: "var(--fs-small)" }}>↗</span>
                </a>
              ))}
            </div>

            {/* Special thanks. Credit belongs to the people who found a bug and
                described it precisely enough to fix — that's rarer and more
                useful than generic feedback, and naming them publicly is the
                only payment on offer. Content lives in about.js (ABOUT_THANKS)
                so adding someone needs no JSX. */}
            {ABOUT_THANKS?.people?.length > 0 && (
              <div style={{ marginTop: 26, paddingTop: 20, borderTop: "1px solid var(--tint-white-08)" }}>
                <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-gold)", marginBottom: 8 }}>
                  ⭐ Special thanks
                </div>
                <p style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 14 }}>
                  {ABOUT_THANKS.intro}
                </p>
                {/* One wrapped line of names, not a card per person: the list grows and cards ate the modal. */}
                <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", lineHeight: 1.9 }}>
                  {ABOUT_THANKS.people.map((pr, i) => (
                    <span key={pr.handle} title={pr.note || undefined} style={{ whiteSpace: "nowrap" }}>
                      {i > 0 && <span style={{ color: "var(--text-faint)", margin: "0 8px" }}>·</span>}
                      {pr.url ? (
                        <a href={pr.url} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline", textDecorationColor: "var(--text-faint)" }}>{pr.handle}</a>
                      ) : pr.handle}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 1 — The App */}
        {tab === 1 && (
          <div>
            <div style={{ fontSize: "var(--fs-body)", color:"var(--chart-2)", fontWeight:600, marginBottom:10 }}>
              {ABOUT_PRODUCT.tagline}
            </div>
            <p style={{ fontSize: "var(--fs-body)", color:"var(--text-secondary)", lineHeight:1.75, margin:"0 0 18px" }}>
              {ABOUT_PRODUCT.description}
            </p>
            <div style={{ fontSize: "var(--fs-label)", fontWeight:700, color:"var(--text-secondary)", marginBottom:10, letterSpacing:"0.5px" }}>
              Key Capabilities
            </div>
            <ul style={{ margin:0, padding:0, listStyle:"none", display:"flex", flexDirection:"column", gap:8 }}>
              {ABOUT_PRODUCT.bullets.map((b, i) => (
                <li key={i} style={{ display:"flex", gap:10, fontSize: "var(--fs-body)", color:"var(--text-secondary)" }}>
                  <Dot tone="ok" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button onClick={() => setOpen(false)}
          style={{ marginTop:22, width:"100%", background:"rgba(96,165,250,0.08)",
            border:"1px solid rgba(96,165,250,0.25)", borderRadius:8, padding:"9px 0",
            color:"var(--chart-2)", fontSize: "var(--fs-body)", fontWeight:600, cursor:"pointer" }}>
          Close
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button className="mbtn" onClick={() => { setTab(0); setOpen(true); }}>
        About
      </button>
      {overlay}
    </>
  );
}

// Searchable knowledge base — pulled out of AboutButton's old "How It Works"
// tab so it's reachable on its own (people look for help mid-task, not from
// an about-the-author screen) and so a search box actually has somewhere
// findable to live. Content stays in ABOUT_FEATURES (src/about.js); this is
// purely presentation + filtering.
function HelpButton() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [openId, setOpenId] = React.useState(null); // one topic open at a time

  const overlay = open ? ReactDOM.createPortal(
    <div
      onClick={() => setOpen(false)}
      style={{ position:"fixed", inset:0, background:"var(--tint-black-70)", zIndex:99999,
        display:"flex", alignItems:"flex-start", justifyContent:"center",
        padding:"40px 16px 60px", overflowY:"auto" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:"#0f1729", border:"1px solid rgba(96,165,250,0.2)",
          borderRadius:14, padding:"24px 28px 28px", maxWidth:600, width:"100%",
          boxShadow:"0 24px 60px var(--tint-black-80)" }}
      >
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div style={{ fontSize: "var(--fs-h2)", fontWeight:800, color:"var(--text-primary)", letterSpacing:"-0.3px" }}>
            ❓ Help &amp; How It Works
          </div>
          <button onClick={() => setOpen(false)}
            style={{ background:"transparent", border:"none", color:"var(--text-muted)",
              cursor:"pointer", fontSize: "var(--fs-h1)", lineHeight:1, padding:"0 2px" }}>×</button>
        </div>

        <input
          type="text"
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search help topics… e.g. IRMAA, Roth, buckets, spending smile"
          style={{ width:"100%", boxSizing:"border-box", padding:"10px 14px", marginBottom:16,
            background:"var(--row-highlight)", border:"1px solid var(--tint-white-12)",
            borderRadius:9, color:"var(--text-primary)", fontSize: "var(--fs-body)", fontFamily:"inherit" }}
        />

        {ABOUT_PRODUCT.intro && !query.trim() && (
          <p style={{ fontSize: "var(--fs-body)", color:"var(--text-secondary)", lineHeight:1.75,
            margin:"0 0 18px", padding:"12px 14px", background:"rgba(96,165,250,0.06)",
            border:"1px solid rgba(96,165,250,0.15)", borderRadius:9 }}>
            {ABOUT_PRODUCT.intro}
          </p>
        )}

        {(() => {
          const q = query.trim().toLowerCase();
          const stripHtml = (html) => html.replace(/<[^>]+>/g, " ");
          const filtered = !q ? ABOUT_FEATURES : ABOUT_FEATURES.filter(e =>
            e.title.toLowerCase().includes(q) ||
            (e.group || "").toLowerCase().includes(q) ||
            (e.keywords || []).join(" ").toLowerCase().includes(q) ||
            stripHtml(e.body).toLowerCase().includes(q)
          );
          const groups = [...new Set(filtered.map(e => e.group).filter(Boolean))];
          const ungrouped = filtered.filter(e => !e.group);

          if (ABOUT_FEATURES.length === 0) {
            return (
              <div style={{ fontSize: "var(--fs-label)", color:"var(--text-faint)", textAlign:"center", padding:24 }}>
                No entries yet. Add items to ABOUT_FEATURES in src/about.js.
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div style={{ fontSize: "var(--fs-label)", color:"var(--text-faint)", textAlign:"center", padding:24 }}>
                No help topics match "{query.trim()}". Try a different word, or use Feedback to ask directly.
              </div>
            );
          }
          return (
            <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
              {groups.map(g => (
                <div key={g}>
                  <div style={{ fontSize: "var(--fs-small)", fontWeight:700, color:"var(--text-faint)", letterSpacing:"1px", marginBottom:9 }}>{g}</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                    {filtered.filter(e => e.group === g).map(e => (
                      <CollapsibleAboutCard key={e.id} entry={e} defaultOpen={!!q} {...(q ? {} : { open: openId === e.id, onToggle: () => setOpenId((id) => (id === e.id ? null : e.id)) })} />
                    ))}
                  </div>
                </div>
              ))}
              {ungrouped.map(e => (
                <CollapsibleAboutCard key={e.id} entry={e} defaultOpen={!!q} {...(q ? {} : { open: openId === e.id, onToggle: () => setOpenId((id) => (id === e.id ? null : e.id)) })} />
              ))}
            </div>
          );
        })()}

        <button onClick={() => setOpen(false)}
          style={{ marginTop:22, width:"100%", background:"rgba(96,165,250,0.08)",
            border:"1px solid rgba(96,165,250,0.25)", borderRadius:8, padding:"9px 0",
            color:"var(--chart-2)", fontSize: "var(--fs-body)", fontWeight:600, cursor:"pointer" }}>
          Close
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button className="mbtn" onClick={() => { setQuery(""); setOpen(true); }} title="Look up how a feature or number works">
        Help
      </button>
      {overlay}
    </>
  );
}

// Small hover / click / focus info popover — replaces flaky native `title`
// tooltips so the (i) always reveals its explanation (works on touch + keyboard too).
function Hint({ text, width = 240 }) {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", marginLeft: 4 }}>
      <span
        role="button"
        tabIndex={0}
        aria-label="More information"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        onClick={(e) => { e.stopPropagation(); setShow((s) => !s); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShow((s) => !s); } }}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 15, height: 15, borderRadius: "50%", background: "rgba(96,165,250,0.15)",
          color: "var(--chart-2)", fontSize: "var(--fs-small)", fontWeight: 700, fontStyle: "normal", lineHeight: 1,
          cursor: "help", border: "1px solid rgba(96,165,250,0.4)",
        }}
      >
        i
      </span>
      {show && (
        <span
          style={{
            position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
            width, background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 8,
            padding: "9px 11px", fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.55, fontWeight: 400,
            textAlign: "left", zIndex: 100001, boxShadow: "0 10px 30px var(--tint-black-60)", pointerEvents: "none",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

// `maxWidth` / `bodyMaxHeight` are additive overrides for content-heavy modals
// (the tax-detail breakdown needs a wide, scrollable panel — 480px with no cap
// would overflow the viewport). Defaults are unchanged, so every existing
// caller renders exactly as before.
//
// `open` / `onClose` add a CONTROLLED mode. A per-row detail panel mounts once
// and is pointed at whichever row was clicked, instead of one self-managing
// InfoModal per row (40 rows would otherwise mean 40 independent dialogs whose
// content each had to be built up front). Uncontrolled callers pass neither and
// behave exactly as before.
// ── Info-modal body text kit ────────────────────────────────────────────────
// A shared FORMAT for modal bodies so they read as one family instead of a wall
// of gray text. The template, top to bottom:
//   <ModalLede>       one-line takeaway (bold, primary)         — the answer
//   <ModalP>          supporting copy, with <Em>key terms</Em>  — the why
//   <ModalNote>       tinted callout for a caveat / "it is NOT" — the catch
// Em defaults to a neutral emphasis; pass color for a semantic key term
// (e.g. the modal's INFO_ACCENT so the term matches the header).
function ModalLede({ children }) {
  return <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.5, marginBottom: 10 }}>{children}</div>;
}
function ModalP({ children }) {
  return <p style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.75, margin: "0 0 10px" }}>{children}</p>;
}
function Em({ children, color = "var(--text-primary)" }) {
  return <strong style={{ color, fontWeight: 700 }}>{children}</strong>;
}
function ModalNote({ children, accent = "var(--accent-gold)", icon = "→" }) {
  return (
    <div style={{ display: "flex", gap: 9, background: withAlpha(accent, "12"), border: `1px solid ${withAlpha(accent, "33")}`,
      borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "10px 12px", marginTop: 4 }}>
      <span aria-hidden="true" style={{ color: accent, fontWeight: 800, flexShrink: 0, lineHeight: 1.5 }}>{icon}</span>
      <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function InfoModal({ title, children, accent = "var(--chart-2)", trigger, maxWidth = 480, bodyMaxHeight = null, open: controlledOpen, onClose }) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const trigRef = React.useRef(null);
  const setOpen = (next) => {
    if (isControlled) { if (!next && onClose) onClose(); }
    else setUncontrolledOpen(next);
    if (!next) trigRef.current?.focus(); // hand focus back to whatever opened it
  };
  // Keyboard users open with Enter/Space; the trigger is a span (not a
  // <button>) because some callers place it inside other controls.
  const trigProps = {
    ref: trigRef, role: "button", tabIndex: 0, "aria-haspopup": "dialog", className: "info-trig",
    onClick: () => setOpen(true),
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } },
  };
  const overlay = open ? ReactDOM.createPortal(
    <div
      onClick={() => setOpen(false)}
      style={{ position:"fixed", inset:0, background:"var(--tint-black-60)", zIndex:99999,
        display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
    >
      <div
        role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        ref={(el) => { if (el && !el.contains(document.activeElement)) el.focus(); }}
        onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } }}
        onClick={e => e.stopPropagation()}
        style={{ outline:"none", background:"#0f1729", border:`1px solid ${withAlpha(accent, "44")}`, borderRadius:12,
          padding:28, maxWidth, width:"100%", boxShadow:"0 24px 60px var(--tint-black-60)",
          maxHeight: "calc(100vh - 48px)", display: "flex", flexDirection: "column" }}
      >
        {/* Uniform header for EVERY info modal: an accent icon badge + the
            title carrying a 2.5px accent underline (the "how it works" look),
            over a faint accent hairline that separates header from body. One
            shell, so every modal in the app reads as one family. */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12,
          marginBottom:18, paddingBottom:12, borderBottom:`1px solid ${withAlpha(accent, "22")}`, flexShrink: 0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
            <span aria-hidden="true" style={{ display:"inline-flex", alignItems:"center", justifyContent:"center",
              width:28, height:28, borderRadius:8, background:withAlpha(accent, "1a"),
              border:`1px solid ${withAlpha(accent, "44")}`, color:accent, fontSize: "var(--fs-body)", fontWeight:800, flexShrink:0 }}>ⓘ</span>
            <div style={{ fontSize: "var(--fs-h2)", fontWeight:800, color:"var(--text-primary)", lineHeight:1.3,
              borderBottom:`2.5px solid ${accent}`, paddingBottom:4 }}>{title}</div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close"
            style={{ background:"transparent", border:"none", color:"var(--text-muted)",
              cursor:"pointer", fontSize: "var(--fs-h2)", lineHeight:1, marginTop:2, flexShrink:0 }}>×</button>
        </div>
        <div style={{ fontSize: "var(--fs-body)", color:"var(--text-secondary)", lineHeight:1.7,
          ...(bodyMaxHeight ? { maxHeight: bodyMaxHeight, overflowY: "auto", paddingRight: 6 } : {}) }}>{children}</div>
        <button onClick={() => setOpen(false)}
          style={{ marginTop:20, width:"100%", background:withAlpha(accent, "22"),
            border:`1px solid ${withAlpha(accent, "44")}`, borderRadius:8, padding:"8px 0",
            color:accent, fontSize: "var(--fs-body)", fontWeight:600, cursor:"pointer", flexShrink: 0 }}>
          Got it
        </button>
      </div>
    </div>,
    document.body
  ) : null;
  return (
    <>
      {trigger ? (
        <span {...trigProps} style={{ cursor:"pointer", display:"inline-flex" }}>
          {trigger}
        </span>
      ) : isControlled ? null : (
        <span
          {...trigProps} aria-label="More info"
          style={{ display:"inline-flex", alignItems:"center", justifyContent:"center",
            width:16, height:16, borderRadius:"50%", background:"var(--card-border)",
            border:`1px solid ${withAlpha(accent, "44")}`, color:accent, fontSize: "var(--fs-small)", fontWeight:700,
            cursor:"pointer", flexShrink:0 }}
          title="Click for more info"
        >?</span>
      )}
      {overlay}
    </>
  );
}

/**
 * A table header whose explanation opens on click.
 *
 * These used to be `<th title="…">Label <InfoIcon/></th>`. Adding the
 * visible info marker fixed discoverability (you can see an explanation
 * exists) but not reachability — `title=` doesn't exist on touch devices at
 * all, so every phone and tablet user could see the marker and never
 * actually read the text. The explanation is what tells you how to read the
 * column, so it needs to actually open: visible affordance, opens on click.
 *
 * `tip` accepts the same plain strings the old `title=` used, including the
 * `&#10;&#10;` / "\n\n" paragraph breaks and "• " bullets they were written with.
 */
// `...rest` is forwarded to the <th> so callers can hang attributes on the
// header cell itself — the withdrawal table tags each column with `data-cg`
// (its column group) so a view can hide whole groups in CSS. Without the
// forward, those tags were silently dropped and the group views did nothing.
function ThInfo({ children, tip, style, accent = "#93c5fd", modalTitle, ...rest }) {
  const paras = String(tip)
    .replace(/&#10;/g, "\n")
    .split(/\n\s*\n/)
    .filter(Boolean);
  return (
    <th style={style} {...rest}>
      {children}{" "}
      <InfoModal
        title={modalTitle || (typeof children === "string" ? children.trim() : "About this column")}
        accent={accent}
        trigger={
          <span style={{ cursor: "pointer", display: "inline-flex", color: accent }}
                title="Tap or click for more info">
            <InfoIcon size={12} />
          </span>
        }
      >
        {paras.map((para, i) => {
          const bullets = para.split("\n").filter(l => l.trim().startsWith("•"));
          if (bullets.length) {
            const lead = para.split("\n").filter(l => !l.trim().startsWith("•")).join(" ").trim();
            return (
              <div key={i} style={{ marginBottom: 10 }}>
                {lead && <p style={{ margin: "0 0 6px" }}>{lead}</p>}
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {bullets.map((b, j) => <li key={j}>{b.replace(/^\s*•\s*/, "")}</li>)}
                </ul>
              </div>
            );
          }
          return <p key={i} style={{ margin: "0 0 10px" }}>{para}</p>;
        })}
      </InfoModal>
    </th>
  );
}

function Slider({ label, value, min, max, step, stepNudge, format, onChange, quickPills, accent, titleHint, valueWidth, dataLever }) {
  const clamped = Math.max(min, Math.min(max, value));
  const pct = max > min ? ((clamped - min) / (max - min)) * 100 : 0;
  const trackRef = useRef(null);

  // Stepper nudge increments
  const nudge = stepNudge || step;
  const handleDec = useCallback((e) => {
    e?.stopPropagation();
    const next = Math.min(value, Math.max(min, Number((value - nudge).toFixed(4)))); // a typed value past the track must not snap back
    onChange(next);
  }, [value, min, nudge, onChange]);

  const handleInc = useCallback((e) => {
    e?.stopPropagation();
    const next = Math.max(value, Math.min(max, Number((value + nudge).toFixed(4))));
    onChange(next);
  }, [value, max, nudge, onChange]);

const [draft, setDraft] = useState(null);
  const commitDraft = useCallback(() => {
    if (draft !== null) {
      const n = parseMoneyInput(draft, min); // "$1,250,000", "1.25M", spaces
      if (n !== null) onChange(n);
      setDraft(null);
    }
  }, [draft, min, onChange]);

  const handleClick = useCallback(
    (e) => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width)
      );
      const stepped = Math.round((min + ratio * (max - min)) / step) * step;
      const rounded = Number(Math.max(min, Math.min(max, stepped)).toFixed(4));
      onChange(rounded);
    },
    [min, max, step, onChange]
  );

  const handleDrag = useCallback(
    (e) => {
      e.preventDefault();
      const move = (ev) => {
        if (!trackRef.current) return;
        const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const rect = trackRef.current.getBoundingClientRect();
        const ratio = Math.max(
          0,
          Math.min(1, (clientX - rect.left) / rect.width)
        );
        const stepped = Math.round((min + ratio * (max - min)) / step) * step;
        const rounded = Number(Math.max(min, Math.min(max, stepped)).toFixed(4));
        onChange(rounded);
      };
      const up = () => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("touchmove", move);
        window.removeEventListener("touchend", up);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
      window.addEventListener("touchmove", move, { passive: false });
      window.addEventListener("touchend", up);
    },
    [min, max, step, onChange]
  );

  // If no label is passed (e.g. inside DualInput), render a compact full-width track directly.
  if (!label) {
    return (
      <div className="sl-row-compact" style={{ width: "100%", padding: "3px 0 6px" }}>
        <div
          ref={trackRef}
          onClick={handleClick}
          style={{
            position: "relative",
            height: 20,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: 6,
              borderRadius: 3,
              background: "var(--tint-white-12)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              width: `${pct}%`,
              height: 6,
              borderRadius: 3,
              background: accent || "linear-gradient(90deg,#0d9488,var(--positive))",
            }}
          />
          <div
            onMouseDown={handleDrag}
            onTouchStart={handleDrag}
            style={{
              position: "absolute",
              left: `calc(${pct}% - 9px)`,
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: "var(--positive, #10b981)",
              border: "2.5px solid var(--positive)",
              boxShadow: "0 1px 4px var(--tint-black-50)",
              cursor: "grab",
              zIndex: 2,
              transition: "transform 0.1s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="sl-row" data-lever={dataLever}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span className="sl-label" title={titleHint || undefined} style={titleHint ? { cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 2 } : undefined}>{label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            className="sl-nudge-btn"
            onClick={handleDec}
            disabled={value <= min}
            title={`Decrease (${stepNudge ? `-${stepNudge}` : `-${step}`})`}
            aria-label={`Decrease ${label}`}
          >
            −
          </button>
          <input
            className="sl-val sl-val-input"
            type="text"
            inputMode="decimal"
            aria-label={label}
            value={draft !== null ? draft : format(value)}
            // width:auto on a text input doesn't shrink to its own value
            // (unlike block elements) — it just sits at the CSS max-width
            // regardless of content, so short values like "80%" got the
            // same box as "$1,250,000/yr". valueWidth lets a caller with
            // short, bounded content (a plain percentage, an age) opt into
            // a tighter box; every other Slider is untouched (prop omitted).
            style={valueWidth ? { minWidth: valueWidth, maxWidth: valueWidth } : undefined}
            title="Click to type an exact value — you can enter more than the slider's range"
            onFocus={(e) => {
              setDraft(String(value));
              requestAnimationFrame(() => e.target.select());
            }}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitDraft();
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                setDraft(null);
                e.currentTarget.blur();
              }
            }}
          />
          <button
            type="button"
            className="sl-nudge-btn"
            onClick={handleInc}
            disabled={value >= max}
            title={`Increase (${stepNudge ? `+${stepNudge}` : `+${step}`})`}
            aria-label={`Increase ${label}`}
          >
            +
          </button>
        </div>
      </div>

      {/* Full-width interactive slider track */}
      <div
        ref={trackRef}
        onClick={handleClick}
        style={{
          position: "relative",
          height: 20,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          width: "100%",
          padding: "0 2px",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 6,
            borderRadius: 3,
            background: "var(--tint-white-12)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 0,
            width: `${pct}%`,
            height: 6,
            borderRadius: 3,
            background: accent || "linear-gradient(90deg,#0d9488,var(--positive))",
            boxShadow: "0 0 10px var(--tint-sky-30)",
          }}
        />
        <div
          onMouseDown={handleDrag}
          onTouchStart={handleDrag}
          style={{
            position: "absolute",
            left: `calc(${pct}% - 9px)`,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#ffffff",
            border: "2.5px solid var(--positive)",
            boxShadow: "0 2px 6px var(--tint-black-60)",
            cursor: "grab",
            zIndex: 2,
            transition: "transform 0.12s, box-shadow 0.12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.25)";
            e.currentTarget.style.boxShadow = "0 0 12px rgba(90,209,240,0.8)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 6px var(--tint-black-60)";
          }}
        />
      </div>

      {/* Quick selection pill chips (if provided) */}
      {Array.isArray(quickPills) && quickPills.length > 0 && (
        <div style={{ display: "flex", gap: 5, marginTop: 1, flexWrap: "wrap" }}>
          {quickPills.map((pill) => {
            const isActive = Math.abs(value - pill.val) < 0.001;
            return (
              <button
                key={pill.lbl}
                type="button"
                onClick={() => onChange(pill.val)}
                style={{
                  background: isActive ? "var(--tint-sky-20)" : "var(--tint-white-04)",
                  border: isActive ? "1px solid var(--positive)" : "1px solid var(--tint-white-08)",
                  color: isActive ? "#5ad1f0" : "var(--text-muted)",
                  borderRadius: 4,
                  padding: "2px 6px",
                  fontSize: "var(--fs-small)",
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {pill.lbl}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
// Helper UI functions
function DualInput({ label, value, min, max, step, format, onChange }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}>{label}</span>
        <ANumInput
          value={value}
          onSet={onChange}
          min={min}
          max={max}
          step={step}
        />
      </div>
      <Slider label="" value={value} min={min} max={max} step={step} format={format} onChange={onChange} />
    </div>
  );
}

// Import / export
const LS_PROFILE_KEY = "aira_profile_v1";

// Progress check-ins.
// A running journal of plan snapshots (success rate, portfolio, spending,
// ages). Stored under their own key, outside the profile — unlike Export/
// Import, check-ins never get loaded back into the planner, they only feed
// the Progress view.
const LS_CHECKINS_KEY = "aira_checkins_v1";
function loadCheckIns() {
  try {
    const arr = JSON.parse(localStorage.getItem(LS_CHECKINS_KEY) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveCheckIns(list) {
  try {
    localStorage.setItem(LS_CHECKINS_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}
// Cache of the last completed Monte Carlo run, so reopening the app shows the
// numbers you last pressed Run for instead of spending a fresh simulation (or
// showing an empty screen) on every visit. Stored with the signature of the
// inputs that produced it, so the app can tell "these are your current
// numbers" from "these are from before you changed something", and stamped
// with the build so an engine change never re-presents results the current
// code wouldn't reproduce.
const LS_RESULTS_KEY = "aira_results_v1";
function saveResultsToLocal(sig, mc, stress) {
  try {
    localStorage.setItem(LS_RESULTS_KEY, JSON.stringify({
      sig, mc, stress, savedAt: new Date().toISOString(), buildTag: BUILD_TAG,
    }));
    return true;
  } catch {
    return false;   // private mode or quota — the app still works, it just re-runs
  }
}
function loadResultsFromLocal() {
  try {
    const raw = localStorage.getItem(LS_RESULTS_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.buildTag !== BUILD_TAG) return null;
    if (typeof data.sig !== "string" || !data.mc || typeof data.mc.rate !== "number") return null;
    return data;
  } catch {
    return null;
  }
}
// "just now" / "14:32" / "Mar 3" — enough to tell this session's numbers from
// ones restored from a previous visit, without a precision nobody needs.
function fmtLastRun(iso) {
  try {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return "";
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const sameDay = then.toDateString() === new Date().toDateString();
    if (sameDay) return then.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return then.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
export function saveProfileToLocal(values) {
  try {
    const hasPropIncome = (values.properties || []).some(pr => Number(pr.income) > 0);
    const payload = {
      ...values,
      ab: hasPropIncome ? 0 : (values.ab || 0),
      // rothConversionTarget preserves its raw value (e.g. "fill_22") so the dropdown
      // round-trips correctly. Engine strips "fill_" at the params boundary.
      fafsaEndYear: values.fafsaEndYear || null,
      cssEndYear: values.cssEndYear || null,
      savedAt: new Date().toISOString(),
      buildTag: BUILD_TAG,
    };
    localStorage.setItem(LS_PROFILE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}
/** Start over: forget the saved plan and its cached results, nothing else
 * (check-ins, bucket config, theme, year-end ack are separate keys and stay). */
export function resetLocalProfile() {
  try {
    localStorage.removeItem(LS_PROFILE_KEY);
    localStorage.removeItem(LS_RESULTS_KEY);
    return true;
  } catch {
    return false;
  }
}
function loadProfileFromLocal() {
  try {
    const raw = localStorage.getItem(LS_PROFILE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Migration: spSpendOutofState (legacy scenario-swap field) -> spOutOfCountry (additive).
    if (data && data.spOutOfCountry == null && data.spSpendOutofState) {
      data.spOutOfCountry = data.spSpendOutofState;
    }
    // Migration: legacy saves stripped the "fill_" prefix from rothConversionTarget.
    // Restore it so the dropdown matches an option again.
    if (data && data.rothConversionTarget && /^\d+$/.test(data.rothConversionTarget)) {
      data.rothConversionTarget = "fill_" + data.rothConversionTarget;
    }
    // Migration: six distribution strategies got retired. A saved profile
    // still naming one would match no branch in either engine, which reads
    // as a silent ~1%/yr spending cut instead of an error — so remap here,
    // at the one point every saved profile enters the app, and stamp
    // `withdrawalStrategyMigratedFrom` so the UI can say it happened.
    return migrateOrderingMode(migrateWithdrawalStrategy(data));
  } catch {
    return null;
  }
}
function exportProfile(values, name = "AiRA_Profile") {
  const blob = new Blob([JSON.stringify(values, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
    a.href = url;
    a.download = `${name}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
}

function importProfile(onLoad) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".json";

  input.onchange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        
        // Ensure accounts is always a valid array
        if (!Array.isArray(parsed.accounts)) {
          parsed.accounts = BLANK_PROFILE.accounts;
        }
        
        // Ensure properties is always a valid array
        if (!Array.isArray(parsed.properties)) {
          parsed.properties = BLANK_PROFILE.properties;
        } else {
            parsed.properties = parsed.properties.map((p, i) =>
              p.id ? { ...p, id: String(p.id) } : { ...p, id: "p" + (i + 1) }
            );
        }
        
        // Ensure checkpoints is always an array and all entries have string IDs
        if (!Array.isArray(parsed.checkpoints)) {
          parsed.checkpoints = [];
        } else {
          parsed.checkpoints = parsed.checkpoints.map(cp =>
            cp.id != null ? { ...cp, id: String(cp.id) } : { ...cp, id: Date.now().toString() + Math.random() }
          );
        }
        
        // Ensure otherIncomes is always a valid array
        if (!Array.isArray(parsed.otherIncomes)) {
          parsed.otherIncomes = [];
        }

        // Fix date format if needed
        if (parsed.dob && !/^\d{4}-\d{2}-\d{2}$/.test(parsed.dob)) {
          const d = new Date(parsed.dob);
          if (!isNaN(d.getTime())) {
            parsed.dob = d.toISOString().slice(0, 10);
          }
        }
        
        onLoad(parsed);
      } catch {
        alert("Invalid profile file — must be a valid AiRA JSON export.");
      }
    };
    reader.readAsText(file);
  };
  input.click();
}

// SSA 2022 Period Life Table — annual death probability qx by single age
// Source: https://www.ssa.gov/oact/STATS/table4c6.html
// Index 0 = age 50, index 50 = age 100
const SSA_QX_MALE = [
  0.00520,0.00568,0.00619,0.00674,0.00733, // 50-54
  0.00796,0.00865,0.00940,0.01022,0.01112, // 55-59
  0.01213,0.01324,0.01446,0.01580,0.01729, // 60-64
  0.01892,0.02069,0.02263,0.02475,0.02706, // 65-69
  0.02960,0.03239,0.03548,0.03890,0.04267, // 70-74
  0.04685,0.05149,0.05661,0.06224,0.06840, // 75-79
  0.07514,0.08250,0.09054,0.09930,0.10882, // 80-84
  0.11915,0.13033,0.14237,0.15527,0.16903, // 85-89
  0.18359,0.19878,0.21432,0.22962,0.24419, // 90-94
  0.25855,0.27357,0.28997,0.30826,0.32870, // 95-99
  0.35148,                                  // 100
];
const SSA_QX_FEMALE = [
  0.00280,0.00304,0.00330,0.00359,0.00390, // 50-54
  0.00425,0.00463,0.00507,0.00556,0.00612, // 55-59
  0.00675,0.00746,0.00824,0.00909,0.01003, // 60-64
  0.01107,0.01222,0.01349,0.01490,0.01646, // 65-69
  0.01819,0.02012,0.02228,0.02469,0.02739, // 70-74
  0.03044,0.03388,0.03774,0.04205,0.04683, // 75-79
  0.05211,0.05793,0.06433,0.07134,0.07900, // 80-84
  0.08733,0.09637,0.10613,0.11664,0.12795, // 85-89
  0.14011,0.15315,0.16711,0.18203,0.19792, // 90-94
  0.21476,0.23238,0.25058,0.26894,0.28700, // 95-99
  0.30462,                                  // 100
];

/* P(alive at toAge | alive at fromAge) from the SSA period life table above,
 * the one mortality source for the whole app. Ages below 50 are treated as q=0 (negligible for this app's planning
 * ranges); ages past 100 hold the age-100 rate. Exported for tests. */
export function survivalToAge(fromAge, toAge, sex = "blended") {
  if (toAge <= fromAge) return 1;
  let survM = 1, survF = 1;
  for (let age = fromAge; age < toAge; age++) {
    const i = Math.min(age - 50, 50);
    if (i >= 0) {
      survM *= 1 - (SSA_QX_MALE[i] || 0);
      survF *= 1 - (SSA_QX_FEMALE[i] || 0);
    }
  }
  return sex === "male" ? survM : sex === "female" ? survF : (survM + survF) / 2;
}

function FanChart({ pcts, retireAge, ssAge, rmdAge, inf, useReal, title, checkpoints, earlyRetireTarget, dob, portfolioGoal, currentAge, currentPort, contrib, hhProfile, preRetireEq, hoveredAge, table }) {
  const [showTargets, setShowTargets] = useState(true);
  const [view, setView] = useState("chart");

  const rawData = useMemo(() => deflate(pcts, inf, useReal), [pcts, inf, useReal]);

  // Deterministic accumulation path: current portfolio → retirement
  const accumData = useMemo(() => {
    if (!currentPort || currentAge == null || currentAge >= retireAge) return [];
    const ret = expectedReturn(preRetireEq ?? 91) / 100;
    let p = currentPort;
    const pts = [];
    for (let age = currentAge; age <= retireAge; age++) {
      pts.push({ age, accum: p });
      // Household total for this age when the full profile is available: the
      // 401(k) line alone understated every other stream, and a spouse who
      // retires first has to drop out on their own date. Falls back to the
      // bare `contrib` prop so any other caller keeps working.
      const add = hhProfile ? householdAnnualContribution(hhProfile, age) : (contrib || 0);
      // One-off inflows landing in this pre-retirement year. All three
      // engines deposit these during accumulation (runMC,
      // simulateDeterministicWithStrategy, accumulateToRetirement) but this
      // line didn't, so a windfall entered for a year before retirement was
      // invisible until the MC bands took over at retirement — the money
      // appeared to arrive on the retirement date no matter which year the
      // user typed. Same year mapping the engines use: accumulation year y
      // is calendar year CURRENT_YEAR + y.
      //
      // Inflows only, matching the engines — pre-retirement one-off costs
      // are assumed to be paid from wages, which no engine models.
      const evIn = computeCashFlowEvents(
        hhProfile?.cashFlowEvents, CURRENT_YEAR + (age - currentAge), hhProfile?.inf ?? 2.5, CURRENT_YEAR,
      ).inflow;
      p = Math.round(p * (1 + ret) + add + evIn);
    }
    return pts;
  }, [currentPort, currentAge, retireAge, contrib, preRetireEq, hhProfile]);

  // Merge accumulation into chart data so XAxis spans both phases.
  // When toggle is OFF, revert to retirement-only range (rawData).
  const data = useMemo(() => {
    if (!showTargets || accumData.length === 0) return rawData;
    const preRetire = accumData.slice(0, -1).map(d => ({ age: d.age, accum: d.accum }));
    const atRetire = rawData[0] ? { ...rawData[0], accum: accumData[accumData.length - 1].accum } : null;
    return [...preRetire, ...(atRetire ? [atRetire] : []), ...rawData.slice(atRetire ? 1 : 0)];
  }, [accumData, rawData, showTargets]);

  // Interpolate which percentile a value falls at within the fan bands
  const calcPercentile = (value, row) => {
    if (!row || !row.p10 || !row.p90) return null;
    const { p10, p25, p50, p75, p90 } = row;
    if (value <= p10) return "≤10th";
    if (value <= p25) return `~${Math.round(10 + (value - p10) / (p25 - p10) * 15)}th`;
    if (value <= p50) return `~${Math.round(25 + (value - p25) / (p50 - p25) * 25)}th`;
    if (value <= p75) return `~${Math.round(50 + (value - p50) / (p75 - p50) * 25)}th`;
    if (value <= p90) return `~${Math.round(75 + (value - p75) / (p90 - p75) * 15)}th`;
    return "≥90th";
  };

  // "You Are Here" percentile (only meaningful if already in retirement)
  const nowPct = currentAge >= retireAge
    ? calcPercentile(currentPort, pcts.find(d => d.age === currentAge))
    : null;

  const maxY = useMemo(() => {
    if (!data || data.length === 0) return 5_000_000;
    const maxPortfolio = Math.max(...data.map(d => Math.max(d.p90 || 0, d.p75 || 0, d.p50 || 0, d.accum || 0)));
    return Math.max(maxPortfolio, portfolioGoal || 0, earlyRetireTarget || 0, currentPort || 0) * 1.05;
  }, [data, portfolioGoal, earlyRetireTarget, currentPort]);

  return (
    <div className="chart-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          {title && <div className="ct" style={{ margin: 0 }}>{title}</div>}
          {showTargets && (() => {
            const currentYear = new Date().getFullYear();
            // One line per goal: the amount, then when the median path reaches it.
            const goalLine = (icon, name, amount, color) => {
              const cross = (pcts || []).find(d => d.p50 >= amount);
              const diff = cross ? cross.age - (retireAge || 65) : 0;
              const yrs = `${Math.abs(diff)} yr${Math.abs(diff) !== 1 ? "s" : ""}`;
              return (
                <span>
                  <strong style={{ color }}>{icon} {name} ${Math.round(amount).toLocaleString()}</strong>{" "}
                  {!cross
                    ? "not reached on the median path"
                    : `age ${cross.age} · ${currentYear + (cross.age - (currentAge || 60))} · ${diff < 0 ? `${yrs} before retirement` : diff === 0 ? "at retirement" : `${yrs} after retirement`}`}
                </span>
              );
            };
            return (
              <div style={{ display: "flex", gap: "2px 18px", flexWrap: "wrap", fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {goalLine("🚀", "Trigger", earlyRetireTarget, "var(--chart-9)")}
                {goalLine("🎯", "Reassess", portfolioGoal, "var(--accent-gold)")}
              </div>
            );
          })()}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {table && ["chart", "table"].map((k) => (
            <button key={k} type="button" className={`mbtn ${view === k ? "on" : ""}`} aria-pressed={view === k} onClick={() => setView(k)} style={{ padding: "3px 10px" }}>
              {k === "chart" ? "Chart" : "Table"}
            </button>
          ))}
          <Toggle val={showTargets} onChange={setShowTargets} label="Milestones" accent="var(--accent-gold)" />
        </div>
      </div>
      {view === "table" ? table : (<>
      {/* Height follows the viewport so the whole chart fits on one screen. */}
      <div style={{ height: "clamp(320px, 50vh, 640px)" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 28, right: 48, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="g90v5" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--chart-4)" stopOpacity={0.15} />
              <stop offset="100%" stopColor="var(--chart-4)" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="g75v5" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--chart-5)" stopOpacity={0.22} />
              <stop offset="100%" stopColor="var(--chart-5)" stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="var(--row-highlight)"
          />
          <XAxis
            dataKey="age"
            stroke="var(--divider)"
            tickMargin={6}
            tick={{ fill: "var(--text-muted)", fontSize: "var(--fs-small)", fontFamily: "var(--font-mono)", letterSpacing: "0.01em" }}
          />
          <YAxis
            yAxisId="port"
            stroke="var(--divider)"
            tickMargin={4}
            tick={{ fill: "var(--text-muted)", fontSize: "var(--fs-small)", fontFamily: "var(--font-mono)" }}
            tickFormatter={(v) => fmtDollar(v)}
            width={MONEY_AXIS_WIDTH}
            domain={[0, maxY]}
          />
          <Tooltip content={<Tip />} />

          {/* Vertical reference lines (D-Day, SS, RMD) */}
          <ReferenceLine yAxisId="port" x={retireAge} stroke="var(--accent-gold)" strokeWidth={1.5} strokeDasharray="4 3" label={{ value: "D-Day (Retirement Date)", fill: "var(--accent-gold)", fontSize: "var(--fs-small)", fontWeight: 600, position: "top" }} />
          <ReferenceLine yAxisId="port" x={ssAge} stroke="#c084fc" strokeWidth={1.5} strokeDasharray="4 3" label={{ value: "SS", fill: "#c084fc", fontSize: "var(--fs-small)", fontWeight: 600, position: "top" }} />
          <ReferenceLine yAxisId="port" x={rmdAge} stroke="var(--positive)" strokeWidth={1} strokeDasharray="4 3" label={{ value: "RMD", fill: "var(--positive)", fontSize: "var(--fs-small)", fontWeight: 600, position: "top" }} />

          {/* Fan areas and percentile lines */}
          <Area yAxisId="port" type="monotone" dataKey="p90" stroke={FAN_COLORS["90th"]} strokeWidth={1} strokeDasharray="4 2" fill="url(#g90v5)" dot={false} name="90th" legendType="none" />
          <Area yAxisId="port" type="monotone" dataKey="p75" stroke={FAN_COLORS["75th"]} strokeWidth={1} strokeDasharray="3 2" fill="url(#g75v5)" dot={false} name="75th" legendType="none" />
          <Line yAxisId="port" type="monotone" dataKey="p50" stroke={FAN_COLORS["Median"]} strokeWidth={2.5} dot={false} name="Median" />
          <Line yAxisId="port" type="monotone" dataKey="p25" stroke={FAN_COLORS["25th"]} strokeWidth={1.5} dot={false} strokeDasharray="5 3" name="25th" />
          <Line yAxisId="port" type="monotone" dataKey="p10" stroke={FAN_COLORS["10th"]} strokeWidth={1.5} dot={false} strokeDasharray="3 3" name="10th" />

          {/* Hover highlight — when a row in the age-band table below is hovered,
              spotlight that age column and mark its percentile values on the fan. */}
          {hoveredAge != null && (() => {
            const row = data.find((d) => d.age === hoveredAge);
            if (!row) return null;
            const dot = (val, color) =>
              val == null ? null : (
                <ReferenceDot yAxisId="port" x={hoveredAge} y={val} r={4} fill={color} stroke="var(--bg-base)" strokeWidth={1.5} isFront />
              );
            return (
              <>
                <ReferenceLine yAxisId="port" x={hoveredAge} stroke="rgba(56,189,248,0.5)" strokeWidth={2}
                  label={{ value: `Age ${hoveredAge}`, fill: "var(--accent)", fontSize: "var(--fs-small)", position: "top" }} />
                {dot(row.p90, FAN_COLORS["90th"])}
                {dot(row.p75, FAN_COLORS["75th"])}
                {dot(row.p50, FAN_COLORS["Median"])}
                {dot(row.p25, FAN_COLORS["25th"])}
                {dot(row.p10, FAN_COLORS["10th"])}
              </>
            );
          })()}

          {/* Deterministic accumulation path (pre-retirement) */}
          {showTargets && accumData.length > 0 && (
            <Line yAxisId="port" type="monotone" dataKey="accum" stroke="var(--chart-2)" strokeWidth={2}
              strokeDasharray="6 3" dot={false} name="Expected path" connectNulls={false} />
          )}

          {/* You Are Here */}
          {showTargets && currentAge != null && currentPort != null && (
            <ReferenceDot
              yAxisId="port"
              x={currentAge} y={currentPort} r={7}
              fill="var(--chart-2)" stroke="#0f1729" strokeWidth={2}
              label={{ value: nowPct ? `▶ Now · ${nowPct} %ile` : "▶ Now", fill: "var(--chart-2)", fontSize: "var(--fs-small)", position: "top" }}
            />
          )}

          {/* Checkpoint dots — color + percentile label, one label per age */}
          {(() => {
            if (!checkpoints || !dob) return null;
            const labeledAges = new Set();
            return checkpoints.map((cp) => {
              if (!cp.date) return null;
              // Shared helper. The hand-rolled version here compared
              // month-day as plain strings, so e.g. "9-5" < "10-1" was true
              // as strings but wrong as a date — mis-aging checkpoints in
              // some months.
              const age = ageFromDob(dob, cp.date);
              if (age == null) return null;

              const isPreRetire = age < retireAge;
              if (isPreRetire && !showTargets) return null;

              let color = "var(--text-muted)";
              let pctLabel = "";

              if (isPreRetire) {
                const accumAtAge = accumData.find(d => d.age === age)?.accum;
                if (accumAtAge) {
                  const ratio = cp.value / accumAtAge;
                  if (ratio >= 1)         { color = "var(--positive)"; pctLabel = `+${Math.round((ratio - 1) * 100)}%`; }
                  else if (ratio >= 0.85) { color = "var(--accent-gold)"; pctLabel = `${Math.round((ratio - 1) * 100)}%`; }
                  else                    { color = "var(--negative)"; pctLabel = `${Math.round((ratio - 1) * 100)}%`; }
                }
              } else {
                const fanRow = pcts.find(d => d.age === age);
                if (fanRow) {
                  if (cp.value >= fanRow.p50)      color = "var(--positive)";
                  else if (cp.value <= fanRow.p25) color = "var(--negative)";
                  else                             color = "var(--accent-gold)";
                  pctLabel = calcPercentile(cp.value, fanRow) || "";
                }
              }

              // Only the first checkpoint at each age gets a text label
              const showLabel = !labeledAges.has(age);
              if (showLabel) labeledAges.add(age);
              const labelText = showLabel
                ? ([cp.note, pctLabel].filter(Boolean).join(" · ") || "●")
                : "";

              return (
                <ReferenceDot
                  yAxisId="port"
                  key={cp.id}
                  x={age} y={cp.value} r={5}
                  fill={color} stroke="#fff" strokeWidth={1.5}
                  label={showLabel ? { value: labelText, fill: color, fontSize: "var(--fs-small)", position: "top" } : undefined}
                />
              );
            });
          })()}

          {/* Target horizontal lines (toggled via showTargets) */}
          {showTargets && <ReferenceLine
            yAxisId="port"
            y={portfolioGoal}
            stroke="var(--accent-gold)"
            strokeWidth={2.5}
            strokeDasharray="0"
            label={{
              value: `🎯 Reassess $${Math.round(portfolioGoal).toLocaleString()}`,
              fill: "var(--bg-base)",
              fontSize: "var(--fs-label)",
              fontWeight: 700,
              position: "right",
              style: { background: "var(--accent-gold)", padding: "4px 8px", borderRadius: 4, boxShadow: "0 2px 6px rgba(0,0,0,0.3)" }
            }}
          />}
          {showTargets && <ReferenceLine
            yAxisId="port"
            y={earlyRetireTarget}
            stroke="var(--chart-9)"
            strokeWidth={2.5}
            strokeDasharray="0"
            label={{
              value: `🚀 Trigger $${Math.round(earlyRetireTarget).toLocaleString()}`,
              fill: "#fff",
              fontSize: "var(--fs-label)",
              fontWeight: 700,
              position: "right",
              style: { background: "var(--chart-9)", padding: "4px 8px", borderRadius: 4, boxShadow: "0 2px 6px rgba(0,0,0,0.3)" }
            }}
          />}
          {showTargets && (() => {
            const pctsForDots   = pcts || [];
            const reassessCross = pctsForDots.find(d => d.p50 >= portfolioGoal);
            const triggerCross  = pctsForDots.find(d => d.p50 >= earlyRetireTarget);
            return (
              <>
                {reassessCross && (
                  <ReferenceDot
                    yAxisId="port"
                    x={reassessCross.age} y={portfolioGoal}
                    r={6} fill="var(--accent-gold)" stroke="var(--bg-base)" strokeWidth={2}
                    label={{ value: `Age ${reassessCross.age}`, fill: "var(--accent-gold)", fontSize: "var(--fs-small)", position: "top" }}
                  />
                )}
                {triggerCross && (
                  <ReferenceDot
                    yAxisId="port"
                    x={triggerCross.age} y={earlyRetireTarget}
                    r={6} fill="var(--chart-9)" stroke="var(--bg-base)" strokeWidth={2}
                    label={{ value: `Age ${triggerCross.age}`, fill: "var(--chart-9)", fontSize: "var(--fs-small)", position: "top" }}
                  />
                )}
              </>
            );
          })()}
        </ComposedChart>
      </ResponsiveContainer>
      </div>

      {/* Legend: the two goal lines are named in the status line above. */}
      <div className="leg">
        {[
          ...(showTargets && accumData.length > 0 ? [{ c: "var(--chart-2)", l: "Expected path" }] : []),
          { c: FAN_COLORS["Median"], l: "Median" },
          { c: `linear-gradient(90deg, ${FAN_COLORS["75th"]} 50%, ${FAN_COLORS["25th"]} 50%)`, l: "75th / 25th %ile" },
          { c: `linear-gradient(90deg, ${FAN_COLORS["90th"]} 50%, ${FAN_COLORS["10th"]} 50%)`, l: "90th / 10th %ile" },
        ].map((i) => (
          <div key={i.l} className="li">
            <div className="ll" style={{ background: i.c }} />
            {i.l}
          </div>
        ))}
      </div>
      </>)}
    </div>
  );
}

// Splits a profile's "Other Expenses" carveouts by label into Medical /
// Long-Term Care / Other, for the Income & Expenses breakdown below. Carveouts
// are matched by keyword in their label so users can drive these categories
// just by naming a carveout "Medical" or "Long-Term Care".
function categorizeCarveouts(carveouts, yr, inf) {
  const iF = Math.pow(1 + (inf ?? 2.5) / 100, yr - new Date().getFullYear());
  let medical = 0, ltc = 0, other = 0;
  for (const c of carveouts || []) {
    if (yr > (c.endYear || 9999)) continue;
    const amt = Math.round((c.annual || 0) * iF);
    const label = (c.label || "").toLowerCase();
    if (/medical|health/.test(label)) medical += amt;
    else if (/long.?term|ltc|nursing/.test(label)) ltc += amt;
    else other += amt;
  }
  return { medical, ltc, other };
}

/* One vocabulary for income, everywhere.
 *
 * On the Income tab it wasn't called "fixed income," it was called SS on
 * that chart, so it didn't map well against the other surfaces. The same
 * three streams were named three different ways across surfaces — "SS"/
 * "Rental"/"Other Inc" in the schedule table, "Social Security"/"Rental/
 * Passive"/"Other Income" here, and enumerated as "Social Security +
 * Pension/Other + Annuity/Rental" in the withdrawal table's own legend. A
 * label should mean the same thing everywhere it appears, and the component
 * names should be recognizable as the parts of the "Income" aggregate.
 *
 * Canonical names (match the engine fields they render — r.ss,
 * r.otherIncome, r.annuityRental): "Social Security", "Pension/Other",
 * "Annuity/Rental". Aggregate of the three: "Income". No fourth spelling. */
/* "One-Off Income" is `r.eventInflow` — an inheritance, home sale or
 * lump-sum pension landing in a retirement year. It was missing from this
 * list entirely, so the one surface a user goes to for "where does my money
 * come from" was the only one that dropped it: the engine row carries it,
 * and the Withdrawal Plan table already marks it (+💰). It's not
 * double-counted against Savings Drawdown — the waterfall deposits an
 * inflow into its bucket and computes the year's draw separately, so the
 * two are independent money-in figures. */
const INCOME_CATS = [
  [INCOME_LABELS.savingsDrawdown, "var(--accent-teal)"],
  ["Social Security", withAlpha("#7c3aed", "cc")],
  ["Annuity/Rental", withAlpha("#295ff1", "cc")],
  ["Pension/Other", withAlpha("#eab308", "cc")],
  ["One-Off Income", withAlpha("#ec4899", "cc")],
  ["Roth Conversion", "var(--accent-gold)"],
];

const EXPENSE_CATS = [
  ["General/Living", "var(--accent)"],
  ["Mortgage/Housing", "var(--chart-3)"],
  ["Medical", "var(--positive)"],
  ["Long-Term Care", "var(--accent-purple)"],
  ["Other Expenses", "var(--text-secondary)"],
  ["Income Tax", "var(--chart-1)"],
  ["Capital Gains Tax", "var(--text-muted)"],
  ["Moved to Roth", "var(--accent-gold)"],
  ["Saved to Accounts", withAlpha("#ec4899", "cc")],
];

// "Income & Expenses" — a Boldin-style pair of stacked-bar charts sourced from
// the Smart Waterfall plan, so the figures shown here (including Roth
// conversions, mortgage payoff, and carveouts) match the Withdrawal Plan tab
// by construction. Hovering a year updates the side panel from "Lifetime"
// totals to that year's breakdown.

// Everything a waterfall row spends out of the accounts. The engine keeps the
// forced RMD out of fromPretax, so leaving it off made the bar collapse at RMD
// age while the accounts were still full; the reinvested surplus never left.
// Conversion tax counts only where it was paid from an account rather than
// withheld from the conversion itself (that part is inside the conversion bar).
export function savingsDrawdownOf(r) {
  const convTaxPaid = (r.convTaxFromCash || 0) + (r.convTaxFromTaxable || 0) + (r.convTaxFromPretax || 0);
  const convTaxWithheld = (r.conversionAmount || 0) - (r.convToRoth ?? r.conversionAmount ?? 0);
  return r.fromCash + r.fromTaxable + r.fromPretax + r.fromRoth + (r.rmd || 0) - (r.rmdSurplus || 0)
    + convTaxPaid - convTaxWithheld;
}

// One chart row per waterfall year: the income stack and the expense stack.
export function incomeExpenseRow(r, carveouts, inf) {
  const { medical, ltc, other } = categorizeCarveouts(carveouts, r.yr, inf);
  return {
    yr: r.yr, age: r.age,
    "Social Security": r.ss,
    "Annuity/Rental": r.annuityRental,
    "Pension/Other": r.otherIncome,
    [INCOME_LABELS.savingsDrawdown]: savingsDrawdownOf(r),
    "One-Off Income": r.eventInflow || 0,
    "Roth Conversion": r.conversionAmount,
    "General/Living": r.spending,
    "Mortgage/Housing": r.housingCost,
    "Medical": medical,
    "Long-Term Care": ltc,
    "Other Expenses": other + (r.eventCost || 0),
    // The 10% early-withdrawal penalty is an IRS charge the draws pay, so it belongs here.
    "Income Tax": r.fedTax + r.stateTax + r.irmaa + (r.earlyPenalty || 0),
    "Capital Gains Tax": 0,
    // Money that stays in the plan: the two stacks only balance if it shows.
    "Moved to Roth": r.convToRoth ?? r.conversionAmount ?? 0,
    "Saved to Accounts": (r.eventInflow || 0) + (r.surplusToTaxable || 0),
  };
}

function IncomeExpensesChart({ p, inf }) {
  const rows = useMemo(() => waterfallForActiveStrategy(p)?.smart?.rows ?? [], [p]);

  const data = useMemo(() => rows.map((r) => incomeExpenseRow(r, p.carveouts, inf)), [rows, p.carveouts, inf]);

  const [hoverYr, setHoverYr] = useState(null);
  const hoverRow = hoverYr != null ? data.find((d) => d.yr === hoverYr) : null;
  const onMove = (e) => { if (e && e.activeLabel != null) setHoverYr(e.activeLabel); };
  const onLeave = () => setHoverYr(null);

  if (!data.length) return null;

  return (
    <>
      <IncomeExpenseStack
        title="Estimated Income, Drawdowns & Roth Conversions"
        /* The horizon is stated explicitly because leaving it out reads as a
           dropped entry. A user who enters a windfall for a pre-retirement
           year finds no trace of it here and concludes the chart is broken
           — it isn't, the chart just starts at retirement and that money is
           already inside the balances these years begin from. The silence
           was the actual bug. */
        subtitle={`Sourced from the Smart Waterfall plan — hover a year to see its breakdown on the right. Retirement years only (${data[0]?.yr} onward): money arriving before then is already inside the balances these years start from.`}
        data={data} categories={INCOME_CATS}
        hoverYr={hoverYr} hoverRow={hoverRow}
        onMove={onMove} onLeave={onLeave}
        footnote="Where each row comes from: Annuity/Rental = your Annuity/Benefit input plus each property's Annual income. Social Security and Pension/Other come from their own inputs. Withdrawals from 401(k)/IRA, taxable, cash and Roth accounts are all in Savings & 401(k)/IRA Drawdown, never in Annuity/Rental."
      />
      <IncomeExpenseStack
        title="Estimated Expenses"
        subtitle="Living costs, housing, taxes, and carveouts (e.g. medical, long-term care) — hover a year for its breakdown"
        data={data} categories={EXPENSE_CATS}
        hoverYr={hoverYr} hoverRow={hoverRow}
        onMove={onMove} onLeave={onLeave}
        dashKeys={notEnteredKeys(p)}
        zeroNotes={{ "Mortgage/Housing": housingRowNote(p, data.reduce((a, d) => a + (d["Mortgage/Housing"] || 0), 0)) }}
        reconcile
        footnote="A dash means nothing was entered for that row, which is not the same as zero cost: Medical, Long-Term Care and Other Expenses appear only if you add carveouts (or Planned One-Off Expenses), and Mortgage/Housing says why when it is empty (nothing entered, housing already inside your spending, or the loan is paid off before these years begin). Only the primary property's mortgage is charged as a payment. Capital Gains Tax is not yet separately modeled (shown as —) — realized gains on taxable-account draws are folded into Income Tax. Moved to Roth is this year's conversion after any tax withheld from it; Saved to Accounts is one-off income and income beyond what you spend, both deposited back into your accounts. Each year's total matches the Income chart."
      />
      {p.ssAge > p.retireAge && (
        <div className="flag-w" style={{ fontSize: "var(--fs-small)" }}>
          Social Security gap, ages {p.retireAge}–{p.ssAge - 1} — with no SS yet, your
          portfolio carries the full spending need (the green Savings & 401(k)/IRA Drawdown above is
          largest here). This is the highest sequence-of-returns risk window.
        </div>
      )}
    </>
  );
}

// One row of the portfolio-draw reconciliation panel. Negative values render with a
// leading minus and in the muted offset color; the dollar magnitude is always shown.
function ReconLine({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0", fontSize: "var(--fs-small)" }}>
      <div style={{ color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: "inline-block", flexShrink: 0 }} />
        {label}
      </div>
      <div style={{ color: "var(--text-secondary)", fontFamily: "'JetBrains Mono',monospace" }}>
        {value < 0 ? `−${fmtDollar(Math.abs(value))}` : fmtDollar(value)}
      </div>
    </div>
  );
}

/** Expense rows that are 0 only because nothing was entered, so they show "—" instead of a confident $0. */
export function notEnteredKeys(p) {
  const keys = ["Capital Gains Tax"];
  const oneOffOut = (p.cashFlowEvents || []).some((e) => e.direction !== "in");
  if (!(p.carveouts || []).length) keys.push("Medical", "Long-Term Care", ...(oneOffOut ? [] : ["Other Expenses"]));
  if (!(p.mortBalance > 0)) keys.push("Mortgage/Housing");
  return keys;
}

/** Why the Mortgage/Housing row is empty, from the same fields the engine reads (housingType, mortBalance,
 * annualRent). "" when a housing cost is actually charged. `lifetimeTotal` = the row's total over the chart's years. */
export function housingRowNote(p, lifetimeTotal) {
  const type = p.housingType || "own";
  if (type === "none") return "included in your spending";
  if (type === "rent") return p.annualRent > 0 ? "" : "no rent entered";
  if (!(p.mortBalance > 0)) return "no mortgage entered";
  return lifetimeTotal > 0 ? "" : "paid off before retirement";
}

function IncomeExpenseStack({ title, subtitle, data, categories, hoverYr, hoverRow, onMove, onLeave, footnote, reconcile, dashKeys = [], zeroNotes = {} }) {
  const rows = categories.map(([key, color]) => ({
    key, color,
    value: hoverRow ? (hoverRow[key] || 0) : data.reduce((s, d) => s + (d[key] || 0), 0),
  }));
  const total = rows.reduce((s, r) => s + r.value, 0);

  // Portfolio-draw reconciliation (expense panel only): everything going out,
  // minus every other source of money, equals the green drawdown bar exactly.
  const sumKey = (k) => hoverRow ? (hoverRow[k] || 0) : data.reduce((s, d) => s + (d[k] || 0), 0);
  const recon = reconcile ? (() => {
    const otherIncome  = sumKey("Social Security") + sumKey("Annuity/Rental") + sumKey("Pension/Other")
      + sumKey("One-Off Income") + sumKey("Roth Conversion");
    const draw         = sumKey(INCOME_LABELS.savingsDrawdown);
    const spendNeed    = sumKey("General/Living") + sumKey("Mortgage/Housing") + sumKey("Medical")
      + sumKey("Long-Term Care") + sumKey("Other Expenses");
    const taxes        = sumKey("Income Tax") + sumKey("Capital Gains Tax");
    const kept         = sumKey("Moved to Roth") + sumKey("Saved to Accounts");
    return { otherIncome, draw, spendNeed, taxes, kept };
  })() : null;

  return (
    <div className="chart-card">
      <div className="ct">{title}</div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 8 }}>{subtitle}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 12 }}>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} onMouseMove={onMove} onMouseLeave={onLeave}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
            <XAxis dataKey="yr" stroke="#1e3a5f" tick={{ fill: "#71a8f7", fontSize: "var(--fs-small)" }} />
            <YAxis stroke="#1e3a5f" tick={{ fill: "#71a8f7", fontSize: "var(--fs-small)" }} tickFormatter={(v) => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
            <Tooltip content={<Tip />} />
            {categories.map(([key, color], i) => (
              <Bar key={key} dataKey={key} stackId="a" fill={color} radius={i === categories.length - 1 ? [2, 2, 0, 0] : undefined} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <div style={{ background: "var(--tint-white-02)", border: "1px solid var(--tint-white-06)", borderRadius: 8, padding: 12, fontSize: "var(--fs-label)", alignSelf: "start" }}>
          <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 8 }}>
            {hoverYr != null ? `Year ${hoverYr}` : "Lifetime"}
          </div>
          {rows.map(({ key, color, value }) => (
            <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-secondary)" }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: color, display: "inline-block" }} />
                <span>
                  {key}
                  {value === 0 && zeroNotes[key] && <span style={{ display: "block", fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{zeroNotes[key]}</span>}
                </span>
              </div>
              <div style={{ color: "var(--text-primary)", fontFamily: "'JetBrains Mono',monospace" }}>{value === 0 && (zeroNotes[key] || dashKeys.includes(key)) ? "—" : fmtDollar(value)}</div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--tint-white-08)", marginTop: 8, paddingTop: 6, fontWeight: 700 }}>
            <div style={{ color: "var(--text-primary)" }}>Total</div>
            <div style={{ color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(total)}</div>
          </div>

          {recon && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed var(--tint-white-12)" }}>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>
                What your portfolio must cover
              </div>
              <ReconLine label="Spending, housing, carveouts, one-offs" value={recon.spendNeed} color="var(--text-secondary)" />
              <ReconLine label="+ Taxes" value={recon.taxes} color="var(--chart-1)" />
              <ReconLine label="+ Moved to Roth / saved to accounts" value={recon.kept} color="var(--accent-gold)" />
              <ReconLine label="− Other income (SS, rental, pension, one-off, conversion)" value={-recon.otherIncome} color="#7c3aed" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--tint-white-08)", marginTop: 6, paddingTop: 6, fontWeight: 700 }}>
                <div style={{ color: "var(--chart-11)" }}>= Drawn from savings</div>
                <div style={{ color: "var(--chart-11)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(recon.draw)}</div>
              </div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 8, lineHeight: 1.5 }}>
                “Drawn from savings” is the green <strong style={{ color: "var(--chart-11)" }}>Savings &amp; 401(k)/IRA Drawdown</strong> bar above, including RMDs and the tax the draws themselves cause. These lines add up exactly, so the two charts have the same total every year.
              </div>
            </div>
          )}
        </div>
      </div>
      {footnote && <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 8, lineHeight: 1.5 }}>ℹ️ {footnote}</div>}
    </div>
  );
}

// Two-way mapping between the Conversion Plan tab's button vocabulary
// ("fill_22", "no_convert", "irmaa_safe" — matches rothConversionPlan.js's
// ROTH_MODE_TO_TARGET) and the persisted profile field
// params.rothConversionTarget ("fill_22", "off", "irmaa", plus the legacy
// un-prefixed "37"). This keeps the Conversion Plan tab's selector and the
// Withdrawal Plan / Monte Carlo's stored setting as a single value, no
// separate "rothMode" state.
const PROFILE_TO_ROTHMODE = {
  off: "no_convert",
  irmaa: "irmaa_safe",
  // Un-prefixed values: the params memo strips "fill_" for the engines'
  // getBracketCeiling lookups, so both spellings need to map here —
  // otherwise a stored fill_12/24/32/35 silently falls back to fill_22 in
  // the Roth tab.
  "10": "fill_10", "12": "fill_12", "22": "fill_22", "24": "fill_24",
  "32": "fill_32", "35": "fill_35", "37": "fill_37",
  fill_10: "fill_10", fill_12: "fill_12", fill_22: "fill_22", fill_24: "fill_24",
  fill_32: "fill_32", fill_35: "fill_35", fill_37: "fill_37",
};
const ROTHMODE_TO_PROFILE = {
  no_convert: "off",
  irmaa_safe: "irmaa",
  fill_10: "fill_10", fill_12: "fill_12", fill_22: "fill_22", fill_24: "fill_24",
  fill_32: "fill_32", fill_35: "fill_35", fill_37: "fill_37",
};

function RothLadder({ params, onSaveConversionOverride, onRemoveConversionOverride, onAssumptionChange }) {

  const [showInputs, setShowInputs] = useState(false);
  const [view, setView] = useState("optimized");
  // Bracket-fill strategy is a single value, persisted to the profile as
  // params.rothConversionTarget — the Conversion Plan tab is where it gets
  // tuned, but the Withdrawal Plan tab / Monte Carlo runs read the same
  // stored value, no separate, disconnected "rothMode" local setting.
  const rothMode = PROFILE_TO_ROTHMODE[params?.rothConversionTarget] ?? "fill_22";
  const setRothMode = (mode) => {
    if (onAssumptionChange) onAssumptionChange("rothConversionTarget", ROTHMODE_TO_PROFILE[mode] ?? "fill_22");
  };

  // Current-Year Calculator state
  const currentCalYear = new Date().getFullYear() + 1; // plan for next year by default
  const [cyYear,   setCyYear]   = useState(currentCalYear);
  const [cyW2,     setCyW2]     = useState(0);
  const [cySS,     setCySS]     = useState(0);
  const [cyRental, setCyRental] = useState(0);
  const [cyOther,  setCyOther]  = useState(0);
  // Cash available for taxes defaults from the profile's Bucket 1 (Cash)
  // allocation — the same "pay bills now" reserve shown on the Withdrawal
  // Plan / Bucket Strategy tab, including any per-account splits the user
  // has assigned to Bucket 1. A one-off override lets the user refine it
  // for this analysis without creating a second, disconnected balance.
  const profileCashForTaxes = expandAccountBuckets(params?.accounts || [])
    .filter(a => a.bucket === 1)
    .reduce((s, a) => s + (a.balance || 0), 0);
  const [cySGOVOverride, setCySGOVOverride] = useState(null);
  const cySGOV = cySGOVOverride ?? profileCashForTaxes;

  // Depend on `params` itself, never an enumerated subset of its fields.
  // These four memos each used to carry their own hand-maintained field
  // list, and each list was missing something different: `ex` omitted sp/
  // endAge/gkFloor/gkCeiling/stateOfResidence/irmaaGuard while `convRows`
  // (the table rendered directly below ex's summary cards) tracked them —
  // so editing spend in the sidebar refreshed the ladder table but left the
  // Lifetime Tax Delta/RMD Reduction/Eff. Rate cards above it showing the
  // previous profile. `params` is itself a useMemo in the parent, so it's
  // referentially stable between real edits; depending on it is both
  // correct and cheap, and it can never go stale as new profile fields get added.
  const ex = useMemo(
    () => buildWaterfallComparison(params ?? {}, rothMode),
    [params, rothMode]
  );

  // No-tax state scenario: same profile but state tax zeroed out (twoHousehold flag)
  const exNoTax = useMemo(
    () => buildWaterfallComparison({ ...(params ?? {}), twoHousehold: true }, rothMode),
    [params, rothMode]
  );

  const {
    opt,
    cur,
    taxD,
    estD,
    leOpt,
    leCur,
    rmdRed,
    isNoTaxState,
    retireYear,
    rmdAge,
    endAge,
    filingStatus,
  } = ex;

  // Conversion Plan ladder — built directly from buildWithdrawalWaterfall so the
  // "Conversion" column always equals the Withdrawal Schedule tab's "Roth Conv"
  // figure for the same year (see rothConversionPlan.js::buildConversionLadder).
  const convRows = useMemo(
    () => buildConversionLadder(params ?? {}, rothMode).rows,
    [params, rothMode]
  );

  // Reconciled conversion plan — single source of truth, matches the
  // Withdrawal Schedule tab's "Roth Conv" figures (see rothConversionPlan.js).
  const conversionPlan = useMemo(
    () => buildConversionPlan(params ?? {}),
    [params]
  );

const state = params.stateOfResidence || "NJ";   // fallback to your actual state
const domLabel = isNoTaxState
  ? "No Tax State Move or Out of Country"
  : `${state} Domicile (with tax)`;
  const domColor = isNoTaxState ? "var(--positive)" : "var(--chart-3)";
  
  const modeLabels = {
      no_convert: "Off",
      fill_10: "Fill 10%",
      fill_12: "Fill 12%",
      fill_22: "Fill 22%",
      fill_24: "Fill 24%",
      fill_32: "Fill 32%",
      fill_35: "Fill 35%",
      fill_37: "Fill 37%",
      irmaa_safe: "IRMAA-Safe",
  };

const modeDescs = {
    no_convert: "No conversions — pretax stays pretax until RMDs force withdrawals.",
    fill_10: "Ultra‑conservative — stay in 10% bracket. Minimal tax, slowest conversion.",
    fill_12: "Conservative — stay in 12% bracket. Low tax, slower conversion.",
    fill_22: "Moderate — fill to top of 22%. IRMAA‑safe. AiRA default.",
    fill_24: "Aggressive — fill to 24%. IRMAA risk at 65 (2‑yr lookback).",
    fill_32: "Very aggressive — fill to 32%. IRMAA and NIIT implications.",
    fill_35: "High income — fill to 35%. Only for large conversions.",
    fill_37: "Maximum — fill to 37%. Rarely optimal; consult CPA.",
    irmaa_safe: "Dynamic — fills 22% normally, auto‑throttles near IRMAA threshold.",
};

  const InputsPanel = () => (
    <div
      style={{
        background: "var(--tint-white-02)",
        border: "1px solid var(--tint-white-08)",
        borderRadius: 8,
        padding: showInputs ? "12px" : "0",
        maxHeight: showInputs ? "600px" : "0",
        overflow: "hidden",
        transition: "all 0.3s ease",
        marginBottom: showInputs ? 10 : 0,
      }}
    >
      {showInputs && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "6px 20px",
            fontSize: "var(--fs-small)",
          }}
        >
          {[
            ["Domicile", domLabel, domColor],
            ["Filing Status", filingStatus === "mfj" ? "MFJ" : "Single", "var(--text-secondary)"],
            ["Bracket Target", modeLabels[rothMode], "var(--accent-teal)"],
            [
              // Used to be two hardcoded literals ($32,200 / $16,100) — a
              // second copy of a TAX_REFERENCE constant that would silently
              // disagree with the engine the moment one changed. Reads the
              // engine's own helper at inflFactor 1 (today's dollars) now,
              // and states the age basis, which the old label never did.
              "Std Deduction (under 65)",
              fmtDollar(getStandardDeduction(64, filingStatus, 1)) +
                " (indexed " + params.inf + "%/yr; higher from 65)",
              "var(--text-secondary)",
            ],
            [
              // Used to be labeled "Other Income" while showing rental, with
              // a hardcoded $20,000 fallback and a hardcoded "3% growth"
              // that ignored params.abGrowth. Three bugs in five lines:
              // wrong name, invented value, an asserted rate the plan might
              // not even use.
              "Annuity/Rental",
              (params.ab > 0
                ? fmtDollar(params.ab) + "/yr"
                : "Not set") +
                " (" + (params.abGrowth ?? 3) + "% growth)",
              "var(--text-secondary)",
            ],
            [
              "SS Start",
              "Age " +
                (params.ssAge ?? "—") +
                " / $" +
                (params.ssb || 0).toLocaleString() +
                "/yr",
              "var(--text-secondary)",
            ],
            [
              "Portfolio",
              (() => {
                const accts = params.accounts || [];
                const total = accts.reduce((s, a) => s + (a.balance || 0), 0);
                const pretax = accts
                  .filter((a) => a.category === "pretax")
                  .reduce((s, a) => s + (a.balance || 0), 0);
                const pct = total > 0 ? Math.round((pretax / total) * 100) : 0;
                return (
                  fmtDollar(params.port || 0) +
                  (total > 0 ? ` (${pct}% pre-tax)` : "")
                );
              })(),
              "var(--text-secondary)",
            ],
            ["Growth Assumption", "7% nominal (balance projection)", "var(--text-secondary)"],
            ["IRMAA Guard", "Ages 63-65 auto-throttled to 22%", "var(--accent-gold)"],
            ["FAFSA Guard", "Through 2029 · capped at 12%", "var(--accent-gold)"],
            [
              "Conversion Window",
              "Age " + (params.retireAge || 60) + "–" + (rmdAge - 1) + " (dynamic fill)",
              "var(--accent-teal)",
            ],
            [
              "RMD Start Age",
              rmdAge + " (SECURE Act 2.0)",
              "var(--text-secondary)",
            ],
            [
              "RMD Table",
              "Joint & Last Survivor (Spouse)",
              "var(--text-secondary)",
            ],
          ].map(([k, v, c]) => (
            <div
              key={k}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "3px 0",
                borderBottom: "1px solid var(--tint-white-04)",
              }}
            >
              <span style={{ color: "var(--text-muted)" }}>{k}</span>
              <span
                style={{
                  color: c,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontWeight: 500,
                }}
              >
                {v}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  const barData = convRows.map((r) => ({
    yr: r.yr,
    age: r.age,
    conv: r.conv,
    label: r.label,
    "10%": r.conv10 || 0,
    "12%": r.conv12 || 0,
    "22%": r.conv22 || 0,
    "24%": r.conv24 || 0,
    "32%": r.conv32 || 0,
    "35%": r.conv35 || 0,
    "37%": r.conv37 || 0,
  }));
  const taxCompare = opt.rows
    .filter((_, i) => i % 2 === 0 || i < 10)
    .map((r, i) => {
      const c = cur.rows.find((cr) => cr.yr === r.yr);
      return {
        yr: r.yr,
        age: r.age,
        optRate: (r.effR * 100).toFixed(0),
        curRate: c ? (c.effR * 100).toFixed(0) : "0",
        optTax: r.totT,
        curTax: c ? c.totT : 0,
      };
    });
  const rmdYears = opt.rows
    .filter((r) => r.age >= rmdAge && r.age <= (params.endAge || 90))
    .map((r) => {
      const c = cur.rows.find((cr) => cr.yr === r.yr);
      return {
        age: r.age,
        optRmd: r.rmd,
        curRmd: c ? c.rmd : 0,
        optPT: r.pT,
        curPT: c ? c.pT : 0,
      };
    });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
        {/* Same PillTab idiom. Tax Room keeps its amber because the colour is
            meaningful here, not decorative: it marks the views grounded in
            income you actually entered, as against the optimizer's projections. */}
        <PillTab
          ariaLabel="Roth conversion view"
          size="sm"
          options={[
            ["thisyear", "Tax Room"],
            ["optimized", "Conversion Plan"],
            ["taxes", "Taxes"],
            ["table", "Year-by-Year"],
            ["scenarios", "3-Scenario"],
          ]}
          value={view}
          onChange={setView}
          accentFor={(k) => (k === "thisyear" ? "var(--accent-gold)" : INFO_ACCENT.method)}
        />
        </div>
        <button
          onClick={() => setShowInputs(!showInputs)}
          style={{
            padding: "3px 8px",
            borderRadius: 5,
            border: "1px solid var(--tint-white-10)",
            cursor: "pointer",
            fontSize: "var(--fs-small)",
            fontFamily: "inherit",
            background: "transparent",
            color: "var(--text-muted)",
          }}
        >
          {showInputs ? "▲ Hide" : "▼ Show"} Assumptions
        </button>
      </div>
      <InputsPanel />
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            fontSize: "var(--fs-small)",
            color: "var(--text-faint)",
            letterSpacing: "0.08em",
          }}
        >
          Bracket Fill Strategy
        </div>
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>

          {/* One idiom now (see PillTab). The risk grading is preserved as the
              ACCENT rather than a coloured border-box: green = safe bracket,
              amber = caution, red = 32%+ / IRMAA exposure. Flattening these to a
              single colour would delete a warning that stops someone converting
              into a bracket cliff, so the hue stays meaningful and only the
              chrome is shared. */}
          <PillTab
            ariaLabel="Bracket fill strategy"
            size="sm"
            options={Object.entries(modeLabels)}
            value={rothMode}
            onChange={setRothMode}
            accentFor={(k) => {
              if (["fill_32", "fill_35", "fill_37"].includes(k)) return "var(--negative)";
              if (k === "fill_24") return "var(--accent-gold)";
              if (["no_convert", "fill_10", "fill_12"].includes(k)) return "var(--positive)";
              return INFO_ACCENT.method;
            }}
          />
        </div>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontStyle: "italic" }}>
          {modeDescs[rothMode]}
        </div>
      </div>
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--fs-small)" }}
      >
        <span
          style={{ width: 8, height: 8, borderRadius: 4, background: domColor }}
        />
        <span style={{ color: domColor, fontWeight: 600 }}>
          Domicile: {domLabel}
        </span>
        <span style={{ color: "var(--text-faint)" }}>
          · {isNoTaxState
            ? "Solo mode (lower spend, no state tax)"
            : `Both in ${params.stateOfResidence || "your state"} (full spend, state tax applies)`}
        </span>
      </div>
      {(() => {
        const _cy = new Date().getFullYear();
        const _pinned   = convRows.filter(r => r.capReason?.startsWith("manual") && r.yr >= _cy).length;
        const _forecast = convRows.filter(r => !r.capReason?.startsWith("manual") && r.yr >= _cy).length;
        const _past     = convRows.filter(r => r.yr < _cy).length;
        return (
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 6, display: "flex", gap: 16, alignItems: "center" }}>
            <span>📌 <strong style={{ color: "var(--accent-gold)" }}>{_pinned} pinned</strong> — anchored to real income data</span>
            <span>🔮 <strong style={{ color: "var(--accent-teal)" }}>{_forecast} forecasted</strong> — optimizer projection</span>
            {_past > 0 && <span>📅 <strong style={{ color: "var(--text-faint)" }}>{_past} past</strong> — historical</span>}
          </div>
        );
      })()}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5,1fr)",
          gap: 8,
        }}
      >
        <div className="met">
          <div className="ml">Conversions</div>
          <div className="mv" style={{ color: "var(--accent-teal)", fontSize: "var(--fs-h1)" }}>
            {convRows.length}
          </div>
          <div className="ms">during plan window</div>
        </div>
        <div className="met" style={{ border: convRows.filter(r => r.capReason?.startsWith("manual")).length > 0 ? "1px solid rgba(244,211,94,0.3)" : undefined }}>
          <div className="ml">📌 Pinned / 🔮 Forecast</div>
          <div className="mv" style={{ fontSize: "var(--fs-body)" }}>
            <span style={{ color: "var(--accent-gold)" }}>{convRows.filter(r => r.capReason?.startsWith("manual") && r.yr >= new Date().getFullYear()).length}</span>
            <span style={{ color: "var(--text-faint)" }}> / </span>
            <span style={{ color: "var(--accent-teal)" }}>{convRows.filter(r => !r.capReason?.startsWith("manual") && r.yr >= new Date().getFullYear()).length}</span>
          </div>
          <div className="ms">real anchor vs optimizer</div>
        </div>
        <div className="met">
          <div className="ml">Lifetime Tax Delta</div>
          <div
            className="mv"
            style={{ color: taxD > 0 ? "var(--chart-3)" : "var(--positive)", fontSize: "var(--fs-h2)" }}
          >
            {taxD > 0 ? "+" : ""}
            {fmtDollar(Math.abs(taxD))}
          </div>
          <div className="ms">
            {taxD > 0 ? "more" : "less"} with conversions
          </div>
        </div>
        <div className="met">
          <div className="ml">RMD Reduction</div>
          <div className="mv" style={{ color: "var(--positive)", fontSize: "var(--fs-h1)" }}>
            {rmdRed}%
          </div>
          <div className="ms">lower forced distributions</div>
        </div>
        <div className="met">
          <div className="ml">Lifetime Eff. Rate</div>
          <div className="mv" style={{ color: "var(--accent-teal)", fontSize: "var(--fs-h2)" }}>
            {(leOpt * 100).toFixed(1)}% vs {(leCur * 100).toFixed(1)}%
          </div>
          <div className="ms">optimized vs current</div>
        </div>
      </div>
      {conversionPlan.totalTraditional > 0 && (
        <div
          style={{
            background: "var(--tint-sky-06)",
            border: "1px solid var(--tint-sky-25)",
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 8,
            fontSize: "var(--fs-small)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: "var(--fs-label)", fontWeight: 600, color: "var(--accent-teal)" }}>
              Recommended Conversion — Year 1: {fmtDollar(conversionPlan.recommendedSchedule[0]?.amount || 0)}
            </div>
            <div style={{ color: "var(--text-muted)" }}>
              Matches the "Roth Conv" figure for {conversionPlan.recommendedSchedule[0]?.year ?? "year 1"} on the Withdrawal Schedule tab
            </div>
          </div>
          {conversionPlan.needs_schedule ? (
            <>
              <div style={{ color: "var(--accent-gold)", marginTop: 6 }}>
                A multi-year conversion schedule is recommended because:
              </div>
              <ul style={{ margin: "4px 0 6px 18px", padding: 0, color: "var(--text-secondary)" }}>
                {conversionPlan.reasons.headroomTooSmall && (
                  <li>This year's conversion headroom ({fmtDollar(conversionPlan.headroomYear0)}) is less than 20% of your total Traditional balance ({fmtDollar(conversionPlan.totalTraditional)}) — converting it all at once would push you into much higher brackets.</li>
                )}
                {conversionPlan.reasons.cannotPayTaxFromCash && (
                  <li>The tax owed on this year's conversion can't be covered by cash/taxable savings alone.</li>
                )}
                {conversionPlan.reasons.rmdBracketIncrease && (
                  <li>Without conversions, projected RMDs at age {conversionPlan.rmdAge} would push you into a higher bracket than today.</li>
                )}
              </ul>
              <div style={{ color: "var(--text-muted)" }}>
                Recommended schedule (through age {conversionPlan.rmdAge}, {conversionPlan.recommendedSchedule.length} year{conversionPlan.recommendedSchedule.length === 1 ? "" : "s"}):{" "}
                {conversionPlan.recommendedSchedule.slice(0, 6).map((s, i) => (
                  <span key={s.year} style={{ color: "var(--accent-teal)" }}>
                    {i > 0 && ", "}
                    {s.year} (age {s.age}): {fmtDollar(s.amount)}
                  </span>
                ))}
                {conversionPlan.recommendedSchedule.length > 6 && <span style={{ color: "var(--text-faint)" }}> …</span>}
              </div>
            </>
          ) : (
            <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
              This year's headroom covers a large share of your Traditional balance — no multi-year schedule needed.
            </div>
          )}
        </div>
      )}
      {view === "thisyear" && (() => {
        const isMFJ   = (params?.filingStatus || "mfj") !== "single";
        const infRate = (params?.inf ?? 2.5) / 100;
        const f       = Math.pow(1 + infRate, cyYear - CURRENT_YEAR);
        const fedBase = isMFJ ? FED_BRACKETS_2026_MFJ : FED_BRACKETS_2026_SINGLE;
        const fB      = idxB(fedBase, f);
        const b12t    = fB.find(b => b.rate === 0.12)?.hi ?? 0;
        const b22t    = fB.find(b => b.rate === 0.22)?.hi ?? 0;
        const b24t    = fB.find(b => b.rate === 0.24)?.hi ?? 0;
        const b32t    = fB.find(b => b.rate === 0.32)?.hi ?? 0;
        const b35t    = fB.find(b => b.rate === 0.35)?.hi ?? 0;
        const b37t    = fB.find(b => b.rate === 0.37)?.hi ?? 0;

        // SS provisional income → 0% / 50% / 85% taxable per IRC §86 tiers
        const ssTaxable = Math.round(taxableSocialSecurity(cySS, cyW2 + cyRental + cyOther, isMFJ));
        const grossInc  = cyW2 + ssTaxable + cyRental + cyOther;
        // Deduction for this panel's year. This used to be a bare
        // `(isMFJ ? 32200 : 16100) * f` with no age-65 add-on at all —
        // another copy of the standard deduction that understated the
        // deduction (and so overstated tax and understated conversion
        // headroom) for every 65+ user. Now routed through the same
        // canonical helpers as calcYearTax so all three can't drift:
        // age-aware standard deduction plus the OBBBA senior bonus, whose
        // phase-out reads this year's own MAGI.
        const cyAge     = (params?.currentAge || 0) + (cyYear - CURRENT_YEAR);
        const stdD      = getStandardDeduction(cyAge, params?.filingStatus || "mfj", f);
        const cySeniorBonus = getSeniorBonusDeduction(
          cyAge, params?.filingStatus || "mfj", grossInc, cyYear
        );
        const dedD      = stdD + cySeniorBonus;
        const taxableBC = Math.max(0, grossInc - dedD);           // taxable before conversion
        const fedTaxBC  = Math.round(progTax(taxableBC, fB));

        // State tax on non-conversion income
        const stateBr   = getStateBrackets(params?.stateOfResidence || "NJ", isMFJ);
        const stTaxBC   = stateBr ? Math.round(progTax(taxableBC, stateBr)) : 0;

        // Bracket headroom
        const room12 = Math.max(0, b12t - taxableBC);
        const room22 = Math.max(0, b22t - taxableBC);
        const room24 = Math.max(0, b24t - taxableBC);
        const room32 = Math.max(0, b32t - taxableBC);
        const room35 = Math.max(0, b35t - taxableBC);
        const room37 = Math.max(0, b37t - taxableBC);

        // Tax cost for converting the full room at each bracket
        function convTax(convAmt) {
          const fedInc = Math.round(progTax(taxableBC + convAmt, fB)) - fedTaxBC;
          const stInc  = stateBr
            ? Math.round(progTax(taxableBC + convAmt, stateBr)) - stTaxBC
            : 0;
          return { fedInc, stInc, total: fedInc + stInc };
        }
        const tax12 = convTax(room12);
        const tax22 = convTax(room22);
        const tax24 = convTax(room24);
        const tax32 = convTax(room32);
        const tax35 = convTax(room35);
        const tax37 = convTax(room37);

        // Map rothMode to the bracket row that should be highlighted and recommended
        const modeHighlight = {
          fill_10: "12%", fill_12: "12%",
          fill_22: "22%", irmaa_safe: "22%",
          fill_24: "24%", fill_32: "32%", fill_35: "35%", fill_37: "37%",
        };
        const targetLabel = modeHighlight[rothMode] ?? "22%";
        const recRoom =
          targetLabel === "12%" ? room12 :
          targetLabel === "24%" ? room24 :
          targetLabel === "32%" ? room32 :
          targetLabel === "35%" ? room35 :
          targetLabel === "37%" ? room37 :
          room22;
        const recRoomTax =
          targetLabel === "12%" ? tax12 :
          targetLabel === "24%" ? tax24 :
          targetLabel === "32%" ? tax32 :
          targetLabel === "35%" ? tax35 :
          targetLabel === "37%" ? tax37 :
          tax22;

        // Recommended: fill to selected bracket, capped by available SGOV cash
        let recConv, recTax, recNote;
        if (recRoom === 0) {
          recConv = 0; recTax = convTax(0); recNote = `Already above ${targetLabel} bracket — no room`;
        } else if (cySGOV <= 0) {
          recConv = recRoom; recTax = recRoomTax; recNote = "Enter Cash/Treasury/Short Term cash balance to check cash constraint";
        } else if (recRoomTax.total <= cySGOV) {
          recConv = recRoom; recTax = recRoomTax; recNote = `Cash/Treasury/Short Term cash covers full ${targetLabel} fill`;
        } else {
          // Scale down proportionally — linear approx within bracket
          const ratio = cySGOV / recRoomTax.total;
          recConv = Math.round(recRoom * ratio);
          recTax  = convTax(recConv);
          recNote = "Cash/Treasury/Short Term cash limits conversion — increase cash to fill full bracket";
        }

        const inputStyle = {
          width: "100%", background: "#0d1b2a", border: "1px solid #1e3a5f",
          color: "var(--text-primary)", borderRadius: 6, padding: "5px 8px", fontSize: "var(--fs-label)",
          fontFamily: "'JetBrains Mono',monospace", textAlign: "right",
        };
        const rowSep = { borderBottom: "1px solid var(--tint-white-04)", padding: "5px 0" };
        const fmtN = (n) => n === 0 ? "$0" : `$${Math.round(n).toLocaleString()}`;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* ── Inputs ── */}
            <div className="chart-card">
              <div className="ct">Current Year Conversion Calculator</div>
                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontStyle: "italic", marginBottom: 10 }}>
                    Enter your expected income for {cyYear}, then see how much bracket room remains 
                    for a Roth conversion. Give the recommended number to your CPA by December.
                  </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: "var(--fs-label)" }}>
                <div>
                  <div style={{ color: "var(--text-secondary)", marginBottom: 6, fontSize: "var(--fs-small)" }}>Tax Year & Income</div>
                  {[
                    ["Tax Year",          cyYear,   setCyYear,   false, 2026, 2060],
                    ["W-2 / SE Income",   cyW2,     setCyW2,     true],
                    ["Social Security",   cySS,     setCySS,     true],
                    ["Rental / Airbnb",   cyRental, setCyRental, true],
                    ["Other Income",      cyOther,  setCyOther,  true],
                  ].map(([lbl, val, setter, isDollar, mn, mx]) => (
                    <div key={lbl} style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8, alignItems: "center", ...rowSep }}>
                      <span style={{ color: "var(--text-secondary)" }}>{lbl}</span>
                      <input
                        type="number" value={val}
                        onChange={e => setter(Number(e.target.value) || 0)}
                        onFocus={e => e.target.select()}
                        min={mn ?? 0} max={mx ?? 9999999} step={isDollar ? 1000 : 1}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ color: "var(--text-secondary)", marginBottom: 6, fontSize: "var(--fs-small)" }}>Cash Available for Taxes</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8, alignItems: "center", ...rowSep }}>
                    <span style={{ color: "var(--accent-gold)" }}>Cash/Treasury/Short Term cash for Taxes</span>
                    <input
                      type="number" value={cySGOV}
                      onChange={e => setCySGOVOverride(Number(e.target.value) || 0)}
                      min={0} step={1000} style={{ ...inputStyle, borderColor: "var(--accent-gold)" }}
                onFocus={selectAllOnFocus}
              />
                  </div>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 6 }}>
                    {cySGOVOverride != null
                      ? <>Overridden for this analysis · Bucket 1 (Cash) balance is {fmtN(profileCashForTaxes)}{" "}
                          <button
                            onClick={() => setCySGOVOverride(null)}
                            style={{ background: "none", border: "none", color: "var(--accent-teal)", cursor: "pointer", fontSize: "var(--fs-small)", textDecoration: "underline", padding: 0, fontFamily: "inherit" }}
                          >↺ reset to profile</button>
                        </>
                      : <>From Bucket 1 — Cash ({fmtN(profileCashForTaxes)}) — edit to refine for this analysis only.</>}
                  </div>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 6 }}>
                    Filing: {isMFJ ? "Married Filing Jointly" : "Single"} ·{" "}
                    State: {params?.stateOfResidence || "NJ"}{stateBr ? "" : " (no state tax)"}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Income Breakdown ── */}
            <div className="chart-card">
              <div className="ct">Income Breakdown — Tax Year {cyYear}</div>
              <table className="roth-tbl">
                <tbody>
                  {[
                    ["W-2 / SE income",       fmtN(cyW2),       "var(--text-primary)"],
                    ["Social Security (taxable " + (cySS > 0 ? Math.round((ssTaxable / cySS) * 100) : 0) + "%)", fmtN(ssTaxable), "var(--text-primary)"],
                    ["Rental / Airbnb net",   fmtN(cyRental),   "var(--text-primary)"],
                    ["Other income",          fmtN(cyOther),    "var(--text-primary)"],
                    ["Gross income",          fmtN(grossInc),   "var(--accent-teal)"],
                    ["Standard deduction",    `(${fmtN(stdD)})`, "var(--chart-1)"],
                    ["Taxable income before conversion", fmtN(taxableBC), "var(--accent-gold)"],
                  ].map(([lbl, val, col]) => (
                    <tr key={lbl}>
                      <td style={{ color: "var(--text-secondary)" }}>{lbl}</td>
                      <td style={{ color: col, textAlign: "right", fontFamily: "'JetBrains Mono',monospace" }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Bracket Headroom ── */}
            <div className="chart-card">
              <div className="ct">Bracket Headroom — {cyYear} (inflation-indexed)</div>
              <table className="roth-tbl">
                <thead>
                  <tr>
                    <th>Bracket</th>
                    <th>Top of Bracket</th>
                    <th>Headroom</th>
                    <th>Fed Tax Cost</th>
                    <th>State Tax Cost</th>
                    <th>Total Tax Cost</th>
                    <th>Eff Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["12%", b12t, room12, tax12],
                    ["22%", b22t, room22, tax22],
                    ["24%", b24t, room24, tax24],
                    ...( ["32%","35%","37%"].includes(targetLabel) ? [["32%", b32t, room32, tax32]] : []),
                    ...( ["35%","37%"].includes(targetLabel) ? [["35%", b35t, room35, tax35]] : []),
                    ...( ["37%"].includes(targetLabel) ? [["37%", b37t, room37, tax37]] : []),
                  ].map(([bracket, top, room, tax]) => (
                    <tr key={bracket} style={{ background: bracket === targetLabel ? "var(--tint-teal-600-08)" : undefined }}>
                      <td style={{ color: bracket === targetLabel ? "var(--accent-teal)" : "var(--text-secondary)", fontWeight: bracket === targetLabel ? 700 : 400 }}>{bracket}</td>
                      <td style={{ fontFamily: "'JetBrains Mono',monospace" }}>{fmtN(top)}</td>
                      <td style={{ color: room > 0 ? "var(--text-primary)" : "var(--text-faint)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtN(room)}</td>
                      <td style={{ color: "var(--chart-1)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtN(tax.fedInc)}</td>
                      <td style={{ color: stateBr ? "var(--chart-3)" : "var(--positive)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtN(tax.stInc)}</td>
                      <td style={{ color: "var(--chart-1)", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{fmtN(tax.total)}</td>
                      <td style={{ color: "var(--text-secondary)", fontFamily: "'JetBrains Mono',monospace" }}>
                        {room > 0 ? ((tax.total / room) * 100).toFixed(1) + "%" : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Recommendation ── */}
            <div className="chart-card" style={{ border: "1px solid rgba(244,211,94,0.4)", background: "rgba(244,211,94,0.05)" }}>
              <div className="ct" style={{ color: "var(--accent-gold)" }}>Recommended Conversion — {cyYear}</div>
              {(() => {
                const cyTaxFunding = params?.taxFunding || "from_taxable";
                const cyNetRoth = cyTaxFunding === "from_conv"
                  ? Math.max(0, recConv - recTax.total)
                  : recConv;
                const cyFundLabel = cyTaxFunding === "from_conv"
                  ? "From conversion (taxes deducted)"
                  : cyTaxFunding === "outside_cash"
                  ? "Outside cash"
                  : "From taxable / HSA bucket";
                const cyCpaTax = cyTaxFunding === "from_conv"
                  ? `Tax cost is ${fmtN(recTax.total)} — deducted from conversion; ${fmtN(cyNetRoth)} net reaches Roth.`
                  : `Tax cost is ${fmtN(recTax.total)} — pay from ${cyTaxFunding === "outside_cash" ? "outside cash (e.g. SGOV/HYSA)" : "your taxable / HSA bucket"}.`;
                return (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                      <div className="met" style={{ border: "1px solid rgba(244,211,94,0.3)" }}>
                        <div className="ml">Convert This Amount</div>
                        <div className="mv" style={{ color: "var(--accent-gold)", fontSize: "var(--fs-h1)" }}>{fmtN(recConv)}</div>
                        <div className="ms">pretax → Roth</div>
                      </div>
                      <div className="met" style={{ border: "1px solid var(--tint-red-30)" }}>
                        <div className="ml">Total Tax Cost</div>
                        <div className="mv" style={{ color: "var(--chart-1)", fontSize: "var(--fs-h1)" }}>{fmtN(recTax.total)}</div>
                        <div className="ms">fed {fmtN(recTax.fedInc)} + state {fmtN(recTax.stInc)}</div>
                      </div>
                      <div className="met" style={{ border: "1px solid var(--tint-sky-30)" }}>
                        <div className="ml">Net → Roth</div>
                        <div className="mv" style={{ color: "var(--accent-teal)", fontSize: "var(--fs-h1)" }}>{fmtN(cyNetRoth)}</div>
                        <div className="ms">{cyFundLabel}</div>
                      </div>
                    </div>
                    <div style={{ background: "var(--bg-warning)", border: "1px solid rgba(244,211,94,0.25)", borderRadius: 8, padding: "10px 14px", fontSize: "var(--fs-label)", color: "var(--accent-gold)", fontWeight: 600 }}>
                      Give this to your CPA: Convert {fmtN(recConv)} from pretax to Roth in {cyYear}. {cyCpaTax}
                    </div>
                  </>
                );
              })()}
              {recNote && (
                <div style={{ marginTop: 8, fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>{recNote}</div>
              )}
              {(() => {
                const cyPin = (params?.conversionOverrides || []).find(o => Number(o.year) === cyYear);
                return (
                  <>
                    {cyPin && (
                      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, background: "rgba(167,139,250,0.08)", border: "1px solid var(--tint-violet-400-30)", borderRadius: 8, padding: "8px 12px" }}>
                        <span style={{ fontSize: "var(--fs-label)", color: "var(--accent-purple)", fontWeight: 600, flex: 1 }}>
                          📌 Pinned for {cyYear}: {fmtN(cyPin.amount)}
                        </span>
                        {onRemoveConversionOverride && (
                          <button
                            onClick={() => onRemoveConversionOverride(cyYear)}
                            style={{ background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, cursor: "pointer", fontSize: "var(--fs-label)", padding: "3px 10px", fontFamily: "inherit" }}
                          >× Remove pin</button>
                        )}
                      </div>
                    )}
                    {recConv > 0 && onSaveConversionOverride && (
                      <button
                        onClick={() => {
                          onSaveConversionOverride(cyYear, recConv, { w2: cyW2, ss: cySS, rental: cyRental, other: cyOther, sgov: cySGOV });
                          alert(`Saved: Convert ${fmtN(recConv)} in ${cyYear} to your Lifetime Projection ladder.`);
                        }}
                        style={{
                          marginTop: 8, width: "100%", padding: "10px 0",
                          background: "rgba(244,211,94,0.2)", border: "1px solid var(--accent-gold)",
                          color: "var(--accent-gold)", borderRadius: 8, cursor: "pointer",
                          fontSize: "var(--fs-body)", fontWeight: 700, fontFamily: "inherit",
                        }}
                      >
                        {cyPin ? "↺ Update Lifetime Ladder pin" : "→ Save to Lifetime Ladder"}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* ── How this works ── */}
            <div style={{ background: "var(--tint-white-02)", border: "1px solid var(--tint-white-06)", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 8 }}>
                ℹ️ How Tax Room and the Lifetime Ladder work together
              </div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.7 }}>
                <strong style={{ color: "var(--text-secondary)" }}>Tax Room (this tab)</strong> is grounded in reality — you enter your actual known income
                for the year and get the exact conversion amount to hand your CPA. It reflects what you
                can actually do right now, not a forecast.
                <br /><br />
                <strong style={{ color: "var(--text-secondary)" }}>Lifetime Ladder (Conversion Plan tab)</strong> projects conversions over every future year.
                For years where you have not saved a real number, it uses the bracket-fill optimizer —
                which does not know about your working income, Cash or Taxable/Brokerage balance, or IRMAA timing.
                <br /><br />
                <strong style={{ color: "var(--accent-gold)" }}>Press "Save to Lifetime Ladder" above</strong> to anchor the current year to your real number.
                The Ladder will then use the optimizer only for future years it does not have a pin for.
                Do this every December to keep the projection grounded year by year — the same way
                Monte Carlo checkpoints anchor the portfolio balance to reality.
              </div>
            </div>
          </div>
        );
      })()}

      {view === "optimized" && (
        <>
          <div className="chart-card">
            <div className="ct">
              Conversion Plan · Ages {convRows[0]?.age}–
              {convRows[convRows.length - 1]?.age} · {domLabel}
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={barData}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="var(--row-highlight)"
                />
                <XAxis
                  dataKey="yr"
                  stroke="#1e3a5f"
                  tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
                />
                <YAxis
                  stroke="#1e3a5f"
                  tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
                  tickFormatter={(v) => fmtDollar(v)}
                  width={MONEY_AXIS_WIDTH}
                />
                <Tooltip content={<Tip />} />
                <Legend wrapperStyle={{ fontSize: "var(--fs-small)" }} />
                <Bar dataKey="10%" stackId="br" fill="#1d4ed8" name="10%" />
                <Bar dataKey="12%" stackId="br" fill="#0ea5e9" name="12%" />
                <Bar dataKey="22%" stackId="br" fill="var(--accent-teal)" name="22%" />
                <Bar dataKey="24%" stackId="br" fill="var(--accent-gold)" name="24%" radius={[4, 4, 0, 0]} />
                <Bar dataKey="32%" stackId="br" fill="var(--chart-7)" name="32%" radius={[4, 4, 0, 0]} />
                <Bar dataKey="35%" stackId="br" fill="var(--negative)" name="35%" radius={[4, 4, 0, 0]} />
                <Bar dataKey="37%" stackId="br" fill="#991b1b" name="37%" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", margin: "8px 0 4px", display: "flex", gap: 14, flexWrap: "wrap" }}>
              <span>📌 <span style={{ color: "var(--accent-gold)" }}>Amber</span> = pinned from Tax Room or manual entry</span>
              <span>🔮 <span style={{ color: "var(--accent-teal)" }}>Default</span> = optimizer projection</span>
              <span>📅 <span style={{ color: "var(--text-faint)" }}>Gray</span> = historical (already past)</span>
              <span style={{ marginLeft: "auto", color: "var(--text-muted)" }}>
                Net→Roth basis:{" "}
                {params?.taxFunding === "from_conv"
                  ? <span style={{ color: "var(--chart-1)" }}>From conversion (Conv − total tax)</span>
                  : params?.taxFunding === "outside_cash"
                  ? <span style={{ color: "var(--positive)" }}>Outside cash (full conversion)</span>
                  : <span style={{ color: "var(--accent-teal)" }}>From taxable bucket (full conversion)</span>}
              </span>
            </div>
            <table className="roth-tbl" style={{ marginTop: 4 }}>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Age</th>
                  <th>Label</th>
                  <th>Source</th>
                  <ThInfo tip={"Pre-tax IRA/401k balance at the start of this year, before the conversion"}>Pre-Tax (before)</ThInfo>
                  <th>Conversion</th>
                  <th>Fed Tax</th>
                  <th>State Tax</th>
                  <th>Bracket</th>
                  <ThInfo tip={"True marginal rate: Δ(fed+state+IRMAA) / conversion. Compare to BETR to decide convert vs defer."}>True Marg</ThInfo>
                  <th>Eff Rate</th>
                  <th>Net→Roth</th>
                  <ThInfo tip={"Cumulative Roth balance at end of this year (includes growth and withdrawals)"}>Roth Bal</ThInfo>
                </tr>
              </thead>
              <tbody>
                {convRows.map((r) => {
                  const isPast   = r.yr < new Date().getFullYear();
                  const isPinned = !isPast && r.capReason?.startsWith("manual");
                  const rowBg = isPast
                    ? "var(--tint-white-02)"
                    : isPinned
                    ? "rgba(244,211,94,0.07)"
                    : undefined;
                  const sourceLabel = isPast
                    ? <span style={{ color: "var(--text-faint)", fontSize: "var(--fs-small)" }}>📅 Past</span>
                    : isPinned
                    ? <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ color: "var(--accent-gold)", fontWeight: 700, fontSize: "var(--fs-small)" }}>📌 Pinned</span>
                        <button
                          onClick={() => {
                            setCyYear(r.yr);
                            const pin = (params?.conversionOverrides || []).find(o => Number(o.year) === r.yr);
                            if (pin) {
                              if (pin.w2     !== undefined) setCyW2(pin.w2);
                              if (pin.ss     !== undefined) setCySS(pin.ss);
                              if (pin.rental !== undefined) setCyRental(pin.rental);
                              if (pin.other  !== undefined) setCyOther(pin.other);
                              if (pin.sgov   !== undefined) setCySGOV(pin.sgov);
                            }
                            setView("thisyear");
                          }}
                          title={`Edit pin for ${r.yr} in calculator`}
                          style={{ background: "rgba(167,139,250,0.15)", border: "1px solid var(--tint-violet-400-30)", color: "var(--accent-purple)", borderRadius: 4, cursor: "pointer", fontSize: "var(--fs-small)", padding: "1px 5px", fontFamily: "inherit", lineHeight: 1.4 }}
                        >✏</button>
                        {onRemoveConversionOverride && (
                          <button
                            onClick={() => onRemoveConversionOverride(r.yr)}
                            style={{ background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 4, cursor: "pointer", fontSize: "var(--fs-small)", padding: "1px 5px", fontFamily: "inherit", lineHeight: 1.4 }}
                          >×</button>
                        )}
                      </span>
                    : <span style={{ color: "var(--text-faint)", fontSize: "var(--fs-small)" }}>🔮 Forecast</span>;
                  return (
                  <tr
                    key={r.yr}
                    className={r.label === "Golden Year ★" ? "gold" : ""}
                    style={{ background: rowBg }}
                  >
                    <td style={{ color: isPast ? "#334155" : undefined }}>{r.yr}</td>
                    <td style={{ color: isPast ? "#334155" : "var(--text-secondary)" }}>{r.age}</td>
                    <td
                      style={{
                        color: isPast ? "#334155" : r.label.includes("Golden") ? "var(--accent-gold)" : "var(--text-secondary)",
                        fontWeight: r.label.includes("Golden") ? 700 : 400,
                      }}
                    >
                      {r.label}
                    </td>
                    <td>{sourceLabel}</td>
                    <td style={{ color: isPast ? "#334155" : "var(--text-muted)" }}>{fmtDollar(r.pTStart || 0)}</td>
                    <td style={{ color: isPast ? "#334155" : isPinned ? "var(--accent-gold)" : "var(--text-primary)", fontWeight: isPinned ? 700 : 400 }}>{fmtDollar(r.conv)}</td>
                    <td style={{ color: isPast ? "#334155" : "var(--chart-1)" }}>{fmtDollar(r.fedT)}</td>
                    <td style={{ color: isPast ? "#334155" : isNoTaxState ? "var(--positive)" : "var(--chart-3)" }}>
                      {isNoTaxState ? "$0" : fmtDollar(r.stT)}
                    </td>
                    <td
                      style={{
                        color: isPast ? "#334155"
                          : r.bracketUsed === "24%" ? "var(--accent-gold)"
                          : r.bracketUsed === "22%" ? "var(--accent-teal)"
                          : "var(--text-secondary)",
                        fontSize: "var(--fs-small)",
                      }}
                    >
                      {r.bracketUsed}
                    </td>
                    <td
                      style={{
                        color: r.margR >= 0.32
                          ? "var(--negative)"
                          : r.margR >= 0.24
                          ? "var(--accent-gold)"
                          : r.margR >= 0.22
                          ? "var(--accent-teal)"
                          : "var(--positive)",
                        fontWeight: 600,
                      }}
                      title="Δ(fed+state+IRMAA) / conversion"
                    >
                      {((r.margR || 0) * 100).toFixed(1)}%
                    </td>
                    <td style={{ color: "var(--text-secondary)" }}>
                      {(r.effR * 100).toFixed(1)}%
                    </td>
                    <td style={{ color: isPast ? "#334155" : "var(--accent-teal)", fontWeight: 600 }}>
                      {fmtDollar(
                        params?.taxFunding === "from_conv"
                          ? Math.max(0, r.conv - r.fedT - (isNoTaxState ? 0 : r.stT))
                          : r.conv
                      )}
                    </td>
                    <td style={{ color: isPast ? "#334155" : "var(--accent-teal)" }} title={`Start of year: ${fmtDollar(r.roStart || 0)} → end of year: ${fmtDollar(r.ro || 0)}`}>
                      {fmtDollar(r.ro || 0)}
                    </td>
                  </tr>
                  );
                })}
                <tr style={{ borderTop: "1px solid var(--tint-white-12)" }}>
                  <td style={{ fontWeight: 700 }} colSpan={4}>
                    Total
                  </td>
                  <td>—</td>
                  <td style={{ fontWeight: 700 }}>{fmtDollar(convRows.reduce((s, r) => s + r.conv, 0))}</td>
                  <td style={{ color: "var(--chart-1)", fontWeight: 700 }}>
                    {fmtDollar(convRows.reduce((s, r) => s + r.fedT, 0))}
                  </td>
                  <td
                    style={{
                      color: isNoTaxState ? "var(--positive)" : "var(--chart-3)",
                      fontWeight: 700,
                    }}
                  >
                    {isNoTaxState
                      ? "$0"
                      : fmtDollar(convRows.reduce((s, r) => s + r.stT, 0))}
                  </td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td style={{ color: "var(--accent-teal)", fontWeight: 700 }}>
                    {fmtDollar(
                      convRows.reduce(
                        (s, r) => s + (params?.taxFunding === "from_conv"
                          ? Math.max(0, r.conv - r.fedT - (isNoTaxState ? 0 : r.stT))
                          : r.conv),
                        0
                      )
                    )}
                  </td>
                  <td style={{ color: "var(--accent-teal)", fontWeight: 700 }}
                    title="Final Roth balance at end of conversion window">
                    {fmtDollar(convRows[convRows.length - 1]?.ro || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
            {(() => {
              const orphaned = (params?.conversionOverrides || []).filter(
                o => !convRows.some(r => r.yr === Number(o.year))
              );
              if (!orphaned.length) return null;
              return (
                <div style={{ marginTop: 14, padding: "10px 14px", background: "var(--tint-red-07)", border: "1px solid var(--tint-red-25)", borderRadius: 8 }}>
                  <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--chart-1)", marginBottom: 6 }}>
                    Stale Pins — producing $0 conversion
                  </div>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 10, lineHeight: 1.6 }}>
                    The pins below exist in your plan but result in <strong style={{ color: "var(--text-primary)" }}>$0 converted</strong> — the pre-tax balance was likely exhausted before that year, or your income already exceeds the bracket ceiling. They have no effect and can be safely removed.
                  </div>
                  {orphaned.map(o => (
                    <div key={o.year} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono',monospace", flex: 1 }}>
                        📌 {o.year} — pinned at {fmtDollar(Number(o.amount))} → effective: $0
                      </span>
                      {onRemoveConversionOverride && (
                        <button
                          onClick={() => onRemoveConversionOverride(o.year)}
                          style={{ background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, cursor: "pointer", fontSize: "var(--fs-small)", padding: "2px 10px", fontFamily: "inherit" }}
                        >× Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <div className="chart-card">
            <div className="ct">
              Projected Account Balances · Pre-Tax vs Roth
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={opt.rows.filter((_, i) => i % 2 === 0)}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="var(--row-highlight)"
                />
                <XAxis
                  dataKey="age"
                  stroke="#1e3a5f"
                  tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
                />
                <YAxis
                  stroke="#1e3a5f"
                  tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
                  tickFormatter={(v) => fmtDollar(v)}
                  width={MONEY_AXIS_WIDTH}
                />
                <Tooltip content={<Tip />} />
                <Bar dataKey="pT" name="Pre-Tax" stackId="a" fill="#1e3a5f" />
                <Bar
                  dataKey="ro"
                  name="Roth"
                  stackId="a"
                  fill="var(--positive)"
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
          {/* Traced this, and the value is correct: `nw` here is `r.totalPort`
              (rothConversionPlan.js ~225), i.e. all four buckets. But the
              chart directly above only stacks Pre-Tax and Roth, so these
              cards are legitimately larger than the bars a reader just
              looked at, with nothing saying why. Disclosing the composition
              instead of changing the number — an aggregate should say what
              it contains. */}
          <div className="met">
              <div className="ml">Savings at Age {params.endAge || 90} — Without</div>
              <div className="mv" style={{ color: "var(--text-secondary)", fontSize: "var(--fs-h2)" }}>
                {fmtDollar(cur.rows[cur.rows.length - 1]?.nw || 0)}
              </div>
              <div className="ms">All buckets: cash + taxable + pre-tax + Roth</div>
            </div>
            <div className="met">
              <div className="ml">Savings at Age {params.endAge || 90} — With Conversions</div>
              <div className="mv" style={{ color: "var(--accent-teal)", fontSize: "var(--fs-h2)" }}>
                {fmtDollar(opt.rows[opt.rows.length - 1]?.nw || 0)}
              </div>
              <div className="ms">All buckets — more than the two bars above</div>
            </div>
          </div>
          {rmdYears.length > 0 && (
            <div className="chart-card">
              <div className="ct">Required minimum distributions (RMDs)</div>
              <div className="ct-sub">IRS Joint &amp; Last Survivor table</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <div className="met">
                  <div className="ml">Lifetime RMDs — Without Conversions</div>
                  <div
                    className="mv"
                    style={{ color: "var(--chart-1)", fontSize: "var(--fs-h2)" }}
                  >
                    {fmtDollar(cur.cRmd)}
                  </div>
                  <div className="ms">{formulaFor("cmp-rmd-without")}</div>
                </div>
                <div className="met">
                  <div className="ml">Lifetime RMDs — With Conversions</div>
                  <div
                    className="mv"
                    style={{ color: "var(--positive)", fontSize: "var(--fs-h2)" }}
                  >
                    {fmtDollar(opt.cRmd)}
                  </div>
                  <div className="ms">{formulaFor("cmp-rmd-with")}</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={rmdYears.filter((_, i) => i % 2 === 0)}
                  margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke="var(--row-highlight)"
                  />
                  <XAxis
                    dataKey="age"
                    stroke="#1e3a5f"
                    tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
                  />
                  <YAxis
                    stroke="#1e3a5f"
                    tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
                    tickFormatter={(v) => fmtDollar(v)}
                    width={MONEY_AXIS_WIDTH}
                  />
                  <Tooltip content={<Tip />} />
                  <Bar
                    dataKey="curRmd"
                    name="RMD — No Convert"
                    fill="rgba(255,111,122,0.4)"
                  />
                  <Bar
                    dataKey="optRmd"
                    name="RMD — With Convert"
                    fill="rgba(79,216,143,0.5)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {(() => {
            const g = convRows.find((r) => r.label === "Golden Year ★");
            return g ? (
              <div
                style={{
                  fontSize: "var(--fs-small)",
                  color: "var(--accent)",
                  background: "var(--tint-violet-600-08)",
                  borderRadius: 7,
                  padding: "7px 11px",
                  border: "1px solid rgba(124,58,237,0.2)",
                }}
              >
                ★ Golden year {g.yr} (age {g.age}): last year before Social
                Security begins. Maximum bracket room up to the 24% ceiling.
                Once SS starts, available space compresses — prioritize larger
                conversions here.
              </div>
            ) : null;
          })()}
        </>
      )}
      {view === "taxes" && (() => {
        const optFedTotal  = opt.rows.reduce((s, r) => s + (r.fedT  || 0), 0);
        const optStTotal   = opt.rows.reduce((s, r) => s + (r.stT   || 0), 0);
        const curFedTotal  = cur.rows.reduce((s, r) => s + (r.fedT  || 0), 0);
        const curStTotal   = cur.rows.reduce((s, r) => s + (r.stT   || 0), 0);
        const optAbTotal   = opt.rows.reduce((s, r) => s + (r.abn         || 0), 0);
        const curAbTotal   = cur.rows.reduce((s, r) => s + (r.abn         || 0), 0);
        const optSSTotal   = Math.round(opt.rows.reduce((s, r) => s + (r.ss || 0), 0) * 0.85);
        const curSSTotal   = Math.round(cur.rows.reduce((s, r) => s + (r.ss || 0), 0) * 0.85);
        const optPxsTotal  = opt.rows.reduce((s, r) => s + (r.pretaxSpend  || 0), 0);
        const curPxsTotal  = cur.rows.reduce((s, r) => s + (r.pretaxSpend  || 0), 0);
        const optTotInc    = opt.rows.reduce((s, r) => s + (r.totInc       || 0), 0);
        const curTotInc    = cur.rows.reduce((s, r) => s + (r.totInc       || 0), 0);

        const taxChartData = opt.rows
          .filter(r => r.age >= (params.retireAge || 60) && r.age <= (params.endAge || 90))
          .filter((_, i) => i % 2 === 0 || i < 6)
          .map(r => {
            const c = cur.rows.find(cr => cr.yr === r.yr) || {};
            return {
              age: r.age,
              opt_fed:   r.fedT   || 0,
              opt_st:    r.stT    || 0,
              opt_irmaa: r.irmaa  || 0,
              cur_fed:   c.fedT   || 0,
              cur_st:    c.stT    || 0,
              cur_irmaa: c.irmaa  || 0,
            };
          });

        const incChartData = opt.rows
          .filter(r => r.age >= (params.retireAge || 60) && r.age <= (params.endAge || 90))
          .filter((_, i) => i % 2 === 0 || i < 6)
          .map(r => {
            const c = cur.rows.find(cr => cr.yr === r.yr) || {};
            return {
              age: r.age,
              opt_ss:   Math.round((r.ss  || 0) * 0.85),
              opt_ab:   r.abn          || 0,
              opt_rmd:  r.rmd          || 0,
              opt_pxs:  r.pretaxSpend  || 0,
              opt_conv: r.conv         || 0,
              cur_ss:   Math.round((c.ss  || 0) * 0.85),
              cur_ab:   c.abn          || 0,
              cur_rmd:  c.rmd          || 0,
              cur_pxs:  c.pretaxSpend  || 0,
            };
          });

        const tblBox  = { flex: "0 0 250px", background: "var(--card-bg)", borderRadius: 8, border: "1px solid var(--tint-white-07)", overflow: "hidden" };
        const tblHead = { padding: "7px 10px", borderBottom: "1px solid var(--tint-white-07)", fontSize: "var(--fs-small)", fontWeight: 600, color: "var(--text-secondary)", display: "flex", justifyContent: "space-between" };

        const ltRow = (color, label, optVal, curVal) => (
          <tr style={{ borderBottom: "1px solid var(--tint-white-04)" }}>
            <td style={{ padding: "5px 8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: "inline-block", flexShrink: 0 }} />
                <span style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>{label}</span>
              </div>
            </td>
            <td style={{ padding: "5px 8px", textAlign: "right", fontSize: "var(--fs-label)", color: "var(--text-primary)", fontWeight: 500 }}>{fmtDollar(optVal)}</td>
            <td style={{ padding: "5px 8px", textAlign: "right", fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>{fmtDollar(curVal)}</td>
          </tr>
        );

        return (
          <>
            <div className="chart-card">
              <div className="ct">Estimated Taxes</div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 10 }}>
                Annual tax by type — bright bars = with conversions (OPT), muted bars = without (CUR)
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={taxChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%" barGap={2}>
                      <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
                      <XAxis dataKey="age" stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} />
                      <YAxis stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} tickFormatter={v => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
                      <Tooltip content={<TaxYearTip />} />
                      <Bar dataKey="opt_fed"   stackId="opt" fill="var(--chart-8)"                name="OPT Federal" />
                      <Bar dataKey="opt_st"    stackId="opt" fill="var(--chart-3)"                name="OPT State" />
                      <Bar dataKey="opt_irmaa" stackId="opt" fill="var(--chart-1)"                name="OPT IRMAA"  radius={[2,2,0,0]} />
                      <Bar dataKey="cur_fed"   stackId="cur" fill="var(--tint-indigo-30)"   name="CUR Federal" />
                      <Bar dataKey="cur_st"    stackId="cur" fill="rgba(251,146,60,0.3)"   name="CUR State" />
                      <Bar dataKey="cur_irmaa" stackId="cur" fill="var(--tint-red-30)"  name="CUR IRMAA"  radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={tblBox}>
                  <div style={tblHead}>
                    <span>Lifetime</span>
                    <div style={{ display: "flex", gap: 12 }}>
                      <span style={{ color: "var(--chart-4)" }}>With Conv</span>
                      <span style={{ color: "var(--text-faint)" }}>Without</span>
                    </div>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {ltRow("var(--chart-8)", "Federal Income Tax", optFedTotal, curFedTotal)}
                      {!isNoTaxState && ltRow("var(--chart-3)", "State Income Tax", optStTotal, curStTotal)}
                      {ltRow("var(--chart-1)", "IRMAA Surcharges", opt.cIrmaa || 0, cur.cIrmaa || 0)}
                      <tr style={{ borderTop: "2px solid var(--tint-white-10)", background: "var(--card-bg)" }}>
                        <td style={{ padding: "6px 8px", fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-primary)" }}>Total</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, color: taxD > 0 ? "var(--chart-3)" : "var(--positive)" }}>{fmtDollar(opt.cTax)}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-secondary)" }}>{fmtDollar(cur.cTax)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="chart-card">
              <div className="ct">Gross Taxable Income by Source</div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 10 }}>
                What generates taxable income each year — bright bars = with conversions, muted = without
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={incChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%" barGap={2}>
                      <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
                      <XAxis dataKey="age" stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} />
                      <YAxis stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} tickFormatter={v => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
                      <Tooltip content={<IncYearTip />} />
                      <Bar dataKey="opt_ss"   stackId="opt" fill="var(--accent-teal)"                  name="OPT Social Sec." />
                      <Bar dataKey="opt_ab"   stackId="opt" fill="var(--accent-gold)"                  name="OPT Annuity" />
                      <Bar dataKey="opt_rmd"  stackId="opt" fill="var(--accent-purple)"                  name="OPT RMD" />
                      <Bar dataKey="opt_pxs"  stackId="opt" fill="var(--chart-2)"                  name="OPT Pretax Draw" />
                      <Bar dataKey="opt_conv" stackId="opt" fill="var(--positive)"                  name="OPT Conversion"  radius={[2,2,0,0]} />
                      <Bar dataKey="cur_ss"   stackId="cur" fill="var(--tint-sky-30)"     name="CUR Social Sec." />
                      <Bar dataKey="cur_ab"   stackId="cur" fill="rgba(251,191,36,0.3)"     name="CUR Annuity" />
                      <Bar dataKey="cur_rmd"  stackId="cur" fill="var(--tint-violet-400-30)"    name="CUR RMD" />
                      <Bar dataKey="cur_pxs"  stackId="cur" fill="rgba(96,165,250,0.3)"     name="CUR Pretax Draw" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={tblBox}>
                  <div style={tblHead}>
                    <span>Lifetime</span>
                    <div style={{ display: "flex", gap: 12 }}>
                      <span style={{ color: "var(--accent-teal)" }}>With Conv</span>
                      <span style={{ color: "var(--text-faint)" }}>Without</span>
                    </div>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {ltRow("var(--accent-teal)", "Social Security (85%)", optSSTotal, curSSTotal)}
                      {(optAbTotal > 0 || curAbTotal > 0) && ltRow("var(--accent-gold)", "Annuity / Benefit", optAbTotal, curAbTotal)}
                      {ltRow("var(--accent-purple)", "Required Min. Dist.", opt.cRmd || 0, cur.cRmd || 0)}
                      {(optPxsTotal > 0 || curPxsTotal > 0) && ltRow("var(--chart-2)", "Pretax Withdrawals", optPxsTotal, curPxsTotal)}
                      {(opt.cConv || 0) > 0 && ltRow("var(--positive)", "Roth Conversions", opt.cConv || 0, 0)}
                      <tr style={{ borderTop: "2px solid var(--tint-white-10)", background: "var(--card-bg)" }}>
                        <td style={{ padding: "6px 8px", fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-primary)" }}>Total Taxable Inc.</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--accent-teal)" }}>{fmtDollar(optTotInc)}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-secondary)" }}>{fmtDollar(curTotInc)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="chart-card">
              <div className="ct">Effective Tax Rate · Optimized vs Current Plan</div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart
                  data={opt.rows.filter((_, i) => i % 2 === 0).map(r => {
                    const c = cur.rows.find(cr => cr.yr === r.yr);
                    return { age: r.age, "OPT Rate": r.effR, "CUR Rate": c ? c.effR : 0 };
                  })}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
                  <XAxis dataKey="age" stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} />
                  <YAxis stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} tickFormatter={v => (v * 100).toFixed(0) + "%"} width={36} />
                  <Tooltip content={<RateTip />} />
                  <Legend wrapperStyle={{ fontSize: "var(--fs-small)" }} />
                  <Line type="monotone" dataKey="OPT Rate" stroke="var(--positive)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="CUR Rate" stroke="var(--chart-1)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        );
      })()}
      {view === "table" && (
        <div className="chart-card" style={{ overflowX: "auto" }}>
          <div className="ct">Year-by-Year Comparison Table</div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 10, lineHeight: 1.5, padding: "6px 8px", background: "var(--card-bg)", borderRadius: 6, border: "1px solid var(--tint-white-06)" }}>
            <strong style={{ color: "var(--text-primary)" }}>OPT</strong> = With Roth conversion ladder &nbsp;|&nbsp; <strong style={{ color: "var(--text-primary)" }}>CUR</strong> = Without conversions &nbsp;|&nbsp; <span style={{ color: "var(--chart-1)" }}>Red numbers</span> = you're paying more tax that year by choice to pay less at {rmdAge}
          </div>
          <table className="roth-tbl">
            <thead>
              <tr>
                <th>Year</th>
                <th>Age</th>
                <th style={{ borderLeft: "2px solid var(--tint-indigo-40)", color: "#a5b4fc" }}>OPT Rate</th>
                <th style={{ color: "var(--text-secondary)" }}>CUR Rate</th>
                <th style={{ borderLeft: "2px solid rgba(255,111,122,0.4)", color: "var(--chart-1)" }}>OPT Tax</th>
                <th style={{ color: "var(--text-secondary)" }}>CUR Tax</th>
                <th style={{ borderLeft: "2px solid rgba(52,211,153,0.4)", color: "var(--positive)" }}>OPT RMD</th>
                <th style={{ color: "var(--text-secondary)" }}>CUR RMD</th>
              </tr>
            </thead>
            <tbody>
              {opt.rows
                .filter((r) => r.age >= (params.retireAge || 60) && r.age <= (params.endAge || 90))
                .filter((_, i) => i % 2 === 0 || i < 12)
                .map((r) => {
                  const c = cur.rows.find((cr) => cr.yr === r.yr);
                  return (
                    <tr key={r.yr} className={r.conv > 0 ? "gold" : ""}>
                      <td>{r.yr}</td>
                      <td style={{ color: "var(--text-secondary)" }}>{r.age}</td>
                      <td style={{ borderLeft: "2px solid var(--tint-indigo-20)", background: "var(--tint-indigo-05)" }}>{(r.effR * 100).toFixed(0)}%</td>
                      <td style={{ background: "var(--tint-indigo-05)" }}>{c ? (c.effR * 100).toFixed(0) : "0"}%</td>
                      <td style={{ color: "var(--chart-1)", borderLeft: "2px solid var(--tint-red-20)", background: "var(--tint-red-05)" }}>{fmtDollar(r.totT)}</td>
                      <td style={{ color: "var(--text-secondary)", background: "var(--tint-red-05)" }}>
                        {c ? fmtDollar(c.totT) : "$0"}
                      </td>
                      <td style={{ color: "var(--positive)", borderLeft: "2px solid rgba(52,211,153,0.2)", background: "var(--tint-green-05)" }}>
                        {r.rmd > 0 ? fmtDollar(r.rmd) : "-"}
                      </td>
                      <td style={{ color: "var(--chart-1)", background: "var(--tint-green-05)" }}>
                        {c && c.rmd > 0 ? fmtDollar(c.rmd) : "-"}
                      </td>
                    </tr>
                  );
                })}
              <tr style={{ borderTop: "2px solid var(--tint-white-15)", background: "var(--card-bg)" }}>
                <td colSpan={2} style={{ fontWeight: 700, color: "var(--text-primary)", textAlign: "left" }}>Lifetime Totals</td>
                <td colSpan={2} style={{ borderLeft: "2px solid var(--tint-indigo-20)", background: "var(--tint-indigo-05)" }} />
                <td style={{ color: "var(--chart-1)", fontWeight: 700, borderLeft: "2px solid var(--tint-red-20)", background: "var(--tint-red-05)" }}>{fmtDollar(opt.cTax)}</td>
                <td style={{ color: "var(--text-secondary)", fontWeight: 700, background: "var(--tint-red-05)" }}>{fmtDollar(cur.cTax)}</td>
                <td colSpan={2} style={{ color: cur.cTax - opt.cTax > 0 ? "var(--positive)" : "var(--chart-1)", fontWeight: 700, textAlign: "left", borderLeft: "2px solid rgba(52,211,153,0.2)", background: "var(--tint-green-05)" }}>
                  {cur.cTax - opt.cTax > 0 ? `Saves ${fmtDollar(cur.cTax - opt.cTax)}` : `Costs ${fmtDollar(opt.cTax - cur.cTax)} more`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {view === "scenarios" && (() => {
        const baseCur = cur;            // user's state, no conversions
        const baseOpt = opt;            // user's state, with conversions
        const noTaxOpt = exNoTax.opt;   // no-tax state, with conversions
        const userState = params.stateOfResidence || "Your State";
        const allYears = baseOpt.rows.filter(r => r.age >= (params.retireAge || 60));
        const stSave = baseCur.cTax - baseOpt.cTax;
        const ntSave = baseCur.cTax - noTaxOpt.cTax;
        return (
          <div className="chart-card" style={{ overflowX: "auto" }}>
            <div className="ct">Three scenarios compared</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 10, lineHeight: 1.5, padding: "6px 8px", background: "var(--card-bg)", borderRadius: 6, border: "1px solid var(--tint-white-06)" }}>
              <strong style={{ color: "var(--chart-1)" }}>No Conversion</strong> = stay put, do nothing &nbsp;|&nbsp;
              <strong style={{ color: "var(--accent-teal)" }}>{userState} + Convert</strong> = stay put, execute Roth ladder &nbsp;|&nbsp;
              <strong style={{ color: "var(--positive)" }}>No-Tax State + Convert</strong> = move to a no-income-tax state, execute Roth ladder
            </div>
            <table className="roth-tbl">
              <thead>
                {/* Row 1: scenario group banners */}
                <tr>
                  <th rowSpan={2} style={{ verticalAlign: "bottom" }}>Year</th>
                  <th rowSpan={2} style={{ verticalAlign: "bottom" }}>Age</th>
                  <th colSpan={3} style={{ color: "var(--chart-1)", background: "var(--bg-danger)", textAlign: "center", borderBottom: "2px solid rgba(255,111,122,0.45)", borderLeft: "2px solid rgba(255,111,122,0.45)", paddingBottom: 5, fontSize: "var(--fs-small)", fontWeight: 700, letterSpacing: "0.04em" }}>
                    No Conversion
                  </th>
                  <th colSpan={3} style={{ color: "var(--accent-teal)", background: "rgba(90,209,240,0.13)", textAlign: "center", borderBottom: "2px solid var(--tint-sky-45)", borderLeft: "2px solid var(--tint-sky-45)", paddingBottom: 5, fontSize: "var(--fs-small)", fontWeight: 700, letterSpacing: "0.04em" }}>
                    {userState} + Convert
                  </th>
                  <th colSpan={3} style={{ color: "var(--positive)", background: "rgba(52,211,153,0.13)", textAlign: "center", borderBottom: "2px solid rgba(52,211,153,0.45)", borderLeft: "2px solid rgba(52,211,153,0.45)", paddingBottom: 5, fontSize: "var(--fs-small)", fontWeight: 700, letterSpacing: "0.04em" }}>
                    No-Tax State + Convert
                  </th>
                </tr>
                {/* Row 2: measure sub-headers */}
                <tr>
                  <th style={{ color: "var(--chart-1)", background: "var(--tint-red-07)", borderLeft: "2px solid rgba(255,111,122,0.35)" }}>Roth $</th>
                  <ThInfo style={{ color: "var(--chart-1)", background: "var(--tint-red-07)", cursor: "help" }} tip={`Total income tax owed this year = federal + ${userState} state tax combined. Hover any data cell for the federal / state split.`}>Total Tax</ThInfo>
                  <th style={{ color: "var(--text-secondary)", background: "var(--tint-red-07)" }}>IRA Bal</th>
                  <th style={{ color: "var(--accent-teal)", background: "var(--tint-sky-07)", borderLeft: "2px solid rgba(90,209,240,0.35)" }}>Roth $</th>
                  <ThInfo style={{ color: "var(--accent-teal)", background: "var(--tint-sky-07)", cursor: "help" }} tip={`Total income tax owed this year = federal + ${userState} state tax combined. Hover any data cell for the federal / state split.`}>Total Tax</ThInfo>
                  <th style={{ color: "var(--text-secondary)", background: "var(--tint-sky-07)" }}>IRA Bal</th>
                  <th style={{ color: "var(--positive)", background: "var(--tint-green-07)", borderLeft: "2px solid rgba(52,211,153,0.35)" }}>Roth $</th>
                  <ThInfo style={{ color: "var(--positive)", background: "var(--tint-green-07)", cursor: "help" }} tip={"Federal income tax only — no state income tax applies in this scenario (no-tax state). Hover any data cell to confirm the $0 state split."}>Total Tax</ThInfo>
                  <th style={{ color: "var(--text-secondary)", background: "var(--tint-green-07)" }}>IRA Bal</th>
                </tr>
              </thead>
              <tbody>
                {allYears.map(r => {
                  const nc = baseCur.rows.find(x => x.yr === r.yr) || {};
                  const bo = baseOpt.rows.find(x => x.yr === r.yr) || {};
                  const nt = noTaxOpt.rows.find(x => x.yr === r.yr) || {};
                  const hasConv = bo.conv > 0 || nt.conv > 0;
                  return (
                    <tr key={r.yr} style={{ background: hasConv ? "var(--tint-teal-600-06)" : undefined }}>
                      <td>{r.yr}</td>
                      <td style={{ color: "var(--text-secondary)" }}>{r.age}</td>
                      {/* No Conversion columns */}
                      <td style={{ color: "var(--chart-1)", background: "var(--tint-red-07)", borderLeft: "2px solid var(--tint-red-25)" }}>{nc.conv > 0 ? fmtDollar(nc.conv) : "—"}</td>
                      <td style={{ color: "var(--chart-1)", background: "var(--tint-red-07)", cursor: "help" }} title={`Fed: ${fmtDollar(nc.fedT || 0)} | State: ${fmtDollar(nc.stT || 0)}`}>{fmtDollar(nc.totT || 0)}</td>
                      <td style={{ color: "var(--text-secondary)", background: "var(--tint-red-07)" }}>{fmtDollar(nc.pT || 0)}</td>
                      {/* {userState} + Convert columns */}
                      <td style={{ color: "var(--accent-teal)", fontWeight: bo.conv > 0 ? 600 : 400, background: "var(--tint-sky-07)", borderLeft: "2px solid var(--tint-sky-25)" }}>{bo.conv > 0 ? fmtDollar(bo.conv) : "—"}</td>
                      <td style={{ color: "var(--accent-teal)", background: "var(--tint-sky-07)", cursor: "help" }} title={`Fed: ${fmtDollar(bo.fedT || 0)} | State: ${fmtDollar(bo.stT || 0)}`}>{fmtDollar(bo.totT || 0)}</td>
                      <td style={{ color: "var(--text-secondary)", background: "var(--tint-sky-07)" }}>{fmtDollar(bo.pT || 0)}</td>
                      {/* No-Tax State + Convert columns */}
                      <td style={{ color: "var(--positive)", fontWeight: nt.conv > 0 ? 600 : 400, background: "var(--tint-green-07)", borderLeft: "2px solid rgba(52,211,153,0.25)" }}>{nt.conv > 0 ? fmtDollar(nt.conv) : "—"}</td>
                      <td style={{ color: "var(--positive)", background: "var(--tint-green-07)", cursor: "help" }} title={`Fed: ${fmtDollar(nt.fedT || 0)} | State: $0 (no-tax state)`}>{fmtDollar(nt.totT || 0)}</td>
                      <td style={{ color: "var(--text-secondary)", background: "var(--tint-green-07)" }}>{fmtDollar(nt.pT || 0)}</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: "2px solid var(--tint-white-15)", background: "var(--row-highlight)", fontWeight: 700 }}>
                  <td colSpan={2} style={{ color: "var(--text-primary)" }}>Lifetime Totals</td>
                  <td style={{ color: "var(--chart-1)", background: "var(--tint-red-07)", borderLeft: "2px solid var(--tint-red-25)" }}>—</td>
                  <td style={{ color: "var(--chart-1)", background: "var(--tint-red-07)" }}>{fmtDollar(baseCur.cTax)}</td>
                  <td style={{ color: "var(--text-secondary)", background: "var(--tint-red-07)" }}>{fmtDollar(baseCur.rows[baseCur.rows.length-1]?.pT || 0)}</td>
                  <td style={{ color: "var(--accent-teal)", background: "var(--tint-sky-07)", borderLeft: "2px solid var(--tint-sky-25)" }}>{fmtDollar(baseOpt.cConv)}</td>
                  <td style={{ color: stSave > 0 ? "var(--positive)" : "var(--chart-1)", background: "var(--tint-sky-07)" }}>{fmtDollar(baseOpt.cTax)} {stSave > 0 ? `(saves ${fmtDollar(stSave)})` : `(costs ${fmtDollar(-stSave)} more)`}</td>
                  <td style={{ color: "var(--text-secondary)", background: "var(--tint-sky-07)" }}>{fmtDollar(baseOpt.rows[baseOpt.rows.length-1]?.pT || 0)}</td>
                  <td style={{ color: "var(--positive)", background: "var(--tint-green-07)", borderLeft: "2px solid rgba(52,211,153,0.25)" }}>{fmtDollar(noTaxOpt.cConv)}</td>
                  <td style={{ color: ntSave > 0 ? "var(--positive)" : "var(--chart-1)", background: "var(--tint-green-07)" }}>{fmtDollar(noTaxOpt.cTax)} {ntSave > 0 ? `(saves ${fmtDollar(ntSave)})` : `(costs ${fmtDollar(-ntSave)} more)`}</td>
                  <td style={{ color: "var(--text-secondary)", background: "var(--tint-green-07)" }}>{fmtDollar(noTaxOpt.rows[noTaxOpt.rows.length-1]?.pT || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}
      <div
        style={{
          fontSize: "var(--fs-small)",
          color: "var(--text-faint)",
          fontStyle: "italic",
          textAlign: "center",
        }}
      >
        This analysis is for planning purposes only. Consult a tax professional
        before executing Roth conversions. Progressive fed brackets (IRS Rev.
        Proc. 2025-32) · NJ graduated rates · Joint & Last Survivor RMD table ·
        7% growth assumption · Conversion tax default funded from your taxable /
        HSA / cash bucket (Vanguard BETR best practice — lowest effective rate).
        Override in Assumptions → Roth Conversion Strategy.
      </div>
    </div>
  );
}

/**
 * Year-end check — the December deadline.
 *
 * Every tax lever here is use-it-or-lose-it on Dec 31: a Roth conversion has
 * to settle (not just be requested), harvesting has to actually trade, a QCD
 * has to clear. Unused bracket room doesn't roll over — you can't go back
 * and fill last year's 12% bracket. The app modeled all of it and said
 * nothing when it actually counted.
 *
 * Two surfaces on purpose: a popup once per year so it can't be missed, and
 * a persistent strip on the Net Worth card so dismissing the popup doesn't
 * throw the information away for the rest of the month.
 */
const LS_YEAREND_KEY = "aira_yearend_ack_v1";

function yearEndAcked(year) {
  try { return Number(localStorage.getItem(LS_YEAREND_KEY)) === year; } catch { return false; }
}
function ackYearEnd(year) {
  try { localStorage.setItem(LS_YEAREND_KEY, String(year)); } catch { /* private mode */ }
}

/** Shared body: the countdown, the room, and what to do about it. */
function YearEndBody({ room, days, year, compact }) {
  const money = (n) => (n === Infinity ? "no limit" : fmtDollar(n));
  return (
    <div style={{ fontSize: compact ? 10 : 12, color: "var(--text-secondary)", lineHeight: 1.6 }}>
      <div style={{ marginBottom: compact ? 4 : 8 }}>
        <strong style={{ color: days <= 10 ? "var(--chart-1)" : "var(--accent-gold)", fontSize: compact ? 11 : 13 }}>
          {days} day{days === 1 ? "" : "s"} left
        </strong>{" "}
        to act in {year}. Conversions must <strong>settle</strong> by Dec 31 — not just be requested.
      </div>
      {room.hasData ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <span>Room to top of {room.marginalBracket != null ? "your target" : ""} bracket</span>
            <strong style={{ color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace" }}>{money(room.bracketRoom)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <span>Room before the IRMAA cliff (MAGI)</span>
            <strong style={{ color: "var(--accent-purple)", fontFamily: "'JetBrains Mono',monospace" }}>{money(room.irmaaRoom)}</strong>
          </div>
          <div style={{
            display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4,
            paddingTop: 4, borderTop: "1px solid var(--tint-slate-400-15)",
          }}>
            <span style={{ color: "var(--text-primary)" }}>
              Convertible now{" "}
              <span style={{ color: "var(--text-muted)" }}>({room.bindingConstraint === "irmaa" ? "IRMAA binds" : "bracket binds"})</span>
            </span>
            <strong style={{ color: "var(--positive)", fontFamily: "'JetBrains Mono',monospace" }}>{money(room.conversionRoom)}</strong>
          </div>
          {room.alreadyConverted > 0 && (
            <div style={{ fontSize: compact ? 9 : 10, color: "var(--text-muted)", marginTop: 3 }}>
              {fmtDollar(room.alreadyConverted)} already converted this year — the figures above are what remains.
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: compact ? 9 : 11, color: "var(--text-muted)", fontStyle: "italic" }}>
          No tax-room figure: {room.reason}. Compare your own YTD taxable income against the
          ceilings on the Withdrawal Plan tab.
        </div>
      )}
    </div>
  );
}

/** Compact strip for the Net Worth card. Visible all December. */
function YearEndStrip({ room, days, year }) {
  return (
    <div style={{
      marginTop: 8, padding: "6px 8px", borderRadius: 6,
      background: "var(--tint-amber-08)", border: "1px solid rgba(251,191,36,0.25)",
    }}>
      <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-gold)", marginBottom: 3 }}>
        Year-end check
      </div>
      <YearEndBody room={room} days={days} year={year} compact />
    </div>
  );
}

/** Once-a-year popup. Portalled so no ancestor can clip it. */
function YearEndModal({ room, days, year, onClose, onSaveCheckIn, canCheckIn }) {
  const [saved, setSaved] = React.useState(false);
  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(2,6,23,0.72)", zIndex: 10000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f172a", border: "1px solid var(--tint-amber-35)", borderRadius: 12,
          padding: "18px 20px", maxWidth: 480, width: "100%", maxHeight: "85vh", overflowY: "auto",
          boxShadow: "0 10px 40px var(--tint-black-80)",
        }}
      >
        <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--accent-gold)", marginBottom: 4 }}>
          Year-end check &mdash; {year}
        </div>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 12 }}>
          Shown once each December. Two things are worth doing before the 31st.
        </div>

        <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
          1 &middot; Your remaining tax room
        </div>
        <YearEndBody room={room} days={days} year={year} />

        <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--text-primary)", margin: "14px 0 6px" }}>
          2 &middot; Record a check-in
        </div>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 8 }}>
          A year-end snapshot of your real balances and success rate. Check-ins anchor the
          projection to what actually happened, so next year&rsquo;s plan starts from fact
          rather than from last year&rsquo;s forecast.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="mbtn"
            disabled={!canCheckIn || saved}
            onClick={() => { onSaveCheckIn?.(); setSaved(true); }}
            style={{ fontSize: "var(--fs-label)", padding: "5px 12px", opacity: (!canCheckIn || saved) ? 0.5 : 1 }}
          >
            {saved ? "Check-in saved" : "Save check-in"}
          </button>
          <button className="mbtn" onClick={onClose} style={{ fontSize: "var(--fs-label)", padding: "5px 12px" }}>
            Close
          </button>
        </div>
        {!canCheckIn && (
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 6, fontStyle: "italic" }}>
            Run the Monte Carlo first — a checkpoint stores the success rate alongside the balances.
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

const LANDMINE_TIP_W = 290;

/**
 * Landmine hover card.
 *
 * Rendered through a portal to document.body rather than as an absolutely
 * positioned child. The table it lives in sits inside `overflowX: auto`, and
 * per the CSS spec a scroll value on one axis forces the other to `auto`
 * too — so the old `position:absolute; bottom:100%` card got clipped by its
 * own scroll container the moment it extended above the first row. (A user
 * reported this with a screenshot: the 72(t) card was sheared off by the
 * panel above it.)
 *
 * Portalling escapes every ancestor clip. Position then gets computed in
 * viewport space from the trigger's rect, flipped below when there isn't
 * room above, and clamped horizontally so a landmine in the last column
 * can't run off-screen.
 */
function LandmineTip({ emoji, label, detail, color }) {
  const [show, setShow] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const ref = React.useRef(null);

  const place = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Measured lazily: the card is ~1 line of title + wrapped detail. Estimating
    // high is the safe direction — it only decides flip, never final size.
    const estH = 150;
    const above = r.top;
    const flipDown = above < estH + 12;
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - LANDMINE_TIP_W / 2),
      window.innerWidth - LANDMINE_TIP_W - 8
    );
    setPos({
      left,
      top: flipDown ? r.bottom + 8 : undefined,
      bottom: flipDown ? undefined : window.innerHeight - r.top + 8,
    });
  }, []);

  const open = () => { place(); setShow(true); };
  const close = () => setShow(false);

  // A scroll or resize while open would strand the card away from its trigger.
  React.useEffect(() => {
    if (!show) return;
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [show, place]);

  return (
    <span
      ref={ref}
      style={{ position: "relative", cursor: "help", marginRight: 3, display: "inline-block" }}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {emoji}
      {show && pos && typeof document !== "undefined" && ReactDOM.createPortal(
        <div style={{
          position: "fixed", left: pos.left, top: pos.top, bottom: pos.bottom,
          background: "#0f172a",
          border: "1px solid var(--tint-white-12)", borderRadius: 8,
          padding: "9px 13px", width: LANDMINE_TIP_W, maxWidth: "calc(100vw - 16px)",
          boxShadow: "0 4px 20px var(--tint-black-70)", zIndex: 9999, pointerEvents: "none",
          whiteSpace: "normal", textAlign: "left",
          maxHeight: "70vh", overflowY: "auto",
        }}>
          <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: color || "var(--text-primary)", marginBottom: 5 }}>
            {emoji} {label}
          </div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.6 }}>{detail}</div>
        </div>,
        document.body
      )}
    </span>
  );
}

/**
 * Combined Withdrawal Plan tab — wraps the two views that answer the two
 * different planning questions, with each question called out prominently
 * and a twisty (collapsible) body. Both sections default to open so the
 * full plan is visible on first load; the user can collapse either to focus.
 */
// Sourcing guardrails — the "which bucket" controls, kept next to the
// waterfall they shape. They persist to the profile via the same
// onAssumptionChange setter the Profile panel uses, so the MC stale-flag
// fires the same way. Distribution strategy stays in Profile (it's global,
// drives MC).
// Plain-language names for the four drawable buckets (shared by the order control
// and the templated Section-1 subtitle).
const BUCKET_LABELS = { cash: "Cash ", taxable: "Taxable brokerage", pretax: "Pre-tax (IRA/401k)", roth: "Roth" };
const BUCKET_LABELS_SHORT = { cash: "cash", taxable: "taxable", pretax: "pre-tax", roth: "Roth" };

// "Account draw order" — which bucket drains first. Orthogonal to the distribution
// strategy (how much to spend) and to the guardrails (how deep to draw pre-tax/Roth).
function AccountDrawOrder({ p, onAssumptionChange }) {
  const set = onAssumptionChange ?? (() => {});
  const mode = p.orderingMode || "tax_reactive";
  const effective = resolveDrawOrder(mode, p.withdrawalOrder);          // shown for non-custom modes
  const customOrder = resolveDrawOrder("custom", p.withdrawalOrder);    // the editable list

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= customOrder.length) return;
    const next = customOrder.slice();
    [next[i], next[j]] = [next[j], next[i]];
    set("withdrawalOrder", next);
  };

  // Every option here genuinely reorders the draw. A fourth, "three_bucket",
  // was removed in v1.2.129: it never changed draw order at all, and its
  // asset-location half was measured to be a bit-for-bit no-op (Bucket 2 and
  // Bucket 3 both resolve to postRetireEq, which IS the portfolio-wide
  // retirement return, and cash already earns cashRealReturn in every mode).
  // Time-horizon buckets live on as an organizing/planning view in the
  // 🧺 Buckets tab, which is where they belong — they are an investing and
  // behavioral frame, not a withdrawal-ordering rule.
  const MODES = [
    ["tax_reactive", "Tax-reactive", true],
    ["custom",       "Custom",       false],
    ["pretax_first", "Pre-tax first", false],
  ];
  const radioLbl = { fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5, cursor: "pointer", whiteSpace: "nowrap" };
  const arrow = (dis) => ({ background: dis ? "transparent" : "#0a1628", border: "1px solid #1e3a5f", color: dis ? "#334155" : "var(--accent-teal)", borderRadius: 4, width: 20, height: 18, cursor: dis ? "default" : "pointer", fontSize: "var(--fs-small)", lineHeight: 1, padding: 0 });

  return (
    <div style={{
      background: "rgba(56,189,248,0.05)", border: "1px solid rgba(56,189,248,0.18)",
      borderRadius: 8, padding: "10px 14px", margin: "10px 0",
    }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        <span style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent)" }}>
          Account draw order
        </span>
        {MODES.map(([val, name, rec]) => (
          <label key={val} style={radioLbl}>
            <input type="radio" name="orderingMode" checked={mode === val} onChange={() => set("orderingMode", val)} style={{ cursor: "pointer" }} />
            {name}{rec && <em style={{ color: "var(--text-muted)", fontStyle: "normal", fontSize: "var(--fs-small)" }}>&nbsp;(recommended)</em>}
          </label>
        ))}
        {mode !== "custom" && (
          <span style={{ marginLeft: "auto", fontSize: "var(--fs-small)", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono',monospace" }}>
            {effective.map((b) => BUCKET_LABELS[b]).join(" → ")}
          </span>
        )}
      </div>

      {mode === "custom" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {customOrder.map((b, i) => (
            <div key={b} style={{ display: "flex", alignItems: "center", gap: 6, background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 7, padding: "5px 8px" }}>
              <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{i + 1}</span>
              <span style={{ fontSize: "var(--fs-label)", color: "var(--text-primary)" }}>{BUCKET_LABELS[b]}</span>
              <button style={arrow(i === 0)} disabled={i === 0} onClick={() => move(i, -1)} title="Move earlier">▲</button>
              <button style={arrow(i === customOrder.length - 1)} disabled={i === customOrder.length - 1} onClick={() => move(i, 1)} title="Move later">▼</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 8, lineHeight: 1.5 }}>
        Earlier = drained first. RMDs are always taken first by law; the bracket cap, IRMAA guard, and Roth reserve below still apply to wherever pre-tax and Roth land.
      </div>

    </div>
  );
}

function SourcingGuardrails({ p, onAssumptionChange, summary }) {
  const set = onAssumptionChange ?? (() => {});
  const saved = summary?.taxSavings ?? 0;
  const ctl = { background: "#0a1628", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", cursor: "pointer", fontFamily: "inherit" };
  const lbl = { fontSize: "var(--fs-small)", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" };
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16,
      background: "var(--tint-sky-06)", border: "1px solid var(--tint-sky-20)",
      borderRadius: 8, padding: "10px 14px", margin: "10px 0",
    }}>
      <span style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-teal)" }}>
        Sourcing guardrails
      </span>
      <label style={lbl} title="Stop pre-tax (IRA/401k) draws when ordinary income would hit this tax bracket. Roth covers the rest. 'Off' = naive (pretax first, no ceiling).">
        Stop pre-tax draws at
        <select value={p.withdrawalBracketTarget || "22"} onChange={(e) => set("withdrawalBracketTarget", e.target.value)} style={ctl}>
          <option value="off">Off — pretax first</option>
          <option value="10">10% bracket</option>
          <option value="12">12% bracket</option>
          <option value="22">22% bracket (rec.)</option>
          <option value="24">24% bracket</option>
          <option value="irmaa">IRMAA-safe</option>
        </select>
      </label>
      <label style={lbl} title="IRMAA = income-based Medicare premium surcharge. Caps pre-tax draws below the Tier-1 income limit (ages 63+) so your Medicare premiums don't spike 2 years later.">
        <input type="checkbox" checked={p.irmaaGuard || false} onChange={(e) => set("irmaaGuard", e.target.checked)} style={{ cursor: "pointer", width: 15, height: 15 }} />
        Avoid Medicare (IRMAA) surcharges
      </label>
      <label style={lbl} title="AiRA will not draw your Roth below this balance — protects tax-free funds during market downturns.">
        Keep Roth above $
        <input type="number" value={p.rothEmergencyReserve ?? 0} onChange={(e) => set("rothEmergencyReserve", Number(e.target.value) || 0)} min={0} max={2_000_000} step={10_000}
          style={{ ...ctl, width: 110, fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
      </label>
      {/* IRC 72(t): only asked when it can actually apply. Determined up
          front from retireAge, not discovered mid-plan. The Rule of 55
          option only appears when a former-employer plan is actually
          detected among the pretax accounts — it doesn't reach an IRA, so
          offering it to a pure-rollover holder would invite a wrong answer.
          Detection is name-based (the account model has no 401k/IRA subtype). */}
      {(p.retireAge ?? 99) < EARLY_PENALTY_AGE && (() => {
        const plan = detectEmployerPlan(p.accounts || []);
        return (
          <div style={{
            width: "100%", marginTop: 6, padding: "8px 10px", borderRadius: 8,
            background: "var(--tint-red-08)", border: "1px solid var(--tint-red-25)",
          }}>
            <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "#fca5a5", marginBottom: 4 }}>
              Retiring at {p.retireAge} — before 59.5
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 6, lineHeight: 1.5 }}>
              Pre-tax withdrawals before 59.5 owe a <strong>10% penalty</strong> on top of income tax
              (IRC 72(t)). AiRA charges it. Tick an exception only if it genuinely applies to you.
            </div>
            {plan.hasEmployerPlan ? (
              <label style={lbl} title="Rule of 55: penalty-free withdrawals from the plan of the employer you separated from, if you left in or after the year you turned 55. It does NOT apply to an IRA — rolling the 401k over forfeits it permanently.">
                <input type="checkbox" checked={p.ruleOf55 || false}
                  onChange={(e) => set("ruleOf55", e.target.checked)}
                  style={{ cursor: "pointer", width: 15, height: 15 }} />
                Rule of 55 — I left this employer at 55+ and did NOT roll it over
                <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-small)", marginLeft: 4 }}>
                  ({plan.matches.join(", ")} = {Math.round(plan.share * 100)}% of pre-tax)
                </span>
              </label>
            ) : (
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontStyle: "italic", marginBottom: 4 }}>
                No former-employer 401k/403b/457 found in your pre-tax accounts, so the Rule of 55
                is unavailable — it never applies to an IRA. If you still hold one, name the account
                so it contains "401k".
              </div>
            )}
            <label style={lbl} title="72(t) SEPP: substantially equal periodic payments. Penalty-free, but the series must continue for the LONGER of 5 years or until age 59.5 — breaking it early retroactively penalizes every payment.">
              <input type="checkbox" checked={p.sepp72t || false}
                onChange={(e) => set("sepp72t", e.target.checked)}
                style={{ cursor: "pointer", width: 15, height: 15 }} />
              72(t) SEPP starting at age
              <input type="number" value={p.sepp72tStartAge ?? p.retireAge ?? 55}
                onChange={(e) => set("sepp72tStartAge", Number(e.target.value) || null)}
                min={30} max={59} step={1} disabled={!p.sepp72t}
                style={{ ...ctl, width: 60, fontFamily: "'JetBrains Mono',monospace", textAlign: "right",
                         opacity: p.sepp72t ? 1 : 0.4 }}
                onFocus={selectAllOnFocus} />
              {p.sepp72t && (
                <span style={{ color: "var(--accent-gold)", fontSize: "var(--fs-small)", marginLeft: 4 }}>
                  must run to age {Math.max((p.sepp72tStartAge ?? p.retireAge ?? 55) + 5, EARLY_PENALTY_AGE)}
                </span>
              )}
            </label>
          </div>
        );
      })()}
      <label style={lbl} title="The 'tax torpedo': as income rises, up to 85% of your Social Security becomes taxable. This flags the years where that happens (thresholds frozen since the 1980s).">
        <input type="checkbox" checked={p.ssTorpedoGuard ?? false} onChange={(e) => set("ssTorpedoGuard", e.target.checked)} style={{ cursor: "pointer", width: 15, height: 15 }} />
        Flag when Social Security gets taxed at 85%
      </label>
      {/* Live feedback: the effect of these controls, surfaced at the strip (design #3) */}
      <span
        style={{
          marginLeft: "auto", fontSize: "var(--fs-small)", fontWeight: 700,
          color: saved > 0 ? "var(--positive)" : "var(--text-secondary)",
          background: saved > 0 ? "var(--tint-green-07)" : "transparent",
          borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap",
        }}
        title="Estimated lifetime tax saved by this sourcing plan vs. drawing pre-tax first with no guardrails. Updates as you change the controls."
      >
        {saved > 0 ? `≈ ${fmtDollar(saved)} lifetime tax saved vs no plan` : "No tax savings vs pretax-first at these settings"}
      </span>
    </div>
  );
}

// Hoisted out of WithdrawalPlanCombined so it isn't recreated on every render
// (a fresh component identity each render remounts the subtree and drops focus).
function WithdrawalSectionHeader({ open, onToggle, color, question, subtitle, badge }) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        background: "var(--tint-white-03)",
        border: `1px solid ${withAlpha(color, "44")}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 8,
        padding: "16px 18px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        marginBottom: open ? 10 : 0,
      }}
    >
      <span style={{ color, fontSize: "var(--fs-h2)", lineHeight: 1, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s", display: "inline-block" }}>▸</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--text-primary)", fontStyle: "italic", lineHeight: 1.35 }}>
          “{question}”
        </div>
        <div style={{ fontSize: "var(--fs-body)", color: "var(--text-muted)", marginTop: 5 }}>{subtitle}</div>
      </div>
      {/* Badge intercepts its own clicks so opening the "how this works" info
          doesn't also collapse/expand the section it sits on top of. */}
      {badge && (
        <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, marginTop: 1 }}>
          {badge}
        </span>
      )}
    </button>
  );
}

function WithdrawalPlanCombined({ p, inf, withdrawalStrategy, onAssumptionChange, mc }) {
  // design-authority (2026-09-09): both defaulted to true, which is why every
  // first visit rendered draw order + guardrails + waterfall table AND the
  // strategy chart all at once, on top of the disclaimer/orientation/strategy
  // banners already above them — the "very very confusing" complaint. Closed
  // by default; the user opens what they came here for.
  const [openSourcing, setOpenSourcing] = useState(false);
  const [openStrategy, setOpenStrategy] = useState(false);
  // The Section-2 selector is PREVIEW-ONLY: it drives the year-by-year table below
  // without overwriting the Profile default (which drives the whole app + MC). A
  // separate "Set as default" action commits it. Seed from the global default and
  // re-sync if that default changes elsewhere (e.g. the Profile panel).
  const [previewStrategy, setPreviewStrategy] = useState(withdrawalStrategy);
  useEffect(() => { setPreviewStrategy(withdrawalStrategy); }, [withdrawalStrategy]);
  const previewIsDefault = previewStrategy === withdrawalStrategy;
  // Compute the waterfall once here so the guardrail strip (always visible) and the
  // table (when expanded) share it — no double compute, and the strip can show the
  // live "tax saved vs no plan" delta from the same summary.
  const waterfall = useMemo(() => waterfallForActiveStrategy(p), [p]);
  // Set when this profile was loaded off a strategy that's since been retired.
  const migrated = migrationNotice(p.withdrawalStrategyMigratedFrom);

  // Was its own persistent banner above both sections; moved into the
  // top-right corner of both section headers, so it's visible on the content
  // regardless of which one the user has open — rather than floating above
  // both in a strip. Both sections share the SAME static badge: neither
  // section's content actually flips on `previewStrategy` (Section 1 reads
  // `p` directly; Section 2's dropdown-driven preview is a separate, already
  // self-labeled state — see its "Preview how X paces spending" subtitle and
  // the "Previewing your saved default" line under the dropdown below, which
  // is where the preview/saved explanation now lives instead of on the badge
  // itself). The badge just always names the one strategy actually saved.
  const savedPlanBadge = (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 9px", borderRadius: 5, fontSize: "var(--fs-small)", fontWeight: 700,
        letterSpacing: ".03em", whiteSpace: "nowrap",
        border: "1px solid rgba(52,211,153,0.35)",
        background: "var(--tint-green-07)",
        color: "var(--positive)",
      }}
    >
      {"Saved: " + getStrategyLabel(withdrawalStrategy)}
    </span>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Retired-strategy notice. A saved plan silently changing which spending
          rule it runs is exactly the defect class this cull was meant to reduce,
          so the swap is stated, the replacement is named, and we say plainly
          whether the numbers moved. Dismissing clears the stamp for good. */}
      {migrated && (
        <div style={{
          background: migrated.fidelity === "changed" ? "var(--tint-amber-08)" : "var(--tint-teal-600-08)",
          border: `1px solid ${migrated.fidelity === "changed" ? "var(--tint-amber-35)" : "rgba(13,148,136,0.35)"}`,
          borderRadius: 8, padding: "12px 14px", fontSize: "var(--fs-label)",
          color: "var(--text-secondary)", lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: migrated.fidelity === "changed" ? "var(--accent-gold)" : "var(--accent-teal)", marginBottom: 4 }}>
            <Dot tone={migrated.fidelity === "changed" ? "warn" : "ok"} />Your saved strategy “{migrated.fromLabel}” was retired — this plan now uses{" "}
            {migrated.toLabel}.
          </div>
          <div>{migrated.basis}</div>
          <div style={{ marginTop: 6, color: migrated.fidelity === "changed" ? "var(--accent-gold)" : "var(--text-secondary)" }}>
            {migrated.impact}
          </div>
          <button
            className="mbtn"
            style={{ marginTop: 9, fontSize: "var(--fs-small)" }}
            onClick={() => onAssumptionChange("withdrawalStrategyMigratedFrom", null)}
          >
            Got it — don't show this again
          </button>
        </div>
      )}

      {/* Orientation card — explains the two-question framing */}
      <div style={{
        background: "var(--tint-teal-600-06)",
        border: "1px solid rgba(13,148,136,0.18)",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: "var(--fs-small)",
        color: "var(--text-secondary)",
        lineHeight: 1.55,
      }}>
        This tab answers two questions. <strong style={{ color: "var(--accent-teal)" }}>(1) Which accounts should you draw from each year</strong> to
        pay the least tax over your lifetime — set the guardrails below and see the plan. <strong style={{ color: "var(--accent-gold)" }}>(2) How much
        does your chosen strategy plan to spend</strong> year by year. Start with the first question.
        <span style={{ display: "block", color: "var(--text-muted)", marginTop: 4 }}>
          Both use the same tax engine, so the per-year tax figures agree.
        </span>
      </div>

      {/* Global strategy control.
          This block used to render between Section 2's header and Section
          2's collapsible body — a dead zone belonging to no visible
          container, and structurally inconsistent with Section 1 (which
          puts every control inside its collapsible).

          It's hoisted to tab level rather than pushed into Section 2's
          collapsible because it writes `withdrawalStrategy` — the single
          global consumed by runMC, simulateDeterministicWithStrategy, the
          home Net Worth card, the MCTab InputCard row, the engine InfoModal
          and buildRothExplorer. Gating the app's only strategy-write behind
          a twisty would recreate the exact confusion the banner below was
          built to fix.

          Relocated only — dropdown, button styling and commit logic are
          untouched. The persistent SAVED/PREVIEW state that used to sit here
          as its own banner now renders as a small badge in the top-right
          corner of each section header below — see `strategyStatusBadge`. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
          <select
            value={previewStrategy}
            onChange={(e) => setPreviewStrategy(e.target.value)}
            style={{
              width: "100%",
              background: "#0d1b2a",
              border: "1px solid #1e3a5f",
              color: "var(--text-primary)",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: "var(--fs-body)",
              fontFamily: "var(--font-sans)",
            }}
          >
            <option value="smart">Smart Waterfall (Tax-Optimal · GK→Bengen at 15yr)</option>
            <option value="gk">Guyton‑Klinger (Dynamic)</option>
            <option value="bengen">Bengen 4% Rule (fixed, inflation-adjusted)</option>
            <option value="fixed">Fixed % of Portfolio</option>
            <option value="ninety_five_rule">95% Rule (Cut Protection)</option>
            <option value="vpw">VPW (Variable Percentage · spends to zero)</option>
          </select>
          <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.5 }}>{getStrategyDescription(previewStrategy)}</div>
          {/* Commit action — only shown when the preview differs from the
              saved default. This is the one explicit path from "previewing"
              to "applied app-wide." */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 24 }}>
            {previewIsDefault ? (
              <span style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 5 }}>
                Previewing your saved default — the whole app (including Monte Carlo) uses this strategy.
                <InfoModal
                  title="How this works"
                  accent="var(--positive)"
                  trigger={
                    <span style={{ color: "var(--text-secondary)", cursor: "pointer", display: "inline-flex" }}
                          title="Tap or click for more info">
                      <InfoIcon size={11} />
                    </span>
                  }
                >
                  <p style={{ margin: "0 0 10px" }}>The dropdown above is a <strong>PREVIEW</strong>. Changing it redraws the year-by-year table on this page so you can compare strategies — it does not change your plan, and it does not re-run the Monte Carlo.</p>
                  <p style={{ margin: "0 0 10px" }}><strong>TO ACTUALLY CHANGE YOUR PLAN: </strong>Pick a strategy in the dropdown, then click the yellow "Use ... in my plan" button that appears just below it. That is the only place in the app that saves your strategy — there is no strategy setting in the Profile tab.</p>
                  <p style={{ margin: 0 }}>ONCE SAVED, it drives everything: the Monte Carlo success rate, the Net Worth summary card on the home page, and every projection tab. Until you click it, all of those keep using your saved strategy — which is why the home card can show a different strategy than this dropdown. Your saved plan currently uses <strong style={{ color: "var(--text-primary)" }}>{getStrategyLabel(withdrawalStrategy)}</strong>.</p>
                </InfoModal>
              </span>
            ) : (
              <>
                <span style={{ fontSize: "var(--fs-label)", color: "var(--accent-gold)" }}>
                  Nothing has changed in your plan yet.
                </span>
                {/* Promoted from a ghost button to a solid primary. This is
                    the only control in the entire app that writes
                    withdrawalStrategy — verified by grep; the Profile panel
                    displays it but has no selector. A single, conditional,
                    low-contrast button being the sole commit path is why the
                    preview/saved split read as a bug. */}
                <button
                  onClick={() => {
                    onAssumptionChange("withdrawalStrategy", previewStrategy);
                    // Choosing a strategy answers the migration notice —
                    // leaving it up would keep telling the user about a swap
                    // they've now overridden.
                    if (p.withdrawalStrategyMigratedFrom) onAssumptionChange("withdrawalStrategyMigratedFrom", null);
                  }}
                  style={{
                    padding: "7px 16px", fontSize: "var(--fs-label)", fontWeight: 800, borderRadius: 6,
                    border: "none", background: "var(--accent-gold)", color: "#0d1b2a",
                    cursor: "pointer", whiteSpace: "nowrap",
                    boxShadow: "0 2px 10px var(--tint-amber-35)",
                  }}
                  title="Save this strategy to your plan. This re-runs the Monte Carlo simulation and updates every tab, including the Net Worth summary card."
                >
                  Use {getStrategyLabel(previewStrategy)} in my plan →
                </button>
                <button
                  onClick={() => setPreviewStrategy(withdrawalStrategy)}
                  style={{
                    padding: "4px 10px", fontSize: "var(--fs-small)", borderRadius: 6,
                    border: "1px solid #1e3a5f", background: "transparent",
                    color: "var(--text-secondary)", cursor: "pointer", whiteSpace: "nowrap",
                  }}
                  title="Discard the preview and go back to your saved default."
                >
                  Reset
                </button>
              </>
            )}
          </div>
        </div>

      {/* Section 1: Sourcing */}
      <div>
        <WithdrawalSectionHeader
          open={openSourcing}
          onToggle={() => setOpenSourcing(v => !v)}
          color="var(--accent-teal)"
          question="Where does each year's spending come from?"
          subtitle={`Account-by-account sourcing — ${resolveDrawOrder(p.orderingMode, p.withdrawalOrder).map((b) => BUCKET_LABELS_SHORT[b]).join(" → ")} — with tax landmines flagged`}
          badge={savedPlanBadge}
        />
        {/* Guardrails live inside the collapsible now — they're only
            revealed when the green sourcing section is expanded, alongside
            the waterfall table they shape. The order control sits above the
            guardrails strip since ordering is the outer sequence and the
            guardrails are inner caps. */}
        {openSourcing && (
          <div style={{ paddingLeft: 4 }}>
            <AccountDrawOrder p={p} onAssumptionChange={onAssumptionChange} />
            <SourcingGuardrails p={p} onAssumptionChange={onAssumptionChange} summary={waterfall.summary} />
            <WaterfallPlanView p={p} result={waterfall} />
          </div>
        )}
      </div>

      {/* Section 2: Strategy pacing */}
      <div>
        <WithdrawalSectionHeader
          open={openStrategy}
          onToggle={() => setOpenStrategy(v => !v)}
          color="var(--accent-gold)"
          question="How does my chosen strategy pace spending year by year?"
          subtitle={`Preview how ${getStrategyLabel(previewStrategy)} paces spending — expand for the full year-by-year schedule and chart.`}
          badge={savedPlanBadge}
        />
        {openStrategy && (
          <div style={{ paddingLeft: 4 }}>
            <DeterministicWithdrawalView p={p} inf={inf} withdrawalStrategy={previewStrategy} smartRows={waterfall.smart.rows} mc={mc} />
          </div>
        )}
      </div>

      {/* design-authority (2026-09-09): moved from the top of the tab. A "not
          financial advice" banner needs to be reliably present, not first —
          footer placement is standard for this disclaimer type and stops it
          from eating the fold before the user sees any content. */}
      <SectionDisclaimer>
        This schedule is an illustration produced by a model — not a recommendation to
        withdraw from, convert, or roll over any specific account. It cannot see your full
        tax picture, and the tax, RMD, IRMAA, and penalty figures are estimates based on the
        assumptions you entered and on current law, both of which change. Do not act on
        these amounts without confirming them with a licensed tax adviser, CPA, or fiduciary.
      </SectionDisclaimer>
    </div>
  );
}

/**
 * Tax Calculation Details — the click-open breakdown of ONE year's tax on the
 * year-by-year withdrawal schedule.
 *
 * Replaces the `title=` tooltips that used to sit on the Fed Tax / State Tax /
 * IRMAA cells. Those were genuinely unreachable: `title=` renders nothing on
 * many desktop setups and does not exist at all on touch, so the explanation
 * that makes the column readable was invisible to every phone and tablet user.
 *
 * RULE 8 — read only, never recompute. Every figure below comes off the
 * buildWithdrawalWaterfall row this modal was opened from (r). The component
 * calls no tax function, holds no bracket table, and embeds no threshold:
 *   r.totInc / r.magi / r.taxableIncome / r.marginalBracket / r.taxSS / r.ss
 *   r.fedTax / r.ltcgTax / r.niit / r.stateTax / r.irmaa / r.irmaaFull
 *   r.earlyPenalty / r.effectiveRate / r.realizedGain
 *   r.fromCash / r.fromTaxable / r.fromPretax / r.rmd / r.fromRoth
 *   r.annuityRental / r.otherIncome / r.conversionAmount / r.conversionTax
 *   r.cashEnd / r.taxableEnd / r.pretaxEnd / r.rothEnd / r.totalPort
 *   r.stdDedYr / r.bracketTopYr   (null outside Roth-conversion years)
 *
 * The two derivations that DO appear are decompositions of numbers the engine
 * already published, and they are labelled as such:
 *   - federal ordinary tax = r.fedTax − r.ltcgTax − r.niit. The engine returns
 *     fedTax as exactly that sum (buildWithdrawalWaterfall.js, `fedT`), so this
 *     splits a published total into its published parts rather than recomputing
 *     it. The long-standing Fed Tax tooltip used the same split.
 *   - Monthly = Annualized ÷ 12, a unit conversion of a displayed figure.
 */
function TaxDetailsModal({ r, p, dollarBasis, open, onClose }) {
  if (!r) return null;
  const money = (v) => fmtDollar(Math.round(Number(v) || 0));
  const monthly = (v) => fmtDollar(Math.round((Number(v) || 0) / 12));
  const mfj = filingStatusAt(p, r.age) !== "single";
  const stateLabel = p?.twoHousehold ? "Non-resident (no state tax)" : (p?.stateOfResidence || "NJ");

  const totInc   = Number(r.totInc) || 0;          // ordinary income (excludes LTCG)
  const magi     = Number(r.magi) || 0;            // totInc + realized gain
  const gain     = Number(r.realizedGain) || 0;
  const fedTax   = Number(r.fedTax) || 0;
  const ltcgTax  = Number(r.ltcgTax) || 0;
  const niit     = Number(r.niit) || 0;
  const stateTax = Number(r.stateTax) || 0;
  const irmaa    = Number(r.irmaa) || 0;
  const penalty  = Number(r.earlyPenalty) || 0;
  // Decomposition of the published fedTax (see header note) — never negative.
  const fedOrdinary = Math.max(0, fedTax - ltcgTax - niit);
  const totalLoaded = fedTax + stateTax + irmaa + penalty;
  // Gross income actually RECEIVED this year (spendable), summed from the same
  // source rows shown below — excludes the Roth conversion amount, which is an
  // internal pre-tax→Roth transfer, not spendable income (its tax IS in
  // totalLoaded). "After tax" = what you keep = gross received − total tax, the
  // basis the reference tool uses (Gross − Tax = After-Tax). MAGI was the wrong
  // base: a Roth-funded year (MAGI ≈ 0) showed ≈ $0 after-tax while the retiree
  // actually spent from tax-free Roth.
  const grossIncome = (Number(r.rmd) || 0) + (Number(r.fromPretax) || 0) + (Number(r.fromTaxable) || 0)
    + (Number(r.fromRoth) || 0) + (Number(r.ss) || 0) + (Number(r.annuityRental) || 0) + (Number(r.otherIncome) || 0);
  const afterTax    = grossIncome - totalLoaded;
  // SS inclusion, straight off the row's own two figures.
  const ssPct = (Number(r.ss) || 0) > 0 ? Math.round((Number(r.taxSS) || 0) / Number(r.ss) * 100) : 0;

  const section = { background: "var(--row-highlight)", border: "1px solid var(--tint-white-08)", borderRadius: 9, padding: 14, marginBottom: 12 };
  const secHead = { fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 10 };
  const tbl = { width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-label)" };
  const thC = { textAlign: "right", color: "var(--text-faint)", fontWeight: 600, fontSize: "var(--fs-small)", padding: "4px 6px", borderBottom: "1px solid var(--tint-white-10)" };
  const tdL = { textAlign: "left", color: "var(--text-secondary)", padding: "5px 6px" };
  const tdR = { textAlign: "right", fontFamily: "'JetBrains Mono',monospace", color: "var(--text-primary)", padding: "5px 6px", whiteSpace: "nowrap" };
  const balCell = { flex: 1, textAlign: "center", padding: "8px 4px" };
  const balLbl = { fontSize: "var(--fs-small)", color: "var(--text-faint)" };
  const balVal = { fontSize: "var(--fs-body)", fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: "var(--text-primary)", marginTop: 3 };

  // Income-source rows, assembled only from row fields. A source with no
  // dollars this year is dropped, so a 2031 RMD-free year doesn't render a
  // column of zeros.
  const sources = [
    r.rmd > 0 && { label: "Pre-Tax — RMD (forced)", amt: r.rmd, treat: "Ordinary income — required by law" },
    r.fromPretax > 0 && { label: "Pre-Tax — discretionary draw", amt: r.fromPretax, treat: "Ordinary income" },
    r.fromTaxable > 0 && { label: "Taxable brokerage", amt: r.fromTaxable, treat: gain > 0 ? `Capital gains on ${money(gain)} of realized gain` : "Cost basis only — no gain realized" },
    r.fromRoth > 0 && { label: "Roth / tax-free", amt: r.fromRoth, treat: "Tax-free — no bracket, SS or IRMAA impact" },
    r.ss > 0 && { label: "Social Security", amt: r.ss, treat: ssPct > 0 ? `${ssPct}% taxable (${money(r.taxSS)} included)` : "Not taxable this year" },
    r.annuityRental > 0 && { label: "Annuity / rental income", amt: r.annuityRental, treat: "Ordinary income" },
    r.otherIncome > 0 && { label: "Other income", amt: r.otherIncome, treat: "As entered" },
    r.conversionAmount > 0 && { label: "Roth conversion (above)", amt: r.conversionAmount, treat: `Ordinary income — adds ${money(r.conversionTax)} to this year's tax` },
  ].filter(Boolean);

  // Federal bracket fill — walk taxable income across THIS year's own indexed
  // bracket array (r.fedBrackets, straight from the engine's yearTax). Per
  // bracket, amount = overlap of [lo,hi] with [0, taxableIncome] and tax =
  // amount × rate — the exact same arithmetic progTax runs, so the column sums
  // to the engine's fedOrdinary. No bracket table or inflation factor is
  // rebuilt here (Rule 8): if the engine changed the ladder, this follows.
  const fedBr = Array.isArray(r.fedBrackets) ? r.fedBrackets : [];
  const ti    = Number(r.taxableIncome) || 0;
  const bracketFill = [];
  for (const b of fedBr) {
    if (ti <= b.lo) break;
    const amt = Math.min(ti, b.hi) - b.lo;
    bracketFill.push({ rate: b.rate, lo: b.lo, hi: b.hi, amt, tax: Math.round(amt * b.rate) });
  }
  const marginalPct  = Math.round((Number(r.marginalBracket) || 0) * 100);
  const deduction    = Number(r.deduction) || 0;
  const effOnTaxable = ti > 0 ? (fedOrdinary / ti) * 100 : 0;
  const bracketColor = (rate) => rate <= 0.12 ? "#22c55e" : rate <= 0.24 ? "var(--accent-gold)" : "#f87171";

  return (
    <InfoModal
      title={`Tax Calculation Details — Age ${r.age} (${r.yr})`}
      accent="var(--accent-teal)"
      maxWidth={760}
      bodyMaxHeight="62vh"
      open={open}
      onClose={onClose}
    >
      {/* Header — who, which rules, and the one number that matters */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
          Age {r.age} · {mfj ? "Married filing jointly" : "Single"} · {stateLabel}
          {dollarBasis ? ` · ${dollarBasis}` : ""}
        </div>
        <div style={{ border: "1px solid var(--positive)", borderRadius: 8, padding: "6px 12px", textAlign: "right", background: "rgba(16,185,129,0.07)" }}>
          <div style={{ fontSize: "var(--fs-h2)", fontWeight: 800, color: "var(--positive)", fontFamily: "'JetBrains Mono',monospace" }}>{money(afterTax)}</div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--positive)" }}>After tax</div>
        </div>
      </div>

      {/* 1. Portfolio balances — end-of-year, per bucket, from the row */}
      <div style={section}>
        <div style={secHead}>Portfolio Balances (end of {r.yr})</div>
        <div style={{ display: "flex", gap: 6 }}>
          <div style={balCell}><div style={balLbl}>Pre-Tax</div><div style={balVal}>{money(r.pretaxEnd)}</div></div>
          <div style={{ ...balCell, borderLeft: "1px solid var(--tint-white-07)" }}><div style={balLbl}>Roth</div><div style={{ ...balVal, color: "var(--accent-purple)" }}>{money(r.rothEnd)}</div></div>
          <div style={{ ...balCell, borderLeft: "1px solid var(--tint-white-07)" }}><div style={balLbl}>Taxable</div><div style={{ ...balVal, color: "var(--accent-gold)" }}>{money(r.taxableEnd)}</div></div>
          <div style={{ ...balCell, borderLeft: "1px solid var(--tint-white-07)" }}><div style={balLbl}>Cash</div><div style={{ ...balVal, color: "var(--chart-5)" }}>{money(r.cashEnd)}</div></div>
          <div style={{ ...balCell, borderLeft: "1px solid var(--tint-white-10)" }}><div style={balLbl}>Total</div><div style={balVal}>{money(r.totalPort)}</div></div>
        </div>
      </div>

      {/* 2. Income sources — annualized and monthly, with the treatment applied */}
      <div style={section}>
        <div style={secHead}>Income Sources</div>
        <table style={tbl}>
          <thead><tr><th style={{ ...thC, textAlign: "left" }}>Source</th><th style={thC}>Annualized</th><th style={thC}>Monthly</th><th style={{ ...thC, textAlign: "left" }}>Tax Treatment</th></tr></thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.label}>
                <td style={tdL}>{s.label}</td>
                <td style={tdR}>{money(s.amt)}</td>
                <td style={{ ...tdR, color: "var(--text-muted)" }}>{monthly(s.amt)}</td>
                <td style={{ ...tdL, fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{s.treat}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...tdL, fontWeight: 700, color: "var(--text-primary)", borderTop: "1px solid var(--tint-white-12)" }}>Total gross income</td>
              <td style={{ ...tdR, fontWeight: 700, borderTop: "1px solid var(--tint-white-12)" }}>{money(grossIncome)}</td>
              <td style={{ ...tdR, color: "var(--text-muted)", borderTop: "1px solid var(--tint-white-12)" }}>{monthly(grossIncome)}</td>
              <td style={{ ...tdL, fontSize: "var(--fs-small)", color: "var(--text-faint)", borderTop: "1px solid var(--tint-white-12)" }}>Received this year{r.conversionAmount > 0 ? " (excl. Roth conversion transfer)" : ""}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 3. How the taxable base was arrived at */}
      <div style={section}>
        <div style={secHead}>How This Year's Taxable Income Is Calculated</div>
        <table style={tbl}>
          <tbody>
            <tr><td style={tdL}>Ordinary income (pre-tax draws + RMD + taxable SS + rental/other)</td><td style={tdR}>{money(totInc)}</td></tr>
            <tr><td style={tdL}>+ Realized long-term capital gains</td><td style={tdR}>{money(gain)}</td></tr>
            <tr>
              <td style={{ ...tdL, fontWeight: 700, color: "#86efac", borderTop: "1px solid rgba(134,239,172,0.3)" }}>= MAGI (AGI before deductions)</td>
              <td style={{ ...tdR, fontWeight: 700, color: "#86efac", borderTop: "1px solid rgba(134,239,172,0.3)" }}>{money(magi)}</td>
            </tr>
            {r.stdDedYr != null && (
              <tr><td style={tdL}>− Deductions applied in {r.yr} (standard + senior bonus)</td><td style={tdR}>−{money(r.stdDedYr)}</td></tr>
            )}
            <tr><td style={{ ...tdL, fontWeight: 700, color: "var(--text-primary)", borderTop: "1px solid var(--tint-white-12)" }}>= Taxable income</td><td style={{ ...tdR, fontWeight: 700, borderTop: "1px solid var(--tint-white-12)" }}>{money(r.taxableIncome)}</td></tr>
          </tbody>
        </table>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.6, marginTop: 8 }}>
          {r.stdDedYr != null
            ? `Marginal bracket this year: ${Math.round((r.marginalBracket || 0) * 100)}%${r.bracketTopYr != null ? ` · bracket top ${money(r.bracketTopYr)} in ${r.yr} dollars` : ""}. Bracket tops are inflation-indexed, which is why this year's ceiling is above today's published figure — compare TAXABLE income against it, not the gross draw.`
            : `Marginal bracket this year: ${Math.round((r.marginalBracket || 0) * 100)}%. The deduction and bracket ceiling for this year aren't stored on a row without a Roth conversion, so only the resulting taxable income is shown here — it is the engine's own figure, not a re-derivation.`}
        </div>
      </div>

      {/* 4. The tax calculation itself */}
      <div style={section}>
        <div style={secHead}>Tax Calculation</div>
        <table style={tbl}>
          <tbody>
            <tr><td style={tdL}>Federal tax on ordinary income</td><td style={tdR}>{money(fedOrdinary)}</td></tr>
            <tr><td style={tdL}>Federal long-term capital gains tax{ltcgTax === 0 && gain > 0 ? " (0% bracket — gain fits under the threshold)" : ""}</td><td style={tdR}>{money(ltcgTax)}</td></tr>
            <tr><td style={tdL}>Net investment income tax (NIIT 3.8%)</td><td style={tdR}>{money(niit)}</td></tr>
            <tr><td style={tdL}>State tax ({p?.twoHousehold ? "not applied — non-resident" : (p?.stateOfResidence || "NJ")}{p?.stateOfResidence === "NJ" && (Number(r.taxSS) || 0) > 0 && !p?.twoHousehold ? " — SS exempt" : ""})</td><td style={tdR}>{money(stateTax)}</td></tr>
            <tr>
              <td style={tdL}>Medicare IRMAA surcharge <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>(from {r.yr - 2} MAGI — 2-yr lookback)</span></td>
              <td style={tdR}>{money(irmaa)}</td>
            </tr>
            {penalty > 0 && (
              <tr><td style={{ ...tdL, color: "#f87171" }}>Early-withdrawal penalty (IRC §72(t), 10%) — a separate additional tax</td><td style={{ ...tdR, color: "#f87171" }}>{money(penalty)}</td></tr>
            )}
            <tr>
              <td style={{ ...tdL, fontWeight: 700, color: "var(--text-primary)", borderTop: "1px solid var(--tint-white-12)" }}>= Total tax{r.earlyPenalty > 0 ? " (incl. penalty)" : ""}</td>
              <td style={{ ...tdR, fontWeight: 700, borderTop: "1px solid var(--tint-white-12)" }}>{money(totalLoaded)}</td>
            </tr>
            <tr>
              <td style={{ ...tdL, color: "var(--text-muted)" }}>Effective rate on {money(magi)} of income <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>(federal + state, excl. IRMAA &amp; penalty)</span></td>
              <td style={{ ...tdR, color: "var(--text-muted)" }}>{((Number(r.effectiveRate) || 0) * 100).toFixed(1)}%</td>
            </tr>
            <tr><td style={{ ...tdL, fontWeight: 700, color: "var(--positive)" }}>= After tax</td><td style={{ ...tdR, fontWeight: 700, color: "var(--positive)" }}>{money(afterTax)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* 5. Federal Tax Bracket Analysis — how taxable income stacks across the
             year's inflation-indexed brackets. Bar + table sum to fedOrdinary. */}
      {bracketFill.length > 0 && ti > 0 && (
        <div style={section}>
          <div style={secHead}>Federal Tax Bracket Analysis</div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.55, marginBottom: 10 }}>
            Income ranges are inflation-adjusted to {r.yr}. Compare your <strong>taxable</strong> income ({money(ti)}) against them — the IRS widens brackets each year, so this year's ranges sit above today's published figures.
          </div>
          <div style={{ display: "flex", height: 26, borderRadius: 6, overflow: "hidden", marginBottom: 6, background: "var(--tint-white-05)" }}>
            {bracketFill.map((f) => (
              <div key={f.rate} title={`${Math.round(f.rate * 100)}% bracket — ${money(f.amt)} taxed here`} style={{ width: `${(f.amt / ti) * 100}%`, background: bracketColor(f.rate), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "var(--fs-small)", fontWeight: 800, color: "#04120a", minWidth: 24 }}>
                {Math.round(f.rate * 100)}%
              </div>
            ))}
          </div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 12 }}>Your taxable income: {money(ti)}</div>
          <table style={tbl}>
            <thead><tr><th style={{ ...thC, textAlign: "left" }}>Tax Rate</th><th style={{ ...thC, textAlign: "left" }}>Income Range ({r.yr} $)</th><th style={thC}>Amount in Bracket</th><th style={thC}>Tax</th></tr></thead>
            <tbody>
              {bracketFill.map((f) => (
                <tr key={f.rate}>
                  <td style={{ ...tdL, color: bracketColor(f.rate), fontWeight: 700 }}>{Math.round(f.rate * 100)}%</td>
                  <td style={tdL}>{money(f.lo)} – {f.hi === Infinity ? "no ceiling" : money(f.hi)}</td>
                  <td style={tdR}>{money(f.amt)}</td>
                  <td style={tdR}>{money(f.tax)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...tdL, fontWeight: 700, color: "var(--text-primary)", borderTop: "1px solid var(--tint-white-12)" }}>Total</td>
                <td style={{ ...tdL, color: "var(--text-muted)", borderTop: "1px solid var(--tint-white-12)" }}>{bracketFill.length} bracket{bracketFill.length === 1 ? "" : "s"} filled</td>
                <td style={{ ...tdR, fontWeight: 700, borderTop: "1px solid var(--tint-white-12)" }}>{money(ti)}</td>
                <td style={{ ...tdR, fontWeight: 700, borderTop: "1px solid var(--tint-white-12)" }}>{money(fedOrdinary)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: 10, background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.25)", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-purple)", marginBottom: 6 }}>◇ Key Insights</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.8 }}>
              Marginal tax rate (top bracket reached): <strong style={{ color: "var(--text-primary)" }}>{marginalPct}%</strong><br />
              Effective rate on taxable income: <strong style={{ color: "var(--text-primary)" }}>{effOnTaxable.toFixed(1)}%</strong>
              {deduction > 0 && (<><br />Tax saved from deductions: <strong style={{ color: "var(--positive)" }}>~{money(Math.round(deduction * (Number(r.marginalBracket) || 0)))}</strong> <span style={{ color: "var(--text-faint)" }}>({money(deduction)} deduction × {marginalPct}% marginal)</span></>)}
            </div>
          </div>
        </div>
      )}

      {/* 6. Monthly summary — the same Gross − Tax = After-Tax identity as the
             reference tool, per month. All three are ÷12 of figures above; the
             identity holds because afterTax is defined as grossIncome − totalLoaded. */}
      <div style={section}>
        <div style={secHead}>Monthly Summary</div>
        <div style={{ display: "flex", gap: 6 }}>
          <div style={balCell}>
            <div style={balLbl}>Gross Income</div>
            <div style={balVal}>{monthly(grossIncome)}</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 2 }}>{money(grossIncome)}/yr</div>
          </div>
          <div style={{ ...balCell, borderLeft: "1px solid var(--tint-white-07)" }}>
            <div style={balLbl}>Tax</div>
            <div style={{ ...balVal, color: "#f87171" }}>−{monthly(totalLoaded)}</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 2 }}>−{money(totalLoaded)}/yr</div>
          </div>
          <div style={{ ...balCell, borderLeft: "1px solid var(--tint-white-10)" }}>
            <div style={balLbl}>After-Tax</div>
            <div style={{ ...balVal, color: "var(--positive)" }}>{monthly(afterTax)}</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 2 }}>{money(afterTax)}/yr</div>
          </div>
        </div>
      </div>

      {/* Disclosures — every total above shows what it was built from */}
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.65, borderTop: "1px solid var(--tint-white-08)", paddingTop: 10 }}>
        <div style={{ marginBottom: 6 }}>
          <strong style={{ color: "var(--text-secondary)" }}>What's inside these numbers.</strong>{" "}
          {Number(r.ss) > 0
            ? `Social Security is ${ssPct}% taxable this year (${money(r.taxSS)} of ${money(r.ss)})${r.landmines?.ssTorpedo ? " — the SS torpedo is active, meaning provisional income crossed the threshold where 85% becomes taxable" : ""}. `
            : "Social Security is not being taxed this year. "}
          {irmaa > 0
            ? `IRMAA is charged this year because your ${r.yr - 2} MAGI exceeded the base Medicare tier; this year's income affects premiums in ${r.yr + 2}. `
            : "No IRMAA surcharge this year. "}
          {gain > 0 && ltcgTax === 0
            ? `The realized gain of ${money(gain)} fell inside the 0% long-term capital gains bracket, so no federal tax is due on it. `
            : ""}
          {r.rothReserveBroken ? `The Roth emergency reserve was breached this year. ` : ""}
        </div>
        <div>
          These are model estimates from the same tax engine that produced the schedule row above — a second, independent calculation is never run for this panel.
          State tax is an estimate; actual liability depends on items not modeled here (itemized deductions, credits, local taxes). Consult a qualified tax professional.
        </div>
      </div>
    </InfoModal>
  );
}

/**
 * Column-group segments for the year-by-year withdrawal table.
 *
 * `key`  — unique (React key, and the segment identity)
 * `cg`   — the CSS group tag the columns carry; a view hides whole `cg`s
 * `span` — how many columns the segment owns; constant, since hiding is per
 *          group rather than per column, so a visible header span is always right
 * `view` — which lens this segment belongs to (null = always visible)
 *
 * Order matches the table's left-to-right reading (the funding identity), so the
 * group header can never disagree with the columns beneath it.
 */
const WF_SEGMENTS = [
  { key: "time",   cg: "time",   span: 1, label: "Timeline",                     view: null,     cls: "" },
  { key: "spend",  cg: "spend",  span: 2, label: "Spending",                     view: "flows",  cls: "wf-grp-s" },
  { key: "income", cg: "income", span: 2, label: "Income — offsets spend first", view: "flows",  cls: "wf-grp-i" },
  { key: "draw",   cg: "draw",   span: 9, label: "Withdrawals — in draw order",  view: "flows",  cls: "wf-grp-d" },
  { key: "conv",   cg: "conv",   span: 1, label: "Roth",                         view: "flows",  cls: "wf-grp-d" },
  { key: "b1",     cg: "bal",    span: 1, label: "Bucket 1",                     view: "bal",    cls: "wf-grp-b" },
  { key: "tax",    cg: "tax",    span: 4, label: "Taxes",                        view: "tax",    cls: "wf-grp-t" },
  { key: "risk",   cg: "risk",   span: 2, label: "Risk checks",                  view: "tax",    cls: "wf-grp-t" },
  { key: "total",  cg: "bal",    span: 1, label: "Total",                        view: "bal",    cls: "wf-grp-b" },
];

function WaterfallPlanView({ p, result }) {
  const [mode, setMode] = useState("smart");
  // One dialog, pointed at whichever year's Tax details was clicked. The old
  // cells carried `title=` tooltips for this — a string that many desktop
  // setups never render and touch devices cannot open at all, so the breakdown
  // behind Fed Tax / State Tax / IRMAA was unreachable for a whole class of
  // users. Click is the pattern that works everywhere.
  const [taxRow, setTaxRow] = useState(null);
  // Which column groups the year-by-year table shows. "all" is the full 23-column
  // schedule; the others are lenses onto the same rows (see .wf-tbl CSS).
  const [wfView, setWfView] = useState("all");
  const { smart, naive, summary } = result;
  const rows = mode === "smart" ? smart.rows : naive.rows;

  // Bucket 1's true dollar share, summed across whichever categories it's
  // tagged in — NOT "pick one category and show its whole balance" (the old
  // b1Cats approach), which silently dropped a second category entirely and
  // overstated Bucket 1 whenever its category also held Bucket 2/3 money
  // (e.g. a taxable account split 40% B1 / 60% B2 showed the FULL taxable
  // balance as "B1 End", so it could never visibly deplete). Reuses the
  // exact fraction math the 3-Bucket engine itself uses (bucketStrategy.js),
  // so this display can't disagree with what the simulation actually did.
  const b1Pieces = expandAccountBuckets(p.accounts).filter(piece => piece.bucket === 1);
  const hasB1 = b1Pieces.length > 0;
  const b1FracsByCategory = bucketFractionsByCategory(p.accounts);

  // Operator cells — visually wire the withdrawal columns into the equation they satisfy
  const opThStyle = { padding: "0 3px", color: "var(--text-faint)", fontWeight: 400, fontSize: "var(--fs-small)", textAlign: "center" };
  const opTdStyle = { padding: "0 3px", color: "var(--text-faint)", fontWeight: 600, fontSize: "var(--fs-label)", textAlign: "center" };

  const btnStyle = (active) => ({
    padding: "5px 14px", fontSize: "var(--fs-label)", borderRadius: 6, border: "none",
    cursor: "pointer",
    background: active ? "var(--tint-teal-600-25)" : "transparent",
    color: active ? "var(--accent-teal)" : "var(--text-faint)",
  });

  const chartData = rows.map(r => ({
    age: r.age,
    Cash:    Math.round(r.fromCash),
    Taxable: Math.round(r.fromTaxable),
    // Pre-Tax = TOTAL pretax outflow (forced RMD + discretionary draw). The RMD
    // funds spending like any other dollar, so omitting it left a hole in the
    // stack during RMD years even though the money was flowing out of the IRA.
    "Pre-Tax": Math.round(r.fromPretax + r.rmd),
    Roth:    Math.round(r.fromRoth),
    "Fed Tax":   Math.round(r.fedTax),
    "State Tax": Math.round(r.stateTax),
    "IRMAA":     Math.round(r.irmaa),
    "Roth Conversion": Math.round(r.conversionAmount),
  }));

  // earlyPenalty wasn't included here, so a row could show BOTH the ⛔ early-
  // withdrawal-penalty icon (gated on r.earlyPenalty > 0, below) AND the ✓
  // "no landmines" checkmark side by side — contradictory, and it also meant
  // the row didn't get the red highlight every other landmine type gets.
  const anyLandmine = (r) => r.landmines.ssTorpedo || r.landmines.irmaaTriggered || r.landmines.rmdActive || r.earlyPenalty > 0;
  const anyConversion = rows.some(r => r.conversionAmount > 0);
  /* Withdrawal rate — one definition.
   *
   * A user reported the WR column read 17.8% in a year whose actual draw was
   * $26K. He worked out why himself — 17.8% was his $100,000 spending over
   * the portfolio, not his withdrawal.
   *
   * The column divided spending by the end-of-year portfolio, wrong twice over:
   *   - Numerator was spending, not the withdrawal. For anyone with
   *     meaningful income (his pension covered about $81K of a $100K spend)
   *     those differ enormously, and the column claimed to be "withdrawal
   *     rate vs portfolio." A plan drawing under 5% displayed as drawing
   *     17.8% — a number that reads as severe distress and could push
   *     someone into cutting spending they don't need to cut.
   *   - Denominator was `totalPort`, the end-of-year balance, after the
   *     year's draw already came out. Dividing by the smaller, post-draw
   *     number inflates the rate further.
   *
   * On top of that, the same tab already computed this correctly for the
   * "Avg. Withdrawal Rate" card (`totalWithdrawal / start-of-year port`), so
   * one page carried two different withdrawal rates that disagreed by a
   * factor of four. Both now come from `wrAt` below — one definition can't
   * drift from itself.
   *
   * Numerator is `totalWithdrawal` = rmd + cash + taxable + pretax + roth:
   * the actual gross portfolio outflow, the same figure the "Total Draw"
   * column shows.
   */
  const portAtRetire = accumulateToRetirement(p).total;
  const wrAt = (i) => {
    const r = rows[i];
    if (!r) return null;
    const startPort = i === 0 ? portAtRetire : rows[i - 1].totalPort;
    return startPort > 0 ? r.totalWithdrawal / startPort : null;
  };

  /* The GK guardrail band is ±20% around the first year's actual withdrawal
   * rate, which is what Guyton-Klinger actually compares against — the
   * initial WR. It used to be anchored to `p.sp / p.port`, a spending rate,
   * so the band and the value being banded were different quantities. Falls
   * back to the user's own safe-withdrawal-rate benchmark when year one has
   * no draw at all. */
  const initialWR = wrAt(0) || (p.safeWithdrawalRate ?? 0.04);
  const rowWRColor = (i) => {
    const wr = wrAt(i);
    if (wr == null) return "var(--text-secondary)";
    return wr > initialWR * 1.2 ? "var(--chart-1)" : wr < initialWR * 0.8 ? "var(--accent-gold)" : "var(--positive)";
  };

  // "Average retirement withdrawal rate" / "Out of money date" — Boldin-style
  // decision metrics, computed from this scenario's own rows (no separate model).
  const wrSeries = rows.map((_, i) => wrAt(i)).filter(v => v != null);
  const avgWithdrawalRate = wrSeries.length ? wrSeries.reduce((a, b) => a + b, 0) / wrSeries.length : 0;
  const depletionRow = rows.find(r => r.totalPort <= 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Summary cards — auto-fit so an odd card count (this grid gained 2
          conditional 3-Bucket cards) fills each row edge-to-edge instead of
          a fixed repeat(3,1fr) leaving a stranded partial row. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
        {/* "Smart" collided with the unrelated "Smart Waterfall" PACING
            strategy option in the Section 2 dropdown — same word, two
            different things (this card is about DRAW ORDER, not how much to
            spend), and a user tripped on it. Relabeled to match the "Your
            plan" / "No plan" wording the toggle below already uses, instead
            of inventing a third term. "waterfall" (engine jargon) replaced
            with the actual order, spelled out. */}
        <div className="met">
          <div className="ml">Your Plan's Lifetime Tax</div>
          <div className="mv" style={{ color: "var(--positive)", fontSize: "var(--fs-h2)" }}>{fmtDollar(summary.lifetimeTaxSmart)}</div>
          <div className="ms">drawn cash → taxable → pre-tax → Roth, in that order</div>
        </div>
        <div className="met">
          <div className="ml">Tax Savings vs No Plan</div>
          <div className="mv" style={{ color: summary.taxSavings > 0 ? "var(--positive)" : "var(--chart-1)", fontSize: "var(--fs-h2)" }}>
            {summary.taxSavings >= 0 ? "+" : ""}{fmtDollar(summary.taxSavings)}
          </div>
          <div className="ms">vs draining pre-tax first, no plan</div>
        </div>
        <div className="met">
          <div className="ml">Roth at Age {p.endAge || 90}</div>
          <div className="mv" style={{ color: "var(--accent-purple)", fontSize: "var(--fs-h2)" }}>{fmtDollar(summary.finalRothSmart)}</div>
          <div className="ms">your plan · {fmtDollar(summary.finalRothNaive)} without a plan</div>
        </div>
        <div className="met">
          <div className="ml">IRMAA Years Triggered</div>
          <div className="mv" style={{ color: summary.irmaaYearsTriggered > 0 ? "var(--chart-3)" : "var(--positive)", fontSize: "var(--fs-h2)" }}>
            {summary.irmaaYearsTriggered}
          </div>
          <div className="ms">{summary.ssTorpedoYears} SS torpedo yrs</div>
        </div>
        <div className="met">
          <div className="ml">Avg. Withdrawal Rate</div>
          <div className="mv" style={{ color: avgWithdrawalRate > initialWR * 1.2 ? "var(--chart-1)" : "var(--accent-teal)", fontSize: "var(--fs-h2)" }}>
            {(avgWithdrawalRate * 100).toFixed(1)}%
          </div>
          <div className="ms">total withdrawal ÷ prior-year portfolio</div>
        </div>
        <div className="met">
          <div className="ml">Portfolio Depletion</div>
          <div className="mv" style={{ color: depletionRow ? "var(--chart-1)" : "var(--positive)", fontSize: "var(--fs-h2)" }}>
            {depletionRow ? `Age ${depletionRow.age}` : "Never"}
          </div>
          <div className="ms">{depletionRow ? `(${depletionRow.yr})` : `lasts to age ${p.endAge || 90}`}</div>
        </div>
      </div>

      {/* Toggle */}
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginRight: 4 }}>View:</span>
        <button style={btnStyle(mode === "smart")} onClick={() => setMode("smart")}>Your plan</button>
        <button style={btnStyle(mode === "naive")} onClick={() => setMode("naive")}>No plan (pretax first, uncapped)</button>
        {mode === "naive" && (
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginLeft: 8 }}>
            The common default — pretax drains first, no bracket ceiling, Roth used last
          </span>
        )}
      </div>

      {/* Stacked bar chart */}
      <div className="chart-card">
        <div className="ct">Annual Withdrawals by Source — {mode === "smart" ? "Your plan" : "No plan"}</div>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 8 }}>
          Stacks show where each year's spending comes from; Fed Tax / State Tax / IRMAA sit on top (match the table columns exactly)
          {anyConversion && mode === "smart" && <> · Roth Conversion (purple) is a pretax→Roth transfer, not spending — shown for visibility</>}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
            <XAxis dataKey="age" stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} />
            <YAxis stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} tickFormatter={v => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
            <Tooltip content={<Tip />} />
            <Legend wrapperStyle={{ fontSize: "var(--fs-small)" }} />
            <Bar dataKey="Cash"     stackId="a" fill="var(--text-muted)" />
            <Bar dataKey="Taxable"  stackId="a" fill="var(--chart-10)" />
            <Bar dataKey="Pre-Tax"  stackId="a" fill="var(--accent-gold)" />
            <Bar dataKey="Roth"     stackId="a" fill="var(--positive)" />
            <Bar dataKey="Fed Tax"   stackId="a" fill="var(--negative)" />
            <Bar dataKey="State Tax" stackId="a" fill="#ec4899" />
            <Bar dataKey="IRMAA"     stackId="a" fill="#f43f5e" />
            <Bar dataKey="Roth Conversion" stackId="a" fill="var(--accent-purple)" radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Year-by-year table */}
      <div className="chart-card" style={{ overflowX: "auto" }}>
        <div className="ct">Year-by-Year Withdrawal Schedule</div>
        {/* Which lens the table is showing. 23 columns is more than anyone can
            read across, so the same rows are offered grouped: everything, just
            the flows, just the taxes, or just the balances. The funding identity
            never changes — only how much of it is on screen at once. */}
        <div className="wf-views">
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginRight: 2 }}>Show:</span>
          {[["all", "All columns"], ["flows", "Flows (in → out)"], ["tax", "Taxes & risk"], ["bal", "Balances"]].map(([k, label]) => (
            <button key={k} type="button" className={wfView === k ? "on" : ""} onClick={() => setWfView(k)}>{label}</button>
          ))}
        </div>
        {wfView !== "all" && (
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
            {wfView === "flows" && "Income is applied first; only the shortfall becomes a withdrawal. Draw columns are in the order the waterfall uses them."}
            {wfView === "tax" && "Only the tax columns and the two risk checks — the funding columns are hidden so the tax picture fits on one screen."}
            {wfView === "bal" && "Bucket 1 and the total portfolio at each year end. Switch to Flows to see what moved them."}
            {" "}The full schedule is always one click away under <strong style={{ color: "var(--text-secondary)" }}>All columns</strong>.
          </div>
        )}
          LEGEND:
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
         ⚡ SS Torpedo &nbsp;|&nbsp; 💊 IRMAA triggered &nbsp;|&nbsp; 📋 RMDs active &nbsp;|&nbsp;
          Bracket cap reason shown in Pre-Tax column
          <br />
          Columns read left→right in draw order. Funding identity each year: <strong>Income + Cash + Taxable + Pre-Tax (incl. RMD)  + Roth = Spending + Housing + Carveouts + Planned one-off expenses + Fed/State/IRMAA taxes</strong> (Income = Social Security + Pension/Other + Annuity/Rental — All of it offsets spending before any draw) (Any RMD forced out beyond that need is reinvested into Taxable — hover the Pre-Tax cell for the split). Hover the Spending cell for that year's full need breakdown. In a Roth-conversion year, Cash/Taxable/Pre-Tax also carry a small <span style={{ color: "var(--accent-purple)" }}>+conv $X</span> badge — the share of that year's conversion tax paid from that account. Total Draw and the identity above include it.
        </div>
        <table className="roth-tbl wf-tbl" data-view={wfView}>
          <thead>
            {/* Group header. Column counts are the group's own column count and
                are constant, because a view hides whole groups — so a visible
                group's span is always right without recomputation. */}
            <tr>
              {WF_SEGMENTS.map((seg) => {
                if (seg.key === "conv" && !anyConversion) return null;
                return (
                  <th key={seg.key} colSpan={seg.span} data-cg={seg.cg} className={`wf-grp ${seg.cls || ""}`}>
                    {seg.label}
                  </th>
                );
              })}
            </tr>
            <tr>
              <th data-cg="time">Age</th>
              <ThInfo data-cg="spend" tip={"Target spending this year.\n\nOpen any row's Spending cell for the full need breakdown — housing, carveouts, planned one-off costs, other income and taxes."}>Spending</ThInfo>
              <ThInfo data-cg="spend" style={opThStyle} tip={"Spending is funded by the income + draw columns to the right — see the funding identity above the table."}>←</ThInfo>
              {/* This is the aggregate whose components a user couldn't
                  find, and its explanation used to be hover-only, so absent
                  on every phone. Click-open modal, visible affordance. */}
              <th data-cg="income">
                Income{" "}
                <InfoModal title="Income — what this column contains" accent="var(--accent-teal)"
                  trigger={<span style={{ cursor: "pointer", display: "inline-flex", color: "var(--accent-teal)" }}><InfoIcon size={12} /></span>}>
                  <p style={{ margin: "0 0 10px" }}>
                    Money that arrives whether or not you sell anything. It is used <strong style={{ color: "var(--text-primary)" }}>first</strong>,
                    before any portfolio draw — only the shortfall becomes a withdrawal.
                  </p>
                  <p style={{ margin: "0 0 6px", color: "var(--text-primary)" }}><strong>This column is the sum of exactly three things:</strong></p>
                  <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
                    <li><strong style={{ color: "#c4b5fd" }}>Social Security</strong> — your benefit, once claiming starts</li>
                    <li><strong style={{ color: "#fde047" }}>Pension/Other</strong> — any stream you added under Other Income (pensions, annuities, part-time work, royalties)</li>
                    <li><strong style={{ color: "#93c5fd" }}>Annuity/Rental</strong> — property and Airbnb income, after the reliability haircut</li>
                  </ul>
                  <p style={{ margin: 0 }}>
                    Those three names are used identically on the Income &amp; Expenses chart and in the
                    Year-by-Year Schedule, where they appear as separate columns instead of one total.
                  </p>
                </InfoModal>
              </th>
              <th data-cg="income" style={opThStyle}>+</th>
              <ThInfo data-cg="draw" tip={"Step 3 — drawn first from the portfolio"}>Cash</ThInfo>
              <th data-cg="draw" style={opThStyle}>+</th>
              <ThInfo data-cg="draw" tip={"Step 4 — drawn after cash is exhausted"}>Taxable</ThInfo>
              <th data-cg="draw" style={opThStyle}>+</th>
              <ThInfo data-cg="draw" tip={"Step 5 — TOTAL pretax outflow this year: forced RMD + discretionary draw (capped at your bracket-ceiling target). This is the amount to actually withdraw from your IRA/401k."}>Pre-Tax</ThInfo>
              <th data-cg="draw" style={opThStyle}>+</th>
              <ThInfo data-cg="draw" tip={"Step 6 — last resort; emergency reserve floor maintained"}>Roth</ThInfo>
              <th data-cg="draw" style={opThStyle}>=</th>
              <ThInfo data-cg="draw" tip={"Total leaving your portfolio this year: Cash + Taxable + Pre-Tax (incl. RMD) + Roth. The single number to enact — it covers spending, housing, carveouts, and all taxes."}>Total Draw</ThInfo>
              {anyConversion && (
                <ThInfo data-cg="conv" tip={"Roth conversion this year (pinned in Conversion Plan, or bracket-fill if set in Withdrawal Order). Stacks on top of this year's spending withdrawal as ordinary income — Fed/State/IRMAA columns reflect the combined total."}>Roth Conv</ThInfo>
              )}
              <ThInfo data-cg="bal" style={{ borderLeft: "1px solid var(--tint-slate-400-15)" }} tip={"Bucket 1 is the safe cash reserve — the slice of your accounts tagged for near-term spending, tracked separately from Bucket 2 (income bridge) and Bucket 3 (long-term growth).\n\nThis column is Bucket 1's true dollar share of the portfolio at the end of each year, summed across every account/category it's tagged in — not one category's whole balance. A taxable account split 40% Bucket 1 / 60% Bucket 2 only contributes its 40% share here.\n\n\"—\" means no account is tagged Bucket 1 at all. \"$0\" means Bucket 1 has actually depleted. Hover any row's value for that year's exact calculation."}>B1 End</ThInfo>
              <th data-cg="tax">Fed Tax</th><th data-cg="tax">State Tax</th><th data-cg="tax">IRMAA</th><th data-cg="tax">Eff %</th>
              <ThInfo data-cg="risk" tip={"What share of the portfolio this year's DRAW represents: Total Draw ÷ the portfolio at the START of the year.\n\nNot your spending rate. If income covers most of your spending, this stays low even when spending is high — which is the point of the column.\n\nGreen means within ±20% of your first-year rate, the Guyton-Klinger guardrail band. Amber = well below it, red = well above."}>WR <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>(of draw)</span></ThInfo>
              <th data-cg="risk">
                <LandmineTip
                  emoji="💣"
                  label="Tax Landmines"
                  color="var(--chart-1)"
                  detail="Hidden tax traps that quietly increase your bill. ⚡ SS Torpedo — too much income makes 85% of Social Security taxable. 💊 IRMAA — income above ~$218,000 MFJ triggers Medicare premium surcharges. 📋 RMD — forced pretax distributions create mandatory taxable income you cannot defer. Hover each row icon for year-specific details."
                />
                {" "}Landmines
              </th>
              <th data-cg="bal">Port End</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, rowIdx) => {
              // null = no account is tagged Bucket 1 at all ("—" is correct:
              // there's nothing to show). 0 = Bucket 1 exists and has
              // genuinely depleted — the whole point of tracking it, so it
              // must read as "$0", not the same dash as "not applicable".
              const b1End = hasB1
                ? bucketBalancesForRow(r, b1FracsByCategory)[1]
                : null;
              // Every term, not just the final one: each contributing
              // category's own "Start − Draw [− Conv Tax] [+ reinvested] =
              // pre-growth, × (1+rate) = End" line (straight off the
              // cashStart/gr/etc. fields buildWithdrawalWaterfall.js now
              // emits — see its row-push comment — never re-derived here),
              // THEN that category's "End × B1% = contribution" tag line.
              // The general explanation of what Bucket 1 IS lives in the
              // column header's InfoModal (click the ⓘ next to "B1 End");
              // this is the per-row arithmetic only. Categories with 0%
              // tagged Bucket 1 (e.g. Roth, usually) are omitted so the
              // tooltip doesn't pad itself with unused lines.
              const b1Breakdown = hasB1 ? (() => {
                const CAT_META = {
                  cash:    { label: "Cash",    start: r.cashStart,    end: r.cashEnd,    draw: r.fromCash,    gr: r.cashGr,
                             subtract: [["Conv Tax", r.convTaxFromCash]], add: [] },
                  taxable: { label: "Taxable", start: r.taxableStart, end: r.taxableEnd, draw: r.fromTaxable, gr: r.gr,
                             subtract: [["Conv Tax", r.convTaxFromTaxable]],
                             add: [["RMD Surplus", r.rmdSurplus], ["Income Surplus", r.surplusToTaxable]] },
                  pretax:  { label: "Pre-Tax", start: r.pretaxStart,  end: r.pretaxEnd,  draw: r.fromPretax,  gr: r.gr,
                             subtract: [["Conversion Outflow", r.convPretaxOutflow]], add: [] },
                  roth:    { label: "Roth",    start: r.rothStart,    end: r.rothEnd,    draw: r.fromRoth,    gr: r.gr,
                             subtract: [], add: [["Conversion In", r.convToRoth]] },
                };
                const blocks = Object.keys(CAT_META)
                  .map((cat) => {
                    const frac = b1FracsByCategory[cat]?.[1] || 0;
                    const m = CAT_META[cat];
                    if (frac <= 0 || m.start == null) return null;
                    let line = `${m.label} Start ${fmtDollar(m.start)} − Draw ${fmtDollar(m.draw || 0)}`;
                    let preGrowth = m.start - (m.draw || 0);
                    for (const [termLabel, val] of m.subtract) {
                      if (val > 0) { line += ` − ${termLabel} ${fmtDollar(val)}`; preGrowth -= val; }
                    }
                    for (const [termLabel, val] of m.add) {
                      if (val > 0) { line += ` + ${termLabel} ${fmtDollar(val)}`; preGrowth += val; }
                    }
                    preGrowth = Math.max(0, preGrowth);
                    line += ` = ${fmtDollar(preGrowth)}`;
                    if (m.gr != null) line += ` × (1 + ${(Math.round(m.gr * 1000) / 10)}% growth)`;
                    line += ` = ${m.label} End ${fmtDollar(m.end)}`;
                    const tagLine = `${m.label} End ${fmtDollar(m.end)} × ${Math.round(frac * 100)}% tagged B1 = ${fmtDollar(Math.round((m.end || 0) * frac))}`;
                    return `${line}\n${tagLine}`;
                  })
                  .filter(Boolean);
                if (!blocks.length) return null;
                // Only state the sum explicitly when more than one category
                // contributes — with one block its last line already ends
                // in the total, so restating it reads as "$X = $X".
                return blocks.length > 1 ? `${blocks.join("\n\n")}\n\nTotal B1 End = ${fmtDollar(b1End)}` : blocks[0];
              })() : null;
              return (
              <tr key={r.age} style={{ background: anyLandmine(r) ? "var(--tint-red-07)" : undefined }}>
                <td data-cg="time">{r.age}</td>
                <td data-cg="spend" style={{ textAlign: "right" }}
                    title={`Spending ${fmtDollar(r.spending)}`
                      // The smile quietly re-scales this cell — say so.
                      + (r.smileFactor != null && Math.abs(r.smileFactor - 1) >= 0.005
                          ? ` (spending-curve ${r.smileFactor > 1 ? "+" : "−"}${Math.abs(Math.round((r.smileFactor - 1) * 1000) / 10)}% applied to your ${fmtDollar(r.smileBase)} target)`
                          : "")
                      + (r.housingCost > 0 ? ` + housing ${fmtDollar(r.housingCost)}` : "")
                      + (r.carveoutCost > 0 ? ` + carveouts ${fmtDollar(r.carveoutCost)}` : "")
                      // Planned one-off / periodic expenses. These do get charged
                      // to the draw, and until now appeared in no column and no
                      // tooltip — so a $40k roof spiked the withdrawal with
                      // nothing on screen to explain it. Same bug behind a
                      // phantom age-72 jump a user reported.
                      + (r.eventCost > 0
                          ? ` + planned expense${(r.eventLabels || []).length > 1 ? "s" : ""} ${fmtDollar(r.eventCost)}`
                            + ((r.eventLabels || []).length ? ` (${r.eventLabels.join(", ")})` : "")
                          : "")
                      + ` + taxes ${fmtDollar(r.fedTax + r.stateTax + r.irmaa)}`
                      + (r.otherIncome > 0 ? ` − other income ${fmtDollar(r.otherIncome)}` : "")
                      + ` = total need funded by the income + draw columns to the right`
                      // Healthcare shock is absent here on purpose — it's a
                      // probability-weighted risk priced by the Monte Carlo,
                      // never charged in this table.
                      }>
                  {fmtDollar(r.spending)}
                  {r.eventCost > 0 && (
                    // Visible cue — hover-only disclosure is unreachable on touch.
                    <span style={{ color: "#fb7185", fontSize: "var(--fs-small)", marginLeft: 3 }}
                          title={`Includes ${fmtDollar(r.eventCost)} of planned one-off spending`}>+📌</span>
                  )}
                  {/* Visible, not hover-only: this cell isn't the number the
                      user typed — the spending curve has re-scaled it. A
                      user suspected exactly this and had no way to see it. */}
                  {r.smileFactor != null && Math.abs(r.smileFactor - 1) >= 0.005 && (
                    <span style={{ color: "var(--accent-purple)", fontSize: "var(--fs-small)", marginLeft: 3, fontFamily: "'JetBrains Mono',monospace" }}
                          title={`Spending curve: your ${fmtDollar(r.smileBase)} target × ${r.smileFactor.toFixed(3)} for age ${r.age}. Turn it off with "Smile spending" in the sidebar Options.`}>
                      {r.smileFactor > 1 ? "+" : "−"}{Math.abs(Math.round((r.smileFactor - 1) * 1000) / 10)}%
                    </span>
                  )}
                </td>
                <td data-cg="spend" style={opTdStyle}>←</td>
                <td data-cg="income" style={{ textAlign: "right", color: "var(--accent-teal)" }}
                    title={(() => {
                      // A user reported that the components shown didn't add
                      // up to the total. The engine was right — otherIncome
                      // (pensions, annuities, any user-defined stream) gets
                      // netted from need at buildWithdrawalWaterfall ~702 —
                      // but this column rendered only `fixedIncomeTotal`
                      // (= SS + annuity/rental), so a pension simply never
                      // appeared. A retiree with a $44k pension saw a
                      // shortfall that didn't exist.
                      //
                      // Fixed in the display only. The engine field stays
                      // untouched on purpose: withdrawal.test.js asserts the
                      // funding identity as `fixedIncomeTotal + otherIncome +
                      // rmd + ...`, so folding the pension into the engine's
                      // value would double-count it and break a real invariant.
                      const parts = [`Social Security ${fmtDollar(r.ss)}`];
                      if (r.annuityRental > 0) parts.push(`Annuity/Rental ${fmtDollar(r.annuityRental)}`);
                      if (r.otherIncome > 0)   parts.push(`Pension/Other ${fmtDollar(r.otherIncome)}`);
                      const total = (r.fixedIncomeTotal || 0) + (r.otherIncome || 0);
                      let t = parts.length > 1
                        ? `${parts.join(" + ")} = ${fmtDollar(total)}\n\nCovered first, before any portfolio draw.`
                        : `Social Security: ${fmtDollar(r.ss)}`;
                      // One-off money arriving (inheritance, home sale, pension
                      // lump sum). It's deposited into an account bucket
                      // rather than netted against spending, so it never
                      // showed in this column — the balance just jumped with
                      // nothing on screen accounting for it.
                      if (r.eventInflow > 0) {
                        t += `\n\nPLUS ${fmtDollar(r.eventInflow)} arriving this year`
                          + ((r.eventLabels || []).length ? ` (${r.eventLabels.join(", ")})` : "")
                          + ` — deposited into your accounts, not spent. It raises the balances`
                          + ` on the right rather than reducing this year's draw.`;
                      }
                      return t;
                    })()}>
                  {((r.fixedIncomeTotal || 0) + (r.otherIncome || 0)) > 0
                    ? fmtDollar((r.fixedIncomeTotal || 0) + (r.otherIncome || 0))
                    : "—"}
                  {/* The amount renders on screen, not only in the tooltip.
                      This used to be a bare "+💰" next to a "—", so a year
                      in which six figures arrived read as a year with no
                      money in it, and the balance on the right jumped with
                      nothing on the row accounting for it. A user reported
                      exactly that: "the money isn't being calculated here."
                      It was — it gets deposited into a bucket and its tax
                      gets charged — but nothing said so without a hover, and
                      `title=` is dead on touch. Rendered as a separate
                      annotation rather than folded into the income figure
                      because it isn't income offsetting this year's spend —
                      it's a deposit, and the funding identity in the header
                      needs to keep reading true. */}
                  {r.eventInflow > 0 && (
                    <span style={{ color: "var(--positive)", fontSize: "var(--fs-small)", marginLeft: 3, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}
                          title={`${fmtDollar(r.eventInflow)} one-off money arriving${(r.eventLabels || []).length ? ` (${r.eventLabels.join(", ")})` : ""} — deposited into your accounts, not spent. It raises the balances on the right rather than reducing this year's draw.`}>
                      💰+{fmtDollar(r.eventInflow)}
                    </span>
                  )}
                </td>
                <td data-cg="income" style={opTdStyle}>+</td>
                <td data-cg="draw" style={{ textAlign: "right", color: "var(--text-muted)" }} title={fmtDollar(r.fromCash)}>
                  {r.fromCash > 0 ? fmtDollar(r.fromCash) : "—"}
                  {/* This is the conversion's own tax bill, paid from Cash — a
                      real draw the engine tracks (convTaxFromCash) but that
                      isn't part of fromCash (the spending-driven draw). Shown
                      as a separate badge, same pattern as the eventInflow
                      badge above, so fromCash keeps meaning "spending share"
                      while Total Draw's identity still balances with it added. */}
                  {r.convTaxFromCash > 0 && (
                    <span style={{ color: "var(--accent-purple)", fontSize: "var(--fs-small)", marginLeft: 3, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}
                          title={`+${fmtDollar(r.convTaxFromCash)} paid from Cash to fund this year's Roth conversion tax — see the Roth Conv column for the full breakdown.`}>
                      +conv {fmtDollar(r.convTaxFromCash)}
                    </span>
                  )}
                </td>
                <td data-cg="draw" style={opTdStyle}>+</td>
                <td data-cg="draw" style={{ textAlign: "right", color: "var(--chart-10)" }} title={fmtDollar(r.fromTaxable)}>
                  {r.fromTaxable > 0 ? fmtDollar(r.fromTaxable) : "—"}
                  {r.convTaxFromTaxable > 0 && (
                    <span style={{ color: "var(--accent-purple)", fontSize: "var(--fs-small)", marginLeft: 3, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}
                          title={`+${fmtDollar(r.convTaxFromTaxable)} paid from Taxable to fund this year's Roth conversion tax — see the Roth Conv column for the full breakdown.`}>
                      +conv {fmtDollar(r.convTaxFromTaxable)}
                    </span>
                  )}
                </td>
                <td data-cg="draw" style={opTdStyle}>+</td>
                <td data-cg="draw" style={{ textAlign: "right", color: "var(--accent-gold)" }}
                    title={(() => {
                      // The displayed figure is the total pretax outflow the
                      // user actually has to withdraw: forced RMD +
                      // discretionary bracket-capped draw. (The engine
                      // tracks them separately; showing only the
                      // discretionary part used to display "—" in
                      // RMD-funded years while six figures were actually
                      // leaving the IRA.)
                      const totalPretaxOut = r.fromPretax + r.rmd;
                      // Read the engine's figure — it's the number that
                      // actually moved money into the taxable bucket. This
                      // used to be re-derived here as `rmd - (needFromPort +
                      // irmaaFull)`, which matched in ordinary years but
                      // double-subtracted the conversion tax in Roth
                      // conversion years (irmaaFull is with-conversion; the
                      // engine's offset isn't), understating the reinvested
                      // surplus by up to five figures.
                      const rmdExcess = r.rmdSurplus || 0;
                      let t = `${fmtDollar(totalPretaxOut)} total from pretax`;
                      if (r.rmd > 0) t += ` = forced RMD ${fmtDollar(r.rmd)} + discretionary ${fmtDollar(r.fromPretax)}`;
                      t += ` — ${r.pretaxCapReason}`;
                      if (rmdExcess > 0) t += `. ${fmtDollar(rmdExcess)} of the RMD exceeds this year's need and is reinvested into Taxable.`;
                      if (r.convTaxFromPretax > 0) t += ` ${fmtDollar(r.convTaxFromPretax)} more was withheld from the conversion itself to fund its own tax — see the Roth Conv column.`;
                      return t;
                    })()}>
                  {r.rmd > 0 && <span style={{ fontSize: "var(--fs-small)", color: "var(--accent-purple)", marginRight: 2 }}>RMD {fmtDollar(r.rmd)}</span>}
                  {(r.fromPretax + r.rmd) > 0 ? fmtDollar(r.fromPretax + r.rmd) : "—"}
                  {r.convTaxFromPretax > 0 && (
                    <span style={{ color: "var(--accent-purple)", fontSize: "var(--fs-small)", marginLeft: 3, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}
                          title={`+${fmtDollar(r.convTaxFromPretax)} withheld from the conversion to fund its own tax — see the Roth Conv column for the full breakdown.`}>
                      +conv {fmtDollar(r.convTaxFromPretax)}
                    </span>
                  )}
                </td>
                <td data-cg="draw" style={opTdStyle}>+</td>
                <td data-cg="draw" style={{ textAlign: "right", color: "var(--positive)" }} title={fmtDollar(r.fromRoth)}>{r.fromRoth > 0 ? fmtDollar(r.fromRoth) : "—"}</td>
                <td data-cg="draw" style={opTdStyle}>=</td>
                <td data-cg="draw" style={{ textAlign: "right", color: "var(--text-primary)", fontWeight: 600 }}
                    title={`${fmtDollar(r.totalWithdrawal)} leaves the portfolio this year (Cash ${fmtDollar(r.fromCash + (r.convTaxFromCash || 0))} + Taxable ${fmtDollar(r.fromTaxable + (r.convTaxFromTaxable || 0))} + Pre-Tax ${fmtDollar(r.fromPretax + r.rmd + (r.convTaxFromPretax || 0))} + Roth ${fmtDollar(r.fromRoth)}) — covers spending, housing, carveouts, all taxes, and this year's Roth conversion tax (the "+conv" badges to the left)`}>
                  {r.totalWithdrawal > 0 ? fmtDollar(r.totalWithdrawal) : "—"}
                  {/* The delta a user has to compute by eye otherwise — "why
                      is Total Draw bigger than Spending?" This IS the answer:
                      housing + carveouts + taxes + conversion-tax funding,
                      none of which is itself spending. */}
                  {(r.totalWithdrawal - r.spending) !== 0 && (
                    <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", fontWeight: 400, marginTop: 1 }}
                         title={`Total Draw ${fmtDollar(r.totalWithdrawal)} − Spending ${fmtDollar(r.spending)} — the gap is housing, carveouts, taxes, and any Roth-conversion tax funding, none of which is spending itself.`}>
                      {r.totalWithdrawal > r.spending ? "+" : "−"}{fmtDollar(Math.abs(r.totalWithdrawal - r.spending))} vs spend
                    </div>
                  )}
                </td>
                {anyConversion && (
                  <td data-cg="conv" style={{ textAlign: "right", color: "var(--accent-purple)" }}
                      title={(() => {
                        if (!(r.conversionAmount > 0)) return "No conversion this year";
                        let t = `Converted ${fmtDollar(r.conversionAmount)} pretax → Roth`
                          + ` — adds ~${fmtDollar(r.conversionTax)} to this year's tax (included in Fed/State).`;
                        // Where the conversion tax came from. The engine
                        // already routes it (taxable → cash → pretax, or
                        // withheld from the transfer) and records each
                        // source; none of it was ever displayed, so the user
                        // couldn't see which account actually paid — or
                        // whether that was the right account.
                        const src = [];
                        if (r.convTaxFromTaxable > 0) src.push(`Taxable ${fmtDollar(r.convTaxFromTaxable)}`);
                        if (r.convTaxFromCash > 0)    src.push(`Cash ${fmtDollar(r.convTaxFromCash)}`);
                        if (r.convTaxFromPretax > 0)  src.push(`Pre-Tax ${fmtDollar(r.convTaxFromPretax)}`);
                        if (src.length) t += `\n\nTax paid from: ${src.join(" + ")}.`;
                        // Withholding out of the transfer means less money lands in the Roth.
                        if (r.convToRoth > 0 && r.convToRoth < r.conversionAmount) {
                          t += `\n\nOnly ${fmtDollar(r.convToRoth)} actually reaches the Roth —`
                            + ` the tax was withheld from the transfer. Paying it from Taxable or Cash`
                            + ` instead would move the full ${fmtDollar(r.conversionAmount)} across.`;
                        } else if (r.convToRoth > 0) {
                          t += `\nFull ${fmtDollar(r.convToRoth)} reaches the Roth.`;
                        }
                        if (r.convTaxFromPretax > 0 && r.convToRoth >= r.conversionAmount) {
                          t += `\n\nPart of the tax came from Pre-Tax — that draw is itself`
                            + ` ordinary income. Taxable or Cash is the cheaper source.`;
                        }
                        // The reconciliation. A user compared this row's
                        // gross ordinary income against the 2026 nominal
                        // bracket top and concluded the plan had overshot
                        // into the next bracket. It hadn't: the deduction
                        // comes off first, and the ceiling is indexed
                        // forward to the row's own year. Both numbers were
                        // computed by the engine and neither was ever
                        // shown, so the only check the user could run was
                        // one that had to fail.
                        if (r.bracketTopYr != null) {
                          const pct = Math.round((r.marginalBracket || 0) * 100);
                          t += `\n\nWhy this is still inside the ${pct}% bracket, in ${r.yr} dollars:`
                            + `\n  Ordinary income      ${fmtDollar(r.totInc)}`
                            + `\n  − Deductions         ${fmtDollar(r.stdDedYr)}`
                            + `\n  = Taxable income     ${fmtDollar(r.taxableIncome)}`
                            + `\n  ${pct}% bracket top      ${fmtDollar(r.bracketTopYr)}`
                            + `\n\nBracket tops are inflation-indexed, so the ${r.yr} ceiling is`
                            + ` higher than today's published figure. Compare TAXABLE income`
                            + ` (after deductions) against it — not the gross draw.`;
                          if (r.convCapReason === "irmaa_ceil") {
                            t += `\n\nThis year the IRMAA tier bound before the bracket did —`
                              + ` the ceiling shown is the IRMAA cap, which is lower.`;
                          }
                        }
                        return t;
                      })()}>
                    {r.conversionAmount > 0 ? fmtDollar(r.conversionAmount) : "—"}
                    {r.conversionAmount > 0 && r.convToRoth > 0 && r.convToRoth < r.conversionAmount && (
                      <span style={{ color: "var(--accent-gold)", fontSize: "var(--fs-small)", marginLeft: 2 }}
                            title={`Only ${fmtDollar(r.convToRoth)} of the ${fmtDollar(r.conversionAmount)} reaches the Roth — tax withheld from the transfer`}>◑</span>
                    )}
                    {/* The ceiling the conversion was sized to, in this
                        row's dollars. On screen, not just in the tooltip
                        above — the whole problem was a user reaching for a
                        number the UI never gave them, and a tooltip is dead
                        on touch. This is the figure their own arithmetic needs. */}
                    {r.conversionAmount > 0 && r.bracketTopYr != null && (
                      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap", marginTop: 1 }}>
                        {r.convCapReason === "irmaa_ceil"
                          ? `IRMAA cap ${fmtDollar(r.bracketTopYr)}`
                          : `${Math.round((r.marginalBracket || 0) * 100)}% top ${fmtDollar(r.bracketTopYr)}`}
                      </div>
                    )}
                  </td>
                )}
                <td data-cg="bal" style={{ textAlign: "right", color: b1End === 0 || (b1End < (p.bucket1Floor || 0) && (p.bucket1Floor || 0) > 0) ? "var(--chart-1)" : "var(--chart-5)", fontSize: "var(--fs-small)", borderLeft: "1px solid var(--tint-slate-400-15)" }}
                    title={
                      b1End == null
                        ? "No account is tagged Bucket 1"
                        : [b1End === 0 ? "Bucket 1 is depleted." : null, b1Breakdown]
                            .filter(Boolean).join("\n\n") || fmtDollar(b1End)
                    }>
                  {b1End == null ? "—" : fmtDollar(b1End)}
                </td>
                {/* Fed Tax / State Tax / IRMAA used to explain themselves with
                    `title=` strings. The breakdown is now the click-open
                    TaxDetailsModal (one instance, above), so these cells carry
                    one shared "Tax details" affordance instead of three
                    tooltips that touch users could never open. */}
                <td data-cg="tax" style={{ textAlign: "right", color: "var(--chart-1)" }}>
                  {fmtDollar(r.fedTax)}
                  {(r.ltcgTax > 0 || r.niit > 0) && (
                    <span style={{ color: "#fca5a5", fontSize: "var(--fs-small)", marginLeft: 2 }}
                          aria-label="Includes capital-gains tax and/or net investment income tax">*</span>
                  )}
                  <div style={{ marginTop: 1 }}>
                    <button
                      type="button"
                      onClick={() => setTaxRow(r)}
                      aria-label={`Tax details for age ${r.age} (${r.yr})`}
                      style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer",
                        fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-teal)",
                        textDecoration: "underline", textUnderlineOffset: 2, whiteSpace: "nowrap" }}
                    >
                      Tax details
                    </button>
                  </div>
                </td>
                <td data-cg="tax" style={{ textAlign: "right", color: r.stateTax > 0 ? "var(--chart-3)" : "var(--text-faint)" }}>
                  {r.stateTax > 0 ? fmtDollar(r.stateTax) : "—"}
                </td>
                <td data-cg="tax" style={{ textAlign: "right", color: r.irmaa > 0 ? "var(--chart-3)" : "var(--text-faint)" }}>
                  {r.irmaa > 0 ? fmtDollar(r.irmaa) : "—"}
                </td>
                <td data-cg="tax" style={{ textAlign: "right" }}>{(r.effectiveRate * 100).toFixed(1)}%</td>
                <td data-cg="risk" style={{ textAlign: "right", color: rowWRColor(rowIdx), fontWeight: 600, fontSize: "var(--fs-small)" }}
                    title={(() => {
                      const wr = wrAt(rowIdx);
                      if (wr == null) return "No portfolio at the start of this year.";
                      const startPort = rowIdx === 0 ? portAtRetire : rows[rowIdx - 1].totalPort;
                      return `${fmtDollar(r.totalWithdrawal)} drawn ÷ ${fmtDollar(startPort)} portfolio at the START of the year = ${(wr * 100).toFixed(1)}%.

This is the DRAW, not your spending — income covers the rest. Guardrail band is ±20% around your first-year rate of ${(initialWR * 100).toFixed(1)}%.`;
                    })()}>
                  {(() => { const wr = wrAt(rowIdx); return wr == null ? "—" : (wr * 100).toFixed(1) + "%"; })()}
                </td>
                <td data-cg="risk" style={{ textAlign: "center", fontSize: "var(--fs-body)" }}>
                  {r.landmines.ssTorpedo && (() => {
                    const provisional = Math.round((r.ss || 0) * 0.5 + (r.rmd || 0) + (r.fromPretax || 0) + (r.annuityRental || 0));
                    const thresh = (p?.filingStatus || "mfj") !== "single" ? 44_000 : 34_000;
                    return (
                      <LandmineTip
                        emoji="⚡"
                        label="SS Torpedo"
                        color="var(--accent-gold)"
                        detail={`85% of your Social Security (${fmtDollar(r.ss)}/yr) is taxable because provisional income $${provisional.toLocaleString()} exceeds the $${thresh.toLocaleString()} threshold. Provisional = SS×50% + RMD + pretax draws + annuity. Consider drawing from Roth instead to keep provisional income below the threshold.`}
                      />
                    );
                  })()}
                  {r.landmines.irmaaTriggered && (() => {
                    return (
                      <LandmineTip
                        emoji="💊"
                        label="IRMAA Surcharge"
                        color="var(--chart-3)"
                        detail={`This year's ${fmtDollar(r.irmaa)} surcharge is based on your ${r.yr - 2} income (IRMAA uses a 2-year lookback — the current Tier-1 threshold ~$218,000 MFJ was checked against that year's MAGI, not this year's). Income this year affects premiums in ${r.yr + 2}. Enable IRMAA Guard in Plan inputs → Settings → Withdrawal Order to cap pretax draws before this threshold.`}
                      />
                    );
                  })()}
                  {r.earlyPenalty > 0 && (
                    <LandmineTip
                      emoji="⛔"
                      label="Early Withdrawal Penalty"
                      color="var(--chart-1)"
                      detail={`IRC 72(t): at age ${r.age} you are under 59.5, so the ${fmtDollar(r.fromPretax + r.rmd)} taken from pre-tax owes a 10% additional tax of ${fmtDollar(r.earlyPenalty)} ON TOP of income tax. Reason: ${r.earlyPenaltyReason}.${r.earlyPenaltyExempt > 0 ? ` ${fmtDollar(r.earlyPenaltyExempt)} was exempt.` : ""}\n\nWays out: fund spending from taxable / cash / Roth basis until 59.5; keep a former-employer 401k UNROLLED and use the Rule of 55; or start a 72(t) SEPP, which must then run to the LATER of 5 years or age 59.5.`}
                    />
                  )}
                  {r.landmines.rmdActive && (() => {
                    return (
                      <LandmineTip
                        emoji="📋"
                        label="RMDs Active"
                        color="var(--accent-purple)"
                        detail={`Required Minimum Distribution: ${fmtDollar(r.rmd)}/yr — forced withdrawal from your pretax account (IRS Pub 590-B). This is ordinary income you cannot defer or avoid. Roth conversions before RMD age reduce the pretax balance and shrink future RMDs.`}
                      />
                    );
                  })()}
                  {!anyLandmine(r) && <Dot tone="ok" />}
                </td>
                <td data-cg="bal" style={{ textAlign: "right", color: "var(--text-secondary)" }} title={fmtDollar(r.totalPort)}>{fmtDollar(r.totalPort)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* The single dialog the per-row "Tax details" buttons open. Mounted
          once, outside the row map, so 40 rows don't mean 40 dialog
          instances — only the opened one renders its content. Every figure it
          shows comes from the row it was handed (rule 8). */}
      <TaxDetailsModal
        r={taxRow}
        p={p}
        dollarBasis="nominal dollars for that year"
        open={taxRow != null}
        onClose={() => setTaxRow(null)}
      />
    </div>
  );
}

// Guyton-Klinger "Spending Path with Guardrail Adjustments" — extracted from
// DeterministicWithdrawalView so the SAME chart+strip renders both there (the
// Withdrawal Plan tab) and on the Monte Carlo tab, from one source (rule 8 — no
// second copy that could drift). Self-contained: computes its own deterministic
// schedule and the cross-scenario strip reads mc.gkStats. Returns null for
// non-GK strategies and before a run, so callers can drop it in unconditionally.
// The chart JSX is DeepSeek-flash's, moved verbatim; only the data prep and the
// gating/wrapper moved.
// Which distribution strategies flex spending via guardrails (so the GK
// spending-path chart is meaningful). One list, read by GuardrailsSpendingPath
// AND by the callers that gate its container — never duplicate the set.
const GUARDRAIL_STRATEGIES = ["gk", "smart", "risk", "kitces", "vanguard", "endowment", "cape", "one_n"];
function isGuardrailStrategy(strategy) {
  return GUARDRAIL_STRATEGIES.includes(resolveStrategy(strategy));
}

// Guyton-Klinger guardrails — a tabbed view (Spending Path · Adjustment Events ·
// Across All Scenarios), mirroring the competitor's structure but honest to
// AiRA's engine. One shared component rendered by BOTH the Withdrawal Plan tab
// and the Monte Carlo tab (rule 8 — one source, no drift). The Spending Path
// chart is DeepSeek-flash's, verbatim, plus a Portfolio line on a 2nd axis.
// Self-contained: computes its own deterministic schedule; the cross-scenario
// figures read mc.gkStats. Returns null for non-GK strategies and before a run.
function GuardrailsView({ p, inf, withdrawalStrategy, mc, topRule = true }) {
  const [subTab, setSubTab] = useState("path");
  const gkApplies = isGuardrailStrategy(withdrawalStrategy);
  const { schedule } = useMemo(
    () => simulateDeterministicWithStrategy(p, inf, withdrawalStrategy),
    [p, inf, withdrawalStrategy]
  );
  if (!gkApplies || !schedule || schedule.length === 0) return null;

  const realAt = (v, i) => Math.round((v || 0) / Math.pow(1 + (inf || 0) / 100, i));
  const rows = schedule.map((s, i) => ({
    age: s.age, yr: s.yr, gkEvent: s.gkEvent, gkReason: s.gkReason,
    priorReal: realAt(s.spEntering, i),
    actualReal: realAt(s.spAfterGK ?? s.spending, i),
    floorReal: realAt(s.gkFloor, i),
    ceilingReal: realAt(s.gkCeiling, i),
    portReal: realAt(s.portfolioEnd, i),
  }));
  const gkChartData = rows.map((r) => ({
    age: r.age, gkEvent: r.gkEvent, gkReason: r.gkReason,
    "Prior spend (real)": r.priorReal,
    "Actual spend (real)": r.actualReal,
    "Floor (real)": r.floorReal,
    "Ceiling (real)": r.ceilingReal,
    "Portfolio (real)": r.portReal,
  }));
  const events = rows.filter((r) => r.gkEvent === "cut" || r.gkEvent === "raise" || r.gkEvent === "constrained" || r.gkReason === "longevity-hold");
  const cutCount = rows.filter((r) => r.gkEvent === "cut").length;
  const raiseCount = rows.filter((r) => r.gkEvent === "raise").length;
  // Band binds listed with the events (they moved spending) but counted apart,
  // and split by which limit bound so the row can name it.
  const constrainedCount = rows.filter((r) => r.gkEvent === "constrained").length;
  const holdCount = rows.filter((r) => r.gkReason === "longevity-hold").length;
  const money = (v) => fmtDollar(v || 0);
  const perMonth = (v) => fmtDollar(Math.round((v || 0) / 12));
  const retAgeForBasis = effectiveRetireAge(p.retireAge, p.currentAge);
  const retirementYear = CURRENT_YEAR + Math.max(0, retAgeForBasis - (p.currentAge ?? retAgeForBasis));
  const g = mc?.gkStats;
  const pctS = (v) => `${Math.round((v || 0) * 100)}%`;

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setSubTab(id)} style={{
      background: "none", border: "none", cursor: "pointer", padding: "6px 2px",
      fontSize: "var(--fs-label)", fontWeight: subTab === id ? 800 : 600,
      color: subTab === id ? "var(--accent-teal)" : "var(--text-muted)",
      borderBottom: `2px solid ${subTab === id ? "var(--accent-teal)" : "transparent"}`,
    }}>{label}</button>
  );

  return (
    <div style={topRule ? { marginTop: 20, borderTop: "1px solid var(--divider)", paddingTop: 14 } : undefined}>
      {/* Persistent cross-scenario headline (Monte Carlo result). Absent before a
          run — it is an MC figure, so there is nothing honest to show yet. */}
      {g && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "6px 18px", padding: "8px 12px", marginBottom: 12, background: "var(--row-highlight)", border: "1px solid var(--card-border)", borderRadius: 8, fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>
          <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>Across {g.paths.toLocaleString()} simulated scenarios:</span>
          <span>guardrails <strong style={{ color: "var(--chart-1)" }}>cut spending in {pctS(g.cutRate)}</strong> of scenarios</span>
          <span><strong style={{ color: "var(--positive)" }}>raised it in {pctS(g.raiseRate)}</strong></span>
          {/* Median first, mean second, and the mean NAMED as the average among
              the paths that cut. "avg 8.9 cuts per scenario" alone read as "the
              typical plan cuts ~9 times" — while the median path below cuts ZERO
              times. Only a minority cut at all, and they cut repeatedly, so the
              mean is not the typical case. Reporting the median without the
              qualifier would hide the tail; reporting the mean without it
              invented a typical case that does not exist. */}
          <span>
            cuts: typically <strong style={{ color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>{g.medianCutsPerPath}</strong> per scenario
            {g.avgCutsAmongCutters > 0 && (
              <span style={{ color: "var(--text-muted)" }}>
                {" "}(<strong style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{g.avgCutsAmongCutters.toFixed(1)}</strong> on average
                among the {pctS(g.cutRate)} that cut)
              </span>
            )}
          </span>
          <span style={{ color: "var(--text-muted)" }}>spending seen <strong style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{money(g.spendMinReal)}–{money(g.spendMaxReal)}</strong>/yr ({perMonth(g.spendMinReal)}–{perMonth(g.spendMaxReal)}/mo) in {retirementYear} dollars</span>
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 18, borderBottom: "1px solid var(--divider)", marginBottom: 12 }}>
        <TabBtn id="path" label="Spending Path" />
        <TabBtn id="events" label={`Adjustment Events${events.length ? ` (${events.length})` : ""}`} />
        <TabBtn id="scenarios" label="Across All Scenarios" />
      </div>

      {subTab === "path" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", lineHeight: 1.5, flex: "1 1 260px" }}>
              One <strong style={{ color: "var(--text-secondary)" }}>median-returns</strong> path — teal is what you actually spend, riding between your floor and ceiling; the blue dashed line is the portfolio (right axis). ▲/▼ mark the years an adjustment fired.
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
              {/* "· {retirementYear} $" was a bare year + currency sign with no
                  unit word — it read as a broken number ("2041 $") and was the
                  only one of this view's FOUR basis labels spelled that way
                  (§41 A3 says "{retirementYear} dollars", and three of the four
                  already did). Same convention everywhere or the basis stops
                  being trustworthy. */}
              {cutCount} cut{cutCount === 1 ? "" : "s"} · {raiseCount} raise{raiseCount === 1 ? "" : "s"}{holdCount ? ` · ${holdCount} held` : ""} · in {retirementYear} dollars
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={gkChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
              <XAxis dataKey="age" stroke="var(--divider)" tickMargin={6} tick={{ fill: "var(--text-muted)", fontSize: "var(--fs-small)", fontFamily: "var(--font-mono)" }} />
              <YAxis yAxisId="spend" stroke="var(--divider)" tickMargin={4} width={MONEY_AXIS_WIDTH} tick={{ fill: "var(--text-muted)", fontSize: "var(--fs-small)", fontFamily: "var(--font-mono)" }} tickFormatter={(v) => fmtDollar(v)} />
              <YAxis yAxisId="port" orientation="right" stroke="var(--divider)" tickMargin={4} width={MONEY_AXIS_WIDTH} tick={{ fill: "var(--chart-2)", fontSize: "var(--fs-small)", fontFamily: "var(--font-mono)" }} tickFormatter={(v) => fmtDollar(v)} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const row = gkChartData.find((d) => d.age === label) || {};
                  const monthly = (v) => fmtDollar(Math.round((v || 0) / 12));
                  const ev = row.gkEvent;
                  return (
                    <div style={{ background: "#0f1729", border: "1px solid var(--tint-white-14)", borderRadius: 8, padding: "9px 12px", fontSize: "var(--fs-small)", lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Age {label}</div>
                      <div style={{ color: "var(--text-secondary)" }}>Prior spend <strong style={{ fontFamily: "var(--font-mono)" }}>{fmtDollar(row["Prior spend (real)"])}</strong> <span style={{ color: "var(--text-faint)" }}>({monthly(row["Prior spend (real)"])}/mo)</span></div>
                      <div style={{ color: "var(--text-secondary)" }}>After guardrail <strong style={{ fontFamily: "var(--font-mono)" }}>{fmtDollar(row["Actual spend (real)"])}</strong> <span style={{ color: "var(--text-faint)" }}>({monthly(row["Actual spend (real)"])}/mo)</span></div>
                      <div style={{ color: "var(--text-muted)" }}>Floor {fmtDollar(row["Floor (real)"])} · Ceiling {fmtDollar(row["Ceiling (real)"])}</div>
                      <div style={{ color: "var(--chart-2)" }}>Portfolio {fmtDollar(row["Portfolio (real)"])}</div>
                      {ev === "cut" && <div style={{ color: "var(--chart-1)", fontWeight: 700, marginTop: 4 }}>▼ Spending cut {Math.round(GK_ADJUST_PCT * 100)}% — withdrawal rate crossed the {Math.round(GK_BAND_PCT * 100)}% upper band</div>}
                      {ev === "raise" && <div style={{ color: "var(--positive)", fontWeight: 700, marginTop: 4 }}>▲ Spending raised {Math.round(GK_ADJUST_PCT * 100)}% — withdrawal rate fell {Math.round(GK_BAND_PCT * 100)}% below the initial rate</div>}
                      {ev === "constrained" && (
                        <div style={{ color: "var(--chart-6)", fontWeight: 700, marginTop: 4 }}>
                          ◆ {row.gkReason === "floor-bind" ? "Floor bound" : "Ceiling bound"} — the guardrail rule was held at your {row.gkReason === "floor-bind" ? "floor" : "ceiling"} setting ({money(row[`${row.gkReason === "floor-bind" ? "Floor" : "Ceiling"} (real)`])}). This is a settings effect, not a market reaction.
                        </div>
                      )}
                      {!ev && row.gkReason === "longevity-hold" && <div style={{ color: "var(--accent-gold)", marginTop: 4 }}>Band crossed, but the longevity rule held — {GK_LONGEVITY_YEARS} years or fewer remain</div>}
                      {!ev && !row.gkReason && <div style={{ color: "var(--text-faint)", marginTop: 4 }}>No adjustment — inflation only</div>}
                    </div>
                  );
                }}
              />
              <Area yAxisId="spend" type="monotone" dataKey="Ceiling (real)" stroke="none" fill="var(--tint-green-07)" />
              <Line yAxisId="spend" type="monotone" dataKey="Floor (real)" stroke="var(--chart-6)" strokeWidth={1.2} strokeDasharray="4 3" dot={false} />
              <Line yAxisId="spend" type="monotone" dataKey="Ceiling (real)" stroke="var(--positive)" strokeWidth={1.2} strokeDasharray="4 3" dot={false} />
              <Line yAxisId="spend" type="monotone" dataKey="Prior spend (real)" stroke="var(--text-faint)" strokeWidth={1.2} strokeDasharray="2 3" dot={false} />
              <Line yAxisId="port" type="monotone" dataKey="Portfolio (real)" stroke="var(--chart-2)" strokeWidth={2} strokeDasharray="5 3" dot={false} />
              <Line yAxisId="spend" type="monotone" dataKey="Actual spend (real)" stroke="var(--accent-teal)" strokeWidth={2.4}
                    dot={(props) => {
                      const ev = gkChartData[props.index]?.gkEvent;
                      if (!ev) return <g key={`d${props.index}`} />;
                      // Three shapes, not three colors. Cut/raise are vertical
                      // triangles reading as "down/up"; a band bind is neither —
                      // the settings ceiling or floor holding spend at a limit —
                      // so it gets a diamond in the gold already used for the
                      // floor line. Direction is carried by geometry rather than
                      // colour alone because red/green is unreadable for ~8% of
                      // readers, and a third colour would make that worse.
                      if (ev === "constrained") {
                        const d = 7, off = 11;
                        const pts = `${props.cx},${props.cy - off - d} ${props.cx + d},${props.cy - off} ${props.cx},${props.cy - off + d} ${props.cx - d},${props.cy - off}`;
                        return <polygon key={`e${props.index}`} points={pts} fill="var(--chart-6)" stroke="var(--bg-base)" strokeWidth={1.5} />;
                      }
                      const up = ev === "raise";
                      const w = 8.5, h = 13, gap = 11;
                      const baseY = up ? props.cy - gap : props.cy + gap;
                      const apexY = up ? baseY - h : baseY + h;
                      const pts = `${props.cx - w},${baseY} ${props.cx + w},${baseY} ${props.cx},${apexY}`;
                      return <polygon key={`e${props.index}`} points={pts} fill={up ? "var(--positive)" : "var(--chart-1)"} stroke="var(--bg-base)" strokeWidth={1.5} />;
                    }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="leg">
            <div className="li"><div className="ll" style={{ background: "var(--accent-teal)" }} />Actual spending</div>
            <div className="li"><div className="ll" style={{ background: "var(--text-faint)" }} />Prior year's spending</div>
            <div className="li"><div className="ll" style={{ background: "var(--chart-6)" }} />Floor</div>
            <div className="li"><div className="ll" style={{ background: "var(--positive)" }} />Ceiling</div>
            <div className="li"><div className="ll" style={{ background: "var(--chart-2)" }} />Portfolio (right axis)</div>
            <div className="li"><span style={{ color: "var(--chart-1)", fontSize: "var(--fs-label)" }}>▼</span> Cut</div>
            <div className="li"><span style={{ color: "var(--positive)", fontSize: "var(--fs-label)" }}>▲</span> Raise</div>
            <div className="li"><span style={{ color: "var(--chart-6)", fontSize: "var(--fs-label)" }}>◆</span> Band bound (your floor/ceiling setting)</div>
          </div>
        </>
      )}

      {subTab === "events" && (
        events.length === 0 ? (
          <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", lineHeight: 1.6, padding: "10px 4px" }}>
            No guardrail adjustments fired on this median-returns path — spending simply tracked inflation every year. A weaker market sequence would trigger cuts; the <strong style={{ color: "var(--text-secondary)" }}>Across All Scenarios</strong> tab shows how often that happens across all {g ? g.paths.toLocaleString() : "3,000"} simulated runs.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="nw-table" style={{ fontSize: "var(--fs-label)" }}>
              <thead><tr><th>Age</th><th>Year</th><th>Event</th><th>From</th><th>To</th><th>Change</th><th>Why</th></tr></thead>
              <tbody>
                {events.map((r) => {
                  const isCut = r.gkEvent === "cut", isRaise = r.gkEvent === "raise";
                  // A band bind keeps its own row type. Note it can move spend in
                  // EITHER direction (ceiling binds up, floor binds down), so the
                  // colour follows the arithmetic delta while the label names the
                  // limit — never a shared "cut" reading.
                  const isConstrained = r.gkEvent === "constrained";
                  const delta = r.actualReal - r.priorReal;
                  const c = isConstrained ? "var(--chart-6)" : isCut ? "var(--chart-1)" : isRaise ? "var(--positive)" : "var(--accent-gold)";
                  return (
                    <tr key={r.age}>
                      <td style={{ textAlign: "left" }}>{r.age}</td><td>{r.yr}</td>
                      <td style={{ color: c, fontWeight: 700 }}>{isConstrained ? `◆ ${r.gkReason === "floor-bind" ? "Floor bound" : "Ceiling bound"}` : isCut ? "▼ Cut" : isRaise ? "▲ Raise" : "Held"}</td>
                      <td>{money(r.priorReal)}</td>
                      <td>{money(r.actualReal)}</td>
                      <td style={{ color: delta < 0 ? "var(--chart-1)" : delta > 0 ? "var(--positive)" : "var(--text-muted)" }}>{delta > 0 ? "+" : ""}{money(delta)}</td>
                      <td style={{ textAlign: "left", color: "var(--text-muted)", fontSize: "var(--fs-small)" }}>{isConstrained ? `GK rule's figure was held at your ${r.gkReason === "floor-bind" ? "floor" : "ceiling"} setting` : isCut ? `Withdrawal rate crossed the ${Math.round(GK_BAND_PCT * 100)}% upper band` : isRaise ? `Withdrawal rate fell ${Math.round(GK_BAND_PCT * 100)}% below the initial rate` : `Band crossed, but ≤ ${GK_LONGEVITY_YEARS} years remained`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 8 }}>All figures in {retirementYear} dollars. Change is relative to the prior year's spending.</div>
          </div>
        )
      )}

      {subTab === "scenarios" && (
        g ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              {[
                ["Scenarios with a cut", pctS(g.cutRate), "var(--chart-1)", `${Math.round(g.cutRate * g.paths).toLocaleString()} of ${g.paths.toLocaleString()} paths cut spending at least once`],
                ["Scenarios with a raise", pctS(g.raiseRate), "var(--positive)", `${Math.round(g.raiseRate * g.paths).toLocaleString()} of ${g.paths.toLocaleString()} paths raised spending`],
                ["Avg cuts per scenario", g.avgCutsPerPath.toFixed(1), "var(--accent-gold)", "Averaged over every simulated path"],
                ["Spending range seen", `${money(g.spendMinReal)}–${money(g.spendMaxReal)}`, "var(--chart-2)", `${perMonth(g.spendMinReal)}–${perMonth(g.spendMaxReal)}/mo · ${retirementYear} dollars`],
                // Third event type, deliberately last: it answers a different
                // question from the two above. cutRate/raiseRate are how often the
                // MARKET moved you; this is how often your own band settings did.
                // Tightening the floor/ceiling raises this number and should never
                // make the first two look calmer than they are.
                ...(g.constrainedRate != null ? [[
                  "Scenarios where the band bound",
                  pctS(g.constrainedRate),
                  "var(--chart-6)",
                  `${Math.round((g.ceilingBinds || 0) + (g.floorBinds || 0)).toLocaleString()} bind-years (${(g.ceilingBinds || 0).toLocaleString()} ceiling · ${(g.floorBinds || 0).toLocaleString()} floor) across ${g.paths.toLocaleString()} paths — driven by your floor/ceiling settings, not by markets`,
                ]] : []),
              ].map(([l, v, c, sub]) => (
                <div key={l} style={{ background: "var(--row-highlight)", border: "1px solid var(--card-border)", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: "var(--fs-h1)", fontWeight: 800, color: c, fontFamily: "var(--font-mono)" }}>{v}</div>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 3, lineHeight: 1.4 }}>{sub}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.6, marginTop: 12 }}>
              The Spending Path tab illustrates one <em>median</em> path — usually benign, so few adjustments fire there. These figures count how the guardrails actually behave across all {g.paths.toLocaleString()} simulated markets, the bad sequences included.{g.longevityHolds > 0 ? ` In ${g.longevityHolds.toLocaleString()} path-years a cut was skipped because ${GK_LONGEVITY_YEARS} years or fewer remained (the longevity rule).` : ""}
            </div>
          </>
        ) : (
          <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", padding: "10px 4px" }}>Run Monte Carlo to see how the guardrails behave across all simulated scenarios.</div>
        )
      )}

      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.55, marginTop: 12, borderTop: "1px solid var(--divider)", paddingTop: 10 }}>
        Guardrails move spending relative to <em>last year's</em> level. The GK rules in force — {Math.round(GK_BAND_PCT * 100)}% band, {Math.round(GK_ADJUST_PCT * 100)}% adjustment, no cut inside the last {GK_LONGEVITY_YEARS} years, inflation capped at {Math.round(GK_INFLATION_CAP * 100)}% — are set in the Forecast tab's ⚙ Advanced Settings.
      </div>
    </div>
  );
}

function DeterministicWithdrawalView({ p, inf, withdrawalStrategy, smartRows, mc }) {
  const [showTable, setShowTable] = useState(true);
  const data = useMemo(
    () => simulateDeterministicWithStrategy(p, inf, withdrawalStrategy),
    [p, inf, withdrawalStrategy]
  );
  const { schedule, portAtRetire, initWR } = data;
  const chartData = schedule.map((s) => ({
    age: s.age,
    "Total Withdrawal": s.totalWithdrawal,
    "Portfolio End": s.portfolioEnd,
    Spending: s.spending,
    // Guardrail series, straight off the schedule row the engine emitted —
    // including the floor/ceiling it actually clamped to, and the cut/raise it
    // actually applied. Nothing here is re-derived from the spend path.
    "Prior spend": s.spEntering,
    "Post-guardrail": s.spAfterGK ?? s.spending,
    Floor: s.gkFloor,
    Ceiling: s.gkCeiling,
    gkEvent: s.gkEvent,
    gkReason: s.gkReason,
  }));
  // The Guyton-Klinger spending-path chart + cross-scenario strip now live in
  // the shared <GuardrailsSpendingPath> (rendered below, and on the MC tab) —
  // its data prep moved with it.

  if (!schedule || schedule.length === 0) {
    return <div className="chart-card">No data available. Run Monte Carlo first.</div>;
  }

  // One label map for the whole app (getStrategyLabel). A second copy here
  // is how the chart title used to disagree with the picker beside it.
  const strategyLabel = getStrategyLabel(resolveStrategy(withdrawalStrategy));

  // Only the "smart" strategy actually runs through buildWithdrawalWaterfall
  // (see simulateDeterministicWithStrategy's dispatch) — every other
  // strategy (gk, vpw, fixed, ...) falls to a separate, simpler engine: one
  // blended growth rate for the whole portfolio (no cash/taxable/pretax/roth
  // split), and no concept of a Roth conversion at all — Roth Conv reads $0
  // here even in a year the Sourcing section converts real money, because
  // this schedule's Fed/State Tax is borrowed from that same Sourcing-section
  // calculation (so the tax IS real and DOES include any conversion), while
  // every other column on this schedule is this engine's own, independent
  // number. The two schedules can genuinely disagree — most visibly in a
  // conversion year — because they're not the same calculation. Routing
  // every strategy through the real per-account engine is real, tracked
  // future work (three sessions have deferred it so far, per project
  // memory), not a quick fix — this disclosure exists so the disagreement
  // reads as a known limitation instead of a bug the user has to chase.
  const notSmart = resolveStrategy(withdrawalStrategy) !== "smart";

  return (
    <>
      <div className="chart-card">
        <div className="ct">Steady-return schedule · {strategyLabel}</div>
        <div className="ct-sub">
          One fixed path, no market swings: median historical returns every year
          ({expectedReturn(p.preRetireEq ?? 91).toFixed(2)}% before age {resolveGlidepathSwitchAge(p)} / {expectedReturn(p.postRetireEq ?? 70).toFixed(2)}% after) · Inflation {inf}%
        </div>
        {notSmart && (
          <div style={{
            fontSize: "var(--fs-small)", color: "var(--text-secondary)", background: "var(--tint-amber-07)",
            border: "1px solid rgba(251,191,36,0.22)", borderRadius: 6, padding: "8px 10px",
            marginBottom: 10, lineHeight: 1.5,
          }}>
            <strong style={{ color: "#fbbf24" }}>Simplified schedule.</strong> This "{strategyLabel}" view uses
            one blended growth rate for your whole portfolio, not the real per-account draws the
            Sourcing section above uses — and it never models a Roth conversion (Roth Conv. always
            reads $0 here). Its Fed/State Tax figures ARE borrowed from the Sourcing section's real
            plan though, including any conversion's tax — so Tax can be nonzero here in a year Roth
            Conv. shows $0, and Portfolio End can differ from the Sourcing section's Port End by a
            real amount. For your actual plan's numbers, use the Sourcing section above.
          </div>
        )}
        {/* Portfolio Balance is a genuine running total — a line, one $ axis. */}
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
            <XAxis dataKey="age" stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} />
            <YAxis stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} tickFormatter={(v) => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
            <Tooltip content={<Tip />} />
            <Line type="monotone" dataKey="Portfolio End" stroke="var(--accent-teal)" strokeWidth={2.5} dot={false} name="Portfolio Balance" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="leg">
          <div className="li"><div className="ll" style={{ background: "var(--accent-teal)" }} />Portfolio Balance</div>
        </div>

        {/* GK spending-path chart + cross-scenario strip — now the shared
            <GuardrailsSpendingPath> (also rendered on the Monte Carlo tab). */}
        <GuardrailsView p={p} inf={inf} withdrawalStrategy={withdrawalStrategy} mc={mc} topRule />

        {/* Spending/Withdrawal are each year's OWN number, not a flow between
            years — bars, not lines, sharing the same age x-axis as the chart
            above instead of a fabricated second $ scale on one plot. */}
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
            <XAxis dataKey="age" stroke="#1e3a5f" tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }} />
            <YAxis stroke="#1e3a5f" tick={{ fill: "var(--text-muted)", fontSize: "var(--fs-small)" }} tickFormatter={(v) => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
            <Tooltip content={<Tip />} />
            <Bar dataKey="Spending" fill="var(--accent-gold)" name="Spending" />
            <Bar dataKey="Total Withdrawal" fill="var(--chart-1)" name="Total Withdrawal (inc. tax)" />
          </BarChart>
        </ResponsiveContainer>
        <div className="leg">
          <div className="li"><div className="ll" style={{ background: "var(--accent-gold)" }} />Spending</div>
          <div className="li"><div className="ll" style={{ background: "var(--chart-1)" }} />Total Withdrawal (inc. tax)</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {/* This used to say "Median accumulation," which claims the figure
            is the 50th percentile of the Monte Carlo distribution. It isn't:
            `simulateDeterministicWithStrategy` gets it from
            `accumulateToRetirement(p)`, a single deterministic
            compound-growth projection at the expected return (App.jsx
            ~1818). No distribution is involved and there's no median to
            take. Same kind of mislabeling as the "safe spend" and "GK
            guardrails" labels elsewhere — the caption claimed an authority
            the number never had. The whole view is deterministic, so the
            caption says that now. */}
        <div className="met"><div className="ml">Portfolio at Retirement</div><div className="mv" style={{ color: "var(--accent-teal)" }}>{fmtDollar(portAtRetire)}</div><div className="ms">Single projection at the expected return — not a median</div></div>
        <div className="met"><div className="ml">Initial Withdrawal Rate</div><div className="mv" style={{ color: "var(--accent-gold)" }}>{(initWR * 100).toFixed(1)}%</div><div className="ms">Net portfolio draw / portfolio</div></div>
        <div className="met"><div className="ml">Final Portfolio (Age {schedule[schedule.length - 1]?.age})</div><div className="mv" style={{ color: schedule[schedule.length - 1]?.portfolioEnd > 0 ? "var(--positive)" : "var(--negative)" }}>{fmtDollar(schedule[schedule.length - 1]?.portfolioEnd || 0)}</div><div className="ms">{schedule[schedule.length - 1]?.portfolioEnd > 0 ? "Survives" : "Exhausted"}</div></div>
      </div>

      <div className="chart-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div className="ct">Year‑by‑Year Schedule</div>
          <button onClick={() => setShowTable(!showTable)} className="mbtn" style={{ fontSize: "var(--fs-label)", padding: "3px 8px" }}>{showTable ? "Hide Table" : "Show Table"}</button>
        </div>
        {showTable && (
          <div style={{ overflowX: "auto" }}>
            <table className="nw-table" style={{ fontSize: "var(--fs-label)" }}>
              {/* These three used to be "SS" / "Rental" / "Other Inc" here
                  and three other things elsewhere. Canonical names only —
                  see INCOME_CATS. Together they're the "Income" aggregate
                  the Withdrawal Plan table shows as one column. */}
              <thead><tr><th>Age</th><th>Year</th><th>Spending</th><th>Social Security</th><th>Annuity/Rental</th><th>Pension/Other</th><th>Housing</th><th>Carveouts</th><th>Portfolio Draw</th><th>Roth Conv.</th><th>Fed Tax</th><th>State Tax</th><th>IRMAA</th><th>Total Withdrawal</th><th>Portfolio End</th></tr></thead>
              <tbody>
                {schedule.map((s) => (
                  <tr key={s.age}>
                    <td style={{ textAlign: "left" }}>{s.age}</td><td>{s.yr}</td>
                    <td style={{ color: "var(--accent-gold)",fontSize: "var(--fs-h2)", fontWeight: 'bold'  }}>{fmtDollar(s.spending)}</td>
                    <td>{fmtDollar(s.ss)}</td><td>{fmtDollar(s.Rental)}</td><td style={{ color: "#eab308" }}>{fmtDollar(s.OtherIncome)}</td>
                    <td style={{ color: "#fb7185" }}>{fmtDollar(s.housingCost || 0)}</td>
                    <td style={{ color: "#fb7185" }}>{fmtDollar(s.carveoutCost || 0)}</td>
                    <td>{fmtDollar(s.portfolioDraw)}</td>
                    <td style={{ color: "var(--accent-teal)" }}
                        title={s.bracketTopYr != null && s.conversionAmount > 0
                          ? `Sized to fill the bracket, in ${s.yr} dollars:\n`
                            + `  Ordinary income   ${fmtDollar(s.totInc)}\n`
                            + `  − Deductions      ${fmtDollar(s.stdDedYr)}\n`
                            + `  = Taxable income  ${fmtDollar(s.taxableIncome)}\n`
                            + `  Ceiling           ${fmtDollar(s.bracketTopYr)}\n\n`
                            + `Bracket tops are inflation-indexed, so the ${s.yr} ceiling is higher `
                            + `than today's published figure. Compare TAXABLE income against it, `
                            + `not the gross draw.`
                          : undefined}>
                      {fmtDollar(s.conversionAmount || 0)}
                      {s.conversionAmount > 0 && s.bracketTopYr != null && (
                        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                          {s.convCapReason === "irmaa_ceil"
                            ? `IRMAA cap ${fmtDollar(s.bracketTopYr)}`
                            : `${Math.round((s.marginalBracket || 0) * 100)}% top ${fmtDollar(s.bracketTopYr)}`}
                        </div>
                      )}
                    </td>
                    <td style={{ color: "var(--chart-1)" }}>{fmtDollar(s.fedTax)}</td>
                    <td style={{ color: "var(--chart-3)" }}>{fmtDollar(s.stateTax)}</td>
                    <td style={{ color: "var(--accent-purple)" }}>{fmtDollar(s.irmaa)}</td>
                    <td style={{ color: "#8fcfa8",fontSize: "var(--fs-h2)", fontWeight: 'bold' }}>{fmtDollar(s.totalWithdrawal)}</td>
                    <td style={{ color: "var(--accent-teal)", fontWeight: 600 }}>
                      {fmtDollar(s.portfolioEnd)}
                      {(() => {
                        if (!notSmart || !smartRows) return null;
                        const smartRow = smartRows.find(r => r.age === s.age);
                        if (!smartRow) return null;
                        const delta = s.portfolioEnd - smartRow.totalPort;
                        if (Math.abs(delta) < 500) return null; // noise-floor: not worth a click
                        return (
                          <InfoModal
                            title="Why Portfolio End differs this year"
                            accent="#fbbf24"
                            trigger={
                              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontWeight: 400, whiteSpace: "nowrap" }}>
                                {delta >= 0 ? "+" : "−"}{fmtDollar(Math.abs(delta))} vs Sourcing
                              </div>
                            }
                          >
                            <p>This schedule: <strong>{fmtDollar(s.portfolioEnd)}</strong><br />
                              Sourcing section (age {s.age}): <strong>{fmtDollar(smartRow.totalPort)}</strong><br />
                              Difference: <strong>{delta >= 0 ? "+" : "−"}{fmtDollar(Math.abs(delta))}</strong></p>
                            <p>Spending, Fed Tax, and State Tax already match between the two schedules
                              this year — this gap is purely how each one grows the money that's left.</p>
                            <p>This schedule applies <strong>one blended return</strong> to your whole
                              portfolio. The Sourcing section applies a <strong>different return per
                              account</strong> — cash grows conservatively, other accounts follow your
                              equity/bond mix.</p>
                            <p>The Sourcing section's number reflects your real accounts; treat this
                              schedule's Portfolio End as an approximation.</p>
                          </InfoModal>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="flag-i" style={{ fontSize: "var(--fs-label)" }}>
        ℹ️ Deterministic (median) path – shows how the {strategyLabel} strategy would behave in a single "typical" sequence of returns. Actual outcomes will vary.
      </div>
    </>
  );
}

const _BCFG_KEY = "aira_buckets_cfg.v1";
function _loadBCfg() {
  try { return JSON.parse(localStorage.getItem(_BCFG_KEY) || "null") || {}; } catch { return {}; }
}

// Bucket status card — hoisted to module scope so React doesn't unmount it
// on every BucketsTab re-render (which would kill the tooltip hover state).
function BucketCard({ num, color, label, horizon, actual, floor, target, accounts: acctList, role, holdings, monthly }) {
  const [showInfo, setShowInfo] = useState(false);
  const hideTimer = useRef(null);
  const openTip = () => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    setShowInfo(true);
  };
  const closeTipSoon = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowInfo(false), 180);
  };
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  const barPct = target > 0 ? Math.min(100, actual / target * 100) : 0;
  const status = actual < floor ? "below" : actual < target ? "ok" : "full";
  const barClr = status === "below" ? "var(--chart-1)" : status === "full" ? "var(--positive)" : color;
  const runway = monthly > 0 && num === 1 ? (actual / monthly).toFixed(1) : null;
  return (
    <div style={{ background: "var(--tint-white-02)", border: `1px solid ${withAlpha(color, "33")}`, borderLeft: `3px solid ${color}`, borderRadius: 9, padding: 14, flex: 1, position: "relative" }}>
      {(role || holdings) && (
        <div
          onMouseEnter={openTip}
          onMouseLeave={closeTipSoon}
          style={{ position: "absolute", top: 0, right: 0, paddingTop: 6, paddingRight: 6, paddingLeft: 10, paddingBottom: showInfo ? 8 : 4, cursor: "help" }}
          aria-label="Bucket role and recommended holdings"
        >
          <span style={{ color: showInfo ? color : "var(--text-secondary)", userSelect: "none", transition: "color 0.15s", display: "inline-flex" }}><InfoIcon size={13} /></span>
          {showInfo && (
            <div
              onMouseEnter={openTip}
              onMouseLeave={closeTipSoon}
              style={{ position: "absolute", top: "100%", right: 0, background: "rgba(15,23,42,0.98)", border: `1px solid ${withAlpha(color, "66")}`, borderRadius: 6, padding: 10, fontSize: "var(--fs-small)", color: "var(--text-secondary)", zIndex: 20, width: 240, lineHeight: 1.5, boxShadow: "0 6px 16px var(--tint-black-50)", cursor: "default" }}
            >
              {role && (
                <>
                  <div style={{ color, fontWeight: 600, marginBottom: 3, fontSize: "var(--fs-small)" }}>Role</div>
                  <div style={{ marginBottom: holdings ? 8 : 0 }}>{role}</div>
                </>
              )}
              {holdings && (
                <>
                  <div style={{ color, fontWeight: 600, marginBottom: 3, fontSize: "var(--fs-small)" }}>Recommended holdings</div>
                  <div>{holdings}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color }}>{label}</div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{horizon}</div>
        </div>
        <div style={{ textAlign: "right", paddingRight: role || holdings ? 16 : 0 }}>
          <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: barClr, fontFamily: "'JetBrains Mono',monospace" }}>{actual > 0 ? fmtDollar(actual) : "—"}</div>
          {runway && <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{runway} mo runway</div>}
        </div>
      </div>
      {(floor > 0 || target > 0) && (
        <>
          <div style={{ height: 6, background: "var(--card-border)", borderRadius: 3, overflow: "hidden", marginBottom: 4 }}>
            <div style={{ height: "100%", width: `${barPct}%`, background: barClr, borderRadius: 3, transition: "width 0.4s" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>
            <span>Floor {fmtDollar(floor)}</span>
            <span>Target {fmtDollar(target)}</span>
          </div>
        </>
      )}
      {acctList.length > 0 && (
        <div style={{ marginTop: 8, borderTop: "1px solid var(--tint-white-06)", paddingTop: 6 }}>
          {acctList.map(a => (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 2 }}>
              <span>{a.name}</span>
              <span style={{ color: "var(--text-muted)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(a.balance || 0)}</span>
            </div>
          ))}
        </div>
      )}
      {acctList.length === 0 && (
        <div style={{ marginTop: 8, fontSize: "var(--fs-small)", color: "var(--text-faint)", fontStyle: "italic" }}>No accounts assigned — use B{num} chips in <GoStep step={PROFILE_STEP_SAVINGS}>Plan inputs → Current Savings</GoStep></div>
      )}
    </div>
  );
}

function BucketsTab({ params = {}, onAssumptionChange }) {
  const [bCfg, setBCfg] = useState(_loadBCfg);
  const saveBCfg = (patch) => {
    const next = { ..._loadBCfg(), ...patch };
    try { localStorage.setItem(_BCFG_KEY, JSON.stringify(next)); } catch {}
    setBCfg(next);
  };
  // b1Years/b2Years moved OUT of localStorage-only bCfg and into the profile
  // (params.b1Years/.b2Years) — this display and the yield-sweep cap in
  // bucketStrategy.js were reading two disconnected copies of "years of
  // spending Bucket 1 should hold": this stepper wrote to localStorage only,
  // so adjusting it changed nothing in the actual simulation. Now there's
  // one number. drawMode stays localStorage-only (display-only, no engine
  // reads it).
  const b1Years  = params.b1Years ?? 2;     // user-adjustable, default 2yr
  const b2Years  = params.b2Years ?? 5;     // user-adjustable, default 5yr
  const drawMode = bCfg.drawMode ?? "net";  // 'net' (default) or 'gross'

  // Core params
  const sp         = params.sp        || 0;
  const port       = params.port      || 0;
  const retireAge  = params.retireAge || 60;
  const currentAge = params.currentAge|| 50;
  const ssAge      = params.ssAge     || 67;
  const gkFloor    = params.gkFloor   || 0;
  const yrsToRetire = Math.max(0, retireAge - currentAge);
  const retireYear  = new Date().getFullYear() + yrsToRetire;

  // Net draw: gross spend + mortgage P&I − rental/property income
  const mortAnnualPI = (() => {
    if (!params.mortBalance || params.mortBalance <= 0) return 0;
    const ms = mortgageSchedule(params.mortBalance, params.mortRate ?? 6.5, params.mortStart || "2020-01", params.mortTerm || 30, params.mortExtra || 0);
    return ms.pmt * 12;
  })();
  const propIncome  = params.propIncome || 0;
  const netDraw     = Math.max(0, sp + mortAnnualPI - propIncome);
  const spendBasis  = drawMode === "gross" ? sp : netDraw;   // what bucket targets sit on
  const monthly     = spendBasis > 0 ? Math.round(spendBasis / 12) : 0;

  // Actual balances from designated accounts.
  // Expand split accounts into per-bucket pieces so a single account can
  // feed more than one bucket (each piece carries its allocated slice of the balance).
  const accts  = expandAccountBuckets(params.accounts);
  const b1Accts = accts.filter(a => a.bucket === 1);
  const b2Accts = accts.filter(a => a.bucket === 2);
  const b3Accts = accts.filter(a => a.bucket === 3);
  const b1Actual = b1Accts.reduce((s, a) => s + (a.balance || 0), 0);
  const b2Actual = b2Accts.reduce((s, a) => s + (a.balance || 0), 0);
  const b3Actual = b3Accts.reduce((s, a) => s + (a.balance || 0), 0);
  const hasAccounts = (b1Actual + b2Actual + b3Actual) > 0;

  // Thresholds, driven by user-configured year targets (b1Years, b2Years)
  // and the active spend basis (net draw by default; gross spend if the
  // user opts in).
  const b1Floor    = spendBasis;                                       // 1yr — always the replenish trigger
  const b1Target   = spendBasis * b1Years;                             // user-set (default 3yr)
  const ssGapYears = Math.max(b2Years, ssAge - retireAge);             // at least b2Years bridge
  const b2Floor    = spendBasis * b2Years;
  const b2Target   = Math.max(b2Floor, Math.round(ssGapYears * spendBasis));

  // One call to the real engine, shared by the tax-guidance row below and the
  // runway estimate further down — not two independent reads of the same
  // simulation, and not a second, cruder projection of it (see the runway
  // comment below for what this replaced).
  const waterfallResult = useMemo(() => {
    if (!sp || !port) return null;
    try { return waterfallForActiveStrategy(params); }
    catch { return null; }
  }, [params]);
  const simRow = waterfallResult?.smart?.rows?.[0] ?? null;
  const marginalRate = simRow?.marginalBracket ?? 12;
  const irmaaRisk    = simRow?.landmines?.irmaaTriggered ?? false;

  // Bucket 1 runway — see computeBucket1Runway (engine/buckets.js) for the
  // depletion logic. This used to be approximated by a `monthsToFloor` flat
  // linear estimate (today's balance minus today's floor, over today's flat
  // monthly spend); reading the real schedule instead means it can't
  // silently disagree with what the Withdrawal Plan tab shows for the same plan.
  const runway = useMemo(() => {
    if (!hasAccounts || b1Accts.length === 0) return null;
    if (b1Actual < b1Floor) return null; // already below floor — the directive above already covers this
    const fracs = bucketFractionsByCategory(params.accounts);
    return computeBucket1Runway(waterfallResult?.smart?.rows, fracs, b1Years);
  }, [hasAccounts, b1Accts.length, b1Actual, b1Floor, waterfallResult, params.accounts, b1Years]);

  // Directive logic
  const directive = useMemo(() => {
    if (!hasAccounts || !sp) return { type: "setup" };

    const b2Taxable = b2Accts.filter(a => a.category === "taxable");
    const b2Pretax  = b2Accts.filter(a => a.category === "pretax");
    const b3All     = b3Accts;

    // Dynamic label: single account → its name; multiple → comma list
    const acctLabel = (pool, fallback) =>
      pool.length === 1 ? pool[0].name
      : pool.length > 1 ? pool.map(a => a.name).join(" + ")
      : fallback;

    function buildSteps(needed, taxablePool, pretaxPool, rothPool) {
      const steps = [];
      let rem = needed;
      const taxAvail = taxablePool.reduce((s, a) => s + (a.balance || 0), 0);
      if (taxAvail > 0 && rem > 0) {
        const amt = Math.min(rem, taxAvail);
        steps.push({ label: `Sell from ${acctLabel(taxablePool, "taxable accounts")} (B2)`, accounts: taxablePool, amount: amt, tax: "0% LTCG estimated", note: "Lowest-tax source — use first" });
        rem -= amt;
      }
      const preAvail = pretaxPool.reduce((s, a) => s + (a.balance || 0), 0);
      if (preAvail > 0 && rem > 0) {
        const amt = Math.min(rem, preAvail);
        const taxCost = Math.round(amt * marginalRate / 100);
        steps.push({ label: `Withdraw from ${acctLabel(pretaxPool, "pre-tax accounts")} (B2)`, accounts: pretaxPool, amount: amt, tax: `${marginalRate}% ordinary income (~${fmtDollar(taxCost)} tax)`, note: irmaaRisk ? "⚠ Monitor MAGI — IRMAA risk" : `Stays within ${marginalRate}% bracket` });
        rem -= amt;
      }
      if (rem > 0 && rothPool.length > 0) {
        const rothAvail = rothPool.reduce((s, a) => s + (a.balance || 0), 0);
        const amt = Math.min(rem, rothAvail);
        steps.push({ label: `Emergency — draw from ${acctLabel(rothPool, "Roth accounts")} (B3)`, accounts: rothPool, amount: amt, tax: "Tax-free", note: "Last resort — Roth grows tax-free, avoid if possible" });
      }
      return steps;
    }

    if (b1Actual < b1Floor) {
      const needed = b1Target - b1Actual;
      const b1Empty  = b1Accts.length === 0;
      const title    = b1Empty
        ? `Bucket 1 is empty — create a cash account and fund it with ${fmtDollar(needed)}`
        : `Bucket 1 has ${fmtDollar(b1Actual)} — below ${fmtDollar(b1Floor)} floor, replenish now`;
      return { type: "critical", title, needed, b1Actual, b1Empty, steps: buildSteps(needed, b2Taxable, b2Pretax, b3All) };
    }
    if (b1Actual < b1Target) {
      const needed = b1Target - b1Actual;
      return { type: "warning", title: `Bucket 1 has ${fmtDollar(b1Actual)} — ${fmtDollar(needed)} below ${b1Years}-yr target of ${fmtDollar(b1Target)}`, needed, optional: true, steps: buildSteps(needed, b2Taxable, b2Pretax, []) };
    }
    if (b2Actual < b2Floor) {
      const needed = b2Target - b2Actual;
      const b3Pretax = b3All.filter(a => a.category === "pretax");
      const b3Roth   = b3All.filter(a => a.category === "roth");
      const steps = [];
      let rem = needed;
      const preAvail = b3Pretax.reduce((s, a) => s + (a.balance || 0), 0);
      if (preAvail > 0) {
        const amt = Math.min(rem, preAvail);
        steps.push({ label: `Withdraw from ${acctLabel(b3Pretax, "pre-tax accounts")} (B3)`, accounts: b3Pretax, amount: amt, tax: `${marginalRate}% ordinary income`, note: `Within ${marginalRate}% bracket — move proceeds into bonds/balanced fund in taxable (B2)` });
        rem -= amt;
      }
      if (rem > 0 && b3Roth.length > 0) {
        const amt = Math.min(rem, b3Roth.reduce((s, a) => s + (a.balance || 0), 0));
        steps.push({ label: `Sell equity in ${acctLabel(b3Roth, "Roth accounts")} — transfer cash to B2`, accounts: b3Roth, amount: amt, tax: "Tax-free", note: "Sell equity holdings inside Roth; move the cash proceeds to your B2 bond/stable-value account" });
      }
      // Canonical rule: only replenish B2 from B3 when markets are favorable.
      return { type: "warning", title: "Bucket 2 below floor — replenish from Bucket 3", needed, steps, marketWarning: true };
    }
    return { type: "ok", title: "All buckets healthy — no action needed" };
  }, [b1Actual, b2Actual, b1Floor, b1Target, b2Floor, b2Target, b2Accts, b3Accts, marginalRate, irmaaRisk, hasAccounts, sp]);

  // Styles
  const DC = { critical: "var(--chart-1)", warning: "var(--accent-gold)", ok: "var(--positive)", setup: "var(--text-faint)" };
const DI_TONE = { critical: "bad", warning: "warn", ok: "ok" };
  const now = new Date();
  const monthName = now.toLocaleString("default", { month: "long" });


  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Monthly directive */}
      <div className="chart-card" style={{ borderLeft: `3px solid ${DC[directive.type]}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--text-primary)" }}>{monthName} {now.getFullYear()} — Retirement Directive</div>
            <div style={{ fontSize: "var(--fs-label)", color: DC[directive.type], fontWeight: 600, marginTop: 3 }}>
              {DI_TONE[directive.type] && <Dot tone={DI_TONE[directive.type]} />}{directive.title}
            </div>
          </div>
        </div>

        {directive.type === "setup" && (
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.6 }}>
            To get a directive:<br/>
            1. Enter your account balances in <GoStep step={PROFILE_STEP_SAVINGS}>Plan inputs → Current Savings</GoStep><br/>
            2. Use the <strong style={{ color: "var(--text-primary)" }}>[B1] [B2] [B3]</strong> buttons to assign each account to a bucket<br/>
            3. Enter your annual spending in <GoStep step={PROFILE_STEP_SPENDING}>Plan inputs → Spending &amp; Expenses</GoStep>
          </div>
        )}

        {directive.marketWarning && (
          <div style={{ background: "var(--tint-amber-08)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: "var(--fs-small)", color: "var(--accent-gold)", lineHeight: 1.5 }}>
            <strong>Market condition check required:</strong> Only move money from Bucket 3 into Bucket 2 when markets are <em>up or neutral</em>. If stocks are down significantly (&gt;15%), wait — let Bucket 2 cover you until markets recover. Selling growth assets in a downturn defeats the purpose of the bucket strategy.
          </div>
        )}
        {directive.b1Empty && (
          <div style={{ background: "rgba(14,165,233,0.08)", border: "1px solid var(--tint-sky-500-25)", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: "var(--fs-small)", color: "#7dd3fc", lineHeight: 1.6 }}>
            <strong>No B1 account assigned yet.</strong> Bucket 1 currently holds <strong>$0</strong>. To receive this transfer you need a cash account (HYSA, money market, SGOV) tagged as B1 in <GoStep step={PROFILE_STEP_SAVINGS}>Plan inputs → Current Savings</GoStep>. The steps below show which B2 accounts to sell — the proceeds go into that new B1 account.
          </div>
        )}
        {directive.steps?.length > 0 && (
          <>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 8 }}>
              {directive.optional
                ? `Optional top-up — Bucket 1 currently has ${fmtDollar(directive.b1Actual ?? 0)}, target is ${fmtDollar(directive.needed + (directive.b1Actual ?? 0))}:`
                : `Transfer ${fmtDollar(directive.needed)} from Bucket 2 → into your Bucket 1 cash account:`}
            </div>
            {directive.steps.map((step, i) => (
              <div key={i} style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-07)", borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: "var(--fs-label)", fontWeight: 600, color: "var(--text-primary)" }}>Step {i + 1} — {step.label}</span>
                  <span style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--positive)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(step.amount)}</span>
                </div>
                {step.accounts.map(a => (
                  <div key={a.id} style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 1 }}>
                    → {a.name}: {fmtDollar(a.balance || 0)} available
                  </div>
                ))}
                <div style={{ fontSize: "var(--fs-small)", marginTop: 5, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-secondary)" }}>Tax: {step.tax}</span>
                  {step.note && <span style={{ color: "var(--text-faint)" }}>{step.note}</span>}
                </div>
              </div>
            ))}
          </>
        )}

        {directive.type === "ok" && (
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", lineHeight: 1.6 }}>
            Check back periodically — or sooner if spending increases or markets fall significantly.
            {runway?.depleted && <> See the runway card below for when Bucket 1 is projected to need topping up.</>}
          </div>
        )}
      </div>

      {/* Bucket 1 runway — reads the SAME projected schedule the Withdrawal
          Plan tab shows, so this can't quietly disagree with it. See the
          `runway` useMemo above for what "depleted" means here. */}
      {runway && (
        <div className="chart-card" style={{ borderLeft: `3px solid ${runway.depleted ? "var(--accent-gold)" : "var(--positive)"}` }}>
          <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>
            Bucket 1 Runway
          </div>
          {runway.depleted ? (
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", lineHeight: 1.6 }}>
              At your current plan, <strong>Bucket 1 covers spending through {runway.year}</strong> (age {runway.age}).
              Around then, plan to move about <strong style={{ color: "var(--accent-gold)" }}>{fmtDollar(runway.refillAmount)}</strong>{" "}
              from Bucket 2 to refill it to your {b1Years}-year target.
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 6 }}>
                Based on your projected withdrawal schedule (Scenarios → Withdrawal Plan) — not a separate
                projection, and not a crash forecast. A market drop or a spending change would move this date.
              </div>
            </div>
          ) : (
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", lineHeight: 1.6 }}>
              Bucket 1 is projected to stay funded through your full plan — no refill from Bucket 2 is currently expected.
            </div>
          )}
        </div>
      )}

      {/* Buffer + draw-mode controls */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", padding: "6px 2px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>Buffer targets:</span>
        {[
          { label: "B1 years", key: "b1Years", val: b1Years, min: 1, max: 7, hint: "2–3 recommended" },
          { label: "B2 years", key: "b2Years", val: b2Years, min: 3, max: 12, hint: "5–10 recommended" },
        ].map(({ label, key, val, min, max, hint }) => {
          // b1Years/b2Years write to the PROFILE (onAssumptionChange) — this
          // is the field the engine actually reads for the yield-sweep cap.
          const setYears = (n) => (onAssumptionChange ? onAssumptionChange(key, n) : saveBCfg({ [key]: n }));
          return (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{label}:</span>
            <button onClick={() => setYears(Math.max(min, val - 1))} style={{ background: "var(--tint-white-06)", border: "1px solid var(--tint-white-10)", color: "var(--text-secondary)", borderRadius: 4, width: 20, height: 20, cursor: "pointer", fontSize: "var(--fs-body)", lineHeight: 1, padding: 0 }}>−</button>
            <span style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--text-primary)", minWidth: 14, textAlign: "center" }}>{val}</span>
            <button onClick={() => setYears(Math.min(max, val + 1))} style={{ background: "var(--tint-white-06)", border: "1px solid var(--tint-white-10)", color: "var(--text-secondary)", borderRadius: 4, width: 20, height: 20, cursor: "pointer", fontSize: "var(--fs-body)", lineHeight: 1, padding: 0 }}>+</button>
            <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{hint}</span>
          </div>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <span
            style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", cursor: "help" }}
            title={`Net (${fmtDollar(netDraw)}/yr) = spending + mortgage P&I − rental income — what the portfolio actually has to fund.\nGross (${fmtDollar(sp)}/yr) = headline spend, ignoring rental offsets and mortgage.`}
          >Draw basis:</span>
          {[
            { val: "net",   label: `Net ${fmtDollar(netDraw)}/yr` },
            { val: "gross", label: `Gross ${fmtDollar(sp)}/yr` },
          ].map(opt => {
            const active = drawMode === opt.val;
            return (
              <button
                key={opt.val}
                onClick={() => saveBCfg({ drawMode: opt.val })}
                style={{
                  background: active ? "var(--tint-sky-500-18)" : "var(--row-highlight)",
                  border: `1px solid ${active ? "#0ea5e9" : "var(--tint-white-10)"}`,
                  color: active ? "#0ea5e9" : "var(--text-secondary)",
                  borderRadius: 4, padding: "2px 8px", fontSize: "var(--fs-small)", cursor: "pointer", fontWeight: active ? 600 : 400,
                }}
              >{opt.label}</button>
            );
          })}
        </div>
      </div>

      {/* Bucket status cards */}
      <div style={{ display: "flex", gap: 10 }}>
        <BucketCard num={1} color={bucketColor(1)} label="Bucket 1 — Cash" horizon={`0–${b1Years} years · pay bills now`}
          actual={b1Actual} floor={b1Floor} target={b1Target} accounts={b1Accts} monthly={monthly}
          role={`${b1Years}-year runway at ${fmtDollar(spendBasis)}/yr ${drawMode === "net" ? "net draw" : "gross spend"}. Pays bills now — NEVER dual-purpose.`}
          holdings="HYSA · Money market · T-bills · CDs" />
        <BucketCard num={2} color={bucketColor(2)} label="Bucket 2 — Income" horizon={`${b1Years}–${b1Years + b2Years} years · refills B1`}
          actual={b2Actual} floor={b2Floor} target={b2Target} accounts={b2Accts} monthly={monthly}
          role={`Bridges ${ssGapYears}-yr SS gap (age ${retireAge}→${ssAge}). Refills Bucket 1 as it depletes.`}
          holdings="30–50% Equities · 50–70% Bonds · REITs" />
        <BucketCard num={3} color={bucketColor(3)} label="Bucket 3 — Growth" horizon={`${b1Years + b2Years}+ years · last resort`}
          actual={b3Actual} floor={0} target={0} accounts={b3Accts} monthly={monthly}
          role="Protects against inflation & grows wealth for a decade or more."
          holdings="50–100% Equities · Broad-market equity · International" />
      </div>
    </div>
  );
}
// Progress tab — check-in journal
const CHECKIN_TICK = { fill: "var(--text-secondary)", fontSize: "var(--fs-small)" };

// Five absolute 0–100 scores describing the "shape" of a plan snapshot.
// Absolute (not cohort-relative) so the same snapshot always scores the same:
//   confidence — MC success rate
//   retireBy   — planned retirement age, 50 → 100 pts down to 75 → 0 (lower age = better)
//   spend      — how comfortably the 4% rule covers the target: 4% × port vs sp
//   legacy     — median ending portfolio (p50); $1M+ scores 100
//   resilience — success rate under the 2000–2012 stress sequence
// Median ending portfolio that earns a full legacy score. Named because the
// help copy below quotes it — a literal in both places would let the number
// the user reads drift from the number the score uses.
const LEGACY_FULL_SCORE_PORT = 1_000_000;
function planShapeScores(c) {
  const clamp = (v) => Math.max(0, Math.min(100, v));
  return {
    confidence: clamp((c.successRate ?? 0) * 100),
    retireBy:   c.retireAge != null ? clamp(((75 - c.retireAge) / 25) * 100) : 0,
    spend:      c.sp > 0 && c.port != null ? clamp(((c.port * 0.04) / c.sp) * 100) : 0,
    legacy:     clamp(((c.medianTerminal ?? 0) / LEGACY_FULL_SCORE_PORT) * 100),
    resilience: clamp((c.stressRate ?? 0) * 100),
  };
}

// Merge imported check-ins into the existing journal: entries whose id already
// exists locally are skipped (local wins), result sorted by timestamp.
function mergeCheckIns(existing, imported) {
  const base = Array.isArray(existing) ? existing : [];
  const inc  = Array.isArray(imported) ? imported : [];
  const seen = new Set(base.map((c) => c.id));
  const merged = [...base];
  for (const c of inc) {
    if (!c || typeof c !== "object" || !c.id || !c.ts) continue;
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    merged.push(c);
  }
  return merged.sort((a, b) => new Date(a.ts) - new Date(b.ts));
}

const PLAN_SHAPE_AXES = [
  { key: "confidence", label: "Confidence", color: "var(--accent)",
    what: "Odds your money outlasts you",
    how: "Monte Carlo success rate across all simulated paths" },
  { key: "retireBy", label: "Retire by", color: "var(--positive)",
    what: "How early your plan retires you",
    how: "Planned retirement age on an absolute scale (50 scores 100, 75 scores 0 — lower is better)" },
  { key: "spend", label: "Spend", color: "#a3e635",
    what: "How comfortably the plan funds your spending target",
    how: "4% of today's portfolio vs your planned annual spend (at or above target scores 100)" },
  { key: "legacy", label: "Legacy", color: "var(--chart-4)",
    what: "What's left at end of plan",
    how: `Median ending portfolio (p50) — ${fmtDollar(LEGACY_FULL_SCORE_PORT)} or more scores 100` },
  { key: "resilience", label: "Resilience", color: "var(--chart-3)",
    what: "How well the plan weathers market stress",
    how: "Success rate with the 2000–2012 sequence forced at retirement" },
];

/* `exportCheckInsFile` was deleted here. Check-ins now ride in the one
 * profile export (see the Export button's payload — `checkIns` is part of
 * it), so a second button emitting a second file shape was a second thing
 * to remember to click, and the file it produced was the one a user was
 * most likely to be holding when they'd lost everything else.
 *
 * Import stays, and stays tolerant of both shapes: the old
 * `AiRA_Progress_*.json` ({kind:"aira_checkins", checkIns:[...]}) and a full
 * profile export both expose `.checkIns`, and a bare array is accepted too.
 * Removing an export is safe; removing the only way to read files users
 * already have on disk isn't.
 */

function ProgressTab({ checkIns, onDelete, onRename, onImport }) {
  const fmtDate = (ts) => {
    const d = new Date(ts);
    return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }).toUpperCase();
  };
  const isToday = (ts) => {
    const d = new Date(ts), n = new Date();
    return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  };
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        const arr = Array.isArray(data) ? data : data?.checkIns;
        if (Array.isArray(arr)) onImport(arr);
      } catch {}
    };
    reader.readAsText(file);
  };

  if (!checkIns || checkIns.length === 0) {
    return (
      <div style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: "48px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 34, marginBottom: 12 }}>📈</div>
        <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>Start your journey</div>
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.6, maxWidth: 460, margin: "0 auto 14px" }}>
          Save your first check-in to start tracking how your plan changes over time.
          The <strong style={{ color: "var(--accent-teal)" }}>Check-in</strong> button in the top toolbar snapshots
          today's plan — success rate, portfolio, and spending — as a point on your timeline.
          They travel with your profile export, but importing one never overwrites your plan
          inputs — a check-in is a running journal entry, shown here as a trend once you've
          saved a few.
        </div>
        <label style={{ fontSize: "var(--fs-small)", color: "var(--accent)", cursor: "pointer" }}>
          ⬆ Import progress from a previous export
          <input type="file" accept=".json,application/json" onChange={handleImportFile} style={{ display: "none" }} />
        </label>
      </div>
    );
  }

  const sorted = [...checkIns].sort((a, b) => new Date(a.ts) - new Date(b.ts));
  const first = sorted[0], latest = sorted[sorted.length - 1];
  const chartData = sorted.map((c) => ({
    date: fmtDate(c.ts),
    successPct: c.successRate != null ? +(c.successRate * 100).toFixed(1) : null,
    port: c.port ?? null,
  }));
  const ratePP = latest.successRate != null && first.successRate != null
    ? (latest.successRate - first.successRate) * 100 : null;
  const portDelta = latest.port != null && first.port != null ? latest.port - first.port : null;

  // ── Plan shape (radar) ──
  const sFirst = planShapeScores(first);
  const sToday = planShapeScores(latest);
  const hasTwo = sorted.length >= 2;
  const radarData = PLAN_SHAPE_AXES.map(({ key, label }) => ({
    axis: label, first: +sFirst[key].toFixed(0), today: +sToday[key].toFixed(0),
  }));
  const deltas = PLAN_SHAPE_AXES.map(({ key, label }) => ({ label, d: sToday[key] - sFirst[key] }));
  const maxShift = deltas.reduce((a, b) => (Math.abs(b.d) > Math.abs(a.d) ? b : a), { label: "", d: 0 });
  const shapeStable = !hasTwo || Math.abs(maxShift.d) <= 5;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 12 }}>
        <div className="met">
          <div className="ml">Latest success rate</div>
          <div className="mv" style={{ color: "var(--accent-teal)" }}>{latest.successRate != null ? `${(latest.successRate * 100).toFixed(1)}%` : "—"}</div>
          <div className="ms">{fmtDate(latest.ts)}</div>
        </div>
        <div className="met">
          <div className="ml">Since first check-in</div>
          <div className="mv" style={{ color: ratePP == null ? "var(--text-secondary)" : ratePP >= 0 ? "var(--positive)" : "var(--negative)" }}>
            {ratePP == null ? "—" : `${ratePP >= 0 ? "+" : ""}${ratePP.toFixed(1)}pp`}
          </div>
          <div className="ms">{fmtDate(first.ts)} → today</div>
        </div>
        <div className="met">
          <div className="ml">Portfolio change</div>
          <div className="mv" style={{ color: portDelta == null ? "var(--text-secondary)" : portDelta >= 0 ? "var(--positive)" : "var(--negative)" }}>
            {portDelta == null ? "—" : `${portDelta >= 0 ? "+" : "−"}${fmtDollar(Math.abs(portDelta))}`}
          </div>
          <div className="ms">{sorted.length} check-in{sorted.length === 1 ? "" : "s"}</div>
        </div>
      </div>

      {/* ── Plan shape over time (radar) ── */}
      <div style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: 16, marginBottom: 12 }}>
        <div className="ct">Plan shape over time</div>
        <div style={{
          fontSize: "var(--fs-label)", lineHeight: 1.5, padding: "8px 12px", borderRadius: 8, marginBottom: 10,
          background: shapeStable ? "rgba(56,189,248,0.06)" : "rgba(251,146,60,0.08)",
          border: `1px solid ${shapeStable ? "rgba(56,189,248,0.18)" : "var(--tint-orange-25)"}`,
          color: "var(--text-primary)",
        }}>
          {shapeStable
            ? "Your plan shape has been stable — no axis has moved by more than 5 points."
            : `Biggest shift since your first check-in: ${maxShift.label} ${maxShift.d > 0 ? "+" : ""}${maxShift.d.toFixed(0)} points.`}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <div style={{ flex: "1 1 300px", minWidth: 260 }}>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="var(--tint-white-12)" />
                <PolarAngleAxis dataKey="axis" tick={{ fill: "var(--text-primary)", fontSize: "var(--fs-small)" }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "var(--text-secondary)", fontSize: "var(--fs-small)" }} tickCount={5} angle={90} />
                {hasTwo && (
                  <Radar name={`First check-in (${fmtDate(first.ts)})`} dataKey="first"
                    stroke="var(--text-secondary)" fill="var(--text-secondary)" fillOpacity={0.12} strokeWidth={1.5} />
                )}
                <Radar name={`Today (${fmtDate(latest.ts)})`} dataKey="today"
                  stroke="var(--chart-10)" fill="var(--chart-10)" fillOpacity={0.25} strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: "var(--fs-small)" }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ flex: "1 1 280px", minWidth: 250, display: "flex", flexDirection: "column", gap: 8 }}>
            {PLAN_SHAPE_AXES.map(({ key, label, color, what, how }) => (
              <div key={key} style={{ borderLeft: `3px solid ${color}`, paddingLeft: 10 }}>
                <div style={{ fontSize: "var(--fs-label)" }}>
                  <strong style={{ color: "var(--text-primary)" }}>{label}</strong>
                  <span style={{ color: "var(--text-secondary)" }}> {what}</span>
                </div>
                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>{how}</div>
                <div style={{ fontSize: "var(--fs-small)", color, fontFamily: "'JetBrains Mono',monospace" }}>
                  {hasTwo ? `${sFirst[key].toFixed(0)} → ${sToday[key].toFixed(0)}` : sToday[key].toFixed(0)} / 100
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginTop: 8 }}>
          Each axis scores 0–100 on an absolute scale — higher is better on every axis.
          Goal: <strong style={{ color: "var(--accent-teal)" }}>expand the polygon over time</strong>.
        </div>
      </div>

      {sorted.length >= 2 ? (
        <div style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: 16, marginBottom: 12 }}>
          <div className="ct">Plan trend</div>
          {/* Success rate (%) and Portfolio ($) are unrelated scales — two
              single-axis charts sharing the same date x-axis instead of one
              dual-axis plot whose alignment would be arbitrary. */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="var(--row-highlight)" />
                <XAxis dataKey="date" tick={CHECKIN_TICK} />
                <YAxis domain={[0, 100]} tick={CHECKIN_TICK} tickFormatter={(v) => `${v}%`} width={42} />
                <Tooltip
                  contentStyle={{ background: "#0f1729", border: "1px solid var(--tint-white-15)", borderRadius: 8, fontSize: "var(--fs-label)" }}
                  formatter={(value, name) => [`${value}%`, name]}
                />
                <Line type="monotone" dataKey="successPct" name="Success rate" stroke="var(--accent-teal)" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="var(--row-highlight)" />
                <XAxis dataKey="date" tick={CHECKIN_TICK} />
                <YAxis tick={CHECKIN_TICK} tickFormatter={(v) => fmtDollar(v)} width={MONEY_AXIS_WIDTH} />
                <Tooltip
                  contentStyle={{ background: "#0f1729", border: "1px solid var(--tint-white-15)", borderRadius: 8, fontSize: "var(--fs-label)" }}
                  formatter={(value, name) => [fmtDollar(value), name]}
                />
                <Line type="monotone" dataKey="port" name="Portfolio" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="leg">
            <div className="li"><div className="ll" style={{ background: "var(--accent-teal)" }} />Success rate</div>
            <div className="li"><div className="ll" style={{ background: "#0ea5e9" }} />Portfolio</div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", background: "var(--tint-sky-06)", border: "1px solid rgba(90,209,240,0.15)", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
          Save another check-in later to see your trend here — one point doesn't make a line yet.
        </div>
      )}

      {/* ── Check-in history (cards) ── */}
      <div style={{
      background: "var(--card-bg)",
      border: "1px solid var(--tint-white-08)",
      borderRadius: 12, padding: 16,
    }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div className="ct" style={{ marginBottom: 0 }}>Check-in history</div>
          <div style={{ fontSize: "var(--fs-small)", display: "flex", gap: 12, alignItems: "center" }}>
            {/* Says where the export went rather than leaving a gap where a
                button used to be — a user who has clicked "Export progress"
                before needs to be told it is now part of the profile export,
                not left to conclude the feature was removed. */}
            <span style={{ color: "var(--text-muted)" }}>
              ⬇ Included in <strong style={{ color: "var(--text-secondary)" }}>Export</strong> (top toolbar)
            </span>
            <span style={{ color: "var(--text-faint)" }}>·</span>
            <label style={{ color: "var(--accent)", cursor: "pointer" }}>
              ⬆ Import progress
              <input type="file" accept=".json,application/json" onChange={handleImportFile} style={{ display: "none" }} />
            </label>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {[...sorted].reverse().map((c) => (
            <div key={c.id} style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                  {fmtDate(c.ts)}{isToday(c.ts) ? " · TODAY" : ""}
                </span>
                <input
                  defaultValue={c.name || ""}
                  placeholder="Name this check-in…"
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (c.name || "")) onRename(c.id, v); }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  style={{
                    flex: 1, minWidth: 80, background: "transparent", border: "none", outline: "none",
                    color: "var(--text-primary)", fontSize: "var(--fs-body)", fontWeight: 700, fontFamily: "inherit",
                  }}
                />
                <button onClick={() => onDelete(c.id)} title="Delete this check-in"
                  style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "var(--fs-body)", lineHeight: 1 }}>
                  ×
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {[
                  ["SUCCESS RATE", c.successRate != null ? `${(c.successRate * 100).toFixed(0)}%` : "—", "var(--accent-teal)"],
                  ["STRESS SR",    c.stressRate  != null ? `${(c.stressRate  * 100).toFixed(0)}%` : "—", "var(--chart-3)"],
                  ["PORTFOLIO",    c.port           != null ? fmtDollar(c.port)           : "—", "var(--text-primary)"],
                  ["SPENDING",     c.sp             != null ? fmtDollar(c.sp)             : "—", "var(--text-primary)"],
                  ["LEGACY",       c.medianTerminal != null ? fmtDollar(c.medianTerminal) : "—", "var(--chart-4)"],
                  ["RETIRE / PLAN TO", c.retireAge != null ? `${c.retireAge} / ${c.endAge ?? "—"}` : "—", "var(--text-primary)"],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ background: "var(--row-highlight)", border: "1px solid var(--tint-white-09)", borderRadius: 8, padding: "6px 10px", minWidth: 86 }}>
                    <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", letterSpacing: "0.08em" }}>{label}</div>
                    <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color, fontFamily: "'JetBrains Mono',monospace" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Stress-scenario constants.
// Scenario knobs (not tax constants): a memory-care shock and its duration.
// Crash severity is user-driven via the buttons below.
const STRESS_QUICK_PATHS = 200;
const STRESS_QUICK_PATHS_LABEL = STRESS_QUICK_PATHS.toLocaleString();                 // fast, noisy estimate
// How much earlier the stress tab moves an authored first death. Named so
// the scenario label and the mutation can't drift apart.
const STRESS_DEATH_SOONER_YEARS = 10;
const LTC_ANNUAL_COST = 110000;                 // memory care ≈ $110k/yr, today's $
const LTC_YEARS = 3;

// Success-rate → traffic-light color, shared by the gauge ring and the pp delta.
function stressRateColor(rate) {
  if (rate == null) return "var(--text-muted)";
  if (rate >= 0.85) return "var(--positive)";
  if (rate >= 0.70) return "var(--accent-gold)";
  return "var(--chart-1)";
}

// Small donut gauge showing a scenario's surviving-plans percentage.
function ScenarioGauge({ pct, color }) {
  const r = 24, circ = 2 * Math.PI * r;
  const dash = circ * Math.max(0, Math.min(100, pct)) / 100;
  return (
    <svg width="58" height="58" viewBox="0 0 58 58" style={{ flexShrink: 0 }}>
      <circle cx="29" cy="29" r={r} fill="none" stroke="var(--card-border)" strokeWidth="5" />
      <circle cx="29" cy="29" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`} transform="rotate(-90 29 29)" />
      <text x="29" y="34" textAnchor="middle" fill={color} fontSize="16" fontWeight="700"
        fontFamily="'JetBrains Mono',monospace">{pct == null ? "—" : Math.round(pct)}</text>
    </svg>
  );
}

// Card grid of "what if" stress scenarios. Each card shows a quick low-path
// estimate immediately, with a "Run full" button that re-runs the real engine
// (MC_PATHS) for that one scenario. Deltas are computed against a like-for-like
// baseline: estimate-vs-estimate, or (once run) full-vs-full using the app's mc.
function StressScenarioGrid({ p, baseRate, fmtPct, onSeqResult }) {
  const [severity, setSeverity] = useState(-0.40);
  const [crashOpen, setCrashOpen] = useState(false);
  const [est, setEst] = useState(null);        // { base, [id]: rate } low-path
  const [full, setFull] = useState({});        // { [id]: rate } full-path
  const [running, setRunning] = useState(null);
  const [sevNote, setSevNote] = useState("");   // why a precise crash result disappeared
  const endAge = p.endAge || 90;

  /* Has the user authored a first death in their Profile? If so the stress
   * scenario varies their model rather than inventing one. */
  const authoredDeath = !!(p.spouse?.enabled && Number(p.spouse?.deathAge) > 0);
  /* The decedent's own age when the death is moved earlier. Floored at
   * their current age + 1 — a death already in the past isn't a scenario. */
  const soonerDeathAge = useMemo(() => {
    if (!authoredDeath) return null;
    const decedentIsPrimary = p.spouse?.firstToDie === "primary";
    const nowAge = decedentIsPrimary
      ? (personAgeNow(p) ?? p.currentAge ?? 0)
      : (personAgeNow(p.spouse) ?? p.currentAge ?? 0);
    return Math.max((nowAge || 0) + 1, Number(p.spouse.deathAge) - STRESS_DEATH_SOONER_YEARS);
  }, [authoredDeath, p]);

  const scenarios = useMemo(() => [
    {
      id: "crash", label: "Market crashes early",
      sub: `One-year ${Math.round(severity * 100)}% equity shock right at retirement`,
      run: (N, seed) => runMC(p, endAge, N, seed, true, [severity]),
    },
    {
      id: "ltc", label: "Long-term care event",
      sub: `${LTC_YEARS} years of memory care ≈ ${fmtDollar(LTC_ANNUAL_COST)}/yr`,
      run: (N, seed) => runMC(
        { ...p, carveouts: [...(p.carveouts || []), {
          id: "_ltc_shock", label: "LTC shock",
          // 3-year care cost absorbed across the remaining plan (engine
          // carveouts run from retirement; amortizing keeps the total real
          // without a start-year lever the engine doesn't have).
          annual: Math.round(LTC_ANNUAL_COST * LTC_YEARS / Math.max(1, endAge - p.retireAge)),
          endYear: null,
        }] },
        endAge, N, seed, true),
    },
    {
      id: "live100", label: "Live to 100",
      sub: `${Math.max(0, 100 - endAge)} extra years of withdrawals`,
      run: (N, seed) => runMC(p, 100, N, seed, true),
    },
    {
      id: "survivor",
      // This scenario used to be a second death model: it forced
      // filingStatus "single" from day one of retirement, applied its own
      // SS haircut, ignored spouse.deathAge entirely (by setting enabled:
      // false), and never extended the horizon. So a user who carefully
      // modeled a death at 78 clicked here and got an answer computed from
      // a different death and a different survivor rule, with nothing
      // saying their setting was discarded.
      //
      // It's now a variation on the model the user authored. When a first
      // death is on file we move that death earlier and let the engine do
      // everything else — filesJointlyAt handles the filing-status flip
      // (MFJ through the death year), the survivor benefit rules apply the
      // permanent reduction and the PIA basis, and planEndAgeOnPrimaryClock
      // extends the horizon for a younger survivor. The tab then answers
      // the question a user actually has — "how much worse if the timing is
      // bad?" — instead of re-answering "what if there were a death at
      // all," which the base plan already covers and the widow's-penalty
      // card above reports.
      label: authoredDeath ? `First death ${STRESS_DEATH_SOONER_YEARS} years sooner` : "Spouse passes early",
      sub: authoredDeath
        ? `Your modelled first death at ${soonerDeathAge} instead of ${p.spouse.deathAge} — same survivor rules, worse timing`
        : "Survivor keeps the larger SS check AND files Single, from day one — a worst-case bound, not a forecast",
      run: (N, seed) => runMC(
        authoredDeath
          // Only the timing changes. Every other survivor rule is inherited.
          ? { ...p, spouse: { ...p.spouse, deathAge: soonerDeathAge } }
          // No death on file, so there's nothing to move. Keep the
          // long-standing day-one bound (relabeled above so it isn't
          // mistaken for a forecast). The 0.67 fallback stays for profiles
          // with no spousal data: it's only right for a one-earner couple,
          // but it's what those profiles have always seen here, and
          // regressing them to "no haircut at all" would be worse.
          : {
              ...p,
              ssb: p.spouse?.enabled
                ? Math.max(p.ssb || 0, p.spouse.ssb || 0)
                : Math.round((p.ssb || 0) * 0.67),
              spouse: { ...(p.spouse || {}), enabled: false },
              filingStatus: "single",
              twoHousehold: false,
            },
        endAge, N, seed, true
      ),
    },
  ], [p, endAge, severity]);

  // These estimates are six simulations, so they run when asked and not
  // before. The signature is what the last set was computed from: when the
  // plan changes underneath them they are marked stale rather than silently
  // recomputed.
  const [estRunning, setEstRunning] = useState(false);
  const estSigRef = useRef(null);
  const inputSig = useMemo(() => {
    try { return JSON.stringify(p) + "|" + severity + "|" + endAge; } catch { return null; }
  }, [p, severity, endAge]);
  const estStale = est != null && estSigRef.current !== inputSig;

  const runEstimates = () => {
    setEstRunning(true);
    // Yield so the button can paint its running state before the engine
    // takes the main thread; see runSimulation for the same pattern.
    setTimeout(() => {
      const out = { base: runMC(p, endAge, STRESS_QUICK_PATHS, 7, true).rate };
      scenarios.forEach((s) => { out[s.id] = s.run(STRESS_QUICK_PATHS, 7).rate; });
      // The widow's-penalty counterfactual: the same plan with the modeled
      // first death removed. Same seed and path count as `base` above on
      // purpose — with a different seed part of the "penalty" would be RNG
      // noise, and a card labeled "what this death costs" would be
      // reporting randomness. Only computed when a death is actually
      // modeled, so this costs nothing for the profiles that don't use the feature.
      out.noDeath = authoredDeath
        ? runMC({ ...p, spouse: { ...p.spouse, deathAge: null } }, endAge, STRESS_QUICK_PATHS, 7, true).rate
        : null;
      estSigRef.current = inputSig;
      setEst(out);
      setFull({});   // full-path results were measured against the old inputs
      setEstRunning(false);
    }, 0);
  };

  const runFull = (s) => {
    setRunning(s.id);
    setTimeout(() => {
      const rate = s.run(MC_PATHS, 43).rate;
      setFull((f) => ({ ...f, [s.id]: rate }));
      if (s.id === "crash") setSevNote("");
      setRunning(null);
    }, 30);
  };

  const setSev = (v) => {
    setSeverity(v);
    setFull((f) => { const n = { ...f }; delete n.crash; return n; }); // crash result now stale
    setSevNote(full.crash != null ? "Severity changed, so the precise result was cleared. Run it again." : "");
  };

  const worst = est
    ? scenarios.reduce((lo, s) => Math.min(lo, full[s.id] ?? est[s.id] ?? 1), 1)
    : null;

  return (
    <div className="chart-card" style={{ marginBottom: 12 }}>
      <div className="ct" style={{ marginBottom: 4 }}>Stress scenarios against your plan</div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.5 }}>
        Each card re-runs your plan with one thing gone wrong. The big number is how many points the share
        of funded plans falls versus your baseline; <b style={{ color: "var(--text-secondary)" }}>drops to</b> is
        the share still funded. <b style={{ color: "var(--text-secondary)" }}>Quick check</b> gives every card a fast
        estimate; <b style={{ color: "var(--text-secondary)" }}>Precise run</b> gives one card the full
        {" "}{MC_PATHS_LABEL}-path result.
        {/* The success rate is widely read as "the chance my retirement
            works." It's narrower than that, and saying so changes how the
            number should be read. */}
        <div style={{ marginTop: 6, color: "var(--text-muted)" }}>
          What the simulation varies: <b style={{ color: "var(--text-secondary)" }}>market returns, inflation,
          rental reliability and healthcare shocks</b>. Your spending, claim ages, retirement age and
          any modelled death are held at what you entered — so this measures how much
          <em> market</em> risk the plan absorbs, not the overall odds your retirement works out.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <button
          onClick={runEstimates}
          disabled={estRunning}
          className={estStale || !est ? "mbtn on" : "mbtn"}
          style={{ fontSize: "var(--fs-label)", padding: "6px 14px", fontWeight: 700, opacity: estRunning ? 0.6 : 1 }}
        >
          {estRunning
            ? `Running ${STRESS_QUICK_PATHS_LABEL} paths…`
            : est ? "Re-run quick check" : "Quick check"}
        </button>
        <span style={{ fontSize: "var(--fs-small)", color: estStale ? "var(--accent-gold)" : "var(--text-faint)" }}>
          {estRunning ? "Testing your plan against each scenario…"
            : !est ? "Nothing has been run yet — press Quick check to test your plan."
            : estStale ? "Your plan changed. These results are from the previous inputs."
            : "Up to date with your current plan."}
        </span>
      </div>

      {/* The widow's-penalty delta.
          The base plan already contains the modeled death, so the scenario
          grid's "vs baseline" can't show what that death costs — it
          compares against a baseline that includes it. This card is the
          missing comparison: the same plan with the death removed. Without
          it a user sets a death age, watches the success rate change, and
          has no way to attribute the change. */}
      {authoredDeath && est?.noDeath != null && (() => {
        const withDeath = est.base;
        const without   = est.noDeath;
        const pp        = (withDeath - without) * 100;      // negative = it costs you
        const col       = pp <= -10 ? "var(--negative)" : pp <= -3 ? "var(--accent-gold)" : "var(--positive)";
        const whoDies   = p.spouse?.firstToDie === "primary" ? "your" : "your spouse's";
        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 10, marginBottom: 12 }}>
            <div className="met" style={{ background: `${withAlpha(col, "0d")}`, border: `1px solid ${withAlpha(col, "33")}` }}>
              <div className="ml">Widow's penalty</div>
              <div className="mv" style={{ color: col }}>
                {`${pp > 0 ? "+" : ""}${pp.toFixed(1)}pp`}
              </div>
              <div className="ms">
                {fmtPct(without)} without {whoDies} death → {fmtPct(withDeath)} with it, at age {p.spouse.deathAge}
              </div>
            </div>
            <div className="met">
              <div className="ml">What this figure is</div>
              <div className="mv" style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.45 }}>
                Your own plan, run twice
              </div>
              <div className="ms">
                Same {STRESS_QUICK_PATHS_LABEL} market paths and the same random seed both times, so the
                gap is the death alone — not luck. It is the lost benefit plus the tax
                increase from filing Single, net of one person's costs.
              </div>
            </div>
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 10 }}>
        {scenarios.map((s) => {
          const real = full[s.id];
          const measured = real != null;
          const rate = measured ? real : est ? est[s.id] : null;         // 0..1
          const baseline = measured ? (baseRate ?? est?.base) : est?.base;
          const deltaPP = rate != null && baseline != null ? (rate - baseline) * 100 : null;
          const color = stressRateColor(rate);
          const isRunning = running === s.id;
          return (
            <div key={s.id} style={{
              background: `${withAlpha(color, "0d")}`, border: `1px solid ${withAlpha(color, "33")}`,
              borderRadius: 12, padding: "13px 15px",
              ...(s.id === "crash" && crashOpen ? { gridColumn: "1 / -1", order: -1 } : null),
            }}>
              <div style={{ display: "flex", gap: 12 }}>
                <ScenarioGauge pct={rate == null ? null : rate * 100} color={color} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-secondary)" }}>
                    {s.label}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, margin: "3px 0 1px" }}>
                    <span style={{ fontSize: "var(--fs-h1)", fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>
                      {deltaPP == null ? "—" : `${deltaPP > 0 ? "+" : ""}${deltaPP.toFixed(0)}`}
                    </span>
                    <span style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", fontWeight: 600 }}>pp</span>
                  </div>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)" }}>
                    drops to <b style={{ color, fontFamily: "'JetBrains Mono',monospace" }}>{rate == null ? "—" : fmtPct(rate)}</b>
                  </div>
                </div>
              </div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 9, lineHeight: 1.45 }}>{s.sub}</div>

              {s.id === "crash" && (
                <div style={{ marginTop: 9 }}>
                  <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 5 }}>
                    Crash severity
                  </div>
                  <div style={{ display: "flex", gap: 5 }}>
                    {[-0.10, -0.25, -0.40].map((v) => (
                      <button key={v} onClick={() => setSev(v)}
                        style={{
                          flex: 1, padding: "3px 0", minHeight: 28, fontSize: "var(--fs-small)", borderRadius: 5, cursor: "pointer",
                          fontFamily: "'JetBrains Mono',monospace", fontWeight: 700,
                          border: `1px solid ${severity === v ? SEQ_CRASH : "var(--card-border)"}`,
                          background: severity === v ? "var(--bg-danger)" : "transparent",
                          color: severity === v ? SEQ_CRASH : "var(--text-muted)",
                        }}>
                        {Math.round(v * 100)}%
                      </button>
                    ))}
                  </div>
                  {sevNote && <div role="status" style={{ fontSize: "var(--fs-small)", color: "var(--accent-gold)", marginTop: 5 }}>{sevNote}</div>}
                  <button type="button" onClick={() => setCrashOpen((o) => !o)} aria-expanded={crashOpen}
                    style={{ marginTop: 8, width: "100%", padding: "7px 0", minHeight: 32, borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                      fontSize: "var(--fs-label)", fontWeight: 600, color: SEQ_CRASH, background: crashOpen ? "var(--bg-danger)" : "transparent",
                      border: `1px solid ${SEQ_CRASH}` }}>
                    {crashOpen ? "Close crash editor" : "Edit the crash: real history or your own years"}
                  </button>
                </div>
              )}
              {s.id === "crash" && crashOpen && <SequenceBuilder p={p} onResult={onSeqResult} />}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                <span style={{ fontSize: "var(--fs-small)", color: measured ? "var(--positive)" : "var(--text-faint)", fontWeight: 600 }}>
                  {measured ? `Precise, ${MC_PATHS_LABEL} paths` : est ? (estStale ? "previous inputs" : "fast estimate") : "run the quick check first"}
                </span>
                <button onClick={() => runFull(s)} disabled={isRunning || !est} className="mbtn" title={est ? undefined : "Run the quick check first"}
                  style={{ fontSize: "var(--fs-small)", padding: "3px 10px", minHeight: 28, opacity: isRunning || !est ? 0.5 : 1 }}>
                  {isRunning ? "Running…" : measured ? "Re-run" : "Precise run"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", marginTop: 12 }}>
        {worst == null ? "Run the quick check to see your worst case."
          : worst >= 0.70
          ? "Even the worst case here stays in the recoverable range."
          : "At least one scenario pushes the plan into fragile territory — worth a mitigation."}
      </div>
    </div>
  );
}

// Year-by-year look at three representative simulated plans that ran out of
// money: the earliest failure, the median one, and the latest (closest call).
// Dollars are nominal. Rows come from runMC(..., keepFailures).
function FailedPathPanel({ failures, n }) {
  const [which, setWhich] = useState("worst");
  const [allCols, setAllCols] = useState(false);
  const box = { marginTop: 12 };
  if (!failures) {
    return <div className="chart-card" style={box}><div className="ct">Why plans failed</div>
      <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>No simulated path ran out of money in this scenario.</div></div>;
  }
  const f = failures[which];
  const picks = [["worst", "Worst case"], ["typical", "Typical failure"], ["closest", "Closest call"]];
  const WHICH_BLURB = {
    worst: "Worst case: the plan that ran out earliest.",
    typical: "Typical failure: the middle one, half ran out sooner and half later.",
    closest: "Closest call: the plan that ran out latest.",
  };
  const cols = [["Age", "age", true], ["Stocks", "stock", true], ["Portfolio return", "ret"], ["Inflation", "infl"], ["Spend", "spend"], ["Income", "income"], ["Tax", "tax"], ["Start", "start"], ["End", "end", true]]
    .filter((c) => allCols || c[2]);
  const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(1)}%`);
  const th = { textAlign: "right", padding: "3px 8px", fontWeight: 600, color: "var(--text-faint)", whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "3px 8px", fontFamily: "'JetBrains Mono',monospace", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  return (
    <div className="chart-card" style={box}>
      <div className="ct" style={{ marginBottom: 4 }}>Why plans failed</div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 8 }}>
        {failures.count.toLocaleString()}{n ? ` of ${n.toLocaleString()}${` (${((failures.count / n) * 100).toFixed(0)}%)`}` : ""} simulated paths ran out of money. Pick one to see what happened each year (nominal dollars).
      </div>
      <div style={{ fontSize: "var(--fs-label)", color: "var(--text-primary)", marginBottom: 8 }}>
        {WHICH_BLURB[which]} Stocks {pct(f.rows[0]?.stock)} in year 1; money ran out at {f.exhaustAge}.
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {picks.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setWhich(k)}
                  style={{ fontSize: "var(--fs-small)", padding: "3px 10px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit",
                    minHeight: 28, border: `1px solid ${which === k ? "var(--accent-teal)" : "var(--card-border)"}`,
                    background: which === k ? "var(--bg-success)" : "transparent",
                    color: which === k ? "var(--accent-teal)" : "var(--text-secondary)" }}
                  aria-pressed={which === k}>
            {label} · out at {failures[k].exhaustAge}
          </button>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "var(--fs-small)", width: "100%" }}>
          <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Year-by-year path for the {picks.find((x) => x[0] === which)[1].toLowerCase()}</caption>
          <thead><tr>{cols.map(([h, k]) => <th key={k} scope="col" style={k === "age" ? { ...th, position: "sticky", left: 0, background: "var(--bg-base)", textAlign: "left" } : th}>{h}</th>)}</tr></thead>
          <tbody>
            {f.rows.map((r) => {
              const cell = {
                age: <td key="age" style={{ ...td, textAlign: "left", position: "sticky", left: 0, background: r.short > 0 ? "var(--bg-danger)" : "var(--bg-base)" }}>{r.age}</td>,
                stock: <td key="stock" style={{ ...td, color: r.stock < 0 ? "var(--negative)" : "var(--positive)" }}>{pct(r.stock)}</td>,
                ret: <td key="ret" style={td}>{pct(r.ret)}</td>,
                infl: <td key="infl" style={td}>{pct(r.infl)}</td>,
                spend: <td key="spend" style={td}>{fmtDollar(r.spend)}</td>,
                income: <td key="income" style={td}>{fmtDollar(r.income)}</td>,
                tax: <td key="tax" style={td}>{fmtDollar(r.tax)}</td>,
                start: <td key="start" style={td}>{fmtDollar(r.start)}</td>,
                end: <td key="end" style={td}>{r.short > 0 ? `short ${fmtDollar(r.short)}` : fmtDollar(r.end)}</td>,
              };
              return <tr key={r.age} style={r.short > 0 ? { background: "var(--bg-danger)" } : undefined}>{cols.map(([, k]) => cell[k])}</tr>;
            })}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => setAllCols((v) => !v)} aria-expanded={allCols}
              style={{ marginTop: 8, background: "transparent", border: "none", color: "var(--accent-teal)", fontSize: "var(--fs-label)", cursor: "pointer", fontFamily: "inherit", padding: "4px 0", minHeight: 28 }}>
        {allCols ? "Show fewer columns ▴" : "Show all columns: returns, spending, income, tax ▾"}
      </button>
    </div>
  );
}

const SEQ_PICKS = [1929, 1966, 1973, 2000, 2007];
// A sequence this long is a sequence-risk test; past ~10 years it is just a market forecast.
const SEQ_DEFAULT_YEARS = 5;
const SEQ_HISTORY_LENGTHS = [5, 10, 15];
const SEQ_MAX_CUSTOM_YEARS = 10;
const SEQ_INPUT_STYLE = { background: "var(--card-bg-raised)", border: "1px solid var(--card-border)", color: "var(--text-primary)", borderRadius: 6, padding: "5px 8px", fontSize: "var(--fs-label)", minHeight: 28 };
// A year outside this range is almost certainly a typo, not a market.
const SEQ_RETURN_MIN = -90, SEQ_RETURN_MAX = 200;
const parseSeqText = (t) => t.split(/[\s,;]+/).filter((x) => x !== "").slice(0, SEQ_MAX_CUSTOM_YEARS);

// Replay a real historical start year (stocks, bonds and inflation paired) or a
// hand-typed run of equity returns, from retirement or from today, through the
// same runMC the rest of the app uses. Lifts the result to ScenariosTab, which
// shows it in the stress-test slot below.
const SEQ_NAMES = { 1929: "Great Depression", 1966: "Stagflation", 1973: "Oil shock", 2000: "Dot-com bust", 2007: "Financial crisis" };
const SEQ_CRASH = "var(--negative)";

// Bars for a run of yearly returns: below the line is red, above is teal, so a
// crash reads as a picture before it reads as numbers. `items` = [{label, r}].
function SeqBars({ items, big }) {
  const half = big ? 44 : 28;
  const maxAbs = Math.max(0.3, ...items.map((i) => Math.abs(i.r || 0)));
  return (
    <div style={{ display: "flex", gap: big ? 8 : 4, alignItems: "stretch" }}>
      {items.map((it, i) => {
        const r = it.r || 0;
        const h = Math.max(2, Math.round((Math.min(Math.abs(r), maxAbs) / maxAbs) * half));
        return (
          <div key={i} style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
            <div style={{ height: half * 2, position: "relative", borderBottom: "none" }}>
              <div style={{ position: "absolute", left: "18%", right: "18%", borderRadius: 3,
                ...(r < 0 ? { top: half, height: h, background: SEQ_CRASH } : { bottom: half, height: h, background: "var(--positive)" }) }} />
              <div style={{ position: "absolute", left: 0, right: 0, top: half, height: 1, background: "var(--divider)" }} />
            </div>
            <div style={{ fontSize: big ? "var(--fs-label)" : "var(--fs-small)", fontFamily: "'JetBrains Mono',monospace", fontVariantNumeric: "tabular-nums", color: r < 0 ? "var(--negative)" : "var(--positive)", fontWeight: 700 }}>
              {r > 0 ? "+" : ""}{(r * 100).toFixed(0)}%
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{it.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function SequenceBuilder({ p, onResult }) {
  const [mode, setMode] = useState("history");
  const [startYear, setStartYear] = useState(2000);
  const [years, setYears] = useState(SEQ_DEFAULT_YEARS);
  const [custom, setCustom] = useState(["-30", "-20", "-10", "0", "10"]);
  const [today, setToday] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [pasteNote, setPasteNote] = useState("");
  const endAge = p.endAge || 90;

  // Custom years that will actually run, plus why a year was left out.
  const typed = custom.map((v) => v.trim()).filter((v) => v !== "");
  const valid = typed.filter((v) => Number.isFinite(Number(v)));
  const outOfRange = valid.filter((v) => Number(v) < SEQ_RETURN_MIN || Number(v) > SEQ_RETURN_MAX);
  const hist = mode === "history";
  const histSeq = historicalSequence(startYear, years);
  let problem = "";
  if (!hist) {
    if (!valid.length) problem = "Enter at least one year to run.";
    else if (outOfRange.length) problem = `${outOfRange.join(", ")}% looks like a typo. Returns outside ${SEQ_RETURN_MIN}% to +${SEQ_RETURN_MAX}% are not realistic, so the scenario will not run.`;
    else if (valid.length < typed.length) problem = `${typed.length - valid.length} entry isn't a number and will be skipped.`;
  }
  const canRun = !busy && (hist || (valid.length > 0 && !outOfRange.length));

  const run = () => {
    const seq = hist ? histSeq : valid.map((v) => Number(v) / 100);
    if (!seq.length) return;
    setBusy(true);
    setTimeout(() => {
      const r = runMC(p, endAge, STRESS_PATHS, 99, true, seq, today, true);
      const when = today ? "starting today" : "at retirement";
      onResult({
        rate: r.rate, pcts: r.pcts, failures: r.failures ?? null, n: r.N,
        title: hist
          ? `Stress test: ${seq[0].year}–${seq[seq.length - 1].year} actual market sequence ${when}`
          : `Stress test: custom sequence (${valid.join(", ")}%) ${when}`,
        chips: seq.map((e, i) => ({ label: hist ? String(e.year) : `Yr ${i + 1}`, r: hist ? e.stock : e })),
      });
      setBusy(false);
      setStatus(`Done: ${seq.length} ${hist ? "historical" : "custom"} years ${when}. Results are below.`);
      // The result renders further down the page; bring it to the Run button's user.
      if (typeof document !== "undefined") document.getElementById("stress-result")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 30);
  };

  const pill = (on) => ({
    fontSize: "var(--fs-label)", padding: "6px 14px", minHeight: 32, borderRadius: 16, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${on ? SEQ_CRASH : "var(--card-border)"}`,
    background: on ? "var(--bg-danger)" : "transparent", color: on ? SEQ_CRASH : "var(--text-secondary)",
  });
  const lab = { fontSize: "var(--fs-label)", color: "var(--text-muted)", marginRight: 8 };
  const head = { fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 8 };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--divider)" }}>
      <div style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 12, maxWidth: 760 }}>
        Replace the one-year shock with a run of real or imagined markets. These numbers change stock returns only;
        bonds and inflation stay random (in real history they come from the same years). When the run ends,
        returns go back to random history.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <button type="button" style={pill(mode === "history")} aria-pressed={mode === "history"} onClick={() => setMode("history")}>Real history</button>
        <button type="button" style={pill(mode === "custom")} aria-pressed={mode === "custom"} onClick={() => setMode("custom")}>My own numbers</button>
      </div>

      {hist ? (
        <div style={{ marginBottom: 14 }}>
          <div style={head}>Pick a market</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginBottom: 12 }}>
            {SEQ_PICKS.map((y) => {
              const on = startYear === y;
              return (
                <button key={y} type="button" onClick={() => setStartYear(y)} aria-pressed={on}
                        style={{ textAlign: "left", padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", minHeight: 72,
                          border: `1px solid ${on ? SEQ_CRASH : "var(--card-border)"}`, background: on ? "var(--bg-danger)" : "var(--card-bg)",
                          color: "var(--text-primary)" }}>
                  <div style={{ fontSize: "var(--fs-h1)", fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: on ? SEQ_CRASH : "var(--text-primary)" }}>{y}</div>
                  <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}>{SEQ_NAMES[y]}</div>
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
            <label style={lab} htmlFor="seq-start">Or any start year</label>
            <select id="seq-start" value={startYear} onChange={(e) => setStartYear(Number(e.target.value))} style={SEQ_INPUT_STYLE} aria-label="Start year">
              {Array.from({ length: SAMPLE_END_YEAR - SAMPLE_START_YEAR + 1 }, (_, i) => SAMPLE_START_YEAR + i).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <span style={{ ...lab, marginLeft: 10 }}>For</span>
            {SEQ_HISTORY_LENGTHS.map((n) => <button key={n} type="button" style={pill(years === n)} aria-pressed={years === n} onClick={() => setYears(n)}>{n} years</button>)}
          </div>
          <div style={head}>What your plan will live through</div>
          <SeqBars big items={histSeq.map((e) => ({ label: String(e.year), r: e.stock }))} />
        </div>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <div style={head}>Stock return for each year, in %</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(custom.length, 5)}, minmax(84px, 1fr))`, gap: 10, maxWidth: 640 }}>
            {custom.map((v, i) => (
              <div key={i}>
                <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", marginBottom: 3 }}>Year {i + 1}</div>
                <input type="number" inputMode="decimal" value={v} aria-label={`Year ${i + 1} stock return %`}
                       onChange={(e) => setCustom((c) => c.map((x, j) => (j === i ? e.target.value : x)))}
                       onFocus={selectAllOnFocus}
                       style={{ ...SEQ_INPUT_STYLE, width: "100%", boxSizing: "border-box", minHeight: 42, fontSize: "var(--fs-h2)", fontWeight: 700, padding: "6px 8px",
                         fontFamily: "'JetBrains Mono',monospace", fontVariantNumeric: "tabular-nums", color: Number(v) < 0 ? "var(--negative)" : SEQ_INPUT_STYLE.color }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
            {custom.length < SEQ_MAX_CUSTOM_YEARS && (
              <button type="button" style={pill(false)} onClick={() => setCustom((c) => [...c, "0"])}>+ Add year</button>
            )}
            {custom.length > 0 && (
              <button type="button" style={pill(false)} aria-label={`Remove year ${custom.length}`} onClick={() => setCustom((c) => c.slice(0, -1))}>− Remove last</button>
            )}
            <label style={{ ...lab, marginLeft: 10 }} htmlFor="seq-paste">Or paste a list</label>
            <input id="seq-paste" type="text" placeholder="-30, -20, -10, 0, 10" autoComplete="off"
                   onChange={(e) => {
                     const t = parseSeqText(e.target.value);
                     if (!t.length) return;
                     const total = e.target.value.split(/[\s,;]+/).filter((x) => x !== "").length;
                     setCustom(t);
                     setPasteNote(total > t.length ? `Kept the first ${t.length} of ${total} years.` : `Replaced your years with ${t.length} from the list.`);
                   }}
                   style={{ ...SEQ_INPUT_STYLE, width: 220, fontFamily: "'JetBrains Mono',monospace" }} />
          </div>
          {pasteNote && <div role="status" style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", marginTop: 6 }}>{pasteNote}</div>}
          {custom.length >= SEQ_MAX_CUSTOM_YEARS && (
            <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", marginTop: 6 }}>Up to {SEQ_MAX_CUSTOM_YEARS} years. After that, returns go back to random history.</div>
          )}
          {problem && <div role="alert" style={{ fontSize: "var(--fs-body)", color: "var(--accent-gold)", marginTop: 8 }}>{problem}</div>}
          {valid.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={head}>What your plan will live through</div>
              <SeqBars big items={valid.map((v, i) => ({ label: `Year ${i + 1}`, r: Number(v) / 100 }))} />
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <span style={lab}>Starts</span>
        <button type="button" style={pill(!today)} aria-pressed={!today} onClick={() => setToday(false)}>At retirement</button>
        <button type="button" style={pill(today)} aria-pressed={today} onClick={() => setToday(true)}>Today</button>
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" className={canRun ? "mbtn on" : "mbtn"} onClick={run} disabled={!canRun}
                style={{ fontSize: "var(--fs-body)", padding: "10px 24px", minHeight: 42, fontWeight: 700, opacity: canRun ? 1 : 0.5 }}>
          {busy ? "Running…" : "Run scenario"}
        </button>
        <button type="button" onClick={() => { onResult(null); setStatus(""); }}
                style={{ background: "transparent", border: "none", color: "var(--text-secondary)", fontSize: "var(--fs-body)", cursor: "pointer", fontFamily: "inherit", padding: "4px 0", minHeight: 32 }}>
          Clear result
        </button>
        <span role="status" style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)" }}>
          {status || (hist ? `${years} years from ${startYear}` : valid.length ? `${valid.length} ${valid.length === 1 ? "year" : "years"} will run` : "")}
        </span>
      </div>
    </div>
  );
}

function ScenariosTab({
  initialSubTab,
  onSubTabConsumed,
  baseParams,
  mc,
  stress,
  checkIns,
  onDeleteCheckIn,
  onRenameCheckIn,
  onImportCheckIns,
  retireAge,
  ssAge,
  rmdAge,
  inf,
  real,
  fmtPct,
  FanChart,
  SEQ_2000_2012,
  DeterministicWithdrawalView,
  RothLadder,
  BucketsTab,
  withdrawalStrategy,
  checkpoints,
  earlyRetireTarget,
  portfolioGoal,
  dob,
  assumptions,
  onAssumptionChange,
  onSaveConversionOverride,
  onRemoveConversionOverride,
}) {

  const [scenarioSubTab, setScenarioSubTab] = useState(initialSubTab || "roth");
  const [showSeq, setShowSeq] = useState(false);
  // A run from the sequence builder replaces the fixed 2000–2012 view below it.
  const [seqRun, setSeqRun] = useState(null);
  // A builder result belongs to the plan it ran on; drop it when the plan changes.
  useEffect(() => setSeqRun(null), [baseParams]);
  // Destructured, not read as stress.pcts: the array goes whole to FanChart,
  // which deflates it itself (see noRawMcAccess.test.js).
  const { pcts: stressPcts } = stress ?? {};
  const view = seqRun ?? (stress ? {
    rate: stress.rate, pcts: stressPcts,
    title: "Stress test: 2000–2012 actual S&P sequence at retirement",
    chips: SEQ_2000_2012.map((r, i) => ({ label: String(2000 + i), r })),
  } : null);

  // Consume a pending cross-tab navigation request once, on arrival — a
  // pointer elsewhere in the app (e.g. RetirementPanel's Withdrawal Strategy
  // card) asked to land on a specific sub-tab here. Cleared immediately so a
  // later manual visit to Analysis isn't silently redirected.
  useEffect(() => {
    if (initialSubTab) {
      setScenarioSubTab(initialSubTab);
      onSubTabConsumed && onSubTabConsumed();
    }
  }, [initialSubTab]); // onSubTabConsumed intentionally omitted — see comment above

  const SCENARIO_SUBTABS = [
    ["roth",        "Roth conversions"],
    ["withdrawals", "Withdrawal plan"],
    ["stress",      "Stress test"],
    ["buckets",     "Buckets"],
    ["income",      "Income"],
    ["realestate",  "Real estate"],
    ["progress",    "Progress"],
  ];

  return (
    <div>
      {/* Navigation, so one accent throughout — no meaning is carried by hue
          here, unlike the bracket-fill and Tax Room rows. The wrapper no longer
          paints its own bottom rule: PillTab now owns the hairline, and two
          hairlines one pixel apart is what made this row look doubled. */}
      <div style={{ marginBottom: 12 }}>
        <PillTab
          ariaLabel="Analysis view"
          options={SCENARIO_SUBTABS}
          value={scenarioSubTab}
          onChange={setScenarioSubTab}
        />
      </div>

      {scenarioSubTab === "stress" && (
        <div>
          <StressScenarioGrid p={baseParams} baseRate={mc?.rate ?? null} fmtPct={fmtPct} onSeqResult={setSeqRun} />
          {view && (
            <>
          <div id="stress-result" style={{ scrollMarginTop: 12 }} />
          {/* Moved up from below the fan chart — these two headline numbers
              are the reason to open this sub-tab at all, so they sit right
              under the scenario grid instead of buried under the chart. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 10,
            }}
          >
            <div className="met">
              <div className="ml">Stress success</div>
              <div
                className="mv"
                style={{ color: view.rate >= 0.85 ? "var(--positive)" : "var(--accent-gold)" }}
              >
                {fmtPct(view.rate)}
              </div>
              <div className="ms">{formulaFor("stress-success")}</div>
            </div>
            <div className="met">
              <div className="ml">Delta vs base</div>
              <div
                className="mv"
                style={{
                  color: mc && view.rate >= mc.rate ? "var(--positive)" : "var(--negative)",
                }}
              >
                {mc ? `${((view.rate - mc.rate) * 100).toFixed(1)} pp` : "—"}
              </div>
              <div className="ms">{formulaFor("stress-delta")}</div>
            </div>
          </div>
          <FanChart
            // eslint-disable-next-line no-restricted-properties -- FanChart deflates the whole array itself; see noRawMcAccess.test.js.
            pcts={view.pcts}
            retireAge={retireAge}
            ssAge={ssAge}
            rmdAge={rmdAge}
            inf={inf}
            useReal={real}
            title={view.title}
            checkpoints={checkpoints}
            portfolioGoal={portfolioGoal}
            earlyRetireTarget={earlyRetireTarget}
            dob={dob}
          />
          {/* Year-by-year S&P sequence — the raw input the fan chart and the
              two headline numbers above are already built from, so it's
              supporting detail, not something that needs to be visible by
              default. Closed unless asked for. */}
          <button
            onClick={() => setShowSeq((v) => !v)}
            style={{
              marginTop: 10, background: "transparent", border: "none",
              color: "var(--accent-teal)", fontSize: "var(--fs-label)", cursor: "pointer",
              fontFamily: "inherit", padding: "4px 0",
            }}
          >
            {showSeq ? "Hide year-by-year S&P sequence ▴" : "Show year-by-year S&P sequence ▾"}
          </button>
          {showSeq && (
            <div
              style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}
            >
              {view.chips.map(({ label, r }, i) => (
                <span
                  key={i}
                  style={{
                    padding: "2px 6px",
                    borderRadius: 4,
                    fontSize: "var(--fs-small)",
                    fontFamily: "'JetBrains Mono',monospace",
                    background:
                      r < 0 ? "var(--tint-red-15)" : "var(--bg-success)",
                    color: r < 0 ? "var(--chart-1)" : "var(--positive)",
                    border: `1px solid ${
                      r < 0 ? "var(--tint-red-30)" : "rgba(79,216,143,0.25)"
                    }`,
                  }}
                >
                  {label}: {r > 0 ? "+" : ""}
                  {(r * 100).toFixed(1)}%
                </span>
              ))}
            </div>
          )}
          {view.failures !== undefined && <FailedPathPanel failures={view.failures} n={view.n} />}
            </>
          )}
        </div>
      )}

      {scenarioSubTab === "withdrawals" && (
        <WithdrawalPlanCombined p={baseParams} inf={inf} withdrawalStrategy={withdrawalStrategy} onAssumptionChange={onAssumptionChange} mc={mc} />
      )}

      {scenarioSubTab === "roth" && <RothLadder params={baseParams} onSaveConversionOverride={onSaveConversionOverride} onRemoveConversionOverride={onRemoveConversionOverride} onAssumptionChange={onAssumptionChange} />}
      {scenarioSubTab === "buckets"    && <BucketsTab params={baseParams} onAssumptionChange={onAssumptionChange} />}
      {scenarioSubTab === "income"     && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <IncomeExpensesChart p={baseParams} inf={inf} />
        </div>
      )}
      {scenarioSubTab === "realestate" && <MortgageTab values={assumptions ?? baseParams} onChange={onAssumptionChange ?? (() => {})} />}
      {scenarioSubTab === "progress"   && <ProgressTab checkIns={checkIns} onDelete={onDeleteCheckIn} onRename={onRenameCheckIn} onImport={onImportCheckIns} />}
    </div>
  );
}

/**
 * Monte Carlo — ⚙ Advanced Settings.
 *
 * Simulation mechanics live here rather than in Profile, because they answer
 * "how do I run the model?", not "what is my plan?" — the same Proximity split
 * the Withdrawal tab's guardrail strip uses. The controls are the ones that
 * only mean anything inside a simulation:
 *
 *   Simulations per run   — path count (was the fixed MC_PATHS constant)
 *   Historical range      — which calendar years the bootstrap may draw from
 *                           (was: always the full 1928–2025 history)
 *   Spending guardrails   — GK floor/ceiling as % of core spend (moved here
 *                           from the Profile spending card, which now points
 *                           at this dialog instead of duplicating the inputs)
 *
 * The GK paper's own numbers (band, adjustment size, inflation cap, longevity
 * cutoff) are NOT editable: they are the published method, and exposing them as
 * sliders would invite a plan that is no longer Guyton-Klinger. They are shown
 * as read-only disclosure, read from the same constants the engine uses.
 *
 * Every control writes through `onAssumptionChange`, so this dialog and the
 * engine read one value — there is no second copy of these settings anywhere.
 */
// Blank keeps the historical draw; a number grows every account by exactly that
// (nominal) % per year until retirement. Lives beside, not inside, the
// DeepSeek-owned MCAdvancedSettings region.
function PreRetireReturnField({ value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
        Growth until retirement (%/yr)
        <input type="number" step={0.5} placeholder="historical" value={value ?? ""}
               onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
               onFocus={selectAllOnFocus}
               style={{ ...SEQ_INPUT_STYLE, width: 90 }} />
      </label>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", lineHeight: 1.45, marginTop: 4 }}>
        Blank: balances grow with real market draws until you retire. 0 = no growth, only contributions. Re-run to apply.
      </div>
    </div>
  );
}

function MCAdvancedSettings({ p, onAssumptionChange }) {
  const range = resolveSampleRange(p);
  const startYr = Number.isFinite(Number(p?.mcRangeStart)) ? Math.round(Number(p.mcRangeStart)) : SAMPLE_START_YEAR;
  const endYr   = Number.isFinite(Number(p?.mcRangeEnd))   ? Math.round(Number(p.mcRangeEnd))   : SAMPLE_END_YEAR;
  const pathCount = Math.max(MC_PATH_MIN, Math.min(MC_PATH_MAX, Math.round(Number(p?.mcPaths) || MC_PATHS)));

  // Stats for the window actually being sampled. Display-only summary of the
  // SAME arrays the bootstrap draws from — it does not feed the engine, and it
  // is not a second source of truth for any number the engine reports.
  const stat = useMemo(() => {
    const lo = range ? range.start : 0;
    const hi = range ? range.start + range.count - 1 : SP500.length - 1;
    const n = hi - lo + 1;
    const mean = (arr) => {
      let s = 0;
      for (let i = lo; i <= hi; i++) s += arr[i];
      return s / n;
    };
    const worst = (arr) => {
      let w = Infinity;
      for (let i = lo; i <= hi; i++) w = Math.min(w, arr[i]);
      return w;
    };
    return {
      n, years: `${SAMPLE_START_YEAR + lo}–${SAMPLE_START_YEAR + hi}`,
      eq: mean(SP500), bond: mean(BONDS), infl: mean(INFL), worst: worst(SP500),
    };
  }, [range]);

  const set = (k, v) => onAssumptionChange && onAssumptionChange(k, v);
  const setRange = (which, raw) => {
    const v = Math.round(Number(raw));
    if (!Number.isFinite(v)) return;
    if (which === "start") set("mcRangeStart", Math.max(SAMPLE_START_YEAR, Math.min(v, endYr - 1)));
    else set("mcRangeEnd", Math.min(SAMPLE_END_YEAR, Math.max(v, startYr + 1)));
  };

  const sec = { background: "var(--row-highlight)", border: "1px solid var(--tint-white-08)", borderRadius: 9, padding: 14, marginBottom: 12 };
  const secHead = { fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 10 };
  const row = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" };
  const lbl = { fontSize: "var(--fs-label)", color: "var(--text-secondary)", fontWeight: 600 };
  const help = { fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.55, marginTop: -4, marginBottom: 10 };
  const kv = { display: "flex", justifyContent: "space-between", fontSize: "var(--fs-small)", marginBottom: 4 };
  const isDefaultRange = startYr === SAMPLE_START_YEAR && endYr === SAMPLE_END_YEAR;
  const isDefaultPaths = pathCount === MC_PATHS;

  return (
    <InfoModal title="Monte Carlo — Advanced Settings" accent="var(--accent-teal)" maxWidth={640} bodyMaxHeight="64vh"
      trigger={
        <button type="button"
          style={{ padding: "5px 12px", fontSize: "var(--fs-small)", borderRadius: 7, cursor: "pointer",
            border: "1px solid var(--card-border)", background: "transparent", color: "var(--text-secondary)",
            fontWeight: 600, whiteSpace: "nowrap" }}>
          Advanced Settings
        </button>
      }
    >
      {/* Simulations */}
      <div style={sec}>
        <div style={secHead}>Simulations</div>
        <div style={row}>
          <span style={lbl}>Simulated paths per run</span>
          <ANumInput value={pathCount} onSet={(v) => set("mcPaths", Math.max(MC_PATH_MIN, Math.min(MC_PATH_MAX, Math.round(v))))}
                     min={MC_PATH_MIN} max={MC_PATH_MAX} step={500} />
        </div>
        <div style={help}>
          Each path replays your whole plan against a different sequence of historical market years.
          More paths steady the success rate and the percentile bands; fewer finish faster. Default {MC_PATHS_LABEL}.
          {!isDefaultPaths ? ` Currently ${pathCount.toLocaleString()} — re-run Monte Carlo to apply.` : ""}
        </div>
      </div>

      {/* Historical sampling range — the competitor's marquee control */}
      <div style={sec}>
        <div style={secHead}>Historical Range To Sample</div>
        <div style={row}>
          <span style={lbl}>From</span>
          <ANumInput value={startYr} onSet={(v) => setRange("start", v)} min={SAMPLE_START_YEAR} max={SAMPLE_END_YEAR - 1} step={1} plain />
        </div>
        <div style={row}>
          <span style={lbl}>Through</span>
          <ANumInput value={endYr} onSet={(v) => setRange("end", v)} min={SAMPLE_START_YEAR + 1} max={SAMPLE_END_YEAR} step={1} plain />
        </div>
        <div style={help}>
          The engine bootstraps whole calendar years — stocks, bonds and inflation drawn together from the
          same year — so narrowing this window changes which markets your plan is tested against. Sample
          1928–{SAMPLE_END_YEAR} for every kind of history including the Depression and the 1970s stagflation,
          or a later window to test only the modern era.
          {!isDefaultRange ? " Re-run Monte Carlo to apply." : ""}
        </div>
        <div style={{ borderTop: "1px solid var(--tint-white-08)", paddingTop: 10 }}>
          <div style={{ ...kv, fontWeight: 700, color: "var(--text-secondary)" }}>
            <span>{isDefaultRange ? "Sampling full history" : "Sampling a narrowed window"}</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>{stat.years} · {stat.n} yrs</span>
          </div>
          <div style={kv}><span style={{ color: "var(--text-muted)" }}>Average equity year</span><span style={{ fontFamily: "'JetBrains Mono',monospace", color: "var(--text-primary)" }}>{(stat.eq * 100).toFixed(2)}%</span></div>
          <div style={kv}><span style={{ color: "var(--text-muted)" }}>Average bond year</span><span style={{ fontFamily: "'JetBrains Mono',monospace", color: "var(--text-primary)" }}>{(stat.bond * 100).toFixed(2)}%</span></div>
          <div style={kv}><span style={{ color: "var(--text-muted)" }}>Average inflation</span><span style={{ fontFamily: "'JetBrains Mono',monospace", color: "var(--text-primary)" }}>{(stat.infl * 100).toFixed(2)}%</span></div>
          <div style={kv}><span style={{ color: "var(--text-muted)" }}>Worst equity year in window</span><span style={{ fontFamily: "'JetBrains Mono',monospace", color: "#f87171" }}>{(stat.worst * 100).toFixed(2)}%</span></div>
        </div>
        {!isDefaultRange && (
          <button type="button" onClick={() => { set("mcRangeStart", SAMPLE_START_YEAR); set("mcRangeEnd", SAMPLE_END_YEAR); }}
            style={{ marginTop: 10, padding: "4px 10px", fontSize: "var(--fs-small)", borderRadius: 6, cursor: "pointer",
              border: "1px solid var(--card-border)", background: "transparent", color: "var(--accent-teal)", fontWeight: 600 }}>
            Reset to full history ({SAMPLE_START_YEAR}–{SAMPLE_END_YEAR})
          </button>
        )}
      </div>

      {/* Return & cost assumptions — moved out of Profile's "Monte Carlo Model
          Parameters" card, which was a second copy of the sidebar's Macro &
          Sensitivity card. Trimmed to the four that actually change a
          projection's shape: inflation, the cash rate, the SS COLA, and the
          equity glide. Deliberately NOT here: the two portfolio targets
          (earlyRetireTarget / portfolioGoal) — those are GOALS, not model
          parameters, so they stay in Profile where the rest of the plan lives. */}
      <div style={sec}>
        <div style={secHead}>Return &amp; Cost Assumptions</div>
        <div style={row}>
          <span style={lbl}>Inflation (CPI)</span>
          <ANumInput value={p.inf ?? 2.5} onSet={(v) => set("inf", v)} min={0} max={15} step={0.1} suffix="%" />
        </div>
        <div style={help}>
          Indexes spending, deflates every Real-$ figure, and drives the Social Security COLA.
        </div>
        <div style={row}>
          <span style={lbl}>Cash return</span>
          <ANumInput value={p.cashRealReturn ?? 3.0} onSet={(v) => set("cashRealReturn", v)} min={0} max={8} step={0.1} suffix="%" />
        </div>
        <div style={help}>
          What the cash bucket earns (HYSA, SGOV, money market) — in the Monte Carlo and the Withdrawal Plan alike.
        </div>
        <div style={row}>
          <span style={lbl}>SS COLA / yr</span>
          <ANumInput value={p.ssCola ?? 2.4} onSet={(v) => set("ssCola", v)} min={0} max={6} step={0.1} suffix="%" />
        </div>
        <div style={help}>
          Annual Social Security cost-of-living adjustment. Applies from your claim age, not from retirement.
        </div>
        <div style={{ borderTop: "1px solid var(--tint-white-08)", paddingTop: 10, marginTop: 4 }}>
          <div style={{ ...row, marginBottom: 6 }}>
            <span style={lbl}>Equity glide</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ANumInput value={p.preRetireEq ?? 91} onSet={(v) => set("preRetireEq", v)} min={50} max={100} step={1} suffix="%" />
              <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}>→</span>
              <ANumInput value={p.postRetireEq ?? 70} onSet={(v) => set("postRetireEq", v)} min={30} max={100} step={1} suffix="%" />
            </span>
          </div>
          <div style={row}>
            <span style={{ ...lbl, fontWeight: 500, color: "var(--text-muted)" }}>Switch to the second mix at age</span>
            <input
              type="number"
              value={p.glidepathSwitchAge ?? ""}
              placeholder={`${p.retireAge ?? ""}`}
              min={AGE_LIMITS.retire.min}
              max={AGE_LIMITS.retire.max}
              onChange={(e) => set("glidepathSwitchAge", e.target.value ? parseInt(e.target.value, 10) : null)}
              onFocus={selectAllOnFocus}
              style={{ width: 84, background: "var(--row-highlight)", border: "1px solid var(--card-border)", color: "var(--text-primary)", borderRadius: 6, padding: "3px 8px", fontSize: "var(--fs-label)", fontFamily: "var(--font-mono)" }}
            />
          </div>
          <div style={help}>
            Stock share while saving → stock share once retired, and the age you shift. The second number is your
            sequence-of-returns dial: raising it lifts the median outcome but widens the downside, so the success
            rate can fall even as the median rises. Both set the average return AND the volatility. Blank switch age
            = shift at retirement (age {p.retireAge}).
            {" "}Blended expected returns: {expectedReturn(p.preRetireEq ?? 91).toFixed(1)}% before, {expectedReturn(p.postRetireEq ?? 70).toFixed(1)}% after.
          </div>
        </div>
      </div>

      {/* Spending guardrails — moved here from the Profile spending card */}
      <div style={sec}>
        <div style={secHead}>Spending Guardrails</div>
        <div style={row}>
          <span style={lbl}>Floor — % of core spend</span>
          <ANumInput value={p.gkFloorPct ?? GK_FLOOR_DEFAULT_PCT} onSet={(v) => set("gkFloorPct", v)} min={50} max={95} step={5} suffix="%" />
        </div>
        <div style={row}>
          <span style={lbl}>Ceiling — % of core spend</span>
          <ANumInput value={p.gkCeilingPct ?? GK_CEILING_DEFAULT_PCT} onSet={(v) => set("gkCeilingPct", v)} min={105} max={200} step={5} suffix="%" />
        </div>
        <div style={help}>
          The hard floor and cap on real spending, applied after every Guyton-Klinger adjustment: spending
          will never fall below the floor even after repeated cuts, nor rise above the ceiling after repeated raises.
          These apply to the GK-family strategies (Guyton-Klinger, Risk, Kitces) and the Smart waterfall.
        </div>
        {/* Read-only: the published method, cited from the engine's own constants
            so the sentence cannot drift from the arithmetic. */}
        <div style={{ borderTop: "1px solid var(--tint-white-08)", paddingTop: 10, fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, color: "var(--text-secondary)", marginBottom: 4 }}>Guyton-Klinger rules in force (fixed by the method)</div>
          <div>Withdrawal rate {Math.round(GK_BAND_PCT * 100)}% above the initial rate → cut spending {Math.round(GK_ADJUST_PCT * 100)}%.</div>
          <div>Withdrawal rate {Math.round(GK_BAND_PCT * 100)}% below the initial rate → raise spending {Math.round(GK_ADJUST_PCT * 100)}%.</div>
          <div>No cut when {GK_LONGEVITY_YEARS} years or fewer remain to your plan age.</div>
          <div>Inflation pass-through applied only after a positive-return year, capped at {Math.round(GK_INFLATION_CAP * 100)}%/yr.</div>
        </div>
      </div>

      {/* What the run costs, stated plainly — the honest version of the
          reference design's "29 yr plan / 127 simulations" badge. */}
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", lineHeight: 1.6 }}>
        Plan horizon {Math.max(0, (p.endAge || 0) - effectiveRetireAge(p.retireAge, p.currentAge))} yrs ·
        {" "}{pathCount.toLocaleString()} paths × {stat.n}-year sampling window ({stat.years}) ·
        both applied on the next run.
      </div>
    </InfoModal>
  );
}

/**
 * Monte Carlo Overview — the four-card verdict row (REQUIREMENTS §42 Spec A).
 *
 * READ-ONLY, one selector per figure (rule 8 / §41 §2):
 *   Success Rate        mc.rate                    (a scalar; the guard covers
 *                                                    the mc.pcts / mc.term DOLLAR
 *                                                    arrays, not scalars)
 *   Mean Final Balance  selectTerminalMeanAtAge()   — NOT an average of
 *                                                    p10..p90, and NOT a raw
 *                                                    mc.term read, so it deflates
 *                                                    on the Real-$ toggle exactly
 *                                                    as its neighbours do
 *   Best Case / Median  selectPortfolioAtAge()      — mc.term.p50/p90 are the
 *                                                    guarded fields; a direct read
 *                                                    fails noRawMcAccess.test.js
 *
 * Each card declares itself in src/provenance.js with a prose formula, and
 * renders that formula on screen (Rule 1a) — a card whose label class is not
 * declared in the registry turns the build red.
 */
function MCOverviewCards({ mc, inf = 0, real = false, endAge, currentAge, retireAge }) {
  if (!mc) return null;
  const p50 = selectPortfolioAtAge(mc, endAge, { retireAge, real, inf, pct: "p50" });
  const p90 = selectPortfolioAtAge(mc, endAge, { retireAge, real, inf, pct: "p90" });
  const mean = selectTerminalMeanAtAge(mc, endAge, { retireAge, real, inf });
  const money = (v) => (v == null ? "—" : fmtDollar(v));
  // Basis named with the actual calendar year of retirement year one (§41 A3),
  // never "today's dollars" — the spend the user types is consumed as-is in
  // retirement year one and never inflated forward from today.
  const retAge = effectiveRetireAge(retireAge, currentAge);
  const basisYear = CURRENT_YEAR + Math.max(0, retAge - (currentAge ?? retAge));
  const basis = real ? `${basisYear} dollars` : `future (${basisYear}-basis) dollars`;
  const paths = mc.N ? mc.N.toLocaleString() : "simulated";

  // Four explicit cards, NOT a `.map()` over the data above. Deliberate: the
  // provenance gate counts literal label-class occurrences in this file and
  // requires one registry entry each. A mapped card list appears exactly ONCE in
  // source, so it would slide past the count check and let four undeclared cards
  // ship — the loophole the registry exists to close. The values stay computed
  // once, above; only the markup is repeated.
  const cardStyle = { color: INFO_ACCENT.money, textAlign: "right" };
  const provLine = { fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 6, lineHeight: 1.45 };
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
        <div className="met">
          <div className="ml">Success Rate</div>
          <div className="mv" style={{ color: INFO_ACCENT.positive, textAlign: "right" }}>{fmtPct(mc.rate)}</div>
          <div className="ms">{paths} simulated paths, funded all the way to age {endAge}</div>
          <div style={provLine}>{formulaFor("ov-success")}</div>
        </div>
        <div className="met">
          <div className="ml">Mean Final Balance</div>
          <div className="mv" style={cardStyle}>{money(mean)}</div>
          <div className="ms">Average of every path at age {endAge} — the best sequences pull it above the median</div>
          <div style={provLine}>{formulaFor("ov-mean-final")}</div>
        </div>
        <div className="met">
          <div className="ml">Best Case (90th)</div>
          <div className="mv" style={cardStyle}>{money(p90)}</div>
          <div className="ms">90% of simulated outcomes ended below this · at age {endAge}</div>
          <div style={provLine}>{formulaFor("ov-best-case")}</div>
        </div>
        <div className="met">
          <div className="ml">Median Final Balance</div>
          <div className="mv" style={{ color: "var(--accent-teal)", textAlign: "right" }}>{money(p50)}</div>
          <div className="ms">Half of all outcomes ended above this · at age {endAge}</div>
          <div style={provLine}>{formulaFor("ov-median")}</div>
        </div>
      </div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 6 }}>
        Showing {basis}. Every figure is read from the same run — nothing here is recomputed in the UI.
      </div>
    </div>
  );
}

/**
 * Persistent Verdict Header — REQUIREMENTS §41 verdict item #3.
 *
 * The brief's own acceptance test (§7): a cold user answers FOUR questions in
 * 30 seconds without clicking —
 *     is my money going to last · will it outlive ME · what's my worst case ·
 *     when would it run out.
 * Today the app opens on inputs, so that test fails: the answer lives inside a
 * tab, below a Run button the user has to know to press.
 *
 * This strip sits above the tab bar and stays there, so the answer is on screen
 * before any navigation. Every figure is a READ off the existing `mc` payload —
 * no new engine work (rule 8):
 *   survived to plan age   mc.rate            (scalar)
 *   outlives you           mc.mwRate          (scalar, mortality-weighted)
 *   worst case             selectPortfolioAtAge(pct p10)  ← the guarded field
 *   median failure age     mc.medianExhaustAge
 *
 * LABELLING NOTE (Rule 1): the brief calls the p10 figure a "real floor SPEND".
 * It is not spending — `term.p10` is the 10th-percentile final BALANCE, i.e. the
 * pessimistic ending portfolio, not a spending level. Calling it spend would be
 * exactly the mislabel class this codebase keeps paying for, so it is labelled
 * as the worst-case balance and the basis is named with the retirement YEAR
 * (§41 A3), never "today's dollars". The engine's actual spend floor is
 * `mc.gkStats.spendMinReal` — a different number, available if wanted.
 */
function VerdictHeader({ mc, stress, real = false, inf = 0, endAge, currentAge, retireAge, swr, swrBenchmark = 0.04 }) {
  if (!mc) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", marginBottom: 10, background: "var(--row-highlight)", border: "1px solid var(--card-border)", borderRadius: 8, fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
        <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>No verdict yet.</span>
        <span>Run Monte Carlo from the sidebar and this strip will hold your headline answers here, on every tab.</span>
      </div>
    );
  }
  const retAge = effectiveRetireAge(retireAge, currentAge);
  const basisYear = CURRENT_YEAR + Math.max(0, retAge - (currentAge ?? retAge));
  const worst = selectPortfolioAtAge(mc, endAge, { retireAge: retAge, real, inf, pct: "p10" });
  const survived = mc.rate;
  const outlives = mc.mwRate;
  const exhaust = mc.medianExhaustAge;

  const fact = { display: "flex", flexDirection: "column", gap: 1, minWidth: 0 };
  const k = { fontSize: "var(--fs-small)", color: "var(--text-faint)", whiteSpace: "nowrap" };
  const v = { fontSize: "var(--fs-h2)", fontWeight: 800, fontFamily: "var(--font-mono)", lineHeight: 1.1, whiteSpace: "nowrap" };
  const sep = { width: 1, alignSelf: "stretch", background: "var(--divider)", flexShrink: 0 };

  return (
    <div
      role="status"
      style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px 16px", padding: "9px 14px", marginBottom: 10, background: "var(--card-bg)", border: "1px solid var(--card-border)", borderLeft: `3px solid ${rateColor(survived)}`, borderRadius: 8 }}
    >
      <div style={fact}>
        <span style={k}>Withdrawal rate</span>
        <span style={{ ...v, color: "var(--text-primary)" }}>
          {swr != null ? `${swr}%` : "—"}
        </span>
      </div>
      <div style={sep} />
      <div style={fact}>
        <span style={k}>Money outlives you</span>
        <span style={{ ...v, color: rateColor(outlives ?? survived) }}>
          {outlives != null ? fmtPct(outlives) : "—"}
        </span>
      </div>
      <div style={sep} />
      <div style={fact}>
        <span style={k}>Worst case at {endAge}</span>
        <span style={{ ...v, color: worst == null ? "var(--text-muted)" : worst > 0 ? "var(--text-primary)" : "var(--negative)" }}>
          {worst == null ? "—" : fmtDollar(worst)}
        </span>
        {worst != null && <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>1 in 10 outcomes end lower</span>}
      </div>
      <div style={sep} />
      <div style={fact}>
        <span style={k}>Runs out</span>
        <span style={{ ...v, color: exhaust == null ? "var(--positive)" : "var(--text-primary)" }}>
          {exhaust == null ? "Never" : `Age ${exhaust}`}
        </span>
        {exhaust != null && <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>in the paths that fail</span>}
      </div>
      {stress && (<>
        <div style={sep} />
        <div style={fact}>
          <span style={k}>Stress test 2000–12</span>
          <span style={{ ...v, color: rateColor(stress.rate) }}>{fmtPct(stress.rate)}</span>
        </div>
      </>)}
      <div style={sep} />
      <div style={fact}>
        <InfoModal title="Your headline answers" accent={INFO_ACCENT.method}
          trigger={<span aria-label="What are these numbers?" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, color: INFO_ACCENT.method, fontSize: "var(--fs-small)", fontWeight: 600 }}><InfoIcon size={13} /> What are these?</span>}>
          <ModalLede>These numbers answer the questions that actually matter, before you open a single tab.</ModalLede>
          <ModalP><Em color={INFO_ACCENT.money}>Withdrawal rate</Em> — first-year spending net of guaranteed income, divided by the portfolio at retirement, against a {(swrBenchmark * 100).toFixed(0)}% benchmark. It sizes the draw the plan starts from; the success rate on the card above is what says whether that rate holds to age {endAge}.</ModalP>
          <ModalP><Em color={INFO_ACCENT.positive}>Money outlives you</Em> — the same paths, re-weighted by your odds of actually being alive at each failure age. It is always at least as high as the funded-to-age success rate on the card above, and it answers the actuarial question rather than the worst-case one.</ModalP>
          <ModalP><Em color={INFO_ACCENT.money}>Worst case</Em> — the 10th-percentile ending balance: 90% of simulated outcomes finished above it. A small worst case beside a high success rate is one bad sequence away from joining the failures.</ModalP>
          <ModalP><Em color={INFO_ACCENT.risk}>Runs out</Em> — the typical age at which the simulated paths that ran out of money did so (the middle one). It describes only the paths that failed, not your whole plan, so a high success rate can sit beside a young age here. "Never" appears only when no simulated path ran out. When few paths fail, this figure rests on few paths: treat it as indicative.</ModalP>
          {stress && <ModalP><Em color={INFO_ACCENT.risk}>Stress test</Em> — the share of paths funded to age {endAge} when retirement opens with the actual 2000–2012 S&amp;P 500 sequence. The year-by-year detail is on Analysis → Stress Test.</ModalP>}
          <ModalNote accent={INFO_ACCENT.method}>All of them are read from the one simulation you last ran — nothing here is recomputed. Change an input and the strip goes stale with the rest of the results until you re-run.</ModalNote>
        </InfoModal>
      </div>
      <div style={{ marginLeft: "auto" }}>
        <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{real ? `Amounts in ${basisYear} dollars` : "Amounts in future dollars"}</span>
      </div>
    </div>
  );
}

function MCTab({ params, mc, running, onRun, checkpoints, onUpdateCheckpoints, onDeleteCheckpoint, portfolioGoal, earlyRetireTarget, dob, onSetBaselineFromCheckpoint, withdrawalStrategy, inf = 0, real = false, onAssumptionChange, chart }) {
  const [showInputs, setShowInputs] = useState(false);
  const [showGaps, setShowGaps] = useState(false);

  // Clamped the same way runMC/simulateDeterministicWithStrategy clamp
  // their own start age (see effectiveRetireAge) — a user who enters a
  // retireAge in the past relative to currentAge (already retired) actually
  // gets simulated starting today, not at the stale entered age. Without
  // this, accPhase showed a backwards range ("Age 65 → 55") and retPhase
  // named a phase start (55) the engine never actually uses (it starts at
  // 65) — both were describing a simulation that wasn't the one being run.
  const effRetireAge = effectiveRetireAge(params.retireAge, params.currentAge);
  const accPhase = effRetireAge > params.currentAge
    ? `Age ${params.currentAge} → ${effRetireAge}`
    : "already retired — no accumulation phase";
  const retPhase = `Age ${effRetireAge} → ${params.endAge}`;

  // termAt(k) used to hand-roll its own deflation (`termDivisor = (1+inf)^
  // termYears`) because mc.term has no array position for deflate()'s
  // row-index math — a second, parallel implementation of the same "discount
  // to the retirement-year basis" concept deflate() already owns. Verified
  // numerically equivalent (termYears === the array index deflate() would use
  // for this same row), but one future edit to deflate()'s convention away
  // from silently diverging again — the exact failure class in CLAUDE.md
  // rule 8. selectPortfolioAtAge() looks up the SAME terminal row through
  // mc.pcts instead of mc.term, sharing deflate()'s actual math rather than
  // re-deriving it, and — as a side effect — reads the row at THIS card's own
  // age (params.endAge) rather than trusting mc.term's implicit horizon,
  // which can differ from endAge when a survivor's clock extends the plan.
  const termAt = (k) => selectPortfolioAtAge(mc, params.endAge, { retireAge: effRetireAge, real, inf, pct: k }) ?? 0;
  const dollarBasis = dollarBasisLabel(real, retirementBasisYear(params.currentAge, effRetireAge, CURRENT_YEAR));
  // The run's own path count where a RESULT is described; the default only before a run.
  const pathsLabel = mc?.N ? mc.N.toLocaleString() : MC_PATHS_LABEL;
  const mortSched = params.mortBalance > 0
    ? mortgageSchedule(params.mortBalance, params.mortRate ?? 6.5, params.mortStart || "2020-01", params.mortTerm || 30, params.mortExtra || 0)
    : null;
  const mortAnnual = mortSched ? mortSched.pmt * 12 : 0;
  const mortPayoffAge = mortSched
    ? params.currentAge + (mortSched.payoffYr - new Date().getFullYear())
    : 0;
  // One-off cash flows (windfalls in, planned lump costs out). These move
  // the plan as much as any return assumption — a $1M inheritance landing
  // at 68 shifts every downstream percentile — but this panel used to
  // disclose only the recurring inputs, so nothing on screen told the user
  // whether the run actually included the event they'd just entered. Same
  // list the engines read (params.cashFlowEvents), so what's shown is what
  // was simulated.
  const cfEvents   = params.cashFlowEvents || [];
  const cfIsLive   = (e) => Number.isFinite(Number(e.year)) && (Number(e.amount) || 0) !== 0;
  const cfInflows  = cfEvents.filter((e) => e.direction === "in"  && cfIsLive(e));
  const cfOutflows = cfEvents.filter((e) => e.direction !== "in" && cfIsLive(e));
  const cfAgeAt    = (yr) => params.currentAge + (Number(yr) - CURRENT_YEAR);
  const cfWhen     = (ev, fallback) => {
    const every = Number(ev.recurEveryYears) || 0;
    return `${(ev.label || "").trim() || fallback} · ${ev.year} (age ${cfAgeAt(ev.year)})`
      + (every > 0 ? ` · every ${every} yr` : "");
  };
  // `inflate !== false` is the engine's default (see computeCashFlowEvents) —
  // report the treatment that will actually be applied, not the raw field.
  const cfBasis    = (ev) => ev.inflate !== false ? `today's $ · +${params.inf ?? 2.5}%/yr` : "as entered (nominal)";
  const cfInflowRows = cfInflows.flatMap((ev) => [
    [cfWhen(ev, "Windfall"), "+" + fmtDollar(Number(ev.amount) || 0)],
    [`↳ into ${BUCKET_LABELS_SHORT[ev.bucket || "taxable"]}`,
      `${ev.taxable ? "taxed as income" : "not taxed"} · ${cfBasis(ev)}`],
  ]);
  const cfOutflowRows = cfOutflows.flatMap((ev) => [
    [cfWhen(ev, "One-off cost"), "−" + fmtDollar(Number(ev.amount) || 0)],
    [`↳ ${ev.deferrable ? "discretionary" : "committed"}`, cfBasis(ev)],
  ]);

  // `hint` is what stays visible while a panel is shut — the one fact that
  // tells the user whether opening it is worth a click (how many score
  // drivers, how many saved checkpoints). Without it, collapsing hides not
  // just the detail but the fact that there's any.

  const SectionHeader = ({ label, open, onToggle, color = "var(--accent-teal)", hint }) => (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 0", borderBottom: "1px solid var(--tint-white-07)", marginBottom: open ? 14 : 0 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--fs-small)", color, display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform 120ms ease" }}>▶</span>
        <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color }}>{label}</div>
        {hint && <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{hint}</span>}
      </div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{open ? "Hide" : "Show"}</div>
    </div>
  );

  const InputCard = ({ title, rows }) => (
    <div style={{ background: "var(--row-highlight)", border: "1px solid var(--tint-white-08)", borderRadius: 9, padding: 14 }}>
      <div style={{ fontSize: "var(--fs-small)", fontWeight: 600, color: "var(--text-faint)", marginBottom: 10 }}>{title}</div>
      {/* Index in the key: rows are now generated per cash-flow event, and two
          events can legitimately produce the same label ("↳ into taxable"). */}
      {rows.map(([label, val], i) => (
        <div key={`${label}|${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6, fontSize: "var(--fs-label)" }}>
          <span style={{ color: "var(--text-muted)" }}>{label}</span>
          <span style={{ color: "var(--text-primary)", fontFamily: "'JetBrains Mono',monospace", fontWeight: 500, textAlign: "right" }}>{val}</span>
        </div>
      ))}
    </div>
  );

  // strategyHowItWorks moved to module scope (STRATEGY_HOW_IT_WORKS, next to
  // SimMethodModal) so the hero "Success to Age" ⓘ and this tab share ONE copy.

  // A 40+ year retirement runs past the evidence behind two of this
  // model's defaults. The simulation itself is honest — it bootstraps
  // returns across the whole horizon — but a success rate is only as good
  // as the spending path and the costs fed into it, and both weaken the
  // earlier you retire. Said plainly and up front, because the failure mode
  // is a confident number that's optimistic for reasons the user can't see.
  const planHorizon  = Math.max(0, (params.endAge || 0) - effRetireAge);
  const longHorizon  = planHorizon >= 40;
  const preMedicare  = Math.max(0, 65 - effRetireAge);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* What the forecast leaves out: one line that still says WHAT is missing
          and WHICH WAY it errs (so the honesty survives without a click), with
          the detail and the "what to do" in the standard info pop-up. It was a
          full-width block taking a fifth of the screen. Click, not hover: a
          hover-only pop-up never opens on a phone. */}
      {(longHorizon || preMedicare > 0) && (<>
        <button type="button" className="gap-row" onClick={() => setShowGaps(true)} aria-haspopup="dialog">
          <Dot tone="warn" />
          <span style={{ flex: 1, minWidth: 0 }}>
            {preMedicare > 0 && longHorizon
              ? <><strong>Not in this forecast:</strong> {preMedicare} years of health insurance before Medicare, plus 2 long-retirement caveats. The plan looks better than it is.</>
              : preMedicare > 0
              ? <><strong>Not in this forecast:</strong> health insurance for your {preMedicare} years before Medicare. The plan looks better than it is.</>
              : <><strong>{planHorizon}-year retirement:</strong> the spending curve and the 4% rule are stretched past their evidence.</>}
          </span>
          <span className="gap-more">What to do ›</span>
        </button>
        <InfoModal title="What this forecast leaves out" accent="var(--accent-gold)" maxWidth={560} bodyMaxHeight="60vh" open={showGaps} onClose={() => setShowGaps(false)}>
          <ModalLede>These are not modelled, and they make the plan look better than it is.</ModalLede>
          {preMedicare > 0 && (<>
            <ModalP>
              <Em color="var(--accent-gold)">Health insurance before Medicare.</Em>{" "}
              You have {preMedicare} years to cover before 65. AiRA models catastrophic healthcare
              shocks but not ACA marketplace premiums.
            </ModalP>
            <ModalNote><strong>What to do:</strong> include those premiums in your annual spending yourself.</ModalNote>
            <ModalP>
              <Em color="var(--accent-gold)">Roth conversions before 65.</Em>{" "}
              The Roth conversion planner optimises against tax brackets and IRMAA, and IRMAA does not
              begin until 63. It does not know about ACA premium subsidies, which phase out on income.
            </ModalP>
            <ModalNote><strong>What to do:</strong> before 65, check the subsidy you would lose before acting on a recommended conversion. It can cost more than the conversion saves in tax.</ModalNote>
          </>)}
          {longHorizon && (<>
            <ModalP>
              <Em color="var(--accent-gold)">The spending curve is extrapolated.</Em>{" "}
              The Blanchett smile measures retirees in their 60s and 70s; applied from age {effRetireAge} it
              assumes your real spending drifts down to about{" "}
              {Math.round(spendingSmileFactor(Math.min(80, params.endAge), effRetireAge) * 100)}% of today's by 80.
              That is well past the data it was fitted on.
            </ModalP>
            <ModalNote><strong>What to do:</strong> turn off <strong>Reduce spending with age</strong> in the sidebar Options for a flat-real plan. It is a stricter and, over {planHorizon} years, more defensible test.</ModalNote>
            <ModalP>
              <Em color="var(--accent-gold)">4% is a 30-year rule.</Em>{" "}
              Bengen and Guyton-Klinger were derived for ~30-year retirements. The success rate
              is computed honestly for the rate you chose; it is the rule of thumb, not the simulation,
              that does not transfer.
            </ModalP>
            <ModalNote><strong>What to do:</strong> over {planHorizon} years the sustainable rate is materially lower, commonly cited near 3.0–3.5%. Compare your withdrawal rate with that.</ModalNote>
          </>)}
          <ModalP>
            What is modelled: retiring at {effRetireAge} is fully simulated across all {planHorizon} years,
            with the early-withdrawal penalty, the bridge to Social Security, and bracket-capped drawdown.
          </ModalP>
        </InfoModal>
      </>)}

      {/* The picture first: the hero above already gave the number. */}
      {chart}
      {!mc && <div style={{ textAlign: "center", padding: "20px", color: "var(--text-faint)", fontSize: "var(--fs-body)" }}>{running ? `Running ${MC_PATHS_LABEL} paths...` : "Run Monte Carlo from the sidebar to see results here."}</div>}
      {/* The ending balance as one percentile row. It replaces the stress-test
          card (now a cell in the verdict strip) and the median card. */}
      {mc && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 18px", padding: "0 2px", fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
          <span>Balance at age {params.endAge} · {dollarBasis}</span>
          {[["10th", "p10", "10th"], ["25th", "p25", "25th"], ["Median", "p50", "Median"], ["75th", "p75", "75th"], ["90th", "p90", "90th"]].map(([l, k, c]) => (
            <span key={k}>{l} <strong style={{ color: FAN_COLORS[c], fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(termAt(k))}</strong></span>
          ))}
        </div>
      )}


      {/* What's driving this. One line per driver; a driver flagged as the
          biggest risk opens by itself, the rest open on click. Every engine
          bug found here was invisible on screen: a number that can't explain
          itself can't be questioned. */}
      {mc && (() => {
        // Third argument: the verdict strip's withdrawal rate, so the driver
        // and the strip show one figure.
        const ex = explainScore(params, mc, computeInitialWR(params));
        if (!ex.drivers.length) return null;
        const tone = {
          risk:  { fg: "var(--chart-1)", tag: "Biggest risk" },
          watch: { fg: "#fdba74", tag: "Worth knowing" },
          good:  { fg: "var(--positive)", tag: "Working for you" },
        };
        return (
          <div style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: "12px 16px" }}>
            <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 4 }}>What's driving this</div>
            {ex.drivers.map((d, i) => {
              const t = tone[d.severity];
              return (
                <details key={d.id} open={i === 0 && d.severity === "risk"} style={{ borderTop: i ? "1px solid var(--divider)" : "none" }}>
                  <summary style={{ cursor: "pointer", padding: "8px 0", fontSize: "var(--fs-label)" }}>
                    <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "baseline", gap: "2px 10px", width: "calc(100% - 20px)", verticalAlign: "top" }}>
                      <span style={{ flex: "1 1 180px", minWidth: 0, fontWeight: 600, color: "var(--text-primary)" }}>{d.label}</span>
                      <span style={{ fontSize: "var(--fs-small)", color: t.fg, whiteSpace: "nowrap" }}>{t.tag}</span>
                      <span style={{ fontWeight: 700, color: t.fg, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>{d.value}</span>
                    </span>
                  </summary>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.55, paddingBottom: 10 }}>
                    {d.detail}{" "}
                    <strong style={{ color: t.fg }}>What moves it: </strong><span style={{ color: "var(--text-secondary)" }}>{d.lever}</span>
                  </div>
                </details>
              );
            })}
          </div>
        );
      })()}



      {/* Guardrails MOVED to its own sub-tab of the Monte Carlo tab
          (2026-09-24, Vincent's ask: "one is called simulation and the other
          guardrails inside of this main tab"). It renders from the shell beside
          this tab, because the fan chart and band table are shell-level children
          of the Monte Carlo tab — a copy left here would appear under BOTH
          sub-tabs and stop being a tab at all. The card chrome went with it. */}


      {/* Inputs collapsible — source of record for every model assumption. */}
      <div style={{ background: "var(--card-bg)", border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: 16, scrollMarginTop: 16 }}>
        <SectionHeader label="Simulation inputs & assumptions" open={showInputs} onToggle={() => setShowInputs(!showInputs)}
          hint={[
            getStrategyLabel(resolveStrategy(withdrawalStrategy)),
            params.smile !== false ? "spending declines with age" : "flat spending",
            params.tax !== false ? "taxes modelled" : "tax OFF",
          ].join(" · ")}
        />
        {showInputs && (
          <>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 600, color: "#0ea5e9", marginBottom: 10 }}>ACCUMULATION PHASE ({accPhase})</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                <InputCard title="Starting Balances" rows={[...(params.accounts || []).filter(a => (a.balance || 0) > 0).map(a => [a.name || a.category, fmtDollar(a.balance || 0)]), ["Total liquid", fmtDollar(params.port)]]} />
                {/* "Total savings" used to print `params.contrib` alone —
                    the 401(k) deferral — while calling itself the total,
                    silently omitting employer money, HSA, Roth and
                    brokerage. It discloses every component now, and the
                    spouse's own streams and stop age when they have them. */}
                <InputCard title="Annual Contributions" rows={(() => {
                  const yrs = Math.max(0, params.retireAge - params.currentAge);
                  const spX = params.spouse || {};
                  const spTotal = spX.enabled ? (spX.contrib || 0) + (spX.employerContrib || 0) + (spX.rothContrib || 0) : 0;
                  const spStop = spX.enabled ? contribStopOnPrimaryClock(params) : Infinity;
                  const hsaY = params.hsaContrib != null ? params.hsaContrib : (params.hsaMonthly || 0) * 12;
                  const yourTotal = (params.contrib || 0) + (params.employerContrib || 0) + (params.rothContrib || 0);
                  const household = yourTotal + hsaY + (params.taxableContrib || 0) + spTotal;
                  return [
                    ["Your 401(k) + employer + Roth", fmtDollar(yourTotal) + "/yr"],
                    ...(spTotal > 0 ? [["Spouse 401(k) + employer + Roth", fmtDollar(spTotal) + "/yr"]] : []),
                    ...(hsaY > 0 ? [["HSA", fmtDollar(hsaY) + "/yr"]] : []),
                    ...(params.taxableContrib > 0 ? [["Brokerage", fmtDollar(params.taxableContrib) + "/yr"]] : []),
                    ["Total savings", fmtDollar(household) + "/yr"],
                    ["Years contributing", yrs + " yrs"],
                    ...(spTotal > 0 && Number.isFinite(spStop)
                      ? [["Spouse contributes until", `you are ${Math.round(Math.min(spStop, params.retireAge))}`]]
                      : []),
                    ["Projected added", fmtDollar(household * yrs)],
                  ];
                })()} />
                <InputCard title="Plan Parameters" rows={[["Current age", "Age " + params.currentAge], ["Retire age", "Age " + params.retireAge], ["Years to retirement", Math.max(0, params.retireAge - params.currentAge) + " yrs"], ["Pre-retirement glide", `${params.preRetireEq ?? 91}% equity / ${100 - (params.preRetireEq ?? 91)}% bonds`], ["Post-retirement glide", `${params.postRetireEq ?? 70}% equity / ${100 - (params.postRetireEq ?? 70)}% bonds`]]} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 600, color: "var(--accent-purple)", marginBottom: 10 }}>WITHDRAWAL PHASE ({retPhase})</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                {/* Reports the smile curve the engine actually runs. This
                    used to claim fixed bands ("115% until 74, then 85%")
                    that were never implemented — and which would imply a
                    26% spending cliff on a single birthday. The real model
                    is a compounding real rate; see spendingSmileFactor in
                    engine/expenses.js. */}
                <InputCard title="Living Expenses" rows={[
                  ["Base annual spend", fmtDollar(params.sp) + "/yr"],
                  ["Spending model", params.smile !== false ? "Blanchett smile" : "Flat (real)"],
                  ...(params.smile !== false ? [
                    [`Age ${Math.min(80, params.endAge)}`, `${Math.round(spendingSmileFactor(Math.min(80, params.endAge), params.retireAge) * 100)}% of base (real)`],
                    [`Age ${Math.min(90, params.endAge)}`, `${Math.round(spendingSmileFactor(Math.min(90, params.endAge), params.retireAge) * 100)}% of base (real)`],
                  ] : []),
                ]} />
                <InputCard title="Income Offsets" rows={[["Social Security", `$${(params.ssb || 0).toLocaleString()}/yr @ ${params.ssAge || "—"}`], ["SS COLA", `${params.ssCola ?? 2.4}%/yr`], ["Rental income", params.ab > 0 ? `$${(params.ab || 0).toLocaleString()}/yr` : "Not set"], ["SS gap", `Ages ${params.retireAge}–${(params.ssAge || params.retireAge) - 1}: $0`]]} />
                <InputCard title="Additional Costs" rows={[[`Healthcare (age ${params.hcShockAge ?? 72}+)`, `${params.hcProb ?? 3.5}% shock prob/yr`], ["Shock range", `${fmtDollar(params.hcMin ?? 70000)}–${fmtDollar(params.hcMax ?? 130000)}`], ["Mortgage annual", mortAnnual > 0 ? fmtDollar(mortAnnual) + "/yr" : "Paid off"], ["Mortgage payoff", mortPayoffAge > 0 ? "~" + mortPayoffAge : "—"]]} />
              </div>
            </div>
            {/* One-off cash flows — rendered only when the user has entered
                at least one, so the panel doesn't grow an empty section for
                the common case. Inflows and outflows are separate cards
                because they're separate mechanics: a windfall is deposited
                into a bucket and compounds, a one-off cost is added to that
                year's spend. Showing them in one list is what let a $1M
                inheritance read as a $1M expense. */}
            {(cfInflows.length > 0 || cfOutflows.length > 0) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: "var(--fs-small)", fontWeight: 600, color: "var(--accent-gold)", marginBottom: 10 }}>
                  ONE-OFF CASH FLOWS ({cfInflows.length + cfOutflows.length} event{cfInflows.length + cfOutflows.length === 1 ? "" : "s"})
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                  {cfInflows.length > 0 && (
                    <InputCard title={`Income & Windfalls (${cfInflows.length})`} rows={[
                      ...cfInflowRows,
                      ["Total as entered", "+" + fmtDollar(cfInflows.reduce((s, e) => s + (Number(e.amount) || 0), 0))],
                    ]} />
                  )}
                  {cfOutflows.length > 0 && (
                    <InputCard title={`Planned One-Off Expenses (${cfOutflows.length})`} rows={[
                      ...cfOutflowRows,
                      ["Total as entered", "−" + fmtDollar(cfOutflows.reduce((s, e) => s + (Number(e.amount) || 0), 0))],
                    ]} />
                  )}
                  <InputCard title="How AiRA models these" rows={[
                    ["Windfalls", "Deposited to the account you chose, then compound"],
                    ["One-off costs", "Added on top of that year's spend"],
                    ["Timing", "Fires in one year only, accumulation or retirement"],
                    ["Applied to", `All ${pathsLabel} paths + the year-by-year plan`],
                  ]} />
                </div>
              </div>
            )}
            <div>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 600, color: "var(--positive)", marginBottom: 10 }}>MARKET & STATISTICAL MODEL</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                <InputCard title="Return Distribution" rows={[["Model", "Historical bootstrap"], ["Equity data", `${SAMPLE_YEARS}yr S&P 500 (${SAMPLE_START_YEAR}–${SAMPLE_END_YEAR})`], [`Pre-retire mix (${params.preRetireEq ?? 91}/${100 - (params.preRetireEq ?? 91)})`, "Equity / Bonds"], [`Post-retire mix (${params.postRetireEq ?? 70}/${100 - (params.postRetireEq ?? 70)})`, "Equity / Bonds"]]} />
                <InputCard title="Inflation & Guardrails" rows={[["Inflation", "Historical bootstrap"], ["Inflation source", `CPI for the same years (${SAMPLE_START_YEAR}–${SAMPLE_END_YEAR})`], ["GK floor", fmtDollar(params.gkFloor) + "/yr"], ["GK ceiling", fmtDollar(params.gkCeiling) + "/yr"]]} />
                <InputCard title="Simulation Parameters" rows={[["Simulations", `${pathsLabel} paths`], ["Horizon", `Age ${params.endAge || 90} (your plan age)`], ["Withdrawal", getStrategyLabel(params.withdrawalStrategy || "smart")], ["Rental reliability", `${params.abReliability ?? 80}% per year`]]} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* The panel is DeepSeek's src/forecast/CheckpointsPanel.jsx (WP-G): labelled
          row actions, a confirm before "set baseline", and a reason when Save
          cannot proceed. It owns its own collapse header. */}
      <CheckpointsPanel
        checkpoints={checkpoints}
        mc={mc}
        retireAge={effRetireAge}
        dob={dob}
        currentPort={params?.port || 0}
        fmtDollar={fmtDollar}
        onUpdate={onUpdateCheckpoints}
        onDelete={onDeleteCheckpoint}
        onSetBaseline={onSetBaselineFromCheckpoint}
      />

      {/* Disclaimer LAST, matching the Withdrawal tab's placement and the
          earlier design-authority ruling recorded there: a "not financial
          advice" notice has to be reliably present, not first. Sitting above the
          results it pushed the answer the user opened this tab for below the
          fold — the same "promote the summary, don't bury it" problem the
          v1.2.110 regroup fixed. The always-visible short form keeps the two
          legal hooks ("not financial advice", "consult a licensed professional")
          on screen; the elaboration is one click away and also permanent in the
          footer and the Terms of Service. */}
      {/* Disclaimer moved OUT of MCTab to the very bottom of the Monte Carlo
          tab (after the fan chart + band table) — it was rendering mid-page,
          between the inputs panel and the Forecast Portfolio chart, eating
          prime real estate. See the montecarlo tab render (renders
          <McTabDisclaimer/> last). */}
    </div>
  );
}

function McTabDisclaimer() {
  return (
    <SectionDisclaimer>
      These are hypothetical projections generated by a model, not a prediction and not a
      recommendation to buy, sell, or hold any investment. A success rate is the share of
      simulated scenarios in which the portfolio survived — it is not a probability that
      your actual retirement will succeed, and past market results do not guarantee future
      ones. Discuss any decision with a licensed financial, tax, or legal professional who
      knows your full circumstances.
    </SectionDisclaimer>
  );
}

function MortgageTab({ values, onChange }) {
  const bal   = values.mortBalance || 0;
  const rate  = values.mortRate    ?? 6.5;
  const extra = values.mortExtra   || 0;
  const start = values.mortStart   || "2020-01";
  const term  = values.mortTerm    || 30;

  const sched   = useMemo(() => mortgageSchedule(bal, rate, start, term, extra),  [bal, rate, start, term, extra]);
  const schedNE = useMemo(() => mortgageSchedule(bal, rate, start, term, 0),       [bal, rate, start, term]);
  const chartData = useMemo(() => {
    const maxLen = Math.max(sched.years.length, schedNE.years.length);
    return Array.from({ length: maxLen }, (_, i) => ({
      yr: new Date().getFullYear() + i,
      "With extra": sched.years[i]?.bal ?? 0,
      Original:     schedNE.years[i]?.bal ?? 0,
    }));
  }, [sched, schedNE]);

  const properties = values.properties || [{ id: "p1", label: "Primary Residence", value: 0, mortgage: 0, income: 0 }];
  const nav = useContext(NavContext);

  const totalValue    = properties.reduce((s, p) => s + (p.value||0), 0);
  const totalMortgage = properties.reduce((s, p) => s + (p.mortgage||0), 0);
  const totalEquity   = totalValue - totalMortgage;
  const totalIncome   = properties.reduce((s, p) => s + (p.income||0), 0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

      {/* Read-only summary. The one editor lives in Plan inputs > Real Estate & Debt. */}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <div style={{ fontSize: "var(--fs-label)", fontWeight:600, color:"var(--text-primary)" }}>Properties</div>
          {nav && (
            <button type="button" className="cfg-btn" style={{ width:"auto", margin:0, padding:"6px 14px" }}
              onClick={() => nav("assumptions", null, PROFILE_STEP_REAL_ESTATE)}>
              Edit in Plan inputs →
            </button>
          )}
        </div>
        {properties.filter(pr => (pr.value||0) || (pr.mortgage||0) || (pr.income||0)).map((prop) => (
          <div key={prop.id} style={{ display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap", fontSize: "var(--fs-label)", color:"var(--text-secondary)", border:"1px solid var(--card-border)", borderRadius:8, padding:"8px 12px" }}>
            <strong style={{ color:"var(--text-primary)" }}>{prop.label}</strong>
            <span>Value {fmtDollar(prop.value||0)} · Mortgage {fmtDollar(prop.mortgage||0)} · Equity {fmtDollar((prop.value||0)-(prop.mortgage||0))}{(prop.income||0) > 0 ? ` · ${fmtDollar(prop.income)}/yr income` : ""}</span>
          </div>
        ))}

        {/* Totals row */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
          {[
            { l:"Total value",    v:totalValue,    c:"var(--text-primary)" },
            { l:"Total mortgage", v:totalMortgage, c:"var(--text-primary)" },
            { l:"Total equity",   v:totalEquity,   c:"var(--text-primary)" },
            { l:"Annual income",  v:totalIncome,   c:"var(--text-primary)" },
          ].map(m => (
            <div key={m.l} className="met">
              <div className="ml">{m.l}</div>
              <div className="mv" style={{ color:m.c, fontSize: "var(--fs-h2)" }}>{fmtDollar(m.v)}</div>
              <div className="ms">{formulaFor("bucket-metrics")}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PRIMARY MORTGAGE CALCULATOR ── */}
      <div className="chart-card">
        <div className="ct">{properties[0]?.label || "Primary Residence"} · Mortgage calculator</div>

        <div className="metrics" style={{ marginBottom:12 }}>
          <div className="met">
            <div className="ml">Current balance</div>
            <div className="mv" style={{ color:"var(--text-primary)", fontSize: "var(--fs-h2)" }}>{fmtDollar(bal)}</div>
          </div>
          <div className="met">
            <div className="ml">Payoff year</div>
            <div className="mv" style={{ color:"var(--text-primary)", fontSize: "var(--fs-h2)" }}>{sched.payoffYr}</div>
            <div className="ms">With ${extra}/mo extra</div>
          </div>
          <div className="met">
            <div className="ml">Interest saved</div>
            <div className="mv" style={{ color:"var(--text-primary)", fontSize: "var(--fs-h2)" }}>{fmtDollar(sched.interestSaved)}</div>
            <div className="ms">vs no extra</div>
          </div>
          <div className="met">
            <div className="ml">Monthly P&I</div>
            <div className="mv" style={{ color:"var(--text-primary)", fontSize: "var(--fs-h2)" }}>{fmtDollar(sched.pmt)}</div>
            <div className="ms">At {rate}% fixed</div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top:8, right:8, left:0, bottom:0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)"/>
            <XAxis dataKey="yr" stroke="#1e3a5f" tick={{ fill:"var(--text-faint)", fontSize: "var(--fs-small)" }}/>
            <YAxis stroke="#1e3a5f" tick={{ fill:"var(--text-faint)", fontSize: "var(--fs-small)" }}
              tickFormatter={v=>fmtDollar(v)} width={MONEY_AXIS_WIDTH}/>
            <Tooltip content={<Tip/>}/>
            <Line type="monotone" dataKey="With extra" stroke="var(--positive)" strokeWidth={2.5} dot={false}/>
            <Line type="monotone" dataKey="Original" stroke="var(--text-faint)" strokeWidth={1.5} strokeDasharray="4 3" dot={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── AMORTIZATION TABLE ── */}
      <div className="chart-card">
        <div className="ct">Amortization — first 10 years{extra > 0 ? " with extra payments" : ""}</div>
        <table className="nw-table">
          <thead>
            <tr><th>Year</th><th>Principal</th><th>Interest</th><th>Extra</th><th>Balance</th></tr>
          </thead>
          <tbody>
            {sched.years.slice(0,10).map(r => (
              <tr key={r.yr}>
                <td style={{ textAlign:"left", fontFamily:"'DM Sans',sans-serif" }}>{r.yr}</td>
                <td>{fmtDollar(r.pPaid)}</td>
                <td style={{ color:"var(--chart-1)" }}>{fmtDollar(r.iPaid)}</td>
                <td style={{ color:"var(--positive)" }}>{fmtDollar(r.ePaid)}</td>
                <td style={{ color:"#0ea5e9" }}>{fmtDollar(r.bal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// mcMedianAtAge / selectPortfolioAtAge moved to engine/mcSelectors.js
// (imported at the top of this file) — see that file's doc comments for
// the full history (the $0-vs-null bug, the flat-tail clamp bug, and the
// three-call-sites-disagreed bug this consolidation fixed).

/**
 * Net Worth chart tooltip — a component breakdown of the hovered year.
 *
 * The shared <Tip/> lists whatever series happen to be on the chart, which for
 * this one reads "Liquid Portfolio / Mortgage Debt / Real Estate / Net Worth" —
 * four lines where two are the same money counted twice (Mortgage Debt is shown
 * negative and already subtracted inside Net Worth), and none of them says which
 * accounts the liquid figure is made of.
 *
 * This version leads with the total and shows the composition under it:
 * pre-tax · tax-free (Roth + HSA) · taxable · cash, then real estate and the
 * mortgage that comes off. It also names the calendar year and whose figures
 * these are, so a hover answers "what am I looking at" without a trip to the
 * legend.
 *
 * Buckets come from ACCOUNT_CATEGORIES — the same source the sidebar donut and
 * the account editor use — so the split can't drift from what the user entered.
 * The liquid total itself is NOT re-summed here: it stays the engine's number
 * from the row, and the bucket lines are shown only when they reconcile to it
 * (see the check below). If they ever stop reconciling, the breakdown hides
 * rather than contradicting the headline.
 */
function NetWorthTip({ active, payload, label, p }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const liquid = row["Liquid Portfolio"];
  const mort = row["Mortgage Debt"] || 0;
  const re = row["Real Estate"] || 0;
  const nw = row["Net Worth"];
  const yr = Number.isFinite(label) ? new Date().getFullYear() + (label - (p?.currentAge ?? label)) : null;

  // Account-category split of the liquid portfolio, from the profile the user
  // filled in. Cash and "cash-like" categories roll into one line so the block
  // stays short; Roth + HSA group as tax-free.
  const accts = p?.accounts || [];
  const sumOf = (keys) => accts.filter((a) => keys.includes(a.category)).reduce((s, a) => s + (a.balance || 0), 0);
  const groups = [
    { name: "Pre-Tax",     color: "#0ea5e9", value: sumOf(["pretax"]) },
    { name: "Tax-Free",    color: "var(--positive)", value: sumOf(["roth", "hsa"]) },
    { name: "Taxable",     color: "var(--accent-gold)", value: sumOf(["taxable"]) },
    { name: "Cash",        color: "var(--text-muted)", value: sumOf(["cash"]) },
  ].filter((g) => g.value > 0);
  const groupTotal = groups.reduce((s, g) => s + g.value, 0);
  // These are TODAY'S entered balances. The chart line is a projection, so at
  // any age past the current one the two are different numbers by construction -
  // and they must never be presented as if the split adds up to the hovered
  // year. So the block is labelled as what it is (a "from" line under the first
  // data point, where they genuinely do coincide; otherwise an explicitly
  // today-labelled composition), and it is dropped entirely once the projection
  // has run past them by more than a rounding step.
  // "Equal to the hovered year" has to mean actually equal, not "within a few
  // percent" — a 1-2% drift is exactly what one year of growth looks like, and
  // calling that the year's own composition would be the lie this whole guard
  // exists to prevent. So the unlabelled case is tight (rounding only), while
  // the split stays visible-but-labelled for a little while longer, until the
  // projection has overtaken today's total.
  const atStart = Number.isFinite(liquid) && Math.abs(liquid - groupTotal) <= 1;
  const stillTraceableToToday = Number.isFinite(liquid) && liquid > 0 && liquid <= groupTotal * 1.5;

  const line = (name, value, color, opts = {}) => (
    <div key={name} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 1 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ color: "var(--text-secondary)" }}>{name}: </span>
      <span style={{ color: opts.muted ? "var(--text-muted)" : "var(--text-primary)" }}>{fmtDollar(value)}</span>
    </div>
  );

  return (
    <div className="tip-box">
      <div style={{ color: "var(--text-muted)", marginBottom: 4 }}>
        Age {label}{yr ? ` · ${yr}` : ""}
      </div>
      {line("Liquid Portfolio", liquid ?? 0, "#0ea5e9")}
      {groups.length > 0 && (atStart || stillTraceableToToday) && (
        <div style={{ marginLeft: 14, marginBottom: 2 }}>
          {!atStart && (
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 1 }}>
              from today's balances of:
            </div>
          )}
          {groups.map((g) => (
            <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--fs-body)" }}>
              <span style={{ width: 6, height: 6, borderRadius: 1, background: g.color, display: "inline-block", flexShrink: 0, opacity: 0.75 }} />
              <span style={{ color: "var(--text-muted)" }}>{g.name}: </span>
              <span style={{ color: "var(--text-muted)" }}>{fmtDollar(g.value)}</span>
            </div>
          ))}
        </div>
      )}
      {re > 0 && line("Real Estate", re, "var(--accent-gold)")}
      {mort !== 0 && line("Mortgage Debt", mort, "var(--negative)", { muted: true })}
      {Number.isFinite(nw) && (
        <div style={{ marginTop: 5, paddingTop: 5, borderTop: "1px solid var(--tint-white-14)", display: "flex", justifyContent: "space-between", gap: 10 }}>
          <span style={{ color: "var(--text-secondary)", fontWeight: 700 }}>Total Net Worth</span>
          <span style={{ color: "var(--text-primary)", fontWeight: 700 }}>{fmtDollar(nw)}</span>
        </div>
      )}
    </div>
  );
}

export function NetWorthTab({ p, mc, inf, real }) {
  const [showRE, setShowRE] = useState(false);
  // The per-age chart line and "Net worth at age X" card go through
  // selectPortfolioAtAge() below (the shared basis-aware lookup — see its
  // doc comment). `dPcts` stays as a local, array-wide deflate() only because
  // the peak-liquid scan below needs every row at once, which a single-age
  // selector call can't give it; it uses the SAME `deflate()` primitive the
  // selector uses internally, so the two can't drift out of basis with
  // each other again.
  // eslint-disable-next-line no-restricted-properties -- array-wide scan (peak across every age); no single-age selector call could give this.
  const dPcts = useMemo(() => (mc ? deflate(mc.pcts, inf, real) : []), [mc, inf, real]);
  const props    = p.properties || [];
  const reTotal   = props.reduce((s, pr) => s + (pr.value||0), 0);
  const reMortgs  = props.reduce((s, pr) => s + (pr.mortgage||0), 0);
  const reEquity  = reTotal - reMortgs;
  const mortSched = useMemo(
    () =>
      mortgageSchedule(
        p.mortBalance,
        p.mortRate,
        p.mortStart,
        p.mortTerm,
        p.mortExtra
      ),
    [p]
  );

  const nwData = useMemo(() => {
    if (!mc) return [];

    // Current liquid portfolio from accounts (matches home page sidebar)
    const currentPort = (p.accounts || []).reduce((s, a) => s + (a.balance || 0), 0) || p.port || 0;
    const preReturnRate = expectedReturn(p.preRetireEq ?? 91) / 100;

    const maxChartAge = p.endAge;
    const step = 1;
    const ages = [];
    for (let age = p.currentAge; age <= maxChartAge; age += step) {
      ages.push(age);
    }

    return ages.map((age, idx) => {
      const yr = new Date().getFullYear() + idx * step;

      let port;
      if (age < p.retireAge) {
        // Accumulation phase: grow from current portfolio deterministically
        const yearsToAge = age - p.currentAge;
        let acc = currentPort;
        for (let y = 0; y < yearsToAge; y++) {
          // Household total per year, not the 401(k) line alone — and it
          // drops the spouse's streams on their own retirement date, so
          // this projection tracks the engines instead of drifting from them.
          // Grow, then add the year's contributions and any one-off inflow
          // that lands in it — the same order and the same year mapping the
          // engines use (accumulation year y = CURRENT_YEAR + y). Without
          // the inflow term this curve ignored windfalls until retirement,
          // then handed over to the MC median which had counted them: the
          // jump landed on the retirement year regardless of the year the
          // user entered. Inflows only, matching the engines — pre-retirement
          // one-off costs are assumed to be paid from wages.
          acc = acc * (1 + preReturnRate) + householdAnnualContribution(p, p.currentAge + y)
              + computeCashFlowEvents(p.cashFlowEvents, CURRENT_YEAR + y, p.inf ?? 2.5, CURRENT_YEAR).inflow;
        }
        port = Number.isFinite(acc) ? Math.round(acc) : null;
      } else {
        // Retirement phase: the MC median for this age.
        //
        // Looked up by age, not by array position. This used to be
        //   pctIndex = Math.min(age - p.retireAge, pcts.length - 1)
        //   port     = pcts[pctIndex]?.p50 || 0
        // which had two failure modes, both of which drew a confident $0:
        //
        //   1. `Math.min` clamped past the end of the data, so every age
        //      beyond the Monte Carlo horizon repeated the final entry. A
        //      plan whose horizon is shorter than `endAge` got a flat line
        //      of fabricated years, and the "net worth at age" card below
        //      read that fabricated value as though it were a forecast.
        //   2. `|| 0` collapsed three different states into one number —
        //      genuinely zero, no data, and NaN. NaN is falsy, so a single
        //      broken input rendered as $0 from the poisoned year onward,
        //      indistinguishable from a real answer. A user reported
        //      exactly that: a plan the engine scores at 99.2% success,
        //      with a median of $4.05M at 68 and $10.9M at 90, drawn as $0
        //      from 68 to 90.
        //
        // Positional indexing also silently misaligned whenever `mc` was
        // stale (computed at a different retireAge than the one being
        // charted); the rows carry their own `age`, so use it.
        port = selectPortfolioAtAge(mc, age, { retireAge: p.retireAge, real, inf });
      }

      const mortEntry = mortSched.years.find((y) => y.yr === yr);
      const mortBal = mortEntry ? mortEntry.bal : 0;
      const yearsFromNow = yr - new Date().getFullYear();
      const reGrow = Math.pow(1 + (p.reGrowthRate ?? 3.0) / 100, yearsFromNow);
      const re = showRE ? Math.round(reTotal * reGrow) : 0;
      // null (not 0) where there's no portfolio figure: Recharts breaks the
      // line on null, which is the honest rendering of "not modeled." Net
      // Worth has to break with it, or the chart would keep drawing
      // `re - mortBal` as if it were a projection.
      return {
        age,
        "Liquid Portfolio": port,
        "Mortgage Debt": -mortBal,
        "Real Estate": re,
        "Net Worth": port == null ? null : port + re - mortBal,
      };
    });
  }, [p, mc, dPcts, showRE, mortSched, reTotal]);

  // Peak liquid portfolio (median). Both of these read the row's own age
  // rather than deriving it from `retireAge + index`, and both ignore
  // non-finite p50s — one NaN used to turn the headline figure into NaN via
  // Math.max. Reads `dPcts` (same basis as the chart above), not raw
  // `mc.pcts` — otherwise this card and "Net worth at age X" below it would
  // show two different bases.
  const finitePcts = useMemo(
    () => dPcts.filter((d) => Number.isFinite(d.p50)),
    [dPcts]
  );
  const peakRow = finitePcts.reduce(
    (best, d) => (best == null || d.p50 > best.p50 ? d : best),
    null
  );
  const peakPort = peakRow ? peakRow.p50 : 0;
  const peakAge = peakRow ? (peakRow.age ?? p.retireAge + finitePcts.indexOf(peakRow)) : 0;

  // The last age the chart actually has a figure for, and the final value
  // there. Reporting `p.endAge` when the data stops earlier is what let the
  // summary card announce "$0 at age 90" for a plan that was never modeled
  // to 90.
  const lastRealRow = [...nwData].reverse().find((d) => d["Net Worth"] != null);
  const finalNW = lastRealRow ? lastRealRow["Net Worth"] : 0;
  const lastDataAge = lastRealRow ? lastRealRow.age : null;
  const planAge = p.endAge;
  const dataStopsEarly = lastDataAge != null && lastDataAge < planAge;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="metrics">
        <div className="met">
          <div className="ml">Peak portfolio*</div>
          <div className="mv" style={{ color: "var(--positive)", fontSize: "var(--fs-h2)" }}>
            {fmtDollar(peakPort)}
          </div>
          <div className="ms">Age {peakAge} · typical outcome</div>
        </div>
        {/* Names the age the number is actually for. It used to always say
            `planAge` (the age you typed) even when the projection stopped
            earlier, so a missing-data $0 was presented as "net worth at 90". */}
        <div className="met">
          <div className="ml">Net worth at age {lastDataAge ?? planAge}</div>
          <div className="mv" style={{ color: dataStopsEarly ? "var(--accent-gold)" : "var(--text-primary)", fontSize: "var(--fs-h2)" }}>
            {fmtDollar(finalNW)}
          </div>
          <div className="ms">
            {showRE ? "Incl." : "Excl."} real estate · {dollarBasisLabel(real, retirementBasisYear(p.currentAge, p.retireAge, CURRENT_YEAR))}
            {dataStopsEarly && (
              <span style={{ color: "var(--accent-gold)" }}> · projection stops here, not {planAge}</span>
            )}
          </div>
        </div>
        {/* A card for something the user never entered is an empty state dressed
            as a metric ("Mortgage-free —", "Real estate equity $0"). Each shows
            only once there is something to show; the line under the row says
            what is missing and where to add it. */}
        {p.mortBalance > 0 && (
        <div className="met">
          <div className="ml">Mortgage‑free</div>
          <div className="mv" style={{ color: "var(--text-primary)", fontSize: "var(--fs-h2)" }}>
            {mortSched.payoffYr}
          </div>
          <div className="ms">
            {p.mortExtra > 0 ? "With extra payments" : "Standard payments"}
          </div>
        </div>
        )}
        {reTotal > 0 && (
        <div className="met">
          <div className="ml">Real estate equity</div>
          <div className="mv" style={{ color: "var(--text-primary)", fontSize: "var(--fs-h2)" }}>
            {fmtDollar(reEquity)}
          </div>
          <div className="ms">Not counted in your portfolio</div>
        </div>
        )}
        {/* This card used to render three different quantities under one
            label ("Safe spending target") and one strategy caption. Only
            two of the three are computed; the middle branch — by far the
            most common, since it fires for every strategy except `fixed`
            once a target is entered — just echoes the user's own number
            back. Captioning that "GK guardrails" attributed the figure to a
            strategy that hadn't touched it. Each branch now states what it
            actually is, and all three disclose the after-tax basis (runMC
            draws tax on top of the target, never out of it — see the
            fixed-point loop in runMC). */}
        {(() => {
          const port     = (p.accounts||[]).reduce((s,a)=>s+(a.balance||0),0) || p.port || 0;
          const strategy = resolveStrategy(p.withdrawalStrategy);
          const rate     = p.fixedWithdrawalRate || 0.04; // always decimal in params (normalized in the params memo)
          // No literal fallback rate: the benchmark the rest of the app shows
          // is the user's own safeWithdrawalRate, so this card uses the same one.
          const benchRate = p.safeWithdrawalRate ?? 0.04;
          const pctOf     = (r) => (r * 100).toFixed(1).replace(/\.0$/, "");
          const STRAT = { gk:"Guyton-Klinger guardrails", fixed:"Fixed %", vpw:"VPW", ninety_five_rule:"95% rule", bengen:"Bengen 4% rule", smart:"Smart Waterfall" };

          let monthly, label, note, hint;
          if (strategy === "fixed") {
            monthly = Math.round(port * rate / 12);
            label   = "Spending target";
            note    = `${pctOf(rate)}% of portfolio · per month · after tax`;
            hint    = `Computed: ${pctOf(rate)}% of your ${fmtDollar(port)} portfolio, divided by 12. This is money to spend after tax — the engine withdraws extra to cover the tax bill on top of it.`;
          } else if (p.sp > 0) {
            monthly = Math.round(p.sp / 12);
            label   = "Your spending target";
            note    = `what you entered · per month · after tax`;
            hint    = `The annual spending target you entered (${fmtDollar(p.sp)}), shown monthly. This is not a figure AiRA computed — the success rate is what tells you whether it holds. ${STRAT[strategy] || strategy} adjusts it year to year during the simulation. Money to spend after tax: the engine withdraws extra to cover the tax bill on top of it.`;
          } else {
            monthly = Math.round(port * benchRate / 12);
            label   = "Estimated spending target";
            note    = `${pctOf(benchRate)}% of portfolio · no target set · after tax`;
            hint    = `You have not entered a spending target, so this is a placeholder: ${pctOf(benchRate)}% of your ${fmtDollar(port)} portfolio, divided by 12. Enter your real target in Plan inputs → Spending & Expenses. Money to spend after tax.`;
          }

          return (
            <div className="met" title={hint}>
              <div className="ml">{label}</div>
              {/* fmtDollar, not an inline toLocaleString — one money
                  helper. Inline formatting is how an abbreviator slipped in
                  unnoticed once already. */}
              <div className="mv" style={{ color: "#4ade80", fontSize: "var(--fs-h2)" }}>{fmtDollar(monthly)}</div>
              <div className="ms">{note}</div>
            </div>
          );
        })()}
      </div>
      {!(p.mortBalance > 0 && reTotal > 0) && (
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: -4 }}>
          {[!(reTotal > 0) && "No real estate entered", !(p.mortBalance > 0) && "No mortgage modeled"].filter(Boolean).join(" · ")}
          {" · "}<GoStep step={PROFILE_STEP_REAL_ESTATE}>add in Plan inputs</GoStep>
        </div>
      )}

      <div className="chart-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div>
              <div className="ct" style={{ margin: 0 }}>{showRE ? "Net worth projection" : "Portfolio projection"}</div>
              <div className="ct-sub" style={{ margin: "2px 0 0" }}>
                Typical outcome, every 5 years to age {lastDataAge ?? planAge} · {dollarBasisLabel(real, retirementBasisYear(p.currentAge, p.retireAge, CURRENT_YEAR))}{showRE ? "" : " · excludes real estate"}
              </div>
            </div>
            {/* Was a title= tooltip on an emoji, which does nothing on click or touch. InfoDot opens the dialog. */}
            <InfoDot
              heading="Reading this chart"
              title="Liquid Portfolio = investments (excl. real estate). Mortgage Debt shown as negative (dashed red line). Net Worth = Liquid + Real Estate - Mortgage Debt."
            />
          </div>
          <Toggle
            val={showRE}
            onChange={setShowRE}
            label="Include Real Estate In Projection"
            accent="var(--accent-gold)"
          />
        </div>

        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={nwData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="2 4" stroke="var(--row-highlight)" />
            <XAxis
              dataKey="age"
              stroke="#1e3a5f"
              tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
            />
            <YAxis
              stroke="#1e3a5f"
              tick={{ fill: "var(--text-faint)", fontSize: "var(--fs-small)" }}
              tickFormatter={(v) => fmtDollar(v)}
              width={MONEY_AXIS_WIDTH}
            />
            <Tooltip content={<NetWorthTip p={p} />} />
            <Line
              type="monotone"
              dataKey="Liquid Portfolio"
              stroke="#0ea5e9"
              strokeWidth={2.5}
              dot={false}
            />
            {showRE && (
              <Line
                type="monotone"
                dataKey="Real Estate"
                stroke="var(--accent-gold)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="Mortgage Debt"
              stroke="var(--chart-1)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="Net Worth"
              stroke="var(--positive)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* A break in the line now means "no figure for these years" and
            says so. Before, those years were drawn as $0 — a plan the
            engine scores at 99% success could appear to end broke. Silence
            is better than a wrong number, but a stated reason is better than
            silence. */}
        {dataStopsEarly && (
          <div style={{
            marginTop: 8, padding: "10px 12px", borderRadius: 8,
            background: "var(--tint-orange-10)", border: "1px solid var(--tint-orange-35)",
            fontSize: "var(--fs-small)", color: "#fdba74", lineHeight: 1.55,
          }}>
            <strong>The line stops at age {lastDataAge}.</strong> The last Monte Carlo run covers
            ages {p.retireAge}–{lastDataAge}, not through {planAge}, so ages {lastDataAge + 1}–{planAge} are
            left blank rather than guessed. If you changed your retirement or planning age since the
            last run, press <strong>▶ Run Monte Carlo</strong> to refresh. If it keeps stopping short,
            one of your inputs is producing an invalid number for those years — a one-off cash-flow
            event or income entry with a blank or malformed amount is the usual cause.
          </div>
        )}

        {/* Legend */}
        <div className="leg" style={{ marginTop: 8, justifyContent: "center" }}>
          <div className="li"><div className="ll" style={{ background: "#0ea5e9" }} />Liquid Portfolio</div>
          <div className="li"><div className="ll" style={{ background: "var(--chart-1)", borderTop: "1px dashed var(--chart-1)", height: 2 }} />Mortgage Debt (dashed)</div>
          {showRE && <div className="li"><div className="ll" style={{ background: "var(--accent-gold)" }} />Real Estate</div>}
          <div className="li"><div className="ll" style={{ background: "var(--positive)" }} />{showRE ? "Net Worth" : "Portfolio (ex. RE)"}</div>
        </div>

        {/* Footnote about peak age */}
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 6, textAlign: "center" }}>
          * Peak portfolio is the highest point of the typical (median) outcome across your whole plan, to age {p.endAge}.
        </div>
      </div>

      {!mc && (
        <div className="flag-i">
          ℹ Run Monte Carlo first to see net worth projections.
        </div>
      )}
    </div>
  );
}

// Priority colors
const PRIORITY_COLOR = {
  red:    { border: "var(--negative)", bg: "var(--tint-red-07)",  label: "var(--chart-1)" },
  yellow: { border: "var(--accent-gold)", bg: "rgba(244,211,94,0.07)", label: "var(--accent-gold)" },
  green:  { border: "var(--positive)", bg: "rgba(79,216,143,0.07)", label: "var(--positive)" },
};

// Milestone timeline
function buildMilestones(params, rmdAge) {
  const age       = params?.currentAge  || 56;
  const retireAge = params?.retireAge   || 65;
  const ssAge     = params?.ssAge       || 67;
  const yr        = new Date().getFullYear();
  const hasPreTax = (params?.accounts || []).some(a => a.category === "pretax" && (a.balance || 0) > 0);
  const rmd       = rmdAge || getRmdStartAge({ currentAge: age });

  const all = [
    {
      age: age, label: "Now", year: yr,
      items: [
        "Review your withdrawal rate — target below 4%",
        "Ensure emergency fund covers 6 months of expenses",
        "Verify beneficiary designations on all accounts",
      ],
    },
    age < 50 && {
      age: 50, label: "Catch-Up Contributions", year: yr + (50 - age),
      items: [
        "401k limit increases by $7,500/yr (catch-up)",
        "IRA limit increases by $1,000/yr",
        "Review asset allocation — consider gradual de-risking",
      ],
    },
    age < 55 && {
      age: 55, label: "Age 55 Benefits", year: yr + (55 - age),
      items: [
        "HSA catch-up increases by $1,000/yr",
        "Rule of 55 — penalty-free 401k withdrawals if retiring this year",
        "Start projecting Medicare bridge costs if retiring before 65",
      ],
    },
    age < 60 && {
      age: 60, label: "Super Catch-Up (SECURE 2.0)", year: yr + (60 - age),
      items: [
        "401k limit rises to $34,750/yr for ages 60–63 only",
        "Maximize this 4-year window — largest single tax-shelter opportunity",
        "Consider Roth conversion ladder if retiring at 60",
      ],
    },
    {
      age: retireAge, label: "Retirement", year: yr + (retireAge - age),
      items: [
        "Begin tax-optimal withdrawal: HSA first (medical) → taxable → pre-tax → Roth last",
        "Shift to inflation-adjusted withdrawal strategy",
        retireAge < 65 ? `Bridge to Medicare — budget for ACA marketplace until age 65` : "Enroll in Medicare Parts A and B",
        `Social Security: ${ssAge > retireAge ? `consider delaying to age ${ssAge} for higher benefit` : "claim at retirement"}`,
      ],
    },
    retireAge < 65 && {
      age: 65, label: "Medicare Enrollment", year: yr + (65 - age),
      items: [
        "Enroll during 7-month Initial Enrollment Period (3 months before 65)",
        "IRMAA surcharge if income >$103,000 single or >$206,000 MFJ",
        "Stop HSA contributions at 65 (Medicare makes you ineligible)",
        "Compare Original Medicare vs Medicare Advantage",
      ],
    },
    ssAge !== retireAge && {
      age: ssAge, label: `Social Security — Age ${ssAge}`, year: yr + (ssAge - age),
      items: [
        `Claim SS benefit — delayed past FRA earns +8%/yr in higher lifetime income`,
        "Coordinate SS timing with Roth conversions to minimize IRMAA",
        "File for Medicare 3 months before 65 even if delaying SS",
      ],
    },
    hasPreTax && age < rmd && {
      age: rmd, label: "Required Minimum Distributions", year: yr + (rmd - age),
      items: [
        `RMDs begin at age ${rmd} — distributions from 401k/IRA are fully taxable ordinary income`,
        "25% penalty on any missed RMD amount",
        `Do Roth conversions before age ${rmd} to shrink the pre-tax balance`,
        "QCDs (qualified charitable distributions) offset RMDs tax-free if charitably inclined",
      ],
    },
  ].filter(Boolean).sort((a, b) => a.age - b.age);

  return all;
}

function MilestonesSection({ params, rmdAge }) {
  const [open, setOpen] = useState(false);
  const milestones = buildMilestones(params, rmdAge);
  if (!milestones.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%",
          marginBottom: open ? 12 : 0,
        }}
      >
        <span style={{ fontSize: "var(--fs-small)", color: "var(--accent)", transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
        <span style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent)" }}>
          Age Milestones
        </span>
        <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginLeft: 4 }}>({milestones.length} milestones)</span>
      </button>
      {open && (
      <div style={{ position: "relative" }}>
        {milestones.map((m, idx) => (
          <div key={m.age} style={{ display: "flex", gap: 14, marginBottom: 14 }}>
            {/* Timeline spine */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 36, flexShrink: 0 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "var(--tint-indigo-15)", border: "2px solid var(--tint-indigo-40)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--chart-4)", flexShrink: 0,
              }}>
                {m.age === (params?.currentAge || 56) ? "Now" : m.age}
              </div>
              {idx < milestones.length - 1 && (
                <div style={{ width: 2, flex: 1, minHeight: 16, background: "var(--tint-indigo-20)", marginTop: 4 }} />
              )}
            </div>
            {/* Milestone card */}
            <div style={{
              flex: 1, background: "rgba(99,102,241,0.04)", border: "1px solid var(--tint-indigo-15)",
              borderRadius: 9, padding: "10px 14px", marginBottom: 2,
            }}>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "#a5b4fc", marginBottom: 6 }}>
                {m.label}
                <span style={{ fontWeight: 400, color: "var(--text-faint)", marginLeft: 8 }}>{m.year}</span>
              </div>
              {m.items.map((item, j) => (
                <div key={j} style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 3, display: "flex", gap: 6 }}>
                  <span style={{ color: "var(--accent)", flexShrink: 0 }}>•</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

// Card step lookup
function getCardSteps(card) {
  if (card.steps?.length) return card.steps;
  const a = (card.action   || "").toLowerCase();
  const c = (card.category || "").toLowerCase();
  if (a.includes("roth") || c.includes("roth")) return [
    "Calculate your current marginal tax bracket for this year",
    "Convert up to the top of your bracket — stop before jumping to the next",
    "Model the conversion in AiRA's Tax Room tab to see the IRMAA impact",
    "Pay the conversion tax from taxable cash, not from the IRA itself",
  ];
  if (a.includes("withdrawal rate") || a.includes("swr") || a.includes("spending")) return [
    "Target a sustainable rate — 3.5–4% is the historical safe zone",
    "Switch to a dynamic strategy (Guardrails, VPW) to flex with markets",
    "Build a 1–2 year cash buffer so you never sell equities in a downturn",
    "Re-run Monte Carlo in AiRA after any spending or portfolio change",
  ];
  if (a.includes("social security") || c.includes("social security")) return [
    "Get your personalized estimate at SSA.gov → my Social Security",
    "Each year of delay past 62 adds roughly 6–8% to your monthly benefit",
    "Run the breakeven analysis — typically breaks even around age 79–82",
    "Coordinate timing with your spouse to maximize survivor benefit",
  ];
  if (a.includes("rmd") || a.includes("required minimum")) return [
    "Calculate this year's RMD using the IRS Uniform Lifetime Table",
    "Do Roth conversions now to reduce the pre-tax balance before RMDs begin",
    "Set up automatic distributions so you never miss the year-end deadline",
    "Consider QCDs (Qualified Charitable Distributions) to offset RMD income tax-free",
  ];
  if (a.includes("irmaa") || a.includes("medicare")) return [
    "Check the IRMAA brackets — Medicare uses your MAGI from 2 years ago",
    "Keep MAGI below the first cliff ($103,000 single / $206,000 MFJ) when possible",
    "Roth conversions this year affect Medicare premiums in two years — plan ahead",
    "If income dropped (retirement, death of spouse), file for IRMAA appeal (SSA-44)",
  ];
  if (a.includes("emergency") || a.includes("cash reserve") || a.includes("liquidity")) return [
    "Target 6 months of essential expenses in a high-yield savings account",
    "Current HYSA rates are 4%+ — don't leave this in a checking account",
    "Keep this separate from your investment portfolio — don't count brokerage cash",
    "Replenish immediately after any large withdrawal",
  ];
  if (a.includes("beneficiar")) return [
    "Log into each account (401k, IRA, life insurance) and check current beneficiaries",
    "Ensure primary and contingent beneficiaries are named — don't leave it blank",
    "Update after major life events: marriage, divorce, death, new children",
    "Confirm beneficiary designations override your will — they're separate legal documents",
  ];
  return [];
}

// Action plan row
function ActionPlanRow({ card, isSelected, onClick }) {
  const C = PRIORITY_COLOR[card.priority] || PRIORITY_COLOR.yellow;
  return (
    <div
      onClick={onClick}
      style={{
        display:      "flex",
        alignItems:   "center",
        gap:          12,
        padding:      "10px 14px",
        borderRadius: 8,
        background:   isSelected ? "rgba(99,102,241,0.1)" : "var(--tint-white-02)",
        border:       `1px solid ${isSelected ? "var(--tint-indigo-40)" : "var(--tint-white-06)"}`,
        cursor:       "pointer",
        transition:   "all 0.12s",
        marginBottom: 4,
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "var(--row-highlight)"; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "var(--tint-white-02)"; }}
    >
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.border, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: C.label, marginBottom: 1, display: "flex", alignItems: "center", gap: 5 }}>
          {card.category}
          {card.isLiveData  && <span style={{ color: "var(--chart-5)",  fontSize: "var(--fs-small)" }}>LIVE</span>}
          {card.aiGenerated && !card.isLiveData && <span style={{ color: "var(--accent-purple)", fontSize: "var(--fs-small)" }}>✦ AI</span>}
          {card.aiChecked && (
            <span style={{
              background: card.aiNote ? "var(--tint-indigo-15)" : "rgba(71,85,105,0.18)",
              color:      card.aiNote ? "var(--chart-4)"                : "var(--text-muted)",
              border:     `1px solid ${card.aiNote ? "var(--tint-indigo-30)" : "rgba(71,85,105,0.3)"}`,
              borderRadius: 4, padding: "1px 5px", fontSize: "var(--fs-small)", fontWeight: 700,
            }}>
              {card.aiNote ? "AI insight" : "AI reviewed"}
            </span>
          )}
        </div>
        <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {card.action}
        </div>
      </div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", flexShrink: 0, whiteSpace: "nowrap" }}>{card.deadline}</div>
      <div style={{ fontSize: "var(--fs-body)", color: isSelected ? "var(--chart-4)" : "#334155", flexShrink: 0 }}>›</div>
    </div>
  );
}

// Card detail panel
function CardDetailPanel({ card, onClose }) {
  const C     = PRIORITY_COLOR[card.priority] || PRIORITY_COLOR.yellow;
  const steps = getCardSteps(card);
  const label = card.priority === "red" ? "CRITICAL" : card.priority === "yellow" ? "IMPORTANT" : "ON TRACK";

  return (
    <div style={{
      background:   "rgba(15,23,42,0.98)",
      border:       `1px solid ${withAlpha(C.border, "40")}`,
      borderTop:    `3px solid ${C.border}`,
      borderRadius: 10,
      padding:      "20px",
      position:     "sticky",
      top:          8,
      maxHeight:    "80vh",
      overflowY:    "auto",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div style={{ flex: 1, paddingRight: 12 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{
              background: C.bg, border: `1px solid ${C.border}`, color: C.label,
              borderRadius: 4, padding: "2px 8px", fontSize: "var(--fs-small)", fontWeight: 700,
            }}>{label}</span>
            <span style={{
              background: "var(--tint-white-06)", color: "var(--text-secondary)",
              borderRadius: 4, padding: "2px 8px", fontSize: "var(--fs-small)", fontWeight: 600,
            }}>{card.category}</span>
            {card.isLiveData  && <span style={{ background: "rgba(6,182,212,0.1)", color: "var(--chart-5)", borderRadius: 4, padding: "2px 8px", fontSize: "var(--fs-small)", fontWeight: 700 }}>🌐 LIVE</span>}
            {card.aiGenerated && !card.isLiveData && <span style={{ background: "rgba(167,139,250,0.1)", color: "var(--accent-purple)", borderRadius: 4, padding: "2px 8px", fontSize: "var(--fs-small)", fontWeight: 700 }}>✦ AI</span>}
          </div>
          <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.4 }}>{card.action}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-faint)", cursor: "pointer", fontSize: "var(--fs-h1)", lineHeight: 1, flexShrink: 0 }}>×</button>
      </div>

      {/* Details */}
      {card.reason && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 6 }}>DETAILS</div>
          <div style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.65 }}>{card.reason}</div>
        </div>
      )}

      {/* What to do */}
      {steps.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-faint)", marginBottom: 8 }}>WHAT TO DO</div>
          {steps.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
              <div style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                background: "var(--tint-indigo-15)", border: "1px solid var(--tint-indigo-30)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--chart-4)",
              }}>{i + 1}</div>
              <div style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", lineHeight: 1.55, paddingTop: 2 }}>{step}</div>
            </div>
          ))}
        </div>
      )}

      {/* AI insight */}
      {card.aiNote ? (
        <div style={{
          background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.18)",
          borderRadius: 8, padding: "10px 14px", marginBottom: 14,
        }}>
          <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--chart-4)", marginBottom: 4 }}>AI INSIGHT</div>
          <div style={{ fontSize: "var(--fs-label)", color: "var(--accent-purple)", lineHeight: 1.55 }}>{card.aiNote}</div>
        </div>
      ) : card.aiChecked ? (
        <div style={{
          background: "rgba(71,85,105,0.08)", border: "1px solid rgba(71,85,105,0.2)",
          borderRadius: 8, padding: "8px 14px", marginBottom: 14,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <Dot tone="ok" />
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>AI reviewed — this card is already comprehensive, nothing to add.</span>
        </div>
      ) : null}

      {/* Source */}
      {card.source && (
        <div style={{ fontSize: "var(--fs-small)", color: "var(--chart-5)", marginBottom: 12 }}>Source: {card.source}</div>
      )}

      {/* Deadline */}
      <div style={{ fontSize: "var(--fs-label)", color: "var(--text-faint)", display: "flex", alignItems: "center", gap: 6, paddingTop: 8, borderTop: "1px solid var(--tint-white-06)" }}>
        ⏱ <span style={{ color: "var(--text-muted)" }}>{card.deadline}</span>
      </div>
    </div>
  );
}

// ActionPlanTab
function ActionPlanTab({ params, mc, assumptions, mortgagePayoffYear, rmdAge: rmdAgeProp }) {
  const currentYear = new Date().getFullYear();
  const retireYear = currentYear + ((params?.retireAge || 60) - (params?.currentAge || 56));
  const daysToRetire = formatRemaining(
    retirementTarget({ dob: assumptions?.dob, retireAge: params?.retireAge }) ||
      { date: new Date(retireYear, 5, 15) }
  )?.totalDays ?? 0;

  const [cards, setCards]               = useState(null);
  const [loadingAI, setLoadingAI]       = useState(false);
  const [aiError, setAiError]           = useState(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  // Re-open the recovery link later. It's shown once at purchase, but a
  // user who clicked past it then has no way back to the only thing that
  // protects their purchase — so it stays reachable from the panel that
  // shows the balance.
  const [showRecovery, setShowRecovery] = useState(false);
  const [showRestore, setShowRestore]   = useState(false);
  const [liveCards, setLiveCards]       = useState(null);
  const [loadingLive, setLoadingLive]   = useState(false);
  const [liveError, setLiveError]       = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [filterPriority, setFilter]     = useState("all");
  const creditBalance                    = useCreditBalance();

  const runAI = async (baseCards) => {
    const { runAIActionPlan, profileIsComplete } = await import("./ai/ai-analysis.js");
    console.log("[AI] runAI called. profileIsComplete:", profileIsComplete(params, mc));
    console.log("[AI] geminiApiKey present:", !!assumptions?.geminiApiKey, "model:", assumptions?.geminiModel || "default");
    console.log("[AI] baseCards count:", baseCards?.length, "ids:", baseCards?.map(c => c.id));
    if (!profileIsComplete(params, mc)) {
      console.warn("[AI] Profile incomplete — runAI exiting silently. Need: port>50K, sp>0, mcResults.rate>0, ≥1 funded account.");
      return;
    }
    // Pre-flight credit check. The server refuses below MIN_CREDITS_TO_RUN
    // and returns 402, which used to be the first time a user learned they
    // were short: the button looked available, the spinner ran, and the buy
    // modal appeared as the result of a failure. Checking here turns that
    // into an offer made before the attempt.
    //
    // Only when billing is actually in play — a user on their own Gemini
    // key never touches credits, so their balance is irrelevant to whether
    // they can run (ai-analysis.js routes on `getStoredJWT()`, not on the balance).
    if (BILLING_ENABLED && getStoredJWT() && creditBalance < MIN_CREDITS_TO_RUN) {
      console.warn(`[AI] pre-flight: ${creditBalance} credits < ${MIN_CREDITS_TO_RUN} minimum — prompting instead of failing.`);
      setAiError(`AI analysis needs at least ${MIN_CREDITS_TO_RUN.toLocaleString()} credits and you have ${creditBalance.toLocaleString()}. Top up to continue — nothing was charged.`);
      setShowBuyModal(true);
      return;
    }

    setLoadingAI(true);
    setAiError(null);
    try {
      console.log("[AI] Calling runAIActionPlan…");
      const merged = await runAIActionPlan({ ...params, geminiApiKey: assumptions?.geminiApiKey, geminiModel: assumptions?.geminiModel }, mc, baseCards);
      console.log("[AI] Merged result:", merged);
      console.log("[AI] Cards with aiNote:", merged?.filter(c => c.aiNote).length, "/ total:", merged?.length);
      console.log("[AI] AI-generated new cards:", merged?.filter(c => c.aiGenerated).length);
      setCards(merged);
    } catch (e) {
      console.error("[AI] action plan error:", e);
      if (BILLING_ENABLED && e.message?.toLowerCase().includes("credit")) {
        setShowBuyModal(true);
      } else {
        setAiError(e.message || "AI unavailable — check that your Gemini API_KEY is set in the UI in the Profile Section.");
      }
    } finally {
      setLoadingAI(false);
    }
  };

  const runLiveSearch = async () => {
    const { generateTimeSensitiveCards } = await import("./ai/ai-analysis.js");
    setLoadingLive(true);
    setLiveError(null);
    try {
      const found = await generateTimeSensitiveCards(
        { ...params, geminiApiKey: assumptions?.geminiApiKey, geminiModel: assumptions?.geminiModel },
        mc
      );
      setLiveCards(found);
    } catch (e) {
      setLiveError(e.message);
    } finally {
      setLoadingLive(false);
    }
  };

  if (!params || !mc) {
    return (
      <div className="chart-card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontSize: "var(--fs-body)", color: "var(--text-secondary)", marginBottom: 8 }}>Monte Carlo not run yet</div>
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>Press ▶ Run Monte Carlo to generate your personalized action plan.</div>
      </div>
    );
  }

  // Evaluate declarative rules engine
  const baseCards = evaluateRulesEngine({
    params, mc, assumptions,
    currentYear, retireYear, daysToRetire,
  });

  // Display AI-annotated cards if available, else base cards
  const displayCards = cards || baseCards;

  // Gate: AI button only enabled when profile has real data AND either credits (billing) or Gemini key (BYOK)
  const hasGeminiKey = !!(assumptions?.geminiApiKey?.trim());
  const profileReady = (params.port || 0) > 50_000 &&
    (params.sp  || 0) > 0 &&
    mc?.rate > 0 &&
    (params.accounts || []).some(a => (a.balance || 0) > 0);
  // Used to be `creditBalance >= 5` — a bare literal that enabled the
  // button at a balance the server rejects (its floor is MIN_CREDITS_GUARD
  // = 50). Anyone between 5 and 49 credits got an enabled button and a
  // guaranteed failure.
  const hasAiAccess  = BILLING_ENABLED ? (creditBalance >= MIN_CREDITS_TO_RUN || hasGeminiKey) : hasGeminiKey;
  const canRunAI     = !loadingAI && !cards && profileReady && hasAiAccess;
  const aiDisabledReason = !profileReady
    ? "Complete your profile and run Monte Carlo first"
    : !hasAiAccess
    ? "Buy AiRA credits or add a free Gemini API key in Plan inputs → Settings"
    : "Run AI analysis on your plan";

  const COLORS = {
    red:    { bg: "var(--tint-red-08)",   border: "var(--tint-red-25)",   label: "var(--chart-1)", badge: "Critical" },
    yellow: { bg: "rgba(244,211,94,0.08)",  border: "rgba(244,211,94,0.25)",  label: "var(--accent-gold)", badge: "Important" },
    green:  { bg: "rgba(79,216,143,0.08)",  border: "rgba(79,216,143,0.25)",  label: "var(--positive)", badge: "On Track" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

      {/* AI controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>

        {/* Left — action button + status */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <button
            onClick={() => runAI(baseCards)}
            disabled={!canRunAI}
            title={aiDisabledReason}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none", flexShrink: 0,
              background: canRunAI ? "linear-gradient(135deg, #7c3aed, #9a9cff)" : "var(--row-highlight)",
              color: canRunAI ? "white" : "var(--text-faint)",
              fontSize: "var(--fs-body)", fontWeight: 600,
              cursor: canRunAI ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: canRunAI ? "0 2px 8px rgba(124,58,237,0.3)" : "none",
              transition: "all 0.2s",
            }}
          >
            {loadingAI ? "Analyzing…" : cards ? "AI Applied" : "Run AI Analysis"}
          </button>

          {loadingAI && (
            <span style={{ color: "var(--accent-purple)", fontSize: "var(--fs-label)" }}>Aira is thinking…</span>
          )}
          {!loadingAI && !cards && profileReady && !hasAiAccess && !BILLING_ENABLED && (
            <span style={{ fontSize: "var(--fs-small)", color: "var(--accent-gold)" }}>
              🔒 Add a free Gemini key in <GoStep step={PROFILE_STEP_SETTINGS}>Plan inputs → Settings</GoStep> ·{" "}
              <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-gold)", textDecoration: "underline" }}>
                Get one here
              </a>
            </span>
          )}
          {(cards || aiError) && !loadingAI && (
            <button
              onClick={() => { setCards(null); setAiError(null); }}
              style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
            >
              Reset
            </button>
          )}
        </div>

        {/* Right — credit panel (BILLING_ENABLED), or Coming Soon stub */}
        {BILLING_ENABLED ? (
          <div style={{
            display:      "flex",
            alignItems:   "center",
            gap:          16,
            background:   creditBalance < LOW_BALANCE_WARN_AT ? "var(--tint-red-05)" : "rgba(124,58,237,0.06)",
            border:       `1px solid ${creditBalance < LOW_BALANCE_WARN_AT ? "var(--tint-red-25)" : "rgba(124,58,237,0.2)"}`,
            borderRadius: 10,
            padding:      "10px 16px",
            flexShrink:   0,
          }}>
            <div>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-purple)", marginBottom: 2 }}>
                AiRA Credits
              </div>
              {/* "You have 0 credits" and "this browser has no session" are
                  completely different situations, and showing a bare 0 for
                  both told paying customers they had nothing — right next
                  to a Buy Credits button, so the natural response was to
                  pay a second time. Credits live in the database against a
                  Stripe customer; the browser's only claim on them is a JWT
                  in localStorage, which doesn't travel between browsers,
                  devices, or origins, and isn't part of a profile export.
                  With no token we genuinely don't know the balance, so say
                  that instead of asserting zero. */}
              {!getStoredJWT() ? (
                <div style={{ maxWidth: 230 }}>
                  <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--accent-gold)", lineHeight: 1.25 }}>
                    No credits found on this device
                  </div>
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginTop: 3, lineHeight: 1.35 }}>
                    Already bought credits? They're safe — this browser just isn't linked
                    to your purchase.
                  </div>
                  {/* This state told the user to "use your restore link"
                      and then gave them nowhere to put one: the only button
                      on this panel was Buy Credits, so the path of least
                      resistance was paying twice for credits they already
                      owned. redeemRestoreToken existed but was reachable
                      only by loading a ?restore= URL — no use if the link
                      is in a password manager, an email, or on another screen. */}
                  <button
                    onClick={() => setShowRestore(true)}
                    style={{
                      marginTop: 6, background: "rgba(13,148,136,0.18)",
                      border: "1px solid var(--tint-sky-45)", color: "var(--accent-teal)",
                      borderRadius: 6, padding: "5px 12px", fontSize: "var(--fs-label)",
                      fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    Restore my credits
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: "var(--fs-h1)", fontWeight: 700, color: creditBalance < LOW_BALANCE_WARN_AT ? "var(--chart-1)" : "var(--text-primary)", lineHeight: 1 }}>
                  {creditBalance.toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowBuyModal(true)}
              style={{
                background:   "linear-gradient(135deg, #7c3aed, #9a9cff)",
                border:       "none", color: "white",
                borderRadius: 8, padding: "8px 16px",
                fontSize: "var(--fs-body)", fontWeight: 600, cursor: "pointer",
                boxShadow:    "0 2px 8px rgba(124,58,237,0.3)",
                whiteSpace:   "nowrap",
              }}
            >
              Buy Credits
            </button>
            {getStoredRecoveryLink() && (
              <button
                onClick={() => setShowRecovery(true)}
                title="The link that restores these credits in another browser or on a new computer"
                style={{
                  background: "none", border: "none", color: "var(--text-muted)",
                  fontSize: "var(--fs-small)", cursor: "pointer", textDecoration: "underline dotted",
                  textUnderlineOffset: 3, whiteSpace: "nowrap", padding: 0, marginLeft: 4,
                }}
              >
                recovery link
              </button>
            )}
          </div>
        ) : (
          <div
            title="The AiRA Credits billing system is built and audited but not yet live. While in development, all AI calls use your personal Gemini API key (BYOK)."
            style={{
              display:      "flex",
              alignItems:   "center",
              gap:          10,
              background:   "var(--tint-amber-07)",
              border:       "1px solid rgba(251,191,36,0.3)",
              borderRadius: 10,
              padding:      "10px 16px",
              flexShrink:   0,
              cursor:       "help",
            }}
          >
            <div style={{ fontSize: "var(--fs-h1)", lineHeight: 1 }}>🚧</div>
            <div>
              <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--accent-gold)", marginBottom: 2 }}>
                AiRA Credits
              </div>
              <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#fde68a", lineHeight: 1.15 }}>
                Coming Soon
              </div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginTop: 2 }}>
                BYOK active · in development
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Session token usage — shown for BYOK users after any AI call */}
      <AiUsageBadge style={{ marginBottom: 4 }} />

      {showBuyModal && <CreditPackModal onClose={() => setShowBuyModal(false)} />}
      {showRecovery && (() => {
        const rec = getStoredRecoveryLink();
        return rec ? (
          <RecoveryLinkModal url={rec.url} expiresAt={rec.expiresAt} onClose={() => setShowRecovery(false)} />
        ) : null;
      })()}
      {showRestore && <RestoreAccessModal onClose={() => setShowRestore(false)} />}

      {aiError && (
        <div style={{
          background: "var(--tint-red-08)",
          border: "1px solid var(--tint-red-25)",
          borderRadius: 8,
          padding: "10px 14px",
          fontSize: "var(--fs-label)",
          color: "var(--chart-1)",
        }}>
          AI unavailable: {aiError}
        </div>
      )}

      {/* Retirement Date Solver */}
      {(() => {
        const solver = solveRetirementDate(params);
        const { target, currentPort, currentAge, results } = solver;
        const retireAge = params.retireAge || 60;
        const rowColor = (age) => {
          if (age == null) return "var(--chart-1)";
          if (age <= retireAge) return "var(--positive)";
          if (age <= retireAge + 3) return "var(--accent-gold)";
          return "var(--chart-1)";
        };
        return (
          <div style={{ background: "rgba(99,102,241,0.06)", border: "1px solid var(--tint-indigo-20)", borderRadius: 9, padding: "12px 15px", marginBottom: 4 }}>
            <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--chart-4)", marginBottom: 8 }}>
              Retirement Date Solver — Target {fmtDollar(target)}
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 8 }}>
              Portfolio today: <span style={{ color: "var(--text-primary)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(currentPort)}</span>
              &nbsp;·&nbsp;Planned retirement: <span style={{ color: "var(--text-primary)" }}>age {retireAge}</span>
              {/* Household total — this drives a retirement-date answer, so
                  quoting the 401(k) line alone understated the saving that
                  gets the user there, and ignored a working spouse entirely. */}
              &nbsp;·&nbsp;Annual contrib: <span style={{ color: "var(--text-primary)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(householdAnnualContribution(params))}</span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-label)" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--tint-white-06)" }}>
                  <th style={{ textAlign: "left", color: "var(--text-faint)", fontWeight: 600, paddingBottom: 4 }}>Scenario</th>
                  <th style={{ textAlign: "center", color: "var(--text-faint)", fontWeight: 600, paddingBottom: 4 }}>Return</th>
                  <th style={{ textAlign: "right", color: "var(--text-faint)", fontWeight: 600, paddingBottom: 4 }}>Hits {fmtDollar(target)}</th>
                  <th style={{ textAlign: "right", color: "var(--text-faint)", fontWeight: 600, paddingBottom: 4 }}>vs. Plan</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const diff = r.crossoverAge != null ? r.crossoverAge - retireAge : null;
                  const color = rowColor(r.crossoverAge);
                  return (
                    <tr key={r.label} style={{ borderBottom: "1px solid var(--tint-white-04)" }}>
                      <td style={{ color: "var(--text-secondary)", padding: "4px 0" }}>{r.label}</td>
                      <td style={{ textAlign: "center", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono',monospace" }}>{(r.rate * 100).toFixed(1)}%</td>
                      <td style={{ textAlign: "right", color, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace" }}>
                        {r.crossoverAge != null ? `Age ${r.crossoverAge}` : "> 80"}
                      </td>
                      <td style={{ textAlign: "right", color, fontFamily: "'JetBrains Mono',monospace" }}>
                        {diff == null ? "—" : diff === 0 ? "On target" : diff < 0 ? `${Math.abs(diff)}yr early` : `${diff}yr late`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 8 }}>
              Update your portfolio balance in the profile to keep this projection current.
            </div>
          </div>
        );
      })()}

      {/* Summary bar + filter */}
      {(() => {
        const allCards     = [...displayCards, ...(liveCards || [])];
        const counts       = { all: allCards.length, red: 0, yellow: 0, green: 0 };
        allCards.forEach(c => { if (counts[c.priority] !== undefined) counts[c.priority]++; });
        const filtered     = filterPriority === "all" ? allCards : allCards.filter(c => c.priority === filterPriority);
        const canLive      = !loadingLive && (!!(assumptions?.geminiApiKey?.trim()) || BILLING_ENABLED);

        const FILTER_OPTS = [
          { key: "all",    label: `All`,       count: counts.all,    color: "var(--text-muted)",  active: "var(--text-primary)" },
          { key: "red",    label: `Critical`,  count: counts.red,    color: "var(--chart-1)",  active: "#fca5a5" },
          { key: "yellow", label: `Important`, count: counts.yellow, color: "var(--accent-gold)",  active: "#fde68a" },
          { key: "green",  label: `On Track`,  count: counts.green,  color: "var(--positive)",  active: "#6ee7b7" },
        ];

        return (
          <div>
            {/* Filter row */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              {FILTER_OPTS.map(f => (
                <button
                  key={f.key}
                  onClick={() => { setFilter(f.key); setSelectedCard(null); }}
                  style={{
                    background:   filterPriority === f.key ? "var(--card-border)" : "transparent",
                    border:       `1px solid ${filterPriority === f.key ? "rgba(255,255,255,0.2)" : "var(--tint-white-07)"}`,
                    color:        filterPriority === f.key ? (f.key === "all" ? "var(--text-primary)" : f.active) : f.color,
                    borderRadius: 6, padding: "4px 12px",
                    fontSize: "var(--fs-label)", fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {f.label} <span style={{ opacity: 0.7, fontSize: "var(--fs-small)" }}>{f.count}</span>
                </button>
              ))}
              <div style={{ flex: 1 }} />
              <button
                onClick={runLiveSearch}
                disabled={!canLive}
                title={canLive ? "Search the web for current IRS limits, SS COLA, Medicare premiums, and more" : "Add a Gemini API key to enable live search"}
                style={{
                  background:   !canLive ? "var(--card-bg)" : loadingLive ? "var(--row-highlight)" : "linear-gradient(135deg, #0e7490, var(--chart-5))",
                  border:       "none", color: !canLive || loadingLive ? "var(--text-faint)" : "white",
                  borderRadius: 7, padding: "5px 14px",
                  fontSize: "var(--fs-label)", fontWeight: 600, cursor: canLive && !loadingLive ? "pointer" : "not-allowed",
                  whiteSpace:   "nowrap",
                  boxShadow:    canLive && !loadingLive ? "0 2px 8px rgba(6,182,212,0.25)" : "none",
                }}
              >
                {loadingLive ? "Searching…" : liveCards ? "Refresh Live" : "Live Updates"}
              </button>
            </div>

            {liveError && <div style={{ fontSize: "var(--fs-small)", color: "var(--chart-1)", marginBottom: 8 }}>{liveError}</div>}

            {!liveError && liveCards && liveCards.length === 0 && (
              <div style={{ fontSize: "var(--fs-small)", color: "var(--positive)", marginBottom: 8 }}>
                Live search complete — no new time-sensitive updates found. Your plan figures look current.
              </div>
            )}
            {!liveError && liveCards && liveCards.length > 0 && (
              <div style={{ fontSize: "var(--fs-small)", color: "var(--chart-5)", marginBottom: 8 }}>
                Found {liveCards.length} live update{liveCards.length > 1 ? "s" : ""} — see below.
              </div>
            )}

            {/* Master-detail layout */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>

              {/* Left — list of rows */}
              <div style={{ flex: selectedCard ? "0 0 46%" : 1, minWidth: 0, transition: "flex 0.2s" }}>
                {filtered.length === 0 && (
                  <div style={{ fontSize: "var(--fs-label)", color: "var(--text-faint)", padding: "20px 0", textAlign: "center" }}>
                    No cards for this filter.
                  </div>
                )}

                {/* Regular cards */}
                {filtered.filter(c => !c.isLiveData).map((card, i) => (
                  <ActionPlanRow
                    key={card.id || i}
                    card={card}
                    isSelected={selectedCard?.id === card.id}
                    onClick={() => setSelectedCard(prev => prev?.id === card.id ? null : card)}
                  />
                ))}

                {/* Live cards — separated with a label */}
                {filtered.some(c => c.isLiveData) && (
                  <>
                    <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--chart-5)", margin: "12px 0 6px", display: "flex", alignItems: "center", gap: 6 }}>
                      Live Updates
                    </div>
                    {filtered.filter(c => c.isLiveData).map((card, i) => (
                      <ActionPlanRow
                        key={card.id || i}
                        card={card}
                        isSelected={selectedCard?.id === card.id}
                        onClick={() => setSelectedCard(prev => prev?.id === card.id ? null : card)}
                      />
                    ))}
                  </>
                )}

                {/* Milestones — inside the list column */}
                <div style={{ marginTop: 16 }}>
                  <MilestonesSection params={params} rmdAge={rmdAgeProp} />
                </div>
              </div>

              {/* Right — detail panel */}
              {selectedCard && (
                <div style={{ flex: "0 0 51%", minWidth: 0 }}>
                  <CardDetailPanel
                    card={selectedCard}
                    onClose={() => setSelectedCard(null)}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })()}

    </div>
  );
}

/** Plan inputs > Real Estate & Debt: the one editor for properties and the primary mortgage
 * (same sliders and month picker the Analysis tab had; MortgageTab is now a read-only summary). */
function RealEstateStep({ values, onChange }) {
  const properties = values.properties || [{ id: "p1", label: "Primary Residence", value: 0, mortgage: 0, income: 0 }];
  const updateProp = (id, field, val) => {
    onChange("properties", properties.map((p) => (p.id === id ? { ...p, [field]: val } : p)));
    // Keep primary mortgage in sync with mortgage calculator
    if (id === properties[0]?.id && field === "mortgage") onChange("mortBalance", val);
  };
  const addProperty = () => {
    if (properties.length >= 5) return;
    onChange("properties", [...properties, { id: "p" + Date.now(), label: `Property ${properties.length + 1}`, value: 0, mortgage: 0, income: 0 }]);
  };
  const removeProperty = (id) => {
    if (properties.length <= 1) return;
    onChange("properties", properties.filter((p) => p.id !== id));
  };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize: "var(--fs-h2)", fontWeight:600, color:"var(--text-primary)" }}>Real Estate &amp; Debt</div>
          <div style={{ fontSize: "var(--fs-small)", color:"var(--text-muted)" }}>Property values, mortgages, and rental income used by the plan.</div>
        </div>
        {properties.length < 5 && (
          <button onClick={addProperty}
            style={{ padding:"4px 12px", borderRadius:6,
              border:"1px dashed rgba(13,148,136,0.4)", background:"transparent",
              color:"var(--positive)", fontSize: "var(--fs-small)", cursor:"pointer", fontFamily:"inherit" }}>
            + Add property
          </button>
        )}
      </div>

      {properties.map((prop, idx) => {
        const equity  = (prop.value||0) - (prop.mortgage||0);
        const isFirst = idx === 0;
        return (
          <div key={prop.id} style={{
            background: isFirst ? "rgba(13,148,136,0.05)" : "var(--card-bg)",
            border:`1px solid ${isFirst ? "var(--tint-teal-600-25)" : "var(--card-border)"}`,
            borderRadius:10, padding:14,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <input type="text" value={prop.label}
                onChange={e => updateProp(prop.id, "label", e.target.value)}
                style={{ fontSize: "var(--fs-body)", fontWeight:600, color:"var(--text-primary)",
                  background:"transparent", border:"none", outline:"none",
                  borderBottom:"1px solid var(--tint-white-12)",
                  padding:"2px 0", width:180, fontFamily:"'DM Sans',sans-serif" }}/>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                {isFirst && (
                  <span style={{ fontSize: "var(--fs-small)", color:"var(--positive)",
                    background:"rgba(13,148,136,0.1)", border:"1px solid rgba(13,148,136,0.3)",
                    borderRadius:8, padding:"2px 7px" }}>
                    Primary · wired to mortgage calc
                  </span>
                )}
                {properties.length > 1 && (
                  <button onClick={() => removeProperty(prop.id)} aria-label={`Remove ${prop.label || "property"}`}
                    style={{ background:"transparent", border:"none", color:"var(--text-faint)",
                      cursor:"pointer", fontSize: "var(--fs-body)", padding:"2px 4px", transition:"color 0.15s" }}
                    onMouseEnter={e=>e.currentTarget.style.color="var(--chart-1)"}
                    onMouseLeave={e=>e.currentTarget.style.color="var(--text-faint)"}>
                    ×
                  </button>
                )}
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <div>
                <div style={{ fontSize: "var(--fs-small)", color:"var(--text-muted)", marginBottom:4, textAlign:"right" }}>Gross value</div>
                {/* `max` bounds the DRAG range only — DualInput's typed field
                    accepts values above it (see Slider.commitDraft). A 999B max
                    here made one pixel of travel worth ~$1.4B, so the slider
                    could not land on any real house price. */}
                <DualInput label="" value={prop.value||0} min={0} max={10_000_000} step={5_000}
                  format={v=>`$${Math.round(v).toLocaleString()}`} onChange={v=>updateProp(prop.id,"value",v)}/>
              </div>
              <div>
                <div style={{ fontSize: "var(--fs-small)", color:"var(--text-muted)", marginBottom:4, textAlign:"right" }}>Mortgage balance</div>
                <DualInput label="" value={prop.mortgage||0} min={0} max={10_000_000} step={1_000}
                  format={v=>`$${Math.round(v).toLocaleString()}`} onChange={v=>updateProp(prop.id,"mortgage",v)}/>
              </div>
              <div>
                <div style={{ fontSize: "var(--fs-small)", color:"var(--text-muted)", marginBottom:4, textAlign:"right" }}>Annual income (opt)</div>
                <DualInput label="" value={prop.income||0} min={0} max={200_000} step={1_000}
                  format={v=>`$${Math.round(v).toLocaleString()}/yr`} onChange={v=>updateProp(prop.id,"income",v)}/>
              </div>
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize: "var(--fs-small)", color:"var(--text-muted)" }}>Net equity:</span>
              <span style={{ fontSize: "var(--fs-body)", fontWeight:700,
                fontFamily:"'JetBrains Mono',monospace",
                color: equity >= 0 ? "var(--positive)" : "var(--chart-1)" }}>
                {equity < 0 ? "-" : ""}{fmtDollar(Math.abs(equity))}
              </span>
              {(prop.income||0) > 0 && (
                <span style={{ fontSize: "var(--fs-small)", color:"#059669" }}>
                  · {fmtDollar(prop.income)}/yr income
                </span>
              )}
            </div>
            {!isFirst && (
              <div data-testid={`${prop.id}-payment-note`} style={{ fontSize: "var(--fs-small)", color:"var(--text-muted)", marginTop:8, lineHeight:1.5 }}>
                This mortgage balance counts toward net worth only. AiRA charges a payment for the primary property's mortgage only, so include this loan's payment in your spending.
              </div>
            )}
          </div>
        );
      })}

      <div className="chart-card">
        <div className="ct">{properties[0]?.label || "Primary Residence"} · Mortgage</div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <DualInput label="Balance" value={values.mortBalance||0} min={0} max={1_500_000} step={1_000}
            format={v=>fmtDollar(v)}
            onChange={v=>{ onChange("mortBalance",v); updateProp(properties[0]?.id,"mortgage",v); }}/>
          <DualInput label="Rate %" value={values.mortRate??6.5} min={0} max={12} step={0.125}
            format={v=>v.toFixed(3)+"%"} onChange={v=>onChange("mortRate",v)}/>
          <DualInput label="Original term (yrs)" value={values.mortTerm||30} min={10} max={30} step={1}
            format={v=>v+" yrs"} onChange={v=>onChange("mortTerm",v)}/>
          <DualInput label="Extra/mo" value={values.mortExtra||0} min={0} max={5_000} step={50}
            format={v=>"$"+v.toLocaleString()+"/mo"} onChange={v=>onChange("mortExtra",v)}/>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ fontSize: "var(--fs-small)", color:"var(--text-secondary)", minWidth:70 }}>Start date</span>
            <MonthYearSelect value={values.mortStart || "2020-01"} onSet={v=>onChange("mortStart",v)}/>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ProfileWizard step index of "Current Savings" (accounts, balances, split). Keep in sync with STEPS below. */
export const PROFILE_STEP_SAVINGS = 1;
export const PROFILE_STEP_MONEY_IN = 2;
export const PROFILE_STEP_SPENDING = 3;
export const PROFILE_STEP_REAL_ESTATE = 4;
export const PROFILE_STEP_SETTINGS = 6;

export function ProfileWizard({ values, onChange, onNavigateTab, autosavedAt, jumpTo }) {
  const [step, setStep] = useState(jumpTo?.step ?? 0);
  // Deep links from the sidebar ("Edit accounts") land on the step that holds the field.
  // `jumpTo` is {step, n}; n changes on every click so the same step can be requested twice.
  useEffect(() => { if (jumpTo) setStep(jumpTo.step); }, [jumpTo]);
  const [saveStatus, setSaveStatus] = useState("");

  const flashStatus = (msg) => {
    setSaveStatus(msg);
    setTimeout(() => setSaveStatus(""), 2000);
  };

  const handleSave = () => {
    const ok = saveProfileToLocal(values);
    flashStatus(ok ? "Saved to this browser" : "Save failed (localStorage blocked)");
  };

  const handleReload = () => {
    const saved = loadProfileFromLocal();
    if (!saved) {
      flashStatus("No saved profile found");
      return;
    }
    if (!window.confirm("Restore your last saved profile? Unsaved changes will be overwritten.")) return;
    Object.entries(saved).forEach(([k, v]) => {
      if (k === "savedAt" || k === "buildTag") return;
      onChange(k, v);
    });
    flashStatus("Restored saved profile");
  };

  const savedMeta = (() => {
    const s = loadProfileFromLocal();
    if (!s || !s.savedAt) return null;
    try {
      return new Date(s.savedAt).toLocaleString();
    } catch {
      return s.savedAt;
    }
  })();

  /* Step order: personal identity first, then money in, money out, the
   * plan — and global configuration last. About You is the dependency
   * root: `values.dob` drives ageFromDob(), which several later subtitles
   * and the engine derive from, so asking for it first matches what the
   * data actually needs.
   *
   * Assumptions used to be step 1 while also holding five identity fields
   * (name, dob, state, employer start date, filing status). Those moved to
   * About You, so this panel is now purely engine configuration — returns,
   * inflation, tax toggles, API key. Think of it as the phone's Settings
   * app, not part of telling us about yourself.
   *
   * `isSettings` marks it as a global surface rather than a workflow step.
   * The sidebar reads it to drop the row out of the progress-dot language:
   * a settings panel is never "completed," so showing a filled step dot
   * for it would assert something false.
   */
  const STEPS = [
    { label: "About You", icon: "👤", sub: `You are ${ageFromDob(values.dob) ?? values.currentAge} yrs old` },
    { label: "Current Savings", icon: "💰", sub: `Net worth of ${fmtDollar(values.port)} saved. Congratulations!` },
    {
      label: "Money In", icon: "💵",
      // Money In holds both the working-years contributions and every
      // recurring retirement stream (SS, rental, pensions, other income).
      // The old subtitle read only the contribution figure, so a retired
      // user with a $50K pension saw "Contributing $0/yr while working" —
      // exactly backwards as a signal. Show both halves; fall back to a
      // static label only when the household has neither, so the string is
      // never "$0/yr saving · $0/yr in retirement" for a brand-new profile.
      sub: (() => {
        const saving = householdAnnualContribution(values);
        const retInc = totalRetirementIncome(values);
        if (saving === 0 && retInc === 0) return "Income & contributions";
        return `${fmtDollar(saving)}/yr saving · ${fmtDollar(retInc)}/yr in retirement`;
      })(),
    },
    {
      label: "Spending & Expenses", icon: "💸",
      sub: values.spImportMeta
        ? (values.spImportMeta.mode === "multi"
            ? `Multi-year budget · ${values.spImportMeta.years} yrs`
            : `Budget loaded · ${fmtDollar(values.spImportMeta.total)}/yr`)
        : `Spending ${fmtDollar((values.sp || 0) + (values.spOutOfCountry != null ? values.spOutOfCountry : (values.spSpendOutofState || 0)))}/yr`,
    },
    {
      label: "Real Estate & Debt", icon: "🏠",
      sub: (() => {
        const props = values.properties || [];
        const v = props.reduce((a, x) => a + (x.value || 0), 0);
        const m = props.reduce((a, x) => a + (x.mortgage || 0), 0);
        return v || m ? `${fmtDollar(v)} property · ${fmtDollar(m)} mortgage` : "Home value, mortgage, rental income";
      })(),
    },
    { label: "Retirement Plan", icon: "🎯", sub: `Projected Retirement Age ${values.retireAge}` },
    { label: "Settings", icon: "⚙️", sub: "Calculation & app settings", isSettings: true },
  ];

  const PANELS = [
    <AboutYouPanel values={values} onChange={onChange} />,
    <SavingsPanel values={values} onChange={onChange} />,
    <ContribPanel values={values} onChange={onChange} onNavigateStep={setStep} />,
    <ExpensesPanel values={values} onChange={onChange} />,
    <RealEstateStep values={values} onChange={onChange} />,
    <RetirementPanel values={values} onChange={onChange} onNavigateStep={setStep} onNavigateTab={onNavigateTab} />,
    <AssumptionsPanel values={values} onChange={onChange} />,
  ];

  useEffect(() => {
    if (step >= STEPS.length) setStep(STEPS.length - 1);
  }, [step]);

  const goNext = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  const currentStepData = STEPS[step];
  const currentPanel = PANELS[step];

  return (
    <div
      className="wizard-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "280px 1fr",
        background: "var(--tint-white-02)",
        border: "1px solid var(--tint-white-08)",
        borderRadius: 12,
        alignItems: "start",
        flexShrink: 0,
      }}
    >
      {/* LEFT SIDEBAR – unchanged */}
      <div className="wizard-sidebar" style={{ borderRight: "1px solid var(--tint-white-06)", padding: 16 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
          {/* Visual separation for the global-settings surface. Position
              alone didn't express it: everything in this list shares one
              progress-dot language, which implies a linear sequence you complete. */}
          {s.isSettings && (
            <div style={{
              marginTop: 14, marginBottom: 6, paddingTop: 10,
              borderTop: "1px solid var(--tint-white-08)",
              fontSize: "var(--fs-small)", fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-faint)",
            }}>
              App Settings
            </div>
          )}
          <div
            onClick={() => setStep(i)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              marginBottom: 4,
              cursor: "pointer",
              background: i === step ? "rgba(13,148,136,0.15)" : "transparent",
              border: i === step ? "1px solid rgba(13,148,136,0.3)" : "1px solid transparent",
            }}
          >
            {/* A settings panel is never "done," so it gets a static
                marker instead of a progress dot that fills in as you pass it. */}
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: s.isSettings ? 3 : "50%",
                flexShrink: 0,
                background: s.isSettings
                  ? (i === step ? "rgba(148,163,184,0.35)" : "var(--tint-white-07)")
                  : (i < step ? "var(--positive)" : i === step ? "var(--accent-teal)" : "var(--tint-white-10)"),
                border: s.isSettings
                  ? "2px solid rgba(148,163,184,0.4)"
                  : `2px solid ${i <= step ? "var(--positive)" : "var(--tint-white-15)"}`,
                boxShadow: (!s.isSettings && i === step) ? `0 0 8px ${withAlpha("#0d9488", "66")}` : "none",
              }}
            />
            <div>
              <div style={{ fontSize: "var(--fs-h2)", fontWeight: 600, color: i === step ? "var(--text-primary)" : "var(--text-muted)" }}>
                {s.icon} {s.label}
              </div>
              <div style={{ fontSize: "var(--fs-body)", color: "var(--text-muted)" }}>{s.sub}</div>
            </div>
          </div>
          </React.Fragment>
        ))}
      </div>

      {/* RIGHT PANEL */}
      <div className="wizard-panel" style={{ padding: 24 }}>
        {/* Save bar – unchanged */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            padding: "10px 14px",
            marginBottom: 16,
            background: "var(--tint-teal-600-08)",
            border: "1px solid var(--tint-teal-600-25)",
            borderRadius: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: "var(--fs-label)", fontWeight: 600, color: "var(--accent-teal)", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--positive)", display: "inline-block", boxShadow: "0 0 6px var(--positive)" }} />
              Auto-save on
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
              {autosavedAt
                ? `Auto-saved ${autosavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })} — your work is kept in this browser`
                : savedMeta
                  ? `Last saved to this browser: ${savedMeta}`
                  : "Changes save automatically as you edit"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {saveStatus && <span style={{ fontSize: "var(--fs-small)", color: "var(--accent-teal)" }}>{saveStatus}</span>}
            <button
              onClick={handleReload}
              disabled={!savedMeta}
              style={{
                padding: "6px 12px",
                borderRadius: 7,
                border: "1px solid var(--tint-white-15)",
                background: "transparent",
                color: savedMeta ? "var(--text-secondary)" : "#334155",
                cursor: savedMeta ? "pointer" : "not-allowed",
                fontSize: "var(--fs-small)",
                fontFamily: "inherit",
              }}
            >
              Reload Saved
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: "6px 14px",
                borderRadius: 7,
                border: "none",
                background: "linear-gradient(135deg,#0d9488,var(--positive))",
                color: "white",
                cursor: "pointer",
                fontSize: "var(--fs-small)",
                fontFamily: "inherit",
                fontWeight: 600,
              }}
            >
              Save
            </button>
          </div>
        </div>

        {/* Mobile step selector – unchanged */}
        <div className="wizard-mobile-steps" style={{ marginBottom: 16 }}>
          <select
            value={step}
            onChange={(e) => setStep(Number(e.target.value))}
            style={{
              width: "100%",
              background: "#0d1b2a",
              border: "1px solid #1e3a5f",
              color: "var(--text-primary)",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: "var(--fs-body)",
              fontFamily: "var(--font-sans)",
            }}
          >
            {STEPS.map((s, i) => (
              <option key={i} value={i}>
                {s.icon} {s.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>
          {currentStepData.icon} {currentStepData.label}
        </div>
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-faint)", marginBottom: 20 }}>
          {currentStepData.sub}
        </div>

        {/* Panel content */}
        {currentPanel}

        {/* Navigation – unchanged */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid var(--tint-white-06)",
          }}
        >
          <button
            onClick={goPrev}
            disabled={step === 0}
            style={{
              padding: "7px 18px",
              borderRadius: 7,
              border: "none",
              background: step === 0 ? "var(--row-highlight)" : "linear-gradient(135deg,#0d9488,var(--positive))",
              color: step === 0 ? "#334155" : "white",
              cursor: step === 0 ? "not-allowed" : "pointer",
              fontSize: "var(--fs-label)",
              fontFamily: "inherit",
              fontWeight: 600,
              opacity: step === 0 ? 0.4 : 1,
            }}
          >
            ← Previous
          </button>

          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{step + 1} / {STEPS.length}</div>

          <button
            onClick={goNext}
            disabled={step === STEPS.length - 1}
            style={{
              padding: "7px 18px",
              borderRadius: 7,
              border: "none",
              background: step === STEPS.length - 1 ? "var(--row-highlight)" : "linear-gradient(135deg,#0d9488,var(--positive))",
              color: step === STEPS.length - 1 ? "#334155" : "white",
              cursor: step === STEPS.length - 1 ? "not-allowed" : "pointer",
              fontSize: "var(--fs-label)",
              fontFamily: "inherit",
              fontWeight: 600,
              opacity: step === STEPS.length - 1 ? 0.4 : 1,
            }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

/* Stable module-level FieldRow for all ProfileWizard panels.
   Defined outside panel components so the reference never changes between
   renders — prevents React from unmounting/remounting inputs on each keystroke.
*/
// Facts vs levers: Plan inputs records what is, the sidebar asks what if.
// Retire age, plan-to age, US spend and SS claim age are levers — the sidebar
// sliders are their ONE editor (Single Point of Control), so here they are
// read-outs with a way to reach the slider. Outside the app shell (isolated
// renders) there is no sidebar, so only the value shows.
const LeverContext = createContext(null);
function LeverValue({ lever, children }) {
  const focusLever = useContext(LeverContext);
  return (
    <div>
      <div style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>{children}</div>
      {focusLever && <button type="button" className="sb-edit" style={{ fontSize: "var(--fs-small)" }} onClick={() => focusLever(lever)}>Adjust in sidebar</button>}
    </div>
  );
}

function WFieldRow({ label, helper, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "var(--fs-body)", fontWeight: 500, color: "var(--text-primary)", marginBottom: 2 }}>{label}</div>
        {helper && <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.4 }}>{helper}</div>}
      </div>
      <div style={{ marginLeft: 16, minWidth: 130, textAlign: "right" }}>
        {children}
      </div>
    </div>
  );
}

// Quicken-style split editor: one account, several bucket allocations by %.
// The account keeps its single rolled-up balance; this just edits how that
// balance is distributed across B1/B2/B3. Hoisted to module scope so it isn't
// recreated each render (which would drop input focus while typing).
function AccountSplitEditor({ acct, color, onChangeSplits, onClose }) {
  const splits = acct.splits || [];
  const bal = acct.balance || 0;
  const total = splits.reduce((s, x) => s + (Number(x.pct) || 0), 0);
  const remaining = 100 - total;
  const clampPct = (v) => Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
  const update = (i, patch) => onChangeSplits(splits.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div style={{ marginLeft: 106, marginBottom: 8, padding: "8px 10px", background: "var(--tint-white-03)", border: `1px solid ${withAlpha(color, "33")}`, borderRadius: 6 }}>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 6 }}>
        Split <strong style={{ color: "var(--text-primary)" }}>{acct.name}</strong> across buckets · rolls up to {fmtDollar(bal)}
      </div>
      {splits.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          {[1, 2, 3].map(b => {
            // Same cash-only constraint as the main [B1]/[B2]/[B3] chips —
            // a split slice of a non-cash account can't promise "spendable
            // without tax/penalty" any more than the whole account could.
            const disabled = b === 1 && acct.category !== "cash";
            return (
              <button key={b} onClick={() => !disabled && update(i, { bucket: b })}
                disabled={disabled}
                title={disabled ? "Bucket 1 (cash cushion) can only be assigned to Cash accounts" : `Bucket ${b}`}
                style={{
                  background: s.bucket === b ? withAlpha(color, "33") : "transparent",
                  border: `1px solid ${s.bucket === b ? color : "var(--tint-white-10)"}`,
                  color: disabled ? "#1e293b" : s.bucket === b ? color : "#334155",
                  borderRadius: 4, padding: "2px 6px", fontSize: "var(--fs-small)", fontWeight: 700,
                  cursor: disabled ? "not-allowed" : "pointer", lineHeight: 1.4,
                  opacity: disabled ? 0.5 : 1,
                }}>B{b}</button>
            );
          })}
          <input
            type="number" min={0} max={100} value={s.pct}
            onChange={e => update(i, { pct: clampPct(e.target.value) })}
            style={{ width: 52, fontSize: "var(--fs-small)", color: "var(--text-primary)", background: "transparent", border: "1px solid var(--tint-white-10)", borderRadius: 4, padding: "2px 6px", textAlign: "right", outline: "none", fontFamily: "'JetBrains Mono',monospace" }}
                onFocus={selectAllOnFocus}
              />
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>%</span>
          <span style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono',monospace", minWidth: 78, textAlign: "right" }}>{fmtDollar(bal * (Number(s.pct) || 0) / 100)}</span>
          <button onClick={() => onChangeSplits(splits.filter((_, j) => j !== i))} title="Remove this slice"
            style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "var(--fs-body)", opacity: 0.6 }}>×</button>
        </div>
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
        <button onClick={() => onChangeSplits([...splits, { bucket: 2, pct: Math.max(0, remaining) }])}
          style={{ background: "transparent", border: `1px dashed ${withAlpha(color, "55")}`, borderRadius: 4, color, fontSize: "var(--fs-small)", padding: "2px 8px", cursor: "pointer" }}>+ Add slice</button>
        <span style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: total === 100 ? "var(--positive)" : "var(--accent-gold)" }}>
          {total === 100 ? "100% assigned" : `${total}% assigned · ${remaining > 0 ? remaining + "% left" : Math.abs(remaining) + "% over"}`}
        </span>
        <button onClick={() => { onChangeSplits([]); onClose(); }}
          style={{ marginLeft: "auto", background: "transparent", border: "none", color: "var(--text-muted)", fontSize: "var(--fs-small)", cursor: "pointer", textDecoration: "underline" }}>↩ use one bucket</button>
      </div>
    </div>
  );
}

function SavingsPanel({ values, onChange }) {
  const GOAL = values.earlyRetireTarget || 1_000_000;
  const accounts = values.accounts || BLANK_PROFILE.accounts;
  const [splitEditId, setSplitEditId] = useState(null);
  // Cost basis / yield are global (one blended assumption across the WHOLE
  // taxable category, not per-account) — design-authority (2026-09-10):
  // moved here from a disconnected Tax Settings card on a different Profile
  // sub-page, since this is the only place "taxable" is otherwise discussed.
  // Collapsed by default — set-and-forget values, not something to force on
  // every visit.
  const [showTaxAssumptions, setShowTaxAssumptions] = useState(false);

  const CATEGORIES = ACCOUNT_CATEGORIES; // hoisted to module scope — shared with the sidebar AllocationDonut

  const catSum = (cat) => accounts.filter(a => a.category === cat).reduce((s, a) => s + (a.balance || 0), 0);
  const autoTotal = accounts.reduce((s, a) => s + (a.balance || 0), 0);
  const percentToGoal = Math.min(100, (autoTotal / GOAL) * 100);
  const remaining = Math.max(0, GOAL - autoTotal);

  const updateAccounts = (newAccounts) => {
    onChange("accounts", newAccounts);
    const total = newAccounts.reduce((s, a) => s + (a.balance || 0), 0);
    onChange("port", total);
  };

  const handleBalance = (id, bal) => {
    const newAccounts = accounts.map(a => a.id === id ? { ...a, balance: bal } : a);
    updateAccounts(newAccounts);
  };

  const handleName = (id, name) => {
    const newAccounts = accounts.map(a => a.id === id ? { ...a, name } : a);
    onChange("accounts", newAccounts);
  };

  const addAccount = (cat) => {
    const def = CATEGORIES.find(c => c.key === cat);
    const newAccounts = [...accounts, { id: Date.now().toString(), category: cat, name: def ? def.defaultName : cat, balance: 0, bucket: _defaultBucket(cat) }];
    onChange("accounts", newAccounts);
  };

  const setBucket = (id, b) => onChange("accounts", accounts.map(a => a.id === id ? { ...a, bucket: b } : a));

  // Persist a split. Empty array reverts the account to a single bucket
  // (strips the `splits` key) so the data model stays clean.
  const setSplits = (id, newSplits) => onChange("accounts", accounts.map(a => {
    if (a.id !== id) return a;
    if (!newSplits || newSplits.length === 0) { const { splits, ...rest } = a; return rest; }
    return { ...a, splits: newSplits };
  }));

  const toggleSplit = (id) => {
    if (splitEditId === id) { setSplitEditId(null); return; }
    const acct = accounts.find(a => a.id === id);
    if (acct && !(Array.isArray(acct.splits) && acct.splits.length)) {
      setSplits(id, [{ bucket: acct.bucket ?? _defaultBucket(acct.category), pct: 100 }]);
    }
    setSplitEditId(id);
  };

  const removeAccount = (id) => {
    const newAccounts = accounts.filter(a => a.id !== id);
    updateAccounts(newAccounts);
  };



  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ACard title="Accounts" accent="var(--accent-teal)" desc="Every balance the plan draws from, grouped by tax treatment — the grouping is what decides the withdrawal order and the tax on each dollar.">
      <BucketLegend />
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.5, marginTop: -4 }}>
        B1 / B2 / B3 sets how soon each account's money is needed. Each account starts at its category's default; ↺ appears when you change it.
      </div>
      {CATEGORIES.map(cat => {
        const catAccounts = accounts.filter(a => a.category === cat.key);
        return (
          <div key={cat.key} style={{ background: "var(--tint-white-02)", borderRadius: 8, borderLeft: `3px solid ${cat.color}`, padding: "8px 12px" }}>
            <div style={{ fontSize: "var(--fs-small)", color: cat.color, fontWeight: 600, marginBottom: 8 }}>{cat.label}</div>
            {catAccounts.map(acct => {
              const hasSplits = Array.isArray(acct.splits) && acct.splits.length > 0;
              const editing = splitEditId === acct.id;
              return (
              <div key={acct.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: (hasSplits || editing) ? 2 : 6 }}>
                  <input
                    type="text"
                    value={acct.name}
                    onChange={e => handleName(acct.id, e.target.value)}
                    style={{ width: 100, fontSize: "var(--fs-small)", color: "var(--text-primary)", background: "transparent", border: "1px solid var(--tint-white-06)", borderRadius: 4, padding: "2px 6px", fontFamily: "'DM Sans',sans-serif", outline: "none" }}
                    onFocus={e => e.target.style.borderColor = withAlpha(cat.color, "66")}
                    onBlur={e => e.target.style.borderColor = "var(--tint-white-06)"}
                  />
                  <div style={{ flex: 1 }}>
                    <ANumInput value={acct.balance || 0} onSet={(v) => handleBalance(acct.id, v)} min={0} max={999_000_000_000} step={5000} />
                  </div>
                  {hasSplits ? (
                    <span title="Split across buckets — click 🔀 to edit" style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: cat.color, fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>
                      {acct.splits.map(s => `${s.pct}%·B${s.bucket}`).join("  ")}
                    </span>
                  ) : (
                    [1,2,3].map(b => {
                      const active = clampBucket(acct.bucket ?? _defaultBucket(acct.category), acct.category) === b;
                      // Bucket 1 is the cash cushion — spendable without a tax
                      // or penalty hit, which only a cash-category account can
                      // promise. Disabled (not just visually inert) on every
                      // other category so this can't be mis-tagged going
                      // forward; clampBucket (engine/buckets.js) is the
                      // backstop for data saved before this existed.
                      const disabled = b === 1 && cat.key !== "cash";
                      return (
                        <button key={b} onClick={() => !disabled && setBucket(acct.id, b)}
                          disabled={disabled}
                          title={disabled ? "Bucket 1 (cash cushion) can only be assigned to Cash accounts — it has to be spendable without triggering tax or an early-withdrawal penalty" : `Assign to Bucket ${b}`}
                          style={{
                            background: active ? withAlpha(cat.color, "33") : "transparent",
                            border: `1px solid ${active ? cat.color : "var(--tint-white-10)"}`,
                            color: disabled ? "var(--text-faint)" : active ? cat.color : "var(--text-muted)",
                            borderRadius: 4, padding: "2px 6px", fontSize: "var(--fs-small)", fontWeight: 700,
                            cursor: disabled ? "not-allowed" : "pointer", lineHeight: 1.4,
                            opacity: disabled ? 0.5 : 1,
                          }}>B{b}</button>
                      );
                    })
                  )}
                  {!hasSplits && clampBucket(acct.bucket ?? _defaultBucket(acct.category), acct.category) !== clampBucket(_defaultBucket(acct.category), acct.category) && (
                    <BucketResetButton account={acct} onReset={(id, b) => setBucket(id, b)} />
                  )}
                  <button onClick={() => toggleSplit(acct.id)} title="Split this account across buckets" style={{
                    background: (hasSplits || editing) ? withAlpha(cat.color, "33") : withAlpha(cat.color, "14"),
                    border: `1px solid ${(hasSplits || editing) ? cat.color : withAlpha(cat.color, "55")}`,
                    borderRadius: 4, padding: "2px 7px", fontSize: "var(--fs-body)", cursor: "pointer", lineHeight: 1.4,
                    filter: (hasSplits || editing) ? "none" : "opacity(0.85)",
                  }}
                    onMouseEnter={e => { if (!(hasSplits || editing)) { e.currentTarget.style.background = withAlpha(cat.color, "33"); e.currentTarget.style.filter = "none"; } }}
                    onMouseLeave={e => { if (!(hasSplits || editing)) { e.currentTarget.style.background = withAlpha(cat.color, "14"); e.currentTarget.style.filter = "opacity(0.85)"; } }}
                  >🔀</button>
                  <button
                    onClick={() => removeAccount(acct.id)}
                    style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "var(--fs-body)", padding: "2px 4px", opacity: 0.5 }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = "var(--chart-1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.color = "var(--text-muted)"; }}
                  >
                    ×
                  </button>
                </div>
                {editing && (
                  <AccountSplitEditor
                    acct={acct}
                    color={cat.color}
                    onChangeSplits={(ns) => setSplits(acct.id, ns)}
                    onClose={() => setSplitEditId(null)}
                  />
                )}
              </div>
              );
            })}
            <button
              onClick={() => addAccount(cat.key)}
              style={{ background: "transparent", border: `1px dashed ${withAlpha(cat.color, "33")}`, borderRadius: 4, color: cat.color, fontSize: "var(--fs-small)", padding: "2px 8px", cursor: "pointer", opacity: 0.6, marginTop: 2 }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
            >
              + Add
            </button>
            {cat.key === "taxable" && catAccounts.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--tint-white-06)" }}>
                <button
                  onClick={() => setShowTaxAssumptions(v => !v)}
                  style={{ background: "transparent", border: "none", color: cat.color, fontSize: "var(--fs-small)", fontWeight: 600, cursor: "pointer", padding: 0 }}
                >
                  {showTaxAssumptions ? "▾" : "▸"} ⚙ Tax assumptions
                </button>
                {showTaxAssumptions && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 6 }}>
                      Applies to your combined Taxable balance ({fmtDollar(catSum("taxable"))}) — not any single account above.
                    </div>
                    <ARow label="Taxable cost basis" desc="Percent of your taxable brokerage balance that is cost basis (from your brokerage statement). The rest is unrealized gain — selling realizes it as LTCG income, taxed at 0/15/20% federal (plus state, plus NIIT above the MAGI threshold) and counted toward Social Security's provisional income and Medicare IRMAA.">
                      <ANumInput value={values.taxableBasisPct ?? 70} onSet={(v) => onChange("taxableBasisPct", v)} min={0} max={100} step={5} suffix="%" />
                    </ARow>
                    {/* Real securities pay interest/dividends every year, taxed whether or
                        not you reinvest them — the rest of a taxable account's growth stays
                        deferred (untaxed until sold, above). Different holdings split this
                        very differently (a muni bond fund pays none of this; a taxable bond
                        fund pays mostly this); rather than model every security, this is
                        one blended annual rate applied to your whole taxable balance.
                        Off (0%) by default — turning it on will raise your projected tax
                        and lower projected ending balances slightly, even without changing
                        any other input. */}
                    <ARow label="Taxable annual yield" desc="Percent of your taxable brokerage balance assumed to be interest/dividend yield, taxed every year even if reinvested — the rest of its growth stays deferred until you sell, same as above. 0% (off) leaves your plan unchanged; a diversified stock+bond account is often 1.5–3%.">
                      <ANumInput value={values.taxableYieldPct ?? 0} onSet={(v) => onChange("taxableYieldPct", v)} min={0} max={10} step={0.5} suffix="%" />
                    </ARow>
                    {(values.taxableYieldPct ?? 0) > 0 && (
                      <ARow label="— % of that yield taxed as ordinary income" desc="The rest of the yield above is taxed at qualified-dividend/LTCG rates instead. Interest (bonds, cash) is ordinary; qualified stock dividends get the lower rate — this is one blended split across your whole taxable balance, not per security.">
                        <ANumInput value={values.taxableYieldOrdinaryPct ?? 50} onSet={(v) => onChange("taxableYieldOrdinaryPct", v)} min={0} max={100} step={5} suffix="%" />
                      </ARow>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      </ACard>

      <ACard title="Early-Retirement Target" accent="var(--positive)" desc="Your goal number, and how the accounts above track against it.">
        <WFieldRow label="Target Portfolio for Early Retirement" helper={`Goal: ${fmtDollar(GOAL)}`}>
          <ANumInput value={values.earlyRetireTarget || 0} onSet={(v) => onChange("earlyRetireTarget", v)} min={0} max={MAX_MONEY_INPUT} step={50_000} />
        </WFieldRow>
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: "var(--fs-label)", color: "var(--text-primary)" }}>Progress</span>
            <span style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace" }}>{percentToGoal.toFixed(1)}%</span>
          </div>
          <div style={{ height: 10, background: "var(--tint-white-10)", borderRadius: 5, overflow: "hidden" }}>
            <div style={{ width: `${percentToGoal}%`, height: "100%", background: "linear-gradient(90deg,#0d9488,var(--positive))", borderRadius: 5 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 6, marginTop: 12 }}>
            {[
              { label: "Pre-Tax", val: catSum("pretax"),  color: "#0ea5e9" },
              { label: "Roth",    val: catSum("roth"),     color: "var(--accent-purple)" },
              { label: "Taxable", val: catSum("taxable"),  color: "var(--accent-gold)" },
              { label: "HSA",     val: catSum("hsa"),      color: "var(--positive)" },
              { label: "Cash",    val: catSum("cash"),     color: "var(--text-secondary)" },
            ].map(s => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{s.label}</div>
                <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: s.color, fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(s.val)}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
            <span>Total: <strong style={{ color: "var(--text-primary)" }}>{fmtDollar(autoTotal)}</strong></span>
            <span>Remaining: <strong style={{ color: "var(--chart-1)" }}>{fmtDollar(remaining)}</strong></span>
          </div>
        </div>
      </ACard>
    </div>
  );
}

function AboutYouPanel({ values, onChange }) {
  const derivedAge = ageFromDob(values.dob);
  const currentAgeForCalc = derivedAge ?? values.currentAge;
  // Retirement age is a decimal (63.6 for a mid-year D-Day), so subtracting
  // a whole age produced 1.6000000000000014 on screen — binary floating
  // point, shown raw. Rounded to one decimal at the point of display; the
  // engines keep the full precision value, so this changes nothing a
  // projection depends on.
  const round1 = (n) => Math.round(n * 10) / 10;
  const yearsToRetire = round1(Math.max(0, values.retireAge - currentAgeForCalc));
  const yearsInRetire = round1(Math.max(0, values.endAge - values.retireAge));
  const totalHorizon = round1(yearsToRetire + yearsInRetire);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* These five used to be in "Assumptions → Model Parameters," which
          is where a user looking for their own details would never think
          to look — and About You carried a pointer sending them there.
          Name, birthday, home state, employer start date and filing status
          are who you are, not model parameters; the returns, inflation and
          tax assumptions that belong under that heading stayed behind. One
          category, one place. */}
      <ACard title="Who You Are" accent="#0ea5e9" desc="Who you are. Your birthday is the input of record — age, D-Day and every accumulation year derive from it.">
        <ARow label="Name" desc="Appears on the printable report and in the exported JSON filename (AiRA_Profile_&lt;name&gt;_YYYY-MM-DD.json). Not used in any calculation.">
          <input
            type="text"
            value={values.name || ""}
            placeholder="Full Name"
            onChange={(e) => onChange("name", e.target.value)}
            style={{ background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace" }}
          />
        </ARow>
        <ARow
          label="Date of Birth"
          desc={`Current age: ${derivedAge} · Used to derive D-Day (Day of Retirement) and accumulation years`}
        >
          <ADateInput value={values.dob} max={new Date().toISOString().slice(0, 10)} onSet={(v) => onChange("dob", v)} />
        </ARow>
        <ARow
          label="State of Residence at Retirement"
          desc="State where RMD taxes will be applied. Use the Two Household toggle for out-of-country scenarios."
        >
          <AStateSelect value={values.stateOfResidence} onSet={(v) => onChange("stateOfResidence", v)} />
        </ARow>
        <ARow label="Employer Start Date (Countdown to D-Day)" desc="Used for D-Day (Day of Retirement) progress bar (when you started your last job) and counting days until D-Day">
          <ADateInput value={values.employerStartDate} onSet={(v) => onChange("employerStartDate", v)} />
        </ARow>
        <ARow label="Federal Filing Status" desc="Your marital status for federal taxes only — unrelated to the Solo Mode state-tax toggle. MFJ (married): $32,200 std deduction, wider brackets. Single (unmarried): $16,100 deduction, narrower brackets.">
          <select
            value={values.filingStatus || "mfj"}
            onChange={(e) => onChange("filingStatus", e.target.value)}
            style={{ background: "#0a1628", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", cursor: "pointer" }}
          >
            <option value="mfj">Married Filing Jointly (MFJ)</option>
            <option value="single">Single (unmarried)</option>
          </select>
        </ARow>
        {/* The one spouse switch.
            `spouse.enabled` used to live inside the Social Security card,
            labeled "Add my spouse's Social Security," because SS was the
            only thing it gated. It now also gates per-person
            contributions — a step earlier in the wizard — so a user had to
            jump forward to a card about Social Security, tick a box that
            never mentions savings, and come back.

            It belongs here: it's a statement about who the household is,
            next to filing status, and ahead of both features that read it.
            The Social Security and Contributions cards now point at this
            control rather than duplicating it — one flag, one switch. */}
        <ARow label="Include a spouse or partner" desc="Model two people instead of one. Turns on your spouse's own Social Security (their benefit, their claim age, survivor benefits) and lets their retirement contributions stop on their own retirement date. Off leaves your plan exactly as it is today.">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}>
            <input
              type="checkbox"
              checked={!!values.spouse?.enabled}
              onChange={(e) =>
                onChange("spouse", { ...(values.spouse || { ssb: 0, ssAge: 67, ssPia: 0 }), enabled: e.target.checked })
              }
              style={{ width: 15, height: 15, accentColor: "var(--accent-teal)", cursor: "pointer" }}
            />
            {values.spouse?.enabled ? "Included" : "Not included"}
          </label>
        </ARow>
      </ACard>
      <ACard title="Retirement Timeline" accent="var(--accent-teal)" desc="When you stop working and how long the plan must last.">
        <WFieldRow label="Retirement Age" helper="Age at which you plan to retire (D‑Day).">
          <LeverValue lever="retireAge">Age {values.retireAge}</LeverValue>
        </WFieldRow>
        <WFieldRow label="Planning Horizon" helper="Age through which you want the plan to last.">
          <LeverValue lever="endAge">Age {values.endAge}</LeverValue>
        </WFieldRow>
        <WFieldRow label="Sex" helper="Used for SSA mortality overlay on the fan chart (male/female life expectancy tables, or blended average).">
          <select
            value={values.sex || "blended"}
            onChange={(e) => onChange("sex", e.target.value)}
            style={{ background: "#0a1628", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", cursor: "pointer" }}
          >
            <option value="blended">Blended (avg)</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </WFieldRow>
      </ACard>

      <ACard title="Plan Horizon At A Glance" accent="var(--accent-teal)" desc="Derived from your birthday and the two ages above — nothing to edit here.">
        {[
          { label: "Years to retirement", val: yearsToRetire, color: "var(--accent-teal)" },
          { label: "Years in retirement", val: yearsInRetire, color: "var(--accent-purple)" },
          { label: "Total horizon", val: `${totalHorizon} yrs`, color: "var(--text-primary)" },
        ].map((m) => (
          <div key={m.label}>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: "var(--fs-h1)", fontWeight: 700, color: m.color, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.2 }}>
              {m.val}
            </div>
          </div>
        ))}
      </ACard>
    </div>
  );
}

/* Stable module-level helpers for AssumptionsPanel.
   Defined outside the component so their reference never changes between
   renders — prevents React from unmounting/remounting inputs on each keystroke.
*/
function ARow({ label, desc, children }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--tint-white-06)" }}>
      <div>
        <div style={{ fontSize: "var(--fs-label)", color:"var(--text-primary)", fontWeight:500 }}>{label}</div>
        {desc && <div style={{ fontSize: "var(--fs-label)", color:"var(--text-secondary)", marginTop:2 }}>{desc}</div>}
      </div>
      <div style={{ marginLeft:16, flexShrink:0 }}>{children}</div>
    </div>
  );
}

/**
 * Select a numeric field's contents when it gains focus, so typing replaces
 * the value instead of appending to it.
 *
 * A user reported this: a field pre-filled with 0, clicked at the left
 * edge, then typing "40000" left the original zero on the end — the plan
 * silently used $400,000. Every Profile field ships with a default (often
 * 0), so this hit any value the caret happened to land in front of, and
 * produced a 10x error with no visible sign anything was wrong.
 *
 * Deferred a frame because focusing swaps the displayed text (formatted
 * "40,000" -> raw "40000"); selecting synchronously would be undone by that
 * re-render.
 */
function selectAllOnFocus(e) {
  const el = e.target;
  requestAnimationFrame(() => { try { el.select(); } catch { /* detached */ } });
}

function ANumInput({ value, onSet, min, max, step, suffix = "", plain = false }) {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState("");
  // A fractional step (0.1, 0.5) means this field takes decimals — hint the
  // decimal keypad on mobile (type=text already allows "." on desktop).
  const allowDecimals = step != null && !Number.isInteger(step);
  // Absent bounds are unbounded, not NaN. See handleBlur.
  const lo = min ?? -Infinity;
  const hi = max ?? Infinity;
  const inputRef = useRef(null);
  // Set by the first keystroke after focus. The auto-select is deferred to
  // the next frame (it has to run after the focus re-render swaps the
  // display from "6,126" to "6126," or the render wipes the selection) —
  // but a deferred select() that lands once typing has begun selects what
  // was just typed, and the following keystroke replaces the whole field.
  // That's how an edit ends up as a single digit. This flag makes the late
  // select a no-op.
  const typedSinceFocus = useRef(false);

  // Sync local value when prop changes (e.g., after import or external update)
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value != null && !isNaN(value) ? value.toString() : "");
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    const raw = e.target.value;
    typedSinceFocus.current = true;
    setLocalValue(raw);            // show exactly what was typed; don't fight the caret
    // Clearing the field means zero — preserved from the original
    // `Number("")` behavior so wiping a value still sets it to 0 rather
    // than silently restoring the old one.
    const num = raw.trim() === "" ? 0 : parseNumericEntry(raw);
    if (num !== null) {
      // `min` isn't enforced here on purpose — en route to "50" you pass
      // "5," and snapping that up would fight the typist. `max` is,
      // because you never need to pass through a too-large number to reach
      // a valid one, and deferring it to blur loses the clamp whenever the
      // parent re-render remounts this input mid-edit: handleBlur never
      // fires and the out-of-range value stays committed (a 100%-max
      // equity field kept 100,000,000). Clamping here survives that, blur
      // still applies `min`.
      onSet(max != null ? Math.min(max, num) : num);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Clamp what the user actually typed, not the `value` prop.
    //
    // Two bugs lived in the old `Math.max(min, Math.min(max, value))`:
    //
    // 1. Absent bounds became NaN. `Math.max(undefined, 75)` is NaN, and
    //    that went straight back out through onSet — one blur on a field
    //    whose caller omitted `min` or `max` destroyed the value. Every
    //    current call site passes both, so this was latent, but it's one
    //    forgotten prop away from silently zeroing a balance. `lo`/`hi`
    //    remove the trap at the source.
    //
    // 2. It clamped the prop. The prop is one render behind whenever the
    //    parent hasn't yet applied the last keystroke, so blurring could
    //    clamp a stale number and write it back — the final digits of an
    //    edit vanishing on click-away. What the user typed is in
    //    `localValue`; that's the thing to commit.
    const typed  = localValue.trim() === "" ? 0 : parseNumericEntry(localValue);
    const fallbk = value != null && !isNaN(value) ? value : null;
    const base   = typed !== null ? typed : fallbk;
    if (base === null) return;              // unparseable and no prop to fall back on
    const clamped = Math.max(lo, Math.min(hi, base));
    if (!Number.isFinite(clamped)) return;  // never commit NaN/±Infinity
    if (clamped !== value) onSet(clamped);
    setLocalValue(clamped.toString());
  };

  const displayValue = isFocused
    ? localValue
    : (value != null && !isNaN(value)
        ? (plain ? String(value) : new Intl.NumberFormat('en-US').format(value))
        : "");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input
        ref={inputRef}
        type="text"
        inputMode={allowDecimals ? "decimal" : "numeric"}
        value={displayValue}
        onChange={handleChange}
        onFocus={() => {
          setIsFocused(true);
          typedSinceFocus.current = false;
          // Not the shared `selectAllOnFocus` on purpose: this field needs
          // the "has the user started typing?" guard, and the shared
          // helper has no way to know. Still deferred a frame so it runs
          // after the display swaps from formatted to raw.
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (!el || typedSinceFocus.current) return;
            if (typeof document !== "undefined" && document.activeElement !== el) return;
            try { el.select(); } catch { /* detached */ }
          });
        }}
        onBlur={handleBlur}
        style={{
          width: "120px",
          maxWidth: "100%",
          background: "#0d1b2a",
          border: "1px solid #1e3a5f",
          color: "var(--text-primary)",
          borderRadius: 6,
          padding: "4px 8px",
          fontSize: "var(--fs-label)",
          fontFamily: "'JetBrains Mono',monospace",
          textAlign: "right",
        }}
      />
      {suffix && <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{suffix}</span>}
    </div>
  );
}

function AStateSelect({ value, onSet }) {
  return (
    <select
      value={value || "NJ"}
      onChange={(e) => onSet(e.target.value)}
      style={{ background:"#0d1b2a", border:"1px solid #1e3a5f", color:"var(--text-primary)", borderRadius:6, padding:"4px 8px", fontSize: "var(--fs-label)", fontFamily:"'JetBrains Mono',monospace" }}
    >
      {Object.entries(STATE_BRACKETS).map(([state, entry]) => {
        const br = entry?.mfj ?? entry?.single;
        const top = br ? br[br.length - 1].rate : 0;
        const label = top === 0 ? "no tax" : `${(top * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
        return <option key={state} value={state}>{state} ({label})</option>;
      })}
    </select>
  );
}

function ADateInput({ value, onSet, max }) {
  return (
    <DateField
      value={value}
      onSet={onSet}
      max={max}
      style={{ background:"#0d1b2a", border:"1px solid #1e3a5f", color:"var(--text-primary)", borderRadius:6, padding:"4px 8px", fontSize: "var(--fs-label)", fontFamily:"'JetBrains Mono',monospace" }}
    />
  );
}

/**
 * The section container for a Profile panel. One card = one topic.
 *
 * `collapsible` is decided by frequency, not length: set-once groups
 * (identity, API keys, tax mechanics) get `collapsible defaultOpen={false}`
 * so they don't occupy vertical space every visit; anything a user
 * revisits most sessions (spending, contributions, Social Security,
 * pensions) stays open. A long panel gets fixed by splitting it into more
 * cards, not by collapsing one giant one — those are different problems
 * and conflating them is what turned this into four competing patterns
 * before.
 *
 * Heading treatment: 14px near-white with a muted description. This
 * replaced an 11px uppercase accent-colored title on purpose.
 * `ContribPanel` had privately reimplemented this card — identical chrome
 * under local `sectionCard` / `sectionTitle` / `sectionDesc` objects — and
 * its heading was the more legible of the two. I liked the treatment on
 * one of those cards ("Pensions"), so the canonical component adopted the
 * better version instead of flattening the duplicate down to the weaker
 * one. `accent` now draws a thin left border, keeping the color coding
 * without tinting the title.
 *
 * Nesting rule: the neutral card chrome below is reserved for the outer
 * section wrapper. Anything inside it — disclosure strips, totals rows,
 * per-entry rows — should use a lighter hairline or a colored tint, never
 * a second instance of this same grey bordered box, or the panel becomes
 * boxes inside boxes and gets harder to scan. See specs/UI_DESIGN_SPEC.md.
 */
/**
 * Month + year selects for a "YYYY-MM" value.
 *
 * Replaces `<input type="month">`, whose year is a bare spinner: there's
 * no visible range, you can't see what's selectable, and reaching a start
 * date 20 years back means clicking an arrow 240 times. Reported directly:
 * "the year doesn't show a range of years in the mortgage drop down."
 *
 * The year range is derived, never a literal: `MORT_START_YEARS_BACK`
 * covers an existing mortgage already part-paid, and `_FORWARD` covers a
 * purchase you're still planning. Anchored to the current year so it can
 * never go stale.
 *
 * Emits the same "YYYY-MM" string the native control did, so
 * `mortgageSchedule` and every stored profile are unaffected.
 */
const MONTH_NAMES = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];
const MORT_START_YEARS_BACK = 40;   // a 30-yr mortgage taken out a decade before that
const MORT_START_YEARS_FWD  = 10;   // a purchase you are still planning

function MonthYearSelect({ value, onSet }) {
  const now = new Date();
  const m = /^(\d{4})-(\d{2})$/.exec(value || "");
  const year  = m ? Number(m[1]) : now.getFullYear();
  const month = m ? Number(m[2]) : now.getMonth() + 1;
  const first = now.getFullYear() - MORT_START_YEARS_BACK;
  const last  = now.getFullYear() + MORT_START_YEARS_FWD;
  // A stored value outside the range must still be selectable, or opening the
  // control would silently rewrite the user's date.
  const years = [];
  for (let y = Math.min(first, year); y <= Math.max(last, year); y++) years.push(y);
  const emit = (yy, mm) => onSet(`${yy}-${String(mm).padStart(2, "0")}`);
  const sel = {
    background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)",
    borderRadius: 4, padding: "4px 6px", fontSize: "var(--fs-small)",
    fontFamily: "'JetBrains Mono',monospace", cursor: "pointer",
  };
  return (
    <span style={{ display: "inline-flex", gap: 6 }}>
      <select style={sel} value={month} onChange={(e) => emit(year, Number(e.target.value))}>
        {MONTH_NAMES.map((nm, i) => <option key={nm} value={i + 1}>{nm}</option>)}
      </select>
      <select style={sel} value={year} onChange={(e) => emit(Number(e.target.value), month)}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </span>
  );
}

function ACard({ title, accent, desc, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;
  const heading = (
    <div style={{
      fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--text-primary)",
      marginBottom: desc || !isOpen ? 5 : 12,
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <span>{title}</span>
      {collapsible && <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>{isOpen ? "▾" : "▸"}</span>}
    </div>
  );

  return (
    <div style={{
      background: "var(--card-bg)",
      border: "1px solid var(--tint-white-08)",
      // `accent` is a thin left edge, not a tinted title — it keeps the
      // color coding while every card's heading stays the same legible near-white.
      borderLeft: accent ? `3px solid ${accent}` : "1px solid var(--tint-white-08)",
      borderRadius: 12, padding: 16,
    }}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{ background: "none", border: "none", padding: 0, margin: 0, width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}
        >
          {heading}
        </button>
      ) : heading}
      {desc && isOpen && (
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 14, lineHeight: 1.55 }}>{desc}</div>
      )}
      {isOpen && children}
    </div>
  );
}

function AssumptionsPanel({ values, onChange }) {
  // Live model list for the AI Model dropdown. Debounced because this reads a
  // key the user is still typing — firing per keystroke would send a stream of
  // half-keys to Google and rate-limit the one request that matters.
  const [liveModels, setLiveModels] = useState([]);
  const geminiKey = values.geminiApiKey || "";
  useEffect(() => {
    if (!geminiKey.trim()) { setLiveModels([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      import("./ai/ai-analysis.js")
        .then(({ fetchAvailableModels }) => fetchAvailableModels(geminiKey))
        .then((ids) => { if (alive) setLiveModels(ids); })
        .catch(() => { if (alive) setLiveModels([]); });
    }, 600);
    return () => { alive = false; clearTimeout(t); };
  }, [geminiKey]);
  // Live list wins; the shipped constant is the fallback before a key exists.
  // Labels come from the shipped list where we have one, so familiar models keep
  // their "(recommended)" / cost notes instead of becoming bare ids.
  const modelOptions = liveModels.length
    ? liveModels.map((id) => ({ id, label: GEMINI_MODELS.find(m => m.id === id)?.label || id }))
    : GEMINI_MODELS.map(({ id, label }) => ({ id, label }));

  const {
    dob,
    ssCola,
    preRetireEq,
    postRetireEq,
    hcShockAge,
    hcProb,
    hcMin,
    hcMax,
  } = values;

  // Shared helper — this used to divide elapsed ms by 365.25 days, so it
  // could report a different age than the About You panel for the same birthday.
  const derivedAge = ageFromDob(dob) ?? "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Tax settings.
          Cost basis / yield moved to the taxable account group in
          SavingsPanel (design-authority, 2026-09-10) — those are global
          assumptions about the taxable category, but they're meaningless
          without the account context that only lives there, and this page
          was a disconnected second location for the same state. Only the
          joint-RMD toggle stays: it keys off filing status, not accounts. */}
      <ACard title="Tax Settings" accent="var(--accent)"
        desc="How AiRA taxes your withdrawals. Defaults are fine for most people.">
        {(values.filingStatus || "mfj") !== "single" && (
          <Toggle
            val={values.useJointRmdTable}
            onChange={(v) => onChange("useJointRmdTable", v)}
            label="Use Joint & Last Survivor RMD Table (spouse >10 yrs younger)"
            accent="var(--accent-purple)"
          />
        )}
      </ACard>

      {/* EXPENSE MODEL CARD */}
      <ACard title="Housing & Fixed Obligations" accent="var(--accent-gold)" collapsible defaultOpen={false}>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 12 }}>
          Separate housing &amp; fixed obligations from core lifestyle spend. The MC engine adds each carveout to the portfolio draw automatically.
        </div>
        {/* Moved here from Personal Profile: an appreciation rate for
            real estate belongs with the housing inputs it applies to, not
            beside the user's date of birth. */}
        <ARow label="Home / RE Annual Growth" desc="Annual appreciation rate applied to real estate values in the Net Worth projection.">
          <ANumInput value={values.reGrowthRate} onSet={(v) => onChange("reGrowthRate", v)} min={0} max={10} step={0.5} suffix="%" />
        </ARow>
        <ARow label="Housing type" desc="Own = mortgage P&I drawn from portfolio until payoff. Rent = inflation-adjusted annual rent. None = housing already in core spend.">
          <select
            value={values.housingType || "own"}
            onChange={(e) => onChange("housingType", e.target.value)}
            style={{ background: "#0a1628", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", cursor: "pointer" }}
          >
            <option value="own">Own (mortgage)</option>
            <option value="rent">Rent</option>
            <option value="none">None / already in spend</option>
          </select>
        </ARow>
        {(values.housingType || "own") === "rent" && (
          <ARow label="Annual rent" desc="Today's dollars — inflated each year in simulation">
            {/* ✅ Fixed: Now correctly uses annualRent field */}
            <ANumInput value={values.annualRent} onSet={(v) => onChange("annualRent", v)} min={0} max={MAX_MONEY_INPUT} step={500} />
          </ARow>
        )}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", fontWeight: 600, marginBottom: 8 }}>
            Other Expenses or One Time Obligations (HOA fees, subscriptions, etc. )
          </div>
          {(values.carveouts || []).map((c, idx) => (
            <div key={c.id} style={{ display: "grid", gridTemplateColumns: "1fr 90px 80px 28px", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <input
                type="text"
                value={c.label}
                placeholder="Label"
                onChange={(e) => {
                  const updated = [...(values.carveouts || [])];
                  updated[idx] = { ...c, label: e.target.value };
                  onChange("carveouts", updated);
                }}
                style={{ background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace" }}
              />
              <input
                type="number"
                value={c.annual}
                min={0}
                step={100}
                placeholder="$/yr"
                onChange={(e) => {
                  const updated = [...(values.carveouts || [])];
                  updated[idx] = { ...c, annual: Number(e.target.value) };
                  onChange("carveouts", updated);
                }}
                style={{ background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
              <input
                type="number"
                value={c.endYear}
                min={2025}
                max={2080}
                step={1}
                placeholder="End yr"
                onChange={(e) => {
                  const updated = [...(values.carveouts || [])];
                  updated[idx] = { ...c, endYear: Number(e.target.value) };
                  onChange("carveouts", updated);
                }}
                style={{ background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
              <button
                onClick={() => onChange("carveouts", (values.carveouts || []).filter((_, i) => i !== idx))}
                style={{ background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, cursor: "pointer", fontSize: "var(--fs-body)", padding: "2px 6px" }}
              >×</button>
            </div>
          ))}
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 6 }}>Label · $/yr · End year (calendar year when obligation ends)</div>
          <button
            onClick={() => onChange("carveouts", [...(values.carveouts || []), { id: Date.now().toString(), label: "", annual: 0, endYear: new Date().getFullYear() + 5 }])}
            style={{ fontSize: "var(--fs-small)", background: "var(--tint-sky-500-10)", border: "1px solid var(--tint-sky-500-25)", color: "var(--accent)", borderRadius: 6, padding: "5px 12px", cursor: "pointer" }}
          >+ Add obligation</button>
        </div>
      </ACard>

      {/* ROTH CONVERSION CARD */}
      <ACard title="Roth Conversion Strategy" accent="var(--accent-purple)">
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 12 }}>
          After each year's spending withdrawal, AiRA converts additional pretax → Roth to fill up to your target bracket. Tax on conversion is funded from the pretax bucket.
        </div>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", lineHeight: 1.5, marginBottom: 12 }}>
          The bracket-fill target is set on <strong style={{ color: "var(--accent-purple)" }}>Scenarios → Conversion Plan</strong>,
          right above the ladder it shapes — it's saved here in your profile so the Withdrawal Plan
          and Monte Carlo runs use the same setting.
        </div>
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", marginBottom: 6, marginTop: 4 }}>
          FAFSA / CSS College-Aid Protection — enter a year to cap Roth conversions during your child's college aid window. Leave blank to disable.
        </div>
        <div style={{ display: "flex", gap: 16, marginLeft: 4 }}>
          <ARow label="FAFSA cap through year" desc="Cap conversions at 12% bracket through this year (FAFSA uses 2-yr prior income). Leave blank to skip.">
            <input
              type="number"
              value={values.fafsaEndYear || ""}
              onChange={(e) => onChange("fafsaEndYear", e.target.value ? parseInt(e.target.value) : null)}
              placeholder="e.g. 2031"
              min={2026} max={2060}
              style={{ width: 100, background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace" }}
                onFocus={selectAllOnFocus}
              />
          </ARow>
          <ARow label="CSS Profile cap through year" desc="Cap conversions at 22% bracket through this year (CSS Profile period). Leave blank to skip.">
            <input
              type="number"
              value={values.cssEndYear || ""}
              onChange={(e) => onChange("cssEndYear", e.target.value ? parseInt(e.target.value) : null)}
              placeholder="e.g. 2033"
              min={2026} max={2060}
              style={{ width: 100, background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace" }}
                onFocus={selectAllOnFocus}
              />
          </ARow>
        </div>
        {/* Paying conversion tax from money the plan never tracks is the
            single biggest way to flatter a Roth strategy — every dollar
            converted lands in the Roth untouched, funded from a pot the
            simulation never depletes. A user put it well: "unlimited
            outside funds should come with a warning: do you have a money
            tree?" It stays available because some people genuinely hold
            cash outside the modeled accounts, but it no longer passes as a
            neutral default. */}
        <ARow label="Tax funding source" desc="Who pays the tax on each conversion. 'From taxable' debits your real taxable / HSA / cash buckets — the honest default for most people. 'From the conversion' withholds the tax out of the amount transferred, so less lands in the Roth. 'Outside cash' is kept only so older saved profiles still load — it now behaves exactly like 'From taxable'.">
          <select
            value={values.taxFunding || "from_taxable"}
            onChange={(e) => onChange("taxFunding", e.target.value)}
            style={{ background: "#0a1628", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", cursor: "pointer" }}
          >
            <option value="from_taxable">From taxable / HSA / cash bucket (recommended)</option>
            <option value="from_conv">From the conversion (withhold)</option>
            <option value="outside_cash">Outside cash (legacy — same as From taxable)</option>
          </select>
        </ARow>
        {(values.taxFunding === "outside_cash") && (
          <div style={{
            marginTop: 8, padding: "10px 12px", borderRadius: 8,
            background: "var(--tint-orange-10)", border: "1px solid var(--tint-orange-35)",
            fontSize: "var(--fs-small)", color: "#fdba74", lineHeight: 1.55,
          }}>
            <strong>Legacy setting — now identical to 'From taxable'.</strong> This option used to
            pay conversion tax from an unlimited outside pot the simulation never tracked, which
            overstated the benefit. AiRA no longer models money you have not entered.
            <div style={{ marginTop: 6, color: "#fcd9b6" }}>
              Conversion tax is now drawn from your real balances — taxable first, then cash, then
              pre-tax only if those run out — so the plan can genuinely run short paying it. Switch to
              <strong> From taxable</strong> when convenient; the numbers will not change.
            </div>
          </div>
        )}

      </ACard>

      {/* Withdrawal order — sourcing controls moved to the Withdrawal Plan
          tab, next to the waterfall they shape. Profile keeps a read-only
          pointer for discoverability. */}
      <ACard title="Withdrawal Order" accent="var(--accent-teal)">
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", lineHeight: 1.5 }}>
          The <strong style={{ color: "var(--accent)" }}>account draw order</strong> (which bucket drains first —
          tax-reactive, custom, or pre-tax first) and the sourcing guardrails — pre-tax bracket ceiling,
          IRMAA guard, Roth reserve, and SS-torpedo warnings — are set on{" "}
          <strong style={{ color: "var(--accent-teal)" }}>Scenarios → Withdrawal Plan</strong>, right above the
          waterfall they shape. The distribution strategy stays here in Profile.
        </div>
      </ACard>

      {/* MONTE CARLO MODEL PARAMETERS CARD */}
      <div
        style={{
          background: "var(--card-bg)",
          border: "1px solid var(--tint-white-08)",
          borderRadius: 10,
          padding: 16,
        }}
      >
        <div
          style={{
            fontSize: "var(--fs-small)",
            fontWeight: 700,
            color: "var(--positive)",
            letterSpacing: "0.1em",
            marginBottom: 12,
          }}
        >
          Monte Carlo Model Parameters
        </div>
        <ARow label="Target Portfolio Value for Early Retirement" desc="This is your retirement goal. This is the number that answers, What number do I need to retire? What is my retirement $$$ where no matter what, I RETIRE!!.">
          <ANumInput value={values.earlyRetireTarget} onSet={(v) => onChange("earlyRetireTarget", v)} min={0} max={MAX_MONEY_INPUT} step={50000} />
        </ARow>
        <ARow label="Reassess Portfolio Target" desc="Portfolio value at which to start seriously planning exit. This number is a number where, if you hit this, would reconsider your target goal? This is a number that is a secondary decision. If you hit this, would you be ok with this goal if something caused a change in your plan,">
          <ANumInput value={values.portfolioGoal} onSet={(v) => onChange("portfolioGoal", v)} min={0} max={MAX_MONEY_INPUT} step={50000} />
        </ARow>
        {/* The model parameters that used to sit here — inflation, cash return,
            SS COLA, the two equity weights and the glidepath switch age — moved
            into the Monte Carlo tab's ⚙ Advanced Settings, alongside the
            simulation mechanics they act on. Two of them (the equity weights and
            SS COLA) were ALSO in the sidebar's Macro & Sensitivity card, so this
            card was a second edit surface for one value; the trim removes that.
            The two rows above stay: they are plan GOALS, not model parameters. */}
        <div style={{ marginTop: 12, padding: "10px 14px", background: "var(--tint-teal-600-06)", border: "1px solid rgba(13,148,136,0.2)", borderRadius: 8, fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.55 }}>
          Inflation, cash return, Social Security COLA and your equity glide are model parameters, not plan
          facts — they now live in <strong style={{ color: "var(--accent-teal)" }}>Monte Carlo → ⚙ Advanced Settings</strong>,
          with the rest of the simulation settings.
        </div>
      </div>

      {/* HEALTHCARE SHOCK CARD */}
      <ACard title="Healthcare Shock Model" accent="var(--chart-1)" collapsible defaultOpen={false}>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 12 }}>
          In each simulation year after the shock age, there is a random probability of a large one-time healthcare cost.
        </div>
        <ARow label="Shock start age" desc="Age after which annual healthcare shocks can occur (default 72)">
          <ANumInput value={values.hcShockAge} onSet={(v) => onChange("hcShockAge", v)} min={60} max={85} step={1} />
        </ARow>
        <ARow label="Annual shock probability" desc="Chance of a shock in any given year (default 3.5%)">
          <ANumInput value={values.hcProb} onSet={(v) => onChange("hcProb", v)} min={0} max={20} step={0.5} suffix="%" />
        </ARow>
        <ARow label="Shock cost — minimum" desc="Low end of randomized healthcare shock cost (default $70,000)">
          <ANumInput value={values.hcMin} onSet={(v) => onChange("hcMin", v)} min={0} max={MAX_MONEY_INPUT} step={5000} />
        </ARow>
        <ARow label="Shock cost — maximum" desc="High end of randomized healthcare shock cost (default $130,000)">
          <ANumInput value={values.hcMax} onSet={(v) => onChange("hcMax", v)} min={0} max={MAX_MONEY_INPUT} step={5000} />
        </ARow>
      </ACard>
      <div
        style={{
          fontSize: "var(--fs-small)",
          color: "var(--text-faint)",
          fontStyle: "italic",
          textAlign: "right",
        }}
      >
        Changes take effect on next Monte Carlo run · These replace all hardcoded simulation values
      </div>

      {/* AI Assistant. Its own card, per request. These are credentials/
          config for an optional feature — nothing to do with the
          retirement model — so mixing them in with identity fields made
          both harder to scan. Collapsed by default: set once, or never (AI
          also works on credits without a key). */}
      <ACard title="AI Assistant" accent="#c4b5fd" collapsible defaultOpen={false}
        desc="Optional. Bring your own free Gemini key to run AI analysis without spending AiRA credits.">
        <ARow label="Gemini API Key" desc="Bring your own free key from Google AI Studio to unlock AI analysis.">
          <input
            type="password"
            value={values.geminiApiKey || ''}
            onChange={(e) => onChange('geminiApiKey', e.target.value)}
            placeholder="AIza..."
            style={{ width: "260px", background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace" }}
          />
          <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" style={{ fontSize: "var(--fs-small)", color: "var(--chart-2)", marginLeft: 8 }}>Get free key →</a>
        </ARow>
        {/* The list is fetched from Google when a key is present, because a
            hardcoded one is guaranteed to rot: ids get retired per-audience,
            so "gemini-2.5-flash is no longer available to new users" keeps
            working for my own project and 404s for everyone else —
            invisible in testing, and the user's first sign of it is a raw
            Gemini 404 the moment they click Analyze. The shipped list is
            only the fallback for when there's no key yet or the probe fails. */}
        <ARow label="AI Model" desc="Fetched live from Google using your key, so a model Google retires disappears from this list instead of failing mid-analysis.">
          <select
            value={values.geminiModel || DEFAULT_GEMINI_MODEL}
            onChange={(e) => onChange('geminiModel', e.target.value)}
            style={{ width: "260px", background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", cursor: "pointer" }}
          >
            {modelOptions.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
            {/* A saved model that Google no longer lists must still
                render, or the select would silently show a different
                model than the one stored. */}
            {!modelOptions.some(m => m.id === (values.geminiModel || DEFAULT_GEMINI_MODEL)) && (
              <option value={values.geminiModel || DEFAULT_GEMINI_MODEL}>
                {values.geminiModel || DEFAULT_GEMINI_MODEL} (not offered by your key)
              </option>
            )}
          </select>
          <span style={{ fontSize: "var(--fs-small)", color: liveModels.length ? "var(--accent-teal)" : "var(--text-muted)", marginLeft: 8 }}>
            {liveModels.length
              ? `${liveModels.length} models available to your key`
              : (GEMINI_MODELS.find(m => m.id === (values.geminiModel || DEFAULT_GEMINI_MODEL))?.note
                 || "Enter your API key to load the live model list")}
          </span>
        </ARow>
      </ACard>
    </div>
  );
}

function ContribPanel({ values, onChange, onNavigateStep }) {
  const annual401k = values.contrib || 0;
  const hsaMonthly = values.hsaMonthly || 0;
  const employerContrib = values.employerContrib || 0;
  const rothContrib = values.rothContrib || 0;
  const taxableContrib = values.taxableContrib || 0;
  const hsaAnnual = hsaMonthly * 12;

  // Per-person contributions.
  // Only the job-bound streams are per person. Brokerage savings are
  // household money with no employment link, and the HSA stops at Medicare
  // enrollment rather than at retirement — splitting either would model
  // the wrong rule.
  const sp = values.spouse || {};
  const spouseOn = !!sp.enabled;
  const setSpouse = (patch) => onChange("spouse", { ...sp, ...patch });
  const sp401k    = sp.contrib || 0;
  const spEmp     = sp.employerContrib || 0;
  const spRoth    = sp.rothContrib || 0;
  const spouseTotal = spouseOn ? sp401k + spEmp + spRoth : 0;
  const totalSavings = annual401k + hsaAnnual + employerContrib + rothContrib + taxableContrib + spouseTotal;

  // Contributions are modeled only up to the primary's retirement date,
  // because the retirement loop has no concept of them past that. When the
  // spouse's own date lands later, the plan silently uses the primary's —
  // which is exactly what it did before this feature, but the user needs
  // to be told rather than left to assume the later date was honored.
  const spouseStopOnPrimaryClock = spouseOn ? contribStopOnPrimaryClock(values) : Infinity;
  const spouseWorksPastPrimary = Number.isFinite(spouseStopOnPrimaryClock)
    && spouseStopOnPrimaryClock > (values.retireAge || 0)
    && spouseTotal > 0;

  // Shared "profile section card" chrome — one consistent look so sections
  // read as a uniform, logically-ordered stack instead of scattered
  // mismatched blocks. Section chrome comes from <ACard> — this panel used
  // to define its own identical copy (sectionCard/sectionTitle/
  // sectionDesc), which is how the Profile ended up with four competing
  // section styles. One implementation now.

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Section headers split Money In by when each stream applies. A
          retired user skips "While Working" immediately; a still-working
          user knows the retirement half is a plan, not something to fill
          in today. Cards inside each group are the pre-existing ACards —
          this is a grouping label, not a nested chrome. */}
      <div style={{
        marginBottom: 2,
        fontSize: "var(--fs-small)", fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-faint)",
      }}>
        While Working
      </div>

      {/* ── Income card 1: Annual Contributions (inputs + savings total together) ── */}
      <ACard title="Annual Contributions" desc="Still working? Enter your annual retirement-account contributions — leave at 0 if you're already retired.">
        <div style={{ fontSize: "var(--fs-small)", color: "var(--accent-gold)", marginBottom: 10, lineHeight: 1.4 }}>Employer Contribution and Brokerage/After-Tax Savings aren't capped by the tool — the IRS doesn't set a hard per-field limit on either, but real-world amounts are still bounded by your actual plan and income. 401(k), HSA, and Roth IRA below stay capped at their real legal limits.</div>
        <WFieldRow label="401(k) / 403(b) / 457(b) — pre-tax only" helper="Your PRE-TAX employee deferral. Roth 401(k)/403(b) money does NOT belong here — this field is taxed as ordinary income on withdrawal and drives your future RMDs. Put Roth deferrals in the Roth line below until a dedicated Roth 401(k) field exists (REQUIREMENTS §22.1).">
          <ANumInput value={annual401k} onSet={(v) => onChange("contrib", v)} min={0} max={250_000} step={500} suffix="/yr" />
        </WFieldRow>
        <WFieldRow label="HSA Monthly Contribution" helper="Family limit $8,550 + $1,000 catch‑up (2026).">
          <ANumInput value={hsaMonthly} onSet={(v) => onChange("hsaMonthly", v)} min={0} max={2_000} step={50} suffix="/mo" />
        </WFieldRow>
        <WFieldRow label="Employer Contribution ($/yr)" helper="Fixed annual employer money in dollars — 401(k) match + profit sharing. Compounds in the Monte Carlo accumulation until your retirement age.">
          <ANumInput value={employerContrib} onSet={(v) => onChange("employerContrib", v)} min={0} max={999_000_000_000} step={500} suffix="/yr" />
        </WFieldRow>
        <WFieldRow label="Roth IRA Contribution" helper="Direct or backdoor Roth. Grows tax‑free and is drawn last, so it never triggers IRMAA or the SS torpedo.">
          <ANumInput value={rothContrib} onSet={(v) => onChange("rothContrib", v)} min={0} max={250_000} step={500} suffix="/yr" />
        </WFieldRow>
        <WFieldRow label="Brokerage / After‑Tax Savings" helper="Money you invest OUTSIDE a retirement account. Keep it here rather than adding it to your 401(k) — these dollars are withdrawn at long‑term capital-gains rates against their cost basis, and they don't raise your RMDs at 75.">
          <ANumInput value={taxableContrib} onSet={(v) => onChange("taxableContrib", v)} min={0} max={999_000_000_000} step={1_000} suffix="/yr" />
        </WFieldRow>

        {/* Pointer, not a toggle. The one spouse switch lives in About
            You: it used to be buried in the Social Security card, which
            meant discovering per-person contributions required visiting a
            later step and a card about a different subject. */}
        {!spouseOn && (
          <div style={{ padding: "10px 12px", marginTop: 4, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.28)", borderRadius: 8, fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span>Two of you saving? Enter your spouse's contributions separately so they stop on <strong style={{ color: "#c4b5fd" }}>their</strong> retirement date, not yours.</span>
            {/* Index 0 is AboutYouPanel in ProfileWizard's PANELS array. */}
            <button
              type="button"
              onClick={() => onNavigateStep && onNavigateStep(0)}
              style={{
                fontSize: "var(--fs-small)", color: "#c4b5fd", background: "none", border: "none",
                padding: 0, cursor: onNavigateStep ? "pointer" : "default",
                textDecoration: onNavigateStep ? "underline" : "none",
              }}
              disabled={!onNavigateStep}
            >
              Add a spouse in About You →
            </button>
          </div>
        )}

        {/* Spouse's job-bound contributions.
            Shown only when the spouse is enabled, so a single-person
            profile is visually unchanged. Splitting the amounts alone
            would change no number — all three engines sum into buckets —
            so the field that earns this section is "Their retirement
            age": it's the only input here that moves a dollar. */}
        {spouseOn && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--tint-white-08)" }}>
            <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "var(--accent-purple)", marginBottom: 4 }}>
              Your spouse
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 14, lineHeight: 1.5 }}>
              Only the contributions tied to <em>their</em> job go here — they stop when your spouse stops
              working, which may not be when you do. Brokerage savings and the HSA above stay
              household-wide: brokerage money isn't tied to a job, and HSA contributions stop at
              Medicare enrolment rather than at retirement.
            </div>
            {/* Plain input, not ANumInput: this field has to stay
                nullable, and ANumInput clamps a blank to `min` on blur —
                which would silently write a retirement age the user never
                entered. Same pattern the other nullable fields
                (fafsaEndYear/cssEndYear) already use. */}
            <WFieldRow label="Their retirement age" helper="Their OWN age when they stop working — not yours. This is the field that changes your projection: it decides how many more years their contributions keep landing. Leave blank to use your retirement age.">
              <input
                type="number"
                value={sp.retireAge ?? ""}
                onChange={(e) => setSpouse({ retireAge: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                placeholder="same as you"
                min={AGE_LIMITS.retire.min} max={AGE_LIMITS.retire.max}
                style={{ width: 120, background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
            </WFieldRow>
            <WFieldRow label="Their 401(k) / 403(b) / 457(b) — pre-tax only" helper="Their PRE-TAX employee deferral, entered separately from yours so it can stop on their date.">
              <ANumInput value={sp401k} onSet={(v) => setSpouse({ contrib: v })} min={0} max={250_000} step={500} suffix="/yr" />
            </WFieldRow>
            <WFieldRow label="Their employer contribution ($/yr)" helper="Their match + profit sharing. Follows the same job, so it stops on the same date.">
              <ANumInput value={spEmp} onSet={(v) => setSpouse({ employerContrib: v })} min={0} max={999_000_000_000} step={500} suffix="/yr" />
            </WFieldRow>
            <WFieldRow label="Their Roth IRA contribution" helper="Direct or backdoor. Separate from yours because the annual limit and the catch-up age are per person.">
              <ANumInput value={spRoth} onSet={(v) => setSpouse({ rothContrib: v })} min={0} max={250_000} step={500} suffix="/yr" />
            </WFieldRow>

            {spouseWorksPastPrimary && (
              <div style={{
                marginTop: 4, padding: "10px 12px", borderRadius: 8,
                background: "var(--tint-orange-10)", border: "1px solid var(--tint-orange-35)",
                fontSize: "var(--fs-small)", color: "#fdba74", lineHeight: 1.55,
              }}>
                <strong>Not yet modelled past your retirement.</strong> Your spouse keeps working until
                you are {Math.round(spouseStopOnPrimaryClock)}, but AiRA currently stops all
                contributions when <em>you</em> retire at {values.retireAge}. Their last{" "}
                {Math.round(spouseStopOnPrimaryClock - (values.retireAge || 0))} year(s) of saving are
                NOT in the projection — the plan is conservative here, not optimistic.
                <div style={{ marginTop: 6, color: "#fed7aa" }}>
                  In the meantime you can enter their ongoing pay under <strong>Other Income</strong>,
                  which does model income after you retire — it just won't add to an account balance.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Total belongs WITH the contributions that make it up (logical flow). */}
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--tint-white-08)" }}>
          <span style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}>Total annual savings <span style={{ color: "var(--text-faint)" }}>
            {spouseTotal > 0
              ? `(you ${fmtDollar(annual401k + employerContrib + rothContrib)} + spouse ${fmtDollar(spouseTotal)} + HSA ${fmtDollar(hsaAnnual)} + brokerage ${fmtDollar(taxableContrib)})`
              : "(incl. employer, HSA, Roth & brokerage)"}
          </span></span>
          <span style={{ fontSize: "var(--fs-h1)", fontWeight: 800, color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace" }}>{fmtDollar(totalSavings)}<span style={{ fontSize: "var(--fs-label)", fontWeight: 400, color: "var(--text-muted)" }}>/yr</span></span>
        </div>
      </ACard>

      {/* Second section header. Border-top separates the two groups; the
          old 6-card flat list read as peer streams, which buried the
          timing distinction. */}
      <div style={{
        marginTop: 4, marginBottom: 2, paddingTop: 12,
        borderTop: "1px solid var(--tint-white-10)",
        fontSize: "var(--fs-small)", fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-faint)",
      }}>
        In Retirement
      </div>

      {/* Income card 2: Social Security (moved from the Retirement Plan
          step) — SS is money coming in, not a withdrawal setting. Moved
          here so every income source lives in the same step. The primary +
          spouse + widow's-penalty block moved as one unit; every field,
          helper, and disclosure is unchanged from where it lived before. */}
      <ACard title="Social Security" accent="#7c3aed">
        <WFieldRow label="Social Security Benefit" helper="Monthly benefit at your SS start age. (Per Month)">
          <ANumInput value={Math.round((values.ssb || 0) / 12)} onSet={(v) => onChange("ssb", Math.round(v * 12))} min={0} max={10_000} step={50} suffix="/mo" />
        </WFieldRow>
        <WFieldRow label="SS Start Age" helper="Age you plan to claim Social Security.">
          <LeverValue lever="ssAge">Age {values.ssAge || 67}</LeverValue>
        </WFieldRow>

        {/* Spouse Social Security.
            Everything here is a number the user reads off ssa.gov. We
            don't derive benefits from a PIA + claim-age schedule on
            purpose: SSA's own estimate beats our reconstruction of it, and
            it can't drift out of date on us.

            Only fields the engine actually reads are shown. There's no
            "when does my spouse die" input yet, because the engine doesn't
            model that event — shipping the control first would create a
            dead setting that does nothing. */}
        {/* Read-only pointer, not a second checkbox. `spouse.enabled` is
            one household fact with one switch, in About You — see the note
            there. Duplicating the toggle here is the kind of thing that
            got removed elsewhere. */}
        {!values.spouse?.enabled && (
          <div style={{ padding: "10px 12px", marginBottom: 16, background: "var(--tint-violet-600-08)", border: "1px solid rgba(124,58,237,0.28)", borderRadius: 8, fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span>Modelling one benefit. Add a spouse to include <strong style={{ color: "#c4b5fd" }}>their</strong> benefit, claim age and survivor benefits.</span>
            {/* Index 0 is AboutYouPanel in ProfileWizard's PANELS array. */}
            <button
              type="button"
              onClick={() => onNavigateStep && onNavigateStep(0)}
              style={{
                fontSize: "var(--fs-small)", color: "#c4b5fd", background: "none", border: "none",
                padding: 0, cursor: onNavigateStep ? "pointer" : "default",
                textDecoration: onNavigateStep ? "underline" : "none",
              }}
              disabled={!onNavigateStep}
            >
              Add a spouse in About You →
            </button>
          </div>
        )}

        {values.spouse?.enabled && (() => {
          const sp = values.spouse || {};
          const setSpouse = (patch) => onChange("spouse", { ...sp, ...patch });
          // The spousal top-up is 50% of the higher earner's PIA (the
          // full-retirement-age amount) — not 50% of whatever they
          // actually claim, and delayed credits never raise it. So we have
          // to ask for the FRA amount separately; it's the one number that
          // can't be inferred from what someone is receiving.
          const primaryPia = Number(values.ssPia) || 0;
          const spousePia  = Number(sp.ssPia) || 0;
          const higherPia  = Math.max(primaryPia, spousePia);
          const topUp      = Math.max(0, higherPia * 0.5 - spousePia);
          // Per-person clock. The engines walk one age — the primary's —
          // so the gap is what places the spouse's milestones on that timeline.
          const gap        = spouseAgeOffset(values);
          const spouseAge  = personAgeNow(sp);
          const yourAge    = personAgeNow(values);
          const spouseClaimAtYourAge = (sp.ssAge || 67) + gap;
          return (
            <>
              {/* This is the enabler. Without it the spouse's claim age
                  was compared against the primary's age, so a younger
                  spouse started collecting early by exactly the age gap
                  (see spousalSS.test.js). Asked for first, because every
                  other spouse figure below is interpreted against it. */}
              <WFieldRow
                label="Spouse's date of birth"
                helper="Their own age drives when their benefit starts, when they reach Medicare at 65, and their own RMD age. Leave blank to assume they are the same age as you."
              >
                <SpouseDobField value={sp.dob || ""} onSet={(dob) => setSpouse({ dob })} />
              </WFieldRow>
              {/* Disclose the derivation where it's entered: a date field
                  that silently shifts ten years of income needs to show
                  its own effect. */}
              <div style={{
                margin: "-8px 0 16px", padding: "8px 12px", borderRadius: 8, fontSize: "var(--fs-small)", lineHeight: 1.55,
                background: sp.dob ? "var(--tint-violet-600-08)" : "rgba(148,163,184,0.07)",
                border: `1px solid ${sp.dob ? "rgba(167,139,250,0.28)" : "rgba(148,163,184,0.2)"}`,
                color: sp.dob ? "#c4b5fd" : "var(--text-secondary)",
              }}>
                {sp.dob && spouseAge != null && yourAge != null ? (
                  <>
                    Your spouse is <strong>{spouseAge}</strong> and you are <strong>{yourAge}</strong> —
                    {gap === 0 ? " the same age" : ` a ${Math.abs(gap)}-year gap (${gap > 0 ? "they are younger" : "they are older"})`}.
                    {" "}Their benefit therefore starts when <strong>you</strong> are {spouseClaimAtYourAge},
                    since the plan is drawn on your age.
                  </>
                ) : (
                  <>
                    No spouse birthdate entered, so the plan assumes you are the <strong>same age</strong>.
                    If there is a real gap this matters: a younger spouse's benefit would otherwise be
                    counted years before it actually starts.
                  </>
                )}
              </div>
              <WFieldRow label="Spouse's benefit" helper="What SSA estimates your spouse will receive at the age they plan to claim. (Per Month)">
                <ANumInput value={Math.round((sp.ssb || 0) / 12)} onSet={(v) => setSpouse({ ssb: Math.round(v * 12) })} min={0} max={10_000} step={50} suffix="/mo" />
              </WFieldRow>
              <WFieldRow label="Spouse's SS start age" helper="Their own age when they claim — can differ from yours, since each of you claims independently.">
                <ANumInput value={sp.ssAge || 67} onSet={(v) => setSpouse({ ssAge: v })} min={AGE_LIMITS.ss.min} max={AGE_LIMITS.ss.max} step={1} suffix=" yrs" />
              </WFieldRow>
              <WFieldRow label="Your benefit at full retirement age" helper="From your SSA statement — the amount at FRA, not at the age you plan to claim. Used only for the spousal top-up. (Per Month)">
                <ANumInput value={Math.round(primaryPia / 12)} onSet={(v) => onChange("ssPia", Math.round(v * 12))} min={0} max={10_000} step={50} suffix="/mo" />
              </WFieldRow>
              <WFieldRow label="Spouse's benefit at full retirement age" helper="Same, for your spouse. Enter it even if they never worked — a $0 here still earns the spousal top-up. (Per Month)">
                <ANumInput value={Math.round(spousePia / 12)} onSet={(v) => setSpouse({ ssPia: Math.round(v * 12) })} min={0} max={10_000} step={50} suffix="/mo" />
              </WFieldRow>

              {higherPia > 0 && (
                <div style={{
                  margin: "2px 0 14px", padding: "9px 12px", borderRadius: 8, fontSize: "var(--fs-small)", lineHeight: 1.55,
                  background: topUp > 0 ? "var(--tint-sky-07)" : "rgba(148,163,184,0.08)",
                  border: `1px solid ${topUp > 0 ? "var(--tint-sky-30)" : "rgba(148,163,184,0.22)"}`,
                  color: topUp > 0 ? "var(--accent-teal)" : "var(--text-secondary)",
                }}>
                  {topUp > 0 ? (
                    <>
                      <strong>Spousal top-up: {fmtDollar(Math.round(topUp / 12))}/mo</strong> — your spouse's own
                      benefit is below half of the higher earner's full-retirement amount, so Social Security
                      tops it up to that level. Already included in your plan.
                    </>
                  ) : (
                    <>No spousal top-up: your spouse's own benefit already exceeds half of the higher
                    earner's full-retirement amount, so they simply receive their own.</>
                  )}
                </div>
              )}

              {/* The widow's penalty, now modeled. Replaces the "Not
                  modeled yet" note that sat here. One user-entered age, not
                  a mortality draw, so the event lands in a known year and
                  can be explained rather than merely averaged. */}
              {/* Who dies first. Not cosmetic: it decides whose age drives
                  the plan horizon, Medicare, the age-65 add-on, the RMD
                  clock and the survivor's own FRA. Asked before the age,
                  because the age below is read as belonging to whoever is
                  selected here. */}
              <WFieldRow
                label="Who passes first"
                helper="The higher earner is often the older partner, so this is frequently the more realistic case — and it changes far more than the benefit: the plan then has to fund the survivor's whole life, not yours."
              >
                <div style={{ display: "flex", gap: 6 }}>
                  {[["spouse", "My spouse"], ["primary", "Me"]].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setSpouse({ firstToDie: val })}
                      style={{
                        background: (sp.firstToDie || "spouse") === val ? "rgba(251,146,60,0.18)" : "transparent",
                        border: `1px solid ${(sp.firstToDie || "spouse") === val ? "rgba(251,146,60,0.55)" : "#1e3a5f"}`,
                        color: (sp.firstToDie || "spouse") === val ? "#fdba74" : "var(--text-secondary)",
                        borderRadius: 6, padding: "5px 12px", fontSize: "var(--fs-label)", cursor: "pointer", fontWeight: 600,
                      }}
                    >{lbl}</button>
                  ))}
                </div>
              </WFieldRow>
              <WFieldRow
                label={(sp.firstToDie === "primary") ? "Model my death at my age" : "Model the first death at spouse's age"}
                helper="Optional. Leave blank to skip. This is the hardest thing for a couple to plan for and the easiest to underestimate — the tax increase is usually larger than the benefit lost."
              >
                <ANumInput
                  value={sp.deathAge ?? 0}
                  onSet={(v) => setSpouse({ deathAge: v > 0 ? v : null })}
                  min={0} max={AGE_LIMITS.end.max} step={1}
                  suffix={sp.deathAge ? " yrs" : ""}
                />
              </WFieldRow>
              {sp.deathAge > 0 ? (() => {
                // Everything here depends on who dies. `deathAge` is
                // always the decedent's own age, so translate once and
                // derive the rest from the survivor's perspective, exactly
                // as the engine does.
                const primarySurvives = (sp.firstToDie || "spouse") !== "primary";
                const deathAtYourAge = primarySurvives ? sp.deathAge + gap : sp.deathAge;
                const survivorDob = primarySurvives ? values.dob : sp.dob;
                const survFraAge = survivorFra(
                  survivorDob ? parseInt(String(survivorDob).slice(0, 4), 10) : null
                );
                // The survivor's own age in the death year.
                const survAgeAtDeath = primarySurvives ? deathAtYourAge : sp.deathAge - gap;
                const survClaim = resolveSurvivorClaimAge(sp.survivorClaimAge, survAgeAtDeath);
                const decOwnClaimAge = primarySurvives ? (sp.ssAge || 67) : (values.ssAge || 67);
                const deceasedHadClaimed = sp.deathAge >= decOwnClaimAge;
                const basis = survivorBasis({
                  deceasedCheck: primarySurvives ? (Number(sp.ssb) || 0) : (Number(values.ssb) || 0),
                  deceasedPia:   primarySurvives ? (Number(sp.ssPia) || 0) : (Number(values.ssPia) || 0),
                  deceasedHadClaimed,
                });
                const quoted = Number(sp.survivorBenefitAtClaim) || 0;
                const factor = quoted > 0 ? 1 : survivorReductionFactor(survClaim, survFraAge);
                const survAmt = quoted > 0 ? quoted : basis * factor;
                const ownAmt = primarySurvives ? (Number(values.ssb) || 0) : (Number(sp.ssb) || 0);
                const jointTotal = ownAmt + (Number(sp.ssb) || 0) + topUp;
                return (
                  <>
                    {/* The survivor's own benefit and the survivor benefit
                        are independent (deemed filing doesn't apply), so
                        this needs its own claim age. It's the only real
                        flexibility a survivor has and the app couldn't
                        express it before. */}
                    <WFieldRow
                      label="Survivor claims the survivor benefit at age"
                      helper={`Survivor benefits can start at 60 — earlier than the 62 floor on your own benefit — and they are a SEPARATE benefit, so this age is independent of your own claim age. Blank = claim as soon as eligible (${survClaim}).`}
                    >
                      <ANumInput
                        value={sp.survivorClaimAge ?? 0}
                        onSet={(v) => setSpouse({ survivorClaimAge: v > 0 ? v : null })}
                        min={0} max={AGE_LIMITS.ss.max} step={1}
                        suffix={sp.survivorClaimAge ? " yrs" : ""}
                      />
                    </WFieldRow>
                    <WFieldRow
                      label="Survivor benefit SSA quotes at that age"
                      helper="Optional. If SSA has given you a figure, enter it and AiRA uses it as-is. Leave blank and AiRA derives it from the deceased's benefit, reduced for claiming before survivor full retirement age. (Per Month)"
                    >
                      <ANumInput
                        value={Math.round(quoted / 12)}
                        onSet={(v) => setSpouse({ survivorBenefitAtClaim: Math.round(v * 12) })}
                        min={0} max={10_000} step={50} suffix="/mo"
                      />
                    </WFieldRow>

                    <div style={{
                      margin: "-8px 0 16px", padding: "10px 12px", borderRadius: 8, fontSize: "var(--fs-small)", lineHeight: 1.6,
                      background: "rgba(251,146,60,0.09)", border: "1px solid rgba(251,146,60,0.3)", color: "#fdba74",
                    }}>
                      <strong>
                        {firstDeathHeadline(primarySurvives, deathAtYourAge, survAgeAtDeath)}
                      </strong>{" "}
                      {!primarySurvives && gap > 0 && (
                        <span>
                          The plan now runs {gap} year{gap === 1 ? "" : "s"} longer, because the money has to
                          last until <em>your spouse</em> reaches your planning age — not until you would have.
                          That is why the success rate drops.{" "}
                        </span>
                      )}
                      Three things change, and the last is the one people miss:
                      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                        <li>
                          The survivor benefit is{" "}
                          <strong>{fmtDollar(Math.round(survAmt / 12))}/mo</strong>
                          {quoted > 0 ? " (as you entered it, SSA-quoted)" : (
                            <>
                              {" "}— {Math.round(factor * 1000) / 10}% of the{" "}
                              {deceasedHadClaimed ? "benefit they were receiving" : "full-retirement amount they were entitled to"}
                              {factor < 1
                                ? `, permanently reduced for claiming at ${survClaim}, before the survivor full retirement age of ${Math.round(survFraAge * 12) / 12 === survFraAge ? survFraAge : survFraAge.toFixed(2)}`
                                : ", unreduced"}
                            </>
                          )}.
                          {!deceasedHadClaimed && " They died before claiming, so it is based on their FRA amount — eligibility does not depend on them having filed."}
                        </li>
                        <li>
                          Household Social Security falls from{" "}
                          <strong>{fmtDollar(Math.round(jointTotal / 12))}/mo</strong> to the larger of the
                          survivor benefit and the survivor's own{" "}
                          <strong>{fmtDollar(Math.round(ownAmt / 12))}/mo</strong> — never both.
                          {ownAmt > 0 && survAmt > ownAmt && (values.ssAge || 67) > survClaim
                            ? " Because the two are independent, the survivor benefit can be drawn now while your own keeps growing, then switched when yours is larger."
                            : ""}
                        </li>
                        <li>
                          You file <strong>Single</strong> from the following year: narrower brackets, roughly
                          half the standard deduction, halved IRMAA thresholds and half the senior bonus —
                          on a portfolio and an RMD that barely changed.
                        </li>
                      </ul>
                      <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px solid var(--tint-orange-25)", color: "#fcd9b6" }}>
                        <strong>See what it costs:</strong> the <em>Stress Test</em> tab shows a
                        <strong> Widow&apos;s penalty</strong> card — your plan run twice, with and without this
                        death, on the same market paths — so the change in success rate is attributable to
                        the death alone.
                      </div>
                      <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px solid var(--tint-orange-25)", color: "#fcd9b6" }}>
                        Survivor claiming is one of the highest-stakes decisions in Social Security and it
                        turns on both benefit amounts, two different full-retirement ages, health, and whether
                        the survivor is still working. AiRA does not model the earnings test. Get both figures
                        from SSA and have a fee-only advisor check the sequence before anyone files.
                      </div>
                    </div>
                  </>
                );
              })() : (
                <div style={{
                  margin: "-8px 0 16px", padding: "9px 12px", borderRadius: 8, fontSize: "var(--fs-small)", lineHeight: 1.55,
                  background: "rgba(148,163,184,0.07)", border: "1px solid rgba(148,163,184,0.2)", color: "var(--text-secondary)",
                }}>
                  No first death modelled — the plan assumes you both live to the end of it and file jointly
                  throughout. The <em>Spouse passes early</em> scenario on the Stress Test tab is a one-off
                  version of the same question.
                </div>
              )}
            </>
          );
        })()}
      </ACard>

      {/* Income card 3: Rental / Airbnb (moved from the Retirement Plan
          step) — blanket rental income is money coming in, so it belongs
          in the same step as SS and pensions. Per-property rental stays on
          the Housing tab (that's a separate object with its own fields);
          this card is the household-level fallback the engine uses when no
          property has its own income set. */}
      <ACard title="Rental Income" accent="#295ff1" collapsible defaultOpen={false}>
        <WFieldRow
          label="Rental Net Income (annual)"
          helper={(values.properties || []).some((pr) => Number(pr.income) > 0)
            ? "Ignored — a property below has its own income set, which takes precedence."
            : "Net rental / Airbnb income. Drives the “Rental/Passive” bar in the Income & Expenses chart."}
        >
          <ANumInput value={values.ab || 0} onSet={(v) => onChange("ab", v)} min={0} max={MAX_MONEY_INPUT} step={1000} suffix="/yr" />
        </WFieldRow>
        <WFieldRow label="Rental Growth Rate" helper="Annual growth rate for rental income (default 3%).">
          <ANumInput value={values.abGrowth ?? 3} onSet={(v) => onChange("abGrowth", v)} min={0} max={10} step={0.5} suffix="%" />
        </WFieldRow>
        <WFieldRow label="Rental Reliability" helper="Probability rental income is received each year (default 80%).">
          <ANumInput value={values.abReliability || 80} onSet={(v) => onChange("abReliability", v)} min={0} max={100} step={5} suffix="%" />
        </WFieldRow>
         <WFieldRow label="Rental Income Ends (year)" helper="Year when rental income is expected to end.">
          <input type="number" value={values.abEndYear || ''} min={2026} max={2100} step={1} onChange={(e) => onChange("abEndYear", Number(e.target.value))} style={{ background:"#0d1b2a", border:"1px solid #1e3a5f", color:"var(--text-primary)",
            borderRadius:6, padding:"6px 8px", fontSize: "var(--fs-label)", fontFamily:"'JetBrains Mono',monospace",
            width:80, textAlign:"right" }}
                onFocus={selectAllOnFocus}
              /> year
        </WFieldRow>
      </ACard>

      {/* Income card 4: Pensions.
          Split from Other Income because a pension is a distinct object
          with a type, and the three types behave completely differently in
          the engine: a monthly pension is a recurring stream that offsets
          spending, while a lump sum or cash balance is a deposit into an
          account that compounds from the year it arrives. The type
          selector is embedded per pension — people commonly have more than
          one, and each can be a different kind. Switching type migrates
          the entry between the two stores. */}
      <ACard title="Pensions">
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 14, lineHeight: 1.55 }}>
          Defined-benefit income. Pick the type for each one — AiRA models them differently, and
          getting it wrong is the most common source of bad numbers here.
        </div>

        {/* Monthly pensions live in otherIncomes; lump/cash live in cashFlowEvents. */}
        {(values.otherIncomes || []).filter(x => x.kind === "pension").map((inc) => (
          <PensionEntry
            key={inc.id}
            type="monthly"
            onTypeChange={(t) => convertPension(values, onChange, inc, "monthly", t)}
            onRemove={() => onChange("otherIncomes", (values.otherIncomes || []).filter((x) => x.id !== inc.id))}
          >
            <OtherIncomeCard
              inc={inc}
              onChange={(updated) => onChange("otherIncomes", (values.otherIncomes || []).map((x) => x.id === inc.id ? updated : x))}
              onRemove={() => onChange("otherIncomes", (values.otherIncomes || []).filter((x) => x.id !== inc.id))}
            />
          </PensionEntry>
        ))}

        {(values.cashFlowEvents || []).filter(e => e.direction === "in" && e.source === "pension").map((ev) => {
          const upd = (patch) => onChange("cashFlowEvents", (values.cashFlowEvents || []).map(x => x.id === ev.id ? { ...x, ...patch } : x));
          const type = ev.bucket === "pretax" && !ev.taxable ? "cash" : "lump";
          return (
            <PensionEntry
              key={ev.id}
              type={type}
              onTypeChange={(t) => convertPension(values, onChange, ev, type, t)}
              onRemove={() => onChange("cashFlowEvents", (values.cashFlowEvents || []).filter(x => x.id !== ev.id))}
            >
              <LumpPensionFields ev={ev} upd={upd} />
            </PensionEntry>
          );
        })}

        <button
          onClick={() => onChange("otherIncomes", [...(values.otherIncomes || []), {
            id: Date.now().toString(), kind: "pension", name: "", annual: 0,
            startYear: new Date().getFullYear(), endYear: null,
            growthMode: "pct", growthRate: 0, growthAmount: 0, growthCapYears: null, taxable: true,
          }])}
          style={{ background: "var(--tint-sky-500-10)", border: "1px dashed var(--tint-sky-500-45)", borderRadius: 6, color: "var(--accent)", fontSize: "var(--fs-label)", fontWeight: 600, padding: "6px 12px", cursor: "pointer", marginTop: 8 }}
        >+ Add pension</button>
      </ACard>

      {/* Income card: Other Income */}
      <ACard title="Other Income">
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 14, lineHeight: 1.55 }}>
          Everything that isn't a pension — part-time work, an annuity, royalties, alimony. Recurring
          amounts paid <em>to</em> you, not balances you draw down.
        </div>
        {(values.otherIncomes || []).filter(x => x.kind !== "pension").map((inc, idx) => (
          <OtherIncomeCard
            key={inc.id}
            inc={inc}
            autoFocus={idx === (values.otherIncomes || []).filter(x => x.kind !== "pension").length - 1 && !inc.annual}
            onChange={(updated) => onChange("otherIncomes", (values.otherIncomes || []).map((x) => x.id === inc.id ? updated : x))}
            onRemove={() => onChange("otherIncomes", (values.otherIncomes || []).filter((x) => x.id !== inc.id))}
          />
        ))}
        <button
          onClick={() => onChange("otherIncomes", [...(values.otherIncomes || []), { id: Date.now().toString(), name: "", annual: 0, startYear: new Date().getFullYear(), endYear: null, growthMode: "pct", growthRate: 0, growthAmount: 0, growthCapYears: null, taxable: true }])}
          style={{ background: "var(--tint-sky-500-10)", border: "1px dashed var(--tint-sky-500-45)", borderRadius: 6, color: "var(--accent)", fontSize: "var(--fs-label)", fontWeight: 600, padding: "6px 12px", cursor: "pointer", marginTop: 8 }}
          onMouseEnter={e => e.currentTarget.style.background = "var(--tint-sky-500-18)"}
          onMouseLeave={e => e.currentTarget.style.background = "var(--tint-sky-500-10)"}
        >+ Add another income source</button>
      </ACard>

      {/* Income card: One-off income & windfalls.
          A single future inflow — an inheritance, a home sale, a business
          exit. Stored as a cashFlowEvents inflow (direction:"in") so the
          engines deposit it into the chosen account bucket in the year it
          arrives and it compounds from there. It must never be entered as
          recurring Other Income: need = max(0, sp − income) nets a stream
          against one year's spending and silently discards the rest — the
          trap this card exists to avoid. Before this card existed the only
          way to enter an inheritance was to disguise it as a lump-sum
          pension. */}
      <ACard title="One-Off Income & Windfalls">
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", marginBottom: 14, lineHeight: 1.55 }}>
          Money you expect <em>once</em> — an inheritance, a home or business sale. AiRA deposits it into
          the account you pick in the year it arrives, and it compounds from there. It is income, not
          spending — it will never appear under Planned One-Off Expenses.
        </div>
        {(values.cashFlowEvents || []).filter(e => e.direction === "in" && e.source !== "pension").map((ev) => {
          const upd = (patch) => onChange("cashFlowEvents", (values.cashFlowEvents || []).map(x => x.id === ev.id ? { ...x, ...patch } : x));
          return (
            <div key={ev.id} style={{ border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: 12, marginBottom: 10, background: "var(--tint-white-02)", position: "relative" }}>
              <button onClick={() => onChange("cashFlowEvents", (values.cashFlowEvents || []).filter(x => x.id !== ev.id))}
                title="Remove this windfall"
                style={{ position: "absolute", top: 8, right: 8, background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, cursor: "pointer", fontSize: "var(--fs-label)", padding: "2px 8px" }}>×</button>
              <LumpPensionFields ev={ev} upd={upd} placeholder="Inheritance"
                taxHint="(most inheritances are NOT ordinary income — leave unchecked unless it's an inherited pre-tax IRA distribution or similar)" />
            </div>
          );
        })}
        <button
          onClick={() => onChange("cashFlowEvents", [...(values.cashFlowEvents || []), {
            id: Date.now().toString(), source: "windfall", label: "", amount: 0,
            year: new Date().getFullYear() + 5, direction: "in",
            bucket: "taxable", taxable: false, inflate: false,
          }])}
          style={{ background: "var(--tint-sky-500-10)", border: "1px dashed var(--tint-sky-500-45)", borderRadius: 6, color: "var(--accent)", fontSize: "var(--fs-label)", fontWeight: 600, padding: "6px 12px", cursor: "pointer", marginTop: 8 }}
        >+ Add a windfall</button>
      </ACard>

    </div>
  );
}

const PENSION_TYPES = [
  { id: "monthly", icon: "📅", label: "Monthly / Annual", desc: "Paid every year from a start age" },
  { id: "lump",    icon: "💰", label: "Lump Sum",         desc: "One payment at a set age" },
  { id: "cash",    icon: "🏦", label: "Cash Balance",     desc: "Balance you'll roll over" },
];

/**
 * Moves a pension between the two storage models when its type changes.
 *
 * A monthly pension is a recurring stream (`otherIncomes`), while a lump
 * sum or cash balance is a one-time inflow event (`cashFlowEvents`).
 * They're stored separately because the engines treat them completely
 * differently — one offsets spending each year, the other gets deposited
 * into an account and compounds — so switching type has to migrate the
 * record rather than flip a flag.
 *
 * Destination defaults are chosen so most users never touch them: a
 * cash-balance plan is almost always rolled to an IRA (no tax on receipt,
 * becomes RMD-able), whereas a lump sum taken as cash is ordinary income
 * that year and the net proceeds land liquid.
 */
function convertPension(values, onChange, entry, fromType, toType) {
  if (fromType === toType) return;
  const incomes = values.otherIncomes  || [];
  const events  = values.cashFlowEvents || [];
  const label   = entry.name || entry.label || "Pension";
  const amount  = entry.annual || entry.amount || 0;
  const year    = entry.startYear || entry.year || new Date().getFullYear() + 5;

  if (toType === "monthly") {
    onChange("cashFlowEvents", events.filter(x => x.id !== entry.id));
    onChange("otherIncomes", [...incomes.filter(x => x.id !== entry.id), {
      id: entry.id, kind: "pension", name: label, annual: amount,
      startYear: year, endYear: null,
      growthMode: "pct", growthRate: 0, growthAmount: 0, growthCapYears: null, taxable: true,
    }]);
    return;
  }

  const isCash = toType === "cash";
  onChange("otherIncomes", incomes.filter(x => x.id !== entry.id));
  onChange("cashFlowEvents", [...events.filter(x => x.id !== entry.id), {
    id: entry.id, source: "pension", label,
    amount, year, direction: "in",
    bucket: isCash ? "pretax" : "cash",
    taxable: !isCash,
    inflate: false,
  }]);
}

/** Wraps one pension with its embedded type selector. */
function PensionEntry({ type, onTypeChange, onRemove, children }) {
  return (
    <div style={{ border: "1px solid var(--tint-white-08)", borderRadius: 10, padding: 12, marginBottom: 10, background: "var(--tint-white-02)" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {PENSION_TYPES.map(t => {
          const on = t.id === type;
          return (
            <button key={t.id} onClick={() => onTypeChange(t.id)} title={t.desc}
              style={{
                background: on ? "rgba(14,165,233,0.22)" : "transparent",
                border: `1px solid ${on ? "rgba(56,189,248,0.55)" : "var(--tint-white-10)"}`,
                color: on ? "#e0f2fe" : "var(--text-muted)",
                borderRadius: 6, padding: "4px 10px", fontSize: "var(--fs-small)",
                fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit",
              }}>{t.icon} {t.label}</button>
          );
        })}
        <button onClick={onRemove} title="Remove this pension"
          style={{ marginLeft: "auto", background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, cursor: "pointer", fontSize: "var(--fs-label)", padding: "2px 8px" }}>×</button>
      </div>
      {children}
    </div>
  );
}

/** Fields for a one-time inflow (lump-sum / cash-balance pension, or a
 *  windfall like an inheritance — the One-Off Income card reuses this). */
function LumpPensionFields({ ev, upd, placeholder = "Cash balance pension", taxHint = "(uncheck for a direct rollover)" }) {
  const cell = { background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "5px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace", width: "100%" };
  const lbl  = { fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 3, display: "block" };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 0.8fr 1.1fr", gap: 8 }}>
      <label><span style={lbl}>Label</span>
        <input type="text" value={ev.label || ""} placeholder={placeholder}
          onChange={(e) => upd({ label: e.target.value })} style={cell} /></label>
      <label><span style={lbl}>Amount</span>
        <input type="number" value={ev.amount || 0} min={0} step={1000}
          onChange={(e) => upd({ amount: Number(e.target.value) })} style={{ ...cell, textAlign: "right" }}
                onFocus={selectAllOnFocus}
              /></label>
      <label><span style={lbl}>Year</span>
        <input type="number" value={ev.year} min={new Date().getFullYear()} max={2090}
          onChange={(e) => upd({ year: Number(e.target.value) })} style={{ ...cell, textAlign: "right" }}
                onFocus={selectAllOnFocus}
              /></label>
      <label><span style={lbl}>Deposit into</span>
        <select value={ev.bucket || "cash"} onChange={(e) => upd({ bucket: e.target.value })} style={{ ...cell, cursor: "pointer" }}>
          <option value="pretax">Pre-tax (IRA rollover)</option>
          <option value="cash">Cash</option>
          <option value="taxable">Taxable brokerage</option>
          <option value="roth">Roth</option>
        </select></label>
      <label style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 6, fontSize: "var(--fs-small)", color: "var(--text-secondary)", cursor: "pointer" }}>
        <input type="checkbox" checked={!!ev.taxable} onChange={(e) => upd({ taxable: e.target.checked })} />
        Taxable as ordinary income in {ev.year}
        <span style={{ color: "var(--text-faint)" }}>{taxHint}</span>
      </label>
    </div>
  );
}

function OtherIncomeCard({ inc, autoFocus, onChange, onRemove }) {
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    if (autoFocus && nameRef.current) nameRef.current.focus();
  }, [autoFocus]);

  const onFocusCard = () => { clearTimeout(blurTimer.current); setFocused(true); };
  const onBlurCard = () => { blurTimer.current = setTimeout(() => setFocused(false), 150); };

  const upd = (patch) => onChange({ ...inc, ...patch });

  return (
    <div
      onFocus={onFocusCard}
      onBlur={onBlurCard}
      style={{
        background: focused ? "rgba(14,165,233,0.05)" : "var(--tint-white-02)",
        border: `1px solid ${focused ? "rgba(14,165,233,0.4)" : "var(--tint-white-07)"}`,
        borderRadius: 8,
        padding: "8px 12px",
        marginBottom: 8,
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: focused ? 8 : 0 }}>
        <input
          ref={nameRef}
          type="text"
          value={inc.name}
          placeholder="Income source name"
          onChange={(e) => upd({ name: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          style={{
            flex: 1,
            fontSize: "var(--fs-label)",
            fontWeight: 600,
            color: "var(--text-primary)",
            background: "transparent",
            border: "none",
            borderBottom: `1px solid ${focused ? "rgba(14,165,233,0.4)" : "var(--tint-white-10)"}`,
            outline: "none",
            padding: "2px 0",
            fontFamily: "'DM Sans',sans-serif",
            transition: "border-color 0.15s",
          }}
        />
        {!focused && (
          <span style={{ fontSize: "var(--fs-small)", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>
            {inc.annual ? <span style={{ color: "var(--accent-teal)", fontWeight: 700 }}>{`$${Math.round(inc.annual).toLocaleString()}/yr`}</span> : ""}
            <span style={{ color: "var(--text-muted)" }}>
              {inc.startYear ? ` · ${inc.startYear}` : ""}
              {inc.endYear ? `–${inc.endYear}` : inc.startYear ? "+" : ""}
              {inc.growthMode === "fixed" && inc.growthAmount ? ` · +$${Math.round(inc.growthAmount).toLocaleString()}/yr` : ""}
              {inc.growthMode !== "fixed" && inc.growthRate ? ` · +${inc.growthRate}%/yr` : ""}
            </span>
          </span>
        )}
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={onRemove}
          style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "var(--fs-body)", padding: "2px 4px", opacity: 0.5 }}
          onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = "var(--chart-1)"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.color = "var(--text-muted)"; }}
        >×</button>
      </div>
      {focused && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 72px 72px 108px 60px auto", gap: 6, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 2 }}>Annual ($)</div>
            <ANumInput value={inc.annual || 0} onSet={(v) => upd({ annual: v })} min={0} max={MAX_MONEY_INPUT} step={1000} suffix="/yr" />
          </div>
          <div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 2 }}>Start yr</div>
            <input type="number" value={inc.startYear || ""} min={2025} max={2100}
              onChange={(e) => upd({ startYear: e.target.value ? Number(e.target.value) : null })}
              style={{ width: "100%", background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 6px", fontSize: "var(--fs-small)", fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
          </div>
          <div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 2 }}>End yr</div>
            <input type="number" value={inc.endYear || ""} min={2025} max={2100}
              onChange={(e) => upd({ endYear: e.target.value ? Number(e.target.value) : null })}
              placeholder="∞"
              style={{ width: "100%", background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 6px", fontSize: "var(--fs-small)", fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
          </div>
          <div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 3 }}>Grows by</div>
            {/* Segmented control — both units visible so it's obvious you can switch. */}
            <div style={{ display: "flex", border: "1px solid #1e3a5f", borderRadius: 5, overflow: "hidden", marginBottom: 4 }}>
              {[["pct", "% /yr"], ["fixed", "$ /yr"]].map(([m, lbl]) => {
                const on = (inc.growthMode || "pct") === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => upd({ growthMode: m })}
                    title={m === "pct" ? "Percentage increase each year (compounds)" : "Fixed dollar increase each year — many pensions raise by a set $ amount"}
                    style={{ flex: 1, border: "none", cursor: "pointer", fontSize: "var(--fs-small)", fontWeight: 700, padding: "3px 0", lineHeight: 1, background: on ? "#0ea5e9" : "transparent", color: on ? "#fff" : "var(--text-muted)" }}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
            {inc.growthMode === "fixed"
              ? <ANumInput value={inc.growthAmount || 0} onSet={(v) => upd({ growthAmount: v })} min={0} max={MAX_MONEY_INPUT} step={100} suffix="/yr" />
              : <ANumInput value={inc.growthRate || 0} onSet={(v) => upd({ growthRate: v })} min={0} max={20} step={0.5} suffix="%" />}
          </div>
          <div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 2 }}>Cap yrs</div>
            <input type="number" value={inc.growthCapYears || ""} min={1} max={50}
              onChange={(e) => upd({ growthCapYears: e.target.value ? Number(e.target.value) : null })}
              placeholder="∞"
              style={{ width: "100%", background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 6px", fontSize: "var(--fs-small)", fontFamily: "'JetBrains Mono',monospace", textAlign: "right" }}
                onFocus={selectAllOnFocus}
              />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingTop: 14 }}>
            <Toggle val={inc.taxable} onChange={(v) => upd({ taxable: v })} accent="#0ea5e9" />
            <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>Taxable</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Trigger a client-side download of CSV text (used for the budget templates).
function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Detailed-expense CSV import. Sits inside the Spending section, beside
 * the core-spend inputs. A one-year budget sums to the US Spending field
 * (inflated forward like a typed number); a multi-year budget becomes an
 * explicit per-year spend schedule that overrides the withdrawal-strategy
 * spend rule. Follows the Boldin "Detailed Budgeter" exclusion convention:
 * mortgage/rent, debt, medical, long-term care, and income tax are modeled
 * elsewhere and shouldn't be in the uploaded file.
 */
function ExpenseImport({ values, onChange }) {
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const fileRef = useRef(null);
  const meta = values.spImportMeta || null;

  // One apply path for both the uploaded file and the inline editor's rows.
  const applyCsv = (text, fileName) => {
    setError(""); setWarnings([]);
    try {
      const r = parseExpenseCsv(text);
      setWarnings(r.warnings || []);
      const importedAt = new Date().toISOString();
      if (r.mode === "multi") {
        onChange("spSchedule", r.schedule);
        onChange("spImportMeta", {
          mode: "multi", fileName, importedAt,
          years: r.schedule.length,
          firstYear: r.schedule[0].year, lastYear: r.schedule[r.schedule.length - 1].year,
        });
      } else {
        // Single-year budget lands in the US Spending field; clear any prior schedule.
        onChange("sp", r.total);
        onChange("spSchedule", null);
        onChange("spImportMeta", {
          mode: "single", fileName, importedAt,
          total: r.total, essentialTotal: r.essentialTotal ?? null,
          lineCount: r.lineItems.length,
        });
      }
    } catch (err) {
      setError(err.message || "Could not read that file.");
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => applyCsv(String(e.target.result || ""), file.name);
    reader.onerror = () => setError("Could not read that file.");
    reader.readAsText(file);
  };

  const budgetLines = values.budgetLines || [];
  const budgetReady = budgetLines.length > 0 && budgetLines.every((l) => !lineError(l));

  const clearImport = () => {
    onChange("spSchedule", null);
    onChange("spImportMeta", null);
    setError(""); setWarnings([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <>
    <BudgetEditor value={budgetLines} onChange={(next) => onChange("budgetLines", next)} />
    {budgetLines.length > 0 && (
      <button type="button" className="cfg-btn" disabled={!budgetReady} data-testid="budget-apply"
        style={{ width: "auto", margin: "8px 0 0", padding: "6px 14px", opacity: budgetReady ? 1 : 0.5 }}
        onClick={() => applyCsv(budgetLinesToCsv(budgetLines), "the budget you typed")}>
        Use this budget as my US spending
      </button>
    )}
    <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.25)", borderRadius: 8 }}>
      <div style={{ fontSize: "var(--fs-label)", fontWeight: 700, color: "#c4b5fd", marginBottom: 4 }}>Import detailed expenses (CSV)</div>
      <div style={{ fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 8 }}>
        Replace the typed spend above with a line-item budget. <strong style={{ color: "var(--text-secondary)" }}>Exclude</strong> mortgage/rent,
        debt, medical, long-term care, and income tax — those are modeled separately (Expense Model, carveouts, tax engine).
        Upload <strong style={{ color: "var(--text-secondary)" }}>one year</strong> (summed and inflated forward) or
        <strong style={{ color: "var(--text-secondary)" }}> multiple years</strong> (one column or row per year — used as the spend plan for those years).
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => handleFile(e.target.files && e.target.files[0])}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{ fontSize: "var(--fs-label)", background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.4)", color: "#d8b4fe", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontWeight: 600 }}
        >⬆ Choose CSV file</button>
        <button
          onClick={() => downloadCsv("AiRA_budget_template_one_year.csv", SINGLE_YEAR_TEMPLATE)}
          style={{ fontSize: "var(--fs-small)", background: "transparent", border: "1px solid #334155", color: "var(--text-secondary)", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}
        >↓ One-year template</button>
        <button
          onClick={() => downloadCsv("AiRA_budget_template_multi_year.csv", MULTI_YEAR_TEMPLATE)}
          style={{ fontSize: "var(--fs-small)", background: "transparent", border: "1px solid #334155", color: "var(--text-secondary)", borderRadius: 6, padding: "6px 10px", cursor: "pointer" }}
        >↓ Multi-year template</button>
      </div>

      {error && (
        <div style={{ marginTop: 8, fontSize: "var(--fs-small)", color: "#fca5a5", background: "var(--tint-red-08)", border: "1px solid var(--tint-red-30)", borderRadius: 6, padding: "6px 10px" }}>
          {error}
        </div>
      )}

      {meta && (
        <div style={{ marginTop: 10, fontSize: "var(--fs-small)", color: "var(--text-secondary)", background: "var(--tint-sky-06)", border: "1px solid var(--tint-sky-20)", borderRadius: 6, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div style={{ lineHeight: 1.5 }}>
            {meta.mode === "multi" ? (
              <>
                <strong style={{ color: "var(--accent-teal)" }}>Multi-year budget loaded</strong> — {meta.years} years
                ({meta.firstYear}–{meta.lastYear}) from <em>{meta.fileName}</em>.
                <div style={{ color: "var(--accent-gold)", fontSize: "var(--fs-small)", marginTop: 2 }}>
                  This overrides the withdrawal-strategy spend rule. After the last year, the final amount inflates forward.
                </div>
              </>
            ) : (
              <>
                <strong style={{ color: "var(--accent-teal)" }}>One-year budget loaded</strong> — {fmtDollar(meta.total)}/yr
                from {meta.lineCount} line item(s) in <em>{meta.fileName}</em>.
                {meta.essentialTotal != null && (
                  <div style={{ color: "var(--text-secondary)", fontSize: "var(--fs-small)", marginTop: 2 }}>
                    Essential (Must Spend): {fmtDollar(meta.essentialTotal)}/yr · Total (Like to Spend) set as US Spending above.
                  </div>
                )}
              </>
            )}
          </div>
          <button
            onClick={clearImport}
            style={{ flexShrink: 0, fontSize: "var(--fs-small)", background: "var(--bg-danger)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, padding: "4px 10px", cursor: "pointer" }}
          >Clear</button>
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{ marginTop: 8, fontSize: "var(--fs-small)", color: "var(--accent-gold)" }}>
          {warnings.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}
    </div>
    </>
  );
}

/**
 * Spending & Expenses tab — the typed spending fields and the
 * detailed-budget CSV uploader live together: they're two ways of
 * expressing the same thing, so they belong on one screen — type a number,
 * or upload a budget that replaces/overrides it, with the interaction
 * visible in place. The uploaded budget drives the same fields it always
 * did (sp / spSchedule / spImportMeta); the Retirement Plan tab keeps a
 * compact spending summary.
 */
function ExpensesPanel({ values, onChange }) {
  const meta = values.spImportMeta || null;
  const usSp = values.sp || 0;
  const outOfCountrySp = values.spOutOfCountry != null ? values.spOutOfCountry : (values.spSpendOutofState || 0);
  const combinedSp = usSp + outOfCountrySp;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <ACard title="Spending" accent="var(--accent-teal)">
        {/* The after-tax basis used to be stated on the results surfaces
            and in the About tab, but not here — which is where the user
            actually forms their mental model of what number to type.
            Wording matches the results-bar tooltip and the About card on
            purpose so the three agree. Inline and visible, not a tooltip:
            this changes how the number gets read, and `title=` doesn't
            exist on touch. */}
        <div style={{
          marginBottom: 16, padding: "9px 12px", borderRadius: 8,
          background: "var(--tint-amber-07)", border: "1px solid rgba(251,191,36,0.28)",
          fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.55,
        }}>
          <strong style={{ color: "var(--accent-gold)" }}>Enter what you want to spend — after tax.</strong>{" "}
          This is money that reaches your household. AiRA withdraws extra from the portfolio
          to pay the tax bill <em>on top of</em> this figure, so do not add taxes in yourself
          and do not reduce it for them.
          <div data-testid="spend-housing-note" style={{ marginTop: 6 }}>
            {(values.housingType || "own") === "none"
              ? <>Include your housing costs in this number: Housing type is set to “None / already in spend”, so AiRA adds nothing for housing.</>
              : (values.housingType === "rent"
                ? <>Leave rent out of this number: AiRA adds your Annual rent on top of it.</>
                : <>Leave your mortgage payment out of this number: AiRA adds it on top, from <GoStep step={PROFILE_STEP_REAL_ESTATE}>Real Estate &amp; Debt</GoStep>, until the loan is paid off.</>)}
          </div>
        </div>
        <WFieldRow label="US Spending (annual)" helper="Domestic household spending in today's dollars, after tax. Subject to state income tax when residing in-state.">
          <LeverValue lever="sp">{fmtDollar(values.sp || 0)}/yr</LeverValue>
        </WFieldRow>
        <WFieldRow label="Out-of-Country Spending (annual)" helper="Spending that occurs abroad in today's dollars, after tax. Always drawn from the portfolio but never subject to US state tax.">
          <ANumInput value={values.spOutOfCountry != null ? values.spOutOfCountry : (values.spSpendOutofState || 0)} onSet={(v) => onChange("spOutOfCountry", v)} min={0} max={MAX_MONEY_INPUT} step={1000} suffix="/yr" />
        </WFieldRow>
        <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--tint-sky-06)", border: "1px solid var(--tint-sky-20)", borderRadius: 8, fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Total combined annual spending, after tax (used for portfolio draw)</span>
          <strong style={{ color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace", fontSize: "var(--fs-body)" }}>{fmtDollar(combinedSp)}/yr</strong>
        </div>
        {/* The spending curve is the one setting that silently changes
            the number typed above, and it lives in the sidebar — so a user
            who suspects their spending is being padded looks here and
            finds nothing. Read-only pointer, not a second control: the
            toggle stays a single point of control. */}
        <div style={{ marginTop: 8, fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.5 }}>
          {values.smile !== false ? (
            <>
              <span style={{ color: "var(--accent-purple)" }}>Spending curve is ON</span> — this target is re-scaled
              per year (down through the active years, up again in late retirement), so year-by-year
              spending will not equal the figure above. The Withdrawal Plan table shows the exact
              percentage each year. Turn it off under <strong style={{ color: "var(--text-secondary)" }}>Options → Spending curve</strong> in the sidebar.
            </>
          ) : (
            <>
              <span style={{ color: "var(--text-muted)" }}>Spending curve is OFF</span> — this target stays flat
              in real terms for every year of the plan. Enable it under <strong style={{ color: "var(--text-secondary)" }}>Options → Spending curve</strong> in the sidebar.
            </>
          )}
        </div>
      </ACard>

      <ACard title="Detailed Expense Budget" accent="#c4b5fd" desc="Optional. Type or upload a real line-item budget instead of one number." collapsible defaultOpen={(values.budgetLines || []).length > 0}>
        <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 12 }}>
          Instead of typing a single spending number above, upload your real line-item budget.
          A <strong style={{ color: "var(--text-secondary)" }}>one-year</strong> budget is summed into the US Spending field
          above and inflated forward like a typed number.
          A <strong style={{ color: "var(--text-secondary)" }}>multi-year</strong> budget becomes an explicit per-year spending
          plan that overrides your withdrawal strategy's spend rule for those years — the Monte Carlo,
          Withdrawal Plan, and deterministic schedule all follow it.
        </div>
        <ExpenseImport values={values} onChange={onChange} />
        {!meta && (
          <div style={{ marginTop: 12, fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.5 }}>
            No budget loaded — the plan currently uses the typed spending numbers above.
          </div>
        )}
      </ACard>

      {/* Planned one-off expenses.
          Distinct from the recurring spend above: this is the
          roof-in-10-years / car-every-7 case, a cost that lands in one
          future year rather than running continuously. Additive on top of
          the base spend, so the withdrawal strategy still governs the
          recurring plan. */}
      <ACard title="Planned One-Off Expenses" accent="var(--chart-3)"
        desc="Big costs you can see coming — a new roof, a car, a wedding, a heavy travel year. Enter today's price; AiRA inflates it to the year it happens.">
        {/* Outflows only. cashFlowEvents also stores inflows (pension
            lump sums, inheritances — direction:"in"), which are deposits
            into an account, not spending. Rendering the whole array here
            made a $1M inheritance appear as a $1M planned expense, so users
            reasonably concluded their windfall was being canceled out.
            Inflows are managed from the Income step (Pensions / One-Off
            Income cards). */}
        {(values.cashFlowEvents || []).filter(e => e.direction !== "in").length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 70px 70px 78px 28px", gap: 6, marginBottom: 4, fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>
            <span>What</span><span style={{ textAlign: "right" }}>Cost</span><span style={{ textAlign: "right" }}>Year</span>
            <span style={{ textAlign: "right" }}>Every</span><span style={{ textAlign: "right" }}>Until</span><span />
          </div>
        )}
        {(values.cashFlowEvents || []).filter(e => e.direction !== "in").map((ev) => {
          // Patch/remove by id, not index — the render list is filtered,
          // so an index into it doesn't address the same element in the full array.
          const upd = (patch) => onChange("cashFlowEvents",
            (values.cashFlowEvents || []).map(x => x.id === ev.id ? { ...x, ...patch } : x));
          const cell = { background: "#0d1b2a", border: "1px solid #1e3a5f", color: "var(--text-primary)", borderRadius: 6, padding: "4px 8px", fontSize: "var(--fs-label)", fontFamily: "'JetBrains Mono',monospace" };
          const num = { ...cell, textAlign: "right" };
          return (
            <div key={ev.id} style={{ marginBottom: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 70px 70px 78px 28px", gap: 6, alignItems: "center" }}>
                <input type="text" value={ev.label} placeholder="New roof"
                  onChange={(e) => upd({ label: e.target.value })} style={cell} />
                <input type="number" value={ev.amount} min={0} step={1000} placeholder="$"
                  onChange={(e) => upd({ amount: Number(e.target.value) })} style={num}
                onFocus={selectAllOnFocus}
              />
                <input type="number" value={ev.year} min={new Date().getFullYear()} max={2090} step={1}
                  onChange={(e) => upd({ year: Number(e.target.value) })} style={num}
                onFocus={selectAllOnFocus}
              />
                {/* Blank = one-time. */}
                <input type="number" value={ev.recurEveryYears || ""} min={0} max={50} step={1} placeholder="—"
                  title="Repeat every N years. Leave blank for a one-time cost."
                  onChange={(e) => upd({ recurEveryYears: Number(e.target.value) || 0 })} style={num}
                onFocus={selectAllOnFocus}
              />
                <input type="number" value={ev.recurUntilYear || ""} min={new Date().getFullYear()} max={2090} step={1} placeholder="—"
                  title="Stop repeating after this year. Leave blank to repeat for the whole plan."
                  disabled={!ev.recurEveryYears}
                  onChange={(e) => upd({ recurUntilYear: Number(e.target.value) || null })}
                  style={{ ...num, opacity: ev.recurEveryYears ? 1 : 0.35 }}
                onFocus={selectAllOnFocus}
              />
                <button
                  onClick={() => onChange("cashFlowEvents", (values.cashFlowEvents || []).filter(x => x.id !== ev.id))}
                  style={{ background: "var(--tint-red-15)", border: "1px solid var(--tint-red-30)", color: "var(--chart-1)", borderRadius: 5, cursor: "pointer", fontSize: "var(--fs-body)", padding: "2px 6px" }}
                >×</button>
              </div>
              {/* Committed vs deferrable decides whether the guardrails may trim
                  this in a bad market — the same idea as Must Spend vs Like to
                  Spend for recurring costs. A roof can't wait; a trip can. */}
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: "var(--fs-small)", color: "var(--text-secondary)", cursor: "pointer" }}>
                <input type="checkbox" checked={!!ev.deferrable} onChange={(e) => upd({ deferrable: e.target.checked })} />
                I could delay this in a bad market <span style={{ color: "var(--text-faint)" }}>(guardrails may trim it)</span>
              </label>
            </div>
          );
        })}
        <button
          onClick={() => onChange("cashFlowEvents", [
            ...(values.cashFlowEvents || []),
            { id: Date.now().toString(), label: "", amount: 0, year: new Date().getFullYear() + 5, recurEveryYears: 0, recurUntilYear: null, deferrable: false, direction: "out" },
          ])}
          style={{ fontSize: "var(--fs-small)", background: "var(--tint-orange-10)", border: "1px solid var(--tint-orange-25)", color: "var(--chart-3)", borderRadius: 6, padding: "5px 12px", cursor: "pointer", marginTop: 4 }}
        >+ Add planned expense</button>
        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginTop: 8, lineHeight: 1.5 }}>
          Leave <strong style={{ color: "var(--text-muted)" }}>Every</strong> blank for a one-time cost. Set it to 7 for a car
          every seven years, and use <strong style={{ color: "var(--text-muted)" }}>Until</strong> to stop it (e.g. when you'd stop driving).
        </div>
      </ACard>
    </div>
  );
}

function RetirementPanel({ values, onChange, onNavigateStep, onNavigateTab }) {
  const usSp = values.sp || 0;
  const outOfCountrySp = values.spOutOfCountry != null ? values.spOutOfCountry : (values.spSpendOutofState || 0);
  const combinedSp = usSp + outOfCountrySp;
  const twoHousehold = values.twoHousehold ?? false;   // toggle ONLY controls state tax now
  const baseSpend = combinedSp || 100000;
  const floorPct = values.gkFloorPct ?? GK_FLOOR_DEFAULT_PCT;
  const ceilingPct = values.gkCeilingPct ?? GK_CEILING_DEFAULT_PCT;
  // An imported Must/Like budget drives the floor/ceiling; else % of core spend
  // (keep the 100k display fallback for the percent path when no spend is set yet).
  const guard = resolveSpendGuardrails({ sp: usSp, spOutOfCountry: outOfCountrySp, gkFloorPct: floorPct, gkCeilingPct: ceilingPct, spImportMeta: values.spImportMeta });
  const guardFromImport = guard.source === "import";
  const floor = guardFromImport ? guard.gkFloor : Math.round(baseSpend * (floorPct / 100));
  const ceiling = guardFromImport ? guard.gkCeiling : Math.round(baseSpend * (ceilingPct / 100));
  const strategy = resolveStrategy(values.withdrawalStrategy);

  // Initial WR diagnostic — pass combined sp so the helper, the GK card, the metrics WR
  // badge, and the gk-bar strategy strap all show the same number.
  const wr = computeInitialWR({ ...values, sp: combinedSp });
  // The guardrail band judges the ongoing rate; a one-off is shown as a separate year-one line.
  const { steadyWRpct, initWRpct, projectedPort, steadyDraw, initDrawEst, oneOffAtRetire, ssAtRetire, rentalAtRetire,
    otherIncAtRetire, housingAtRetire, carveoutAtRetire, annualAdds, accumRate, nominalRate, inflRate, yrsToRetire } = wr;
  const retireAge = values.retireAge || 65;
  const inSafeBand = steadyWRpct >= 5.0 && steadyWRpct <= 5.5;
  const wrColor = inSafeBand ? "var(--positive)" : (steadyWRpct > 5.5 ? "var(--chart-1)" : "var(--accent-gold)");

  const activeScenario = twoHousehold
    ? "Claiming non-residency — no state income tax"
    : `Resident of ${values.stateOfResidence || "your state"} — state tax applies`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ background: "rgba(14,165,233,0.08)", border: "1px solid var(--tint-sky-500-25)", borderRadius: 8, padding: "10px 14px", fontSize: "var(--fs-label)", color: "#7dd3fc" }}>
        <strong>State tax:</strong> {activeScenario} · Sidebar toggle → "Non-resident (no state tax)"
      </div>

      <ACard title="Withdrawal Strategy" accent="var(--accent-purple)">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{
            width: "100%",
            background: "#0d1b2a",
            border: "1px solid #1e3a5f",
            color: "var(--text-primary)",
            borderRadius: 6,
            padding: "8px 10px",
            fontSize: "var(--fs-body)",
            fontFamily: "var(--font-sans)",
          }}>
            {getStrategyLabel(strategy)}
          </div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.4 }}>{getStrategyDescription(strategy)}</div>
          {/* Real navigation: jumps to Analysis and lands directly on its
              Withdrawal Plan sub-tab (ScenariosTab's own sub-tab state is
              seeded from AiRAForecaster's pendingScenarioSubTab — see
              navigateToTab). There's no "Withdrawal Schedule" tab; that was
              the original bug — this pointer named a tab that never existed. */}
          <button
            type="button"
            onClick={() => onNavigateTab && onNavigateTab("scenarios", "withdrawals")}
            style={{
              fontSize: "var(--fs-small)", color: "var(--accent-teal)", background: "none", border: "none",
              padding: 0, cursor: onNavigateTab ? "pointer" : "default",
              textDecoration: onNavigateTab ? "underline" : "none", textAlign: "left",
            }}
            disabled={!onNavigateTab}
          >
            Change this on the Withdrawal Plan tab →
          </button>
        </div>
      </ACard>

      {/* Spending inputs and the budget uploader live together in the
          Spending & Expenses tab now. This compact summary keeps the
          number visible here (the WR diagnostic and guardrails below are
          calibrated to it). */}
      <ACard title="Spending" accent="var(--accent-teal)">
        <div style={{ padding: "10px 12px", background: "var(--tint-sky-06)", border: "1px solid var(--tint-sky-20)", borderRadius: 8, fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <span>
            Combined annual spending <span style={{ color: "var(--accent-gold)" }}>(after tax)</span>{values.spImportMeta ? (
              <> · <strong style={{ color: "#c4b5fd" }}>{values.spImportMeta.mode === "multi" ? "multi-year budget active" : "budget-driven"}</strong></>
            ) : null}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <strong style={{ color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace", fontSize: "var(--fs-body)" }}>{fmtDollar(combinedSp)}/yr</strong>
            {/* Same wizard, different step — index 3 is ExpensesPanel
                in ProfileWizard's PANELS array (see the "Spending &
                Expenses" STEPS entry it's paired with). */}
            <button
              type="button"
              onClick={() => onNavigateStep && onNavigateStep(3)}
              style={{
                fontSize: "var(--fs-small)", color: "#c4b5fd", background: "none", border: "none",
                padding: 0, cursor: onNavigateStep ? "pointer" : "default",
                textDecoration: onNavigateStep ? "underline" : "none",
              }}
              disabled={!onNavigateStep}
            >
              Edit in Spending &amp; Expenses →
            </button>
          </span>
        </div>
      </ACard>

      {/* Social Security moved to the Money In step, alongside
          contributions and pensions. Everything about it (primary + spouse
          + widow's penalty) lives there now — this card is a pointer, kept
          so users who learned to find SS here aren't left staring at a blank. */}
      <ACard title="Social Security" accent="#7c3aed">
        <div style={{ padding: "10px 12px", background: "var(--tint-violet-600-08)", border: "1px solid rgba(124,58,237,0.28)", borderRadius: 8, fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <span>Now in the <strong style={{ color: "#c4b5fd" }}>Money In</strong> step — SS is income, not a withdrawal setting.</span>
          {/* Index 2 is ContribPanel in ProfileWizard's PANELS array. */}
          <button
            type="button"
            onClick={() => onNavigateStep && onNavigateStep(2)}
            style={{
              fontSize: "var(--fs-small)", color: "#c4b5fd", background: "none", border: "none",
              padding: 0, cursor: onNavigateStep ? "pointer" : "default",
              textDecoration: onNavigateStep ? "underline" : "none",
            }}
            disabled={!onNavigateStep}
          >
            Edit in Money In →
          </button>
        </div>
      </ACard>

      {/* Rental / Airbnb moved to the Money In step alongside SS and
          pensions. Pointer left in place so the field's old home still
          says where to find it. */}
      <ACard title="Rental Income" accent="#295ff1" collapsible defaultOpen={false}>
        <div style={{ padding: "10px 12px", background: "rgba(41,95,241,0.08)", border: "1px solid rgba(41,95,241,0.28)", borderRadius: 8, fontSize: "var(--fs-label)", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <span>Now in the <strong style={{ color: "#c4b5fd" }}>Money In</strong> step — it is income, same as SS and pensions.</span>
          {/* Index 2 is ContribPanel in ProfileWizard's PANELS array. */}
          <button
            type="button"
            onClick={() => onNavigateStep && onNavigateStep(2)}
            style={{
              fontSize: "var(--fs-small)", color: "#c4b5fd", background: "none", border: "none",
              padding: 0, cursor: onNavigateStep ? "pointer" : "default",
              textDecoration: onNavigateStep ? "underline" : "none",
            }}
            disabled={!onNavigateStep}
          >
            Edit in Money In →
          </button>
        </div>
      </ACard>

      <ACard title="Strategy Detail" accent="var(--accent-purple)" desc="The parameters the strategy you picked above actually uses.">
        {strategy === "gk" && (
          <>
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", marginBottom: 12 }}>Guyton‑Klinger Guardrails</div>
            <div style={{
              marginBottom: 14,
              padding: "10px 12px",
              background: inSafeBand ? "var(--tint-green-07)" : "var(--tint-amber-08)",
              border: `1px solid ${withAlpha(wrColor, "40")}`,
              borderRadius: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-secondary)" }}>Initial portfolio draw rate</div>
                <div style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: wrColor, fontFamily: "'JetBrains Mono',monospace" }}>{steadyWRpct.toFixed(1)}%</div>
                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginLeft: "auto", fontStyle: "italic" }}>
                  {inSafeBand ? "in 5–5.5% safe band" : steadyWRpct > 5.5 ? "above safe band — consider lower spend or later retirement" : "below safe band — room to spend more or retire earlier"}
                </div>
              </div>
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.5 }}>
                spend {fmtDollar(wr.baseSpend)} − SS {fmtDollar(ssAtRetire)} − rental {fmtDollar(rentalAtRetire)} − other income {fmtDollar(otherIncAtRetire)} (floored at $0) + housing {fmtDollar(housingAtRetire)} + carveouts {fmtDollar(carveoutAtRetire)} = portfolio draw {fmtDollar(steadyDraw)}<br/>
                projected portfolio at age {retireAge} (today's dollars): {fmtDollar(projectedPort)}<br/>
                = current {fmtDollar(values.port || 0)} grown {yrsToRetire}yr @ {(accumRate * 100).toFixed(1)}% real ({(nominalRate * 100).toFixed(1)}% nominal − {(inflRate * 100).toFixed(1)}% infl) + {fmtDollar(annualAdds)}/yr contrib<br/>
                ⇒ {fmtDollar(steadyDraw)} ÷ {fmtDollar(projectedPort)} = <strong style={{ color: wrColor }}>{steadyWRpct.toFixed(1)}%</strong>
                {oneOffAtRetire > 0 && <><br/>year one also pays one-off expenses of {fmtDollar(oneOffAtRetire)}: {fmtDollar(initDrawEst)} ÷ {fmtDollar(projectedPort)} = {initWRpct.toFixed(1)}% that year only</>}
              </div>
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 16 }}>
              {guardFromImport
                ? <>Floor = <strong style={{ color: "var(--text-secondary)" }}>Must Spend</strong> · Ceiling = <strong style={{ color: "var(--text-secondary)" }}>Like to Spend</strong> (from your imported budget — the slider %s below are ignored)</>
                : <>Floor = {floorPct}% of core spend · Ceiling = {ceilingPct}% of core spend</>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 20, justifyContent: "center" }}>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 4 }}>Floor</div><div style={{ fontSize: 28, fontWeight: 700, color: "var(--accent-gold)", fontFamily: "'JetBrains Mono',monospace" }}>{guardFromImport ? fmtDollar(floor) : `${floorPct}%`}</div><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{guardFromImport ? "essentials / yr" : `${fmtDollar(floor)} / yr`}</div></div>
              <div style={{ width: 1, height: 30, background: "var(--tint-white-10)" }} />
              <div style={{ textAlign: "center" }}><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", marginBottom: 4 }}>Ceiling</div><div style={{ fontSize: 28, fontWeight: 700, color: "var(--positive)", fontFamily: "'JetBrains Mono',monospace" }}>{guardFromImport ? fmtDollar(ceiling) : `${ceilingPct}%`}</div><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>{guardFromImport ? "full budget / yr" : `${fmtDollar(ceiling)} / yr`}</div></div>
            </div>
            {/* The Floor/Ceiling inputs used to live here AND nowhere else. They
                are simulation guardrails, not plan facts, so they moved into
                the Monte Carlo tab's ⚙ Advanced Settings dialog (see
                MCAdvancedSettings) — the tab where you actually watch them
                bind. This card keeps the read-outs above, so nothing is lost
                here, and points at the one place they are now edited rather
                than keeping a second set of controls (rule 1: single point of
                control). */}
            <div style={{ marginTop: 14, padding: "10px 14px", background: "var(--tint-teal-600-06)", border: "1px solid rgba(13,148,136,0.2)", borderRadius: 8, fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.55 }}>
              Floor and ceiling are edited in <strong style={{ color: "var(--accent-teal)" }}>Monte Carlo → ⚙ Advanced Settings</strong> →
              Spending Guardrails, alongside the rest of the simulation settings. The values in force are shown above.
            </div>
            <div style={{ marginTop: 16, padding: "12px 14px", background: "rgba(14,165,233,0.06)", border: "1px solid var(--tint-sky-500-18)", borderRadius: 8, fontSize: "var(--fs-small)", color: "var(--text-secondary)", lineHeight: 1.55 }}>
              <div style={{ fontWeight: 700, color: "#7dd3fc", marginBottom: 6 }}>Guyton‑Klinger — the 5 rules</div>
              <div style={{ marginLeft: 8, marginBottom: 6 }}>
                <strong>1. Initial draw.</strong> Derived from your spending ÷ portfolio at retirement. Safe range is 5–5.5%.<br/>
                <strong>2. Capital Preservation.</strong> If WR rises 20% above initial → cut spending 10%.<br/>
                <strong>3. Prosperity.</strong> If WR falls 20% below initial → raise spending 10%.<br/>
                <strong>4. Inflation rule.</strong> Adjust spending by CPI only after positive-return years, capped at 6%.<br/>
                <strong>5. Longevity rule.</strong> Skip the −10% cut when ≤15 years remain to your end-age.
              </div>
              <div style={{ marginBottom: 4 }}>The <strong style={{ color: "var(--accent-gold)" }}>Floor</strong> and <strong style={{ color: "var(--positive)" }}>Ceiling</strong> sliders above are a safety belt on top of the 5 rules — spending can drift within those bands but never past them.</div>
              <div style={{ fontStyle: "italic", color: "var(--text-muted)" }}>Defaults: 65 / 135. Tighten (e.g. 80 / 120) to keep spending closer to plan; widen (e.g. 55 / 150) to allow larger swings.</div>
            </div>
          </>
        )}
        {strategy === "fixed" && (
          <>
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", marginBottom: 12 }}>Fixed Percentage Withdrawal</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 16 }}>Each year, withdraw a fixed percentage of the current portfolio balance.</div>
            <div style={{ textAlign: "center", marginBottom: 14 }}><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>Withdrawal Rate</div><div style={{ fontSize: "var(--fs-display)", fontWeight: 700, color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace" }}>{values.fixedWithdrawalRate || 4.0}%</div><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>of portfolio balance each year</div></div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <WFieldRow label="Withdrawal Rate" helper="Annual percentage of portfolio to withdraw (default 4%).">
                <ANumInput value={values.fixedWithdrawalRate ?? 4.0} onSet={(v) => onChange("fixedWithdrawalRate", v)} min={2} max={10} step={0.1} suffix="%" />
              </WFieldRow>
            </div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 16, fontStyle: "italic", textAlign: "center" }}>Spending will fluctuate with portfolio value.</div>
          </>
        )}
        {strategy === "vpw" && (
          <>
            <div style={{ fontSize: "var(--fs-body)", color: "var(--text-primary)", marginBottom: 12 }}>Variable Percentage Withdrawal</div>
            <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 16 }}>Each year, the portfolio is amortized over the years remaining to your plan-to age — so the plan is designed to spend down to roughly zero, not to leave an estate.</div>
            <div style={{ display: "flex", alignItems: "center", gap: 20, justifyContent: "center" }}>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>Assumed real return</div><div style={{ fontSize: 28, fontWeight: 700, color: "var(--accent-gold)", fontFamily: "'JetBrains Mono',monospace" }}>{(((values.vpwRealReturn ?? 0.0376)) * 100).toFixed(2)}%</div></div>
              <div style={{ width: 1, height: 30, background: "var(--tint-white-10)" }} />
              <div style={{ textAlign: "center" }}><div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>Amortized to age</div><div style={{ fontSize: 28, fontWeight: 700, color: "var(--positive)", fontFamily: "'JetBrains Mono',monospace" }}>{values.vpwEndAge ?? values.endAge ?? 100}</div></div>
            </div>
          </>
        )}
        {!["gk", "fixed", "vpw"].includes(strategy) && (
          <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", textAlign: "center" }}>{getStrategyLabel(strategy)} strategy active — see documentation for details.</div>
        )}
      </ACard>
    </div>
  );
}



// Landing quick-estimate.
// A simple heuristic on purpose, used only on the visitor landing so the
// sliders feel alive. The real 3,000-path engine takes over the moment
// they enter the app.
const LANDING_SS_ANNUAL = 30000;      // rough real Social Security from 67
const LANDING_REAL_RETURN = 0.045;    // real, after inflation
const LANDING_TARGET = 0.85;          // confidence goal

function landingProject(retireAge, s) {
  const n = Math.max(0, retireAge - s.curAge);
  const grow = Math.pow(1 + LANDING_REAL_RETURN, n);
  const port = s.savings * grow + (n > 0 ? s.contrib * (grow - 1) / LANDING_REAL_RETURN : 0);
  const ss = retireAge >= 67 ? LANDING_SS_ANNUAL : LANDING_SS_ANNUAL * 0.55;
  const need = Math.max(0, s.spend - ss);
  const wr = port > 0 ? need / port : 1;
  const conf = 1 / (1 + Math.exp((wr - 0.042) / 0.006));
  return { port, conf: Math.max(0.02, Math.min(0.985, conf)) };
}
// Search runs to the top of the retire-age range, not a hardcoded 72, so
// the hero can still answer for someone planning to work past 72.
function landingEarliestAge(s) {
  for (let a = Math.max(s.curAge + 1, 50); a <= AGE_LIMITS.retire.max; a++) {
    if (landingProject(a, s).conf >= LANDING_TARGET) return a;
  }
  return null;
}

// Past the normal retirement window the "when can you retire" question is
// the wrong one — the user is already retired (or about to be), and what
// they want to know is whether the money lasts. The hero flips to that
// framing rather than searching future ages that don't apply.
// `landingProject(curAge, ...)` is exactly "retire now," which is the
// right projection for someone drawing down today.
const LANDING_ALREADY_RETIRED_AGE = 66;
function landingIsRetired(s) {
  return s.curAge >= LANDING_ALREADY_RETIRED_AGE;
}

// Age at which the drawdown path hits zero, for the already-retired
// headline. Walks the same path the chart draws, so the number and the
// curve agree. "95+" is the cap because the landing model is a teaser, not
// the real engine — anything beyond that should be answered by the actual
// Monte Carlo.
function landingLastsToAge(s) {
  const pts = landingPath(s, s.curAge);
  const dead = pts.find((p) => p.age > s.curAge && p.val <= 0);
  return dead ? dead.age : "95+";
}
function landingPath(s, retireAge) {
  const pts = [];
  let p = s.savings;
  for (let a = s.curAge; a <= 90; a++) {
    pts.push({ age: a, val: Math.max(0, p) });
    if (a < (retireAge ?? 65)) p = p * (1 + LANDING_REAL_RETURN) + s.contrib;
    else {
      const ss = a >= 67 ? LANDING_SS_ANNUAL : LANDING_SS_ANNUAL * 0.55;
      p = p * (1 + LANDING_REAL_RETURN) - Math.max(0, s.spend - ss);
    }
  }
  return pts;
}
/* Landing input scale.
 * The hero has to serve someone with $200K and someone with $200M on the
 * same 700px track. A linear range can't: at a $999B max each pixel was
 * worth about $1.4B, so the smallest possible nudge threw savings into the
 * billions and the readout leapt from "$850K" to "$40000M." Lowering the
 * max just recreates the original complaint — that nobody could enter more
 * than $5M.
 *
 * Two independent fixes, because they solve two different problems:
 *   1. The slider is logarithmic. Pixels buy percentage change, not
 *      dollars, so one step moves about 0.8% wherever you are: $853K →
 *      $860K, $40.1M → $40.4M. The whole range stays reachable and every
 *      part of it stays precise.
 *   2. The readout is a typed field, so the slider's ceiling is a
 *      convenience bound, not a limit. Type $400M and it takes it; the
 *      thumb just pins at the end. Same contract as the `Slider` component above.
 * Values snap to 3 significant figures so dragging yields $1,250,000
 * rather than $1,253,881 — a number a person would actually say out loud. */
const LANDING_LIMITS = {
  savings: { min: 50_000, max: 250_000_000 },
  contrib: { min:      0, max:   5_000_000 },
  spend:   { min: 40_000, max:   5_000_000 },
};
const LANDING_SLIDER_STEPS = 1000;   // thumb positions, not dollars
const LANDING_LOG_FLOOR = 1_000;     // log(0) is -Infinity; curve starts here

function roundSig(v, digits) {
  if (!(v > 0)) return 0;
  const mag = Math.pow(10, Math.floor(Math.log10(v)) - (digits - 1));
  return Math.round(v / mag) * mag;
}
function landingPosToVal(pos, min, max) {
  if (pos <= 0) return min;
  const lo = Math.log(Math.max(LANDING_LOG_FLOOR, min));
  const hi = Math.log(max);
  return roundSig(Math.exp(lo + (hi - lo) * (pos / LANDING_SLIDER_STEPS)), 3);
}
function landingValToPos(val, min, max) {
  // A typed value may exceed `max` — pin the thumb rather than reject the number.
  const v = Math.max(min, Math.min(max, val || 0));
  if (v <= 0) return 0;
  const lo = Math.log(Math.max(LANDING_LOG_FLOOR, min));
  const hi = Math.log(max);
  return Math.round(((Math.log(Math.max(LANDING_LOG_FLOOR, v)) - lo) / (hi - lo)) * LANDING_SLIDER_STEPS);
}

/* The hero readout, typed. This is what lets someone enter $180M without
 * the slider needing a $180M-wide linear range: the field is the source of
 * truth and the slider is a fast approximate control over the common
 * range. A typed value above `max` is accepted and the thumb pins at the
 * end. `min` is still enforced — a negative portfolio is meaningless, not
 * merely unusual. */
function LandingMoneyField({ row }) {
  // `draft` is non-null only while focused: during editing we show
  // exactly what was typed so the formatter doesn't fight the caret, and
  // on commit we go back to the formatted display.
  //
  // The draft is mirrored in a ref because Escape has to beat the blur
  // that follows it. Escape clears the draft and blurs; blur then fires
  // `commit`, which would still see the pre-Escape draft in React state
  // and commit the very edit Escape just canceled. The ref is already null
  // by then, so commit correctly no-ops. (`Slider` above solves the same
  // race with a functional setState updater; a ref does it without calling
  // a parent setter from inside an updater function.)
  const [draft, setDraft] = useState(null);
  const draftRef = useRef(null);
  const put = (v) => { draftRef.current = v; setDraft(v); };
  const commit = () => {
    const d = draftRef.current;
    if (d === null) return;
    put(null);
    const n = parseMoneyInput(d, row.min);
    if (n !== null) row.set(n);   // unparseable text leaves the value untouched
  };
  return (
    <input
      className="lp-val lp-val-input"
      type="text"
      inputMode="decimal"
      aria-label={row.label}
      title="Click to type an exact value — you can enter more than the slider's range"
      value={draft !== null ? draft : row.fmt(row.val)}
      onFocus={(e) => { put(String(row.val)); selectAllOnFocus(e); }}
      onChange={(e) => put(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { commit(); e.currentTarget.blur(); }
        else if (e.key === "Escape") { put(null); e.currentTarget.blur(); }
      }}
    />
  );
}

// First-screen landing shown to visitors with no saved profile. Answers the one
// question — "when can I retire?" — then routes into the full app via onEnter.
function RetirementLanding({ onEnter }) {
  const [curAge, setCurAge] = useState(45);
  const [savings, setSavings] = useState(850000);
  const [contribL, setContribL] = useState(40000);
  const [spend, setSpend] = useState(90000);
  const canvasRef = useRef(null);
  const s = { curAge, savings, contrib: contribL, spend };
  const retired = landingIsRetired(s);
  // Already retired ⇒ the projection IS "drawing down from today", so the
  // reference age is their current age rather than a future retirement date.
  const age = retired ? s.curAge : landingEarliestAge(s);
  const conf = (age != null
    ? landingProject(age, s)
    : landingProject(AGE_LIMITS.retire.max, s)).conf;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const W = c.clientWidth || 700, H = 120;
    c.width = W * dpr; c.height = H * dpr;
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const pts = landingPath(s, age);
    const pad = 6, maxV = Math.max(...pts.map((p) => p.val), 1);
    const x = (i) => pad + (W - pad * 2) * i / (pts.length - 1);
    const y = (v) => H - pad - (H - pad * 2) * (v / maxV);
    ctx.strokeStyle = "rgba(255,255,255,0.06)"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pad, H - pad); ctx.lineTo(W - pad, H - pad); ctx.stroke();
    if (age != null) {
      const idx = pts.findIndex((p) => p.age === age);
      if (idx >= 0) {
        const rx = x(idx);
        ctx.strokeStyle = "var(--text-faint)"; ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(rx, pad); ctx.lineTo(rx, H - pad); ctx.stroke(); ctx.setLineDash([]);
      }
    }
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "rgba(90,209,240,0.28)"); g.addColorStop(1, "rgba(90,209,240,0.02)");
    ctx.beginPath(); pts.forEach((p, i) => i === 0 ? ctx.moveTo(x(i), y(p.val)) : ctx.lineTo(x(i), y(p.val)));
    ctx.lineTo(x(pts.length - 1), H - pad); ctx.lineTo(x(0), H - pad); ctx.closePath(); ctx.fillStyle = g; ctx.fill();
    ctx.beginPath(); pts.forEach((p, i) => i === 0 ? ctx.moveTo(x(i), y(p.val)) : ctx.lineTo(x(i), y(p.val)));
    ctx.strokeStyle = "var(--accent-teal)"; ctx.lineWidth = 2.5; ctx.lineJoin = "round"; ctx.stroke();
  }, [curAge, savings, contribL, spend, age]);

  // `money: false` keeps the age row on a plain linear slider with no typed
  // field — an age is two digits and the range is 25–85, so neither fix applies.
  const rows = [
    { key: "age", label: "Current age", val: curAge, set: setCurAge,
      min: AGE_LIMITS.current.min, max: AGE_LIMITS.current.max, money: false, fmt: (v) => v },
    { key: "savings", label: "What you've saved so far", val: savings, set: setSavings,
      ...LANDING_LIMITS.savings, money: true, fmt: fmtDollar },
    { key: "contrib", label: "Adding each year", val: contribL, set: setContribL,
      ...LANDING_LIMITS.contrib, money: true, fmt: fmtDollar },
    { key: "spend", label: "Spending in retirement", val: spend, set: setSpend,
      ...LANDING_LIMITS.spend, money: true, fmt: (v) => fmtDollar(v) + "/yr" },
  ];

  return (
    <div className="landing">
      <div className="lp-wrap">
        <div className="lp-brand">
          <div className="logo">AiRA <span className="logo-sub">Freedom Financial</span></div>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)" }}>v{APP_VERSION}</div>
        </div>

        <div className="lp-eyebrow">Your one-number answer</div>
        <h1 className={"lp-answer" + (age != null && conf < 0.9 ? " short" : "")}>
          {retired
            ? <>Your money lasts to <span className="lp-age">{landingLastsToAge(s)}</span></>
            : age != null
              ? <>You can retire at <span className="lp-age">{age}</span></>
              : <>Let's find your <span className="lp-age">number</span></>}
        </h1>
        <p className="lp-sub">
          {age != null
            ? <>Based on your savings and spending, the math says you reach financial independence{" "}
                <b style={{ color: "var(--text-primary)" }}>{age - curAge <= 0 ? "right now" : `${age - curAge} year${age - curAge === 1 ? "" : "s"} from now`}</b>
                {conf >= 0.92 ? " — with real margin." : " — with a modest cushion."}</>
            : <>No age up to {AGE_LIMITS.retire.max} clears 85% confidence at these numbers yet — try saving more, or trimming the spend.</>}
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 38, fontWeight: 500, color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>{Math.round(conf * 100)}</span>
            <span style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", fontWeight: 600 }}>% confident<br />at that age</span>
          </div>
          <div style={{ flex: 1, minWidth: 200, height: 10, borderRadius: 100, background: "var(--tint-white-06)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -3, bottom: -3, left: "85%", width: 2, background: "var(--text-faint)" }} />
            <div style={{ height: "100%", borderRadius: 100, width: `${Math.min(100, Math.round(conf * 100))}%`, background: "var(--accent-teal)", transition: "width 0.35s" }} />
          </div>
        </div>

        <div style={{ marginTop: 26 }}>
          <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 6, fontWeight: 700 }}>Median portfolio path</div>
          <canvas ref={canvasRef} style={{ width: "100%", height: 120, display: "block" }} />
        </div>

        <div className="lp-panel">
          {rows.map((r) => (
            <div key={r.key}>
              <div className="lp-row">
                <span className="lp-label">{r.label}</span>
                {r.money
                  ? <LandingMoneyField row={r} />
                  : <span className="lp-val">{r.fmt(r.val)}</span>}
              </div>
              {r.money ? (
                <input className="lp-range" type="range" min={0} max={LANDING_SLIDER_STEPS} step={1}
                  aria-label={r.label}
                  value={landingValToPos(r.val, r.min, r.max)}
                  onChange={(e) => r.set(landingPosToVal(+e.target.value, r.min, r.max))} />
              ) : (
                <input className="lp-range" type="range" min={r.min} max={r.max} step={1}
                  aria-label={r.label}
                  value={r.val} onChange={(e) => r.set(+e.target.value)} />
              )}
            </div>
          ))}
        </div>

        <button className="lp-cta" onClick={() => onEnter(s)}>Open the app</button>
        <button className="lp-skip" onClick={() => onEnter(null)}>Skip — I'll enter my own details</button>

        <p style={{ marginTop: 32, fontSize: "var(--fs-label)", color: "var(--text-faint)", lineHeight: 1.6, maxWidth: "62ch" }}>
          This is a 60-second estimate. The full app runs {MC_PATHS_LABEL} market simulations across your real
          accounts, taxes, Social Security, and withdrawal order — that's where your true plan lives.
        </p>
      </div>
    </div>
  );
}

/**
 * Show a toast and auto-dismiss it. Returns the timer id so the caller's
 * effect can clear it on unmount/re-fire. Errors get a longer dwell than
 * successes — a failed purchase or restore is something the user may need
 * to read twice or copy into a support email.
 */
function setToastTimed(setter, toast, ms) {
  setter(toast);
  return setTimeout(() => setter(null), ms);
}

export default function AiRAForecaster() {
  const [activeTab, setTab] = useState(() =>
    loadProfileFromLocal() ? "networth" : "assumptions"
  );
  // Monte Carlo's own two views (Vincent, 2026-09-24): "Simulation" holds the
  // verdict + results + fan chart + band table + the per-run input audit trail;
  // "Guardrails" holds the spending-flex analysis. They were one long scroll
  // with the guardrails section wedged between the result and its assumptions.
  // State lives here, not in MCTab, because the fan chart and the band table are
  // SHELL-level children of this tab — a sub-tab bar inside MCTab could not hide
  // them, and the Guardrails view would have appeared underneath both.
  const [mcView, setMcView] = useState("simulation");
  // Sidebar accordion — independently collapsible, not a true single-open
  // accordion, since these are live input controls a user may be actively
  // adjusting in more than one section at once. Starts collapsed so the
  // sidebar opens as a short list of section headers rather than three
  // screens of stacked sliders; opening one is a click. Which sections are
  // open is a per-user view choice and changes nothing that's simulated.
  const [profileSavedBefore] = useState(() => !!loadProfileFromLocal());
  const [sbCoreOpen, setSbCoreOpen] = useState(() => !loadProfileFromLocal()); // first run: show the basics
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const skipAutosave = useRef(false);
  const [sbMacroOpen, setSbMacroOpen] = useState(false);
  const [sbOptionsOpen, setSbOptionsOpen] = useState(false);
  // Cross-tab "Edit this on the X tab" pointers (e.g. RetirementPanel's
  // Withdrawal Strategy card) need to land on a specific sub-tab inside
  // ScenariosTab, not just the top-level tab. ScenariosTab's own sub-tab
  // state is local and resets on mount, so it reads this as its initial
  // value — set right before switching activeTab, consumed once, then
  // cleared so a later manual visit to Analysis doesn't get silently redirected.
  const [pendingScenarioSubTab, setPendingScenarioSubTab] = useState(null);
  const [profileJump, setProfileJump] = useState(null);
  // profileStep: ProfileWizard step index to open (1 = Current Savings). Optional.
  const navigateToTab = useCallback((tab, subTab = null, profileStep = null) => {
    if (subTab) setPendingScenarioSubTab(subTab);
    if (profileStep != null) setProfileJump({ step: profileStep, n: Date.now() });
    setTab(tab);
  }, []);
  // Sidebar launchers switch the tab AND bring the editor into view. On a
  // phone the tab bar sits below the fold, so a bare tab switch changed
  // nothing on screen and the button read as dead.
  const focusLever = useCallback((key) => {
    setSbCoreOpen(true);
    setTimeout(() => { // after the card has opened
      const el = document.querySelector(`[data-lever="${key}"]`), sb = el?.closest(".sidebar");
      if (!el) return;
      if (sb.getBoundingClientRect().top < 0) window.scrollTo({ top: 0 }); // phone: sidebar sits above the editor
      sb.scrollTop += el.getBoundingClientRect().top - sb.getBoundingClientRect().top - 60;
      el.querySelector("input")?.focus({ preventScroll: true });
    }, 0);
  }, []);
  // A clickable section title that also works from the keyboard.
  const twisty = (open, fn) => ({
    role: "button", tabIndex: 0, "aria-expanded": open, onClick: fn,
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } },
  });
  const tabsRef = useRef(null);
  const openPlanInputs = useCallback((step = null) => {
    navigateToTab("assumptions", null, step);
    const tabs = tabsRef.current, main = tabs?.parentElement;
    if (!tabs) return;
    // Desktop: only the main column scrolls. scrollIntoView would also drag
    // the page up under the sticky header and clip the sidebar.
    if (main.scrollHeight > main.clientHeight) main.scrollTop += tabs.getBoundingClientRect().top - main.getBoundingClientRect().top;
    else tabs.scrollIntoView({ block: "start" });
  }, [navigateToTab]);
  // The visitor landing is the homepage for anyone without a saved profile
  // — not a one-time splash. It used to also be gated on a
  // `aira_welcomed_v1` flag, so the moment a visitor clicked through (or
  // hit Skip) the page became permanently unreachable: no link anywhere
  // returns to it. A visitor who bounced and came back tomorrow never saw
  // it again despite never having entered a single number. Gating on the
  // saved profile alone means unregistered visitors always land here,
  // while anyone who has saved a profile goes straight to their dashboard
  // and never sees it. Dismissal still holds for the session (React
  // state), so entering the app is a one-click, non-repeating action.
  const [showWelcome, setShowWelcome] = useState(() => !loadProfileFromLocal());
  // True for the session that arrived through the quick estimate: the app then
  // owes the visitor a list of what it assumed beyond their four answers.
  const [fromLanding, setFromLanding] = useState(false);
  const [running, setRunning] = useState(false);
  const [stale, setStale] = useState(false);
  // Opening the app presents the last run you asked for, rather than spending
  // a simulation to redraw numbers you have already seen. Read once, here,
  // because the staleness check below needs the inputs it was computed from.
  const [cachedRun] = useState(loadResultsFromLocal);
  const [mc, setMc] = useState(cachedRun?.mc ?? null);
  const [stress, setStress] = useState(cachedRun?.stress ?? null);
  // Nothing recomputes on its own any more, so say when these numbers are from.
  const [lastRunAt, setLastRunAt] = useState(cachedRun?.savedAt ?? null);
  // Shared hover state so hovering a row in the age-band table highlights the
  // matching age column on the fan chart above (FanChart + MCBandTable are siblings).
  const [hoveredAge, setHoveredAge] = useState(null);


  // Progress check-ins: a journal of plan snapshots (see LS_CHECKINS_KEY).
  // Requires a completed MC run so the snapshot carries a real success rate.
  const [checkIns, setCheckIns] = useState(loadCheckIns);
  const [checkInFlash, setCheckInFlash] = useState(false);

  const handleSaveCheckIn = () => {
    if (!mc) return;
    const entry = {
      id: `ci_${Date.now()}`,
      ts: new Date().toISOString(),
      successRate: mc.rate,
      stressRate: stress?.rate ?? null,
      port,
      sp,
      retireAge: retAge,
      endAge,
      // eslint-disable-next-line no-restricted-properties -- a permanent point-in-time snapshot written once on Check-in, not a live display re-derived on render.
      medianTerminal: mc.term?.p50 ?? null,
      appVersion: APP_VERSION,
    };
    const next = [...checkIns, entry];
    setCheckIns(next);
    saveCheckIns(next);
    setCheckInFlash(true);
    setTimeout(() => setCheckInFlash(false), 2000);
  };
  const handleDeleteCheckIn = (id) => {
    const next = checkIns.filter((c) => c.id !== id);
    setCheckIns(next);
    saveCheckIns(next);
  };
  const handleRenameCheckIn = (id, name) => {
    const next = checkIns.map((c) => (c.id === id ? { ...c, name } : c));
    setCheckIns(next);
    saveCheckIns(next);
  };
  const handleImportCheckIns = (imported) => {
    const next = mergeCheckIns(checkIns, imported);
    setCheckIns(next);
    saveCheckIns(next);
  };

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [showReport, setShowReport] = useState(false);
  // Re-checked from the server each time the report is opened, so a window that
  // expired (or was unlocked on another device) is picked up without a reload.
  const reportUnlocked = useReportUnlocked(showReport ? 1 : 0);
  // Fail-closed capability probe (GEMINI_API_KEY presence) — see
  // functions/api/report-capability.js. Independent of BILLING_ENABLED so
  // flipping that source-level flag can no longer unlock the report for free
  // on a deployment that isn't actually the operator's own.
  const reportCapable = useReportCapability();
  // Owner preview: true only after /api/admin verified ADMIN_SECRET
  // server-side this session (see admin-panel.js). Lets me read the real
  // report without the purchase prompt end users see. `?aira_admin=1` on
  // its own doesn't grant this — the param is in the bundle, the secret isn't.
  const ownerVerified = useOwnerVerified();
  // { msg, tone: "ok" | "err" }. Failures have to be visible: a silent
  // failure on a paid return or a restore link leaves the customer stuck
  // with no idea why, and no way to tell us what went wrong. Errors persist
  // longer than successes.
  const [stripeToast, setStripeToast] = useState(null);
  const stripeReturn  = useStripeReturn();
  const restoreReturn = useRestoreReturn();
  // Recovery link: opened once on a successful purchase, and re-openable from the
  // credit panel. `null` = closed; an object = showing that link.
  const [recoveryLink, setRecoveryLink] = useState(null);
  useEffect(() => {
    if (stripeReturn?.success && stripeReturn.restoreUrl) {
      setRecoveryLink({ url: stripeReturn.restoreUrl });
    }
  }, [stripeReturn]);
  useEffect(() => {
    if (!stripeReturn) return;
    // A report buyer is granted zero credits by design, so the credits
    // wording told someone who had just paid $9 that they received "0
    // credits added to your account" — the most alarming possible sentence
    // at that moment. Branch on what was actually bought.
    const reportBuy = stripeReturn.packId === "report";
    // The poll above already waited about 12s for the entitlement. If
    // it's still not there the webhook hasn't landed, and a cheerful
    // "unlocking shortly" leaves someone who's just paid staring at a
    // paywall with no idea what to do — the one failure mode worth
    // designing for, because it's the only one that takes money and
    // delivers nothing. Stripe retries, and the grant is idempotent, so it
    // usually self-heals; say that, and give them a route out if it doesn't.
    // Amber, not green: this isn't a completed transaction yet.
    const stalled = reportBuy && !stripeReturn.reportUnlocked;
    const okMsg = reportBuy
      ? (stripeReturn.reportUnlocked
          // Names the next step, because Stripe's redirect is a full page
          // load: the entitlement and the saved profile both survive it,
          // but `mc` doesn't, so the Report button lands disabled. A buyer
          // seeing "unlocked" beside a greyed-out button concludes they
          // paid for nothing — and the only existing hint is a title= on a
          // disabled button, which most browsers never render.
          ? "Report unlocked — it's yours permanently. Press ▶ Run Monte Carlo, then Report."
          : `Payment received — your report is taking longer than usual to unlock. Reload this page in a minute. If it is still locked, email ${FEEDBACK_EMAIL} with your Stripe receipt and we will open it straight away.`)
      : `${(stripeReturn.credits || 0).toLocaleString()} credits added to your account`;
    const t = stripeReturn.success
      ? setToastTimed(setStripeToast, { msg: okMsg, tone: stalled ? "warn" : "ok" }, stalled ? 20000 : 5000)
      : setToastTimed(setStripeToast, { msg: `${stripeReturn.error}`, tone: "err" }, 15000);
    return () => clearTimeout(t);
  }, [stripeReturn]);
  useEffect(() => {
    if (!restoreReturn) return;
    const t = restoreReturn.success
      ? setToastTimed(setStripeToast, { msg: `Account restored — ${restoreReturn.credits.toLocaleString()} credits available`, tone: "ok" }, 6000)
      : setToastTimed(setStripeToast, { msg: `${restoreReturn.error}`, tone: "err" }, 15000);
    return () => clearTimeout(t);
  }, [restoreReturn]);
  // Signature of the inputs the last run used; null until the first run is
  // launched. "Results are stale" means the current inputs differ from these.
  const ranSigRef = useRef(cachedRun?.sig ?? null);
  const runningRef = useRef(false);
  const sigRef = useRef(null);
  // Set true when the visitor enters from the landing so the next params
  // update (after seeding) triggers one real Monte Carlo run with their numbers.
  const pendingRunRef = useRef(false);

  // Slider states – initialized from BLANK_PROFILE
  const [port, setPort] = useState(BLANK_PROFILE.port);
  const [contrib, setContrib] = useState(BLANK_PROFILE.contrib);
  const [inf, setInf] = useState(BLANK_PROFILE.inf);
  const [retAge, setRetAge] = useState(BLANK_PROFILE.retireAge);
  const [endAge, setEndAge] = useState(BLANK_PROFILE.endAge);
  const [sp, setSp] = useState(BLANK_PROFILE.sp);
  const [ssb, setSsb] = useState(BLANK_PROFILE.ssb);
  const [ab, setAb] = useState(BLANK_PROFILE.ab);
  const [smile, setSmile] = useState(BLANK_PROFILE.smile);
  const [tax, setTax] = useState(BLANK_PROFILE.tax);
  const [useAb, setUseAb] = useState(BLANK_PROFILE.useAb);
  const [real, setReal] = useState(BLANK_PROFILE.real);
  const [withdrawalStrategy, setWithdrawalStrategy] = useState(BLANK_PROFILE.withdrawalStrategy || "gk");

  // Assumptions state – all user data lives here
  const [assumptions, setAssumptions] = useState(() => {
    // Try to load saved profile on initialization
    const saved = loadProfileFromLocal();
    if (saved) {
      return {
        ...BLANK_PROFILE,
        ...saved,
        accounts: saved.accounts || BLANK_PROFILE.accounts,
        properties: saved.properties || BLANK_PROFILE.properties,
        checkpoints: saved.checkpoints || BLANK_PROFILE.checkpoints,
        carveouts: saved.carveouts || BLANK_PROFILE.carveouts,
        otherIncomes: saved.otherIncomes || BLANK_PROFILE.otherIncomes,
      };
    }
    return { ...BLANK_PROFILE };
  });

  const dismissWelcome = () => {
    setShowWelcome(false);
  };

  const updateAssumption = useCallback(
    (key, val) => setAssumptions((prev) => ({ ...prev, [key]: val, ...(key === "dob" ? { dobIsEstimate: false } : {}) })),
    []
  );

  // Auto‑load saved profile on mount (already handled in useState initializer, but sync sliders)
  useEffect(() => {
    const saved = loadProfileFromLocal();
    if (!saved) return;

    if (saved.retireAge !== undefined) setRetAge(saved.retireAge);
    if (saved.endAge !== undefined) setEndAge(saved.endAge);
    if (saved.port !== undefined) setPort(saved.port);
    if (saved.contrib !== undefined) setContrib(saved.contrib);
    if (saved.inf !== undefined) setInf(saved.inf);
    if (saved.sp !== undefined) setSp(saved.sp);
    if (saved.ssb !== undefined) setSsb(saved.ssb);
    if (saved.ab !== undefined) setAb(saved.ab);
    if (saved.useAb !== undefined) setUseAb(saved.useAb);
    if (saved.smile !== undefined) setSmile(saved.smile);
    if (saved.tax !== undefined) setTax(saved.tax);
    if (saved.real !== undefined) setReal(saved.real);
    if (saved.withdrawalStrategy !== undefined) setWithdrawalStrategy(saved.withdrawalStrategy);
  }, []);

  // Current age: one value, everywhere.
  // `dob` is the only input of record. This derives from it, and the
  // effect below writes the result back into assumptions.currentAge so
  // that every consumer — the engines via params, every Profile panel via
  // `values`, the AI context builders, the exported JSON — reads the same
  // number. Nothing should compute its own age from dob, and nothing
  // should read a stored age that could disagree with the birthday on
  // file. The stored field survives only so dob-less imported profiles keep working.
  const currentAge = useMemo(
    () => ageFromDob(assumptions.dob) ?? assumptions.currentAge ?? BLANK_PROFILE.currentAge,
    [assumptions.dob, assumptions.currentAge]
  );

  // The complete profile exactly as persisted: `assumptions` merged with
  // the slider states that live outside it (port, sp, retAge, ...). One
  // source of truth for both the manual Save button (ProfileWizard) and
  // the autosave effect below — so a forgotten manual save and an autosave
  // can never write different snapshots, and sliders that call setPort/
  // setSp without touching assumptions still get captured.
  const liveProfile = useMemo(() => ({
    ...assumptions,
    currentAge,
    retireAge: retAge,
    endAge,
    port,
    contrib,
    sp,
    ssAge: assumptions.ssAge,
    ssb,
    ab,
    withdrawalStrategy: assumptions.withdrawalStrategy,
  }), [assumptions, currentAge, retAge, endAge, port, contrib, sp, ssb, ab]);

  // Silent autosave.
  // Debounced persistence of the full profile so a user who forgets to
  // hit Save never loses their work — it auto-restores on next load via
  // loadProfileFromLocal(). Reuses the proven saveProfileToLocal() path
  // (same key, same schema, same private-mode guard) that the manual Save uses.
  //   - Skips the initial hydration render so mount doesn't redundantly re-save.
  //   - Stays dormant on the visitor welcome screen, so an untouched
  //     blank profile never creates a phantom "saved profile" that would
  //     suppress the landing page on the next visit.
  const autosaveReady = useRef(false);
  const [lastAutosaveAt, setLastAutosaveAt] = useState(null);
  useEffect(() => {
    if (!autosaveReady.current) { autosaveReady.current = true; return; }
    if (showWelcome) return;
    const t = setTimeout(() => {
      if (skipAutosave.current) return; // Start over in progress
      if (saveProfileToLocal(liveProfile)) setLastAutosaveAt(new Date());
    }, 1000);
    return () => clearTimeout(t);
  }, [liveProfile, showWelcome]);

  // Keep the stored field reconciled with dob. Without this, editing a
  // birthday updated the simulation (which reads the derived value) while
  // every panel and chart reading assumptions.currentAge kept showing the
  // old age. Guarded by the inequality so it settles in one pass.
  useEffect(() => {
    const derived = ageFromDob(assumptions.dob);
    if (derived != null && derived !== assumptions.currentAge) {
      updateAssumption("currentAge", derived);
    }
  }, [assumptions.dob, assumptions.currentAge]);

  const rmdAge = useMemo(() => {
    const override = assumptions.rmdStartAge;
    if (typeof override === "number" && override > 0) return override;
    return getRmdStartAge({ dob: assumptions.dob, currentAge });
  }, [assumptions.dob, assumptions.rmdStartAge, currentAge]);

  // Main params object for simulations – uses assumptions, NOT BLANK_PROFILE
  const params = useMemo(
    () => ({
      dob: assumptions.dob || "",
      rmdStartAge: assumptions.rmdStartAge,
      currentAge,
      retireAge: retAge,
      endAge,
      ssAge: assumptions.ssAge,
      ssPia: assumptions.ssPia || 0,
      // Spread wholesale so newly-added spouse fields (dob, and deathAge
      // below) can't be silently dropped here — this memo is where dead
      // settings get born if you're not careful. `dob` in the default
      // keeps the shape identical to BLANK_PROFILE.
      spouse: assumptions.spouse || {
        enabled: false, dob: "", ssb: 0, ssAge: 67, ssPia: 0,
        deathAge: null, firstToDie: "spouse",
        survivorClaimAge: null, survivorBenefitAtClaim: 0,
        // Kept in the fallback purely so this object stays shape-identical
        // to BLANK_PROFILE.spouse, as the comment above requires. The
        // engines already default these to 0 via jobContributionsForYear.
        retireAge: null, contrib: 0, employerContrib: 0, rothContrib: 0,
      },
      port,
      contrib,
      employerContrib: assumptions.employerContrib || 0,
      hsaContrib: Math.round((assumptions.hsaMonthly || 0) * 12),
      // Has to be forwarded here or the Profile inputs are a no-op — the
      // engines read `params`, not `assumptions`. (Same trap the sourcing
      // guardrails hit.)
      taxableContrib: assumptions.taxableContrib || 0,
      rothContrib: assumptions.rothContrib || 0,
      accounts: assumptions.accounts,
      // Portfolio draw = US + out-of-country (always combined). The
      // state-tax toggle is independent now: twoHousehold on means
      // "claiming non-residency" and skips state tax, but doesn't swap the
      // spending value.
      sp: (sp || 0) + (assumptions.spOutOfCountry || assumptions.spSpendOutofState || 0),
      spOutOfCountry: assumptions.spOutOfCountry || assumptions.spSpendOutofState || 0,
      spSpendOutofState: assumptions.spSpendOutofState,   // legacy passthrough
      // Floor/ceiling: an imported Must/Like budget drives these; else % of core spend.
      ...resolveSpendGuardrails({
        sp,
        spOutOfCountry: assumptions.spOutOfCountry || assumptions.spSpendOutofState || 0,
        gkFloorPct: assumptions.gkFloorPct ?? GK_FLOOR_DEFAULT_PCT,
        gkCeilingPct: assumptions.gkCeilingPct ?? GK_CEILING_DEFAULT_PCT,
        spImportMeta: assumptions.spImportMeta,
      }),
      ssb,
      propIncome: (() => {
         const raw = (assumptions.properties || []).reduce((s, pr) => s + (Number(pr.income) || 0), 0);
          return isNaN(raw) ? 0 : raw;
        })(),
      ab: (assumptions.properties || []).some(pr => Number(pr.income) > 0) ? 0 : (assumptions.ab || 0),
      useAb,
      abReliability: assumptions.abReliability,
      abGrowth: assumptions.abGrowth,
      ssCola: assumptions.ssCola,
      inf,
      smile,
      tax,
      real,
      filingStatus: assumptions.filingStatus || "mfj",
      stateOfResidence: assumptions.stateOfResidence,
      twoHousehold: assumptions.twoHousehold,
      mortBalance: assumptions.mortBalance || 0,
      mortRate: assumptions.mortRate ?? 6.5,
      mortStart: assumptions.mortStart || "2020-01",
      mortTerm: assumptions.mortTerm || 30,
      mortExtra: assumptions.mortExtra || 0,
      properties: assumptions.properties || [],
      reGrowthRate: assumptions.reGrowthRate ?? 3.0,
      housingType: assumptions.housingType || "own",
      annualRent: assumptions.annualRent || 0,
      carveouts: assumptions.carveouts || [],
      // Has to be forwarded — the engines read `params`, not `assumptions`.
      cashFlowEvents: assumptions.cashFlowEvents || [],
      spSchedule: (assumptions.spSchedule && assumptions.spSchedule.length) ? assumptions.spSchedule : null,
      rothConversionTarget: (() => { const r = assumptions.rothConversionTarget || "off"; return r.startsWith("fill_") ? r.replace("fill_", "") : r; })(),
      taxFunding: assumptions.taxFunding || "from_taxable",
      fafsaGuard: assumptions.fafsaGuard || false,
      fafsaEndYear: assumptions.fafsaEndYear || null,
      cssEndYear: assumptions.cssEndYear || null,
      conversionOverrides: assumptions.conversionOverrides || [],
      preRetireEq: assumptions.preRetireEq,
      preRetireReturn: assumptions.preRetireReturn ?? null,
      postRetireEq: assumptions.postRetireEq,
      // Has to be forwarded — the engines read `params`, not `assumptions`.
      // null keeps the default (switch at retireAge).
      glidepathSwitchAge: assumptions.glidepathSwitchAge ?? null,
      hcShockAge: assumptions.hcShockAge,
      hcProb: assumptions.hcProb,
      hcMin: assumptions.hcMin,
      hcMax: assumptions.hcMax,
      cashRealReturn: assumptions.cashRealReturn ?? 3.0,
      // Mortality weighting for runMC's "money outlives you" metric.
      sex: assumptions.sex || "blended",
      taxableBasisPct: assumptions.taxableBasisPct ?? 70,
      taxableYieldPct: assumptions.taxableYieldPct ?? 0,
      taxableYieldOrdinaryPct: assumptions.taxableYieldOrdinaryPct ?? 50,
      useJointRmdTable: assumptions.useJointRmdTable || false,
      withdrawalStrategy: assumptions.withdrawalStrategy,
      // Set when a retired strategy was remapped on load. Forwarded so the
      // Withdrawal tab can say the plan changed — a silent remap would be
      // the same "app knew something the screen didn't" problem as the
      // strategy deletion itself.
      withdrawalStrategyMigratedFrom: assumptions.withdrawalStrategyMigratedFrom ?? null,
      // Sourcing guardrails — have to be forwarded here or runMC + the
      // Withdrawal Plan tab never see them (they live in `assumptions`,
      // but the engine reads `params`). Defaults mirror BLANK_PROFILE.
      withdrawalBracketTarget: assumptions.withdrawalBracketTarget || "22",
      irmaaGuard: assumptions.irmaaGuard || false,
      rothEmergencyReserve: assumptions.rothEmergencyReserve || 0,
      ruleOf55: assumptions.ruleOf55 || false,
      sepp72t: assumptions.sepp72t || false,
      sepp72tStartAge: assumptions.sepp72tStartAge ?? null,
      ssTorpedoGuard: assumptions.ssTorpedoGuard || false,
      // Account draw order (which bucket drains first) — orthogonal to strategy + guardrails.
      orderingMode: assumptions.orderingMode || "tax_reactive",
      withdrawalOrder: assumptions.withdrawalOrder || ["cash", "taxable", "pretax", "roth"],
      b1Years: assumptions.b1Years ?? 2,
      b2Years: assumptions.b2Years ?? 5,
      fixedWithdrawalRate: (() => { const r = assumptions.fixedWithdrawalRate || 4.0; return r < 1 ? r : r / 100; })(), // normalize: stored as % (4) or decimal (0.04) → always decimal
      // VPW's two inputs. This memo is an allowlist, not a spread —
      // neither used to be forwarded, so `vpwRealReturn` could be set in
      // the profile and the engine would still use its own 3.76% default.
      // That matters now: the 1/N → VPW migration writes vpwRealReturn: 0,
      // and without these two lines it would be a dead setting.
      // `?? null` (not `||`) so a deliberate 0 survives.
      vpwRealReturn: assumptions.vpwRealReturn ?? null,
      vpwEndAge: assumptions.vpwEndAge ?? null,
      safeWithdrawalRate: 0.04,
      checkpoints: assumptions.checkpoints || [],
      earlyRetireTarget: assumptions.earlyRetireTarget,
      portfolioGoal: assumptions.portfolioGoal,
      abEndYear: assumptions.abEndYear ?? null,   // rental income stops after this year
      otherIncomes: assumptions.otherIncomes || [],
    }),
    [
      retAge, endAge, port, contrib, inf, sp, ssb, ab, useAb, smile, tax, real,
      assumptions, currentAge,
    ]
  );

  const mortgageSched = useMemo(
    () =>
      mortgageSchedule(
        assumptions.mortBalance || 0,
        assumptions.mortRate ?? 6.5,
        assumptions.mortStart || "2020-01",
        assumptions.mortTerm || 30,
        assumptions.mortExtra || 0
      ),
    [assumptions.mortBalance, assumptions.mortRate, assumptions.mortStart, assumptions.mortTerm, assumptions.mortExtra]
  );

const mortgagePayoffYear = mortgageSched.payoffYr;

  // Read through a ref so this callback is stable for the life of the app.
  // Effects that trigger a run depend on it, and a callback that changed
  // identity with every params object would re-fire them on renders where
  // nothing the simulation reads actually changed.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const runSimulation = useCallback(() => {
    runningRef.current = true;
    ranSigRef.current = sigRef.current;
    setRunning(true);
    setStale(false);
    // Yield once so React can commit and paint "Running..." before the engine
    // blocks the main thread. Zero delay, and deliberately not a debounce:
    // nothing here waits for quiet, coalesces callers, or cancels a pending
    // run. (requestAnimationFrame is the tempting alternative but paints the
    // indicator only some of the time when a run starts from an effect.)
    setTimeout(() => {
      // Single horizon: every simulation is graded to the profile's own
      // plan age (params.endAge). No hardcoded reference ages. A shorter
      // runway with the same funds correctly scores higher, and the stress
      // test shares the same horizon.
      const p = paramsRef.current;
      // Record the inputs this run actually used, not the ones it was queued
      // with — anything edited before this frame is included in the result.
      ranSigRef.current = sigRef.current;
      const planAge = p.endAge || 90;
      // Path count is user-adjustable (MC tab → ⚙ Advanced Settings); the
      // default is the MC_PATHS constant, so an untouched profile runs exactly
      // as before. Clamped here rather than trusted, because this value
      // arrives from a persisted profile that a hand-edit could malform.
      const paths = Math.max(MC_PATH_MIN, Math.min(MC_PATH_MAX, Math.round(p.mcPaths || MC_PATHS)));
      const rEnd_ = runMC(p, planAge, paths, 43, true);
      const str = runStress(p, planAge, STRESS_PATHS, 99);
      setMc(rEnd_);
      setStress(str);
      // Keep it for the next visit, tagged with the inputs it came from.
      saveResultsToLocal(sigRef.current, rEnd_, str);
      setLastRunAt(new Date().toISOString());
      runningRef.current = false;
      setRunning(false);
    }, 0);
  }, []);

  // "Inputs changed" has to mean the numbers the simulation actually reads
  // changed value. `assumptions` carries fields params doesn't use and gets
  // rewritten with settings that don't affect a run, so keying off the params
  // object itself would mark perfectly good results stale — visibly, right
  // after entering from the landing page.
  const paramsSig = useMemo(() => {
    try { return JSON.stringify(params); } catch { return String(Date.now()); }
  }, [params]);
  sigRef.current = paramsSig;

  // The Run/Re-run button owns every run. The single exception is a visitor
  // with nothing to show: no cached run from last time and nothing run yet
  // this session, where an empty screen would be worse than one simulation.
  // (This used to auto-re-run on a 350ms debounce after any params change,
  // which is what failed on slower machines: a full MC_PATHS-path simulation
  // on the main thread after every slider nudge.)
  useEffect(() => {
    // Landing page is up; there are no real inputs to run on yet.
    if (showWelcome) return;

    if (ranSigRef.current === null) {
      // Nothing cached and nothing run. A seeded landing entry runs itself,
      // on the seeded numbers, in the effect just below.
      if (!pendingRunRef.current) runSimulation();
      return;
    }

    // A queued run reads the latest inputs when it fires, so it already
    // covers this change — flagging stale here would be a lie it then clears.
    if (runningRef.current) return;

    // Compare against the inputs the last run used, so restoring a saved
    // profile, or undoing an edit, doesn't leave a "changed" warning up for
    // someone who changed nothing.
    setStale(paramsSig !== ranSigRef.current);
  }, [paramsSig, runSimulation, showWelcome]);

  // Year-end deadline prompt. Reads the browser clock (this app has no
  // server), so a PC with a wrong system date sees it at the wrong time —
  // accepted, versus adding a network dependency for a reminder. Placed
  // after `params` because it reads it; hoisting it above would be a
  // temporal-dead-zone error.
  //
  // The waterfall is built inside the December branch only, so eleven
  // months of the year this memo costs one getMonth() call and nothing else.
  const yearEndInfo = useMemo(() => {
    const now = new Date();
    // ?yearend=1 forces the December view year-round. A seasonal feature
    // is otherwise unreviewable for eleven months, and "change your PC
    // clock" is a terrible way to QA something that writes to localStorage.
    let forced = false;
    try { forced = new URLSearchParams(window.location.search).get("yearend") === "1"; } catch { /* SSR/test */ }
    if (!forced && !isYearEndWindow(now)) return { show: false };
    const year = now.getFullYear();
    let rows = [];
    try { rows = waterfallForActiveStrategy(params)?.smart?.rows || []; } catch { rows = []; }
    return {
      show: true,
      year,
      days: daysLeftInTaxYear(now),
      room: yearEndTaxRoom(rows, {
        year,
        bracketCeiling: getBracketCeiling,
        irmaaCeiling: getIrmaaCeiling,
        filingStatus: params.filingStatus || "mfj",
        // Prefer the conversion target — this prompt is mostly about conversions —
        // and fall back to the sourcing bracket cap, then the 22% default.
        target: params.rothConversionTarget && params.rothConversionTarget !== "off"
          ? params.rothConversionTarget
          : (params.withdrawalBracketTarget && params.withdrawalBracketTarget !== "off"
              ? params.withdrawalBracketTarget
              : "22"),
      }),
    };
  }, [params]);
  // Popup fires once per calendar year; the Net Worth strip stays all December.
  const [showYearEndModal, setShowYearEndModal] = useState(() => {
    let forced = false;
    try { forced = new URLSearchParams(window.location.search).get("yearend") === "1"; } catch { /* SSR/test */ }
    if (forced) return true;   // preview always opens, ignoring the once-a-year ack
    return isYearEndWindow() && !yearEndAcked(new Date().getFullYear());
  });

 const swr = computeInitialWR(params).steadyWRpct.toFixed(1);   // one-offs would make one year read as the plan's rate
 const analogue = mc ? getAnalogue(mc.rate) : null;

  const TABS = [
    ["networth", "Net Worth"],
    ["montecarlo", "Forecast"],
    ["scenarios", "Analysis"],
    ["actionplan", "Action Plan"],
    ["assumptions", "Plan inputs"],
  ];

  const needsMC = ["montecarlo", "networth"];
  const hasMC = !!mc;

  const handleSendFeedback = () => {
    if (!feedbackType) {
      alert("Please select a feedback type.");
      return;
    }
    // Hands off to the visitor's own mail client. Doesn't close the dialog
    // or clear the fields on purpose: if no mail client is configured
    // nothing visible happens, and closing would silently destroy what
    // they wrote. Leaving it open keeps both the text and the address on screen.
    const subject = `AiRA feedback — ${feedbackType}`;
    const body = [
      feedbackText || "(no details entered)",
      "",
      "—",
      feedbackName  ? `From: ${feedbackName}`   : null,
      feedbackEmail ? `Reply to: ${feedbackEmail}` : null,
      `AiRA v${APP_VERSION}`,
    ].filter(Boolean).join("\n");
    window.location.href =
      `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // After a landing "Take me to the app!" seed, run one real MC on the fresh params.
  useEffect(() => {
    if (pendingRunRef.current) {
      pendingRunRef.current = false;
      runSimulation();
    }
  }, [paramsSig, runSimulation]);

  // Seed the profile from the landing's quick-estimate inputs, then enter the app.
  const enterFromLanding = (inp) => {
    if (inp) {
      const yr = new Date().getFullYear();
      setPort(inp.savings);
      updateAssumption(
        "accounts",
        (assumptions.accounts || BLANK_PROFILE.accounts).map((a) =>
          a.id === "1" ? { ...a, balance: inp.savings } : { ...a, balance: 0 }
        )
      );
      setContrib(inp.contrib);
      setSp(inp.spend);
      updateAssumption("dob", `${yr - inp.curAge}-06-15`);
      updateAssumption("dobIsEstimate", true); // June 15 is a placeholder, not a birthday
      // The landing's headline is "You can retire at N". Open the app on that
      // same N, or the two screens describe different plans.
      const landingAge = landingIsRetired(inp) ? null : landingEarliestAge(inp);
      if (landingAge != null) { setRetAge(landingAge); updateAssumption("retireAge", landingAge); }
      setStale(true);
      pendingRunRef.current = true;
      setFromLanding(true);
    }
    dismissWelcome();
    setTab(inp ? "networth" : "assumptions");
  };

  // --- RENDER ---
  if (showWelcome) {
    return (
      <>
        <style>{CSS}</style>
        <RetirementLanding onEnter={enterFromLanding} />
      </>
    );
  }

  return (
    <NavContext.Provider value={navigateToTab}><LeverContext.Provider value={focusLever}>
    <>
      <style>{CSS}</style>
      <div className="app">
        <div className="hdr">
          <div>
            <h1 className="logo">
              AiRA <span className="logo-sub">Freedom Financial</span>
            </h1>
            <div style={{ fontSize: "var(--fs-label)", color: "var(--text-faint)" }}>
              A DIY retirement forecaster · v{APP_VERSION}
            </div>
          </div>
          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            <button
              className="mbtn"
              disabled={!mc}
              title={mc ? "Generate a printable CFP/CPA-ready report" : "Run Monte Carlo first"}
              onClick={() => setShowReport(true)}
            >
              Report
            </button>
            <button
              className="mbtn"
              title={mc
                ? "Snapshot today's plan (success rate, portfolio, spending) to your Progress journal — Analysis → Progress"
                : "Run Monte Carlo first — a check-in snapshots your current success rate"}
              onClick={handleSaveCheckIn}
              disabled={!mc}
              style={!mc ? { opacity: 0.45, cursor: "default" } : checkInFlash ? { color: "var(--accent-teal)" } : undefined}
            >
              {checkInFlash ? "Saved!" : "Check-in"}
            </button>
            <button
              className="mbtn"
              title="Export profile to JSON"
              onClick={() =>
                exportProfile(
                  {
                    // Spread all assumptions fields so nothing is ever silently omitted
                    ...assumptions,
                    // Override with the separate state variables — these are the live source of truth
                    retireAge: retAge,
                    endAge,
                    port,
                    contrib,
                    inf,
                    sp,
                    ssb,
                    ab: (assumptions.properties || []).some(pr => Number(pr.income) > 0) ? 0 : ab,
                    useAb,
                    smile,
                    tax,
                    real,
                    rothConversionTarget: assumptions.rothConversionTarget || "off",
                    fafsaEndYear: assumptions.fafsaEndYear || null,
                    cssEndYear: assumptions.cssEndYear || null,
                    geminiApiKey: assumptions.geminiApiKey || "",
                    // Progress check-ins ride along with the profile. They
                    // aren't part of `assumptions` — they live in their own
                    // localStorage key (LS_CHECKINS_KEY), so the "spread
                    // everything so nothing is silently omitted" comment
                    // above wasn't true of them. A user who exported their
                    // profile, moved machines and imported it lost the
                    // entire journal with no warning, because the only
                    // export that carried it was a second button on the
                    // Progress tab.
                    checkIns,
                    // Bucket Strategy tab settings (b1Years/b2Years/
                    // drawMode) are also not part of `assumptions` —
                    // BucketsTab owns them in its own localStorage key
                    // (_BCFG_KEY) instead of lifted root state, same gap
                    // the checkIns comment above already describes. Read
                    // fresh here rather than threading bCfg through root
                    // state for one export call.
                    bCfg: _loadBCfg(),
                    savedAt: new Date().toISOString(),
                    exportedAt: new Date().toISOString(),
                    appVersion: APP_VERSION,
                  },
                  assumptions.name
                    ? `AiRA_Profile_${assumptions.name.trim().replace(/[^A-Za-z0-9_-]+/g, "_")}`
                    : "AiRA_Profile"
                )
              }
            >
              Export
            </button>
            <button
              className="mbtn"
              title="Import profile from JSON"
              onClick={() =>
                importProfile((rawData) => {
                  // Same retired-strategy migration the localStorage path
                  // gets. An exported JSON is the other way an old profile
                  // enters the app, and it shouldn't take a different route.
                  const data = migrateOrderingMode(migrateWithdrawalStrategy(rawData));
                  // Merged, never replaced — same rule the Progress tab's
                  // own import uses (handleImportCheckIns). Importing a
                  // profile onto a machine that already has a journal
                  // shouldn't delete history that only exists there;
                  // mergeCheckIns dedupes by id and re-sorts by date.
                  if (Array.isArray(data.checkIns)) handleImportCheckIns(data.checkIns);
                  // Merged into existing local config, never replaced
                  // wholesale — same rule as checkIns above, so an older
                  // export missing a newer bCfg field (e.g. drawMode) can't
                  // reset it back to default on import.
                  if (data.bCfg && typeof data.bCfg === "object") {
                    try { localStorage.setItem(_BCFG_KEY, JSON.stringify({ ..._loadBCfg(), ...data.bCfg })); } catch {}
                  }
                  if (data.retireAge !== undefined) setRetAge(data.retireAge);
                  if (data.endAge !== undefined) setEndAge(data.endAge);
                  if (data.port !== undefined) setPort(data.port);
                  if (data.contrib !== undefined) setContrib(data.contrib);
                  if (data.inf !== undefined) setInf(data.inf);
                  if (data.sp !== undefined) setSp(data.sp);
                  if (data.ssAge !== undefined) updateAssumption("ssAge", data.ssAge);
                  if (data.ssb !== undefined) setSsb(data.ssb);
                  if (data.ab !== undefined) {
                    const hasPropIncome = (data.properties || []).some(pr => Number(pr.income) > 0);
                    setAb(hasPropIncome ? 0 : data.ab);
                  }
                  if (data.useAb !== undefined) setUseAb(data.useAb);
                  if (data.smile !== undefined) setSmile(data.smile);
                  if (data.tax !== undefined) setTax(data.tax);
                  if (data.real !== undefined) setReal(data.real);
                  if (data.withdrawalStrategy !== undefined) setWithdrawalStrategy(data.withdrawalStrategy);

                  // Migrate old account fields if needed
                  // Legacy import migration. The keys below are the wire
                  // format of profiles saved before the accounts array
                  // existed — renaming them would stop those files
                  // importing. The display names are generic on purpose:
                  // labeling an account with its custodian would tell
                  // anyone reading this public repo where my money is held.
                  if (data.solo401k !== undefined && !data.accounts) {
                    data.accounts = [
                      ...(data.solo401k ? [{ id: "m1", category: "pretax", name: "Pre-Tax 401k", balance: data.solo401k }] : []),
                      ...(data.alpha401k ? [{ id: "m2", category: "pretax", name: "Pre-Tax 401k (2)", balance: data.alpha401k }] : []),
                      ...(data.rothFid ? [{ id: "m3", category: "roth", name: "Roth IRA", balance: data.rothFid }] : []),
                      ...(data.rothVgd ? [{ id: "m4", category: "roth", name: "Roth IRA (2)", balance: data.rothVgd }] : []),
                      ...(data.hsaBal ? [{ id: "m5", category: "hsa", name: "HSA", balance: data.hsaBal }] : []),
                      ...(data.taxable ? [{ id: "m6", category: "taxable", name: "Taxable", balance: data.taxable }] : []),
                    ];

                    delete data.solo401k;
                    delete data.alpha401k;
                    delete data.rothFid;
                    delete data.rothVgd;
                    delete data.hsaBal;
                    delete data.taxable;
                  }
                  if (!Array.isArray(data.accounts)) data.accounts = BLANK_PROFILE.accounts;
                  if (data.port === undefined) {
                    const acctTotal = data.accounts.reduce((s, a) => s + (a.balance || 0), 0);
                    if (acctTotal > 0) setPort(acctTotal);
                  }
                  if (data.mortStart && !data.mortStart.includes("-01")) data.mortStart = data.mortStart + "-01";
                 
                  if (!Array.isArray(data.properties)){
                     data.properties = BLANK_PROFILE.properties;
                  } else {
                      data.properties = data.properties.map((p, i) =>
                      p.id ? { ...p, id: String(p.id) } : { ...p, id: "p" + (i + 1) }
                 );
                }
                // --Carveouts and checkpoints should always be arrays, and checkpoints should have string IDs for React keys--
                  if (!Array.isArray(data.carveouts)) data.carveouts = [];
                  if (!Array.isArray(data.otherIncomes)) data.otherIncomes = [];
                  // Detailed-expense import: schedule must be a non-empty [{year,amount}] array or null.
                  if (!Array.isArray(data.spSchedule) || data.spSchedule.length === 0) {
                    data.spSchedule = null;
                    if (data.spImportMeta && data.spImportMeta.mode === "multi") data.spImportMeta = null;
                  }
                  if (!Array.isArray(data.checkpoints)) {
                    data.checkpoints = [];
                  } else {
                    data.checkpoints = data.checkpoints.map(cp =>
                      cp.id != null ? { ...cp, id: String(cp.id) } : { ...cp, id: Date.now().toString() + Math.random() }
                    );
                  }

                  // Migrate old "otherInc" field to "otherIncomes"
                  if (data.otherInc && !data.otherIncomes) {
                    data.otherIncomes = data.otherInc;
                  }
                  // Ensure otherIncomes is always an array
                  if (!Array.isArray(data.otherIncomes)) {
                    data.otherIncomes = [];
                  }

                  const hasPropIncome = (data.properties || []).some(pr => Number(pr.income) > 0);
                  // rothConversionTarget round-trips as "fill_22"/"off"/"irmaa". Legacy exports
                  // (pre-fix) stripped to bare bracket numbers like "22" -- re-prefix them so
                  // the dropdown finds a matching option.
                  const rawRct = data.rothConversionTarget || "off";
                  const restoredRct = /^\d+$/.test(rawRct) ? "fill_" + rawRct : rawRct;
                  setAssumptions((prev) => ({
                    ...prev,
                    ...data,
                    name: data.name || "",
                    dob: data.dob || "",
                    dobIsEstimate: !!data.dobIsEstimate,
                    stateOfResidence: data.stateOfResidence || "NJ",
                    filingStatus: data.filingStatus || "mfj",
                    // Match BLANK_PROFILE's fresh-profile default (false)
                    // so an older export missing this field doesn't
                    // silently flip to zero state tax vs. a hand-entered
                    // profile with identical data.
                    twoHousehold: data.twoHousehold ?? false,
                    portfolioGoal: data.portfolioGoal ?? 3_200_000,
                    earlyRetireTarget: data.earlyRetireTarget ?? 3_500_000,
                    accounts: data.accounts,
                    properties: data.properties,
                    checkpoints: data.checkpoints,
                    carveouts: data.carveouts,
                    otherIncomes: data.otherIncomes,
                    ab: hasPropIncome ? 0 : (data.ab || 0),
                    rothConversionTarget: restoredRct,
                    fafsaEndYear: data.fafsaEndYear || null,
                    cssEndYear: data.cssEndYear || null,
                    // Preserve existing API key if imported file has empty/missing key
                    geminiApiKey: data.geminiApiKey || prev.geminiApiKey || "",
                  }));

                  setStale(true);
                  dismissWelcome();
                  setTab("networth");
                  alert(`Profile loaded${data.name ? ` for ${data.name}` : ""}. Press ▶ Run Monte Carlo to update.`);
                })
              }
            >
              Import
            </button>
            {/* Two groups, one divider: what acts on YOUR PLAN (read it, record it,
                save it, load it), then what is about THE APP. */}
            <div aria-hidden="true" style={{ width: 1, height: 20, background: "var(--tint-white-14)", margin: "0 5px" }} />
            <HelpButton />
            <AboutButton />
            <div style={{ position: "relative", display: "inline-flex" }}>
              <button
                className="mbtn"
                onClick={() => setShowFeedback((prev) => !prev)}
                title="Send feedback"
              >
                Feedback
              </button>
              {showFeedback && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 6,
                    background: "#0f2138",
                    border: "1px solid #1e3a5f",
                    borderRadius: 10,
                    padding: 14,
                    width: 300,
                    zIndex: 999,
                    boxShadow: "0 8px 32px var(--tint-black-50)",
                  }}
                >
                  <div style={{ fontSize: "var(--fs-label)", color: "var(--text-primary)", fontWeight: 600, marginBottom: 10 }}>
                    How's AiRA working for you?
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    {[
                      { emoji: "👍", label: "Great", type: "praise" },
                      { emoji: "💡", label: "Idea", type: "suggestion" },
                      { emoji: "🐛", label: "Bug", type: "bug" },
                      { emoji: "👎", label: "Issue", type: "issue" },
                    ].map((fb) => (
                      <button
                        key={fb.type}
                        onClick={() => setFeedbackType(fb.type)}
                        style={{
                          flex: 1,
                          padding: "6px 4px",
                          borderRadius: 6,
                          cursor: "pointer",
                          border: feedbackType === fb.type ? "1px solid #9a9cff" : "1px solid var(--tint-white-08)",
                          background: feedbackType === fb.type ? "rgba(167,139,250,0.15)" : "var(--card-bg)",
                          transition: "all 0.15s",
                        }}
                      >
                        <div style={{ fontSize: "var(--fs-h2)" }}>{fb.emoji}</div>
                        <div style={{ fontSize: "var(--fs-small)", color: feedbackType === fb.type ? "var(--accent-purple)" : "var(--text-muted)", marginTop: 2 }}>
                          {fb.label}
                        </div>
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Tell us more (optional)..."
                    rows={3}
                    style={{
                      width: "100%",
                      background: "#0a1628",
                      border: "1px solid #1e3a5f",
                      color: "var(--text-primary)",
                      borderRadius: 6,
                      padding: "8px 10px",
                      fontSize: "var(--fs-small)",
                      fontFamily: "'DM Sans',sans-serif",
                      resize: "vertical",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "var(--accent-purple)")}
                    onBlur={(e) => (e.target.style.borderColor = "#1e3a5f")}
                  />
                  {/* Optional contact fields */}
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {[
                      { val: feedbackName, set: setFeedbackName, placeholder: "Name (optional)" },
                      { val: feedbackEmail, set: setFeedbackEmail, placeholder: "Email (optional)" },
                    ].map(({ val, set, placeholder }) => (
                      <input
                        key={placeholder}
                        type="text"
                        value={val}
                        onChange={e => set(e.target.value)}
                        placeholder={placeholder}
                        style={{
                          flex: 1, background: "#0a1628", border: "1px solid #1e3a5f",
                          color: "var(--text-primary)", borderRadius: 6, padding: "6px 8px",
                          fontSize: "var(--fs-small)", fontFamily: "'DM Sans',sans-serif", outline: "none",
                        }}
                        onFocus={e => e.target.style.borderColor = "var(--accent-purple)"}
                        onBlur={e => e.target.style.borderColor = "#1e3a5f"}
                      />
                    ))}
                  </div>
                  {/* Shown, not just linked: a mailto does nothing on a
                      machine with no mail client, and a visitor should
                      never be left with a dead button and no address. */}
                  <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 8, lineHeight: 1.5 }}>
                    Opens your email app. Or write to{" "}
                    <span style={{ color: "var(--accent-purple)", fontFamily: "'JetBrains Mono',monospace", userSelect: "all" }}>
                      {FEEDBACK_EMAIL}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                    <button
                      onClick={() => setShowFeedback(false)}
                      style={{ background: "transparent", border: "none", color: "var(--text-faint)", fontSize: "var(--fs-small)", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendFeedback}
                      style={{
                        padding: "5px 16px",
                        borderRadius: 6,
                        border: "none",
                        background: "linear-gradient(135deg,#7c3aed,#9a9cff)",
                        color: "white",
                        fontSize: "var(--fs-small)",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "'DM Sans',sans-serif",
                      }}
                    >
                      Open email
                    </button>
                  </div>
                </div>
              )}
            </div>
            <a
              href="https://buymeacoffee.com/axwacki"
              target="_blank"
              rel="noopener noreferrer"
              className="coffee-btn"
              title="Support the app — buy me a coffee"
            >
              Buy me a coffee
            </a>
          </div>
          {/* Header day-counter removed — the same figure lives inside
              the D-Day Countdown Panel in the sidebar (bottom of this
              file), so showing it twice was redundant. With this block
              gone, .hdr's justify-content:space-between pushes the buttons
              flush right. */}
        </div>

        <div className="layout">
          <div className="sidebar">
            {/* The launcher stands alone above the cards: inside the countdown
                card it read as that card's action, not as the way into every
                input. It also says when you are already there, instead of
                staying a loud call to action that appears to do nothing. */}
            <button type="button" className="cfg-btn cfg-launch" onClick={() => openPlanInputs()}
              aria-current={activeTab === "assumptions" ? "page" : undefined}>
              {activeTab === "assumptions" ? "Editing plan inputs" : `${profileSavedBefore ? "Adjust values" : "Configure your plan"} →`}
            </button>

            <div className="sb-card">
              <div className="sb-title" {...twisty(sbCoreOpen, () => setSbCoreOpen(v => !v))}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                <span>{sbCoreOpen ? "▾" : "▸"} Plan basics</span>
              </div>
              {sbCoreOpen && (<>
              <Slider
                dataLever="retireAge"
                label="Retire age"
                value={retAge}
                min={AGE_LIMITS.retire.min}
                max={AGE_LIMITS.retire.max}
                step={1}
                stepNudge={1}
                format={(v) => "Age " + v}
                onChange={setRetAge}
              />
              <Slider
                dataLever="endAge"
                label="Plan to age"
                value={endAge}
                min={AGE_LIMITS.end.min}
                max={AGE_LIMITS.end.max}
                step={1}
                stepNudge={1}
                format={(v) => "Age " + v}
                onChange={setEndAge}
              />
              <Slider
                dataLever="sp"
                label="US annual spend"
                value={sp}
                min={0}
                max={300000}
                step={1000}
                stepNudge={5000}
                format={(v) => fmtDollar(v) + "/yr"}
                onChange={setSp}
              />
              <Slider
                label="Annual savings"
                value={contrib}
                min={0}
                max={100000}
                step={500}
                stepNudge={1000}
                format={(v) => fmtDollar(v) + "/yr"}
                onChange={setContrib}
              />
              <Slider
                key={`ssAge-${assumptions.ssAge}`}
                dataLever="ssAge"
                label="SS claim age"
                value={assumptions.ssAge}
                min={AGE_LIMITS.ss.min}
                max={AGE_LIMITS.ss.max}
                step={1}
                stepNudge={1}
                quickPills={[
                  { lbl: "62 (Early)", val: 62 },
                  { lbl: "67 (FRA)", val: 67 },
                  { lbl: "70 (Max)", val: 70 },
                ]}
                format={(v) => "Age " + v}
                onChange={(v) => updateAssumption("ssAge", v)}
              />
              </>)}
            </div>
            {/* The landing shows a rule-of-thumb answer; the app shows the full
                simulation, which needs more than four answers. Say what was
                filled in on the visitor's behalf, each line a way to fix it —
                otherwise the two screens just look like they disagree. Only
                assumptions that still hold are listed. */}
            {fromLanding && (() => {
              const funded = (assumptions.accounts || []).filter((a) => (a.balance || 0) > 0);
              const items = [
                assumptions.dobIsEstimate && [`Born mid-${String(assumptions.dob || "").slice(0, 4)}`, () => openPlanInputs(0)],
                funded.length === 1 && [`All ${fmtDollar(port)} in one ${ACCOUNT_CATEGORIES.find((c) => c.key === funded[0].category)?.label || "account"} account`, () => openPlanInputs(PROFILE_STEP_SAVINGS)],
                ssb === BLANK_PROFILE.ssb && [`Social Security of ${fmtDollar(ssb)}/yr from age ${assumptions.ssAge}`, () => openPlanInputs(PROFILE_STEP_MONEY_IN)],
                retAge === BLANK_PROFILE.retireAge && [`Retiring at ${retAge}, planning to age ${endAge}`, () => focusLever("retireAge")],
              ].filter(Boolean);
              if (!items.length) return null;
              return (
                <div className="sb-card" data-testid="we-assumed">
                  <div className="sb-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>We assumed</span>
                    <button type="button" className="sb-edit" style={{ textTransform: "none", letterSpacing: 0 }} onClick={() => setFromLanding(false)}>Hide</button>
                  </div>
                  <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 8 }}>
                    The quick estimate was a rule of thumb. These results come from the full simulation, using your four answers plus the assumptions below. Change any that are wrong.
                  </div>
                  {items.map(([text, go]) => (
                    <button key={text} type="button" className="assume-row" onClick={go}>
                      <span>{text}</span><span aria-hidden="true">Change</span>
                    </button>
                  ))}
                </div>
              );
            })()}
            <div className="sb-card">
              <CountdownCard
                dob={assumptions.dob}
                retireAge={retAge}
                dobIsEstimate={!!assumptions.dobIsEstimate}
                employerStartDate={assumptions.employerStartDate}
                name={assumptions.name}
                onConfigure={() => openPlanInputs(0)}
              />
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: "1px solid var(--tint-white-06)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>Liquid Portfolio{" "}
                  <button type="button" className="sb-edit" onClick={() => openPlanInputs(PROFILE_STEP_SAVINGS)} aria-label="Edit accounts and balances">Edit accounts</button>
                </span>
                <span style={{ fontSize: "var(--fs-h2)", fontWeight: 700, color: "var(--accent-teal)", fontFamily: "'JetBrains Mono',monospace", letterSpacing: "-0.5px" }}>
                  {fmtDollar(port)}
                </span>
              </div>
              {(() => {
                const props = assumptions.properties || [];
                const propValue = props.reduce((s, pr) => s + (pr.value || 0), 0);
                const propDebt  = props.reduce((s, pr) => s + (pr.mortgage || 0), 0);
                const netWorth  = port + propValue - propDebt;
                if (propValue === 0) return null;
                return (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6 }}>
                    <span style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>Net Worth <span style={{ color: "var(--text-faint)" }}>(+RE equity)</span></span>
                    <span style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "var(--accent-purple)", fontFamily: "'JetBrains Mono',monospace" }}>
                      {fmtDollar(netWorth)}
                    </span>
                  </div>
                );
              })()}
              <AllocationDonut accounts={assumptions.accounts} onEdit={() => openPlanInputs(PROFILE_STEP_SAVINGS)} />
              <div style={{ textAlign: "right", marginTop: 4 }}>
                <button type="button" className="sb-edit" onClick={() => openPlanInputs(PROFILE_STEP_SAVINGS)} aria-label="Edit account split">Edit split</button>
              </div>
              {yearEndInfo.show && (
                <YearEndStrip room={yearEndInfo.room} days={yearEndInfo.days} year={yearEndInfo.year} />
              )}
            </div>
            {yearEndInfo.show && showYearEndModal && (
              <YearEndModal
                room={yearEndInfo.room} days={yearEndInfo.days} year={yearEndInfo.year}
                canCheckIn={!!mc}
                onSaveCheckIn={handleSaveCheckIn}
                onClose={() => { ackYearEnd(yearEndInfo.year); setShowYearEndModal(false); }}
              />
            )}
            <div style={{ textAlign: "center", padding: "2px 0" }}>
              <InfoModal
                title={`MC Engine — v${APP_VERSION}`}
                accent="var(--accent-teal)"
                trigger={
                  <span style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", letterSpacing: "0.04em", textDecoration: "underline dotted", textUnderlineOffset: 3 }}>
                    Engine &amp; assumptions · v{APP_VERSION}
                  </span>
                }
              >
                <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)", lineHeight: 1.9 }}>
                  <div>
                    <span style={{ color: "var(--accent-teal)" }}>Equity:</span> {SAMPLE_YEARS}yr S&P bootstrap [-30 / +30%]
                  </div>
                  <div>
                    <span style={{ color: "var(--accent-purple)" }}>Bonds:</span> {SAMPLE_YEARS}yr bond history [-15 / +20%]
                  </div>
                  <div>
                    <span style={{ color: "var(--accent-gold)" }}>{getStrategyLabel(assumptions.withdrawalStrategy)}</span>{" "}
                    {(() => {
                      const s = resolveStrategy(assumptions.withdrawalStrategy);
                      if (s === "gk") return `Floor: ${fmtDollar(params.gkFloor)} · Ceiling ${fmtDollar(params.gkCeiling)}`;
                      if (s === "fixed") return `Rate: ${((params.fixedWithdrawalRate || 0.04) * 100).toFixed(1)}%`;
                      if (s === "vpw") return `Amortized to age ${params.vpwEndAge ?? params.endAge ?? 100} at ${((params.vpwRealReturn ?? 0.0376) * 100).toFixed(2)}% real`;
                      return "";
                    })()}
                  </div>
                  <div>
                    <span style={{ color: "#059669" }}>Rental:</span> {assumptions.abReliability || 80}% reliability per year
                  </div>
                  <div>
                    <span style={{ color: "var(--chart-1)" }}>Healthcare:</span> {assumptions.hcProb || 3.5}% shock risk age {assumptions.hcShockAge || 72}+
                  </div>
                  {/* One-off events read from `params` — the object the
                      engines actually receive — so this line can never
                      claim an event the simulation didn't run. Full
                      per-event breakdown lives in Simulation Inputs &
                      Assumptions on the MC tab. */}
                  {(() => {
                    const evs  = (params.cashFlowEvents || []).filter((e) => Number.isFinite(Number(e.year)) && (Number(e.amount) || 0) !== 0);
                    if (evs.length === 0) return null;
                    const sum  = (list) => list.reduce((s, e) => s + (Number(e.amount) || 0), 0);
                    const ins  = evs.filter((e) => e.direction === "in");
                    const outs = evs.filter((e) => e.direction !== "in");
                    return (
                      <div>
                        <span style={{ color: "var(--accent-gold)" }}>One-off events:</span>{" "}
                        {ins.length > 0 && `${ins.length} in +${fmtDollar(sum(ins))}`}
                        {ins.length > 0 && outs.length > 0 && " · "}
                        {outs.length > 0 && `${outs.length} out −${fmtDollar(sum(outs))}`}
                      </div>
                    );
                  })()}
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: "var(--accent-teal)" }}>Phase 1 ({assumptions.preRetireEq ?? 91}/{100 - (assumptions.preRetireEq ?? 91)}):</span> {expectedReturn(assumptions.preRetireEq ?? 91).toFixed(2)}% μ
                    <Hint
                      text={`Pre‑retirement expected return. "μ" (mu) is the mean annual return of a ${assumptions.preRetireEq ?? 91}% stocks / ${100 - (assumptions.preRetireEq ?? 91)}% bonds mix — the weighted average of the S&P 500 and bond history the engine bootstraps from. Individual simulated years vary widely around this average; it is not a guaranteed rate.`}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: "var(--chart-3)" }}>Phase 2 ({assumptions.postRetireEq ?? 70}/{100 - (assumptions.postRetireEq ?? 70)}):</span> {expectedReturn(assumptions.postRetireEq ?? 70).toFixed(2)}% μ
                    <Hint
                      text={`Post‑retirement expected return. "μ" (mu) is the mean annual return of a ${assumptions.postRetireEq ?? 70}% stocks / ${100 - (assumptions.postRetireEq ?? 70)}% bonds mix. The lower equity weight vs. Phase 1 means less volatility and a slightly lower average return once you're drawing down.`}
                    />
                  </div>
                </div>
              </InfoModal>
            </div>

            <div className="sb-card">
              <div className="sb-title" {...twisty(sbMacroOpen, () => setSbMacroOpen(v => !v))}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                <span>{sbMacroOpen ? "▾" : "▸"} Market assumptions</span>
              </div>
              {sbMacroOpen && (<>
              {/* These four were editable HERE and again in the Monte Carlo
                  tab's ⚙ Advanced Settings — two live controls writing one
                  value, which is exactly the Single Point of Control violation
                  this project's design rules exist to prevent (and a user could
                  set 5% inflation in one and read 2.5% in the other). The
                  sidebar keeps what a sidebar is for — the current values, all
                  of them at a glance — and the dialog owns the editing. The
                  button below is that dialog, so this is still one click. */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "7px 10px", fontSize: "var(--fs-label)", marginBottom: 12 }}>
                <span style={{ color: "var(--text-muted)" }}>Inflation (CPI)</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{Number(assumptions.inf ?? inf ?? 2.5).toFixed(1)}%/yr</span>
                <span style={{ color: "var(--text-muted)" }}>Equity glide</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>
                  {assumptions.preRetireEq ?? 91}% → {assumptions.postRetireEq ?? 70}%
                  <span style={{ color: "var(--text-faint)" }}> @{assumptions.glidepathSwitchAge ?? assumptions.retireAge ?? "—"}</span>
                </span>
                <span style={{ color: "var(--text-muted)" }}>SS COLA / yr</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{Number(assumptions.ssCola ?? 2.4).toFixed(1)}%</span>
                <span style={{ color: "var(--text-muted)" }}>Cash return</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>{Number(assumptions.cashRealReturn ?? 3.0).toFixed(1)}%</span>
                <span style={{ color: "var(--text-muted)" }}>Expected return</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-primary)" }}>
                  {expectedReturn(assumptions.preRetireEq ?? 91).toFixed(1)}% → {expectedReturn(assumptions.postRetireEq ?? 70).toFixed(1)}%
                </span>
              </div>
              <PreRetireReturnField value={assumptions.preRetireReturn} onChange={(v) => updateAssumption("preRetireReturn", v)} />
              <MCAdvancedSettings p={assumptions} onAssumptionChange={updateAssumption} />
              </>)}
            </div>

            <div className="sb-card">
              <div className="sb-title" {...twisty(sbOptionsOpen, () => setSbOptionsOpen(v => !v))} style={{ cursor: "pointer" }}>
                {sbOptionsOpen ? "▾" : "▸"} Options
              </div>
              {sbOptionsOpen && (<>
              {/* A user put it well: "there are a lot of tabs with sub
                  tabs and I'll be darned if I can find that one." The
                  control was findable only if you already knew the word
                  "smile," and nothing said what it did to your numbers.
                  Renamed to what it is, and the effect is stated now with
                  the engine's own factors (no second copy of the curve —
                  spendingSmileFactor is the one source). The matching
                  per-year disclosure is the badge in the Spend column. */}
              <Toggle
                val={smile} onChange={setSmile}
                label="Reduce spending with age"
                hint={
                  "ON (default): your spending target is re-scaled each year to follow the "
                  + "Blanchett retirement-spending curve — real spending drifts down through the "
                  + "active 'go-go' years, flattens in the 'slow-go' 80s, then rises again in the "
                  + "'no-go' late 80s as health and care costs take over.\n\n"
                  + `On YOUR plan (retiring at ${retAge}), that means spending of `
                  + `${Math.round(spendingSmileFactor(Math.min(80, endAge), retAge) * 100)}% of your target at age ${Math.min(80, endAge)} and `
                  + `${Math.round(spendingSmileFactor(Math.min(90, endAge), retAge) * 100)}% at age ${Math.min(90, endAge)}. `
                  + "Year one is always 100% — the number you enter is what you spend when you stop working, not a lifetime average.\n\n"
                  + "OFF: spending stays flat in real terms for the whole plan. That is the more "
                  + "conservative assumption and it will lower your success rate.\n\n"
                  + "Where to see it: the Spend column of the Withdrawal Plan table shows the exact "
                  + "percentage applied in each year, so you can tell curve effects from your own target."
                }
              />

              <Toggle
                val={tax} onChange={setTax} label="Model taxes" infoAccent={INFO_ACCENT.tax}
                hint={<>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>What it does:</strong> When ON, every year's withdrawal is grossed up by a full tax calculation so the <em>after-tax</em> amount you keep matches your spending target. Without it, the engine would draw exactly your spend number and silently underfund you by whatever taxes are owed.</p>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>What's modeled each year:</strong> federal brackets with the standard deduction, the Social Security tax torpedo (provisional-income inclusion), IRMAA Medicare surcharges, and your state's brackets (skipped when Non-resident is on). Tax rises naturally over retirement as Social Security starts and RMDs force pre-tax draws.</p>
                    <p style={{ margin:"0 0 10px" }}>Single filers owe more than MFJ at the same income because of halved brackets and standard deduction.</p>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>ON (default):</strong> Full tax model applied. The same calculation drives the Monte Carlo, the Stress Test, and the year-by-year table — no pivot reads differently.</p>
                    <p style={{ margin:0 }}><strong style={{ color:"var(--text-primary)" }}>OFF:</strong> Pure pre-tax view — <em>all</em> tax (federal, state, IRMAA, Roth-conversion cost) is zeroed everywhere. Useful for sanity-checking portfolio dynamics without tax noise, but it overstates how long your money lasts.</p>
                </>}
              />
              <Toggle
                val={real} onChange={setReal} label="Show in today's dollars"
                hint={"OFF = future dollars: the actual balance you'd see on a statement that year, inflation included. $3M at 85 sounds like a lot, but decades of inflation are baked into it.\n\nON = today's dollars: the same money re-expressed in what it would buy right now, so you can judge it against prices you know.\n\n(Economists call these 'nominal' and 'real' — same thing, plainer name.)\n\nOne limit: the chart starts at your retirement year, so 'today' means retirement-year purchasing power. Inflation between now and retirement is not removed. If you are already retired, that is the same as today."}
              />
              <Toggle
                val={!!assumptions.twoHousehold} onChange={(v) => updateAssumption("twoHousehold", v)}
                label="Skip state tax (non-resident)" infoAccent={INFO_ACCENT.tax}
                hint={<>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>What it does:</strong> Removes state income tax from every year of the simulation. Use this if you (or you and your spouse) qualify as a non-resident of your listed state for the year.</p>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>OFF (default):</strong> State tax applies to all taxable income. Use this if you're a resident of your listed state.</p>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>ON:</strong> State tax zeroed out. Use this if you've broken residency (e.g. spending most of the year abroad and meeting your state's non-residency rules).</p>
                    <p style={{ margin:"0 0 10px" }}><strong style={{ color:"var(--text-primary)" }}>Spending is independent:</strong> Total portfolio draw = US Spending + Out-of-Country Spending regardless of this toggle. The toggle only changes whether the US-domestic portion is state-taxed. Check your state's non-residency rules before turning this on — every state defines it differently (number of days, place of work, family location, etc.).</p>
                    <p style={{ margin:0 }}><strong style={{ color:"var(--text-primary)" }}>Set it up:</strong> Set <em>US annual spend</em> in the sidebar for your at-home budget, and <em>Out-of-Country Spending</em> in Plan inputs → Spending &amp; Expenses for your travel/abroad budget. If Out-of-Country Spending is left at $0, it falls back to your US spending.</p>
                </>}
              />
              </>)}
            </div>

            <button
              className="run-btn"
              onClick={runSimulation}
              disabled={running}
              style={{
                background: stale ? "linear-gradient(135deg,#3d6fe0,var(--chart-2))" : undefined,
              }}
            >
              {running ? `Running ${Math.round(Number(params?.mcPaths) || MC_PATHS).toLocaleString()} paths...` : stale ? "Inputs changed — Re-run" : "▶ Run Monte Carlo"}
            </button>
            {lastRunAt && !running && (
              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", textAlign: "center", marginTop: 5 }}>
                Last run {fmtLastRun(lastRunAt)}
              </div>
            )}
          </div>

          <main className="main">

            {stale && (
              <div
                style={{
                  background: "var(--bg-warning)",
                  border: "1px solid rgba(244,211,94,0.4)",
                  borderRadius: 8,
                  padding: "7px 12px",
                  fontSize: "var(--fs-label)",
                  color: "var(--accent-gold)",
                }}
              >
                Inputs changed — success rates below are stale. Press Re-run to update.
              </div>
            )}

            {/* Hero: one number that matters, with secondary metrics
                demoted to a compact strip and all interpretive copy behind
                a "What does this mean?" toggle. */}
            {(() => {
              const heroColor = mc ? (mc.rate >= 0.85 ? "var(--positive)" : mc.rate >= 0.7 ? "var(--accent-gold)" : "var(--negative)") : "#334155";
              const sep = <span style={{ color: "var(--text-faint)" }}>·</span>;
              return (
                <div className={`met ${running ? "recomputing" : ""}`} style={{ borderLeft: `4px solid ${heroColor}` }}>
                  {/* Row 1 — the one number that matters, with the disclosure toggle aligned right. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                      <div className="mv" style={{ fontSize: 44, color: heroColor }}>
                        {mc ? fmtPct(mc.rate) : "—"}
                      </div>
                      <div style={{ fontSize: "var(--fs-h2)", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 5 }}>
                        Success to Age {endAge}
                        {/* Opens the SAME rich method modal as the Monte Carlo tab's
                            Success Rate card (one component, one copy — rule 8).
                            This was an InfoDot with one sentence, so the hero ⓘ
                            never reached the real explanation of how the number is
                            made. Trigger mirrors InfoDot's round icon. */}
                        <SimMethodModal
                          params={params}
                          withdrawalStrategy={withdrawalStrategy}
                          trigger={
                            <span
                              role="img"
                              aria-label="How this simulation works"
                              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", background: "var(--card-border)", color: "var(--text-secondary)", cursor: "pointer" }}
                            >
                              <InfoIcon size={12} />
                            </span>
                          }
                        />
                      </div>
                    </div>
                    {/* design-authority (2026-09-17): was an inline expand/collapse
                        panel below the hero card. Moved into an InfoModal so the
                        interpretive content (odds analogy + 26-person visual) is a
                        proper "fun spot" — one click, no layout shift, dismissible —
                        instead of pushing the tabs down every time it's open.
                        RotatingAnalogue's own click-to-cycle still works inside the
                        modal: InfoModal's card stops click propagation to the
                        backdrop, so cycling an analogy never closes the dialog. */}
                    {mc && (
                      <span style={{ marginLeft: "auto" }}>
                        <InfoModal
                          title={`${fmtPct(mc.rate)} Success to Age ${endAge}`}
                          accent={heroColor}
                          trigger={
                            <span style={{ color: "var(--accent-teal)", fontSize: "var(--fs-body)", fontFamily: "inherit", padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                              What does this mean? ▾
                            </span>
                          }
                        >
                          {/* 26-person visual leads — most legible "fun" per pixel,
                              per design-authority. Dots sized to fit InfoModal's
                              480px max width in one row (26 × 12px + 25 × 3px gap
                              ≈ 387px, under the ~424px usable width). */}
                          {(() => {
                            const success = Math.round(mc.rate * 26);
                            const fail = 26 - success;
                            return (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 8 }}>
                                  If 26 people had your exact plan — age {endAge} horizon
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 8 }}>
                                  {Array.from({ length: 26 }, (_, i) => (
                                    <div
                                      key={i}
                                      style={{
                                        width: 12, height: 12, borderRadius: "50%",
                                        background: i < success ? "var(--positive)" : "var(--negative)",
                                        opacity: i < success ? 1 : 0.4,
                                      }}
                                      title={i < success ? "Survives" : "Depleted"}
                                    />
                                  ))}
                                </div>
                                <div style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}>
                                  <span style={{ color: "var(--positive)", fontWeight: 700 }}>{success}</span> make it to {endAge}.{" "}
                                  {fail > 0 && (
                                    <><span style={{ color: "var(--negative)", fontWeight: 700 }}>{fail}</span> run out.</>
                                  )}
                                  {fail === 0 && <span style={{ color: "var(--positive)" }}> Everyone makes it.</span>}
                                </div>
                                <div style={{ fontSize: "var(--fs-small)", color: "var(--text-faint)", fontStyle: "italic", marginTop: 6 }}>
                                  “100% doesn't exist — room for error IS the plan.” — Morgan Housel
                                </div>
                              </div>
                            );
                          })()}
                          {/* Odds analogy second, fully interactive — click cycles
                              through every analogy in the same probability band. */}
                          {analogue && (
                            <div style={{ borderTop: "1px solid var(--tint-white-08)", paddingTop: 12 }}>
                              <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginBottom: 6 }}>Put another way</div>
                              <RotatingAnalogue rate={mc.rate} endAge={endAge} />
                            </div>
                          )}
                        </InfoModal>
                      </span>
                    )}
                  </div>
                  {/* Row 2 — secondary metrics, one line: portfolio at plan age and the
                      spend target. Withdrawal rate moved to the verdict strip as its own
                      card, and the strategy-detail strip was removed, so this row carries
                      no strategy copy. */}
                  <div style={{ fontSize: "var(--fs-body)", color: "var(--text-muted)", marginTop: 11, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px" }}>
                    {/* `mc.medR` is the median portfolio at the start of
                        retirement (runMC captures portAtRetire before the
                        drawdown loop), so showing it under "at age
                        {endAge}" reported the wrong metric entirely — the
                        balance before a single withdrawal, labeled as the
                        balance after a full retirement. medR is still
                        correct on the "Portfolio at Retirement" card.
                        Routed through selectPortfolioAtAge() at this
                        label's own age (endAge) rather than reading
                        mc.term.p50 straight off runMC — mc.term reflects
                        the survivor-extended plan horizon when one is
                        modeled, which isn't always endAge, so reading it
                        directly could show a different age than the one
                        printed right next to it. Follows the Real-$ toggle
                        and names its basis with the same label as the
                        percentile row on the Forecast tab, so the two medians
                        are the same figure. */}
                    <span title="Median projected portfolio value left at your plan age, across all simulated paths.">
                      <strong style={{ color: "var(--text-secondary)" }}>{mc ? fmtDollar(selectPortfolioAtAge(mc, endAge, { retireAge: retAge, real, inf }) ?? 0) : "—"}</strong> at age {endAge} <span style={{ fontSize: "var(--fs-label)", opacity: 0.75 }}>({dollarBasisLabel(real, retirementBasisYear(params.currentAge, retAge, CURRENT_YEAR)).toLowerCase()})</span>
                    </span>
                    {sep}
                    {/* This is params.sp — the spending target the user
                        typed — divided by 12. The engine doesn't solve for
                        it. Labeling it "safe spend" claimed a number the
                        app had computed and certified, when all it does is
                        echo the input back; the success rate to the left
                        is what says whether the target holds. "Your
                        target" is the honest frame.

                        It's also after tax. runMC sizes the portfolio draw
                        as `need + totalTax` (~line 1538) — tax is an
                        additional draw on top of the spend target, never
                        netted out of it — so this is money that reaches
                        the household to spend. Nothing said so before,
                        which is why it needed saying. */}
                    <span title="Your spending target — the figure you entered, shown monthly. This is money to spend AFTER tax: the engine withdraws enough extra from the portfolio to cover the tax bill on top of this amount, so taxes are not taken out of it. Covered by Social Security, rental and other income first, then your portfolio draw. The success rate on the left is what tells you whether this target holds.">
                      <strong style={{ color: "var(--accent)" }}>${(Math.round(params.sp / 12)).toLocaleString()}/mo</strong> your spend target <span style={{ fontSize: "var(--fs-label)", opacity: 0.75 }}>(after tax)</span>
                    </span>
                  </div>
                  {/* Sector / life-phase badge — lower far right, aligned under the toggle. */}
                  {analogue && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, marginTop: 10 }}>
                      <span style={{ fontSize: "var(--fs-small)", fontWeight: 700, color: "var(--text-muted)" }}>Current life phase</span>
                      <SectorBadge age={currentAge} />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* THE VERDICT, before the tabs. The brief's §7 test — answer "am I
                okay?" in 30s without clicking — fails when the answer lives
                inside a tab below a Run button. This strip is above every tab,
                so the four headline answers are on screen the moment a run
                exists, whichever tab the user lands on. */}
            <VerdictHeader
              mc={mc}
              real={real}
              inf={inf}
              endAge={params.endAge}
              currentAge={params.currentAge}
              retireAge={params.retireAge}
              swr={swr}
              stress={stress}
              swrBenchmark={params.safeWithdrawalRate}
            />

            <nav className="tabs" aria-label="Sections" ref={tabsRef}>
              {TABS.map(([k, l]) => (
                <button key={k} className={`tab ${activeTab === k ? "on" : ""}`} onClick={() => setTab(k)}>
                  {l}
                </button>
              ))}
            </nav>
            <h2 className="sr-only">{TABS.find(([k]) => k === activeTab)?.[1]}</h2>

            {needsMC.includes(activeTab) && !hasMC ? (
              <div
                className="chart-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 260,
                  color: "var(--text-faint)",
                }}
              >
                Press ▶ Run Monte Carlo to generate charts.
              </div>
            ) : (
              <>
                {activeTab === "montecarlo" && (
                  <>
                    {/* Monte Carlo's two views, in the app's own tab language:
                        the teal UNDERLINE (`.tab.on`), not a new segmented
                        control. Vincent, 2026-09-24: a filled pill track was a
                        fourth tab idiom in an app whose established one is the
                        underline — consistency wins over my hierarchy argument.
                        Subordinate to the top-level bar by SIZE (12.5px vs 13px)
                        and by FILL (the top bar tints its active tab; this one
                        does not), so the levels still read as levels. */}
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", borderBottom: "1px solid var(--divider)", marginBottom: 12 }}>
                    <div role="tablist" aria-label="Monte Carlo views" style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {[["simulation", "Simulation"], ["guardrails", "Guardrails"]].map(([k, label]) => {
                        const on = mcView === k;
                        return (
                          <button
                            key={k}
                            role="tab"
                            aria-selected={on}
                            onClick={() => setMcView(k)}
                            style={{
                              background: "transparent",
                              border: "none",
                              borderBottom: `2px solid ${on ? "var(--accent-teal)" : "transparent"}`,
                              marginBottom: -1,
                              padding: "8px 20px",
                              fontSize: "var(--fs-label)",
                              fontWeight: on ? 800 : 600,
                              color: on ? "var(--accent-teal)" : "var(--text-muted)",
                              fontFamily: "var(--font-sans)",
                              letterSpacing: "-0.01em",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              transition: "color 0.15s, border-color 0.15s",
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                      {/* Run details share the sub-tab row: path count, sampling
                          window, the method pop-up and Advanced Settings. */}
                      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px 14px", flexWrap: "wrap", padding: "4px 0", fontSize: "var(--fs-small)", color: "var(--text-muted)" }}>
                        <span>
                          {(() => {
                            const paths = Math.max(MC_PATH_MIN, Math.min(MC_PATH_MAX, Math.round(Number(params?.mcPaths) || MC_PATHS)));
                            const r = resolveSampleRange(params);
                            const yrs = r ? `${SAMPLE_START_YEAR + r.start}–${SAMPLE_START_YEAR + r.start + r.count - 1}` : `${SAMPLE_START_YEAR}–${SAMPLE_END_YEAR}`;
                            return `${paths.toLocaleString()} paths · sampling ${yrs}`;
                          })()}
                        </span>
                        <SimMethodModal
                          params={params}
                          withdrawalStrategy={assumptions.withdrawalStrategy}
                          trigger={<span aria-label="How this simulation works" style={{ color: "var(--chart-2)", cursor: "pointer", fontSize: "var(--fs-small)", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "1px dashed rgba(96,165,250,0.45)", display: "inline-flex", alignItems: "center", gap: 4 }}><InfoIcon size={12} /> How this works</span>}
                        />
                        <MCAdvancedSettings p={params} onAssumptionChange={updateAssumption} />
                      </div>
                    </div>

                    {mcView === "simulation" ? (
                    <>
                    <MCTab
                      params={params}
                      mc={mc}
                      running={running}
                      onRun={runSimulation}
                      checkpoints={assumptions.checkpoints}
                      portfolioGoal={assumptions.portfolioGoal}
                      earlyRetireTarget={assumptions.earlyRetireTarget}
                      onUpdateCheckpoints={(newCheckpoints) => updateAssumption("checkpoints", newCheckpoints)}
                      onDeleteCheckpoint={(id) =>
                        updateAssumption(
                          "checkpoints",
                          (assumptions.checkpoints || []).filter((c) => String(c.id) !== String(id))
                        )
                      }
                      dob={assumptions.dob}
                      inf={inf}
                      real={real}
                      withdrawalStrategy={assumptions.withdrawalStrategy}
                      onAssumptionChange={updateAssumption}
                      onSetBaselineFromCheckpoint={(value) => {
                        setPort(value);
                        const currentTotal = port;
                        if (currentTotal > 0) {
                          const scale = value / currentTotal;
                          const scaledAccounts = assumptions.accounts.map((acc) => ({
                            ...acc,
                            balance: Math.round((acc.balance || 0) * scale),
                          }));
                          updateAssumption("accounts", scaledAccounts);
                        }
                        // Changing the baseline is an input edit like any
                        // other: it marks the results stale and waits for the
                        // Run button, rather than racing a run against state
                        // that hasn't committed yet.
                      }}
                      // The chart and the age table are placed BY the tab (chart first,
                      // table after the explanation) rather than stacked after it.
                      chart={mc && (
                      <FanChart
                        // eslint-disable-next-line no-restricted-properties -- FanChart deflates the whole array itself; see noRawMcAccess.test.js.
                        pcts={mc.pcts}
                        retireAge={retAge}
                        ssAge={assumptions.ssAge}
                        rmdAge={rmdAge}
                        inf={inf}
                        useReal={real}
                        checkpoints={assumptions.checkpoints}
                        earlyRetireTarget={assumptions.earlyRetireTarget}
                        dob={assumptions.dob}
                        portfolioGoal={assumptions.portfolioGoal}
                        // Derived age, not assumptions.currentAge — that
                        // stored field never changes when the birthday
                        // does, so the fan kept plotting the old age (and
                        // the accumulation ramp and "you are here" dot with it).
                        currentAge={currentAge}
                        currentPort={params.port}
                        contrib={params.contrib}
                        hhProfile={params}
                        preRetireEq={params.preRetireEq ?? 91}
                        hoveredAge={hoveredAge}
                        // The age-by-age table is the chart card's "Table" view.
                        table={(
                      <MCBandTable
                        embedded
                        // eslint-disable-next-line no-restricted-properties -- FanChart deflates the whole array itself; see noRawMcAccess.test.js.
                        pcts={mc.pcts}
                        inf={inf}
                        useReal={real}
                        ssAge={assumptions.ssAge}
                        rmdAge={rmdAge}
                        // Same fix: this feeds the table's calendar-year
                        // column (yr = CURRENT_YEAR + age - currentAge),
                        // so a stale age shifted every year in the table
                        // by the difference.
                        currentAge={currentAge}
                        endAge={endAge}
                        hoveredAge={hoveredAge}
                        onHoverAge={setHoveredAge}
                        // Path count is user-adjustable (⚙ Advanced Settings),
                        // so the Success Count column reads mc.N rather than the
                        // MC_PATHS constant — otherwise a 500-path run would
                        // print "3,000" beside every row.
                        totalPaths={mc.N}
                        // Phase boundary: the effective retirement age, the same
                        // clamp the engines use (an already-retired user's phase
                        // flips at today, not at a stale entered age).
                        retireAge={effectiveRetireAge(params.retireAge, params.currentAge)}
                      />
                        )}
                      />
                      )}
                    />
                    </>
                    ) : (
                      <div className="chart-card">
                        <div className="ct">Guardrails — how your spending flexes over time</div>
                        <div style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.55, marginBottom: 4 }}>
                          Your withdrawal strategy raises or cuts spending as markets move, keeping the plan funded.
                          The dashed bands are your spending floor and ceiling; ▲/▼ mark the years an adjustment fired.
                        </div>
                        {/* GuardrailsView returns null for a strategy that has no guardrails, which
                            would leave a blank tab with no explanation — the same dead-end the
                            competitor's empty columns create. Say why instead. */}
                        {isGuardrailStrategy(assumptions.withdrawalStrategy) ? (
                          <GuardrailsView p={params} inf={inf} withdrawalStrategy={assumptions.withdrawalStrategy} mc={mc} topRule={false} />
                        ) : (
                          <div style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)", lineHeight: 1.6, padding: "10px 2px" }}>
                            Your current spending strategy doesn’t use Guyton–Klinger guardrails, so there is nothing to
                            flex here. Switch to a guardrail-family strategy (Guyton–Klinger, Risk-based, Kitces, or Smart
                            Waterfall) in <strong style={{ color: "var(--text-secondary)" }}>Scenarios → Withdrawal Plan</strong> to
                            see the spending path, its floor and ceiling, and every year an adjustment fired.
                          </div>
                        )}
                      </div>
                    )}
                    {mc && <McTabDisclaimer />}
                  </>
                )}
                {activeTab === "scenarios" && (
                  <ScenariosTab
                    initialSubTab={pendingScenarioSubTab}
                    onSubTabConsumed={() => setPendingScenarioSubTab(null)}
                    baseParams={params}
                    mc={mc}
                    fmtPct={fmtPct}
                    stress={stress}
                    checkIns={checkIns}
                    onDeleteCheckIn={handleDeleteCheckIn}
                    onRenameCheckIn={handleRenameCheckIn}
                    onImportCheckIns={handleImportCheckIns}
                    retireAge={retAge}
                    ssAge={assumptions.ssAge}
                    rmdAge={rmdAge}
                    inf={inf}
                    real={real}
                    FanChart={FanChart}
                    SEQ_2000_2012={SEQ_2000_2012}
                    DeterministicWithdrawalView={DeterministicWithdrawalView}
                    RothLadder={RothLadder}
                    BucketsTab={BucketsTab}
                    portfolioGoal={assumptions.portfolioGoal}
                    earlyRetireTarget={assumptions.earlyRetireTarget}
                    withdrawalStrategy={assumptions.withdrawalStrategy}
                    checkpoints={assumptions.checkpoints}
                    dob={assumptions.dob}
                    assumptions={assumptions}
                    onAssumptionChange={updateAssumption}
                    onSaveConversionOverride={(year, amount, income) => {
                      setAssumptions(prev => ({
                        ...prev,
                        conversionOverrides: [
                          ...(prev.conversionOverrides || []).filter(o => Number(o.year) !== Number(year)),
                          { id: Date.now().toString(), year: Number(year), amount: Number(amount), ...(income || {}) },
                        ].sort((a, b) => a.year - b.year),
                      }));
                    }}
                    onRemoveConversionOverride={(year) => {
                      setAssumptions(prev => ({
                        ...prev,
                        conversionOverrides: (prev.conversionOverrides || []).filter(o => Number(o.year) !== Number(year)),
                      }));
                    }}
                  />
                )}
                {activeTab === "networth" && <NetWorthTab p={params} mc={mc} inf={inf} real={real} />}
                {activeTab === "actionplan" && (
                  <ActionPlanTab
                    params={params}
                    mc={mc}
                    assumptions={assumptions}
                    mortgagePayoffYear={mortgagePayoffYear}
                    rmdAge={rmdAge}
                  />
                )}
                {/* AiraAITab is dormant — integration target is inside ActionPlanTab (above), not as its own tab. */}
                {activeTab === "assumptions" && (
                  <>
                  <ProfileWizard
                    jumpTo={profileJump}
                    onNavigateTab={navigateToTab}
                    autosavedAt={lastAutosaveAt}
                    values={liveProfile}
                    onChange={(k, v) => {
                      updateAssumption(k, v);
                      if (k === "retireAge") setRetAge(v);
                      if (k === "endAge") setEndAge(v);
                      if (k === "port") setPort(v);
                      if (k === "contrib") setContrib(v);
                      if (k === "sp") setSp(v);
                      if (k === "ssAge") updateAssumption("ssAge", v);
                      if (k === "ssb") setSsb(v);
                      if (k === "ab") setAb(v);
                    }}
                  />
                  <div className="sb-card" style={{ marginTop: 16 }}>
                    <div className="sb-title">Start over</div>
                    {!confirmStartOver ? (
                      <button type="button" className="sb-edit" style={{ fontSize: "var(--fs-label)" }} onClick={() => setConfirmStartOver(true)}>
                        Reset to the quick-start screen…
                      </button>
                    ) : (
                      <div role="alertdialog" aria-label="Confirm start over" style={{ fontSize: 12, lineHeight: 1.6 }}>
                        This deletes your saved plan and returns to the quick-start screen. Export your profile first if you want to keep it.
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button type="button" className="cfg-btn" style={{ margin: 0 }} onClick={() => setConfirmStartOver(false)}>Cancel</button>
                          <button type="button" className="cfg-btn" style={{ margin: 0, background: "var(--chart-1)" }}
                            onClick={() => { skipAutosave.current = true; resetLocalProfile(); window.location.reload(); }}>
                            Delete and start over
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  </>
                )}
              </>
            )}

            <div
              style={{
                fontSize: "var(--fs-small)",
                color: "#bacee9",
                textAlign: "center",
                paddingTop: 4,
                lineHeight: 1.6,
              }}
            >
              AiRA Freedom Financial v{APP_VERSION} · This is not financial advice. Seek a professional fiduciary, CPA, or tax accountant. Use at your own risk.
              This application is open source and covered under the GNU Affero General Public License v3.0.{" "}
              <br />
              "The best financial plan is the one you can stick with." — Morgan Housel
              <br />
              <button
                onClick={() => setShowTerms(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--accent)",
                  cursor: "pointer",
                  fontSize: "var(--fs-small)",
                  textDecoration: "underline",
                  padding: "2px 0",
                }}
              >
                Terms of Service &amp; Disclaimer
              </button>
            </div>
          </main>
        </div>
      </div>

      {/* Admin panel (hidden; activate via ?aira_admin=1) */}
      <AdminPanel />

      {/* Purchase / account-restore toast (success + failure) */}
      {recoveryLink && (
        <RecoveryLinkModal
          url={recoveryLink.url}
          expiresAt={recoveryLink.expiresAt}
          onClose={() => setRecoveryLink(null)}
        />
      )}
      {stripeToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          // Amber is its own state, not a shade of success. A payment
          // that went through but hasn't yet delivered is neither "done"
          // nor "failed," and showing it green would tell someone staring
          // at a paywall that everything worked.
          background: stripeToast.tone === "err"
            ? "rgba(220,38,38,0.97)"
            : stripeToast.tone === "warn"
              ? "rgba(244,211,94,0.97)"
              : "rgba(79,216,143,0.95)",
          color: "white",
          borderRadius: 8, padding: "10px 20px", fontSize: "var(--fs-body)", fontWeight: 600,
          boxShadow: "0 4px 16px var(--tint-black-40)", zIndex: 99999,
          // Clickable when it carries an email address to act on; the success
          // and error toasts stay click-through as before.
          pointerEvents: stripeToast.tone === "warn" ? "auto" : "none",
          maxWidth: "min(92vw, 560px)", textAlign: "center",
          lineHeight: 1.45,
        }}>
          {stripeToast.msg}
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTerms && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setShowTerms(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 10,
              maxWidth: 680,
              width: "100%",
              maxHeight: "85vh",
              overflowY: "auto",
              padding: "28px 32px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.28)",
              fontFamily: "system-ui, sans-serif",
              color: "#1e293b",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: "var(--fs-h1)", fontWeight: 700, color: "#1e3a5f" }}>Terms of Service</h2>
              <button
                onClick={() => setShowTerms(false)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "var(--fs-h1)", lineHeight: 1, color: "var(--text-muted)", padding: "0 4px",
                }}
                aria-label="Close"
              >×</button>
            </div>
            <p style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", marginTop: 0 }}>Last updated: February 22, 2026</p>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>1. Agreement to Terms</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                By accessing and using the AiRA Financial Freedom application, you accept and agree to be bound by the terms and
                provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>2. Disclaimer of Warranties</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                The AiRA Financial Freedom application is provided on an "AS IS" and "AS AVAILABLE" basis. AiRA Financial Freedom nor its Developers, make any
                representations or warranties of any kind, express or implied, as to the operation of the application or the
                information, content, or materials included on the application. To the fullest extent permissible by applicable
                law, AiRA  Financial Freedom and its Developers disclaim all warranties, express or implied, including but not limited to implied warranties
                of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>3. Limitation of Liability</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                In no event shall the AiRA  Financial Freedom application, its directors, employees, agents, or suppliers be liable for any damages
                (including, without limitation, lost profits, savings, or data; business interruption; or any other special,
                indirect, incidental, or consequential damages) arising out of or in connection with the use, inability to use,
                or results of the use of the application, even if AiRA Financial Freedom and its Developers have been advised of the possibility of such damages.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#d97706", marginBottom: 6 }}>4. Not Financial Advice</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                The AiRA Financial Freedom application provides calculations and projections for <strong>educational and informational
                purposes only</strong>. The application does not provide professional investment, tax, or financial advice. All
                calculations are estimates based on the information you provide and are subject to change. You should not rely
                solely on the calculations provided by the AiRA Financial Freedom application for making financial decisions. Always consult with
                a qualified professionals such as a financial advisors, tax advisors, or mortgage professionals before making
                important financial decisions.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>5. Accuracy of Information</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                While we strive to ensure the accuracy of calculations, the AiRA Financial Freedom application makes no guarantee regarding the accuracy
                or completeness of the results. Market conditions, interest rates, inflation, and other factors may vary from
                the assumptions used in the calculator. Historical performance does not guarantee future results. All information displayed are dependent upon information that you provide and by using this 
                application you agree to not hold accountable the application nor it's developers for any financial decisions made based on the results of the calculator.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>6. User Responsibilities</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                You are responsible for ensuring that all information you input into the calculator is accurate and current.
                You are also responsible for protecting the confidentiality of your personal financial information.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>7. Modifications to Terms</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                The AiRA Financial Freedom application reserves the right to modify these terms and conditions at any time. Your continued use of the
                application following the posting of revised terms means that you accept and agree to the changes.
              </p>
            </section>

            <section style={{ marginBottom: 18 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>8. Governing Law</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                These terms and conditions are governed by and construed in accordance with the laws of the United States, and
                you irrevocably submit to the exclusive jurisdiction of the courts in that location.
              </p>
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: "var(--fs-body)", fontWeight: 700, color: "#1e3a5f", marginBottom: 6 }}>9. Indemnification</h3>
              <p style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, margin: 0 }}>
                You agree to indemnify and hold harmless the AiRA Financial Freedom application and its Developers from any and all claims, damages, losses, or expenses
                arising out of your use of the application or violation of these Terms of Service.
              </p>
            </section>

            <div style={{ textAlign: "center" }}>
              <button
                onClick={() => setShowTerms(false)}
                style={{
                  background: "#1e3a5f", color: "#fff", border: "none",
                  borderRadius: 6, padding: "8px 28px", fontSize: "var(--fs-body)",
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showReport && mc && (
        <PrintReport
          params={{ ...params, name: assumptions.name }}
          mc={mc}
          stress={stress}
          rmdAge={rmdAge}
          onClose={() => setShowReport(false)}
          // Paywall gate. `!reportCapable` always wins: the report stays
          // locked on any deployment whose server doesn't have
          // GEMINI_API_KEY configured, regardless of BILLING_ENABLED —
          // closes the "flip one source-level boolean and self-host it
          // free" loophole (BILLING_ENABLED=false used to unconditionally
          // unlock, since the report's data is 100% client-computed and
          // BILLING_ENABLED isn't a real secret). On my real deployment
          // reportCapable is true and behavior is unchanged:
          // BILLING_ENABLED=false still opens the report free for
          // local/dev convenience; BILLING_ENABLED=true gates on
          // useReportUnlocked(), which reconciles against the ledger —
          // this used to read a localStorage flag, so editing one value
          // granted access and clearing it caused a second 250-credit
          // charge for a window already owned. A server-verified owner
          // reads the real report with no purchase prompt. This also
          // bypasses `reportCapable` on purpose: proving ADMIN_SECRET is
          // strictly stronger evidence of being the operator than the
          // GEMINI_API_KEY probe it replaces.
          locked={ownerVerified ? false : (!reportCapable || (BILLING_ENABLED && !reportUnlocked))}
        />
      )}
      {/* Says why the report is unlocked, so an owner preview can never
          be mistaken for what a paying customer sees. Sits above the
          report overlay (z-index 20000 in PrintReport's own CSS). */}
      {showReport && mc && ownerVerified && (
        <div style={{
          position: "fixed", top: 10, left: "50%", transform: "translateX(-50%)",
          zIndex: 20001, background: "rgba(124,58,237,0.95)", color: "#fff",
          borderRadius: 999, padding: "5px 16px", fontSize: "var(--fs-label)", fontWeight: 700,
          boxShadow: "0 4px 16px var(--tint-black-40)", pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          🔧 Owner preview — purchase prompt suppressed (admin secret verified)
        </div>
      )}
    </>
    </LeverContext.Provider></NavContext.Provider>
  );
}

export { runMC, runStress, mortgageSchedule, calcYearTax, getRmdStartAge, guytonKlingerWithdrawal, progTax, irmaaCost, simulateDeterministicWithStrategy, waterfallForActiveStrategy, getStandardDeduction, getIrmaaCeiling, getBracketCeiling, loadCheckIns, saveCheckIns, ProgressTab, planShapeScores, mergeCheckIns, ageFromDob, AGE_LIMITS, InfoIcon, InfoDot, mcMedianAtAge, selectPortfolioAtAge, deflate, ANumInput, parseNumericEntry, TaxDetailsModal, resolveSampleRange, MCAdvancedSettings, GK_BAND_PCT, GK_ADJUST_PCT, GK_LONGEVITY_YEARS, DeterministicWithdrawalView, SimMethodModal, MCBandTable, bandLabel, MCOverviewCards, VerdictHeader, NetWorthTip, PillTab, SequenceBuilder, FailedPathPanel };
